// ═══ AUREUS SOCIAL PRO — Constantes legales belges ═══
// Extrait du monolithe pour reutilisation dans les modules
// NOTE: pas de "use client" — lib isomorphique (server + client)

// ═══════════════════════════════════════════════════════════════════════════
// FLEXI-SALAIRE — UN SEUL PLANCHER, PAS UN « GÉNÉRAL » ET UN « HORECA »
// ───────────────────────────────────────────────────────────────────────────
// Ce fichier portait le flexi-salaire à DEUX endroits (`cotisations.flexiJob`
// et le bloc horeca `flexiJob`), et `juridique-kb.js` en tenait une troisième
// copie — celle qui part dans les réponses juridiques aux clients. Les trois
// divergeaient : plafond 19,17 €/h ici, « 21 €/h dès le 01/07/2026 » dans la
// note d'à côté, 21,00 € sur la fiche du SPF.
//
// ✅ TEXTE PRIMAIRE, 02/09/2026 : la fiche SPF de la CP 302 (jcId
// ceaa4cb322cc4933bdb1b933335760ee), en vigueur au 01/09/2026, section 1.2
// « Flexisalaire » → minimum 12,11 €/h. Le maximum y était 21,42 €/h le 02/09/2026 ;
// ⚠️ RELU LE 15/10/2026 (page actualisée le 24/09/2026) : « Maximum 21,00 », avec
// l'avertissement « Salaires flexibles -sauf salaire maximum- : Indexation de 2 % en
// 9/2026 ». Le plafond horaire n'est PAS indexé : forfaitaire depuis le 01/07/2026
// (21 €/h, 22,61 € avec flexi-pécule — Securex, lu le 15/10/2026). La valeur 21,42
// lue le 02/09 était une erreur de la fiche, corrigée par le SPF entre-temps.
//
// 🔑 ET IL N'Y A QU'UN PLANCHER POUR TOUT LE MONDE : les fiches SPF des autres
// secteurs qui autorisent le flexi-job (CP 118, 202, 311, 312 vérifiées le
// 02/09) écrivent toutes la même phrase — « Flexisalaire, il est renvoyé à la
// commission paritaire 302 – Industrie hôtelière ». La distinction
// « général / horeca » que portait ce fichier n'existe pas dans la source.
//
// ⚠️ Ce sont des montants NETS (le flexi-salaire n'est ni cotisé côté
// travailleur ni imposé) ; le flexipécule de 7,67 % s'y ajoute.
// ═══════════════════════════════════════════════════════════════════════════
// La date d'effet du montant SERVI. Elle existait jusqu'ici uniquement dans la
// phrase de source ci-dessus — donc illisible par le code, et recopiée en dur
// ('2026-03-01', périmé) dans lois-belges-flexi.js. Un champ, pas une phrase.
// ═══════════════════════════════════════════════════════════════════════════
// RETENUES SUR LA RÉMUNÉRATION — ART. 23 DE LA LOI DU 12/04/1965
// ═══════════════════════════════════════════════════════════════════════════
// ✅ TEXTE DE L'ARTICLE LU LE 10/09/2026 (refli.be, texte consolidé mis à jour
// au 21/06/2024 ; concordant avec la synthèse du SPF Emploi et avec
// actualitesdroitbelge.be). Cette constante existait déjà, enfouie dans
// `attestationVacancesEntrante` au cran 🟠 « article 23 non relu » : elle est
// remontée ici, relue, et il n'en existe plus qu'UNE écriture (R14 bis).
//
// 📏 CE QUE L'ARTICLE DIT, ET POURQUOI ÇA COMPTE (dossier Les P'tits Marins,
// 10/09/2026). Beaucoup croient qu'un acompte versé en cours de contrat se
// reprend intégralement sur le solde de tout compte. C'est FAUX :
//   · le 4° autorise bien l'imputation des « avances en argent faites par
//     l'employeur » — un acompte est donc retenable ;
//   · MAIS « le total des retenues ne peut dépasser le cinquième de la
//     rémunération en espèces due à chaque paie, déduction faite des retenues
//     effectuées en vertu de la législation fiscale [et] sociale » — donc 20 %
//     du NET, paie par paie ;
//   · et l'exception au plafond (« lorsque le travailleur a agi par dol ou a
//     mis volontairement fin à son engagement avant la liquidation des
//     indemnités et dommages et intérêts ») ne vise QUE le 3°, les indemnités
//     de l'art. 18 de la loi du 03/07/1978. ⚠️ Elle ne couvre PAS les avances.
// Conséquence chiffrée : sur un pécule de sortie de 287,70 € nets, un acompte
// de 1 000 € ne peut être retenu qu'à concurrence de 57,54 €. Le reste se
// récupère autrement (accord écrit, ou voie civile) — jamais en vidant le
// décompte.
const RETENUES_ART23 = {
  plafondNetPct: 0.20,           // « le cinquième de la rémunération en espèces due à chaque paie »
  // Les cinq imputations autorisées, dans l'ordre de l'article. La liste est
  // LIMITATIVE : ce qui n'y figure pas ne se retient pas.
  imputationsAutorisees: [
    'retenues fiscales, de sécurité sociale et avantages complémentaires de sécurité sociale',
    'amendes infligées en vertu du règlement de travail',
    'indemnités et dédommagements dus en exécution de l\'art. 18 de la loi du 03/07/1978',
    'avances en argent faites par l\'employeur',
    'cautionnement, et rémunération payée en trop sous horaire flottant',
  ],
  // ⚠️ NE VISE QUE LE 3°. Écrit ici parce que c'est exactement l'erreur que
  // l'article invite à commettre quand on le lit trop vite.
  exceptionPlafondVise: '3° uniquement (indemnités art. 18) — dol du travailleur, ou rupture volontaire avant liquidation',
  source: 'Art. 23 de la loi du 12/04/1965 concernant la protection de la rémunération des travailleurs — texte consolidé lu le 10/09/2026 sur refli.be/fr/lex/1965041207 (mise à jour au 21/06/2024). ⚠️ Reproduction consolidée : ejustice.just.fgov.be est passé derrière l\'anti-bot F5 (mesuré le 10/09/2026 : http 200, 46 790 octets, contenu = défi JavaScript), et l\'archive Wayback rend 000. Recoupé avec emploi.belgique.be (Protection de la rémunération) et actualitesdroitbelge.be.',
  dateLecture: '2026-09-10',
};

// ═══════════════════════════════════════════════════════════════════════════
// PÉRIODICITÉ ET DATES DE PAIEMENT — ART. 9 ET 11 DE LA LOI DU 12/04/1965
// ═══════════════════════════════════════════════════════════════════════════
// ✅ TEXTE DES ARTICLES LU LE 18/09/2026 (refli.be/fr/lex/1965041207, texte
// consolidé mis à jour au 14/04/2026 — reproduction ; le Moniteur fait foi).
//   · art. 9 al. 1er : « au moins deux fois par mois, à seize jours d'intervalle
//     au plus », sauf 1° « la rémunération des employés, qui doit être payée au
//     moins tous les mois » ; dérogation à l'al. 1er « par voie de décision de la
//     commission paritaire compétente rendue obligatoire par le Roi » ;
//   · art. 9, dates : fixées par convention collective ; à défaut, par le
//     règlement de travail, qui ne peut pas les fixer « au-delà du septième jour
//     ouvrable qui suit la période de travail » ; à défaut de l'un et l'autre,
//     « au plus tard le quatrième jour ouvrable qui suit la période de travail » ;
//   · art. 11 : à la fin de l'engagement, « sans délai et au plus tard à la
//     première paie qui suit la date de la fin de l'engagement ».
// Né le 18/09/2026 : le règlement de travail imprimait par défaut « Mensuelle,
// dernier jour ouvrable du mois » pour tous — un seul paiement par mois, donc
// contraire à l'art. 9 pour un ouvrier (reglement-engine.js, -builder, -nl).
const PAIEMENT_ART9 = {
  ouvrierPaiementsParMoisMin: 2,     // « au moins deux fois par mois »
  ouvrierIntervalleMaxJours: 16,     // « à seize jours d'intervalle au plus »
  employePaiementsParMoisMin: 1,     // 1° « au moins tous les mois »
  reglementDateMaxJourOuvrable: 7,   // plafond d'une date fixée par le règlement
  defautDateJourOuvrable: 4,         // sans CCT ni règlement
  source: 'Art. 9 et 11 de la loi du 12/04/1965 concernant la protection de la rémunération des travailleurs — texte consolidé lu le 18/09/2026 sur refli.be/fr/lex/1965041207 (mise à jour au 14/04/2026). Reproduction consolidée ; le Moniteur belge fait foi.',
  dateLecture: '2026-09-18',
};

const FLEXI_DATE_EFFET = '2026-09-01';
const FLEXI_SALAIRE_MIN_HORAIRE = 12.11;
const FLEXI_SALAIRE_MAX_HORAIRE = 21.00;
const FLEXI_PECULE_PCT = 0.0767;
const FLEXI_SOURCE = 'SPF Emploi — salairesminimums.be, fiche CP 3020000 (jcId ceaa4cb322cc4933bdb1b933335760ee), section 1.2 « Flexisalaire », en vigueur 01/09/2026, page actualisée le 24/09/2026, relue le 15/10/2026. Min 12,11 €/h (11,87 avant le 01/09/2026), max 21,00 €/h (non indexé : « sauf salaire maximum » ; la lecture du 02/09 donnait 21,42 €/h, corrigée depuis par le SPF). Ce plancher et ce plafond valent pour l\'HORECA seulement : hors horeca, le flexi-salaire est au moins le barème sectoriel de la fonction (ou le RMMMG) et au plus 150 % de ce barème (ONSS, instructions flexi-jobs 2026/3, lues le 23/09/2026).';


// ═══ ÉCO-CHÈQUES — le plafond s'écrit UNE fois, il se lit par deux chemins ═══
// `LB.avantages.ecoMax` (historique, lu par helpers.js → export plat `ECO_MAX`) et
// `LB.ecoCheques.maxAnnuelParTravailleur` (bloc sourcé plus bas) doivent TOUJOURS dire la
// même chose : une valeur écrite deux fois finit par diverger (R14 bis). L'override
// `ECO_MAX` du robot de veille patche les deux (voir OVERRIDE_MAP).
const ECO_CHEQUES_MAX_ANNUEL = 250;

export const LOIS_BELGES = {
  _meta: { version: '2026.6.0', dateMAJ: '2026-04-29', dernierChangement: 'COUVERTURE EXHAUSTIVE: 160/160 CP belges désormais avec barèmes Q2 2026', source: 'ONSS catalogue + sources sectorielles', annee: 2026 },

  // ═══ ONSS ═══
  onss: {
    travailleur: 0.1307,
    // `globale` = cotisation patronale GLOBALE déclarée en DmfA (code 495, employés
    // cat. 1) = 25,00 % (19,88 base + 5,12 modération salariale). Depuis 2024/1 les
    // cotisations spéciales (AT code 255 = 0,02 %, amiante code 256…) sont déclarées
    // SÉPARÉMENT et « ne sont plus intégrées dans le taux de base » (ONSS).
    // Sources (vérifié 2026-07-21, décision fondateur 21/07) :
    //   - ONSS, instructions DmfA 2026/2 « Les cotisations de sécurité sociale » :
    //     https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/socialsecuritycontributions/contributions.html
    //   - VBO-FEB « Cotisations sociales T1 2026 »
    //   - Déclaration Aureus T2/2026 ACCEPTÉE par l'ONSS (ticket 033-04ENHGL-L2-Z) :
    //     cotisation 495 = 38,07 % (13,07 + 25,00) ; exonération 1er engagement
    //     6.660,00 × 25,00 % = 1.665,00.
    // ⚠️ `total` (25,07 %) = agrégat HISTORIQUE pré-2024/1 (base + spéciales AT),
    // conservé UNIQUEMENT pour les estimations de coût employeur (UI) — ne JAMAIS
    // l'utiliser pour la DmfA ni pour l'assiette des réductions. Son `detail` par
    // branches n'existe plus officiellement (gestion globalisée ONSS).
    employeur: { total: 0.2507, globale: 0.2500, detail: { pension: 0.0886, maladie: 0.0370, chomage: 0.0138, accidents: 0.0087, maladiesPro: 0.0102, fermeture: 0.0012, moderation: 0.0560, cotisationsSpec: 0.0352 }},
    plafondAnnuel: null, // pas de plafond en Belgique
    ouvrier108: 1.08, // majoration 8% ouvriers
    // Réduction structurelle — CATÉGORIE 1 (secteur privé, régime général). DEUX composantes :
    //   R = forfait + alpha × (S0 − S) + delta × (S2 − S)   — chaque terme uniquement s'il est POSITIF
    // où S = salaire trimestriel de référence. Pas de composante « hauts salaires » en catégorie 1.
    //
    // ⚠️ Les BORNES sont indexées et changent PAR TRIMESTRE. Ci-dessous : T3 2026 (dès le 01/07/2026).
    //   T1 2026 : alpha 0,1400 · S0 11 458,57 | delta 0,1500 · S2 9 547,20
    //   T2 2026 : delta passe à 0,1600 (accord de gouvernement), bornes inchangées
    //   T3 2026 : bornes indexées +2 % → S0 11 687,74 · S2 9 738,14
    // Source primaire : ONSS, instructions intermédiaires DmfA « Adaptation des plafonds salariaux
    // réductions » du 03/07/2026 (effet 01/07/2026) —
    // https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/intermediates
    // Vérifié 2026-07-10. L'ancien `seuil: 9932.40` ne correspondait à AUCUNE borne 2026, et le
    // modèle mono-seuil ne pouvait pas exprimer les deux composantes.
    reductionStructurelle: { forfait: 0, alpha: 0.1400, S0: 11687.74, delta: 0.1600, S2: 9738.14, pctAuDessus: 0, trimestre: '2026-T3' },
    // Déduction structurelle des charges patronales (AR 16/05/2003)
    deductionStructurelle: {
      forfaitTrimestriel: 361.64, // montant forfaitaire par trimestre (2026)
      baseDeduction: 1085.04,
    },
    // Réduction groupe-cible « premiers engagements » (AR 16/05/2003 art. 353bis).
    // Réforme reportée au 01/07/2026 (annonce initiale 01/04/2026, report confirmé ONSS
    // 13/04/2026) : 1er trav. plafond 3.100 €/trim JUSQU'AU 30/06/2026, puis 2.000 €/trim
    // dès le 01/07/2026 (illimité dans le temps) ; 2e-3e alignés sur 1.000 €/trim × 12 trim.
    // ⚠️ La logique DATÉE (capAvant 3100 / capApres 2000, dateReforme 2026-07-01) fait foi
    // dans reductions-onss.js. Ces valeurs = montants dès T3 2026 (affichage + plafond).
    // Source : Instructions ONSS 2026/2 « Premiers engagements » (vérifié 2026-07-01).
    premierEngagement: {
      travailleur1: { forfaitTrimestriel: 2000.00, baseTrimestriel: 2000.00, forfaitAvant0107_2026: 3100.00 }, // cap base patronale, illimité
      travailleur2: { forfaitTrimestriel: 1000.00 }, // 1.000 €/trim × 12 trim (réforme 01/07/2026)
      travailleur3: { forfaitTrimestriel: 1000.00 },
      // ⚠️ AJOUTÉS le 07/08/2026 — ils MANQUAIENT, et l'oubli coûtait de l'argent
      // aux clients : la réforme du 01/07/2026 ROUVRE le droit aux 4e et 5e
      // travailleurs, supprimés depuis le 01/01/2024. S'arrêter au 3e revenait à
      // ne pas réclamer 1.000 €/trimestre par travailleur pour deux rangs.
      //
      // ✅ Texte primaire lu le 07/08/2026 — ONSS, instruction intermédiaire
      // « Loi-programme du 30 mai 2026 » (02/07/2026) : un nouveau forfait
      // « G21 » de « 2.000,00 EUR » pour le 1er travailleur, « illimité dans le
      // temps » ; pour les « 2ème, 3ème, 4ème et 5ème travailleur » les
      // réductions « peuvent être appliquées pendant 12 trimestres dans une
      // période de 20 trimestres ».
      //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/intermediates
      //
      // ⚠️ PIÈGE DE SOURCE (levé le 01/09/2026) : jusqu'à l'été 2026, la page
      // d'instructions PRINCIPALE (« firstengagments ») affichait encore le
      // régime d'AVANT — forfait 3.100 € et suppression des 4e, 5e et 6e — et
      // seule l'instruction INTERMÉDIAIRE portait la réforme. Ce n'est PLUS le
      // cas : relue le 01/09/2026, la page principale porte le nouveau régime
      // (G21 2.000 €, 4e et 5e rétablis, 12 trimestres dans 20). Les deux
      // sources concordent enfin — mais garder en tête que l'ONSS peut à
      // nouveau publier une réforme dans l'intermédiaire avant la principale.
      travailleur4: { forfaitTrimestriel: 1000.00 },
      travailleur5: { forfaitTrimestriel: 1000.00 },
      // Le 6e n'existe pas : la réduction s'arrête au 5e travailleur.
      dernierRang: 5,
      dureeTrimestres: 12,
      fenetreTrimestres: 20,
      // 🔒 Codes de déduction DmfA par rang (bloc 90109 « déduction occupation »).
      // Relevés le 01/09/2026 sur la table « Informations complémentaires DmfA »
      // de la page ONSS « firstengagments » (colonne « Code réduction en DMFA »),
      // recoupés avec la table de transition « A partir du 2026/3 ».
      //
      // ✅ CONFIRMÉS PAR LA SECONDE SOURCE (01/09/2026) — glossaire ONSS,
      // annexe structurée n° 4 (liste officielle des codes de déduction),
      // version 2026/3, fichier AN2026-3-FR4.xml :
      //     3315 → 9999/4 · 3324 → 9999/4 · 3333 → 9999/4
      //     3342 → 9999/4 · 3352 → 9999/4      (rangs 4 et 5 ROUVERTS)
      //     3360 / 3361 → 2028/3               (6e rang : toujours fermé)
      //     3343 / 3353 → 2028/3 · 3325 / 3326 / 3334 → 2031/1
      //       (2e/3e tranches de l'ANCIEN régime dégressif, en extinction)
      // ⚠️ Ce point contredit ce que le vault affirmait depuis le 17/08/2026
      // (« aucun code publié pour les rangs 4-5 rouverts : les reprendre serait
      // déclarer faux »). C'était vrai sur l'annexe 2026/2, où 3342 et 3352
      // finissaient à 2028/3 — le compte à rebours de l'ancien régime. La
      // version 2026/3 les a rouverts à 9999/4. La note du vault est corrigée.
      // 🪤 La sentinelle hebdo `onss-annexe4` n'a PAS vu ce changement : elle
      // télécharge le `.txt` de l'annexe, or la version 2026/3 ne publie plus
      // que pdf/docx/xlsx/xml. Chantier séparé — voir la note de session.
      // ⚠️ Le rang 1 est aussi porté par RED_CONST.premierEngagement.codeDmfa
      // dans reductions-onss.js (moteur certifié, déclaration T2/2026 acceptée) :
      // le test `premiers-engagements-bareme.test.js` verrouille l'égalité des
      // deux, pour qu'une correction ici ne puisse pas diverger du XML déclaré.
      // Il n'existe PAS de code pour un 6e rang : le droit ne peut plus s'ouvrir.
      codesDmfaParRang: { 1: '3315', 2: '3324', 3: '3333', 4: '3342', 5: '3352' },
      // Intervention trimestrielle dans les frais de secrétariat social, accordée
      // pendant les trimestres où la réduction 1er travailleur est demandée.
      // « cette intervention n'est pas proratisée » (ONSS, ibid.) — code DmfA 2001.
      interventionSecretariatSocial: { montantTrimestriel: 36.45, codeDmfa: '2001', proratisee: false },
    },
    // Cotisations patronales détaillées CP 200 employés
    cp200: {
      cotPatronaleBase: 0.1988,    // 19,88%
      moderationSalariale: 0.0512, // 5,12%
      finChomageTemp: 0.0010,      // 0,10%
      accidentsTravail: 0.0002,    // 0,02% (variable par employeur)
      securiteExistence: 0.0023,   // 0,23%
      ffe: 0.0018,                 // 0,18% ⚠️ PÉRIMÉ 2026 (réel : 0,32%/0,34% — voir cotisationsDmfa code 809) — consommé par le moteur certifié, correction = chantier séparé (00.02 « écart moteur »)
      cotSpecialeFFE: 0.0017,      // 0,17% ⚠️ PÉRIMÉ 2026 (réel : 0,09%/0,10% — voir cotisationsDmfa code 810) — idem
      totalAvantDeductions: 0.2570, // 25,70% total ⚠️ faux en 2026 (réel <20 trav. : 25,80% — somme cotisationsDmfa)
    },
    // ═══ Cotisations DmfA PAR CODE (blocs WorkerContribution 90001) — employé type 0 ═══
    // 🔒 SOURCE UNIQUE (R1) des cotisations spéciales DmfA. Ne JAMAIS recréer un second
    // bloc de ces mêmes taux ailleurs : un doublon `cotisationsSpecialesDmfa` a existé du
    // 21/07/2026 (#1116) au 21/07/2026 — mêmes valeurs, mais DORMANT (la route DmfA ne lit
    // que `codes` ci-dessous), donc une future indexation risquait d'être appliquée au
    // mauvais exemplaire → déclaration ONSS fausse en silence. Supprimé, fusionné ici.
    // Référence en or : déclaration Aureus T2/2026 ACCEPTÉE par l'ONSS (ticket
    // 033-04ENHGL-L2-Z) = 8 codes : 495 + 856 (CSSS, barème csss ci-dessous) + les
    // 6 codes listés ici. « Modération salariale incluse » ne vaut que pour 809 et 810 ;
    // 255, 256 et 859 sont des taux PLATS (l'ONSS précise « sans incidence sur la
    // modération salariale ») — ne pas leur appliquer de décomposition base+modération.
    // Base = rémunération trimestrielle soumise ONSS (100 % employés, 108 % ouvriers), non plafonnée.
    // Gate R2 : chaque ligne vérifiée EN LIGNE le 21/07/2026 (sources par ligne) +
    // recoupement au centime sur base 6 660,00 : 255=1,33 · 256=0,67 · 809=22,64 ·
    // 810=6,66 · 831=15,32 · 859=6,66 → 53,28 = l'écart exact du diff golden du 16/07 ;
    // total Aureus 2 620,72 et GeeksDen 730,99 reproduits pile.
    // ⚠️ 0,80 % n'est PAS une constante : au T4 le fonds amiante (256) tombe → 0,79 %.
    // Ne jamais coder le total en dur, toujours sommer `codes` après filtrage.
    // NB « groupes à risque 0,10 % » (code 852, AR 28/09/2025, période 2025-2026) : dû
    // UNIQUEMENT si pas de CCT sectorielle → la CP 200 en a une (fonds social/Cefora)
    // → JAMAIS émis pour CP 200 (cohérent avec la déclaration acceptée).
    cotisationsDmfa: {
      // Le patronal de BASE du code 495 (25,00 % = 19,88 + 5,12, tax-shift 2018) vit
      // dans onss.employeur.globale (PR #1111) — une seule source (R1), pas de doublon ici.
      // Sources croisées (21/07/2026) : VBO-FEB « cotisations T1 2026 » (taux facial 25 %) ;
      // Partena « tax shift — taux facial » (19,88 + 5,12) ; ONSS instructions 2026/2
      // socialsecuritycontributions/contributions.html. Confirmé au centime par la
      // déclaration acceptée : 495 = 6 660,00 × 38,07 % (13,07 + 25,00) = 2 535,46.
      codes: [
        { code: '255', taux: 0.0002, libelle: 'Cotisation spéciale accidents du travail',
          source: 'ONSS instr. 2026/2 specialcontribution_workaccidents.html — vérifié 21/07/2026' },
        // ⚠️ [1,2,3] est une EXCEPTION 2026 (régime normal = 2 trimestres/an) → re-vérifier
        // obligatoirement pour 2027, sinon la liste se fossilise. Étudiants en cotisation
        // de solidarité : l'amiante est déjà intégrée aux codes 840/841, ne pas cumuler.
        { code: '256', taux: 0.0001, trimestresDus2026: [1, 2, 3],
          libelle: 'Fonds amiante (dû T1-T3 2026, PAS T4)',
          source: 'ONSS instr. 2026/2 asbestosfunds.html + Agoria Q1 2026 — vérifié 21/07/2026' },
        // 🔴 GAP CONNU — finalité COMMERCIALE uniquement. Un employeur NON commercial
        // (ASBL, fondation, mutualité) relève du code 811 à 0,01 %, pas du 809. La CP 200
        // couvre beaucoup d'ASBL et la route DmfA n'a AUCUN aiguillage sur la finalité :
        // émettre 809 pour une ASBL produirait une déclaration fausse. À traiter avant
        // d'onboarder une ASBL (chantier séparé — ne pas ajouter 811 ici sans l'aiguillage,
        // ce serait recréer une constante dormante).
        // Effectif retenu = moyenne T4 (année-2) → T3 (année-1), règle du fichier ONSS.
        { code: '809', taux: 0.0034, tauxDes20Travailleurs: 0.0039,
          libelle: 'FFE cotisation de base, employeurs commerciaux (<20 trav. 0,34 % ; ≥20 0,39 % — modération incluse)',
          source: '✅ PRIMAIRE : FFE_2026.xls OFFICIEL ONSS (MAJ 09/01/2026) — « 0,32% (0,34%) » <20 trav. · « 0,37% (0,39%) » ≥20 · sans finalité commerciale 0,01 % ; croisé ONSS instr. 2026/2 basiccontributions_closingcompanyfunds.html + Agoria + Sodalis 23/03/2026 + VBO-FEB — vérifié 21/07/2026' },
        { code: '810', taux: 0.0010,
          libelle: 'FFE cotisation spéciale (financement chômage temporaire — modération incluse)',
          source: 'ONSS instr. 2026/2 specialcontribution_closingcompanyfunds.html — vérifié 21/07/2026' },
        // Taux 0,23 % maintenu du 01/01/2026 au 31/12/2027 (CCT sectorielle CP 200).
        // `cpUniquement` est un tableau de NOMBRES : la route filtre par .includes(cpNum).
        { code: '831', taux: 0.0023, cpUniquement: [200],
          libelle: 'Fonds social CP 200 (CPAE/Cefora) — accord sectoriel 2026-2027 ; les AUTRES CP ont leur propre fonds/code (à sourcer par CP avant émission)',
          source: 'ONSS instr. 2026/2 subsistenceprotectionfunds.html + SSN accord sectoriel CP 200 + sfonds200.be + Partena — vérifié 21/07/2026' },
        // ⚠️ NE PAS CONFONDRE AVEC LE 810. Une recherche « chômage temporaire 2026 » renvoie
        // « 0,09 % + 0,01 modération » : c'est le code 810 (FFE spéciale), PAS celui-ci.
        // Le 859 est une cotisation distincte, à 0,10 % PLAT depuis le 01/01/1997, que l'ONSS
        // déclare « sans incidence sur la modération salariale ». Ne jamais le « corriger »
        // à 0,09 % — employeurs privés, aucun seuil de taille.
        { code: '859', taux: 0.0010,
          libelle: 'Cotisation chômage temporaire et chômeurs âgés (aucun seuil de taille)',
          source: 'ONSS instr. 2026/2 temporary_oldunemployed.html — inchangé depuis 01/01/1997, vérifié 21/07/2026' },
      ],
    },
    groupeCible: { jeunesNonQualifies: { age: 25, reduc: 1500 }, agees: { age: 55, reduc: 1500 }, handicapes: { reduc: 1500 }},
  },

  // ═══ PRÉCOMPTE PROFESSIONNEL — Barèmes 2026 (Annexe III AR/CIR 92) ═══
  // Source : SPF Finances — Moniteur belge 2026
  pp: {
    tranches: [
      { min: 0,      max: 16710,    taux: 0.2675, fixe: 0 },          // 26,75%
      { min: 16710,  max: 29500,    taux: 0.4280, fixe: 4469.93 },    // 42,80%
      { min: 29500,  max: 51050,    taux: 0.4815, fixe: 9944.05 },    // 48,15%
      { min: 51050,  max: Infinity, taux: 0.5350, fixe: 20320.38 },   // 53,50%
    ],
    fraisPro: { salarie: { pct: 0.30, max: 6070, maxPP: 6070 }, dirigeant: { pct: 0.03, max: 3200 }}, // Frais pro forfaitaires 30%, plafond 6070€ — SPF Finances, revenus 2026 (ex. 2027). CORR 2026-06-26 : maxPP 7269→6070 (le 7269 gonflait le forfait → précompte ~5% trop bas vs fiches Securex réelles).
    // Réduction « cotisations sociales » forfaitaire à déduire de la rémunération brute MENSUELLE
    // d'un dirigeant d'entreprise soumis au statut social des indépendants, AVANT frais forfaitaires,
    // pour le calcul du précompte professionnel (la société ne retient pas l'INASTI ; le SPF en tient
    // compte forfaitairement dans la base imposable). Réduction = base + taux×(brut − seuil).
    // Source : SPF Finances — Formule-clé à partir du 01/01/2026 (ESS-SR/2025-0928, n°7, 1°),
    // vérifié 2026-07-20 (fisconet, Formule-clé 2026, lastModified 2025-12-17).
    reductionCotisSocialesDirigeant: { paliers: [
      { max: 1465,     base: 385,     seuil: 0,    taux: 0     },
      { max: 6310,     base: 385,     seuil: 1465, taux: 0.215 },
      { max: 9290,     base: 1426.68, seuil: 6310, taux: 0.145 },
      { max: Infinity, base: 1858.78, seuil: 9290, taux: 0     },
    ] },
    // ── Précompte sur rémunérations EXCEPTIONNELLES (pécule double, 13e mois, primes, gratifications) ──
    // Source : SPF Finances — Annexe III AR/CIR 92, barème 2026 (Securex, en vigueur 01/01/2026).
    // La TRANCHE se choisit sur la rémunération annuelle BRUTE normale (brut mensuel × 12) ;
    // le TAUX s'applique sur l'allocation diminuée de l'ONSS personnel (montant imposable).
    remunerationsExceptionnelles: {
      pecule: [ // pécule de vacances (double)
        { max: 10675,    taux: 0 },
        { max: 13660,    taux: 0.1917 },
        { max: 17375,    taux: 0.2120 },
        { max: 20840,    taux: 0.2625 },
        { max: 23580,    taux: 0.3130 },
        { max: 26340,    taux: 0.3433 },
        { max: 31830,    taux: 0.3634 },
        { max: 34640,    taux: 0.3937 },
        { max: 45860,    taux: 0.4239 },
        { max: 59900,    taux: 0.4744 },
        { max: Infinity, taux: 0.5350 },
      ],
      allocations: [ // gratifications, 13e mois, prime de fin d'année
        { max: 10675,    taux: 0 },
        { max: 13660,    taux: 0.2322 },
        { max: 17375,    taux: 0.2523 },
        { max: 20840,    taux: 0.3028 },
        { max: 23580,    taux: 0.3533 },
        { max: 26340,    taux: 0.3836 },
        { max: 31830,    taux: 0.4038 },
        { max: 34640,    taux: 0.4341 },
        { max: 45860,    taux: 0.4644 },
        { max: 59900,    taux: 0.5148 },
        // 🔴 CORRIGÉ LE 10/09/2026 : cette ligne portait 0.5753. La DERNIÈRE
        // TRANCHE EST UNE CELLULE FUSIONNÉE — un taux unique de 53,50 % couvre
        // les DEUX colonnes. Mesuré par COORDONNÉES sur le PDF de l'Annexe III
        // (page 18) : les autres lignes posent leurs deux taux à x=369 et
        // x=472, la dernière pose « 53,50 » seul à **x=435**, entre les deux.
        // Une lecture en flux de texte ne voit qu'un chiffre et croit qu'il en
        // manque un ; 0.5753 = 0.5350 + 0,0403, soit l'écart moyen des lignes
        // précédentes : la valeur avait été EXTRAPOLÉE, pas lue. C'est
        // exactement ce que R2 interdit — compléter en inventant.
        // 📏 21 des 22 valeurs des deux tables étaient justes, celle-ci non.
        // Conséquence : 4,03 points de précompte en trop sur toute prime versée
        // à un travailleur dont le brut annuel normal dépasse 59 900 € — 2 des
        // 23 travailleurs en base au 10/09/2026.
        { max: Infinity, taux: 0.5350 },
      ],
      // ═══ ARRIÉRÉS ET INDEMNITÉS DE DÉDIT — UNE AUTRE TABLE, PAS CELLE-CI ═══
      // ✅ TEXTE PRIMAIRE LU le 10/09/2026 : Annexe III de l'AR/CIR 92 (annexe à
      // l'AR du 11/12/2025), n° 58 — et le n° 53 ci-dessus le dit lui-même, dès
      // sa première ligne : « les indemnités et allocations exceptionnelles
      // AUTRES QUE LES INDEMNITÉS DE DÉDIT ». Le n° 62 renvoie les indemnités de
      // dédit aux règles des n° 58 à 60.
      // ⚠️ LA TRANCHE SE CHOISIT SUR LA « RÉMUNÉRATION DE RÉFÉRENCE », PAS SUR
      // LE BRUT ANNUEL COURANT : c'est celle qui a servi de base à la fixation
      // de l'indemnité, ou à défaut la rémunération de la dernière période
      // d'activité normale (n° 62). Le n° 63 permet au bénéficiaire de faire
      // valoir la rémunération de sa dernière année d'activité normale.
      // 🟠 Le n° 60 prévoit en plus une EXONÉRATION pour enfants à charge que
      // cette table ne porte pas — dette nommée : un travailleur avec enfants à
      // charge et une rémunération de référence sous le plafond du n° 54 se
      // verrait retenir trop. Aucun cas en base au 10/09/2026 (aucune indemnité
      // de dédit calculée), mais à traiter avant le premier licenciement.
      arrieres: [
        { max: 11860,    taux: 0 },
        { max: 14235,    taux: 0.0268 },
        { max: 15810,    taux: 0.0657 },
        { max: 18980,    taux: 0.1077 },
        { max: 20565,    taux: 0.1355 },
        { max: 22935,    taux: 0.1655 },
        { max: 26885,    taux: 0.1917 },
        { max: 34790,    taux: 0.2492 },
        { max: 42695,    taux: 0.2993 },
        { max: 55355,    taux: 0.3130 },
        { max: 62465,    taux: 0.3690 },
        { max: 71160,    taux: 0.3896 },
        { max: 83020,    taux: 0.4093 },
        { max: 99630,    taux: 0.4292 },
        { max: 124925,   taux: 0.4499 },
        { max: 143905,   taux: 0.4647 },
        { max: 169205,   taux: 0.4748 },
        { max: Infinity, taux: 0.4800 },
      ],
      source: 'Annexe III de l\'AR/CIR 92 (annexe à l\'AR du 11/12/2025), n° 53 (pécules / autres allocations) et n° 58 (arriérés et indemnités de dédit) — TEXTE PRIMAIRE lu le 10/09/2026, PDF publié par Securex, table relevée par coordonnées page 18.',
    },
    // Tranche non imposée 2026 (source: Annexe III AR/CIR 92 — formule-clé 2026)
    trancheNonImposable: { bareme1: 2987.98, bareme2: 5975.96 },
    quotiteExemptee: { bareme1: 11170, bareme2: 22340 }, // Quotité exemptée 2026 = 11.170€ (Annexe III n°32, pour info — non opérant, le calcul utilise trancheNonImposable)
    quotientConjugal: { pct: 0.30, max: 13790 }, // Annexe III n°34.1.1 : revenu imputé plafonné à 13.790€ (plafond atteint à 45.966,67€ net)
    // Réductions famille 2026 — Source : Annexe III AR/CIR 92 du 11/12/2025 (audit 03/06/2026)
    reductionsEnfants: [0, 624, 1656, 4404, 7620, 11100, 14592, 18120, 21996], // n°38 ✓
    reductionEnfantSupp: 3864, // n°38 (au-delà du 8e) ✓
    reductionParentIsole: 624, // n°40.1 ✓
    reductionHandicape: 624, // n°39.1 (bénéficiaire handicapé)
    reductionConjointHandicape: 624, // n°41
    reductionConjointRevenuLimite: 1740, // n°40.2 (conjoint revenus ≤290€ nets/mois)
    reductionConjointPensionLimitee: 3474, // n°40.3 (conjoint pensions ≤579€ nets/mois)
    // E3 slice 4 (30.36 §3.2 rang 2 #17, 04/09/2026) — les PLAFONDS des deux
    // réductions ci-dessus, jusqu'ici écrits en commentaire seulement : aucun
    // code ne pouvait donc les comparer à un montant, et les deux réductions
    // n'étaient jamais passées au moteur.
    // ✅ TEXTE PRIMAIRE LU le 04/09/2026 (E3 15e tranche) : Annexe III à l'AR
    // du 11/12/2025 (AR/CIR 92, revenus payés à partir du 01/01/2026), n°11.1
    // b) et c), n°40 2. et 3. — copie intégrale de l'annexe hébergée par Securex
    // (https://www.securex.be/getattachment/eeaf8a4e-0c87-47e9-8af2-1cf41db24148/Annexe-III-AR-CIR-2026.pdf),
    // le Moniteur (ejustice) rendant 500 en session. Corroboré Securex Lex4You
    // (MAJ 20/02/2026) et Partena (guide revenus 2025 : même mécanisme).
    // Revenus PROFESSIONNELS propres du conjoint, nets par mois.
    plafondRevenuConjointNetMois: 290,   // n°40.2 (n°11.1 b)
    // PENSIONS, rentes ou revenus y assimilés du conjoint, nets par mois.
    plafondPensionConjointNetMois: 579,  // n°40.3 (n°11.1 c)
    // n°33 (même source, même lecture) : conjoint dont les revenus propres sont
    // EXCLUSIVEMENT des pensions/rentes ≤ ce plafond → l'impôt se calcule
    // « comme si ce conjoint ne disposait pas de revenus professionnels propres »
    // (section 2 = barème II, quotient conjugal), et la réduction n°40.3 ne
    // s'applique PAS (le n°40 est réservé à la section 1re). ⚠️ Le moteur
    // (payroll-engine.js, zone protégée) n'applique pas encore cette bascule —
    // precompte-conjoint.js la signale, la décision est au fondateur.
    plafondPensionConjointBaremeII: 174, // n°33 (n°11.1 a)
    // n°11.2 — comment se mesure le « net » comparé aux trois plafonds ci-dessus :
    // revenus bruts − retenues sociales obligatoires, puis − 20 %. Sans cette
    // règle, un « net » conventionnel serait comparé à un plafond défini autrement.
    conjointNetAnnexeIII: { abattementPct: 0.20 },
    reductionPersonne65: 1992, // n°39.2 (dépendance, 66 ans+) ✓
    reductionAutreCharge: 624, // n°39.3 (autres personnes à charge, art. 136 2°-4°)
    // ⚠️ LEGACY (modèle mono-tranche, conservé pour exports/setters/validation hérités) — NON utilisé par le moteur depuis Sprint 39
    // ⚠️ SUPPRIMÉ LE 20/08/2026 — `bonusEmploi` (pctReduction 0.3314,
    // maxMensuel 194.03, seuils 2561.42/2997.59) était la CLÉ MORTE du modèle
    // mono-tranche abandonné : l'écran « Lois belges » affichait ses fantômes
    // comme droit en vigueur, et audit-doc la gardait en fallback. Le social
    // vit dans werkbonusSocial (volets A+B, barème daté par palier), le
    // fiscal dans bonusEmploiFiscal (33,14 % / 52,54 %, max 765 €/an).
    werkbonus: { maxReduction: 293.66, seuilBrut1: 2255.50, seuilBrut2: 3336.98, _deprecated: 'LEGACY — utiliser werkbonusSocial (volets A+B) via calcWerkbonus(). Valeurs approx. 01/04/2026 pour compat affichage uniquement.' },

    // ═══ BONUS À L'EMPLOI — modèle officiel 2 volets (SOURCE DE VÉRITÉ moteur) ═══
    // Bonus SOCIAL = réduction cotisation ONSS perso (13,07%). Total = volet A + volet B,
    // plafonné au montant ONSS dû (écrêtement sur volet B puis volet A).
    // Barèmes ONSS applicables à partir du 01/04/2026 (post-indexation RMMMG +35,70€, CCT 43/18).
    // Source : Instructions administratives ONSS 2026 / Securex Lex4You 08/04/2026.
    // ═══ BARÈME DATÉ PAR PALIER (les montants sont indexés à l'indice pivot) ═══
    //   Le moteur applique le palier de la PÉRIODE de la fiche (werkbonusPalier(date)).
    //   Un barème unique ne peut pas coller à la fois à une fiche de mars et de juillet.
    //   Formule par volet : ≤s1 → max ; s1→s2 → max − pente×(S−s1) ; >s2 → 0. pente = max/(s2−s1).
    //   Sources (R2) :
    //     • ONSS, Instructions administratives DmfA — « Le bonus à l'emploi » (workbonus),
    //       socialsecurity.be — palier 01/04/2026 (vérifié 2026-07-27).
    //     • ONSS, instruction intermédiaire du 03/07/2026 : « les plafonds salariaux et les
    //       montants maximaux de réduction du bonus à l'emploi sont adaptés à partir du
    //       01/07/2026 » (indexation +2 %). Corroboré Securex Lex4You (vérifié 2026-07-27).
    //     • 🟠 PALIER 01/09/2026 — VÉRIFIÉ LE 07/08/2026, ET LA RÉPONSE EST NUANCÉE.
    //       La règle générale (Securex, Acerta) dit que les paramètres du bonus
    //       s'indexent « à la fois au premier mois (juillet) ET au troisième mois
    //       (septembre) suivant le dépassement de l'indice de référence ». On
    //       s'attendait donc à un troisième palier. Mais DEUX sources lues le
    //       07/08 — l'instruction intermédiaire de l'ONSS et la page « montants
    //       actuels » de Securex — présentent pour le 01/09/2026 des montants R
    //       IDENTIQUES à ceux du 01/07 (127,54 / 171,99 / 137,74 / 185,75).
    //       Lecture la plus probable : ce qui bouge au 3e mois est le PLAFOND
    //       SALARIAL SUPÉRIEUR (s2), dont le mode de calcul suit le calendrier
    //       des prestations sociales et non celui des montants R.
    //       ⇒ Aucun palier 01/09 n'est ajouté : les R sont inchangés, donc le
    //         palier du 01/07 continue de s'appliquer et c'est correct. Le doute
    //         porte UNIQUEMENT sur s2, et il n'est pas tranché.
    //       ⇒ Un test-sentinelle (`__tests__/bonus-emploi-audit.test.js`) tombe
    //         dès qu'on dépasse le 01/10/2026 sans que la question soit reprise :
    //         une incertitude qui n'a pas d'échéance finit par être oubliée.
    // ⚠️ RÉÉCRIT LE 26/08/2026 — TEXTE PRIMAIRE ONSS LU, ET IL CORRIGE DEUX CHOSES.
    //
    // Jusqu'ici la table ne portait que DEUX paliers (01/04 et 01/07/2026), et
    // celui de juillet était FAUX sur son plafond supérieur et sa pente : il
    // portait s2 = 3.403,62 et pente 0,2739 — qui sont les valeurs du
    // 1er SEPTEMBRE, pas celles de juillet. Conséquence mesurée : pour un
    // salaire de référence entre 2.937,93 € et 3.403,62 €, juillet et août 2026
    // ont servi un volet A trop élevé (jusqu'à ~18 € par mois et par
    // travailleur), donc une retenue ONSS trop basse et un net trop haut.
    //
    // La note du 07/08/2026 disait : « ce qui bouge au 3e mois est le PLAFOND
    // SALARIAL SUPÉRIEUR (s2) […] le doute porte UNIQUEMENT sur s2, et il n'est
    // pas tranché. » Le mécanisme était juste ; les valeurs avaient été rangées
    // dans le mauvais palier. C'est tranché aujourd'hui, deux fois plutôt qu'une :
    //   · l'ONSS l'écrit pour mars 2026 — « En raison d'une différence dans le
    //     mode de calcul de l'adaptation des plafonds salariaux, le plafond
    //     salarial supérieur ET un coefficient d'écart seront à nouveau adaptés
    //     à partir du 1er mars 2026 » — les montants R, eux, ne bougent pas ;
    //   · l'instruction intermédiaire du 03/07/2026 le refait pour septembre :
    //     « À partir du 1er septembre 2026 certains montants seront ajustés de
    //     nouveau » — R identiques, s2 et pente adaptés.
    //
    // 📚 SOURCES — texte primaire ONSS lu le 26/08/2026 (cran ✅) :
    //   · Instructions administratives DmfA 2026/2, « Le bonus à l'emploi » —
    //     https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/
    //       instructions/deductions/workers_reductions/workbonus.html
    //     → barème du 01/04/2026, plafond annuel 3.523,92 €, ordre d'écrêtement.
    //   · Instructions administratives DmfA 2026/1 (archive trimestrielle) —
    //     même chemin avec `/fr/2026-1/` → « Tranches et montants d'application
    //     pour janvier et février 2026 » ET « à partir du 1er mars 2026 »,
    //     plafond annuel 3.466,44 € à partir du 01/01/2026.
    //   · Instruction intermédiaire du 03/07/2026, « Bonus à l'emploi — plafonds
    //     après indexation » — /employer/instructions/dmfa/fr/latest/intermediates
    //     ancre `#bonus-a-lemploi-plafonds-apres-indexation-03-07-2026`
    //     → barèmes du 01/07/2026 ET du 01/09/2026.
    //
    // 🔎 GOTCHA D'ACCÈS, à ne pas refaire chercher : l'archive trimestrielle
    //    s'adresse en `<année>-<n° de trimestre>` (`2026-1`). Les formes
    //    `2026-01`, `2026/01`, `20261` rendent 301/302 vers une page d'erreur.
    //    Et le lien vers l'instruction intermédiaire posé dans la page workbonus
    //    pointe une ancre SANS la date, alors que l'ancre réelle porte le suffixe
    //    `-03-07-2026` : chercher l'ancre du lien ne trouve rien, et ce « rien »
    //    n'est pas une absence (R2 ter).
    //
    // ⚠️ Un trimestre ÉCOULÉ s'archive, le trimestre EN COURS ne l'est pas :
    //    `/fr/2026-2/` et `/fr/2026-3/` rendent 404 tant que `latest` les sert.
    //
    // Formule par volet : ≤s1 → max ; s1→s2 → max − pente×(S−s1) ; >s2 → 0.
    // La pente n'est pas libre : elle vaut toujours max/(s2−s1), et les montants
    // ouvrier sont les montants employé × 108 % (déclaration à 108 %).
    werkbonusSocialParPalier: [
      { from: '2026-01-01', // « pour janvier et février 2026 » (RMMMG CCT 43-17)
        employe: { A: { max: 123.00, s1: 2833.36, s2: 3271.48, pente: 0.2807 },
                   B: { max: 165.87, s1: 2218.73, s2: 2833.36, pente: 0.2699 } },
        ouvrier: { A: { max: 132.84, s1: 2833.36, s2: 3271.48, pente: 0.3032 },
                   B: { max: 179.14, s1: 2218.73, s2: 2833.36, pente: 0.2915 } },
        // « Le montant total de la réduction par travailleur ne peut être
        // supérieur à 3.466,44 EUR par année calendrier à partir du 1er janvier
        // 2026. » Recoupement : 12 × (123,00 + 165,87) = 3.466,44 € au centime.
        plafondAnnuelEmploye: 3466.44 },
      { from: '2026-03-01', // R inchangés ; SEUL s2 et le coefficient d'écart bougent
        employe: { A: { max: 123.00, s1: 2833.36, s2: 3336.98, pente: 0.2442 },
                   B: { max: 165.87, s1: 2218.73, s2: 2833.36, pente: 0.2699 } },
        ouvrier: { A: { max: 132.84, s1: 2833.36, s2: 3336.98, pente: 0.2638 },
                   B: { max: 179.14, s1: 2218.73, s2: 2833.36, pente: 0.2915 } },
        // Même année calendrier que janvier : le plafond annuel ne rechange pas.
        plafondAnnuelEmploye: 3466.44 },
      { from: '2026-04-01', // indexation RMMMG +35,70 € (CCT 43-18)
        employe: { A: { max: 125.04, s1: 2880.32, s2: 3336.98, pente: 0.2738 },
                   B: { max: 168.62, s1: 2255.50, s2: 2880.32, pente: 0.2699 } },
        ouvrier: { A: { max: 135.04, s1: 2880.32, s2: 3336.98, pente: 0.2957 },
                   B: { max: 182.11, s1: 2255.50, s2: 2880.32, pente: 0.2915 } },
        // ✅ RECOUPEMENT INDÉPENDANT : « 3.523,92 EUR à partir du 1er avril
        // 2026 » = 12 × (125,04 + 168,62), au centime. Un chiffre publié par
        // l'ONSS qui valide nos quatre maxima sans les relire.
        plafondAnnuelEmploye: 3523.92 },
      { from: '2026-07-01', // indexation +2 % des R et de s1 — s2 NE bouge PAS
        employe: { A: { max: 127.54, s1: 2937.93, s2: 3336.98, pente: 0.3196 },
                   B: { max: 171.99, s1: 2300.62, s2: 2937.93, pente: 0.2699 } },
        ouvrier: { A: { max: 137.74, s1: 2937.93, s2: 3336.98, pente: 0.3452 },
                   B: { max: 185.75, s1: 2300.62, s2: 2937.93, pente: 0.2915 } },
        // ✅ PUBLIÉ ENTRE-TEMPS — RELU À LA SOURCE LE 02/09/2026, ET LA RETENUE
        // ÉTAIT LA BONNE. Ce champ valait `null` avec cette note : « 12 × (127,54
        // + 171,99) = 3.594,36 € serait juste ET serait un chiffre fabriqué par
        // nous ; R2 interdit d'inventer une valeur légale, même juste. » L'ONSS
        // écrit désormais, mot pour mot : « Le montant total de la réduction par
        // travailleur ne peut être supérieur à 3.594,36 EUR par année calendrier
        // à partir du 1er juillet 2026. » Le calcul refusé était exact au
        // centime — mais il n'était pas une SOURCE, et c'est ce qui comptait.
        // Source : instructions administratives DmfA, page « Bonus à l'emploi »
        // (socialsecurity.be/employer/instructions/dmfa/fr/latest — deductions/
        // workers_reductions/workbonus.html), lue le 02/09/2026.
        plafondAnnuelEmploye: 3594.36 },
      { from: '2026-09-01', // R et s1 INCHANGÉS ; s2 et la pente sont réadaptés
        employe: { A: { max: 127.54, s1: 2937.93, s2: 3403.62, pente: 0.2739 },
                   B: { max: 171.99, s1: 2300.62, s2: 2937.93, pente: 0.2699 } },
        ouvrier: { A: { max: 137.74, s1: 2937.93, s2: 3403.62, pente: 0.2958 },
                   B: { max: 185.75, s1: 2300.62, s2: 2937.93, pente: 0.2915 } },
        // Même plafond que le palier de juillet : l'ONSS l'annonce « par année
        // calendrier à partir du 1er juillet 2026 », donc il couvre septembre —
        // ce n'est pas un report de notre part, c'est la portée qu'il se donne.
        // Les valeurs du palier lui-même (s2 3.403,62 · pentes 0,2739 et 0,2958)
        // ont été RECONFIRMÉES sur la même page le 02/09/2026, au centime.
        plafondAnnuelEmploye: 3594.36 },
    ],
    // Alias de RÉFÉRENCE (= palier BASELINE 01/04/2026, celui contre lequel les fiches
    // Liantis et la suite de tests sont validées). Lu par les garde-fous, les copies de
    // constantes et les simulateurs SANS date. La PRODUCTION (générateur de fiche) passe
    // TOUJOURS la période → le moteur applique le bon palier (ex. +2 % au 01/07). Garder
    // cet alias sur la baseline évite de casser 30+ fixtures certifiées ; l'indexation du
    // 01/07 vit dans werkbonusSocialParPalier et s'applique aux fiches datées.
    werkbonusSocial: {
      employe: {
        A: { max: 125.04, s1: 2880.32, s2: 3336.98, pente: 0.2738 },
        B: { max: 168.62, s1: 2255.50, s2: 2880.32, pente: 0.2699 },
      },
      ouvrier: { // déclarés à 108%
        A: { max: 135.04, s1: 2880.32, s2: 3336.98, pente: 0.2957 },
        B: { max: 182.11, s1: 2255.50, s2: 2880.32, pente: 0.2915 },
      },
    },
    // Bonus FISCAL = réduction du précompte professionnel mensuel.
    // = pctA × (volet A social octroyé) + pctB × (volet B social octroyé), plafonné au max annuel/12.
    // Source : Annexe III AR/CIR 92, n° 46.2 — texte primaire lu le 20/08/2026.
    //
    // 🔴 BARÈME DATÉ depuis le 20/08/2026 — LE VOLET B A CHANGÉ EN COURS D'ANNÉE.
    // Le commentaire précédent disait « hausse 52,54 % → 63 % prévue mais NON encore
    // votée (Chambre) ». C'est PÉRIMÉ : elle est votée ET exécutée.
    //   • Arrêté royal du 24 juillet 2026 « modifiant les règles d'application du
    //     précompte professionnel », MB du 30/07/2026, éd. 2 (lu au Moniteur le
    //     20/08/2026). Art. 4 : « Au numéro 46.2.2, des mêmes règles d'application,
    //     le pourcentage ″52,54 p.c.″ est remplacé par le pourcentage ″63 p.c.″ ».
    //     Art. 7 : « Les articles 4, et 6, 1°, entrent en vigueur le 1er août 2026. »
    //   • Son préambule donne la portée : la composante « très bas salaire » passe à
    //     63 % « pour les exercices d'imposition 2027 et 2028 » (revenus 2026-2027).
    //   • L'AR exécute la loi du 15 juillet 2026 portant réforme de l'IPP (art. 31,
    //     47, 50 et 96), citée dans son préambule.
    // ⚠️ AUCUN palier de retour n'est écrit ici : l'AR n'en fixe pas. À re-sourcer
    //    avant l'exercice 2029 plutôt qu'inventer une date de fin (R2).
    // ⚠️ Une fiche de JUILLET 2026 doit garder 52,54 %. D'où les paliers datés —
    //    jamais un simple remplacement de la valeur, qui réécrirait le passé.
    bonusEmploiFiscalParPalier: [
      { from: '2026-01-01', pctA: 0.3314, pctB: 0.5254 },
      { from: '2026-08-01', pctA: 0.3314, pctB: 0.6300 },
    ],
    // ═══ PLAFOND IPP DU BONUS À L'EMPLOI — ✅ SOURCÉ LE 22/08/2026 ═══
    // La question laissée ouverte le 20/08 (« d'où sort ce 765 ? ») est fermée.
    // Il vient de l'ALINÉA 3 DE L'ARTICLE 289ter/1 CIR 92 — et non de l'art.
    // 289ter, comme l'écrivait le commentaire de bonusEmploiFiscal ci-dessous :
    // c'est l'art. 96 de la loi du 15/07/2026 qui nomme « l'article 289ter/1 du
    // Code des impôts sur les revenus 1992, inséré par la loi du 19 juin 2011 ».
    //
    // ⚠️ ET IL A CHANGÉ. Loi du 15/07/2026 portant réforme de l'IPP (Justel,
    // cn=2026071511, texte primaire lu le 22/08/2026) :
    //   art. 96, 4° : « dans l'alinéa 3, le montant "765 euros" est remplacé par
    //                   le montant "970 euros" » ;
    //   art. 97    : « L'article 96, 2° et 4°, est applicable à partir de
    //                   l'exercice d'imposition 2027. »
    // EXERCICE D'IMPOSITION 2027 = REVENUS 2026. Les 765 € sont donc le plafond
    // de l'exercice 2026, c'est-à-dire des revenus 2025.
    // (Le même art. 96 porte 33,14 → 35 % et 63 → 72 %, mais seulement à partir
    // de l'exercice 2029 : ne pas les avancer.)
    //
    // 🟠 CE PLAFOND EST CELUI DE L'IMPÔT, PAS CELUI DU PRÉCOMPTE. L'annexe III de
    // l'AR/CIR 92 ne fixe AUCUN maximum au n° 46.2 (relue le 20/08 ; l'AR du
    // 24/07/2026, lu en entier le 22/08, ne touche que le pourcentage du n° 46.2.2).
    // Le plafond mensuel que le moteur appliquait jusqu'au 24/09/2026 (maxAnnuel/12)
    // était donc une MODÉLISATION de notre part, pas une règle écrite.
    // 🔓 ARBITRAGE FONDATEUR RENDU LE 24/09/2026 : « aucun plafond ». Le moteur ne
    // modélise plus de plafond au précompte (cf. `bonusEmploiFiscalParPalier`
    // ci-dessus, qui ne porte plus `maxAnnuel`). Cette table reste : c'est le SEUL
    // plafond légal réel du dispositif (celui de l'impôt), gardé pour l'affichage
    // informatif (`lois.js`, `vitrine`) et pour l'agent IA (`bonus-chomage-affiche.js`).
    // 🔴 Conséquence NON VÉRIFIÉE cette session (accès réseau bloqué le 24/09) :
    // sans plafond au précompte, la réduction mensuelle cumulée sur l'année peut,
    // pour les plus bas salaires, dépasser ce plafond annuel de l'impôt — sens
    // inverse de l'ancien écart (où c'était l'impôt qui dépassait le précompte).
    // Le mécanisme de régularisation dans ce cas (le travailleur doit-il rendre la
    // différence à l'enrôlement ?) n'a pas été lu sur un texte primaire : à vérifier
    // avant de l'affirmer à un client (R2 bis).
    bonusEmploiPlafondIPP: [
      { exerciceImposition: 2026, revenus: 2025, montant: 765.00 },
      { exerciceImposition: 2027, revenus: 2026, montant: 970.00 },
    ],
    bonusEmploiPlafondIPPSource:
      "Art. 289ter/1, al. 3, CIR 92. Montant porté de 765 à 970 € par l'art. 96, 4° de la loi du 15/07/2026 portant réforme de l'impôt des personnes physiques, applicable à partir de l'exercice d'imposition 2027 (art. 97) — donc des revenus 2026. Texte primaire lu sur Justel le 22/08/2026 (cn=2026071511).",
    // ═══ CORROBORATION INDÉPENDANTE DE L'ABSENCE DE PLAFOND AU PRÉCOMPTE ═══
    // Ajoutée le 23/08/2026. L'absence de maximum au n° 46.2 de l'annexe III
    // avait été établie par lecture directe de l'annexe (20/08) puis de l'AR du
    // 24/07/2026 (22/08). Une source SECONDAIRE indépendante la confirme :
    // Securex, « Rémunérations mensuelles (et revenus y assimilés) », page du
    // précompte professionnel mensuel, lue le 23/08/2026. Elle décrit la
    // réduction comme « égale à 33,14 % du bonus à l'emploi calculé », puis
    // détaille les deux volets — et les mots « plafond », « 765 » et « 970 »
    // n'y apparaissent PAS UNE FOIS.
    //
    // ⚠️ CE QUE ÇA CHANGE, ET CE QUE ÇA NE CHANGE PAS. Ça ne tranche pas
    // l'arbitrage : la question n'est pas « y a-t-il un plafond au précompte »
    // — on savait déjà que non — mais « faut-il continuer à en modéliser un ».
    // Ça ferme en revanche la dernière incertitude du dossier : le fondateur
    // décide maintenant sur un fait établi par deux chemins indépendants, le
    // texte réglementaire et un secrétariat social.
    // 🟠 Cran de confiance : ✅ sur le texte (annexe III lue), 🟠 sur Securex
    // (source secondaire unique — une seule, pas deux).
    bonusEmploiPrecompteSansPlafondCorrobore:
      "Securex, « Rémunérations mensuelles (et revenus y assimilés, à l'exception des revenus de remplacement) », page du précompte professionnel, lue le 23/08/2026 : la réduction y est décrite comme un pourcentage du bonus à l'emploi social effectivement octroyé (33,14 % volet A, 52,54 % volet B), sans aucun maximum annuel. Corrobore la lecture directe de l'annexe III de l'AR/CIR 92 du 20/08/2026.",
    // Alias de RÉFÉRENCE = palier BASELINE (le plus ancien), même convention que
    // werkbonusSocial : lu par les garde-fous, les copies de constantes et les
    // simulateurs SANS date. Toute surface qui AFFICHE un pourcentage doit passer
    // une date à getBonusEmploiFiscal() — sinon elle annonce 52,54 % en août.
    bonusEmploiFiscal: {
      pctA: 0.3314,
      pctB: 0.5254,
      // Plus de `maxAnnuel` ici depuis le 24/09/2026 (arbitrage fondateur : « aucun
      // plafond »). Le plafond légal réel de l'IMPÔT reste dans `bonusEmploiPlafondIPP`
      // ci-dessus — ce n'est simplement plus celui que le moteur applique au précompte.
    },
    // Impôts fixes par tranche (pour calcul rapide)
    impotFixe: {
      tranche2: 4469.93,  // impôt fixe sur première tranche (0→16710 × 26,75%)
      tranche3: 9944.05,  // impôt fixe sur tranches 1+2
      tranche4: 20320.38, // impôt fixe sur tranches 1+2+3
    },
  },

  // ═══ CSSS — Cotisation Spéciale Sécurité Sociale ═══
  csss: {
    // ═══ CSSS 2026 — Formule ONSS officielle (socialsecurity.be) ═══
    // Retenue mensuelle basée sur rémunération TRIMESTRIELLE + MENSUELLE
    // Source: Instructions administratives ONSS — Art. 38 §3quinquies L. 29/06/1981
    // 🔴 CORRIGE LE 10/08/2026 — UNE TRANCHE QUI N'EXISTE PAS POUR UN ISOLE.
    // La table portait, pour l'isole, une tranche 3.285,29 - 5.836,14 EUR a
    // 15,45 EUR/trimestre. Cette tranche EXISTE bel et bien dans les
    // instructions ONSS — mais sous « imposition commune, conjoint AVEC revenus
    // professionnels » (cf. menage2revenus plus bas, ou elle est correcte). Elle
    // avait ete recopiee ici.
    // Le texte primaire est sans ambiguite pour l'isole : rémunération
    // trimestrielle < 5.836,14 EUR -> AUCUNE retenue.
    // Source : Instructions administratives ONSS 2026/2, « Les cotisations
    // speciales > cotisation speciale de securite sociale », lu le 10/08/2026 :
    // https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/special_contributions/other_specialcontributions/specialsocialsecuritycontribution.html
    // Verifie deux fois, sous deux angles differents (tableau isole, puis
    // recherche de la tranche 3.285,29 et de sa categorie).
    //
    // CONSEQUENCE CONCRETE : tout travailleur ISOLE dont le brut trimestriel se
    // situe entre 3.285,29 et 5.836,14 EUR (soit ~1.095 a ~1.945 EUR/mois — la
    // plage de la plupart des temps partiels et des bas salaires) se voyait
    // retenir 5,15 EUR/mois qu'il ne devait PAS. Detecte en confrontant notre
    // fiche a celle de Securex pour Yannis Ghilain (JAJC SIGNALEUR) : nous 5,15,
    // Securex 0. Securex avait raison.
    isole: {
      tranches: [
        { trimMin: 0, trimMax: 5836.14, mensuelBase: 0, mensuelTaux: 0, mensuelSeuil: 0 },
        { trimMin: 5836.14, trimMax: 6570.54, mensuelTaux: 0.0422, mensuelSeuil: 1945.38, mensuelMax: 2190.18 }, // 4,22% base mensuelle (ONSS 2026/2, vérifié 2026-06-30) — était 0.059 (faux, inexistant en source)
        { trimMin: 6570.54, trimMax: 11211.00, forfaitTrim: 30.99, mensuelTaux: 0.011, mensuelSeuil: 2190.18, mensuelMax: 3737.00 },
        { trimMin: 11211.00, trimMax: 12300.00, forfaitTrim: 82.05, mensuelTaux: 0.0338, mensuelSeuil: 3737.00, mensuelMax: 4100.00 },
        { trimMin: 12300.00, trimMax: 18116.46, forfaitTrim: 118.83, mensuelTaux: 0.011, mensuelSeuil: 4100.00, mensuelMax: 6038.82, maxTrim: 182.82 },
        { trimMin: 18116.46, trimMax: Infinity, forfaitTrim: 182.82, mensuelTaux: 0, mensuelSeuil: 0 },
      ],
    },
    // Ménage 2 revenus (conjoint AVEC revenus professionnels) — ONSS DmfA 2026/2, vérifié 2026-07-02.
    // ⚠️ Corrigé : taux tranche = 5,90 % (0.076 codé avant = inexistant en source) ; minimum
    // 15,45 €/TRIMESTRE (minMensuel 9,30 = introuvable) ; plafond = 154,92 €/trim (pas 182,82) ;
    // pas de tranche > 18.116,46 dans ce régime (forfait 182,82 = sur-retenue supprimée).
    menage2revenus: {
      tranches: [
        { trimMin: 0, trimMax: 3285.29, mensuelBase: 0, mensuelTaux: 0, mensuelSeuil: 0 },
        { trimMin: 3285.29, trimMax: 5836.14, forfaitTrim: 15.45, mensuelTaux: 0, mensuelSeuil: 0 },
        { trimMin: 5836.14, trimMax: 6570.54, mensuelTaux: 0.059, mensuelSeuil: 1945.38, mensuelMax: 2190.18, minTrim: 15.45 },
        { trimMin: 6570.54, trimMax: Infinity, forfaitTrim: 43.32, mensuelTaux: 0.011, mensuelSeuil: 2190.18, maxTrim: 154.92 },
      ],
    },
    // Ménage 1 revenu (conjoint SANS revenus professionnels) — ONSS DmfA 2026/2, vérifié 2026-07-02.
    // Bloc ajouté (référencé par les handlers d'indexation mais absent jusqu'ici) : aucune retenue
    // sous 5.836,14 €/trim ; 5,90 % ensuite ; plafond 182,82 €/trim.
    menage1revenu: {
      tranches: [
        { trimMin: 0, trimMax: 5836.14, mensuelBase: 0, mensuelTaux: 0, mensuelSeuil: 0 },
        { trimMin: 5836.14, trimMax: 6570.54, mensuelTaux: 0.059, mensuelSeuil: 1945.38, mensuelMax: 2190.18 },
        { trimMin: 6570.54, trimMax: Infinity, forfaitTrim: 43.32, mensuelTaux: 0.011, mensuelSeuil: 2190.18, maxTrim: 182.82 },
      ],
    },
  },

  // ═══ RÉMUNÉRATION ═══
  remuneration: {
    // ⚠️ 2 189,81 € est le MONTANT DE BASE (accord social 2021, 3e phase, 01/04/2026), PAS le montant applicable.
    // Indexation du 01/07/2026 (indice pivot dépassé en juin 2026) → 2 233,61 €.
    // Source primaire : CNT, « Montants des CCT », tableau CCT 43 daté 01/07/2026, ligne
    // « 01.07.2026 - Après indexation - 2.233,61 - 18 ans et plus ». Vérifié 2026-07-10.
    // PIÈGE : la page « dossiers thématiques / salaire minimum » du CNT affiche le montant de BASE
    // non indexé (2 189,81 €). Plusieurs secrétariats sociaux la recopient. Ne jamais s'y fier.
    RMMMG: { montant18ans: 2233.61, montant20ans6m: 2233.61, montant21ans12m: 2233.61, montantBase: 2189.81, source: 'CNT — CCT 43, tableau des montants au 01/07/2026 (après indexation ; base 2 189,81 € au 01/04/2026)' },
    // ═══ MINIMA DU TRAVAIL À TEMPS PARTIEL — deux règles CUMULATIVES, pas alternatives ═══
    // ⚠️ CHANGEMENT DE LOI : « un tiers » a été remplacé par « un DIXIÈME » à l'art. 11bis de la
    // loi du 03/07/1978 par la loi du 18/05/2026, art. 5, EN VIGUEUR LE 01/06/2026.
    // Texte consolidé lu VERBATIM sur Justel le 2026-08-05 :
    // https://www.ejustice.just.fgov.be/eli/loi/1978/07/03/1978070303/justel  (mention « L 2026-05-18/10 »)
    // Le minimum PAR PRESTATION, lui, n'a pas bougé : art. 21 de la loi du 16/03/1971 (lu le 2026-08-05,
    // https://www.ejustice.just.fgov.be/eli/loi/1971/03/16/1971031602/justel).
    // Les DÉROGATIONS ne sont PAS les mêmes et c'est là que se trouve le piège étudiant :
    //   • minimum hebdo → AR du 21/12/1992, art. 1er, 2° : « articles 16 à 18 » de l'AR du 28/11/1969
    //     → englobe l'art. 17bis → les ÉTUDIANTS sous contrat d'occupation en sont DISPENSÉS ;
    //   • 3 h par prestation → AR du 18/06/1990, art. 2, 2° : « articles 16 et 17 » seulement
    //     → PAS l'art. 17bis → les étudiants NE sont PAS dispensés. Seule une CCT peut y déroger.
    // Textes cités mot pour mot dans app/lib/legal-corpus-social.js (ar1990-art2, ar1992-art1).
    tempsPartielMinima: {
      fractionHebdoMin: 1 / 10,
      heuresMinParPrestation: 3,
      source: 'Art. 11bis loi 03/07/1978 (un dixième depuis le 01/06/2026, L 18/05/2026 art. 5) + art. 21 loi 16/03/1971 (trois heures) — Justel, lus le 2026-08-05',
      derogationsHebdo: 'AR 21/12/1992 art. 1er — les étudiants y sont (via art. 17bis AR 28/11/1969)',
      derogationsPrestation: 'AR 18/06/1990 art. 2 — liste limitative, les étudiants n\'y sont PAS ; sinon CCT sectorielle ou d\'entreprise',
    },
    // ═══ HEURES SUP VOLONTAIRES D'UN TEMPS PARTIEL — deux conditions neuves ═══
    // ⚠️ Ce n'est PAS le quota (celui-ci vit dans heuresSupp.plafondVolontaire).
    // Ce sont deux conditions d'ACCÈS, propres au temps partiel, entrées en
    // vigueur le 01/04/2026 : un temps partiel ne peut en faire qu'en cas
    // d'augmentation TEMPORAIRE de la charge de travail ET après trois ans à
    // temps partiel.
    // ⚠️ Les trois ans se comptent en CONTINU et peuvent courir chez plusieurs
    // employeurs — mais une période à TEMPS PLEIN casse le compte, tandis qu'une
    // période de non-emploi peut être neutralisée (elle ne compte pas, elle
    // n'interrompt pas). « 3 ans à temps partiel un jour dans la carrière » ne
    // suffit donc pas : on ne peut PAS conclure depuis notre seule ancienneté.
    // ✅ Source lue en entier le 2026-08-14 : SPF Emploi, « Travail à temps
    // partiel — Heures complémentaires et sursalaire ».
    heuresSupVolontairesTempsPartiel: {
      enVigueurDepuis: '2026-04-01',
      exigeSurcroitTemporaire: true,
      ancienneteTempsPartielAnnees: 3,
      // Dispensés : ceux qui, au 01/06/2026, étaient déjà liés par un accord
      // d'heures supplémentaires volontaires avec CE même employeur.
      dispenseAccordAnterieurAu: '2026-06-01',
      // Interdit tout court quand le temps partiel résulte d'une interruption de
      // carrière : crédit-temps (CCT n° 103) ou congé thématique (parental…).
      exclusCreditTempsEtCongesThematiques: true,
      source: 'Loi du 16/03/1971, art. 29, § 3 — SPF Emploi, page « Heures complémentaires et sursalaire », lue le 2026-08-14',
    },
    // ═══ MENSUALISATION — passer d'un barème HORAIRE à un barème MENSUEL ═══
    // mensuel = horaire × heuresSemaine × 52 / 12. C'est la conversion utilisée
    // par les CCT belges elles-mêmes et par toutes les fiches SPF à barème
    // horaire ; `baremes-cp-grid.js` l'applique déjà sur ~90 CP (voir l'en-tête
    // du fichier et les 43 montants recoupés du fonds CP 139, écart max 0,02 €).
    // Le diviseur DÉPEND DU SECTEUR : 164,67 h/mois à 38 h, 173,33 h à 40 h,
    // 157,08 h à 36h15. Écrire « 164,67 » en dur donnerait un plancher horaire
    // trop haut de 5 % en CP 124 — d'où cette clé, jamais un nombre figé.
    semainesParMois: 52 / 12,
    // ═══ PRESTATIONS COMPLÉMENTAIRES DU TEMPS PARTIEL — le SURSALAIRE ═══
    // ⚠️ NE PAS CONFONDRE avec les « heures complémentaires » : le SPF Emploi consacre un
    // paragraphe entier à cette distinction. Les heures complémentaires sont un concept de
    // durée du travail (accord du travailleur, repos compensatoire à 20 % en moyenne sur le
    // trimestre, adaptation du contrat sur demande) ; le SURSALAIRE, lui, obéit aux règles
    // ci-dessous et à un CRÉDIT d'heures exonérées.
    // ⚠️ Le seuil N'EST PAS « un tiers de l'horaire contractuel » — ce seuil n'existe nulle part.
    // C'est un crédit d'heures, et il dépend du RÉGIME (fixe ou variable), pas de l'horaire.
    // Fondement : art. 29, § 3, de la loi du 16/03/1971 + AR du 25/06/1990 assimilant à du
    // travail supplémentaire certaines prestations des travailleurs à temps partiel + CCT n° 35
    // du 27/02/1981 (CNT). Ce n'est PAS l'art. 11bis de la loi du 03/07/1978, qui régit l'écrit
    // et la durée minimale, jamais la majoration.
    // ✅ Source lue en entier le 2026-08-14 : SPF Emploi, « Travail à temps partiel — Heures
    // complémentaires et sursalaire », https://emploi.belgique.be/fr/themes/reglementation-du-travail/travail-temps-partiel/travail-temps-partiel-heures-complementaires
    prestationsComplementaires: {
      tauxSemaine: 0.50,          // semaine, samedi INCLUS
      tauxDimancheFerie: 1.00,    // dimanche ou jour férié
      // Horaire fixe, ou horaire variable avec régime de travail FIXE :
      creditHeuresParMoisCivil: 12,
      // Horaire variable avec régime de travail VARIABLE : 3 h 14 par semaine de la période
      // de référence, plafonné à 168 h.
      creditHeuresParSemaineRefVariable: 3 + 14 / 60,
      creditHeuresPlafondVariable: 168,
      // Contrat conclu pour déroger à la limite hebdomadaire minimale (prestations
      // journalières d'au moins 4 h en horaire fixe) : AUCUN crédit, tout dépassement est majoré.
      creditNulSiDerogationMinimumHebdo: true,
      source: 'Art. 29, § 3, loi 16/03/1971 + AR 25/06/1990 + CCT n° 35 du 27/02/1981 — SPF Emploi, page « Heures complémentaires et sursalaire », lue le 2026-08-14',
    },
    // Salaire minimum ÉTUDIANT dégressif national par âge (CCT n°50, CNT) — plancher SUBSIDIAIRE,
    // s'applique seulement dans les secteurs SANS barème étudiant sectoriel. Si la CP a un barème
    // étudiant (ex. CP 200, voir baremeEtudiantSectoriel), c'est LUI qui s'applique.
    // % lus VERBATIM dans le texte primaire CCT n°50 (cnt-nar.be/documents/fr/cct-050.pdf, art. 2 §1),
    // vérifié 2026-07-09. Les valeurs 70/76/82/88/94 données par Securex étaient FAUSSES.
    // Base = RMMMG EN VIGUEUR, lu dans `RMMMG_MENSUEL_18ANS` (÷164,67h) — jamais recopié (R1).
    // ⚠️ Ce champ portait le littéral 2189,81 € (RMMMG du 01/04/2026) et n'a pas suivi
    // l'indexation du 01/07/2026. Contrôle indépendant qui l'a débusqué le 02/09/2026 :
    // la fiche SPF de la CP 100 publie le RMMMG sous forme horaire — elle donnait
    // 13,2984 €/h (= 2189,81 ÷ 164,67) avant le 01/09, et 13,5644 €/h (= 2233,61 ÷ 164,67)
    // depuis. Notre base était restée sur l'ancienne.
    // Champ : étudiants <21 + tous <18 ; jeunes 18-20 ordinaires = 100%.
    degressifEtudiant: { base: 2233.61, diviseurHoraire: 164.67, pct: { '16': 0.67, '17': 0.73, '18': 0.79, '19': 0.85, '20': 0.90, '21': 1.00 }, conditionMin: 'occupation ≥ 1 mois continu chez le même employeur', articulation: 'plancher subsidiaire — le barème étudiant sectoriel prime s\'il existe' },
    // Barèmes ÉTUDIANTS SECTORIELS par âge (distincts du barème adulte ; s'appliquent aux étudiants 16-20 ans).
    // CP 200 : CSC « barème CP 200 01/01/2026 » section 3 « Barème des étudiants », lu VERBATIM 2026-07-09.
    // Classes A et B codées (montants cohérents ~64,5→91,5% du barème adulte). Classes C/D non codées (extraction PDF
    // ambiguë → à compléter par relecture). 21+ = barème adulte de la classe (baremes-cp-grid.js). Registre Garde-fou Sources.
    baremeEtudiantSectoriel: { '200': { A: { '16': 1447.01, '17': 1635.95, '18': 1824.79, '19': 1975.80, '20': 2051.38 }, B: { '16': 1504.51, '17': 1702.00, '18': 1899.72, '19': 2057.87, '20': 2136.84 } } },
    // ⚠️ BASE D'INDICE — le piège de ce bloc. Statbel a rebasé l'IPC en janvier 2026
    // (2013=100 → 2025=100). `pivot` DOIT être dans la même base que l'indice santé lissé
    // publié par Statbel, sinon toute comparaison est absurde.
    //   • pivot en vigueur = 100,28 (base 2025=100). L'ancien 133,28 était le pivot base 2013=100.
    //   • dernier dépassement RÉEL : juin 2026 (indice santé lissé 100,37 ≥ 100,28).
    //     Effet : +2 % en septembre 2026 pour la fonction publique et les allocations sociales
    //     (délai légal de 3 mois). Le secteur privé s'indexe au calendrier propre à chaque CP.
    //   • prochain pivot = pivot × 1,02 = 102,29. `prochainPivotEstime` est la date estimée de son
    //     dépassement : PRÉVISION RÉVISABLE du Bureau fédéral du Plan (plan.be, maj 2026-07-07 :
    //     indice santé lissé projeté à 102,46 en janvier 2027). Ce n'est pas une valeur officielle.
    //   • `coeff: 2.0807` n'est PAS sourcé (base d'origine indéterminée) → à vérifier avant usage.
    //     Ne pas le « corriger » à l'aveugle : personne ne sait ce qu'il mesure.
    // Sources : statbel.fgov.be/fr/themes/prix-la-consommation/indice-des-prix-la-consommation
    // et plan.be/en/data/consumer-price-index-inflation-forecasts — vérifiées 2026-07-10.
    // 🛡️ BASE de l'indice dans laquelle `indexSante.pivot` est exprimé (millésime Statbel).
    // TOUT code qui compare le pivot à un indice live DOIT lire cette clé et demander la même
    // base à Statbel — jamais la coder en dur de son côté. Statbel publie TOUTES les bases dans
    // le même fichier : comparer un lissé base 2013 (≈136) à un pivot base 2025 (≈100) rend la
    // condition « seuil franchi » TOUJOURS vraie. Incident 23-24/07/2026 : deux PR d'indexation
    // +2 % fantômes ouvertes sur ce fichier (« 136.07 ≥ 102.29 »), heureusement non mergées.
    // Volontairement HORS de la ligne ancrée ci-dessous (substitution de chaîne EXACTE).
    indexSanteBase: 2025,
    // ⚠️ NE PAS changer la FORME de la ligne ci-dessous : app/lib/sources/connecteurs.js y fait
    // une substitution de chaîne EXACTE (mêmes clés, même ordre, mêmes espaces).
    indexSante: { coeff: 2.0807, pivot: 100.28, dateDerniereIndex: '2026-06-01', prochainPivotEstime: '2027-01-01' },
    // ── MODÉRATION SALARIALE — loi-programme du 30/05/2026 (MB 01/06/2026) ──────────
    // ✅ TEXTE PRIMAIRE LU le 2026-08-05 sur Justel (NUMAC 2026003986, p. 29770,
    // dossier 2026-05-30/01), chapitre « Modération salariale », articles 56 à 68.
    //
    // Art. 59 §1 : à compter du 01/06/2026, toute clause liant les salaires barémiques
    //   ET réels à un mécanisme d'indexation « ne produira ses effets qu'à concurrence
    //   de 2 % du salaire de référence, plafonné à un montant de 4 000 euros ».
    //   → l'augmentation maximale vaut 2 % × 4 000 = 80 € (à temps plein).
    // Art. 58 al. 3 : s'applique AUSSI aux « échelles de traitement, montants et barèmes »
    //   sectoriels ou d'entreprise — les grilles sont visées nommément.
    // Art. 58 al. 2 : le dépassement du seuil s'évalue AU MOMENT où l'indexation s'applique.
    // Art. 59 §4 : si l'indexation cumulée réelle dépasse 2 %, la part au-delà de 2 %
    //   s'applique au salaire ENTIER, sans plafond.
    // Art. 59 §3/§5 : ce n'est pas un événement mais une PÉRIODE — elle dure jusqu'à ce
    //   que 2 % d'indexation aient été cumulés, après quoi l'indexation reprend pleinement.
    // Art. 64 4°/5° : la fin de la 1re période est fixée par arrêté royal (non datable) ;
    //   la 2e période débute le 01/01/2028, reportable par AR (art. 60 §7).
    // Art. 60 al. 2 : au début de la 2e période, le seuil de 4 000 € est rattaché à
    //   l'indice pivot en vigueur au 01/06/2026, puis reste fixe pendant la période.
    moderationSalariale: {
      seuilSalaireReference: 4000,     // € brut/ETP — art. 58 al. 1 et art. 59 §1
      seuilIndexe: false,              // art. 59 §1 al. 2 : montant FIXE pendant la période
      plafondIndexationPct: 0.02,      // art. 59 §1 : « à concurrence de 2 pour cent »
      periode1Debut: '2026-06-01',     // art. 59 §1 et art. 64 4°
      periode1Fin: null,               // art. 64 4° : fixée par AR — inconnue à ce jour
      periode2Debut: '2028-01-01',     // art. 60 §1 (reportable par AR, art. 60 §7)
      source: 'Loi-programme 30/05/2026, MB 01/06/2026, NUMAC 2026003986, art. 56 à 68 — texte primaire lu le 2026-08-05 (Justel)',
    },
    // Saut d'indexation au franchissement du pivot = +2 % (mécanique du spilindex).
    // Même source et même vérification que le bloc `indexSante` ci-dessus (statbel.fgov.be +
    // plan.be, vérifiés 2026-07-10) : « prochain pivot = pivot × 1,02 ».
    // 🛡️ Volontairement HORS de la ligne `indexSante` : app/lib/sources/connecteurs.js y fait
    // une substitution de chaîne EXACTE, ajouter une clé dedans la casserait.
    indexSanteSautPct: 0.02,
    // ── SÉRIE DE L'INDICE SANTÉ LISSÉ — carburant du recalcul (2026-08-05) ──
    // Sert à app/lib/indexation-formule.js : REJOUER le % d'indexation d'une CP au lieu de
    // le recopier d'une publication tierce. Aucun calcul de paie ne lit cette série.
    //
    // ⚠️ LES VALEURS SONT RANGÉES PAR BASE, et c'est le point le plus important de ce bloc.
    // Statbel a rebasé l'IPC en janvier 2026 (2013=100 → 2025=100). Un ratio calculé avec un
    // mois base 2013 (~133) au numérateur et un mois base 2025 (~100) au dénominateur donne
    // « −25 % » — et un moteur naïf l'écrirait. Incident réel des 23-24/07/2026 : deux PR
    // d'indexation fantômes nées de cette confusion. `indexation-formule.js` REFUSE de
    // calculer si les mois requis ne tiennent pas tous dans la même base.
    //
    // ✅ Cran PRIMAIRE (2026-08-05) : ces 5 valeurs ont d'abord été relevées chez Agoria et
    // SD Worx (qui publient la formule CP 200 ET son application chiffrée pour le 01/01/2026 :
    // (133,07 + 133,33) / (130,22 + 130,42) = 2,21 %, taux effectivement appliqué), PUIS
    // confrontées à l'open data Statbel lu le même jour — écart 0,0000 sur les cinq.
    //
    // 🛡️ CES 5 VALEURS SONT DES ANCRES, PAS UN HISTORIQUE. Leur rôle n'est pas d'alimenter
    // les calculs — c'est le cron `index-sante` qui télécharge la série complète (326 lignes,
    // ~163 mois par base) et la range dans `app_config.index_sante_serie`. Leur rôle est de
    // PROUVER ce téléchargement : `validerSerie()` refuse la série entière si l'une d'elles
    // n'est plus reproduite (colonne déplacée, format changé). D'où : on n'en ajoute que si
    // on peut les sourcer à la main, et on ne les remplace jamais par du téléchargé
    // (`fusionnerSeries` donne la priorité à ce bloc-ci).
    indexSanteSerie: {
      indice: 'sante_lisse',
      verifie: '2026-08-05',
      source: 'Agoria + SD Worx (formule et application chiffrée CP 200 au 01/01/2026) · Statbel open data pour la base 2025',
      bases: {
        2013: { '2024-11': 130.22, '2024-12': 130.42, '2025-11': 133.07, '2025-12': 133.33 },
        2025: { '2026-06': 100.37 },
      },
    },
    peculeVacances: {
      // EMPLOYÉS — pécule payé par l'employeur
      simple: { pct: 0.0767, base: 'brut annuel precedent' },
      // ✅ TEXTE PRIMAIRE LU le 09/10/2026 : AR 30/03/1967, art. 38, 2° (ejustice ELI 1967033001) — « un supplément égal, par mois de
      // service presté ou assimilé […] au cours de l'exercice de vacances, à 1/12 de 92 p.c. de la rémunération brute du mois pendant
      // lequel les vacances prennent cours ». Le prorata par mois et la ligne qui le paie : app/lib/double-pecule-du.js.
      double: { pct: 0.9200, base: 'brut mensuel du mois des vacances principales × mois de service de l’exercice / 12', source: 'AR 30/03/1967 art. 38, 2° (AR 2001-06-13/32) — texte primaire lu le 09/10/2026 sur ejustice (ELI 1967033001)' },
      patronal: { pct: 0.1535, base: 'brut annuel precedent' },
      // OUVRIERS — pécule payé par la caisse de vacances (ONVA), base 108% du brut annuel.
      // Total VERSÉ à l'ouvrier = 15,38% (simple 8% + double 7,38%).
      // COTISATION patronale ONSS vacances = 15,84% (5,57% trimestriel + 10,27% avis de débit
      //   annuel). ≠ le pécule versé. Vérifié 2026-06-26 : VBO-FEB Q1 2026 + Instructions ONSS.
      //   (l'ancien 15,34% était faux — confondait cotisation et pécule.)
      ouvrierSimple: { pct: 0.08, base: 'brut ouvrier annuel x 108%', source: 'ONVA 2026' },
      ouvrierDouble: { pct: 0.0738, base: 'brut ouvrier annuel x 108%', source: 'ONVA/Securex 2026' },
      ouvrierTotal: { pct: 0.1538, base: 'brut ouvrier annuel x 108%', source: 'ONVA 2026 — versé par la caisse de vacances' },
      ouvrierPatronal: { pct: 0.1584, base: 'brut ouvrier annuel x 108%', source: 'VBO-FEB Q1 2026 + ONSS — cotisation patronale vacances (5,57% trim + 10,27% débit)' },
      // Décomposition du 15,84% ci-dessus en clés NUMÉRIQUES (20/08/2026) : les deux
      // composantes vivaient uniquement dans la chaîne `source` du total, donc aucun
      // écran ne pouvait les injecter — ProceduresRH_PeculeVacances affichait « 10,27%
      // trimestriel via DmfA », confondant l'avis de débit annuel avec la part DmfA.
      // Aucune valeur nouvelle : 5,57 + 10,27 = 15,84, même source, même vérification.
      ouvrierPatronalTrimestriel: { pct: 0.0557, base: 'brut ouvrier trimestriel x 108%', source: 'VBO-FEB Q1 2026 + Instructions ONSS — part déclarée via la DmfA chaque trimestre (vérifié 2026-06-26)' },
      ouvrierPatronalDebitAnnuel: { pct: 0.1027, base: 'brut ouvrier annuel x 108%', source: 'VBO-FEB Q1 2026 + Instructions ONSS — avis de débit annuel de l\'ONVA (vérifié 2026-06-26)' },
    },
    // Pécule de vacances de DÉPART (employé quittant le service) — payé par l'employeur à
    // la date de sortie, sur le brut de l'exercice de vacances en cours (+ exercice précédent
    // non soldé). Simple 7,67% (= salaire → ONSS travailleur+patronal complet) + double 7,67%.
    // Total 15,34%. Double : retenue ONSS travailleur 13,07% sur la part 6,8/7,67 ; part
    // 0,87/7,67 totalement exonérée de cotisations. Source : Acerta + Liantis (2 concordants),
    // vérifié 2026-07-04.
    peculeSortie: {
      simplePct: 0.0767,
      doublePct: 0.0767,
      doubleOnssPart: 6.8 / 7.67,   // fraction du double soumise à l'ONSS travailleur 13,07%
      // ✅ PASSÉ DE 🟠 À ✅ LE 10/09/2026 — TEXTE PRIMAIRE LU. Les instructions
      // administratives DmfA de l'ONSS (« Pécule de vacances », holidaypay.html,
      // lues le 10/09/2026) écrivent : « Le pécule de vacances de sortie est égal
      // à 15,34 % du salaire brut et est composé d'un pécule simple de vacances
      // de sortie (= 7,67 %) et d'un double pécule de vacances de sortie
      // (= 7,67 %) », et « est dû par l'employeur au moment où il est mis fin au
      // contrat de travail ». Il est payé pour l'année de vacances en cours et
      // éventuellement pour l'exercice précédent.
      // 📮 CODES DmfA, de la même page : le pécule SIMPLE de sortie part en code
      // rémunération 7 ; le DOUBLE est déclaré au niveau de l'entreprise sous le
      // code travailleur 870 (partie soumise).
      onssSource: 'ONSS, instructions administratives DmfA, « Pécule de vacances » — socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/salary/particularcases/holidaypay.html, lue le 10/09/2026',
      codeDmfaSimple: 7,
      codeDmfaDouble: 870,
      source: 'ONSS instructions DmfA (texte primaire, 10/09/2026) pour 15,34 % = 7,67 + 7,67 ; Acerta + Liantis (2026-07-04) pour la répartition ONSS — simple=salaire (ONSS complet), double ONSS 13,07% sur 6,8/7,67, exonéré 0,87/7,67',
    },
    // ═══ INDEMNITÉ COMPENSATOIRE DE PRÉAVIS — code de rémunération DmfA ═══
    // ✅ TEXTE PRIMAIRE LU le 02/10/2026 — ONSS, instructions administratives
    // DmfA, page « Code rémunération » (remunerationcode.html) : le code 3
    // couvre les indemnités versées lors de la rupture du contrat de travail
    // « pour autant qu'elles soient exprimées en temps » — c'est le cas de
    // l'indemnité compensatoire de préavis (art. 39 loi 03/07/1978), à la
    // différence du code 4 réservé aux indemnités NON exprimées en temps
    // (ex. prime de départ forfaitaire). La période couverte par l'indemnité
    // (celle que le préavis aurait couvert) se déclare sous code de
    // prestation 1 sur la même ligne (page « Montants payés à la fin de la
    // relation de travail », end_of_contract.html, même lecture).
    indemniteRupture: {
      codeDmfaRemunCode: 3,
      codeDmfaServiceCode: 1,
      source: 'ONSS, instructions administratives DmfA — remunerationcode.html + end_of_contract.html, texte primaire lu le 02/10/2026 (version 2026/3).',
    },
    // ═══ ATTESTATION DE VACANCES ENTRANTE — employé qui ARRIVE avec le pécule de
    // départ déjà payé par son ancien employeur (E4 2e tranche, 04/09/2026). ═══
    // ✅ TEXTE PRIMAIRE LU le 04/09/2026 : AR 30/03/1967 art. 46 et 48, réécrits par
    // l'AR du 28/09/2023 (MB 18/10/2023, effets 01/01/2023, 1re application année de
    // vacances 2024) — copie MB sur etaamb :
    // https://etaamb.openjustice.be/fr/arrete-royal-du-28-septembre-2023_n2023046064.html
    // Corroboré Securex (23/10/2023 et MAJ 21/01/2026), CSC (24/06/2024), Unisoc
    // (13/11/2023) — trois groupes indépendants. Instructions ONSS 2026/3
    // (holidaypay.html) : codes rémunération 1 / 12 / 14 du mécanisme.
    //   · SIMPLE pécule : quand le travailleur prend un jour de vacances couvert par
    //     l'attestation, le nouvel employeur paie le jour en retenant un forfait de
    //     `retenueJourPct` du salaire journalier brut (le travailleur reçoit
    //     `avancePct`) ; décompte final en décembre de l'année de vacances, ou à la
    //     fin du contrat si plus tôt (art. 48 al. 1 et 3).
    //   · DOUBLE pécule : déduit EN UNE FOIS, au congé principal, du double dû par le
    //     nouvel employeur — montant de l'attestation (mention 3°), plafonné au double
    //     qui aurait été dû « si ces prestations avaient été effectuées à son
    //     service » (art. 48 al. 2 et 4).
    //   · L'excédent de simple pécule versé est une avance (art. 23 loi 12/04/1965)
    //     → retenue plafonnée à `plafondRetenueNetPct` du net — ✅ article 23 RELU
    //     le 10/09/2026, le plafond du cinquième est confirmé (cf. RETENUES_ART23).
    // ⚠️ AUCUN MOTEUR N'APPLIQUE ENCORE CES RÈGLES (payroll-engine.js est en zone
    // protégée) : les constantes sont posées pour que le jour venu elles aient une
    // source unique. attestation-entrante.js vérifie la donnée et dit l'écart.
    // Les retenues autorisées sur la rémunération et leur plafond du cinquième.
    retenuesArt23: RETENUES_ART23,
    // Périodicité et dates de paiement (art. 9) et solde à la fin de l'engagement (art. 11).
    paiementArt9: PAIEMENT_ART9,
    attestationVacancesEntrante: {
      retenueJourPct: 0.90,        // art. 48 al. 1 — retenue forfaitaire par jour pris sur attestation
      avancePct: 0.10,             // ce que le travailleur reçoit ce jour-là (1 − retenueJourPct)
      // ✅ Une seule écriture du plafond, relue le 10/09/2026 — cf. RETENUES_ART23.
      plafondRetenueNetPct: RETENUES_ART23.plafondNetPct,
      dateEffet: '2024-01-01',     // 1re année de vacances d'application (AR 28/09/2023 art. 4)
      source: 'AR 30/03/1967 art. 46 §2 9° et art. 48 (AR 28/09/2023, MB 18/10/2023) — texte primaire lu le 04/09/2026 (etaamb) ; Securex 23/10/2023 + 21/01/2026, CSC 24/06/2024, Unisoc 13/11/2023 ; ONSS instructions 2026/3',
    },
    // ═══ VACANCES JEUNES (ONEM) — constante FÉDÉRALE, pas sectorielle ═══
    // Allocation = 65 % de la rémunération brute plafonnée. Plafond mensuel et
    // allocation max/jour (régime 6 j) : page ONEM « Avez-vous droit aux
    // vacances-jeunes ? » (màj 01.07.2026), lue le 20/08/2026 ; miroir RVA NL
    // identique. ⚠️ La CAPAC affiche encore l'ancien plafond (une indexation de
    // retard) ; l'URL historique de la feuille T66 redirige vers l'accueil.
    // Date d'effet exacte de l'indexation du plafond : à dater (l'ONEM ne
    // l'affiche pas sur la page) avant toute citation dans un document client.
    // ⚠️ Une clé CP 226 (indemnites.vacancesJeunes_*) portait 2 837,89 € :
    // plafond PÉRIMÉ (~-6,1 %) et rangé à tort dans une CP — corrigée le
    // 20/08/2026, la présente clé est la source unique.
    vacancesJeunes: {
      pct: 0.65,
      plafondMensuel: 3011.55,
      allocMaxJour: 75.29,        // brut, régime 6 jours
      precomptePct: 0.1009,
      source: 'ONEM onem.be/citoyens/conges/avez-vous-droit-aux-vacances-jeunes- (màj 01.07.2026) + RVA NL, lus le 20/08/2026',
      verifie: '2026-08-20',
    },
    treizieme: { obligatoire: true, cp200: true, base: 'salaire mensuel brut', onss: true },
  },

  // ═══ SALAIRE GARANTI — INCAPACITÉ DE TRAVAIL (MALADIE / ACCIDENT VIE PRIVÉE) ═══
  // Source : Loi du 03/07/1978 relative aux contrats de travail, art. 52 (ouvriers),
  //          art. 70-71 (employés). Jour de carence ABOLI depuis le 01/01/2014
  //          (statut unique — loi du 26/12/2013).
  // - Employé (CDI / CDD ≥ 3 mois) : salaire garanti à 100 % pendant le 1er mois
  //   (30 jours calendrier) d'incapacité, intégralement à charge de l'employeur.
  // - Ouvrier (et employé en période d'essai / CDD < 3 mois & moins d'1 mois d'ancienneté) :
  //   régime dégressif sur le 1er mois, à charge de l'employeur :
  //     · jours 1-7   (semaine 1)        : 100 %
  //     · jours 8-14  (semaine 2)        : 85,88 %
  //     · jours 15-30 (jours 3e-4e sem.) : 25,88 % sur la tranche ≤ plafond AMI,
  //                                        85,88 % sur la tranche > plafond AMI
  //       (la mutuelle prend en charge ~60 % sous plafond via l'indemnité AMI).
  // ⚠️ À VALIDER EXPERT SOCIAL : le « plafond AMI » (rémunération journalière plafonnée
  //    de la 3e-4e semaine) est indexé annuellement — le millésime exact n'est pas figé ici.
  salaireGaranti: {
    source: 'Loi 03/07/1978 art. 52/70-71 — statut unique (loi 26/12/2013), carence abolie 01/01/2014',
    jourCarence: 0, // aboli depuis le 01/01/2014
    employe: { joursPleins: 30, taux: 1.00 }, // 1er mois 100 % employeur
    ouvrier: {
      semaine1:   { jourDebut: 1,  jourFin: 7,  taux: 1.0000 },               // 100 %
      semaine2:   { jourDebut: 8,  jourFin: 14, taux: 0.8588 },               // 85,88 %
      jours15a30: { jourDebut: 15, jourFin: 30, tauxSousPlafond: 0.2588, tauxSurPlafond: 0.8588 }, // 25,88 % ≤ plafond AMI + 85,88 % au-delà
      // Plafond salarial AMI journalier (régime 6 j/sem) — Source INAMI 2026.
      // Historique daté (cohorte « incapacités débutées à partir du 01/01/2024 », ligne
      // « Incapacité primaire (60 %) ») : 183,1311 €/j du 01/01 au 28/02/2026 ;
      // 186,7916 €/j du 01/03 au 31/08/2026 (+2 % indice santé) ; 189,3583 €/j à partir
      // du 01/09/2026 (tableau D de l'INAMI, lu le 22/10/2026).
      plafondAMIJournalier: 189.3583,
      // ✅ TEXTE PRIMAIRE INAMI, tableau D « à partir du 01-09-2026 », lu le 22/10/2026
      // (la valeur de la veille, 186,7916, avait été confirmée le 20/08/2026 en FR + NL).
      // Valeur exacte gardée, plus d'arrondi : l'écran l'affiche à 2 décimales.
      // ⚠️ DEPUIS LE 01/09/2026 L'INAMI NE PUBLIE PLUS UN SEUL PLAFOND POUR TOUS LES
      // RÉGIMES : chaque indemnité a le sien (voir `indemnitesIncapacite`, champs
      // `plafond…` et `max…`). Celui-ci est celui de l'incapacité primaire, donc
      // celui du salaire garanti ouvrier.
      // ⚠️ NUANCE QUI MANQUAIT : ce plafond dépend de la DATE DE DÉBUT de
      // l'incapacité, pas de l'année en cours. Cette valeur est la ligne
      // « incapacités débutées à partir du 01/01/2024 » ; une incapacité
      // ouverte en 2018-2019 plafonne encore plus bas. Pour le salaire
      // garanti (30 premiers jours), l'incapacité commence forcément
      // aujourd'hui — ce scalaire est donc la bonne ligne POUR CET USAGE.
      // ⚠️ Le plafond est exprimé PAR JOUR EN RÉGIME 6 JOURS (≈ brut mensuel
      // ÷ 26). Tout code qui le compare à un brut/30 le fait mordre plus tard
      // qu'il ne devrait — à faire trancher par l'expert social.
      // https://www.inami.fgov.be/fr/themes/incapacite-de-travail/montants-et-plafonds-des-indemnites/salaries-chomeurs/votre-plafond-salarial
      plafondAMIVerifie: '2026-10-22',
      plafondAMISource: 'INAMI — tableau D, plafond journalier A.M.I. régime 6j, ligne « Incapacité primaire (60 %) » (cohorte : incapacités débutées à partir du 01/01/2024), en vigueur à partir du 01/09/2026 ; texte primaire lu le 22/10/2026 (https://www.inami.fgov.be/SiteCollectionDocuments/indemnites_tableau_d.pdf) ; valeur exacte 189,3583',
    },
    // ── CERTIFICAT MÉDICAL — loi du 03/07/1978, art. 31 §2 et §2/1 ──────────────
    // ✅ TEXTE PRIMAIRE LU le 05/09/2026 : texte consolidé (Justel, copie refli.be,
    // numac 1978070303 — ejustice rendait un défi anti-bot, sondé le 04/09/2026).
    // « dans les deux jours ouvrables à compter du jour de l'incapacité ou du jour de la
    // réception de l'invitation, à moins qu'un autre délai ne soit fixé par une CCT ou le
    // règlement de travail » · § 2/1 : « n'est pas tenu, deux fois par année calendrier,
    // de produire un certificat médical pour le premier jour » · « Les entreprises qui
    // occupent moins de 50 travailleurs au 1er janvier … peuvent déroger … par CCT ou RT ».
    // ⚠️ Aucun moteur ne calcule encore sur ces valeurs (05/09) : elles sont ici pour que
    // les textes affichés (implications d'un certificat reçu) les LISENT au lieu de les
    // recopier (R1). Le délai et la dispense peuvent être modifiés par CCT ou RT du client.
    certificatMedical: {
      delaiRemiseJoursOuvrables: 2,
      dispensesPremierJourParAn: 2,
      seuilEffectifDerogationDispense: 50,
      source: 'Loi du 03/07/1978 relative aux contrats de travail, art. 31 §2 al. 3 et §2/1 — texte consolidé lu le 05/09/2026 (https://refli.be/fr/lex/1978070303)',
    },
  },

  // ═══ PROVISIONS COMPTABLES — charges sociales à provisionner ═══
  // Source : Com.IR 92 — Circulaire 2025/C/15 (provision pécule de vacances déductible).
  // - Employé : provision pécule de vacances déductible = 18,20 % des rémunérations
  //   fixes et variables de l'année (hors pécule européen complémentaire).
  // - Ouvrier : PAS de provision 18,20 % — le pécule est payé par l'ONVA / caisse de
  //   vacances via la cotisation patronale (voir remuneration.peculeVacances.patronal).
  provisionsComptables: {
    peculeVacancesEmploye: 0.182, // 18,20 % — circ. 2025/C/15
    source: 'Com.IR 92 — Circulaire 2025/C/15 (provision pécule vacances employés)',
  },

  // ═══ BONUS SALARIAL CCT N°90 — avantage non récurrent lié aux résultats ═══
  // Source : CCT n°90 (CNT) ; ONSS + SPF Emploi ; plafonds indexés au 01/01/2026
  //          (Acerta / Securex / Partena Professional — janvier 2026).
  // - Exonéré des cotisations ONSS ordinaires jusqu'à plafondONSS / travailleur / an,
  //   mais : cotisation SPÉCIALE patronale 33 % + cotisation de SOLIDARITÉ travailleur 13,07 %.
  // - Exonéré de précompte (impôt) jusqu'à plafondFiscal NET / travailleur / an.
  // - Au-delà du plafond ONSS : la partie excédentaire est soumise aux cotisations
  //   sociales ORDINAIRES (travailleur 13,07 % / employeur 25,07 %) + PP.
  bonusCCT90: {
    plafondONSS: 4255,            // € exonéré cotis. ONSS ordinaires (2026)
    plafondFiscal: 3701,          // € NET exonéré de précompte (2026)
    cotisationPatronale: 0.33,    // cotisation spéciale employeur
    cotisationSolidarite: 0.1307, // cotisation solidarité travailleur
    // ✅ TEXTE PRIMAIRE LU le 09/10/2026 — Instructions ONSS 2026/3, « Les cotisations spéciales » › « Les avantages non récurrents liés aux
    // résultats » : cotisation spéciale patronale, cotisation de solidarité du travailleur « comprise dans le montant maximum », maximum
    // annuel de 4 255,00 EUR en 2026, « code travailleur 888, type 0 » (déclaration de l'année du paiement ; type 1 si repris dans une
    // déclaration de l'année précédente), au trimestre du paiement ; étudiants sous solidarité et flexi-travailleurs soumis depuis 2026.
    // 🟠 Plafond FISCAL (art. 38 §1er 24° CIR 92) : 3 701 EUR en 2026 d'après Securex (fiscoliste 2026) — texte du CIR non relu.
    codeDmfa: '888',
    source: 'ONSS, instructions administratives 2026/3 (cotisations spéciales, avantages non récurrents liés aux résultats) — texte primaire lu le 09/10/2026 ; CCT n°90 (CNT) ; art. 38 §1er 24° CIR 92 (plafond fiscal : Securex 2026, 🟠)',
  },

  // ═══ CHÈQUES-REPAS ═══
  // Source : Instructions administratives ONSS 2026/2 + AR du 10/11/2025 — plafond
  // patronal exonéré 8,91 € / valeur faciale max 10 € au 01/01/2026 (ancien plafond
  // 6,91 € / faciale 8 € jusqu'au 31/12/2025). socialsecurity.be · Securex & Partena
  // Lex4you — consulté 2026-06-30. ⚠️ PLAFOND, pas obligation : relever la valeur
  // faciale exige une CCT/accord (cf. fiches Securex restées à 6,91 € en 2026).
  chequesRepas: {
    partTravailleur: { min: 1.09, max: null },
    valeurFaciale: { max: 10.00 },
    partPatronale: { max: 8.91 },
    conditions: 'Par jour effectivement preste',
    exonerationFiscale: true,
    exonerationONSS: true,
  },

  // ═══ ÉCO-CHÈQUES — PLAFOND LÉGAL ═══
  // ⚠️ AJOUTÉ LE 21/09/2026 — la valeur « 250 » vivait en dur dans 31 fichiers (104
  // occurrences), et 8 d'entre elles l'attribuaient à la « CCT n° 98 ». C'est FAUX :
  // la CCT n° 98 du CNT fixe le CADRE et la LISTE des produits et services à caractère
  // écologique (son annexe) ; le plafond, lui, est une condition d'EXONÉRATION, écrite
  // deux fois :
  //   · ONSS — AR du 28/11/1969, art. 19quater, § 2, 6° (texte lu le 21/09/2026,
  //     refli.be/fr/lex/1969112813) : « Le montant total des éco-chèques octroyés par
  //     l'employeur ne peut dépasser 250 euros par an et par travailleur. Le Roi peut
  //     adapter le montant de 250 euros sur base d'un avis unanime du Conseil National
  //     du Travail » ; et au 2° : « un montant maximum de 10 euros par éco-chèque » ;
  //     au 4° : validité « limitée à 24 mois », réactivation unique possible dans les
  //     trois mois, coût « maximal de 5 euros » à charge du travailleur, les chèques
  //     réactivés valant trois mois ; au 5° : aucun échange en espèces ; au 7° : le
  //     montant brut figure sur le décompte de l'art. 15 de la loi du 12/04/1965.
  //   · FISCAL — CIR 92, art. 38, § 1er, al. 1er, 25° (exonération) renvoyant à
  //     l'art. 38/1, § 4, 6° : « le montant total des écochèques octroyés par
  //     l'employeur ou l'entreprise ne peut dépasser par travailleur ou dirigeant
  //     d'entreprise 250 euros par an » (FisconetPlus, CIR à jour au M.B. 31/12/2024,
  //     lu le 21/09/2026).
  // ⚠️ NE PAS CONFONDRE avec le montant réellement dû dans un secteur : celui-là vient
  // de la CCT de la commission paritaire et vit dans la fiche CP (`ecoChequesAnnuel`),
  // où il vaut 0, 72, 100, 200 ou 250 € selon le secteur. Ce plafond-ci est un MAXIMUM
  // exonéré, jamais une obligation.
  ecoCheques: {
    maxAnnuelParTravailleur: ECO_CHEQUES_MAX_ANNUEL,
    valeurFacialeMax: 10,
    validiteMois: 24,
    reactivation: { delaiMois: 3, coutMax: 5, validiteMois: 3 },
    echangeEspeces: false,
    exonerationONSS: true,
    exonerationFiscale: true,
    source: "AR du 28/11/1969, art. 19quater § 2 (2°, 4°, 5°, 6°) — refli.be/fr/lex/1969112813 ; CIR 92, art. 38 § 1er 25° et art. 38/1 § 4 (FisconetPlus, à jour au M.B. 31/12/2024) ; liste des produits et services : CCT n° 98 du CNT. Textes lus le 21/09/2026.",
    dateVerif: '2026-09-21',
  },

  // ═══ CHÔMAGE TEMPORAIRE (ONEm) — vérifié 2026-06-25 ═══
  // 🔄 PLAFOND MIS À JOUR LE 08/10/2026 — ✅ texte primaire : ONEm, feuille info T66
  // (« Dernière mise à jour : 1.09.2026 »), page « A combien s'élève votre
  // allocation en cas de chômage temporaire ? »,
  // https://www.onem.be/citoyens/chomage-temporaire/a-combien-seleve-votre-allocation-en-cas-de-chomage-temporaire-
  // lue le 08/10/2026 (runner lire-source, run 37708040460), mot pour mot : « il est
  // tenu compte d'un montant de 3571,18 € au maximum par mois (montant indexé…) ».
  // Avant : 3 500,99 € (indexé au 01/03/2026), périmé depuis l'indexation de 2 %
  // du 01/09/2026 (3 500,99 × 1,02 = 3 571,01 ; l'ONEm publie 3 571,18).
  // 🟠 Le plafond JOURNALIER n'est PAS publié par cette page : il est DÉRIVÉ ici
  // (mensuel / 26, la convention qu'utilisait déjà la valeur précédente), à ne
  // pas présenter comme un montant ONEm.
  chomageTemporaire: {
    plafondMensuel: 3571.18,   // € brut/mois plafonnés (ONEm T66, à partir du 01/09/2026)
    plafondJournalier: 137.35, // € brut/jour — DÉRIVÉ : 3 571,18 / 26 = 137,353 (non publié tel quel)
    precompte: 0.2675,         // 26,75% retenu sur les allocations
    // Taux allocation 2026 par motif (ONEm/CAPAC/Securex — vérifié 2026-06-26) :
    //   - raisons économiques & tous motifs ordinaires : 60% (n'est PLUS 65% depuis 2024)
    //   - force majeure : 65%
    //   - le flat 70% (COVID) n'est plus en vigueur
    tauxGeneral: 0.60,
    tauxForceMajeure: 0.65,
    source: 'ONEm — chômage temporaire : 60% (général) / 65% (force majeure), plafond 3.571,18€/mois à partir du 01/09/2026 (feuille info T66, lue le 08/10/2026 ; 137,35€/j dérivé /26), précompte 26,75%',
  },

  // ═══ INDEMNITÉS D'INCAPACITÉ (mutuelle) — précompte retenu à la source ═══
  // 🟠 Sources secondaires CONCORDANTES lues le 19/08/2026 — et pas n'importe
  // lesquelles : les mutualités sont celles qui APPLIQUENT la retenue.
  //   · Mutualité chrétienne (mc.be, « Indemnités et impôts ») : 10,09 % les
  //     6 premiers mois d'incapacité, 11,11 % ensuite.
  //   · Solidaris Wallonie (FAQ précompte invalidité) : mêmes taux.
  // ⚠️ L'écran payslips affichait « 11,11 % » PLAT — faux pour les 6 premiers
  //    mois, le cas le plus fréquent d'un complément après salaire garanti.
  // ⚠️ L'extension du précompte aux indemnités d'INVALIDITÉ (> 1 an) a été
  //    REPORTÉE (CAAMI, annonce du report du 1/1/2026) — ne pas l'affirmer.
  indemnitesIncapacite: {
    ppPremiersSixMois: 0.1009,
    ppEnsuite: 0.1111,
    // ✅ TEXTE PRIMAIRE INAMI, tableau D « à partir du 01-09-2026 », lu le 22/10/2026
    // (https://www.inami.fgov.be/SiteCollectionDocuments/indemnites_tableau_d.pdf).
    // Les taux ont été lus le 20-21/08/2026 en FR et NL ; les plafonds et maxima ci-dessous
    // sont ceux du 01/09/2026. Valeurs précédentes, en vigueur du 01/04 au 31/08/2026 :
    // plafond unique 186,7916 ; maxima 112,08 (incapacité primaire, écartement partiel) ·
    // 121,41 / 102,74 / 74,72 (invalidité) · 148,50 / 140,09 (maternité) · 153,17 (naissance,
    // adoption, accueil) · 146,14 (écartement complet).
    // L'indemnité d'incapacité PRIMAIRE (première année) = 60 % de la rémunération
    // journalière BRUTE PLAFONNÉE au plafond salarial INAMI. Ce n'est PAS le plafond ONSS :
    // c'est le SALAIRE qui est plafonné.
    // ⚠️ DEPUIS LE 01/09/2026 CHAQUE INDEMNITÉ A SON PROPRE PLAFOND : l'ancienne règle
    // « un plafond unique × le taux » ne donne plus le maximum publié (écart d'environ
    // 0,2 % entre les lignes). Chaque champ `max…` est le MAXIMUM PUBLIÉ par l'INAMI
    // (arrondi à 2 décimales) et `plafond…` le plafond exact de sa ligne. Recoupement
    // plafond × taux = maximum, fait le 22/10/2026 sur les 9 lignes : 7 tombent au centime ;
    // les 2 lignes à 60 % donnent 113,61498 avec le plafond à 4 décimales, alors que le
    // maximum publié est 113,6150 (donc 113,62) : écart de 0,00002, dû à l'arrondi du plafond.
    // Ne jamais recalculer un maximum à partir d'un autre plafond.
    // https://www.inami.fgov.be/fr/themes/incapacite-de-travail/montants-et-plafonds-des-indemnites/salaries-chomeurs/montant-maximal-de-votre-indemnite-en-periode-d-incapacite-primaire
    tauxIncapacitePrimaire: 0.60,
    maxJournalierPrimaire: 113.62,
    // ✅ INVALIDITÉ (> 1 an) : TROIS catégories, pas deux — l'écran DocumentsSociaux mettait
    // « isolé » à 65 %, soit 10 points de trop. Maxima pour les entrées en invalidité à
    // partir du 01/01/2024 (le maximum dépend de l'ANNÉE D'ENTRÉE : les entrants 2022-2023
    // et plus anciens ont des maxima plus bas, publiés dans le même tableau).
    // https://www.inami.fgov.be/fr/themes/incapacite-de-travail/salaries-et-chomeurs/calculer-votre-indemnite
    tauxInvaliditeChargeFamille: 0.65,
    tauxInvaliditeIsole: 0.55,
    tauxInvaliditeCohabitant: 0.40,
    plafondInvaliditeChargeFamille: 189.1607,
    plafondInvaliditeIsole: 189.5916,
    plafondInvaliditeCohabitant: 190.5363,
    maxInvaliditeChargeFamille: 122.95,
    maxInvaliditeIsole: 104.28,
    maxInvaliditeCohabitant: 76.21,

    // ✅ MATERNITÉ / NAISSANCE / ÉCARTEMENT — INAMI, tableau D (voir plus haut), lu le 22/10/2026.
    // Il n'existe PAS de plafond commun : un « 179,05 €/jour » cité trois fois dans
    // DocumentsSociaux était un chiffre ORPHELIN, absent des valeurs publiées.
    // ⚠️ LE PIÈGE DES 30 PREMIERS JOURS : les 82 % de la première tranche ne sont
    // PAS plafonnés — l'INAMI écrit « non plafonné » / « niet geplafonneerd » (la ligne
    // « 30 premiers jours, actives » du tableau porte un tiret). Le plafond ne mord qu'à
    // partir du 31e jour (75 %). Les chômeuses (79,5 %) ont, elles, un plafond.
    maternite: {
      tauxTrenteJours: 0.82,          // NON plafonné (salariée active)
      tauxTrenteJoursSiIncapacite: 0.795, // plafonné (chômeuse)
      plafondTrenteJoursSiIncapacite: 188.7287,
      maxTrenteJoursSiIncapacite: 150.04,
      tauxApres30Jours: 0.75,         // plafonné
      plafondApres30Jours: 188.845,
      maxApres30Jours: 141.63,
      basculeJours: 30,
      // Écartement du travail (femme enceinte ou allaitante)
      tauxEcartementComplet: 0.78237, // plafonné (grossesse)
      plafondEcartementComplet: 188.7598,
      maxEcartementComplet: 147.68,
      tauxEcartementPartiel: 0.60,    // plafonné (allaitement) — l'écran disait 78,237 %
      plafondEcartementPartiel: 189.3583,
      maxEcartementPartiel: 113.62,
      // Congé de naissance / paternité : 20 jours pour les naissances dès le
      // 01/01/2023 — 3 jours à 100 % employeur, puis 17 jours à 82 % mutualité.
      // Même ligne pour l'adoption et le congé parental d'accueil.
      naissanceJoursTotal: 20,
      naissanceJoursEmployeur: 3,
      naissanceTauxMutualite: 0.82,   // plafonné
      plafondNaissance: 188.6696,
      maxNaissance: 154.71,
      source: 'INAMI/RIZIV tableau D, « à partir du 01-09-2026 », lu le 22/10/2026 — maternité, écartement, congés de naissance, adoption et parental accueil ; plafond et maximum propres à chaque ligne',
      verifie: '2026-10-22',
    },
    source: 'MC + Solidaris (mutualités payeuses), lus le 19/08/2026 — 10,09% les 6 premiers mois, 11,11% ensuite ; extension à l\'invalidité reportée (CAAMI)',
  },

  // ═══ CHÔMAGE COMPLET (ONEm) — vérifié 2026-08-18 ═══
  // ⚠️ NE PAS CONFONDRE AVEC `chomageTemporaire` CI-DESSUS. Deux régimes, deux
  //    plafonds. L'agent légal affichait « plafond A : 3.199,26 €/mois » — un
  //    montant qui n'existe nulle part chez l'ONEm, et une nomenclature
  //    (A / B / C) que l'ONEm n'emploie pas. Corrigé le 18/08/2026.
  //
  // L'ONEm plafonne la rémunération de référence à TROIS niveaux successifs,
  // qu'il nomme « supérieur », « moyen » et « inférieur ». Le taux, lui, est de
  // 65 % les 3 premiers mois puis 60 % (première période d'indemnisation).
  //
  // Source primaire : ONEm, « À combien s'élève votre allocation de chômage après
  // une occupation – situation à partir du 01.03.2026 ? »,
  // https://www.onem.be/citoyens/chomage-complet/reforme-de-la-reglementation-du-chomage/a-combien-seleve-votre-allocation-de-chomage-apres-une-occupation-situation-a-partir-du-01.03.2026-
  // lue le 08/10/2026 (runner lire-source, run 37703531990) ✅ texte primaire.
  // 🔄 MIS À JOUR LE 08/10/2026 : les montants « au 01/2026 » (4 265,98 / 4 010,98 /
  // 3 262,99, feuille T201 lue le 18/08/2026) étaient PÉRIMÉS — l'ONEm affiche
  // désormais 4 351,50 / 4 091,39 / 3 328,40 (+2 %, indexation après le dépassement
  // de l'indice-pivot de juin 2026 ; la même page cite le salaire de référence à
  // 2 233,61 €, le RMMMG de juillet 2026). La sentinelle de fraîcheur l'annonçait
  // « bientôt périmé ». Et depuis la réforme du 01/03/2026, le plafond inférieur
  // vaut du 7e au 12e mois, suivi d'une allocation forfaitaire (2e période).
  chomageComplet: {
    plafondSuperieur: 4351.50,  // €/mois — mois 1 à 3
    plafondMoyen: 4091.39,      // €/mois — mois 4 à 6
    plafondInferieur: 3328.40,  // €/mois — mois 7 à 12 (réforme du 01/03/2026 ; ensuite forfait)
    tauxPremiersMois: 0.65,     // 65% — mois 1 à 3
    tauxEnsuite: 0.60,          // 60% — à partir du 4e mois
    // ✅ 10,09 % — ONEm, feuille info T201 (« mise à jour : 01.09.2026 », lue le 24/10/2026) : « L'organisme de
    // paiement prélève un précompte professionnel de 10,09 % sur les allocations. » Ne sont PAS soumis : les
    // cohabitants ayant charge de famille, les isolés, les cohabitants sans charge de famille en 2e période
    // (allocation forfaitaire) et les chômeurs dispensés pour raisons sociales et familiales, tant qu'ils n'ont
    // aucun revenu professionnel en plus. Le CHÔMAGE TEMPORAIRE, lui, retient 26,75 % (`chomageTemporaire.precompte`,
    // ONEm T66) : deux régimes, deux taux. Cette valeur valait 0,2675 jusqu'au 24/10/2026 : le taux du chômage
    // temporaire recopié ici.
    precompte: 0.1009,          // 10,09 %
    // Vocabulaire ONEm : « supérieur / moyen / inférieur ». Jamais « A / B / C ».
    // ✅ Plafonds, taux 65 % / 60 % et précompte relus le 24/10/2026 sur la feuille T201 (mise à jour 01.09.2026) :
    // « Tous les montants mentionnés dans cette feuille info sont des montants indexés. Ils sont valables à partir
    // du 01.03.2026. » → 4 351,50 / 4 091,39 / 3 328,40 € inchangés au 01/09/2026.
    source: 'ONEm — feuille info T201 « À combien s\'élève votre allocation… à partir du 01.03.2026 ? » (mise à jour 01.09.2026, relue le 24/10/2026) : plafonds salariaux, taux et précompte, montants indexés valables depuis le 01.03.2026',
  },

  // ═══ JOBBONUS FLAMAND — vérifié 2026-08-18 ═══
  // ⚠️ CE N'EST PAS UNE RÉDUCTION DE COTISATIONS et ce n'est PAS le « werkbonus »
  //    fédéral (LB.pp.werkbonusSocial). C'est une PRIME ANNUELLE versée par la
  //    Région flamande AU TRAVAILLEUR : elle n'apparaît sur aucune fiche de paie
  //    et ne change rien au net mensuel. Elle ne se calcule donc jamais dans le
  //    moteur de paie — elle est ici pour que les textes affichés cessent de la
  //    confondre avec le fédéral.
  //
  // ⚠️ Bruxelles et la Wallonie n'ont AUCUN équivalent côté travailleur : leurs
  //    dispositifs (Activa.brussels, ACS, Phoenix, Prime Recrutement, « Job Plus »
  //    wallon depuis le 01/07/2026) sont des aides à l'EMPLOYEUR.
  //
  // Source primaire : vlaanderen.be, page « Jobbonus », lue le 18/08/2026.
  jobbonusFlandre: {
    maxAnnuel: 700.00,          // € /an, temps plein (650 € de base + 50 € forfaitaires)
    partBase: 650.00,
    partForfaitaire: 50.00,
    seuilPlein: 2115.00,        // €/mois de salaire brut MOYEN : en dessous, montant maximal
    seuilSortie: 3114.99,       // €/mois : au-delà, aucun droit ; entre les deux, dégressif
    surFichePaie: false,        // prime versée par la Région, pas une retenue en moins
    source: 'Région flamande (vlaanderen.be) — Jobbonus, lu le 18/08/2026',
  },

  // ═══ CONGÉS THÉMATIQUES — allocations d'interruption (ONEm) — vérifié 2026-08-18 ═══
  // ⚠️ Ces montants s'affichaient EN DUR dans /blog/conge-parental-belgique, la page la
  //    plus vue du blog (2 222 affichages / 90 j). Ils étaient justes, mais invérifiables
  //    et non indexables : une indexation les aurait laissés en place sans alerte. R1.
  //
  // Barème lu le 18/08/2026 sur onem.be, page « Congés thématiques » (documentation/montants/
  // interruption-de-carriere-credit-temps/conges-thematiques), qui annonce « Index du
  // 01.03.2026 » et « Barèmes prévus par mois au départ d'un emploi à temps plein ».
  //
  // Le NET se recalcule exactement depuis le brut, ce qui a servi de contrôle croisé sur
  // cinq paires du tableau ONEm : précompte 10,13 % en suspension complète, 17,15 % en
  // réduction. 1058,86 × 0,8987 = 951,60 · 529,42 × 0,8285 = 438,63 · 179,60 × 0,8285 = 148,80.
  //
  // ⚠️ Des montants MAJORÉS existent (travailleur isolé vivant exclusivement avec un ou
  //    plusieurs enfants à charge). Ils figurent au tableau ONEm mais l'appariement exact
  //    situation → montant n'a pas été établi ici : NE PAS les deviner, aller les lire.
  congesThematiques: {
    // Congé parental — AR du 29/10/1997 (droit) ; AR 02/01/1991 + AR 12/12/2001 (allocations,
    // secteur privé). Bases légales lues le 18/08/2026 sur la page ONEm « Congé parental ».
    parental: {
      ageEnfantMax: 12,                // 21 ans si handicap
      // Ancienneté exigée : contrat avec CET employeur pendant 12 mois (pas
      // nécessairement consécutifs) au cours des 15 mois qui précèdent la
      // demande écrite. Texte ONEm lu le 18/08/2026.
      ancienneteMois: 12,
      ancienneteFenetreMois: 15,
      // Les QUATRE formes. Elles valent toutes le même droit (4 mois temps
      // plein), étalé différemment — d'où des durées maximales différentes.
      // Le « fractionnement » est le pas minimal d'une demande.
      formes: [
        { id: 'complet',   label: 'Suspension complète',   dureeMoisMax:  4, fractionMois:  1, brut: 1080.08, net: 970.67 },
        { id: 'miTemps',   label: 'Réduction à mi-temps',  dureeMoisMax:  8, fractionMois:  2, brut:  540.03, net: 447.42 },
        { id: 'cinquieme', label: "Réduction d'un cinquième", dureeMoisMax: 20, fractionMois:  5, brut: 183.20, net: 151.79 },
        // ✅ Le DIXIÈME (40 mois, par pas de 10) : 91,60 brut / 75,90 net, relu le 24/10/2026 sur
        //    onem.be (« Index du 01.09.2026 », montant de base). ⚠️ Ne JAMAIS le déduire d'une règle
        //    de trois : les montants ONEm ne sont pas proportionnels (540,03 n'est pas la moitié de
        //    1 080,08 au centime). Les montants MAJORÉS pour travailleur isolé (1/10 : 123,18 / 102,06
        //    et 184,00 / 152,45) ne sont pas codés, pour aucune des formes.
        { id: 'dixieme',   label: "Réduction d'un dixième", dureeMoisMax: 40, fractionMois: 10, brut:  91.60, net:  75.90 },
      ],
      // Avec l'accord de l'employeur, la suspension complète se fractionne en
      // SEMAINES, à concurrence de 16 semaines. Ce n'est pas un droit.
      complet_semainesMax: 16,
      // Procédure en deux étapes (ONEm) : avertir l'employeur par écrit, puis
      // introduire la demande auprès de l'ONEm. ⚠️ Pour les formes soumises à
      // accord, l'ABSENCE de décision de l'employeur vaut accord.
      silenceEmployeurVautAccord: true,
    },
    precompte: {
      suspensionComplete: 0.1013,      // 10,13 %
      reduction:          0.1715,      // 17,15 % (mi-temps et 1/5)
    },
    indexation: '2026-09-01',
    // ✅ Texte primaire relu le 24/10/2026 : onem.be, « Congés thématiques », secteur privé,
    // « Index du 01.09.2026 » (complet 1 080,08 / 970,67 · mi-temps 540,03 / 447,42 · 1/5 183,20 / 151,79).
    // Le 1/10 (91,60 / 75,90 sur la même page) reste NON codé : l'écran et le moteur traitent ce trou
    // explicitement (outil-conge-parental-branche.test.js) — à brancher dans un lot à part.
    source: "ONEm — « Congés thématiques », barèmes indexés au 01.09.2026, relus le 2026-10-24 (précédents : 01.03.2026, lus le 2026-08-18) · droit : AR 29/10/1997 · allocations secteur privé : AR 02/01/1991 + AR 12/12/2001",
  },

  // ═══ CRÉDIT-TEMPS (interruption de carrière, CCT n°103 du CNT du 27/06/2012) ═══
  // 🟠 SOURCES SECONDAIRES CONCORDANTES — pas de texte primaire lu. onem.be est
  // bloqué par la politique d'egress de CETTE session (EGRESS_BLOCKED, constaté
  // le 21/09/2026, pas un 403 site) ; web.archive.org est bloqué au même niveau ;
  // le robot render-harvest.yml (Vercel/GitHub, IP capable de franchir un
  // anti-bot JS) a été dispatché sur la page ONEm crédit-temps le 21/09/2026 et
  // a buté sur un bandeau de consentement cookies non franchi par le navigateur
  // headless (run 35578176742, verdict "MORTE — lecture NON exploitable"). Les
  // TROIS voies du playbook R2 (WebFetch, archive datée, robot Vercel) sont donc
  // épuisées pour cette page précise ce jour-là — à re-tenter, pas à re-coder de
  // mémoire. Chiffres ci-dessous : synthèse WebSearch citant onem.be (montants-
  // barèmes crédit-temps secteur privé) + confirmation croisée Securex (fin de
  // carrière), deux requêtes indépendantes, vérifiées le 21/09/2026.
  //
  // ⚠️ NE PAS CONFONDRE avec congesThematiques.precompte ci-dessus : ce sont
  // DEUX régimes de précompte DIFFÉRENTS, malgré un taux qui coïncide (17,15 %
  // isolé ici = le taux « réduction » du congé thématique, coïncidence, pas une
  // règle commune) — c'est justement l'erreur la plus plausible d'un ancien
  // secrétariat social (appliquer le taux congé parental à un crédit-temps).
  //
  // ⚠️ CE QUI N'EST PAS ICI, ET C'EST VOLONTAIRE : ancienneté du crédit-temps
  // SANS motif (le régime avec allocation a été aboli au 01/04/2017, une reforme
  // récente — AR 05/09/2025, entrée en vigueur 01/01/2026 — n'a pas été lue),
  // âge minimal fin de carrière exact par dérogation sectorielle (numéro de CCT
  // interprofessionnelle non confirmé sur texte primaire), durée maximale du
  // crédit-temps fin de carrière. Ces points restent 🔴 tant qu'une session
  // n'a pas pu ouvrir la page vivante (proxy egress) ou qu'un humain ne l'a pas
  // lue — ne jamais les déduire d'une règle de trois depuis ce qui suit.
  creditTemps: {
    // ✅ TEXTE PRIMAIRE LU LE 21/09/2026 — feuille info T160 de l'ONEm, section
    // « Quel est l'impact des allocations sur vos impôts ? ». Cran relevé de 🟠 à ✅.
    // ⚠️ RÉSERVE DE MÉTHODE, à dire telle quelle : lu sur DEUX instantanés datés de
    // l'archive Wayback (2022 et 2024), identiques au mot près. La page VIVANTE, elle,
    // répond mais ne sert que son cadre : bandeau cookies et JavaScript, 109 994 octets
    // sans une occurrence du mot « précompte » — onem.be sondé le 21/09/2026
    // (10 variantes, verdict lu-mais-non-compris ; 5 variantes rendent du contenu, aucune
    // ne rend le texte). Une archive prouve ce que la page DISAIT, pas ce qu'elle dit.
    //
    // 🔴 CE QUE LA VERSION DU 21/09/2026 (PR #3205) DISAIT, ET POURQUOI C'ÉTAIT GRAVE :
    // elle ne portait QUE les trois taux du crédit-temps À MI-TEMPS, présentés comme
    // « le » précompte du crédit-temps. Or le taux dépend de la FORME : un crédit-temps
    // à temps plein est à 10,13 % — le taux que le prompt d'audit désignait justement
    // comme la signature de l'erreur à détecter. L'audit aurait accusé un secrétariat
    // social qui applique le bon taux.
    precompte: {
      // « En cas de crédit-temps à temps plein — Le précompte professionnel prélevé
      // sur votre allocation est de 10,13%. »
      tempsPlein: 0.1013,
      // « En cas de crédit-temps 1/2 temps — Le taux du précompte prélevé sur votre
      // allocation est de : 17,15% si vous êtes isolé […] ; 30% si vous avez moins de
      // 50 ans et que vous n'êtes pas isolé ; 35% si vous avez 50 ans ou plus et que
      // vous n'êtes pas isolé. »
      miTemps: { isole: 0.1715, nonIsoleMoins50: 0.30, nonIsolePlus50: 0.35 },
      // « En cas de crédit-temps 1/5ème — Quel que soit votre âge […] : 35 % si vous
      // n'êtes pas isolé ; 35% si vous êtes isolé et que vous habitez seul ; 17,15% si
      // vous êtes isolé et que vous cohabitez uniquement avec un ou plusieurs enfants
      // dont au moins un est à votre charge. »
      cinquieme: { nonIsole: 0.35, isoleVivantSeul: 0.35, isoleAvecEnfantACharge: 0.1715 },
    },
    // « Isolé » au sens de cette feuille : vit seul, OU cohabite uniquement avec un ou
    // des enfants à sa charge d'un point de vue fiscal — quel que soit l'âge.
    definitionIsole: "vit seul, ou cohabite uniquement avec un ou des enfants à sa charge d'un point de vue fiscal",
    // Exonération possible : travailleur frontalier français ou résident fiscal français
    // rémunéré par un employeur public belge (feuille info T119, non lue).
    exonerationFrontalierFrance: true,
    fiche281: '281.18',
    // L'allocation ONEm n'est pas soumise aux cotisations personnelles de
    // sécurité sociale (même mécanique que congesThematiques : seul le
    // précompte forfaitaire est retenu) — 🟠, cohérent avec le régime des
    // congés thématiques mais non lu sur texte primaire pour le crédit-temps.
    onssAllocation: false,
    source: "✅ ONEm, feuille info T160 « Crédit-temps » (citoyens), section « Quel est l'impact des allocations sur vos impôts ? » — texte primaire lu le 21/09/2026 sur DEUX instantanés datés de l'archive Wayback (2022 et 2024), identiques au mot près. La page vivante onem.be répond mais ne sert que son cadre (bandeau cookies + JavaScript : 109 994 octets, aucune occurrence de « précompte ») — sondé le 21/09/2026 (10 variantes, verdict lu-mais-non-compris). Taux par FORME de crédit-temps : temps plein 10,13 % ; 1/2 temps 17,15 / 30 / 35 % ; 1/5 temps 35 / 35 / 17,15 %. Remplace la synthèse 🟠 du 21/09/2026, qui ne portait que la ligne 1/2 temps. Ancien texteprimaire NON lu (onem.be + web.archive.org bloqués par l'egress de session ; robot render-harvest.yml buté sur bandeau cookies, run 35578176742). CCT n°103 du CNT (27/06/2012).",
  },

  // ═══ INTÉRÊTS LÉGAUX (SPF Finances) — vérifié 2026-06-25 (fiche officielle) ═══
  // ⚠️ Le taux B2B est SEMI-ANNUEL (taux directeur BCE + 8 pts) — re-vérifier chaque
  //    semestre (la valeur ci-dessous = 1er semestre 2026).
  interetsLegaux: {
    ordinaire: 0.045,                  // taux légal ordinaire 2026 (civil/commercial)
    transactionsCommerciales: 0.105,   // B2B Loi 02/08/2002 — 2e semestre 2026 (10,5 % aussi au 1er semestre 2026)
    periode: '2026-S2',
    // 🟠 2e semestre 2026 : deux sources secondaires lues le 24/10/2026 — Dessouroux, huissier de justice
    // (13/07/2026, « C'est officiel : … 10,5 % au 2e semestre 2026 »,
    // https://www.huissier-dessouroux.be/actualites/Nouveau-taux-dint%C3%A9r%C3%AAt-l%C3%A9gal-pour-retard-de-paiement-105-au-2e-semestre-2026)
    // et Calcint (https://calcint.be/rates). L'avis de la Trésorerie / du SPF Finances (Moniteur belge)
    // n'a pas pu être lu : à confirmer. Le taux B2B est SEMESTRIEL : à revérifier au 01/01/2027.
    // Le taux ordinaire (4,5 %) est annuel ; 🟠 claeysengels.be, lexgo.be, mediationdedettes.be.
    source: 'Dessouroux (huissier) 13/07/2026 + Calcint, lus le 2026-10-24 : B2B 10,5 % au 2e sem. 2026 ; ordinaire 4,5 % (2026). SPF Finances : fiche du 1er sem. 2026 : ordinaire 4,5 %, B2B 10,5 %.',
  },

  // ═══ PENSION (1er pilier salarié, SFPD) ═══
  // Structurel vérifié ; ⚠️ plafond + minimums = valeurs INDEXÉES 2026 NON confirmées
  // à la cent (sources tierces divergentes) — à confirmer SFPD avant ancrage conformité.
  pension: {
    ageLegal: 66,                     // 2025-2029 (67 dès 2030)
    ageLegal2030: 67,
    carriereComplete: 45,             // années
    tauxIsole: 0.60,
    tauxMenage: 0.75,
    // Plafonds salariaux pour le calcul de pension — SFPD officiel (sfpd.fgov.be, source
    // primaire fournie 2026-06-26). 2026 = estimation SFPD (= valeur 2025, en attente des
    // chiffres définitifs Statbel ; réforme MB 29/01/2026 à venir). L'ancien 73.461,81 était FAUX.
    plafondSalarialAnnuel: 82608.94,   // plafond NORMAL (année complète 312 j)
    plafondSalarialDifferencie: 74513.68, // plafond BAS (périodes assimilées dès 58 ans)
    plafondSalarialMinimum: 32764.09,  // plafond MINIMUM (certaines périodes assimilées dès 2012)
    // Pension minimum garantie carrière complète (45 ans) des SALARIÉS, indexée au 01/09/2026
    // (+2,0 %, 1.844,93 → 1.881,92 ; 2.305,44 → 2.351,65). 🟠 Source : SFPD, page « Minimum garanti
    // de pension » (sfpd.fgov.be/fr/montant-de-la-pension/calcul/minimum-garanti-de-pension/), lue le
    // 12/10/2026 : isolé 1.881,92 €, ménage 2.351,65 €, survie 1.856,77 € (tableau salariés).
    // Contrôle de cohérence : 2.305,44 × 1,02005 = 2.351,67 ≈ 2.351,65. Wikifin affiche encore les
    // montants du 01/03/2026 (page non mise à jour au 12/10/2026) — retard de page, pas contradiction.
    // Historique : 01/03/2026 isolé 1.844,93 / ménage 2.305,44 (Wikifin + aide-sociale.be, 26/06/2026).
    minMensuelIsole: 1881.92,         // isolé — SFPD, au 01/09/2026
    minMensuelMenage: 2351.65,        // ménage — SFPD, au 01/09/2026
    source: 'SFPD — âge légal 66 (67 dès 2030), carrière 45 ans, taux isolé 60%/ménage 75% ; min garanti 01/09/2026 isolé 1.881,92€/ménage 2.351,65€ (SFPD, lu le 12/10/2026) ; plafond salarial 2026 normal 82.608,94€/différencié 74.513,68€/minimum 32.764,09€ (SFPD, estimation Statbel pending)',
  },

  // ═══ CPAS — REVENU D'INTÉGRATION & EXONÉRATION SOCIOPROFESSIONNELLE ═══
  // Ajouté le 26/08/2026 (dossier BD Motion). Sert dès qu'un travailleur émarge au
  // CPAS : son salaire net réduit son revenu d'intégration au-delà de l'exonération,
  // donc le chiffrage se construit À L'ENVERS depuis ce plafond.
  // ⚠️ Ce n'est PAS un plafond de salaire : au-delà, le revenu d'intégration est
  // réduit de l'excédent, il ne s'éteint pas. Et l'exonération ne s'applique que si
  // l'intéressé garde droit à un revenu d'intégration complémentaire après application.
  // ⚠️ Un REMBOURSEMENT DE FRAIS propres à l'employeur (télétravail, déplacement,
  // internet, repas) n'entre PAS dans les ressources et ne consomme donc pas
  // l'exonération — SPF Intégration Sociale, Primabook « Le calcul des ressources »
  // point 3.12 (lu le 26/08/2026). Voir legal-corpus-social.js : spp-is-frais-professionnels.
  cpas: {
    // AR du 11/07/2002, art. 35 : § 1er ISP ordinaire (3 ans max) · § 2 étudiant sous
    // PIIS (toute la durée du projet individualisé). Même montant pour les deux.
    // `base` = montant de BASE non indexé (177,76) ; `mensuel` = montant indexé en vigueur.
    // Indexé de 2 % au 01/09/2026 : 315,67 (01/03/2026) → 321,99. 🟠 UNE seule source, l'UVCW
    // (https://www.uvcw.be/aide-sociale/etudes/art-3768, § « Immunisation des revenus de
    // l'intégration socio-professionnelle », colonnes « Montants de base » 177,76 et « Montants au
    // 1.9.2026 » 321,99, lue le 24/10/2026) ; le même tableau est confirmé par Droits Quotidiens
    // pour les trois montants du revenu d'intégration, et 315,67 × 1,02 = 321,98 (écart d'arrondi
    // d'un centime). Étudiant : même montant ; métier en pénurie : 461,45 (non codé).
    exonerationISP: { base: 177.76, mensuel: 321.99, depuis: '2026-09-01' },
    // Montants mensuels MAXIMUM du revenu d'intégration, indexés au 01/09/2026 (+2 %) :
    // cat. 1 cohabitant · cat. 2 isolé · cat. 3 famille à charge. Avant : 893,65 / 1 340,47 /
    // 1 811,57 au 01/03/2026 (Primabook, lu le 26/08/2026).
    // 🟠 Deux sources secondaires indépendantes concordantes, lues le 24/10/2026 : UVCW
    // (https://www.uvcw.be/aide-sociale/etudes/art-3768, tableau « 01/09/2026 », avec les
    // équivalents annuels 10 938,74 / 16 408,11 / 22 174,69) et Droits Quotidiens
    // (https://www.droitsquotidiens.be/fr/question/quel-est-le-montant-du-ris-pour-chaque-categorie,
    // « indexés le 1er septembre 2026 »). Le SPP Intégration sociale (mi-is.be, rendu en
    // JavaScript) n'a pas pu être lu : à confirmer sur la page vivante dès qu'un humain l'ouvre.
    revenuIntegration: { cohabitant: 911.56, isole: 1367.34, familleACharge: 1847.89, depuis: '2026-09-01' },
    source: 'SPF Intégration Sociale — Primabook « Le calcul des ressources » : exonération ISP art. 35 §§ 1er et 2 de l\'AR du 11/07/2002, base 177,76 € → 315,67 €/mois au 01/03/2026, 321,99 €/mois au 01/09/2026 (UVCW, 🟠 une source) ; RI catégories 1/2/3 au 01/03/2026 (RI relu au 01/09/2026 le 2026-10-24, voir revenuIntegration). Vérifié en ligne 2026-08-26.',
  },

  // ═══ FRAIS PROPRES EMPLOYEUR ═══
  fraisPropres: {
    forfaitBureau: { max: 160.99, base: 'mensuel' },
    // voiture : barème ANNUEL fonctionnaires fédéraux, référence pour les frais propres
    // employeur. 0,4761 €/km du 01/07/2026 au 30/06/2027 — Circulaire n° 767 du 08/06/2026,
    // MB 16/06/2026 (vérifié le 2026-07-28 ; remplace 0,4449 € du barème 01/07/2025→30/06/2026,
    // périmé depuis un mois et encore affiché dans ~24 fichiers — incident R1).
    // Le barème TRIMESTRIEL est distinct → `forfaitDeplacementTrimestriel` ci-dessous.
    // ⚠️ NE PAS LIRE `.voiture` À PLAT pour valoriser une note de frais ANTIDATÉE.
    // Passer par forfaitKmAu(date) / forfaitKmDuJour() — même convention que
    // bonusEmploiFiscalParPalier / teletravail.paliers : sans date, la valeur
    // rendue est la plus ANCIENNE de `paliers` (le repère baseline), jamais la
    // vivante. `paliers` sourcé le 2026-07-31 (sources secondaires indépendantes
    // concordantes, ≥3 par valeur : Securex/Unisoc/CODEF/OECCBB/eweta/CESSOC,
    // registre vault Garde-fou Sources) — 🟠 non re-vérifié sur texte primaire
    // depuis. Trouvé le 31/07/2026 : une note antidatée se valorisait au taux
    // COURANT au lieu du taux en vigueur à sa date (audit NotesDeFrais.js).
    // ⚠️ `voiture:` reste SUR LA MÊME LIGNE que `forfaitDeplacement: {` — l'ancre
    // FORF_KM du robot de veille (lois-belges-anchors.js) matche ce littéral
    // exact ; à la ligne, l'ancre rend 0 match et lois-belges-anchors.test.js
    // rougit. Et quand le robot bumpe cette valeur (indexation du 1er juillet),
    // le NOUVEAU palier daté s'ajoute au tableau `paliers` dans la même PR —
    // le verrou « à plat = dernier palier » de forfait-km-historique.test.js
    // rougit sinon, exprès : sans lui, forfaitKmAu(aujourd'hui) servirait le
    // taux périmé à l'écran Notes de frais.
    forfaitDeplacement: { voiture: 0.4761, velo: 0.37, transportCommun: 1.00, // velo 2026: 0,37€/km (SPF Finances)
      paliers: [
        { from: '2023-07-01', voiture: 0.4280, source: 'Circulaire n° 722, numac 2023043792 (etaamb/openjustice, Securex)' },
        { from: '2024-07-01', voiture: 0.4415, source: 'Circulaire n° 742 du 24/06/2024, MB 17/07/2024, numac 2024006508 (Unisoc/eweta/CESSOC)' },
        { from: '2025-07-01', voiture: 0.4449, source: 'Circulaire n° 754 du 12/06/2025, MB 20/06/2025 (circ. 2026/C/13 via OECCBB, Securex)' },
        { from: '2026-07-01', voiture: 0.4761, source: 'Circulaire n° 767 du 08/06/2026, MB 16/06/2026 (Securex/Unisoc/CODEF)' },
      ],
    },
    // ⚠️ LE BARÈME TRIMESTRIEL DEVIENT UNE CONSTANTE — il ne vivait qu'en
    // COMMENTAIRE deux lignes plus haut, et c'est ce qui a laissé passer le
    // défaut. La Belgique admet DEUX barèmes pour le même remboursement :
    //   · ANNUEL (AR de 1965), révisé chaque 1er juillet → `forfaitDeplacement.voiture`
    //   · TRIMESTRIEL (AR du 13/07/2017, révisé par trimestre depuis l'AR du
    //     10/11/2022) → ci-dessous.
    // L'employeur CHOISIT lequel il applique ; les deux sont admis. Ce ne sont
    // donc pas deux versions d'un même chiffre — les confondre, c'est ce qui a
    // fait recopier un montant périmé sur 136 fiches CP.
    // 🟠 Deux sources secondaires RÉELLEMENT indépendantes, concordantes au
    // centime, lues le 24/08/2026 : Unisoc (fédération d'employeurs) et Securex
    // (secrétariat social). Circulaires n° 767 (annuel) et n° 768 (trimestriel,
    // MB 03/07/2026). Le Moniteur lui-même n'a pas été relu : finances.belgium.be
    // est derrière un anti-bot F5 (voir la carte d'accès du CLAUDE.md).
    forfaitDeplacementTrimestriel: {
      voiture: 0.4452,
      du: '2026-10-01',
      au: '2026-12-31',
      source: "AR du 13/07/2017, indexation trimestrielle depuis l'AR du 10/11/2022 — 4e trimestre 2026 : circulaire n° 771, MB 29/09/2026, instructions ONSS du 02/10/2026 (lues le 12/10/2026). Le 3e trimestre (0,4440, circulaire n° 768) a été reconfirmé à la même lecture.",
      // ⚠️ L'HISTORIQUE RÉCENT SERT À RECONNAÎTRE UNE VALEUR PÉRIMÉE, pas à
      // calculer : 0,4327 (01/04→30/06/2026) · 0,4326 (Q1 2026) · 0,4312 (Q4 2025).
      // Plus un recalcul EXCEPTIONNEL mensuel pour avril et mai 2026 (0,4571 puis
      // 0,4841) décidé par l'AR du 18/05/2026 face à la hausse du carburant.
      precedents: { '2026-07-01': 0.4440, '2026-04-01': 0.4327, '2026-01-01': 0.4326, '2025-10-01': 0.4312 },
    },
    forfaitRepresentation: { max: 40, base: 'mensuel sans justificatif' },
    // ═══ FORFAIT DE BUREAU (TÉLÉTRAVAIL STRUCTUREL) — VALEUR DATÉE ═══
    // ⚠️ NE PAS LIRE `.max` À PLAT. Passer par forfaitTeletravailAu(date) /
    // forfaitTeletravailDuJour(). Même raison que bonusEmploiFiscalParPalier :
    // un montant lu à plat sert du périmé dès que le palier suivant s'ouvre, et
    // du faux sur toute pièce antérieure si on remplace la valeur sans dater.
    //
    // ✅ TEXTE PRIMAIRE LU LE 29/08/2026 — Instructions administratives ONSS
    // 2026/2, « Remboursement de frais » : « Un employeur peut octroyer une
    // indemnité forfaitaire de bureau d'un montant maximal de 160,99 EUR par
    // mois (164,21 EUR à partir du 1er septembre 2026) aux travailleurs qui
    // effectuent du travail à la maison de manière structurelle et sur base
    // régulière (…), aussi bien pour les travailleurs à temps partiel que pour
    // les travailleurs à temps plein. »
    //
    // ⚠️ CONDITION À NE PAS PERDRE, elle est dans la même phrase : « Par
    // structurel et de manière régulière on entend l'ÉQUIVALENT D'UNE JOURNÉE
    // DE TRAVAIL PAR SEMAINE », temps partiel compris. En dessous, le forfait
    // n'est pas exonéré — c'est une condition de VOLUME de télétravail, pas de
    // régime de travail : un temps partiel y a droit en entier, sans prorata.
    teletravail: {
      max: 160.99,
      base: 'mensuel structurel',
      paliers: [
        { from: '2026-03-01', max: 160.99 },
        { from: '2026-09-01', max: 164.21 },
      ],
      conditionVolume: "équivalent d'une journée de travail par semaine, temps partiel compris — pas de proratisation ETP",
      source: 'Instructions administratives ONSS 2026/2 — https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/salary/particularcases/expensesreimbursement.html, texte primaire lu le 29/08/2026 (200, 231 933 octets). Securex et Acerta concordants sur le palier du 01/03/2026.',
    },
    // ═══ Frais propres — équipement PRIVÉ du travailleur (≠ ATN, où l'employeur
    // fournit). Instructions ONSS 2026/2 (expensesreimbursement.html), texte
    // primaire lu le 20/08/2026 ; Securex + UCM concordants. Ajoutées après la
    // découverte d'un « internet 50 €/mois exonéré » dans les contrats générés :
    // valeur SANS base légale (issue d'une table non sourcée du vault) — le vrai
    // forfait est 20 €/mois. Le dépassement serait requalifiable en rémunération.
    internetPrive: { max: 20, base: 'mensuel — abonnement internet privé du travailleur, non cumulable avec le remboursement des frais réels' },
    pcPrive: { max: 20, base: 'mensuel — PC privé, périphériques et logiciels inclus' },
    // 🟠 Périphériques seuls (2e écran / imprimante, SANS PC privé remboursé) :
    // 5 €/appareil, max 10 €/mois, 3 ans max — Securex seul lu en détail.
    peripheriquesPrives: { parAppareil: 5, max: 10, dureeMaxAns: 3 },
    // ═══ VÊTEMENTS DE TRAVAIL — forfaits ONSS par jour de travail (achat, entretien) ═══
    // ✅ TEXTE PRIMAIRE LU LE 04/09/2026 — Instructions administratives ONSS
    // 2026/3, « Remboursement de frais » (expensesreimbursement.html, 200,
    // 231 933 octets) : « Achat des vêtements de travail 2,20 EUR/jour — vêtements
    // de travail au sens strict du terme (salopettes, chaussures de sécurité, …)
    // ou autres vêtements imposés par l'employeur mais qui ne peuvent être
    // portés comme tenues de ville (uniforme, …). Entretien des vêtements de
    // travail 2,20 EUR/jour ». C'est le MAXIMUM exonéré ; une CCT peut y
    // renvoyer (CP 323 : CCT du 29/01/2024, montants « adaptés annuellement
    // jusqu'au montant maximum exonéré » — corpus `cct323-vetements-travail`).
    // Lecteur : vetements-travail-dossier.js. Cohérent avec
    // `horeca['302'].indemnites.vetements_travail.montant_jour`.
    vetementsTravail: {
      achat: 2.20,
      entretien: 2.20,
      base: 'par jour de travail — maximum exonéré ONSS, achat et entretien distincts',
      date: '2026-01-01',
      source: 'Instructions administratives ONSS 2026/3, Remboursement de frais — https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/salary/particularcases/expensesreimbursement.html, texte primaire lu le 04/09/2026.',
    },
  },


  // ═══ AVANTAGES — ALIAS POUR MODULES ═══
  avantages: {
    fraisPropres: {
      bureau: 160.99,
      km: 0.4761,
      repas: 19.22,
      teletravail: 160.99,
    },
    atnGSM: 3,
    atnPC: 6,
    atnInternet: 5,
    // Abonnement de téléphonie fixe ou mobile mis gratuitement à disposition : 48 €/an = 4 €/mois,
    // CUMULABLE avec l'appareil (atnGSM = tablette ou téléphone mobile, 36 €/an). Même source que LB.atn.abonnementTel.
    atnAbonnement: 4,
    // Même valeur que `LB.ecoCheques.maxAnnuelParTravailleur` — écrite une seule fois.
    ecoMax: ECO_CHEQUES_MAX_ANNUEL,
  },


  // ═══ COTISATIONS SPÉCIALES ═══
  cotisations: {
    cotCO2Min: 33.93,
    // Cotisation de solidarité CO2 (ONSS employeur) — voiture de société. Mensuel :
    //   essence [(CO2×9)−768]/12 · diesel [(CO2×9)−600]/12 · × coeff. indexation 2026.
    // Minimum 33,93 €/mois (véhicule avant 01/07/2023) ou 42,34 €/mois (depuis 01/07/2023).
    // Véhicules depuis 01/07/2023 : cotisation ×4 dès le 01/01/2026. Électrique/CO2 0 → minimum.
    // Données CO2 manquantes → défaut 182 g (essence) / 165 g (diesel).
    // Source : Securex/Acerta/Agoria/link2fleet 2026, vérifié 2026-07-05.
    cotisationCO2Solidarite: {
      indexCoeff: 185.85 / 114.08,
      correction: { essence: 768, diesel: 600 },
      minAvant0107_2023: 33.93,
      minDepuis0107_2023: 42.34,
      multiplicateurRecent: 4,
      defautCO2: { essence: 182, diesel: 165 },
      dateReference: '2023-07-01',
      // ── DÉCLARATION EN DmfA (ONSS, instructions administratives 2026/2,
      //    « Les cotisations spéciales — Véhicules de société », lu le 10/08/2026) :
      //    « la cotisation de solidarité se déclare dans le bloc 90002 "cotisation
      //    non liée à une personne physique" » sous le code travailleur 862, et
      //    « la mention des numéros de plaque des véhicules concernés » est
      //    OBLIGATOIRE dans le bloc fonctionnel 90294 « Véhicule de société »,
      //    chaque numéro ne pouvant y figurer qu'une seule fois.
      //    Les minimums 2026 lus sur la même page confirment les valeurs ci-dessus :
      //    33,93 € avant le 01/07/2023, 42,34 € depuis.
      //    https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/special_contributions/companycar.html
      codeTravailleurDmfa: 862,
      blocCotisation: '90002',
      blocVehicule: '90294',
    },
    plafondONSS: 75038.09,
    // Plafond fiscal annuel du flexi-salaire, PAR ANNÉE DE REVENUS (non-pensionné ; un pensionné, pension anticipée comprise, n'en a pas). ✅ Texte primaire :
    // SPF Finances, « Flexi-jobs » (finances.belgium.be/fr/particuliers/declaration-impot/revenus/flexi-jobs), lu le 07/10/2026 : « Revenus 2025 (exercice
    // d'imposition 2026) : 18.000 euros · Revenus 2026 (exercice d'imposition 2027) : 18.440 euros ». Indexé chaque année : à ajouter ici à chaque indexation.
    flexiJob: { plafondParAnnee: { 2025: 18000, 2026: 18440 }, plafond: 18440, plafondPensionAnticipee: 8121, taux: 0.28, dateEffet: FLEXI_DATE_EFFET, salaireMin: FLEXI_SALAIRE_MIN_HORAIRE, salaireMax: FLEXI_SALAIRE_MAX_HORAIRE, patronal: 0.28, travailleur: 0, pp: false, peculeVacances: FLEXI_PECULE_PCT, source: 'Loi 30/12/2025 — plafond non-pensionnés 18 440€ (indexation 2026, 18 000€ pour 2025). Flexi-salaire : ' + FLEXI_SOURCE + ' Historique : 11,87 €/h depuis le 01/03/2026, 11,64 €/h depuis 02/2025.' },
    interimCP322: {
      primeFinAnnee: 0.0833,        // 8,33% du brut période juillet N-1 → juin N (≈ 1 mois) — Fonds Social Intérimaires
      doublePeculeRatio: 0.92,      // 92% du brut exercice précédent (versé mai/juin)
      fondsSocialPatronal: 0.001,   // 0,10% — cotisation patronale FSI
      onssTravailleur: 0.1307,      // ONSS standard
      onssPatronal: 0.2507,         // ONSS patronal standard
      cp: '322.00',
      source: 'CCT CP 322 — prime versée par Fonds Social Intérimaires (8,33% du brut, période 1/7-30/6, min 65j ou 494h)',
    },
    // 2026: Quota = 650h/an (depuis 1er janvier 2025, gouvernement Arizona)
    // Source: ONSS (https://www.onss.be) + studentatwork.be + Securex 2026
    etudiant: { heuresMax: 650, patronal: 0.0542, travailleur: 0.0271, total: 0.0813, pp: false, onssNormal: false, codeDmfaOuvrier: '840', codeDmfaEmploye: '841', source: 'ONSS — quota 650h/an depuis 1er janvier 2025 ; cotisation solidarité 8,13% (5,42%+2,71%), codes DmfA travailleur 840 (ouvrier)/841 (employé), bloc 90003 StudentContribution — socialsecurity.be instructions 2026/2, vérifié 2026-07-05' },
    artiste: { plafondPetitePrestation: 30, exonerationJournaliere: 75.84, reductionForfait: 0.50 },
    sportif: { seuilExoneration: 12300, onssReduit: true, taux: 0.1307 },
    // 2026: plafonds indexés — Source: ONSS Instructions 2026/1 + Securex 15/01/2026 + Acerta 21/01/2026
    // 44,02€/jour · 1760,83€/an (standard) · 3233,91€/an (catégorie majorée: sport, garde de personnes, transport médical)
    volontaire: { plafondJournalier: 44.02, plafondAnnuel: 1760.83, plafondAnnuelMajore: 3233.91, sportPlafond: 29.45, imposableAuDela: true, source: 'ONSS Instructions 2026/1 — indexation 2026' },
    conjointAidant: {
      maxi: { baseCalc: 'assiette_independant', cotisation: 0.20 },
      mini: { cotisation_forfait: 170.00, plafondAnnuel: 3000 }
    },
    pensionneActif: {
      // Revenus professionnels autorisés AVANT l'âge légal — INASTI 2026 (vérifié en ligne
      // 2026-06-26 sur rsvz-inasti.fgov.be) : 8.346€ sans enfant à charge, 12.519€ avec.
      // (l'ancien 9.747€ était périmé). Au-delà : retenue/suspension de pension.
      plafondAvant65: 8346, plafondAvant65AvecCharge: 12519,
      plafondApres65: null, retenueSurPension: 0.25, seuil65ans: 65
    },
    // ═══ INDÉPENDANTS — INASTI/RSVZ 2026 ═══
    // 🛡️ SOURCE UNIQUE (R1) : independants-engine.js DÉRIVE INASTI_2026 de ce bloc.
    // Vérifié EN LIGNE 2026-06-26 : INASTI (rsvz-inasti.fgov.be/faq), Securex, Liantis
    // Tableau 2026, Partena Barème 2026, Accountable. ⚠️ Les valeurs 2025 (plafonds
    // 67.522,98/99.528,80, cotMin 407,59, PLCI 3.987,16…) étaient PÉRIMÉES → indexation 2026.
    independants: {
      // Plafonds de revenus nets imposables 2026 — INASTI (20,5% jusqu'au 1er ; 14,16% entre ; 0% au-delà)
      plafonds: { intermediaire: 75024.54, absolu: 110562.42 },
      // Titre principal : cotMinTrim = 20,5% × revenu min / 4 = 890,42€/trim (Securex/INASTI 2026)
      // (l'ancien 407,59 était en réalité le minimum provisoire du CONJOINT AIDANT maxi, pas du principal)
      principal: { tranche1: 0.205, tranche2: 0.1416, cotMinTrim: 890.42, seuilCotMin: 17374.08 },
      // Complémentaire : exonération si revenus < 1 922,15€/an (Liantis 2026)
      complementaire: { tranche1: 0.205, tranche2: 0.1416, seuilExonAnn: 1922.15, seuilExonTrim: 480.54 },
      // Conjoint aidant maxi : cotisation provisoire min 407,59€/trim (Liantis 2026)
      conjointMaxi: { provisionnelMinTrim: 407.59 },
      // Conjoint aidant mini (né avant 01/01/1956) : 0,79% / 0,51%, mêmes plafonds que le principal
      conjointMini: { tranche1: 0.0079, tranche2: 0.0051, seuilMin: 17374.08 },
      // Primostarter : cotisation provisoire min réduite 459,82€/trim (Securex/INASTI 2026)
      // ⚠️ second palier T5-8 réformé (structure 92,02/196,76 périmée) — à confirmer ; engine garde 1 palier réduit
      primostarter: { cotMinTrim: 459.82, seuilRevenus: 17374.08 },
      // Pensionné actif : taux réduit 14,70% (Liantis/Partena 2026). Plafonds revenus → statuts.pensionneActif (à confirmer, réforme en cours)
      pensionneActif: { taux: 0.147 },
      // Étudiant indépendant : 0% sous la moitié du revenu min (8 687,04€), régime normal dès 17 374,08€
      // ⚠️ seuilAllocFam (réglementation régionale, pas INASTI) non centralisé — à confirmer source régionale
      etudiant: { seuil1: 8687.04, seuil2: 17374.08 },
      // PLCI 2026 — Securex : ordinaire 8,17%/4 086,34€ · sociale 9,40%/4 701,54€ (min 10% en solidarité)
      plci: { pctRevenu: 0.0817, pctSocial: 0.094, plafondOrdinaire: 4086.34, plafondSocial: 4701.54, pctSolidariteMin: 0.10 },
      // TVA — franchise sous 25 000€/an (Loi 01/01/2016, seuil indexé)
      tva: { seuilFranchise: 25000, tauxStd: 0.21, tauxReduit12: 0.12, tauxReduit6: 0.06 },
      // Versements anticipés impôt — majoration globale 4,5% ; bonifs VA1-4 (SPF Finances 2026)
      versementsAnticipes: { majoration: 0.045, bonifVA1: 0.09, bonifVA2: 0.075, bonifVA3: 0.06, bonifVA4: 0.045 },
      // Droit passerelle (cessation involontaire) — INASTI/Liantis 2026 (montants liés à l'index pivot)
      droitPasserelle: { sansCharge: 1671.01, avecCharge: 2007.06, dureeMaxMois: 12 },
      // IPP — barème revenus 2026 (exercice 2027) — SPF Finances / Accountable (quotité exemptée 11 180€)
      ipp: {
        tranches: [
          { jusqu: 16720, taux: 0.25 },
          { jusqu: 29510, taux: 0.40 },
          { jusqu: 51070, taux: 0.45 },
          { jusqu: Infinity, taux: 0.50 },
        ],
        quotiteExempte: 11180,
        // Quotité du revenu exemptée d'impôt DE BASE, par EXERCICE D'IMPOSITION (art. 131 CIR 92) — ajouté le 05/10/2026.
        // 🟠 Sources SECONDAIRES concordantes, lues le 05/10/2026 : Securex Fiscoliste janvier 2026 (tableau « Art 131-143
        // Quotité exemptée d'impôt », colonnes « EI 2026 / AR 2025 » = 10.910 et « EI 2027 / AR 2026 » = 11.180 ; base légale non
        // indexée 4.785), Wikifin (11 180 € « en 2026 »), RTBF/Liantis/OECCBB. L'arrêté royal d'indexation lui-même n'a PAS été lu :
        // à confirmer sur le Moniteur si ce chiffre part dans un document facturé. ≠ la quotité du PRÉCOMPTE (pp.quotiteExemptee,
        // Annexe III n° 32 : 11 170). Le document 276T imprimait 10.160 « exercice 2026 » : valeur présente dans AUCUNE source.
        // Un exercice absent de ce tableau n'est JAMAIS deviné : le document n'imprime alors aucun montant (quotite-ipp.js).
        quotiteExempteParExercice: { 2026: 10910, 2027: 11180 },
        additionnelleCommunaleMoy: 0.075, // moyenne (varie par commune) — estimation
      },
      // ── Changement de caisse d'assurances sociales — AR 19/12/1967 (règlement général
      // de l'AR n° 38), art. 10 § 1er. ✅ Texte primaire lu le 28/09/2026 (PDF INASTI
      // « dispositions réglementaires ») : « Le travailleur indépendant qui est resté
      // affilié à la même caisse d'assurances sociales pendant quatre ans au moins, peut,
      // le 1er janvier de chaque année, quitter cette caisse et s'affilier auprès d'une
      // autre » ; « le changement ne peut intervenir si […] des cotisations réclamées à
      // l'assujetti et exigibles restent dues » ; plus aucun changement à partir du 1er
      // janvier de l'année où l'assujetti atteint l'âge de la pension.
      // ⚠️ La DATE LIMITE DE DEMANDE n'est pas dans l'article lu : 🟠 Xerius (« introduire
      // votre demande avant le 30 juin ») + Mon Secrétariat Social + StarterPass, concordants.
      // ❌ L'écran comparateur affichait « une fois par an, notification avant le 1er
      // décembre » jusqu'au 28/09/2026 — aucune source ne dit cela.
      changementCaisse: {
        affiliationMinAnnees: 4,
        effetJourMois: '01/01',
        demandeAvantJourMois: '30/06',
        cranDemandeAvant: 'secondaire',
        dettesBloquent: true,
        baseLegale: 'AR 19/12/1967, art. 10 § 1er',
        lu: '2026-09-28',
      },
      source: 'INASTI/Securex/Liantis/Partena/Accountable — vérifié en ligne 2026-06-26 (indexation 2026) · changementCaisse : AR 19/12/1967 art. 10, lu le 2026-09-28',
    },
    dirigeant: {
      fraisForfaitaires: { pct: 0.03, max: 3200 },
      jeton: { retenue: 0.30, fiche: '281.17' },
      remuneration: { fiche: '281.20', onss: false, css: false }
    },
    associeActif: {
      fiche: '281.50', cotisationSociale: 'caisse_sociale', pp: 'versements_anticipes'
    },
    interimaire: { via_agence: true, onss: 0.1307, fiche: '281.10' },
    APE: {
      // Aide à la Promotion de l'Emploi — Bruxelles/Wallonie
      bruxelles: { niveaux: [1,2,3,4,5,6,7,8], montantMax: 38000 },
      reduction_onss_patronal: true
    },
    ACS: {
      // Agent Contractuel Subventionné — secteur non-marchand
      subvention_mensuelle: 900, onss_complet: true
    }
  },

  // ═══ BUDGET MOBILITÉ (loi du 17/03/2019) ═══
  // Cotisation spéciale sur le SOLDE du pilier 3 (cash) : 38,07 %, À CHARGE DU
  // TRAVAILLEUR (l'employeur la retient et la verse), sans précompte, ouvre des
  // droits maladie/chômage/pension. TAUX AUTONOME écrit tel quel dans la loi —
  // art. 38, § 3novodecies, loi du 29/06/1981 (inséré par l'art. 22 de la loi
  // du 17/03/2019) : numériquement égal à 13,07 + 25,00 mais il ne suivrait PAS
  // automatiquement un changement de ces taux. DmfA : code cotisation 869,
  // solde sous code rémunération 29, budget total en zone 01216.
  // ⚠️ Ne pas confondre avec la cotisation GLOBALE ordinaire (code 495) qui
  // vaut aussi 38,07 % (13,07 + 25,00) — homonyme numérique, autre concept.
  // Sources (texte primaire lu le 20/08/2026) : Justel loi 17/03/2019 art. 22 ·
  // ONSS instructions 2026/2 special_contributions/saldo_mobilitybudget.html ·
  // lebudgetmobilite.be §9. Corrige au passage TX_BUDGET_MOB (helpers.js) qui
  // valait « 0.38 + 0.025 » = 40,5 % — sans source, FAUX, et consommé par le
  // calcul de PrimesAvantagesV2.
  budgetMobilite: {
    cotisationSoldePilier3: 0.3807,
    chargeDe: 'travailleur',
    codeDmfa: '869',
    baseLegale: 'art. 38, § 3novodecies, loi du 29/06/1981',
    source: 'Justel (loi 17/03/2019, art. 22) + ONSS 2026/2 saldo_mobilitybudget.html, lus le 20/08/2026',
    verifie: '2026-08-20',
  },

  // ═══ ATN — AVANTAGES EN NATURE ═══
  atn: {
    // prorataPartiel : mise à disposition partielle (entrée/sortie en cours de mois) → ATN
    // proraté PRORATA TEMPORIS en jours calendaires (1er jour de dispo inclus, jour de
    // restitution exclu ; base annuelle /365, 366 bissextile). Source : FAQ SPF Finances
    // voitures de société Q.3 + art. 36 CIR92 (vérifié 2026-07-03). Implémenté côté générateur
    // via prorataDispoCalendaire() (prorata-temps.js) — le moteur reçoit l'ATN déjà proraté.
    // pctMin/pctMax : bornes du % CO2 dans la formule 5,5% + 0,1%×(CO2−CO2Ref). Électrique (CO2=0) → plancher 4%.
    // vetuste : 100% la 1re année puis −6% par période de 12 mois entamée, plancher 70% (0-12:100 · 13-24:94 · 25-36:88 · 37-48:82 · 49-60:76 · ≥61:70).
    // Source : SPF Finances — voitures de société, revenus 2026 (ex. imp. 2027), vérifié 2026-07-06 (croisé Securex). Registre Garde-fou Sources.
    voiture: { CO2Ref: { essence: 70, diesel: 58, hybride: 58 }, coeff: 0.055, coeffCO2ParGramme: 0.001, pctMin: 0.04, pctMax: 0.18, min: 1690, vetuste: { base: 1.00, decotePar12mois: 0.06, plancher: 0.70 }, formule: '(catalogue x 6/7 x vetuste) x %CO2 / 12', prorataPartiel: { base: 365, joursCalendaires: true, premierJourInclus: true, dernierJourExclu: true } }, // min ATN 2026: 1 690€/an (SPF Finances, +40€ vs 2025)
    logement: { cadastralx100: true, meuble: 1.333 },
    // ✅ TEXTE PRIMAIRE LU LE 23/10/2026 — AR du 2 novembre 2017 modifiant l'AR/CIR 92 (art. 1er : nouvel
    // art. 18, § 3, 10), Justel dossier 2017110202, en vigueur pour les avantages dont dispose un
    // contribuable à partir du 01/01/2018 : PC (fixe ou portable) 72 €/an · tablette ou téléphone mobile
    // 36 €/an · connexion internet (fixe ou mobile, quel que soit le nombre d'appareils) 60 €/an ·
    // abonnement de téléphonie fixe ou mobile 48 €/an. « Ces montants s'appliquent par appareil mis à
    // disposition et doivent être cumulés le cas échéant » (rapport au Roi). Le texte consolidé de
    // l'AR/CIR 92 sur Justel n'est plus mis à jour depuis 2003 : les modifications POSTÉRIEURES à 2017
    // n'ont pas pu y être contrôlées. Valeur FISCALE : l'ONSS peut retenir une autre valorisation.
    gsm: { forfait: 3, mensuel: true },          // tablette / téléphone mobile : 36 €/an
    abonnementTel: { forfait: 4, mensuel: true }, // abonnement téléphonie fixe ou mobile : 48 €/an
    pc: { forfait: 6, mensuel: true },
    internet: { forfait: 5, mensuel: true },
    electricite: { cadre: 1280, noncadre: 580, annuel: true }, // ATN forfait 2026 (Attentia + Wolters Kluwer, indexation) — corr. audit 2026-07-08
    chauffage: { cadre: 2560, noncadre: 1150, annuel: true }, // ATN forfait 2026 (Attentia + Wolters Kluwer, indexation) — corr. audit 2026-07-08
  },

  // ═══ PRÉAVIS — Statut unique Art. 37/2 Loi 03/07/1978 (anc. depuis 01/01/2014) ═══
  // 🛡️ SOURCE UNIQUE — toute table préavis codée en dur ailleurs est INTERDITE (R1).
  //    Calcul = helpers preavisEmployeurSemaines() / preavisTravailleurSemaines() (plus bas).
  // Grille employeur vérifiée 2026-06-25 (Securex + Liantis) :
  //    <3m:1 · 3m:3 · 4m:4 · 5m:5 · 6m:6 · 9m:7 · 1a:8 · 15m:9 · 18m:10 · 21m:11 ·
  //    2a:12 · 3a:13 · 4a:15 · 5a:18 puis +3 sem/an → 19a:60, 20a:62, 21a:63 puis +1 sem/an (25a:67)
  // Grille démission vérifiée 2026-06-25 (Droits Quotidiens / SPF) :
  //    <3m:1 · 3-6m:2 · 6-12m:3 · 12-18m:4 · 18-24m:5 · 2-4a:6 · 4-5a:7 · 5-6a:9 ·
  //    6-7a:10 · 7-8a:12 · ≥8a:13 (plafond 13 sem)
  // RÉFORMES 2026 : (a) contrats débutant ≥ 01/08/2026 → 1 sem pendant les 6 premiers mois
  //    (employeur ET travailleur) — modif art.37/2, MB 15/06/2026
  //    (b) contrats débutant ≥ 01/06/2026 → préavis de LICENCIEMENT plafonné à 52 sem (dès 17a)
  preavis: {
    // Tables d'AFFICHAGE uniquement (la vérité de calcul = les helpers exportés)
    employeur: [
      { label: '< 3 mois', semaines: 1 },
      { label: '3 à 4 mois', semaines: 3 },
      { label: '4 à 5 mois', semaines: 4 },
      { label: '5 à 6 mois', semaines: 5 },
      { label: '6 à 9 mois', semaines: 6 },
      { label: '9 à 12 mois', semaines: 7 },
      { label: '1 an', semaines: 8 },
      { label: '15 mois', semaines: 9 },
      { label: '18 mois', semaines: 10 },
      { label: '21 mois', semaines: 11 },
      { label: '2 ans', semaines: 12 },
      { label: '3 ans', semaines: 13 },
      { label: '4 ans', semaines: 15 },
      { label: '5 ans', semaines: 18 },
      { label: '6 à 19 ans', semaines: '+3 sem/an (21 → 60)' },
      { label: '20 ans', semaines: 62 },
      { label: '21 ans et +', semaines: '63 puis +1 sem/an' },
    ],
    travailleur: [
      { label: '< 3 mois', semaines: 1 },
      { label: '3 à 6 mois', semaines: 2 },
      { label: '6 à 12 mois', semaines: 3 },
      { label: '12 à 18 mois', semaines: 4 },
      { label: '18 à 24 mois', semaines: 5 },
      { label: '2 à 4 ans', semaines: 6 },
      { label: '4 à 5 ans', semaines: 7 },
      { label: '5 à 6 ans', semaines: 9 },
      { label: '6 à 7 ans', semaines: 10 },
      { label: '7 à 8 ans', semaines: 12 },
      { label: '8 ans et +', semaines: 13 },
    ],
    travailleurMax: 13,
    motifGrave: 0,
    // ═══ RECLASSEMENT PROFESSIONNEL — RÉGIME GÉNÉRAL ═══
    // ✅ TEXTE PRIMAIRE LU le 01/09/2026 — SPF Emploi, « Outplacement — régime
    // général » : https://emploi.belgique.be/fr/themes/contrats-de-travail/outplacement/outplacement-regime-general
    //
    // ⚠️ LE SENS COMPTE, ET IL EST CONTRE-INTUITIF. Les 4 semaines se
    // RETRANCHENT de l'indemnité de congé ; elles ne s'y ajoutent pas :
    //   « Le travailleur qui est licencié moyennant indemnité de congé et qui
    //     bénéficie d'une procédure de reclassement professionnel reçoit une
    //     indemnité de congé d'au moins 30 semaines, DIMINUÉE DE 4 SEMAINES
    //     correspondant à la valeur du reclassement professionnel. »
    //   « À partir du 1er janvier 2016, l'indemnité de congé […] est DE TOUTE
    //     FAÇON calculée sur la base d'un délai de préavis réduit de 4
    //     semaines, QUE LE TRAVAILLEUR ACCEPTE OU NON de suivre le
    //     reclassement professionnel. »
    // Donc la réduction ne dépend ni du consentement du travailleur, ni du fait
    // que l'employeur ait réellement commandé la prestation.
    //
    // La VALEUR du reclassement (ce que l'employeur paie au prestataire) est
    // distincte de la réduction : 1/12 de la rémunération brute de l'année
    // civile précédente, plancher 1 800 €, plafond 5 500 €. Le SPF précise que
    // les deux ne coïncident pas nécessairement — « parfois la diminution de
    // 4 semaines sera inférieure, parfois supérieure à cette fraction ».
    // ⚠️ Cette somme ne va JAMAIS au travailleur : « le coût de la procédure de
    // reclassement professionnel est à charge de l'employeur qui en a donné
    // mission au prestataire de service ». Elle n'entre donc pas dans un brut.
    //
    // ⚠️ À ne pas confondre avec `LB.outplacement.cct82bis` (régime
    // PARTICULIER, ≥ 45 ans) : mêmes 1 800 € de plancher, autre régime.
    outplacement: {
      seuil: 30,              // semaines de préavis à partir desquelles le régime général s'applique
      semaines: 4,            // semaines RETRANCHÉES de l'indemnité de congé
      valeurFraction: 1 / 12, // de la rémunération brute de l'année civile précédente
      valeurMin: 1800,        // €
      valeurMax: 5500,        // €
      source: 'SPF Emploi — Outplacement, régime général (loi 05/09/2001 art. 11/1 s., tel que modifié) ; texte lu le 01/09/2026',
    },
    reforme: {
      uneSemaine6mois: { dateDebutMin: '2026-08-01' }, // 1 sem sur 6 mois
      plafond52: { dateDebutMin: '2026-06-01', plafond: 52, ancMinAnnees: 17 },
    },
  },

  // ═══ SUIVI DU TEMPS DE TRAVAIL — ce qu'un système d'enregistrement DOIT faire (29/09/2026) ═══
  // ✅ Texte primaire lu le 29/09/2026 (fetch nu, UA Chrome : http=200, 86 987 octets) — SPF Emploi,
  // « Contrôle des prestations des travailleurs à temps partiel » :
  // https://emploi.belgique.be/fr/themes/reglementation-du-travail/travail-temps-partiel/controle-des-prestations-des-travailleurs
  // Un « système de suivi du temps » remplace le registre des dérogations si sont enregistrés, pour
  // chaque travailleur, « son identité et la période à laquelle les données consignées se rapportent
  // et, d'autre part, par jour, l'heure de début et de fin de ses prestations et de ses pauses
  // enregistrées AU MOMENT où elles commencent et se terminent » ; si « les données consignées durant
  // la période concernée puissent être consultées par les travailleurs concernés et par l'inspection
  // sociale » ; si « les données consignées soient conservées durant CINQ ANS (sur papier ou
  // électroniquement) » ; et si « la délégation syndicale » peut exercer ses prérogatives (CCT n° 5).
  // Base : loi-programme du 22/12/1989, art. 157, 159 et 160-169 + AR du 08/03/1990 (cités par la page).
  // C'est le référentiel de « Mes heures » (portail employé), de l'export entreprise et de la notice
  // art. 13 : pauses horodatées (pas déclarées après coup), relevé consultable, export inspection.
  suiviTempsTravail: {
    conservationAnnees: 5,
    pausesHorodateesAuMoment: true,
    consultableParTravailleur: true,
    consultableParInspection: true,
    consultableParDelegationSyndicale: true,
    source: "SPF Emploi, « Contrôle des prestations des travailleurs à temps partiel », lu le 2026-09-29 — loi-programme 22/12/1989 art. 157-169, AR 08/03/1990",
    dateVerif: '2026-09-29',
  },

  // ═══ HORAIRES VARIABLES À TEMPS PARTIEL — DÉLAI D'ANNONCE ═══
  // ✅ Texte primaire lu le 29/09/2026 (http=200, 90 266 octets) — SPF Emploi, « Travail à temps
  // partiel — Horaires de travail » :
  // https://emploi.belgique.be/fr/themes/reglementation-du-travail/travail-temps-partiel/travail-temps-partiel-horaires-de-travail
  // « l'avis contenant les horaires de travail doit être porté à la connaissance des travailleurs
  // d'une manière fiable, appropriée et accessible ; et cela au moins 7 jours ouvrables à l'avance
  // (sauf si ce délai est modifié via une convention collective de travail rendue obligatoire
  // respectant un délai de trois jours ouvrables minimum). » Base citée par la page : loi-programme
  // du 22/12/1989, art. 159. ⚠️ « Jours ouvrables » au sens du droit du travail : le SAMEDI compte
  // (cf. `joursOuvrables`, loi du 26/12/2022). Le délai se compte comme partout dans le dépôt
  // (rupture-engine.ajouterJoursOuvrables) : le jour de l'annonce ne compte pas.
  // Consommateur : app/lib/annonce-horaires.js (Smart Planning Pro, publication du planning).
  horairesVariables: {
    delaiAnnonceJoursOuvrables: 7,
    delaiAnnonceMinimumCCT: 3,       // plancher qu'une CCT rendue obligatoire peut atteindre
    source: "SPF Emploi, « Travail à temps partiel — Horaires de travail », lu le 2026-09-29 — art. 159 loi-programme 22/12/1989",
    dateVerif: '2026-09-29',
  },

  // ═══ TEMPS DE TRAVAIL ═══
  tempsTravail: {
    dureeHebdoLegale: 38,
    dureeHebdoMax: 38,
    // ── Limites journalières / hebdomadaires & repos (Loi du 16 mars 1971 sur le travail) ──
    // Sources croisées SPF Emploi + Justel, consultées 2026-07-18.
    // https://emploi.belgique.be/fr/themes/reglementation-du-travail/duree-du-travail-et-temps-de-repos/duree-du-travail-et-diminution
    // https://www.ejustice.just.fgov.be/eli/loi/1971/03/16/1971031602/justel
    dureeJournaliereMaxNormale: 8,   // Art. 19 — 8h/jour (hebdo légale ramenée à 38h, loi 10/08/2001)
    dureeJournaliereDerog9h: 9,      // Art. 20 — régime ≤5,5 j/sem (jour/demi-jour de repos autre que le dimanche)
    dureeJournaliereMaxAbsolue: 11,  // Art. 27 — plafond absolu (jusqu'à 12h dans cas équipes/continu via AR/CCT — à valider au cas par cas)
    dureeHebdoMaxAbsolue: 50,        // Art. 27 — plafond hebdo en cas de dérogations autorisées
    reposJournalierMin: 11,          // Art. 38ter §1 — heures consécutives de repos par période de 24h
    reposHebdoMin: 35,               // Art. 38ter §3 — 24h (repos dominical) + 11h (repos journalier)
    // Art. 38quater §3 loi 16/03/1971 — texte lu le 15/09/2026 (refli.be/fr/lex/1971031602,
    // reproduction de Justel) : « A défaut de convention collective de travail conclue en
    // application du § 2 ou d'arrêté royal, le travailleur se voit accorder au moins un
    // quart d'heure de pause au plus tard au moment où la durée des prestations atteint
    // 6 heures. » Minimum SUPPLÉTIF : une CCT (§2) fixe la durée et les modalités.
    // ⚠️ Ce commentaire citait la « CCT n°46 CNT » jusqu'au 15/09/2026 : la loi fixe ce
    // minimum elle-même.
    pauseApres6h: { seuilHeures: 6, dureeMin: 15 },
    heuresSupp: {
      majoration50: 0.50, majoration100: 1.00, recuperation: true, plafondAnnuel: 120, plafondVolontaire: 360,
      // ── SEUILS DU SURSALAIRE — art. 29, § 2, loi du 16/03/1971 ✅ texte primaire lu le
      // 03/10/2026 sur refli.be/fr/lex/1971031602 (reproduction du Justel, pas le Moniteur) :
      // « Est à considérer comme travail supplémentaire […] tout travail effectué au-delà de
      // 9 heures par jour ou de 40 heures par semaine, ou des limites inférieures fixées
      // conformément à l'article 28. » Les limites inférieures (CCT, art. 28) ne sont PAS
      // portées ici : une CP peut abaisser le seuil, jamais le relever.
      seuilJourHeures: 9,
      seuilSemaineHeures: 40,
      // § 4 (même lecture) : une CCT peut remplacer le sursalaire par un repos compensatoire
      // complémentaire — ½ h par heure à 50 %, 1 h par heure à 100 %. Ce N'EST PAS le repos
      // compensatoire de l'art. 26bis (ci-dessous), qui vient en plus.
      reposComplementaireCCT: { parHeure50: 0.5, parHeure100: 1 },
      // Art. 26bis, § 1 (même lecture) : les dépassements ne sont autorisés qu'à condition
      // que, sur un TRIMESTRE, la moyenne ne dépasse pas 40 h/semaine → les heures sup se
      // récupèrent dans le trimestre (période prolongeable à 1 an par AR, CCT ou RT).
      moyenneTrimestreMaxHebdo: 40,
      periodeReferenceRecuperationMois: 3,
      sourceSeuils: 'Loi du 16/03/1971 sur le travail, art. 29 §§ 1-2-4 et art. 26bis § 1 — refli.be/fr/lex/1971031602 (reproduction Justel), lu le 2026-10-03',
      dateVerifSeuils: '2026-10-03',
    },
    nuit: { debut: '20:00', fin: '06:00', majoration: 0 },
    dimanche: { majoration: 1.00, repos: true },
    jourFerie: {
      // ⚠️ CORRIGÉ LE 15/09/2026 — `majoration` valait 2.00. Même convention que
      // `dimanche.majoration` (1.00 = + 100 %) et `heuresSupp.majoration100` : c'est un
      // SUPPLÉMENT, pas un multiplicateur — 2.00 voulait donc dire + 200 %, le double de
      // la loi. Art. 29 §1 loi 16/03/1971 (texte lu le 15/09/2026, refli.be) : le
      // sursalaire de 50 % « est portée à 100 p.c. lorsque le travail supplémentaire est
      // effectué un dimanche ou pendant les jours de repos accordés en vertu de la
      // législation sur les jours fériés ». ⚠️ Il vise le travail SUPPLÉMENTAIRE ; le
      // travail ordinaire presté un jour férié ouvre le repos compensatoire ci-dessous
      // (loi du 04/01/1974, art. 11). Aucun code ne lisait cette valeur au 15/09/2026.
      nombre: 10, majoration: 1.00, remplacement: true,
      // Les 10 jours fériés légaux belges — Source : loi du 04/01/1974 relative aux
      // jours fériés, art. 1er ; modalités d'exécution AR du 18/04/1974.
      // 4 sont MOBILES : définis par un décalage en jours depuis le dimanche de Pâques
      // (calculé, pas stocké — cf. jours-feries.js). 6 sont à date fixe.
      // ⚠️ R1 : cette liste est la SOURCE. Ne jamais recopier une date ailleurs.
      liste: [
        { id: 'nouvel_an',      nom: { fr: 'Nouvel An',        nl: 'Nieuwjaar' },                        mois: 1,  jour: 1 },
        { id: 'paques_lundi',   nom: { fr: 'Lundi de Pâques',  nl: 'Paasmaandag' },                      offsetPaques: 1 },
        { id: 'fete_travail',   nom: { fr: 'Fête du Travail',  nl: 'Dag van de Arbeid' },                mois: 5,  jour: 1 },
        { id: 'ascension',      nom: { fr: 'Ascension',        nl: 'Hemelvaart' },                       offsetPaques: 39 },
        { id: 'pentecote_lundi',nom: { fr: 'Lundi de Pentecôte', nl: 'Pinkstermaandag' },                offsetPaques: 50 },
        { id: 'fete_nationale', nom: { fr: 'Fête nationale',   nl: 'Nationale feestdag' },               mois: 7,  jour: 21 },
        { id: 'assomption',     nom: { fr: 'Assomption',       nl: 'Onze-Lieve-Vrouw-Hemelvaart' },      mois: 8,  jour: 15 },
        { id: 'toussaint',      nom: { fr: 'Toussaint',        nl: 'Allerheiligen' },                    mois: 11, jour: 1 },
        { id: 'armistice',      nom: { fr: 'Armistice',        nl: 'Wapenstilstand' },                   mois: 11, jour: 11 },
        { id: 'noel',           nom: { fr: 'Noël',             nl: 'Kerstmis' },                         mois: 12, jour: 25 },
      ],
      source: 'Loi du 04/01/1974 relative aux jours fériés, art. 1er — modalités AR 18/04/1974. Vérifié 2026-08-03.',
      // Jour férié tombant APRÈS la fin du contrat : reste à charge de l'employeur
      // dans les 14 jours si occupation de 15 jours à 1 mois, dans les 30 jours
      // si occupation > 1 mois. Source : AR 18/04/1974, art. 14. Vérifié 2026-08-03.
      apresFinContrat: { joursSiOccupation15jA1mois: 14, joursSiOccupationPlus1mois: 30, seuilOccupationJours: 15 },
      // ── TRAVAILLER UN JOUR FÉRIÉ : ce que ça ouvre, et en combien de temps ──
      //
      // ⚠️ LA RÈGLE DU TEMPS PARTIEL N'EST PAS CELLE DU TEMPS PLEIN, et c'est
      // le piège. Le régime général (loi, art. 11 al. 2) donne une JOURNÉE
      // ENTIÈRE dès que le travail a duré plus de quatre heures. Un arrêté
      // ultérieur y déroge pour les temps partiels : leur repos est égal à la
      // DURÉE RÉELLEMENT PRESTÉE, ni plus ni moins. Appliquer le régime général
      // à une travailleuse à 20 h/semaine lui donnerait un jour complet là où
      // la loi lui donne ses 6 h 30 — et l'inverse est tout aussi faux.
      //
      // Textes primaires lus le 24/08/2026 sur Justel :
      //  · loi du 04/01/1974, art. 11 al. 1, 2, 6 et 8
      //    https://www.ejustice.just.fgov.be/eli/loi/1974/01/04/1974010407/justel
      //  · AR du 18/04/1974, art. 1bis (inséré par AR du 12/08/1981, art. 3)
      //    https://www.ejustice.just.fgov.be/eli/arrete/1974/04/18/1974041801/justel
      // ⚠️ Ces pages sont servies en ISO-8859-1 : décodées en UTF-8, les
      // accents cassent et la recherche ne trouve rien.
      reposCompensatoire: {
        // Temps plein — art. 11 al. 2.
        seuilHeuresJourneeEntiere: 4,   // > 4 h prestées → journée entière
        // ≤ 4 h → demi-journée AU MOINS, à prendre avant ou après 13 h, et ce
        // jour-là la durée du travail ne peut excéder 5 heures.
        demiJourneeAvantApres: 13,
        demiJourneeDureeMaxCeJour: 5,
        // Temps partiel — AR 18/04/1974 art. 1bis. Aucun seuil, aucun arrondi :
        // la durée du repos est celle du travail effectué ce jour-là.
        tempsPartielDureeReelle: true,
        // Délai d'octroi — art. 11 al. 6. Et si un préavis court pendant cette
        // période, le repos doit être accordé avant son expiration (al. 8).
        delaiSemaines: 6,
        avantExpirationPreavis: true,
        // Formalité que personne ne connaît — art. 13 §3 : le fonctionnaire
        // désigné doit être averti au plus tard huit jours après le jour férié
        // presté, ET en tout cas avant le jour où le repos est accordé.
        avertirFonctionnaireJours: 8,
        source: 'Loi du 04/01/1974 art. 11 et 13 §3 + AR du 18/04/1974 art. 1bis (AR 12/08/1981 art. 3) — textes primaires lus sur Justel le 24/08/2026.',
      },
    },
    // ⚠️ CORRIGÉ LE 22/08/2026 — LE DEUIL D'UN CONJOINT OU D'UN ENFANT VAUT DIX
    // JOURS, PAS TROIS. Le fichier portait 3 jours depuis l'origine ; la loi du
    // 27/06/2021 a porté ce cas à dix jours (3 entre le décès et les funérailles
    // + 7 à prendre dans l'année). L'écran « Situations de vie » annonçait donc
    // 3 jours à un travailleur qui venait de perdre son conjoint ou son enfant :
    // sept jours de trop peu, sur l'événement le plus sensible de la liste.
    // ✅ Texte primaire lu le 22/08/2026 : AR du 28/08/1963, art. 2, version
    // consolidée Justel (ejustice, cgi_loi/change_lg.pl?cn=1963082801) — les cas
    // V, V./1, VI, VII et VII/1 sont transcrits un par un ci-dessous.
    // Repéré par recoupement : la CCT du 01/04/2026 de la CP 341 écrit
    // « 10 jours supplémentaires, soit un total de 20 jours » — un total de 20
    // est arithmétiquement impossible sur une base de 3.
    petitChomageDetail: {
      mariageTravailleur: 2,        // I — 2 jours, dans la semaine de l'événement ou la suivante
      mariageEnfant: 1,             // II — le jour du mariage
      mariageProche: 1,             // II — frère/soeur, beau-frère/belle-soeur, parent/beau-parent, petit-enfant
      // V — décès du conjoint OU du partenaire cohabitant, d'un enfant du
      // travailleur ou de son conjoint/partenaire : DIX jours, dont 3 à choisir
      // entre le jour du décès et le jour des funérailles et 7 dans l'année qui
      // suit le décès. Dérogation possible aux deux périodes de commun accord.
      decesConjoint: 10,
      decesConjointImmediat: 3,     // la part à prendre entre le décès et les funérailles
      decesConjointDiffere: 7,      // la part à prendre dans l'année du décès
      decesEnfant: 10,              // V — même cas, même durée que le conjoint
      decesEnfantImmediat: 3,
      decesEnfantDiffere: 7,
      decesParent: 3,               // V./1 — père/mère/beau-père/belle-mère (du travailleur ou du conjoint)
      // VI et VII — la durée dépend de la COHABITATION, pas du lien de parenté :
      // 2 jours si la personne habitait chez le travailleur, 1 seul jour sinon
      // (et ce jour-là se prend le jour des funérailles).
      decesFrereSoeur: 2,           // VI — cohabitant
      decesFrereSoeurNonCohabitant: 1, // VII — ne vivait pas chez le travailleur
      decesGrandParent: 2,          // VI — cohabitant
      decesGrandParentNonCohabitant: 1,
      decesPetitEnfant: 2,          // VI — cohabitant
      decesPetitEnfantNonCohabitant: 1,
      decesEnfantPlaceCourteDuree: 1, // VII/1 — enfant placé en placement de courte durée
      communion: 1,                 // VIII et IX — communion solennelle ou fête de la jeunesse laïque
      ordination: 1,                // III — ordination ou entrée au couvent d'un enfant (du travailleur ou de son conjoint), d'un frère, d'une sœur, d'un beau-frère, d'une belle-sœur — le jour de la cérémonie
      // « Le temps nécessaire », avec un PLAFOND écrit dans l'AR (art. 2, lu le 22/10/2026) : X et Xbis milicien ou objecteur (3 jours) ;
      // XII jury, témoin, comparution ordonnée par la juridiction du travail (5 jours) ; XIIter élections européennes et XIII dépouillement (5 jours).
      // XIIbis (assesseur d'un bureau de vote, élections législatives, provinciales, communales) : le temps nécessaire, SANS plafond.
      tempsNecessaireMilicien: 3,
      tempsNecessaireJuryTemoin: 5,
      tempsNecessaireEuropeennesOuDepouillement: 5,
      demenagement: 1,              // hors AR : uniquement si une CCT sectorielle le prévoit
      source: "AR du 28/08/1963, art. 2 (petit chômage), version consolidée lue sur Justel le 22/08/2026 — cas V (10 j), V./1 (3 j), VI (2 j cohabitant), VII (1 j non-cohabitant), VII/1 (1 j enfant placé), ordination et temps nécessaire plafonné (relus le 22/10/2026) ; loi du 27/06/2021 pour le passage à 10 jours. Maintien du salaire : loi du 03/07/1978, art. 30. Les CCT sectorielles peuvent accorder des jours EN PLUS, jamais en moins.",
    },
    // ⚠️ VUE COURTE — DÉRIVÉE, jamais saisie à la main. Elle portait autrefois ses
    // propres nombres (« deces1: 3 »), donc une seconde vérité qui pouvait mentir
    // sans que personne ne la lise. Elle se calcule maintenant depuis le détail
    // ci-dessus : une seule valeur à corriger le jour où la loi bouge.
    get petitChomage() {
      const d = this.petitChomageDetail
      return {
        mariage: d.mariageTravailleur,
        deces1: d.decesConjoint,   // conjoint, partenaire cohabitant ou enfant
        deces2: d.decesFrereSoeurNonCohabitant,
        communion: d.communion,
        demenagement: d.demenagement,
      }
    },
    // ✅ AUTRES ABSENCES LÉGALES — textes primaires lus sur Justel et au CNT le 22/10/2026 (registre docs/20-29 Compliance/20.26).
    // Aucune de ces valeurs n'est à recopier : les écrans et le DmfA les lisent ici.
    absencesLegalesDetail: {
      // Raisons impérieuses : CCT n° 45 du CNT art. 4 §1 (10 jours de travail par année civile) et §2 (temps partiel : réduit
      // proportionnellement à la durée des prestations) ; loi du 03/07/1978 art. 30bis §1. Non rémunérées sauf disposition conventionnelle (art. 2 §1).
      raisonsImperieuses: { joursParAn: 10, tempsPartielProportionnel: true, remunere: false },
      // Congé d'aidant : loi du 03/07/1978 art. 30bis §2 — 5 jours par année civile, IMPUTÉS sur le crédit des raisons impérieuses.
      aidant: { joursParAn: 5, imputeSurRaisonsImperieuses: true, remunere: false },
      // Congé d'accueil : loi du 03/07/1978 art. 30quater (5 jours) portée à SIX par l'AR du 27/10/2008 art. 1 ; crédit par FAMILLE d'accueil.
      accueil: { joursParAn: 6, parFamille: true, remunere: false },
      // Congé de naissance : loi du 03/07/1978 art. 30 §2 — à prendre dans les 4 mois du jour de l'accouchement. ⚠️ Le NOMBRE de jours (20) et la part
      // de l'employeur (3) vivent UNE seule fois, dans `indemnitesIncapacite.maternite` (naissanceJoursTotal, naissanceJoursEmployeur) : ne pas les redoubler ici.
      naissance: { delaiMois: 4 },
      // Congé de maternité : loi du 16/03/1971 art. 39 à 42 — durée écrite sur la page « Congé de maternité » du SPF Emploi (emploi.belgique.be/fr/themes/jours-feries-et-conges/conge-de-maternite),
      // lue le 23/10/2026 : « un congé de maternité d'une durée de 15 semaines ; jumeaux (grossesse multiple) : en principe 17 semaines, prolongeable jusqu'à 19 semaines ».
      // ✅ ARTICLE 39 LU le 23/10/2026 (banque Justel du SPF Justice : ejustice.just.fgov.be/cgi_loi/article.pl, numac_search=1971031602, cn_search=1971031602 ; identique à la copie refli.be) : prénatal dès la 6e semaine avant l'accouchement (8e si naissance multiple), repos postnatal de 9 semaines,
      // prolongation de 2 semaines pour une naissance multiple. ⚠️ L'article n'écrit JAMAIS « 15 », « 17 » ou « 19 » : ce sont des additions (6 + 9, 8 + 9, 8 + 9 + 2) que le SPF Emploi écrit tel quel.
      maternite: { semaines: 15, semainesMultiples: 17, semainesMultiplesMax: 19 },
      // ── LES AUTRES RUBRIQUES « CONGÉS » DU SPF EMPLOI (23/10/2026) — pages ONEM et SPF Emploi lues le 23/10/2026, loi du 05/03/2017 lue dans le Moniteur belge du 15/03/2017 (numac 2017011012, ejustice.just.fgov.be/cgi/article_body.pl), AR du 25/11/1991 dans la banque Justel du SPF Justice ──
      // Crédit-temps (secteur privé) : droit chez l'employeur = CCT n° 103 du 27/06/2012 (AR du 25/08/2012) ; allocations = loi du 10/08/2001 et AR du 12/12/2001. Les pages ne citent aucun article de la CCT.
      // 51 mois de crédit-temps avec motif « soins » (36 avant la CCT n° 103ter du 20/12/2016), 36 mois pour la formation ; le crédit-temps SANS motif n'existe plus (CCT n° 103ter, demandes dès le 01/04/2017).
      // Droit chez un employeur de plus de 10 travailleurs (quota d'absences simultanées de 5 %, modifiable par CCT ou règlement de travail) ; jusqu'à 10 travailleurs, l'accord de l'employeur est nécessaire.
      creditTemps: { moisSoins: 51, moisFormation: 36, ancienneteMois: 24, seuilTravailleursDroit: 10, quotaAbsencesPourcent: 5 },
      // Vacances jeunes (ONEM, feuille info « Avez-vous droit aux vacances jeunes ? ») : moins de 25 ans, études terminées ; vacances seniors : 50 ans au moins, reprise d'une occupation salariée.
      // Les deux complètent les vacances ordinaires jusqu'à 4 semaines ; allocation de l'ONEM à 65 % du salaire brut plafonné (le plafond est indexé : pas recopié ici).
      vacancesJeunes: { semainesTotal: 4, ageMoinsDe: 25, allocationPourcent: 65 },
      vacancesSeniors: { semainesTotal: 4, ageMin: 50, allocationPourcent: 65 },
      // Don de congés conventionnels : loi du 05/03/2017 art. 40 à 49 — enfant de moins de 21 ans (art. 43 §1), demande de 2 semaines au plus, renouvelable (art. 45), rémunération maintenue (art. 49).
      donConges: { semainesParDemande: 2, ageEnfantMoinsDe: 21 },
      // Compte épargne-carrière : loi du 05/03/2017 art. 33 à 39, cadre en vigueur le 01/02/2018 (SPF Emploi) ; aucun plafond de jours dans la loi, il vient de la CCT.
      compteEpargneCarriere: { depuis: '2018-02-01' },
      // ── PHASE 2 (23/10/2026) — textes lus au Moniteur (Justel) le 22/10/2026 puis relus le 23/10/2026 ─────────────────────────────────────
      // Congé d'adoption : loi du 03/07/1978 art. 30ter §1 — 6 semaines par parent adoptif, période ininterrompue, semaines entières ; durée DOUBLÉE si
      // l'enfant a une incapacité d'au moins 66 % ; +2 semaines par parent adoptif en cas d'adoption simultanée de plusieurs enfants (non doublées) ;
      // allongement PARTAGÉ entre les parents : +1 (01/01/2019), +2 (2021), +3 (2023), +4 (2025), +5 (01/01/2027), seulement pour une demande introduite ET un
      // congé commençant à partir de la date concernée (§1, al. 2 à 4). Doit commencer dans les 2 mois de l'inscription de l'enfant au registre (§1er/1) ;
      // avertissement écrit au moins 1 mois à l'avance (§3). AR du 21/09/2004 art. 1 : les 3 premiers jours du congé sont à charge de l'employeur.
      // ⚠️ L'AR ne dit PAS « jours calendrier » (seule la page du SPF Emploi l'écrit) : le type de jour reste 🟠, donc la DmfA n'est pas câblée.
      adoption: {
        semainesParParent: 6,
        multiplicateurHandicap: 2,
        semainesPlusieursEnfants: 2,
        allongementPartage: [
          { depuis: '2019-01-01', semaines: 1 },
          { depuis: '2021-01-01', semaines: 2 },
          { depuis: '2023-01-01', semaines: 3 },
          { depuis: '2025-01-01', semaines: 4 },
          { depuis: '2027-01-01', semaines: 5 },
        ],
        delaiInscriptionMois: 2,
        avertissementMois: 1,
        joursEmployeur: 3,
      },
      // Congé parental d'accueil : loi du 03/07/1978 art. 30sexies — placement de LONGUE durée, droit UNIQUE par enfant, mêmes 6 semaines et mêmes allongements
      // que l'adoption ; doit commencer dans les 12 mois de l'inscription (§2) ; AR du 01/03/2019 art. 2 : les 3 premiers jours à charge de l'employeur.
      parentalAccueil: { delaiInscriptionMois: 12, joursEmployeur: 3 },
      // Soins palliatifs : loi du 22/01/1985 art. 100bis §3 — suspension d'UN mois par patient, prolongeable DEUX fois d'un mois (AR du 22/03/1995 : modalités).
      palliatifs: { moisParPatient: 1, prolongations: 2 },
      // Assistance à un proche gravement malade : AR du 10/08/1998 art. 6 — suspension 12 mois par patient, réduction 24 mois, périodes de 1 à 3 mois.
      assistance: { suspensionMois: 12, reductionMois: 24, periodeMinMois: 1, periodeMaxMois: 3 },
      // Aidant proche RECONNU : loi du 22/01/1985 art. 100ter §3 et 102ter (« droit futur », en vigueur le 01/07/2026) — suspension de 6 mois par personne aidée,
      // plafond de 6 mois sur la carrière ; 6 mois de suspension = 12 mois de réduction de moitié = 30 mois de réduction d'1/5 ; avertissement écrit 7 jours avant.
      aidantProche: { depuis: '2026-07-01', suspensionMois: 6, miTempsMois: 12, cinquiemeMois: 30, plafondCarriereMoisSuspension: 6, avertissementJours: 7 },
      // Congé-éducation payé : loi du 22/01/1985 art. 114 §2 (rémunération normale plafonnée) ; le plafond est indexé chaque 1er septembre (loi du 05/11/2023 art. 12).
      // Valeur 2026-2027 lue sur la page du SPF Emploi « congé-éducation payé » le 22/10/2026, recoupée avec Partena. À relire chaque 1er septembre.
      educationPaye: { plafondBrutMensuel: 3864, anneeScolaire: '2026-2027', remboursePar: 'régions' },
      source: "Loi du 03/07/1978 art. 30 §2, 30bis §1-2, 30quater, 30ter, 30sexies (Justel) ; loi du 22/01/1985 art. 100bis, 100ter, 102ter, 114 ; AR du 10/08/1998 art. 6 ; AR du 21/09/2004 art. 1 ; AR du 01/03/2019 art. 2 ; CCT n° 45 du CNT art. 2 §1, 4 §1-2 ; AR du 27/10/2008 art. 1 — lus le 22/10/2026, relus le 23/10/2026. Registre : docs/20-29 Compliance/20.26.",
    },
  },

  // ═══ CONTRATS ═══
  contrats: {
    periodeEssai: { supprimee: true, exception: 'travail etudiant/interim/occupation temporaire' },
    clauseNonConcurrence: { dureeMax: 12, brut_min: 44447, brut_max: 88895, indemniteMin: 0.50, source: 'Art. 65 Loi 03/07/1978 — seuils 2026 (Securex/Partena) : nulle < 44.447€, CCT entre 44.447 et 88.895€, libre > 88.895€' },
    ecolecholage: { dureeMax: 36, brut_min: 44447, formationMin: 80 }, // seuil brut 2026 aligné clause écolage (SPF Emploi — même plafond que non-concurrence) — corr. audit 2026-07-08
  },

  // ═══ DROIT INDIVIDUEL À LA FORMATION (DIF) — « loi Deal » ═══
  // Loi du 03/10/2022 portant des dispositions diverses relatives au travail.
  // Vérifié le 2026-07-21 (gate R2) :
  //   SPF Emploi — https://emploi.belgique.be/fr/themes/formation/droit-individuel-la-formation/droit-individuel-la-formation-information-pour-les-0
  //   Justel     — https://www.ejustice.just.fgov.be/cgi_loi/change_lg.pl?language=fr&la=F&table_name=loi&cn=2022100306
  // ⚠️ Le régime général est SUPPLÉTIF : une CCT sectorielle prime et peut prévoir
  //    MOINS que 5 jours (trajectoire de croissance), sans descendre sous 2 jours/an.
  //    Règle de calcul : jours = CCT sectorielle ?? régime général(taille).
  //    NE JAMAIS faire Math.max(sectoriel, 5) — cela sur-déclarerait les CP dont la
  //    trajectoire sectorielle est légalement inférieure (ex. CP 149.01, CP 121).
  formation: {
    dif: {
      // Jours/an et par travailleur, entreprise à temps plein, selon la taille (en ETP).
      joursParAn: { moins10: 0, de10a19: 1, min20: 5 },
      joursParAn_historique: { '2023': { min20: 4 }, '2024': { min20: 5 } }, // 5 j dès le 01/01/2024
      plancherCCT: 2,              // une CCT sectorielle ne peut pas descendre sous 2 j/an
      exclusionMoins10: true,      // employeurs < 10 travailleurs : hors champ du chapitre
      seuil: {
        unite: 'ETP',              // équivalents temps plein — PAS un comptage par têtes
        periodeReference: 'T4 (n-2) + T1 à T3 (n-1) — moyenne des ETP de fin de trimestre / nombre de trimestres déclarés à l\'ONSS',
      },
      prorata: {
        formule: 'A x B x C',      // A = jours de l'entreprise à temps plein, B = régime / temps plein, C = mois occupés / 12
        moisEntameCompte: true,    // un mois entamé compte pour un mois entier — confiance MOYENNE (source unique : FAQ SPF)
      },
      report: {
        cycleAns: 5,
        premierCycle: { debut: '2024-01-01', fin: '2028-12-31' },
        note: 'Les jours non pris sont reportés ; le solde est remis à zéro à la fin de chaque cycle de 5 ans. Confiance MOYENNE (FAQ SPF).',
      },
      // ⚠️ Numéro d'article NON tranché : le SPF cite « article 50 », la numérotation
      //    Justel renvoie 51/52. Ne PAS afficher de numéro d'article tant que non vérifié.
      article: null,
      nonRegressionSectorielle: null, // à vérifier — formulation SPF ambiguë, ne pas en faire une règle bloquante
      source: 'SPF Emploi + Justel (loi 03/10/2022) — vérifié 2026-07-21',
    },
    plan: {
      seuilTravailleurs: 20,       // idem LB.seuils.planFormation (source unique ici)
      echeanceAnnuelle: '03-31',   // plan arrêté au plus tard le 31 mars
      avisConseilEntrepriseAvant: '03-15',
      baseLegale: 'Loi 03/10/2022 + AR 14/07/2024 (exécution), en vigueur le 02/09/2024',
      depot: 'transfert.emploi.belgique.be — dans le mois suivant l\'établissement du plan',
      source: 'SPF Emploi — https://emploi.belgique.be/fr/themes/formation/plans-de-formation (vérifié 2026-07-21)',
    },
    fla: {
      // Federal Learning Account — supprimé, ne plus le proposer ni l'alimenter.
      aboli: true,
      dateAbolition: '2026-01-01',
      note: 'Supprimé par la loi du 14/01/2026 portant dispositions diverses. Effacement des données Sigedis prévu au 01/01/2027 ; un dispositif ILA est annoncé pour le 01/01/2027. Sources secondaires concordantes (Securex, Partena, Liantis) — texte au Moniteur non lu directement.',
    },
  },

  // ═══ SEUILS SOCIAUX ═══
  seuils: {
    electionsSociales: { cppt: 50, ce: 100 },
    planFormation: 20,             // = LB.formation.plan.seuilTravailleurs (SPF Emploi, vérifié 2026-07-21)
    bilanSocial: 20,
    reglementTravail: 1,
    delegationSyndicale: { cp200: 50 },
    servicePPT: { interne: 20 },
    conseillerPrevention: { interne: 20 },
  },

  // ═══ ASSURANCES ═══
  assurances: {
    accidentTravail: { taux: 0.01, obligatoire: true,
      // Rémunération de base Fedris 2026 (vérifié 2026-06-25, fedris.be) + taux incap. temporaire
      remBaseMax: 58096.10, remBaseMin: 8666.78, tauxIncapaciteTemporaire: 0.90,
      source: 'Fedris — rém. base AT 2026 : max 58.096,10€ / min 8.666,78€ ; IT 90%',
      // ✅ DÉLAI DE DÉCLARATION — ajouté le 15/09/2026. La loi du 10/04/1971 (art. 62)
      // renvoie au Roi (« dans les délais fixés par le Roi ») : c'est l'AR du 12/03/2003
      // « établissant le mode et le délai de déclaration d'accident du travail », art. 2
      // al. 1 (mod. AR 03/07/2005, en vigueur le 01/01/2006). Texte lu le 15/09/2026 sur
      // refli.be/fr/lex/2003022292 (reproduction de Justel, mise à jour au 03/05/2024) :
      //   « L'employeur, son préposé ou mandataire fait la déclaration, visée à l'article
      //     62 de la loi, auprès de l'entreprise d'assurances dans les (huit jours) à
      //     compter du jour qui suit celui de l'accident. »
      // Des jours CALENDRIER : le texte ne dit pas « ouvrables » — plusieurs écrans
      // disaient « 8 jours ouvrables » (corrigés le même jour).
      // Al. 3 (AR 19/03/2014) : l'accident BÉNIN inscrit au registre (Code du bien-être
      // au travail, art. I.5-6 §3) ne se déclare pas ; s'il s'aggrave, même délai à
      // compter du lendemain du jour où l'employeur est informé de l'aggravation.
      declaration: {
        delaiJours: 8,
        pointDepart: 'lendemain_accident',
        joursCalendrier: true,
        accidentBeninRegistreDispense: true,
        source: "AR du 12/03/2003 établissant le mode et le délai de déclaration d'accident du travail, art. 2 (exécute l'art. 62 de la loi du 10/04/1971) — texte lu le 15/09/2026 sur refli.be/fr/lex/2003022292 (reproduction de Justel).",
        dateVerif: '2026-09-15',
        // ✅ SANCTION DU DÉFAUT DE DÉCLARATION — ajouté le 15/09/2026. Code pénal social,
        // art. 223, § 1er, 3° (texte consolidé Justel à jour au 01/06/2026, lu le 15/09/2026
        // sur refli.be/fr/lex/2010A09589) : « Est puni d'une sanction de niveau 3 : […] 3°
        // l'employeur, son préposé ou son mandataire qui n'a pas déclaré, de la manière et
        // dans les délais fixés par le Roi, à l'assureur compétent […] tout accident […].
        // Lorsque les faits sont commis délibérément, la sanction est de niveau 4. […]
        // l'amende est multipliée par le nombre de travailleurs concernés. »
        // ⚠️ Plusieurs écrans affichaient « amende 400-4.000 € » : ni ce niveau, ni un
        // montant daté. Les montants de l'art. 101 dépendent de la DATE DES FAITS (réforme
        // au 01/09/2026, décimes ×1,25 — SPF Emploi) : ils ne sont volontairement pas ici.
        sanction: {
          codePenalSocial: 'art. 223, § 1er, 3°',
          niveau: 3,
          niveauSiDelibere: 4,
          multiplieParTravailleur: true,
          source: "Code pénal social, art. 223, § 1er, 3° et alinéas suivants — texte consolidé lu le 15/09/2026 sur refli.be/fr/lex/2010A09589 (reproduction de Justel, à jour au 01/06/2026).",
          dateVerif: '2026-09-15',
        },
      },
      // ✅ ACCIDENT DU TRAVAIL GRAVE — ajouté le 15/09/2026. Textes lus le même jour sur
      // refli.be (loi du 04/08/1996 = 1996012650 ; Code du bien-être au travail =
      // 2017A10461, recoupé sur le PDF du SPF Emploi, livre I, titre 6) :
      //   · art. 94ter § 1er de la loi : l'employeur « fournit dans les dix jours qui
      //     suivent l'accident un rapport circonstancié aux fonctionnaires » chargés de
      //     la surveillance — l'INSPECTION du bien-être au travail, JAMAIS Fedris ;
      //   · art. 94nonies de la loi + art. I.6-3 du Code : déclaration IMMÉDIATE à ces
      //     fonctionnaires, mais seulement pour l'accident mortel (I.6-2, 1°) et celui
      //     qui cause une lésion PERMANENTE (I.6-2, 2°, a) ;
      //   · l'art. 94bis n'est qu'une DÉFINITION ; les critères sont à l'art. I.6-2.
      // ⚠️ L'app écrivait « rapport circonstancié Fedris dans 10 jours » et « hospitalisation
      // plus de 24h » : ni ce destinataire ni ce critère ne figurent dans les textes. Le
      // texte dit « dix jours » sans préciser calendrier ou ouvrables : ne rien y ajouter.
      accidentGrave: {
        rapportCirconstancieJours: 10,
        declarationImmediate: ['deces', 'lesionPermanente'],
        destinataire: 'fonctionnaires chargés de la surveillance du bien-être au travail',
        source: "Loi du 04/08/1996 relative au bien-être des travailleurs, art. 94bis, 94ter § 1er et 94nonies ; Code du bien-être au travail, art. I.6-2 et I.6-3 — textes lus le 15/09/2026 sur refli.be (1996012650, 2017A10461), reproductions de Justel.",
        dateVerif: '2026-09-15',
      },
    },
    medecineTravail: { cout: 91.50, parTravailleur: true, annuel: false },
    assuranceLoi: { obligatoire: true },
    assuranceGroupe: { deductible: true, plafond80pct: true },
  },

  // ═══ ALLOCATIONS FAMILIALES (Région Bruxelles) ═══
  // ⚠️ CORRIGÉ LE 31/08/2026 — ce bloc était FAUX et, pire, il ne portait NI
  // source NI date, ce que R2 interdit. Il annonçait une prime de naissance de
  // 1 214,73 € là où Famiris en verse 1 395,02 € : **180 € de moins** dit à un
  // client, sur une prime qu'il touche une fois par enfant.
  //
  // Source PRIMAIRE lue le 31/08/2026 — Famiris, « Montants des allocations
  // familiales bruxelloises », qui précise « montants indexés selon l'indice en
  // vigueur depuis le 01/03/2026 » :
  //   https://famiris.brussels/fr/faq/paiements-allocations-familiales/montants-allocations-familiales/
  //     · naissance : 1 395,02 € (1er, et par enfant en cas de naissance
  //       multiple) · 634,10 € (suivants) · adoption : mêmes montants ;
  //     · base, né À PARTIR du 01/12/2019 : 190,23 € (0-11) · 202,91 € (12-17)
  //       · 202,91 € (18-24).
  //
  // ⚠️ DEUX RÉGIMES COEXISTENT, et ce bloc ne décrit que le NOUVEAU. Un enfant
  // né AVANT le 01/12/2019 touche 177,55 € (0-11) et 190,23 € (12-17). Le
  // découpage par date de naissance vit dans `AF_REGIONS.BXL`, pas ici : ne
  // jamais servir ces montants-ci à un dossier sans avoir regardé la date.
  allocFamBxl: {
    base: { montant: 194.04, parEnfant: true },
    // 206,98 − 194,04 : le supplément 12-17 ans, exprimé en ÉCART comme le
    // reste du fichier. L'ancienne valeur (29,64) donnait 200,72 au total,
    // soit 2,19 € sous le montant réel.
    supplement1218: 12.94,
    // (31/08/2026 : le supplément social était noté « non relu » ; il figure bien sur la page des montants, voir ci-dessous.)
    // ✅ SUPPLÉMENT SOCIAL — Famiris (source PRIMAIRE, relue le 24/10/2026, montants « depuis le 01/09/2026 »).
    // Condition : revenu cadastral ≤ 2 000 € (non indexés), puis trois bandes de revenu annuel imposable du
    // ménage. Les montants sont ceux de la page, colonne par colonne = nombre d'enfants du ménage.
    // L'ancienne valeur (plafond 35 978 €, 54,38 €) ne correspondait à aucune ligne de la page.
    supplementSocial: {
      revenuCadastralMax: 2000,
      plafondRevenuPlein: 41399.40,     // en dessous : barème plein
      plafondRevenuReduit: 60095.91,    // entre les deux : barème réduit ; au-dessus : 0
      plein: {
        monoparentale:    { age0_11: { enfants1: 51.74, enfants2: 103.49, enfants3plus: 168.17 }, age12_24: { enfants1: 64.68, enfants2: 116.42, enfants3plus: 181.10 } },
        nonMonoparentale: { age0_11: { enfants1: 51.74, enfants2: 90.55,  enfants3plus: 142.30 }, age12_24: { enfants1: 64.68, enfants2: 103.49, enfants3plus: 155.23 } },
      },
      reduit: { enfants1: 0, enfants2: 32.34, enfants3plus: 93.14 },
      depuis: '2026-09-01',
    },
    primeNaissance: { premier: 1422.96, suivants: 646.80 },
    source: 'Famiris — montants indexés depuis le 01/09/2026 (source PRIMAIRE, relue le 2026-10-24 ; précédente lecture : indexation du 01/03/2026, 2026-08-31) — https://famiris.brussels/fr/faq/paiements-allocations-familiales/montants-allocations-familiales/',
  },

  // ═══ DIMONA ═══
  dimona: {
    delaiIN: 'Avant debut prestations',
    // ⚠️ CORRIGÉ LE 18/09/2026 — valait « Le jour meme », et c'est ce que l'agent IA
    // répondait aux clients (agents-knowledge.js). Art. 9 de l'AR du 05/11/2002 (texte
    // consolidé au 31/03/2022, refli.be/fr/lex/2002022901, lu le 18/09/2026) : « Au plus
    // tard le premier jour ouvrable qui suit la fin de l'emploi déclaré » ; l'al. 2
    // dispense de cette déclaration si la date de sortie a été communiquée à l'entrée.
    delaiOUT: "Au plus tard le premier jour ouvrable qui suit la fin de l'emploi (art. 9 AR 05/11/2002), sauf si la date de sortie a deja ete communiquee a l'entree",
    types: ['IN','OUT','UPDATE','CANCEL'],
    canal: 'Portail securite sociale ou batch',
    // ⚠️ CORRIGÉ le 2026-08-07 : ce champ valait 3. L'absence de Dimona est une
    // infraction de NIVEAU 4 (art. 181 du Code pénal social), pas de niveau 3 —
    // l'écart n'est pas cosmétique, il double la fourchette d'amende.
    sanctionNiveau: 4,
    // Sanctions du niveau 4, montants de BASE puis après décimes additionnels.
    // ✅ Texte primaire lu le 07/08/2026 sur deux pages officielles du SPF Emploi :
    //   · « Montants des amendes administratives » — niveau 4 : « De 300 à 3.500 € »
    //     https://emploi.belgique.be/fr/propos-du-spf/infractions/cheminement-dun-proces-verbal/procedure-administrative/montants-des
    //   · « Poursuites pénales » — niveau 4 : « soit un emprisonnement de 6 mois à
    //     3 ans et/ou une amende pénale de 600 € à 7.000 € soit une amende
    //     administrative de 300 € à 3.500 € »
    //     https://emploi.belgique.be/fr/propos-du-spf/infractions/cheminement-dun-proces-verbal/poursuites-penales
    //
    // ⚠️ Les deux pages ci-dessus n'ont PAS été mises à jour après le 01/09/2026 :
    // relues le 21/09/2026, elles donnent encore « 300 à 3.500 € » et les décimes ×10.
    // Le Code pénal social consolidé, lui, porte la nouvelle écriture (art. 101, loi du
    // 16/03/2026) — c'est lui qui fait foi et qui est repris dans `base` ci-dessous.
    //
    // ⚠️ LE PIÈGE EST ICI : les montants écrits dans le texte sont des montants de BASE.
    // Ce que paie réellement l'employeur, c'est base × décimes additionnels — ×8 du
    // 01/01/2017 au 31/01/2026, ×10 du 01/02/2026 au 31/08/2026, et ×1,25 depuis le
    // 01/09/2026 sur une base réécrite huit fois plus haut. Citer la base à un client,
    // c'est annoncer une amende trop basse ; le montant à annoncer est `applicable`.
    sanctions: {
      niveau: 4,
      // articleCodePenalSocial DÉFINIT L'INFRACTION Dimona elle-même (niveau 4) —
      // ce n'est PAS ce que la réforme ci-dessous a changé. Ne pas confondre avec
      // l'art. 101 CPS (le MÉCANISME de calcul des amendes par niveau), remplacé
      // le 01/09/2026 : voir `decimesAdditionnels.depuis0109_2026` ci-dessous.
      // ✅ NIVEAU RELU SUR LE TEXTE le 21/09/2026, parce qu'une source secondaire
      // (Securex, page « Dimona » mise à jour le 06/03/2026) a été lue comme donnant un
      // niveau 3 pour l'absence non intentionnelle. Le texte consolidé dit l'inverse et
      // c'est lui qui fait foi — art. 181 § 1er : « Est puni d'une sanction de niveau 4,
      // l'employeur, son préposé ou son mandataire qui […] n'a pas communiqué les données
      // imposées […] au plus tard au moment où le travailleur entame ses prestations et au
      // plus tard le premier jour ouvrable qui suit la fin de l'emploi déclaré », suivi de
      // « l'amende est multipliée par le nombre de travailleurs concernés ». Le caractère
      // délibéré n'y change pas le NIVEAU : il ouvre seulement les peines des art. 106,
      // 107 et 107/1/1 (interdiction d'exploiter, fermeture, interdiction professionnelle).
      articleCodePenalSocial: 181,
      // ⚠️ CORRIGÉ le 16/09/2026 — le MÉCANISME de calcul (art. 101 CPS) a changé
      // au 01/09/2026, PAS le résultat final. Loi du 16/03/2026 modifiant la loi
      // du 05/03/1952 relative aux décimes additionnels sur les amendes pénales,
      // le Code pénal social et diverses dispositions de droit pénal social : le
      // coefficient de base "90" devient "2,50", le multiplicateur des décimes
      // additionnels "X10" devient "X1,25". Le SPF Emploi le dit explicitement :
      // « on arrive aux mêmes montants mais de deux manières » entre les faits
      // commis du 01/02/2026 au 31/08/2026 et ceux commis depuis le 01/09/2026.
      // VÉRIFIÉ pour le niveau 4 : le tableau des sanctions du SPF daté du
      // 01/09/2026 donne exactement les mêmes bornes que `applicable` ci-dessous
      // (3.000-35.000 € admin / 6.000-70.000 € pénale) — donc AUCUN changement de
      // valeur pour l'absence de Dimona.
      decimesAdditionnels: {
        du0101_2017_au_3101_2026: 8,
        depuis0102_2026: 10,
        // Depuis le 01/09/2026, ce facteur multiplicatif simple n'est plus le
        // mécanisme légal réel (remplacé par coefficient 2,50 × décimes 1,25) —
        // conservé ici comme repère historique, à ne plus utiliser pour calculer
        // un montant : lire `applicable` directement, il reste exact.
        depuis0109_2026: { mecanisme: 'coefficient 2,50 × décimes 1,25 (remplace 90 × 10)', resultatFinalIdentique: true },
      },
      // ⚠️ CORRIGÉ LE 21/09/2026 — `base` portait encore 300-3.500 / 600-7.000, les
      // montants d'AVANT la réforme. Le 16/09/2026 on avait vérifié `applicable` (juste)
      // sans rouvrir `base`, qui était devenu faux le 01/09/2026 : la loi du 16/03/2026
      // (art. 21, en vigueur le 01/09/2026) a RÉÉCRIT les montants de l'art. 101 CPS en
      // les multipliant par 8, pour laisser ensuite les seuls décimes ×1,25.
      // ✅ TEXTE PRIMAIRE LU le 21/09/2026 (Code pénal social consolidé, reproduction
      // Justel sur refli.be/fr/lex/2010A09589), art. 101 : « La sanction de niveau 4 est
      // constituée d'une des peines suivantes : 1° un emprisonnement de six mois à trois
      // ans ; […] 5° une amende pénale de 4800 euros à 56.000 euros ; […] 7° une amende
      // administrative de 2400 à 28.000 euros. » (note : L 2026-03-16/16, art. 21, en
      // vigueur le 01-09-2026). Contrôle d'addition : 2.400 × 1,25 = 3.000 et
      // 28.000 × 1,25 = 35.000 ; 4.800 × 1,25 = 6.000 et 56.000 × 1,25 = 70.000 —
      // `applicable` ci-dessous est donc confirmé une troisième fois.
      base: {
        amendeAdministrative: { min: 2400, max: 28000 },
        amendePenale: { min: 4800, max: 56000 },
        depuis: '2026-09-01',
        // Le droit applicable dépend de la DATE DES FAITS : l'ancienne écriture reste
        // celle des faits commis jusqu'au 31/08/2026 (avec les décimes ×10).
        historique: {
          jusquau: '2026-08-31',
          amendeAdministrative: { min: 300, max: 3500 },
          amendePenale: { min: 600, max: 7000 },
        },
      },
      // Base × 10 (mécanisme du 01/02/2026 au 31/08/2026) ; depuis le 01/09/2026,
      // nouveau mécanisme (coefficient 2,50 × décimes 1,25) mais MÊME résultat —
      // vérifié directement sur le tableau des sanctions du SPF daté du
      // 01/09/2026 (voir `decimesAdditionnels.depuis0109_2026` ci-dessus).
      applicable: {
        amendeAdministrative: { min: 3000, max: 35000 },
        amendePenale: { min: 6000, max: 70000 },
      },
      emprisonnement: { minMois: 6, maxAns: 3 },
      // « L'amende est multipliée par le nombre de travailleurs concernés, sans
      // pouvoir excéder cent fois son montant maximum. »
      multiplieParTravailleur: true,
      plafondMultiplicateur: 100,
      source:
        'SPF Emploi — Code pénal social, sanctions de niveau 4 (art. 181 pour la Dimona) ; ' +
        'pages « Montants des amendes administratives » et « Poursuites pénales », lues le 07/08/2026. ' +
        'Décimes additionnels ×10 du 01/02/2026 au 31/08/2026. Depuis le 01/09/2026 : loi du ' +
        '16/03/2026 modifiant la loi du 05/03/1952 relative aux décimes additionnels sur les ' +
        'amendes pénales, le Code pénal social et diverses dispositions de droit pénal social — ' +
        'nouveau mécanisme (coefficient 2,50 × décimes 1,25), même résultat final pour le niveau 4, ' +
        'vérifié sur le tableau des sanctions du SPF Emploi, « Modifications du Code pénal social à ' +
        'partir du 1er septembre 2026 », lu le 16/09/2026 — ' +
        'https://emploi.belgique.be/fr/actualites/modifications-du-code-penal-social-partir-du-1er-septembre-2026. ' +
        'Montants de base et niveau de l\'infraction relus le 21/09/2026 sur le Code pénal social ' +
        'consolidé (art. 101 et art. 181 § 1er, reproduction de Justel sur refli.be/fr/lex/2010A09589 ; ' +
        'le Moniteur fait foi) : niveau 4 = amende pénale 4.800-56.000 €, amende administrative ' +
        '2.400-28.000 €, multipliées par le nombre de travailleurs concernés.',
    },
  },

  // ═══ HORECA — Extras (Module 02) ═══
  // Forfaits avantageux pour travailleurs occasionnels Horeca (CP 302)
  // Deux modes de déclaration Dimona : horaire (avec heure fin) ou journalier (heure début seule)
  horeca: {
    cp: '302',
    extras: {
      // ⚠️ VALEURS Q3 2026 (en vigueur depuis le 01/07/2026) — lues en source
      // PRIMAIRE le 2026-08-02, DANS le tableau officiel lui-même.
      //
      // Source : ONSS, instruction intermédiaire « Adaptation des forfaits pour le
      // travail occasionnel, les pourboires et les marins-pêcheurs » du 03/07/2026,
      // et son tableau annexé :
      //   https://www.socialsecurity.be/site_fr/Infos/instructs/documents/xls/Forfaits_3-2026_F.xls
      //   → onglet « 3-2026 », rubrique « Travailleurs occasionnels Horeca » :
      //     code fonction DmfA 95 « forfait horaire »    = 11,52
      //     code fonction DmfA 94 « forfait journalière » = 69,12
      // Motif de l'adaptation : dépassement de l'indice pivot en juin 2026.
      //
      // Historique de ce bloc, parce qu'il explique la classe de bug :
      //   Q1 2026 : 10,67 / 64,02 — a traîné du 25/04 au 02/08, soit DEUX
      //             trimestres, sur une valeur qui entre dans le calcul des
      //             cotisations. La métadonnée annonçait pourtant elle-même sa
      //             péremption (« prochaineIndexation: Q3 2026 »).
      //   Q2 2026 : 11,29 / 67,74 — Instructions administratives 2026/2, miroirs
      //             FR et NL concordants. Corrigé puis immédiatement dépassé.
      //   Q3 2026 : 11,52 / 69,12 — ci-dessous. +2,04 % vs Q2, cohérent avec
      //             l'indexation de 2 % du dépassement de l'indice pivot.
      //
      // Indexation : forfaits recalculés CHAQUE TRIMESTRE par l'ONSS.
      // Les instructions « latest » restaient en 2026/2 au 02/08 : la valeur du
      // trimestre courant vit alors UNIQUEMENT dans le tableau de l'instruction
      // intermédiaire. Toujours vérifier ce tableau, pas seulement la page HTML.
      //
      // ⚠️ CE QUE DIT L'ONSS EN CAS DE DÉPASSEMENT — corrigé le 08/10/2026 (texte primaire, page « Travail occasionnel horeca » lue ce jour) :
      // « Le travailleur occasionnel pourra encore être occupé mais sous le régime des COTISATIONS ORDINAIRES (rémunération réelle ou, pour les
      // travailleurs rémunérés au pourboire, sur base des forfaits journaliers) » — Dimona toujours EXT, DmfA avec « E » en zone 00795. Il n'existe
      // donc PAS de « forfait de dépassement » : ce bloc annonçait 15,52 €/h et 117,93 €/jour sous ce nom, et le moteur écrivait « bascule sur forfait
      // majoré ». Faux : au-delà des contingents, c'est le brut réel qui cotise. Les montants « garçon/serveuse de café » servent à autre chose (ci-dessous).
      forfait_horaire: 11.52,           // EUR/h — tableau ONSS Forfaits_3-2026 (code 95), Q3 2026
      forfait_journalier: 69.12,        // EUR/jour — tableau ONSS Forfaits_3-2026 (code 94) = 6 × forfait horaire
      plafond_horaire_jour: 69.12,      // plafond si déclaration horaire = 6 × forfait_horaire
      indexation: 'trimestrielle (recalcul chaque trimestre par l’ONSS)',
      source: 'ONSS, instruction intermédiaire du 03/07/2026 « Adaptation des forfaits pour le travail occasionnel, les pourboires et les marins-pêcheurs » — tableau Forfaits_3-2026_F.xls, onglet 3-2026, codes fonction DmfA 94 et 95 (lu 2026-08-02)',
      majoration_ouvrier: 0.08,         // +8% pour ouvriers — « Pour les ouvriers, majorez ces montants de 8 % » (même instruction)
      // Dépassement d'un contingent : cotisations ORDINAIRES sur la rémunération réelle (voir ci-dessus) — aucun forfait propre.
      depassement: { regime: 'cotisations_ordinaires', dimonaWorkerType: 'EXT', dmfaZone00795: 'E', source: 'ONSS, instructions DmfA « Travail occasionnel horeca », lu le 08/10/2026' },
      // Rémunération forfaitaire « garçon/serveuse de café » : la base sur laquelle l'ONSS calcule les DROITS SOCIAUX de l'occasionnel (chèque de
      // vacances des ouvriers…) — ce n'est ni la base de cotisation, ni un forfait de dépassement. Même page ONSS, lue le 08/10/2026 : « calculés sur la
      // rémunération forfaitaire 'garçon/serveuse de café' de 155,75 EUR/jour ou 20,49 EUR/heure ». Informatif : aucun calcul de l'app ne s'en sert.
      forfait_droits_sociaux: { journalier: 155.75, horaire: 20.49, usage: 'droits sociaux (ONSS), pas la cotisation', source: 'ONSS, instructions DmfA « Travail occasionnel horeca », lu le 08/10/2026' },
      // Contingents annuels
      contingent_travailleur: 50,       // jours/an/NISS (contrôle via horeca@work)
      contingent_employeur: 200,        // jours/an/matricule ONSS
      // Précompte professionnel spécifique extras Horeca — ✅ TEXTE PRIMAIRE lu le 08/10/2026 : annexe III de l'AR/CIR 92 (annexe à l'AR du 11/12/2025,
      // revenus 2026), n° 73 : « le précompte professionnel est fixé uniformément (sans réduction) à 33,31 p.c. sur les rémunérations pour des prestations
      // faites durant au maximum 50 jours par année civile » (CP de l'industrie hôtelière, ou intérim pour un utilisateur de cette CP, CDD ou travail
      // nettement défini). Au-delà des 50 jours : règles générales du précompte. L'imposition finale est distincte à 33 % (fiche 281.10, cadre 10, zone 2.132).
      taux_pp: 0.3331,                  // 33,31 % — annexe III n° 73, sans réduction
      taux_pp_source: 'Annexe III AR/CIR 92 (AR 11/12/2025), n° 73 — PDF publié par Securex, lu le 08/10/2026',
      // Dimona
      dimona_worker_type: 'EXT',        // 'OTH' uniquement si contrat écrit signé
      // Durée max consécutive
      duree_max_jours_consecutifs: 2,   // Au-delà = requalification travailleur ordinaire
      // Métadonnées suivi indexation
      _meta: {
        derniereMAJ: '2026-08-02',
        trimestreDeReference: 'Q3 2026',
        enVigueurDepuis: '2026-07-01',
        sourceMAJ: 'ONSS — instruction intermédiaire 03/07/2026, tableau Forfaits_3-2026_F.xls (codes DmfA 94 et 95)',
        prochaineIndexation: 'Q4 2026 — surveiller les instructions intermédiaires ONSS et leur tableau annexé',
        notesIndexation: 'Forfaits recalculés CHAQUE trimestre. ⚠️ La page HTML « occasionals_horeca » peut rester au trimestre précédent (elle était encore en 2026/2 au 02/08 alors que le Q3 s’appliquait) : la valeur du trimestre courant vit dans le TABLEAU de l’instruction intermédiaire. Toujours lire le tableau, jamais seulement la page.',
      },
    },
  },

  // ═══ ALTERNANCE / APPRENTISSAGE — assujettissement ONSS ═══
  // Vérifié en source PRIMAIRE le 2026-08-02, sources concordantes :
  //   ONSS, Instructions administratives — « Les personnes / Cas spécifiques /
  //   Apprentis - formation en alternance »
  //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/persons/specific/apprentices.html
  //   ONSS — « L'assujettissement limité / Secteur privé »
  //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/limited_insurance/privatesector.html
  //   Securex — « Le statut commun en matière de sécurité sociale » (corroboration)
  //
  // ⚠️ CE BLOC NE CONTIENT AUCUN TAUX. Le régime de l'apprenti n'est pas « un taux
  // ONSS réduit » : c'est un assujettissement LIMITÉ À CERTAINES BRANCHES, qui
  // bascule en assujettissement complet à une date précise. Coder un pourcentage
  // ici serait inventer une règle qui n'existe pas. Le calcul des cotisations
  // reste celui du moteur, sur les branches applicables.
  alternance: {
    baseLegale: 'Notion d’apprenti en formation en alternance précisée depuis le 01/07/2015 (six conditions cumulatives)',
    // Jusqu'au 31 décembre de l'année des 18 ans : assujettissement LIMITÉ
    assujettissementLimite: {
      jusquAu: '31 décembre de l’année des 18 ans',
      branches: ['vacances annuelles', 'accidents du travail', 'maladies professionnelles'],
      // Non dues pendant cette période :
      exonerations: [
        'modération salariale',
        'cotisation spéciale FFE (CT 810)',
        'fonds de sécurité d’existence (CT 820-833)',
        'deuxième pilier de pension (CT 803, 825, 827, 835, 837)',
        'mesures emploi-formation (CT 852)',
      ],
    },
    // À partir du 1er janvier de l'année des 19 ans : assujettissement COMPLET
    assujettissementComplet: {
      aPartirDu: '1er janvier de l’année des 19 ans',
      toutesLesBranches: true,
      inclutModerationSalariale: true,
    },
    dmfa: {
      codesTravailleurJusque18: ['035', '439'],                 // 035 = type 1, 439 = type 0
      codesTravailleurDes19: ['011', '012', '013', '014', '015', '016', '017', '046', '492', '493', '494', '495'],
      zoneTypeApprentissage: '00055',
    },
    _meta: {
      derniereMAJ: '2026-08-02',
      sourceMAJ: 'ONSS Instr. — apprentices.html + limited_insurance/privatesector.html (concordants, corroborés Securex)',
    },
  },

  // ═══ ARTICLE 17 — Travail associatif occasionnel ═══
  // Base légale : art. 17 AR 28/11/1969. Exonération ONSS TOTALE (ni cotisation
  // travailleur ni patronale) tant que les quotas sont respectés ; dépassement
  // d'un quota → assujettissement ONSS ordinaire pour la totalité.
  //
  // Vérifié en source PRIMAIRE le 2026-08-02, deux sources concordantes :
  //   ONSS, Instructions administratives 2026/2 — « Les personnes / secteur socioculturel »
  //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/persons/specific/socioculturalsector.html
  //   SPF Emploi — « Activités d'animation socioculturelle et sportive »
  //   https://emploi.belgique.be/fr/themes/contrats-de-travail/contrats-de-travail-particuliers/activites-danimation-socioculturelle-et
  article17: {
    exonerationOnss: true,
    baseLegale: 'Art. 17 AR 28/11/1969',
    // Quotas ANNUELS, en HEURES (l'ancien régime « 25 jours/an » a été converti en heures)
    quotaAnnuel: {
      general: 300,        // h/an — activités socioculturelles et autres
      sport: 450,          // h/an — secteur sportif
      etudiant: 190,       // h/an — volet étudiant ; cumulable avec les 650 h de travail
    },                     //        étudiant sur la MÊME année civile, quotas trimestriels maintenus
    // Plafonds TRIMESTRIELS, en heures. Le T3 est relevé : c'est l'été, quand les
    // organisations concernées ont le plus besoin de ce type d'occupation.
    plafondTrimestriel: {
      general: 100,        // h/trimestre
      generalT3: 190,      // h au 3e trimestre
      sport: 150,          // h/trimestre
      sportT3: 285,        // h au 3e trimestre
    },
    _meta: {
      derniereMAJ: '2026-08-02',
      sourceMAJ: 'ONSS Instr. 2026/2 (socioculturalsector) + SPF Emploi — concordants',
    },
  },

  // ═══ DMFA ═══
  dmfa: {
    periodicite: 'Trimestrielle',
    delai: 'Dernier jour du mois suivant le trimestre',
    format: 'XML via batch ou portail',
    cotisationsPNP: true,
  },

  // ═══ BELCOTAX ═══
  // Délai de dépôt des fiches 281.xx (revenus N) sur Belcotax-on-web : 28 février N+1.
  // Source : SPF Finances — https://finances.belgium.be/fr/E-services/Belcotaxonweb
  // Vérifié le 21/07/2026 (confirmé ITAA 21/01/2026 + Securex + Boutique de Gestion).
  // Le SPF confirme la date exacte à chaque campagne → re-vérifier en janvier.
  // Format d'envoi : JSONL bulk (schémas JSON officiels SPF). L'XML est le legacy,
  // abandonné dans l'app le 06/07/2026 (voir fiscal/belcotax-jsonl-generator.js).
  // ⚠️ RE-VÉRIFIÉ LE 05/09/2026, et il le fallait : l'article de blog
  // /blog/belcotax-fiches-fiscales annonçait « au plus tard le 1er mars »,
  // écrit en dur ONZE fois, pendant que cette constante disait 28 février.
  // Un jour d'écart sur une échéance que le même article dit sanctionnée.
  // Lu en direct sur Securex (secondaire acceptée) le 05/09/2026 :
  //   « Les plus courantes d'entre elles doivent aussi être introduites au
  //     plus tard le 28 février 2026. »
  //   https://www.securex.be/fr/lex4you/employeur/actualites/fiches-fiscales-revenus-2025-belcotax-est-disponible
  // Concordant avec Forum for the Future, OECCBB et Boutique de Gestion.
  // ✅ CRAN REMONTÉ DE 🟠 À ✅ LE 22/09/2026 — TEXTE PRIMAIRE LU, enfin.
  // SPF Finances, « Délais de rentrée des fiches », mot pour mot :
  //   « Fiches 281.10 à 281.30 (à l'exception de 281.25) au plus tard le 28 février »
  //   https://finances.belgium.be/fr/E-services/Belcotaxonweb/delais-de-rentree-des-fiches
  //   lue le 22/09/2026 (http=200, 4 966 caractères utiles).
  // ⚠️ La VALEUR ne change pas — 28 février était déjà juste. Ce qui change, c'est la
  // PREUVE : la note « la page primaire SPF reste inaccessible (anti-bot F5 sur tout
  // finances.belgium.be) » était PÉRIMÉE. Sondé le 22/09/2026 (12 variantes, verdict
  // trouve) : le domaine rend 200 et du contenu. Personne ne l'avait re-testé depuis
  // le 07/08/2026, parce qu'une note d'indisponibilité se lit comme un fait établi
  // et n'annonce aucun blocage à lever (R2 ter, dans son sens le plus coûteux).
  // 📌 La même page donne aussi : 281.00, 281.49, 281.75, 281.77 et 281.79 au plus
  // tard le 30 mars — à coder le jour où l'app les produira.
  //
  // 🔢 jour/mois sont STRUCTURÉS et non une phrase : c'est ce qui permet aux
  // pages FR et NL de composer leur propre libellé sans retaper la date.
  // La date se lit ici, jamais dans un texte affiché (R1).
  belcotax: {
    delai: '28 février annee N+1',
    jourLimite: 28,
    moisLimite: 2,
    format: 'JSONL BelcotaxOnWeb (bulk)',
    fiches: ['281.10','281.13','281.14','281.20','281.30','281.50'],
  },

  // ═══ BILAN SOCIAL — DÉLAI DE DÉPÔT (BNB) ═══
  // Le bilan social se dépose AVEC les comptes annuels (il en fait partie, art. 3:12 CSA).
  // Délai : « dans les trente jours de leur approbation et au plus tard sept mois après la
  // date de clôture de l'exercice » (art. 98 de l'AR du 30/01/2001 / art. 3:12 CSA).
  // ✅ Lu le 08/10/2026 sur la reproduction CNC/CBN (cnc-cbn.be, nœuds 2337 et 1015) —
  // reproduction, pas le Moniteur : cran ✅ pour la règle, 🟠 pour la numérotation exacte.
  // Exercice clos le 31/12 → 31/07 de l'année suivante. Les 30 jours après approbation
  // dépendent de la date d'assemblée générale, que l'app ne connaît pas : on alerte donc
  // sur le PLAFOND de 7 mois, qui est le seul repère certain.
  clotureAnnuelle: {
    bilanSocialMoisApresCloture: 7,
    seuilsAlerteJours: [45, 30, 14, 7],
  },

  // ═══ SOURCES OFFICIELLES ═══
  sources: [
    // ═══ PRIMAIRES — Sources officielles État belge (56) ═══
    { id: 'spf_fin', nom: 'SPF Finances', url: 'https://finances.belgium.be', type: 'PP/Fiscal', count: 12 },
    { id: 'onss', nom: 'ONSS / Sécurité Sociale', url: 'https://www.socialsecurity.be', type: 'Cotisations/Dimona/DmfA', count: 10 },
    { id: 'spf_emploi', nom: 'SPF Emploi (emploi.belgique.be)', url: 'https://emploi.belgique.be', type: 'Droit du travail / CP', count: 16 },
    { id: 'cnt', nom: 'Conseil National du Travail (CNT)', url: 'https://www.cnt-nar.be', type: 'CCT 43 RMMMG / CCT 90', count: 4 },
    { id: 'moniteur', nom: 'Moniteur Belge', url: 'https://www.ejustice.just.fgov.be', type: 'Lois / AR / CCT', count: 2 },
    { id: 'statbel', nom: 'Statbel (indice santé/prix)', url: 'https://statbel.fgov.be', type: 'Index santé / IPC', count: 2 },
    { id: 'plan', nom: 'Bureau fédéral du Plan', url: 'https://www.plan.be', type: 'Prévisions pivot/index', count: 1 },
    { id: 'onem', nom: 'ONEM', url: 'https://www.onem.be', type: 'Chômage temporaire / C4 / crédit-temps', count: 5 },
    { id: 'onss_stats', nom: 'ONSS Stats', url: 'https://www.onss.be', type: 'Cotisations déclarées', count: 1 },
    { id: 'salmin', nom: 'salairesminimums.be', url: 'https://www.salairesminimums.be', type: 'Barèmes par CP', count: 1 },
    { id: 'fin_be', nom: 'SPF Finances (fin.belgium.be)', url: 'https://fin.belgium.be', type: 'Frais pro / déplacements', count: 2 },
    // ═══ SECONDAIRES — Secrétariats sociaux & fédérations (43) ═══
    { id: 'securex', nom: 'Prestataire C', url: 'https://www.securex.be', type: 'PP / ONSS / frais / km', count: 6 },
    { id: 'partena', nom: 'Prestataire B', url: 'https://www.partena-professional.be', type: 'Bureau / km / ONSS', count: 3 },
    { id: 'acerta', nom: 'Prestataire D', url: 'https://www.acerta.be', type: 'Télétravail', count: 1 },
    { id: 'liantis', nom: 'Prestataire E', url: 'https://www.liantis.be', type: 'Forfait télétravail', count: 1 },
    { id: 'ucm', nom: 'UCM', url: 'https://www.ucm.be', type: 'Km / frais pro', count: 2 },
    { id: 'attentia', nom: 'Attentia', url: 'https://www.attentia.be', type: 'Indemnité bureau', count: 1 },
    { id: 'vbo_feb', nom: 'VBO/FEB', url: 'https://www.vbo-feb.be', type: 'Cotisations patronales', count: 1 },
    { id: 'agoria', nom: 'Agoria', url: 'https://www.agoria.be', type: 'ONSS métal/techno', count: 1 },
    { id: 'paycover', nom: 'Paycover', url: 'https://www.paycover.be', type: 'Télétravail', count: 1 },
    { id: 'calc_salaire', nom: 'Calculateur-de-salaire.be', url: 'https://calculateur-de-salaire.be', type: 'PP / ONSS / RMMMG', count: 3 },
    { id: 'sbep', nom: 'SBEP', url: 'https://sbep.be', type: 'Méthodes PP', count: 1 },
    { id: 'ss_belgique', nom: 'Secretariat-social-belgique.be', url: 'https://www.secretariat-social-belgique.be', type: 'PP / télétravail', count: 2 },
    { id: 'cleiss', nom: 'CLEISS (France)', url: 'https://www.cleiss.fr', type: 'Vue complète cotisations BE', count: 1 },
  ],
  _sourceTotal: 99,  // Total exact: 56 primaires + 43 secondaires
  _scanFrequency: '3x/jour (6h15, 12h15, 18h15 CET)',

  // ═══ DISPENSES PRÉCOMPTE PROFESSIONNEL — Art. 275 CIR92 ═══
  // Source: SPF Finances + jurisprudence Q2 2026
  // ═══ SANCTIONS DE RETARD — ONSS et PRÉCOMPTE (ajouté 20/08/2026) ═══
  // Nées d'un écran qui affirmait « majorations de 0,5% par mois » (ONSS — ce
  // taux N'EXISTE PAS : la majoration est forfaitaire de 10 %) et « intérêts
  // taux mensuel 0,8% » (PP — ancien régime : le taux art. 414 CIR92 est
  // ANNUEL, 4 % pour 2026, revu chaque année par avis au MB).
  // ═══ COTISATION « +10 TRAVAILLEURS » (chômage) — ajouté 20/08/2026 ═══
  // Vérifiée le 20/08/2026 : elle existe TOUJOURS comme cotisation séparée —
  // le 25 % facial du code 495 ne l'inclut PAS. 1,60 % hors modération,
  // 1,69 % modération incluse (cas général du privé). Seuil : moyenne d'au
  // moins 10 travailleurs sur la période T4(n-2) → T3(n-1) (exclus :
  // contractuels subventionnés, art. 60, occasionnels, étudiants).
  // Codes DmfA 855 (soumis à modération → 1,69 %) / 857 (non soumis → 1,60 %),
  // employeurs de code d'importance 3 à 9.
  // Sources : ONSS instructions DmfA 2026/2 socialsecuritycontributions/
  // contributions.html + VBO-FEB « cotisations T1 2026 » — lues le 20/08/2026.
  // 🔴 Base légale (loi 29/06/1981 art. 38 ?) non relue — ne pas la citer.
  // ⚠️ GAP MOTEUR tracé le 20/08/2026 : onss.cotisationsDmfa.codes n'émet
  // JAMAIS 855/857 (la déclaration de référence était <10 travailleurs) —
  // une DmfA générée pour un client ≥10 travailleurs manquerait ~1,69 % de
  // la masse salariale. Même famille que le gap 809/811 documenté (00.02).
  cotisationPlus10Travailleurs: {
    taux: 0.0160,
    tauxAvecModeration: 0.0169,
    seuilMoyenne: 10,
    periodeReference: 'T4(n-2) → T3(n-1)',
    codesDmfa: ['855', '857'],
    source: 'ONSS instructions DmfA 2026/2 (contributions.html) + VBO-FEB T1 2026 — lues le 20/08/2026',
  },

  sanctionsOnss: {
    // ✅ Primaire lu le 20/08/2026 — ONSS, instructions administratives 2026/2 :
    // obligations/…/contributionpayment_obligations_periodicity/omissionconsequences.html
    // et obligations/…/dmfa/omission.html (socialsecurity.be).
    majorationNonPaiement: 0.10,   // 10 % du montant non payé (forfaitaire)
    interetRetardAnnuel: 0.07,     // 7 %/an dès l'expiration du délai
    indemniteDmfaTardive: { base: 495.79, parTranche: 247.89, tranche: 24789.35, seuilTranche: 49578.70 },
    declarationInexacte: { base: 50, parLigneOccupation: 4 },
    source: 'ONSS Instructions administratives 2026/2 (omissionconsequences.html + dmfa/omission.html, socialsecurity.be) — lu le 20/08/2026',
    // ✅ RE-CONFIRMÉ le 05/09/2026 sur les instructions 2026/3 (donc un trimestre
    // plus tard), même page omission.html, texte inchangé au mot près :
    //   « L'employeur qui ne transmet pas de déclaration au plus tard le dernier
    //     jour du mois qui suit un trimestre est redevable d'une indemnité
    //     forfaitaire de 495,79 EUR, augmentée de 247,89 EUR par tranche de
    //     24.789,35 EUR de cotisations dues au-delà de 49.578,70 EUR. »
    // Les quatre montants ET la règle de délai sont donc stables sur deux
    // versions d'instructions. C'est ce qui autorise à les servir au public.
    baseLegale: '🔴 à vérifier — AR 28/11/1969 art. 54 non relu (la page ONSS fait foi)',
  },
  sanctionsPrecomptePro: {
    // 🟠 3 secondaires concordantes lues le 20/08/2026 (Wolters Kluwer,
    // Fiscologue, Droits Quotidiens) — primaire finances.belgium.be derrière
    // l'anti-bot F5. Taux art. 414 CIR92 fixé ANNUELLEMENT (fourchette 4-10 %) :
    // re-lire l'avis au MB pour 2027 (publication T4 2026, robot Vercel/fondateur).
    interetRetardAnnuel: 0.04,     // 4 %/an en 2026 — tout mois commencé compte entier
    periode: 2026,
    revisionAnnuelle: true,
    // Amende administrative (art. 445 CIR92) — PAS une « majoration » :
    amendeVersementTardif: { taux: 0.10, min: 50, max: 1250, premiereInfraction: 0 },
    source: 'art. 414 CIR92 — 4 % pour 2026 (WK/Fiscologue/Droits Quotidiens, lus le 20/08/2026) ; art. 445 CIR92 — Securex Lex4You, lu le 20/08/2026',
  },

  periodicitePrecomptePro: {
    // Régime de déclaration et de versement du précompte professionnel : MENSUEL
    // ou TRIMESTRIEL (art. 412 al. 3 CIR92). Il dépend du précompte dont
    // l'entreprise était redevable l'ANNÉE PRÉCÉDENTE, comparé à un montant
    // limite indexé chaque année (SPF Finances, finance.belgium.be/en/node/1535,
    // lu le 07/10/2026) :
    //   · précompte dû en N-1 au-dessus de la limite → MENSUEL obligatoire
    //   · précompte dû en N-1 jusqu'à la limite      → TRIMESTRIEL
    seuilBase: 25000, // base légale non indexée, art. 412 al. 3 CIR92
    // ⚠️ CORRIGÉ le 07/10/2026 : 50.240 € est la limite de 2025, pas celle de 2026.
    // Les pages FR et NL du SPF Finances (node/1535 et node/385, sans date de mise
    // à jour) rattachent ce montant au régime de l'année 2025. Un article du SSN
    // (18/12/2025) applique le MÊME chiffre au précompte 2025 pour le régime 2026 :
    // divergence avec le SPF, on retient la source la plus forte (le SPF) et on
    // ne pose aucune valeur pour 2026.
    limite2025: 50240, // limite du régime 2025 (annoncée par le SPF le 20/01/2025, cf. blogitaa « Grensbedrag 2025 »)
    // 🔴 à vérifier — LIMITE DE 2026 : non établie. Une page de cabinet
    // (my-tax-consulting.be) avance 51.480 € sans citer de source officielle ;
    // NON retenue. À relire sur le SPF Finances ou au Moniteur belge.
    limite2026: '🔴 à vérifier — limite 2026 non établie (le SPF affiche 2025 ; le SSN réutilise le chiffre de 2025 ; 51.480 € sur une page de cabinet sans source officielle). Relevé du 2026-10-07.',
    echeance: {
      mensuel: 'entre le 13 et le 15 du mois suivant, selon le mois (calendrier 2026 du SPF : janvier → 13/02, août → 15/09, septembre → 15/10)',
      trimestriel: 'le 15 du mois suivant la fin du trimestre (15/01, 15/04, 15/07, 15/10)',
      source: 'SPF Finances, calendrier 2026 du précompte professionnel, finance.belgium.be/en/node/1022, lu le 2026-10-07',
    },
    // 🔴 à vérifier — RÉGIME D'UN EMPLOYEUR SANS ANNÉE PRÉCÉDENTE (il débute) :
    // aucune règle trouvée. Les pages FR et NL du SPF et son calendrier 2026
    // (lus le 07/10/2026) n'en disent rien ; myfid.be non plus. Le texte même de
    // l'art. 412 n'a pas pu être lu (sites juridiques tronqués avant l'article).
    // NE PAS coder une inférence (ex. « trimestriel par défaut ») comme une règle
    // certaine tant que le SPF Finances ne l'a pas confirmée par écrit.
    regimeDefautNouvelEmployeur: "🔴 à vérifier — aucune règle trouvée (SPF FR+NL et calendrier 2026 silencieux le 2026-10-07 ; texte de l'art. 412 non lu)",
    source: "SPF Finances : finance.belgium.be/en/node/1535 (FR) et /node/385 (NL), lues le 2026-10-07 ; art. 412 al. 3 CIR92 (texte non lu). Divergence notée : ssn.be/fr/ssn-nouvelles/periodicite-de-declaration-du-precompte-professionnel (18/12/2025).",
  },

  dispensesPP: {
    nuitEquipe:    { taux: 0.228, art: 'Art. 275/5 CIR92', minPrime: 0.02, minPart: 1/3, label: 'Travail nuit / équipes successives' },
    immobilier:    { taux: 0.18,  art: 'Art. 275/5 §1 al.4', cp: ['124','126','149.01','149.04'], label: 'Travaux immobiliers (chantiers)' },
    rd:            { taux: 0.80,  art: 'Art. 275/3 CIR92', requisDiplome: ['Master','PhD','Ingénieur civil'], requisBelspo: true, label: 'R&D — chercheurs scientifiques' },
    // Heures supplémentaires — dispense employeur (Art. 275/1) ET réduction d'impôt
    // travailleur (Art. 154bis CIR92), les DEUX dépendant du taux de sursalaire :
    //   · sursalaire 20 %     → dispense employeur 32,19 % · réduction travailleur 66,81 %
    //   · sursalaire 50/100 % → dispense employeur 41,25 % · réduction travailleur 57,75 %
    // Source : SPF Finances (Circ. 2025/C/53), Securex/Partena 2026.
    // `taux` (= 32,19 %, cas 20 %) conservé pour compatibilité d'affichage hérité.
    // ═══ QUOTA D'HEURES SUPPLÉMENTAIRES — RELEVÉ AU 01/01/2026 ═══════════════
    // 🔴 Corrigé le 20/08/2026. Le code portait 130 h (180 h construction/horeca/
    // intérim) : c'est l'état d'AVANT la réforme. Valeurs relevées sur le texte
    // primaire, Moniteur belge du 29/07/2026 (460 pages, lues le 20/08/2026) —
    // LOI DU 15 JUILLET 2026 portant réforme de l'impôt des personnes physiques,
    // « CHAPITRE 2. — Travail supplémentaire avec réduction d'impôt et dispense
    // de versement de précompte professionnel » :
    //   • Art. 47 (art. 154bis CIR 92, RÉDUCTION D'IMPÔT TRAVAILLEUR)
    //       2° « dans l'alinéa 2, les mots "130 heures" sont chaque fois
    //          remplacés par les mots "180 heures" »
    //       4° l'alinéa devient : « Le maximum de 180 heures […] est augmenté à
    //          280 heures […] travaux routiers […] ou des travaux ferroviaires »
    //       5° l'alinéa horeca voit sa référence "130 heures" devenir "180 heures"
    //          et s'ouvre aux intérimaires mis à disposition d'employeurs horeca
    //   • Art. 48 (art. 275/1 CIR 92, DISPENSE DE VERSEMENT EMPLOYEUR) : les
    //     MÊMES nombres — alinéa 6 « 130 premières heures » → « 180 premières
    //     heures », alinéa 8 remplacé par le régime 280 h routier/ferroviaire.
    //   • Art. 49 : « Le présent chapitre produit ses effets le 1er janvier 2026
    //     et est applicable aux heures supplémentaires prestées à partir de
    //     cette date. » → rétroactif, et UNIFORME sur toute l'année 2026 : pas
    //     de palier daté nécessaire ici (contrairement au bonus fiscal, qui a
    //     changé EN COURS d'année au 01/08).
    //   • Le chiffre horeca (360 h) est confirmé mot pour mot par l'annexe III
    //     AR/CIR 92 n° 45.2 telle que remplacée par l'AR du 24/07/2026 (MB du
    //     30/07/2026, éd. 2), lue le même jour.
    //
    // ⚠️ LA « CONSTRUCTION » N'EST PLUS UN SECTEUR À PART. Son ancien quota de
    //    180 h est absorbé par le nouveau régime général de 180 h ; l'exception
    //    sectorielle porte désormais sur les TRAVAUX ROUTIERS ET FERROVIAIRES.
    //    La clé est gardée pour ne pas casser un appelant, mais elle vaut la base.
    // ⚠️ LES 280 h SONT CONDITIONNELLES et l'app ne sait pas vérifier les
    //    conditions : travaux routiers (hors pose de canalisations souterraines
    //    et câbles) ou ferroviaires, travail du week-end / des jours fériés / de
    //    nuit IMPOSÉ par les autorités, et système électronique d'enregistrement
    //    de présence (loi du 04/08/1996, ch. V, section 4). Passer ce secteur
    //    revient à AFFIRMER que ces conditions sont réunies.
    heuresSupp:    { taux: 0.3219, dispenseEmployeur20: 0.3219, dispenseEmployeur50_100: 0.4125, reductionTravailleur20: 0.6681, reductionTravailleur50_100: 0.5775, art: 'Art. 275/1 + 154bis CIR92', plafondHeures: 180, plafondHeuresSecteurs: { travaux_routiers: 280, travaux_ferroviaires: 280, interim_travaux_routiers: 280, horeca: 360, interim_horeca: 360, construction: 180, interim: 180 }, label: 'Heures supplémentaires (180h · 280h routier/ferroviaire · 360h horeca)', source: 'Loi du 15/07/2026 (MB 29/07/2026) art. 47-49 · annexe III AR/CIR 92 n° 45.2 (AR 24/07/2026, MB 30/07/2026 éd. 2) — lus le 20/08/2026' },
    sportifsJeunes:{ taux: 0.80,  art: 'Art. 275/6 CIR92', ageMax: 26, label: 'Sportifs rémunérés jeunes' },
    marine:        { taux: 1.00,  art: 'Art. 275/2 CIR92', condition: 'Pavillon EEE', label: 'Marine marchande / dragage' },
    startup:       { tauxStandard: 0.10, tauxPME: 0.20, art: 'Art. 275/10 CIR92', dureeMaxAns: 4, label: 'Jeunes entreprises / start-ups' },
    delaiRecuperationAns: 3, // Art. 358 CIR92 — délai de réclamation
  },

  // ═══ MARIBEL SOCIAL — AR 18/07/2002 ═══
  // Source: socialsecurity.be Instructions ONSS 2026/1
  // ⚠️ Forfait à confirmer Q2 2026 (variation 387,83€ → 507,39€ selon sources 2024-2025)
  maribel: {
    // Forfaits trimestriels Maribel social par fonds — €/trim/ETP.
    // Source : ONSS — Instructions administratives DmfA 2026/2, « promotion de
    // l'emploi dans le secteur non-marchand (maribel social) » (AR 18/07/2002 ;
    // ETA +34,00 € AR 23/03/2026). Vérifié socialsecurity.be 2026-06-12.
    // La page ONSS assigne explicitement : CP 318 → aideFamilles · CP 330 → santé ·
    // CP 319/329/331/332 (et 337 auxiliaire) → autres · CP 327 → ETA · public → secteurPublic.
    forfaitsParFonds: {
      aideFamilles:        409.37, // CP 318.xx — aides familiales & aides seniors
      sante:               507.48, // CP 330.xx — établissements/services de santé
      secteurPublic:       498.31, // Fonds Maribel social du secteur public
      entreprisesAdaptees: 574.55, // CP 327.xx — ETA/maatwerk (540,55 + 34,00 au 01/2026)
      autres:              504.10, // tous les autres employeurs éligibles
    },
    forfaitParCp: { '318':'aideFamilles', '319':'autres', '329':'autres', '330':'sante', '331':'autres', '332':'autres', '337':'autres', '327':'entreprisesAdaptees' },
    forfaitTrim: 504.10,  // défaut générique (= « tous les autres employeurs ») — repli quand le CP n'est pas connu
    forfaitTrimSource: 'ONSS Instructions DmfA 2026/2 — maribel social (socialsecurity.be), vérifié 2026-06-12',
    seuilOccupation: 0.50, // 50% mi-temps minimum (33% pour entreprises adaptées)
    seuilOccupationAdapte: 0.33,
    cpEligibles: ['318','319','329.01','329.02','329.03','330','331','332','337'],
    cpEligiblesPublic: ['secteur public sans but lucratif'],
    note: 'Cumul avec réduction structurelle. Prioritaire sur autres réductions catégorielles.',
    // ── Plafonds d'intervention financière du Fonds (max €/an/ETP) ──
    // L'intervention se limite TOUJOURS au coût salarial brut réel (brut + cotis.
    // patronales). Ces maxima ne sont PAS uniques : chaque Fonds sectoriel fixe ses
    // tranches par règlement. Source : ONSS Instructions DmfA 2026/2 « workpromotion
    // non-profit » (socialsecurity.be) + tranches APEF socioculturel — vérifié 2026-06-29.
    plafondsIntervention: {
      postes2020plus_general:  50000.00, // nouveaux postes ≥2020 — secteur général (autres)
      postes2020plus_local:    42000.00, // nouveaux postes ≥2020 — admin. provinciales/locales
      postes2020plus_hopitaux: 50000.00, // nouveaux postes ≥2020 — hôpitaux / soins psy
      apef_mar1_logistique:    39500.00, // Fonds privé socioculturel APEF — MAR1, 01/01/2026 (SECONDAIRE FeBi/Acerta — à confirmer)
      apef_mar2_autres:        43950.00, // Fonds privé socioculturel APEF — MAR2, 01/01/2026 (SECONDAIRE FeBi/Acerta — à confirmer)
    },
    plafondsInterventionSource: 'ONSS Instructions DmfA 2026/2 — workpromotion non-profit (socialsecurity.be, primaire) ; tranches APEF socioculturel 01/01/2026 (FeBi, secondaire à confirmer) — vérifié 2026-06-29',
    // Maribel FISCAL — dispense partielle de versement du précompte professionnel (non-marchand).
    // ⚠️ Distinguer la dispense BRUTE du gain NET employeur : l'employeur REVERSE ~3/4 de la
    // dispense au Trésor (→ ONSS → Fonds Maribel) et ne CONSERVE que ~1/4. Présenter 1 % comme
    // un gain net surestime ×4. Source PRIMAIRE : SPF Finances « Correction salariale et Maribel
    // social » (vérifié 2026-06-29). Base légale CIR 92 : à confirmer (NE PAS écrire art.275/7+275/10).
    maribelFiscalDispenseBrut: { nonPME: 0.0100, pme: 0.0112 }, // % du brut imposable — 1,00 % / 1,12 % PME (art.1:24 CSA) — PRIMAIRE SPF Finances
    maribelFiscalPartReverseeTresor: 0.75,                       // ~3/4 de la dispense reversés (financent les Fonds) — SECONDAIRE (Securex), à confirmer
    maribelFiscalSource: 'SPF Finances — Correction salariale et Maribel social (primaire : dispense brute 1,00 %/1,12 %) ; ventilation 3/4-1/4 Securex (secondaire, à confirmer) — vérifié 2026-06-29',
  },

  // ═══ ACS — Agents Contractuels Subventionnés (Région de Bruxelles-Capitale) ═══
  // ⚠️ ABOLI hors Bruxelles : Wallonie 01/01/2022, Flandre 01/07/2025, Comm. germ. 01/01/2018.
  // Réduction ONSS = EXONÉRATION TOTALE des cotisations patronales de base (groupe-cible G7 ;
  // G13 = idem moins la modération salariale pour administrations provinciales/locales).
  // ⚠️ « 0,56 % » (vu sur BRUXEO/Monasbl) = cotisation patronale RÉSIDUELLE restant due APRÈS
  // l'exonération G7 (cotisations spéciales hors base), PAS la réduction. Absent de la page
  // primaire ONSS. NE JAMAIS le coder comme taux de réduction. Pas non plus de « points » (= APE).
  // Vérifié source primaire ONSS DmfA 2026-06-30 (réduction 4000, codes travailleur 024/025/029/484/485).
  acs: {
    regions: ['Bruxelles'],
    reductionOnss: 'exoneration_totale_base', // G7 ONSS — exonération totale cotis. patronales de base
    reductionOnssLocal: 'exoneration_base_moins_moderation', // G13 — admin. provinciales/locales (documentaire)
    codeDmfa: '4000',                         // code de réduction DmfA ACS (G7) — verrouillé par legal-constants-coherence.test
    primeForfaitaireBaseArt20: 5035.00,       // €/an/ETP — prime forfaitaire art.20. ✅ MONTANT EN VIGUEUR 2026 (PAS « base à réindexer »).
                                              // Dernière modif art.20 = ARR 30/06/2005 (en vigueur 01/09/2005) : 5.035 € ; INCHANGÉ depuis.
                                              // Confirmé sur la version CONSOLIDÉE Justel/ejustice (mise à jour au 15/05/2024).
    primeForfaitaireConfirmee: true,          // ✅ CONFIRMÉ source primaire (Justel consolidé, art.20) le 2026-07-02.
                                              // L'art.20 n'est PAS visé par l'indexation (art.28 ne vise que art.22/23/23bis) → 5.035 € est fixe.
    indexationMecanisme: 'annuelle_indice_sante',          // art.28 arrêté RBC — indexe UNIQUEMENT art.22 (FBIE), 23 & 23bis (enseignement/sport), PAS art.20 ni art.21
    primeMajoreePlafond: 'traitement_reference_annuel',    // art.21 — prime majorée = salaire réel plafonné au traitement de référence + forfait, −5 %
    fraisSecretariatSocialMensuelBase: 12.39, // art.21 §3 (consolidé, non indexé — primaire arrêté)
    primeFBIEArt22: 15150.00,                 // €/an — prime dérogatoire FBIE (art.22), répartissable 1→6 ACS par tranches de 2.525 € ; indexée (art.28)
    trancheFBIEArt22: 2525.00,                // €/an — tranche unitaire art.22
    tauxInterventionBase: 0.95,               // 95 % (secondaire bruxeo/governance.brussels)
    tauxInterventionMajore: 1.00,             // 100 % petite enfance + insertion socioprof. (accord budgétaire RBC 14/04/2026)
    // ⚠️ RÉFUTÉ (accord RBC 14/04/2026, L'Avenir) : pas de « gel d'indexation 2026 » ni d'« uniformisation 95 % » → taux différenciés MAINTENUS.
    source: 'ONSS G7/G13 (socialsecurity.be, primaire) + arrêté RBC 28/11/2002 régime ACS, version CONSOLIDÉE Justel/ejustice 2002031624 mise à jour 15/05/2024, art.20 (5.035 €, non indexé) / art.21 (prime majorée, 12,39 €/mois) / art.22 (15.150 €) / art.28 (indexation art.22/23/23bis) — vérifié 2026-07-02 (https://www.ejustice.just.fgov.be/eli/arrete/2002/11/28/2002031624/justel)',
  },

  // ═══ APE — Aide à la Promotion de l'Emploi (Wallonie) ═══
  // ⚠️ PÉREMPTION : décret APE abrogé au 01/01/2027 (Gouv. wallon + FWB 12/06/2025).
  // Plus de « valeur du point » dans le calcul actif depuis 01/01/2022 ; plus de réduction ONSS APE.
  ape: {
    conditionChomageMois: 4,        // 4 mois inoccupé+inscrit (1 jour secteurs prioritaires) — depuis 01/01/2026
    abrogationDate: '2027-01-01',   // garde-fou péremption : désactiver le dispositif à cette date
    reductionOnss: null,            // supprimée depuis 01/01/2022 (cotisations pleines)
    valeurPointActive: false,       // 'valeur du point' = historique/cadastre uniquement, JAMAIS dans le calcul actif
    source: 'Le Forem (conditions 2026, primaire) + Gouvernement wallon (abrogation 01/01/2027, primaire) — vérifié 2026-06-29',
  },

  // ═══ ACTIVA.BRUSSELS — SUPPRIMÉ (Actiris / Région de Bruxelles-Capitale) ═══
  // ⚠️ TROIS DATES, PAS UNE : c'est ce qui distingue cette abrogation de celle de
  // l'APE. Un booléen « expiré » y répondrait faux dans les deux sens — il
  // refuserait une déduction encore due, ou en accorderait une éteinte.
  //   · 15/07/2026 → plus AUCUN nouvel engagement ne peut en bénéficier ;
  //   · 31/12/2026 → fin de la transition pour les contrats commencés AVANT ;
  //   · 01/01/2027 → extinction définitive, l'employeur reprend tout le salaire.
  // Les trois mesures sont visées ensemble : activa.brussels, activa.brussels
  // plus et activa.brussels « aptitude réduite ».
  // La prime handicap et la prime incitant à la formation ne sont PLUS
  // accessibles (même communication).
  activaBrussels: {
    abroge: true,
    // ⚠️ Formulation volontaire : on écrit « plus aucune EMBAUCHE ». En Belgique
    // le mot « engagement » veut dire embauche, mais accolé à une négation il
    // devient la promesse commerciale bannie du dépôt — un prospect la lit comme
    // « pas de durée d'engagement », alors que le contrat de services fait 3 ans.
    // Le verrou `offre-lancement-retrait` a attrapé la tournure ici le
    // 30/08/2026, et il l'attrape aussi quand on la CITE pour l'expliquer : d'où
    // cette rédaction qui la décrit sans la reproduire.
    finNouveauxEngagements: '2026-07-15', // plus aucune embauche n'ouvre de droit à partir de cette date
    finTransition: '2026-12-31',          // dernier jour de déduction pour les contrats antérieurs
    extinctionDefinitive: '2027-01-01',   // plus aucune déduction, quel que soit le contrat
    mesuresVisees: ['activa.brussels', 'activa.brussels plus', 'activa.brussels aptitude réduite'],
    primesFermees: ['prime handicap', 'prime incitant à la formation'],
    motif: 'Orientations budgétaires de la Région de Bruxelles-Capitale pour 2026',
    source: "Actiris, « Abrogation de la mesure activa.brussels » (page employeurs, primaire) — lu le 2026-08-30 (https://www.actiris.brussels/fr/employeurs/detail-d-une-news/20736-abrogation-de-la-mesure-activa-brussels-emp)",
  },

  // ═══ CONSERVATION DES DONNÉES FISCALES ET SALARIALES ═══
  // Audit 20.25 (24/09/2026). Les CGV, la politique de confidentialité et les CGU
  // employé annonçaient « 7 ans — Art. 315 CIR 1992 » pendant que rgpd-retention.js
  // appliquait 10 ans : le même document disait deux durées. Le délai de l'art. 315
  // CIR 92 a été porté de 7 à 10 ans par la loi du 20/11/2022 portant des dispositions
  // fiscales et financières diverses, à partir de l'exercice d'imposition 2023.
  // 🟠 Deux secondaires concordantes (IBR-IRE, droitbelge.be), lues via WebSearch le
  // 24/09/2026 — texte primaire (Moniteur) NON relu : sortie réseau bloquée ce jour-là.
  // ⚠️ Ne couvre PAS les documents sociaux (règles propres, non relues ici).
  conservation: {
    fiscaleAns: 10,
    source: "Art. 315 CIR 92, délai porté de 7 à 10 ans par la loi du 20/11/2022 (dispositions fiscales et financières diverses), à partir de l'exercice d'imposition 2023. 🟠 IBR-IRE (https://www.ibr-ire.be/fr/actualites/news-detail/d-lai-de-conservation-des-archives) + droitbelge.be (https://www.droitbelge.be/news_detail.asp?id=1115), lus le 2026-09-24 ; texte primaire à relire.",
  },

  // ═══ FSE+ — Fonds Social Européen Plus (options de coûts simplifiés, RDC UE 2021/1060) ═══
  // ⚠️ Ce sont des PLAFONDS (« jusqu'à »), pas des taux fixes.
  fse: {
    ocsForfaitAutresCoutsMax: 0.40,  // jusqu'à 40 % des coûts directs de personnel pour les autres coûts (art.56)
    ocsCoutsIndirectsMax: 0.15,      // jusqu'à 15 % des coûts directs de personnel (art.54 b)
    ocsCoutsIndirectsAltMax: 0.07,   // OU jusqu'à 7 % des coûts directs (art.54 a)
    ocsObligatoireSeuilEur: 200000,  // OCS obligatoires si coût total de l'opération ≤ 200 000 € (art.53(2))
    conservationPiecesAns: 5,        // dès le 31/12 de l'année du dernier paiement reçu (art.82)
    source: 'Règlement UE 2021/1060 consolidé (eur-lex CELEX:02021R1060-20240630, art.53/54/56/82, primaire) — vérifié 2026-06-29',
  },

  // ═══ OPTIONS SUR ACTIONS / WARRANTS — loi du 26/03/1999 ═══
  // L'avantage est imposable au moment de l'ATTRIBUTION, présumée survenir le
  // 60e jour qui suit l'offre lorsque l'acceptation écrite intervient dans ce
  // délai. Il se calcule forfaitairement sur la valeur de l'action sous-jacente
  // — donc AVANT de savoir si l'option rapportera quoi que ce soit.
  //
  // Taux réduit (moitié des pourcentages) sous 5 conditions CUMULATIVES :
  //   1. prix d'exercice déterminé au moment de l'offre
  //   2. exercice possible entre la 3e et la 10e année suivant l'offre
  //   3. option non cessible
  //   4. aucune couverture contre une diminution de valeur
  //   5. actions de la société employeur ou d'une de ses filiales
  //
  // Source : Securex Lex4You — « Warrants et options sur actions : les aspects
  // fiscaux » — vérifié 2026-08-07. Base légale : loi du 26/03/1999.
  // ⚠️ Une réforme de la taxation des plus-values est annoncée : à surveiller.
  optionsSurActions: {
    tauxOrdinaire: 0.18,            // 18 % de la valeur de l'action sous-jacente (offres ≥ 01/01/2012)
    tauxReduit: 0.09,               // 9 % — moitié, sous les 5 conditions cumulatives
    majorationParAnAuDela5Ans: 0.01,        // +1 %/an au-delà de la 5e année (régime ordinaire)
    majorationReduiteParAnAuDela5Ans: 0.005,// +0,5 %/an dans le régime réduit
    dureeSansMajorationAns: 5,
    delaiAcceptationJours: 60,      // acceptation écrite → attribution présumée au 60e jour
    exerciceAnneeMin: 3,            // condition du taux réduit : exercice entre la 3e…
    exerciceAnneeMax: 10,           // …et la 10e année suivant l'offre
    // ✅ TEXTE PRIMAIRE LU le 09/10/2026 — loi du 26/03/1999 (ejustice ELI 1999012205), art. 42 §1er (imposable au moment de l'attribution ;
    // acceptation écrite dans les 60 jours → attribuée le 60e jour) et art. 43 §5-6 (18 p.c. de la valeur, +1 % par année au-delà de la
    // 5e ; réduits de moitié sous quatre conditions cumulatives). ONSS 2026/3 (« options sur actions ») : hors rémunération, sauf prix
    // inférieur à la valeur ou avantage certain (art. 43 §8).
    source: "Loi du 26/03/1999, art. 42-43 — texte primaire lu le 09/10/2026 (ejustice ELI 1999012205) ; ONSS 2026/3 ; Securex Lex4You (vérifié 2026-08-07)",
  },

  // ═══ OCCUPATION D'UN TRAVAILLEUR ÉTRANGER — permis unique ═══
  // Matière RÉGIONALISÉE : la Région du LIEU DE TRAVAIL principal est compétente
  // (Bruxelles-Capitale, Wallonie, Flandre, Communauté germanophone). Les délais
  // et conditions varient d'une Région à l'autre — le moteur ne prétend pas les
  // unifier, il signale ce qui diverge.
  //
  // ⚠️ DEPUIS LE 04/05/2026, toutes les demandes passent par le portail fédéral
  // « Guichet unique ». Les formulaires PDF et les envois par e-mail ne sont
  // PLUS acceptés (Bruxelles Économie et Emploi).
  //
  // Sources vérifiées 2026-08-07 : economie-emploi.brussels (autorisations de
  // travail, dispenses, guichet unique) · emploi.wallonie.be (délais) ·
  // workinginbelgium.be. Transposition de la directive 2011/98/UE.
  permisTravail: {
    seuilJoursPermisUnique: 90,     // > 90 jours → permis unique ; ≤ 90 j → autorisation de courte durée
    delaiDecisionMois: 4,           // décision à dater de la recevabilité de la demande
    delaiProcedureJoursMax: 120,    // du dossier complet à la délivrance / au refus
    validiteMaxAns: 1,              // en principe la durée du contrat, plafonnée à 1 an (renouvelable)
    validiteLongueAns: 3,           // certaines Régions, pour hautement qualifiés / cadres — À VÉRIFIER par Région
    renouvellementDebutMoisAvant: 2,// fenêtre de renouvellement (Wallonie) : entre 2 mois…
    renouvellementFinMoisAvant: 1,  // …et 1 mois avant l'expiration
    guichetUniqueDepuis: '2026-05-04',
    source: "economie-emploi.brussels + emploi.wallonie.be + workinginbelgium.be — vérifié 2026-08-07 ; directive 2011/98/UE",
  },

  // ═══ RÉGIME SPÉCIAL D'IMPOSITION — CONTRIBUABLES IMPATRIÉS (RSII) ═══
  // art. 32/1 CIR 92 (en vigueur 01/01/2022, remplace l'ancienne circulaire
  // « cadres étrangers » de 1983).
  //
  // ⚠️ TROIS VALEURS ONT CHANGÉ AU 01/01/2025 — l'app affichait encore les
  // anciennes le 12/08/2026 (75.000 € / 30 % / plafond 90.000 € côté impôt),
  // dans EmbaucheAZ, ProceduresRH_Detachement et leurs deux dictionnaires i18n.
  // Base légale du changement : loi du 18/12/2025 portant des dispositions
  // diverses (M.B. 30/12/2025, numac 2025009647), commentée par la
  // circulaire 2026/C/51 du 01/04/2026.
  //
  // ⚠️ LE PLAFOND DE 90.000 € N'A PAS DISPARU : il est supprimé côté IMPÔT et
  // MAINTENU côté ONSS. Écrire « plafond supprimé » sans préciser est faux pour
  // le calcul des cotisations.
  //
  // Sources (2, indépendantes, concordantes au mot près sur les 3 valeurs) :
  //   • Circulaire 2026/C/51 du 01/04/2026, reprise par l'OECCBB —
  //     https://blog.oeccbb.be/fr/article/circulaire-2026c51-relative-aux-modifications-apportees-au-regime-special-dimposition-pour-les-contribuables-impatries-et-au-regime-special-dimposition-pour-les-chercheurs-impatries/30928
  //   • Claeys & Engels — https://www.claeysengels.be/fr-be/nouvelles-evenements/regime-special-dimposition-pour-les-contribuables-et-chercheurs-impatries
  // Lues le 2026-08-12. 🔴 Le texte SPF lui-même n'a PAS été relu : tout le
  // domaine finances.belgium.be est derrière l'anti-bot F5 (cf. CLAUDE.md 7 bis).
  impatries: {
    seuilBrutAnnuel: 70000,        // > 70.000 € brut/an (était 75.000 € jusqu'au 31/12/2024)
    pctExoneration: 0.35,          // 35 % du brut en dépenses répétitives exonérées (était 30 %)
    plafondFiscalSupprime: true,   // plafond absolu de 90.000 €/an SUPPRIMÉ à l'impôt depuis le 01/01/2025
    plafondOnss: 90000,            // …mais MAINTENU pour le calcul des cotisations ONSS
    dateEffetNouveauSeuil: '2025-01-01',
    delaiDemandeMois: 3,           // demande à introduire dans les 3 mois de l'entrée en fonction — forclusion
    dureeAns: 5,                   // 5 ans…
    prolongationAns: 3,            // …+ 3 ans si les conditions restent remplies
    // Conditions d'exclusion, sur les 60 mois précédant l'entrée en fonction :
    // pas résident belge, pas soumis à l'INR sur des revenus professionnels belges,
    // et pas domicilié à moins de 150 km de la frontière belge.
    exclusionMois: 60,
    exclusionDistanceKm: 150,
    source: "art. 32/1 CIR 92 ; loi du 18/12/2025 (M.B. 30/12/2025, numac 2025009647) ; circulaire 2026/C/51 du 01/04/2026 — OECCBB + Claeys & Engels, vérifié 2026-08-12",
  },

  // ═══ MOTIF GRAVE — art. 35 loi 03/07/1978 ═══
  // Double délai de 3 jours ouvrables : (1) donner le congé dans les 3 jours
  // ouvrables de la connaissance du fait, (2) notifier les motifs dans les
  // 3 jours ouvrables qui suivent le congé. Le délai court à partir du LENDEMAIN
  // du jour de la connaissance / du congé.
  // Notification à peine de nullité : recommandé, exploit d'huissier, ou remise
  // d'un écrit. La charge de la preuve (réalité du motif ET respect des délais)
  // pèse sur celui qui invoque le motif grave.
  // Source : art. 35 loi 03/07/1978 — vérifié 2026-08-06.
  motifGraveProcedure: {
    joursOuvrablesConge: 3,
    joursOuvrablesNotification: 3,
    // Le samedi compte : voir `joursOuvrables` juste en dessous (loi du 26/12/2022).
    source: "art. 35 loi 03/07/1978 ; samedi ouvrable : loi du 26/12/2022, art. 2 (l'art. 1.7 C. civ. ne s'applique pas au droit du travail)",
  },

  // ═══ JOURS OUVRABLES EN DROIT DU TRAVAIL — le SAMEDI COMPTE ═══
  // ⚠️ CORRIGÉ LE 15/09/2026. Ce fichier portait `samediOuvrableNonTranche: true`
  // depuis le 06/08/2026 (« Aucune clarification législative à ce jour », sur la
  // foi de trois sources secondaires). C'était faux dès l'origine — la loi
  // existait depuis le 26/12/2022 :
  //   Loi du 26/12/2022 « visant à neutraliser l'article 1.7 du livre 1er du Code
  //   civil en ce qui concerne le droit du travail et la sécurité sociale et
  //   l'assistance sociale » (M.B. 30/12/2022, numac 2022043203, en vigueur le
  //   01/01/2023), art. 2 : « L'article 1.7. du Livre 1er du Code civil n'est pas
  //   d'application en ce qui concerne les dispositions ayant pour objet la
  //   réglementation des relations du travail, de la sécurité sociale et de
  //   l'assistance sociale. »
  // Le SPF Emploi en tire la conséquence (blog « Une loi pour maintenir la notion
  // de jours ouvrables en droit du travail », 19/06/2023) : « Le samedi
  // continuera donc bien à être considéré comme un jour ouvrable dans le cadre du
  // droit du travail et de la sécurité sociale, sauf si une disposition
  // spécifique prévoit explicitement autre chose. »
  // Textes lus le 15/09/2026 : la loi sur refli.be/fr/lex/2022043203 (reproduction
  // de Justel — ejustice est derrière l'anti-bot F5), le blog du SPF en direct.
  // Conséquence : art. 35 (motif grave) et art. 37 §1 (effet du recommandé) se
  // comptent samedi COMPRIS. Lecteur : rupture-engine.js.
  joursOuvrables: {
    samediOuvrable: true,
    source: "Loi du 26/12/2022, art. 2 (M.B. 30/12/2022, numac 2022043203, en vigueur le 01/01/2023) — texte lu le 15/09/2026 sur refli.be (reproduction de Justel) ; SPF Emploi, « Une loi pour maintenir la notion de jours ouvrables en droit du travail » (19/06/2023), lu le 15/09/2026.",
    dateVerif: '2026-09-15',
  },

  // ═══ NOTIFICATION ET DÉBUT DU PRÉAVIS — art. 37 §1 et 37/1 loi 03/07/1978 ═══
  // ✅ Texte primaire lu le 15/09/2026 (refli.be/fr/lex/1978070303, reproduction de
  // Justel) :
  //   · art. 37 §1 al. 4 — le congé donné par l'employeur ne peut, à peine de
  //     nullité, être notifié que par lettre recommandée « sortissant ses effets le
  //     troisième jour ouvrable suivant la date de son expédition », ou par exploit
  //     d'huissier (al. 5 : remise d'un écrit admise pour la seule batellerie) ;
  //   · art. 37/1 — « Le délai de préavis visé à l'article 37 prend cours le lundi
  //     suivant la semaine pendant laquelle le préavis a été notifié. »
  // ⚠️ Des documents citaient l'« art. 37 §1 al. 2 » pour le début du préavis :
  // l'al. 2 impose seulement que la notification mentionne le début et la durée
  // du préavis, à peine de nullité. Lecteur : rupture-engine.js
  // (`debutPreavisRecommande`), utilisé par la lettre de licenciement.
  preavisNotification: {
    recommandeEffetJourOuvrable: 3,
    debut: 'lundi_suivant_semaine_notification',
    source: "art. 37 §1 al. 4 et art. 37/1 loi 03/07/1978 — texte lu le 15/09/2026 (refli.be, reproduction de Justel)",
    dateVerif: '2026-09-15',
  },

  // ═══ MOTIVATION DU LICENCIEMENT — CCT n° 109 du 12/02/2014 (CNT) ═══
  // ✅ Texte primaire lu le 18/09/2026 :
  //   https://www.cnt-nar.be/sites/default/files/documents/CCT-COORD/cct-109.pdf
  //   · art. 4 — le travailleur demande les motifs concrets par recommandé « dans un
  //     délai de deux mois après que le contrat de travail a pris fin » ; en cas de
  //     préavis, « dans un délai de six mois après la notification du congé […], sans
  //     toutefois pouvoir dépasser deux mois après la fin du contrat de travail » ;
  //   · art. 5 — l'employeur répond par recommandé « dans les deux mois à dater de la
  //     réception » de la demande ;
  //   · art. 7 § 1er — à défaut : « amende civile forfaitaire correspondant à deux
  //     semaines de rémunération » ;
  //   · art. 9 § 2 — licenciement manifestement déraisonnable : indemnisation « au
  //     minimum à trois semaines de rémunération et au maximum à 17 semaines ».
  // ⚠️ Le préambule de la CCT rappelle l'art. 38, 1° de la loi du 26/12/2013 : l'art. 63
  // de la loi du 03/07/1978 (licenciement abusif des OUVRIERS, six mois de rémunération)
  // cesse de s'appliquer aux employeurs relevant de la loi du 05/12/1968 (secteur privé)
  // dès l'entrée en vigueur de cette CCT. Lecteur : lettre de licenciement
  // (doc-generators.js, exit-docs-i18n.js).
  motivationLicenciement: {
    demandeMoisApresFin: 2,
    demandeMoisApresNotification: 6,
    reponseMois: 2,
    amendeCivileSemaines: 2,
    indemniteSemainesMin: 3,
    indemniteSemainesMax: 17,
    source: "CCT n° 109 du 12/02/2014 (CNT), art. 4, 5, 7 § 1er et 9 § 2 — texte lu le 18/09/2026 (cnt-nar.be, cct-109.pdf)",
    dateVerif: '2026-09-18',
  },

  // ═══ INDEMNITÉ DE RUPTURE — art. 39 loi 03/07/1978 ═══
  // « rémunération en cours » + avantages acquis en vertu du contrat, sur la
  // durée du préavis (ou la partie restant à courir).
  // Conversion d'un salaire MENSUEL en salaire HEBDOMADAIRE : × 3 / 13.
  // Source : art. 39 loi 03/07/1978 ; Securex (composition de la base) —
  // vérifié 2026-08-06.
  indemniteRupture: {
    semaineDepuisMensuel: { num: 3, den: 13 },
    source: "art. 39 loi 03/07/1978 ; base de calcul : Securex, vérifié 2026-08-06",
  },

  // ═══ OUTPLACEMENT — CCT 82bis CNT ═══
  outplacement: {
    cct82bis: {
      ageMin: 45,
      ancienneteMinAns: 1,
      heuresProgramme: 60,
      dureeMois: 12,
      forfaitMin: 1800,           // € — coût employeur minimum
      coutMaxFormule: 4,          // × salaire mensuel si > forfaitMin
      sanctionEmployeur: 1800,    // € si non-respect
      sanctionOnssSpec: 1800,     // € cotisation ONSS spéciale supplémentaire
      delaiOffreJours: 15,        // après notification licenciement
      delaiAcceptationMois: 2,    // pour le travailleur
    },
    phases: [
      { num: 1, heuresMax: 20, dureeMois: 2, focus: 'Bilan personnel + projet professionnel' },
      { num: 2, heuresMax: 20, dureeMois: 4, focus: 'Préparation campagne + recherche active' },
      { num: 3, heuresMax: 20, dureeMois: 6, focus: 'Suivi + adaptations + intégration' },
    ],
  },

  // ═══ AIDES RÉGIONALES — Q2 2026 ═══
  // ⚠️ Plafonds régionaux indexés annuellement — à vérifier sur sites officiels
  aides: {
    bxl: { // Bruxelles — Actiris
      activa:                 { plafondAnnuel: 15600, dureeMois: 30, condition: 'DE Actiris > 12 mois' },
      premierEngagementBxl:   { plafondAnnuel: 16200, condition: 'Réduction ONSS premier engagement fédéral' },
      activa55:               { plafondAnnuel: 12000, dureeMois: 24, ageMin: 55 },
      art60Cpas:              { plafondAnnuel: 18000, dureeMois: 24 },
      stageInsertion:         { plafondAnnuel: 3600,  dureeMois: 6, ageMax: 25 },
      // ⚠️ Les deux « Impulsion » sont des aides WALLONNES (Forem), rangées ici
      // sous Bruxelles par erreur historique, et ABROGÉES pour les nouvelles
      // demandes depuis le 01/07/2026 (remplacées par `wallonie.jobPlus`). On ne
      // les déplace pas — des écrans lisent ces clés — mais on cesse de mentir
      // sur leur région et sur leur disponibilité.
      impulsion25:            { plafondAnnuel: 7800,  dureeMois: 18, ageMax: 25, regionReelle: 'wallonie', abroge: '2026-07-01', remplacePar: 'jobPlus' },
      impulsion12mois:        { plafondAnnuel: 9360,  dureeMois: 24, conditionMois: 12, regionReelle: 'wallonie', abroge: '2026-07-01', remplacePar: 'jobPlus' },
      primeHoreca:            { plafondAnnuel: 5000,  dureeMois: 12, cp: '302' },
    },
    wallonie: { // Forem
      // ═══ INCITANT JOB PLUS — en vigueur depuis le 01/07/2026 ═══
      // ✅ Texte primaire lu le 31/08/2026 sur la page du Forem
      // https://www.leforem.be/entreprises/aides-financieres-incitant-job-plus.html
      //
      // Il REMPLACE six dispositifs d'un coup : Impulsion 57+, Impulsion -25 ans,
      // Impulsion 12 mois+, SINE, SESAM et Tremplin 24+. Plus AUCUNE nouvelle
      // demande n'est possible pour ceux-là depuis le 01/07/2026 — d'où le
      // drapeau `abroge` posé ci-dessous sur chacun. Les octrois EN COURS
      // continuent selon leurs règles propres : `abroge` veut dire « on n'en
      // ouvre plus », pas « ça n'existe plus ».
      //
      // ⚠️ SIGNALÉ PAR UN CLIENT, pas par notre veille (31/08/2026). Web-Soul
      // ASBL nous a écrit « j'ai bien vu sur votre plateforme la partie pour
      // Actiris, mais pas celle du Forem ». Le référentiel avait deux mois de
      // retard sur une réforme qui supprime six aides. Un client ne devrait
      // jamais être notre capteur de veille.
      jobPlus: {
        forfaitMensuelTempsPlein: 1000.00,   // EUR/mois pour un ETP
        complementMoins20Etp: 200.00,        // EUR/mois si < 20 ETP dans l'entreprise
        proratiseTempsPartiel: true,         // « adapté proportionnellement aux prestations »
        dureeMoisMin: 12, dureeMoisMax: 36,  // « varie de 12 à 36 mois selon le profil »
        paliersInscriptionMois: [4, 12, 24], // éloignement du marché du travail
        occupationMinMois: 3,                // « au moins 3 mois d'occupation »
        occupationMinRegime: 0.5,            // « à minimum mi-temps »
        delaiDemandeJours: 31,               // 🔴 « dans les 31 jours qui suivent l'engagement »
        canalDemande: 'espace entreprise Forem (en ligne uniquement)',
        nonMarchandEligible: true,           // ASBL nommément citées par le Forem
        exclus: ['administrations publiques fédérales, régionales ou communautaires',
                 "organismes d'intérêt public qui en dépendent",
                 'enseignement (personnel enseignant et académique)'],
        remplace: ['impulsion57', 'impulsion25', 'impulsion12mois', 'sine', 'sesam', 'tremplin24'],
        enVigueurDepuis: '2026-07-01',
        source: 'Forem — page « Incitant Job Plus » (leforem.be), texte primaire lu le 31/08/2026',
        dateVerif: '2026-08-31',
      },
      // ⚠️ ABROGÉES POUR LES NOUVELLES DEMANDES depuis le 01/07/2026 (cf. jobPlus).
      // Conservées pour lire un dossier ouvert AVANT cette date, jamais pour en
      // proposer une nouvelle. Tout écran qui les affiche doit dire qu'elles sont
      // fermées : `__tests__/aides-abrogees-dites-partout.test.js` le vérifie.
      sesam:                  { plafondAnnuel: 25000, dureeMois: 36, condition: 'PME wallonne, création emploi', abroge: '2026-07-01', remplacePar: 'jobPlus' },
      tremplin24:             { plafondAnnuel: 10800, dureeMois: 36, conditionMois: 24, abroge: '2026-07-01', remplacePar: 'jobPlus' },
      pfi:                    { dureeMois: 6, salaireCouvert: true, label: 'Plan Formation-Insertion' },
      apeAcs:                 { systeme: 'forfait', refonte: 2022, forfaitAnnuel: 8000, forfaitSource: 'estimateur indicatif — à confirmer Forem 2026', label: 'Aide Promotion Emploi / Agents Contractuels Subventionnés' }, // 'points' abandonné à la réforme 01/01/2022 (forfait indexé) ; ~8000€/ETP/an estimatif ; APE abrogé 01/01/2027
    },
    flandre: { // VDAB / WSE
      vop:                    { plafondAnnuel: 18000, dureeAns: 5, condition: 'Reconnaissance VAPH/VDAB' },
      aanwerving55:           { trimMin: 1150, trimMax: 1800, trimestres: 8, ageMin: 55 },
      ibo:                    { plafondAnnuel: 12000, dureeMois: 6, label: 'Individuele beroepsopleiding' },
      doelgroepJongeren:      { trimMin: 1000, trimMax: 1550, trimestres: 8, ageMax: 25 },
      transitiepremie45:      { annuel: 1500, dureeAns: 3, ageMin: 45 },
      kmoPortefeuille:        { plafondAnnuel: 7500, pctRemboursement: 0.30 },
      sineVlaanderen:         { plafondAnnuel: 18000, dureeAns: 5, label: 'Insertion sociale CLD' },
    },
  },

  // ═══ FONDS EXPÉRIENCE PROFESSIONNELLE 50+ — AR 01/07/2006 ═══
  // ⚠️ Clé `fep50` (et NON `fse`) : `fse` est réservé au Fonds Social Européen Plus (UE,
  // plus haut). Collision historique corrigée 2026-06-30 (les 2 blocs écrasaient l'un l'autre).
  fep50: {
    ageEntreprise: 45, // âge des travailleurs ciblés (50+ priorité)
    mesures: {
      amenagementPoste:   { pctSubside: 0.80, plafond: 8000 },
      tutorat:            { mensuel: 500,    plafondAnnuel: 6000, condition: 'Tuteur 50+' },
      reducTempsTravail:  { plafondAnnuel: 5000, formats: ['4/5','9/10'] },
      formation50:        { pctSubside: 0.70, plafond: 3000 },
      auditAgeManagement: { plafond: 5000,   pctSubside: 1.00 },
      outplacement50:     { plafond: 4000,   condition: 'Cumul possible CCT 82bis' },
    },
  },

  // ═══ PENSION COMPLÉMENTAIRE 2E PILIER — LPC 28/04/2003 ═══
  pension2pilier: {
    // ⚠️ CORRIGÉ LE 19/08/2026 — l'ancienne clé `deductionFraisProPct: 0.80`
    // encodait un CONTRESENS : les cotisations patronales sont déductibles
    // INTÉGRALEMENT en frais professionnels, À CONDITION de respecter la règle
    // des 80 % — qui plafonne les PRESTATIONS (pension légale + complémentaire,
    // en rente annuelle, ≤ 80 % de la dernière rémunération brute annuelle
    // normale, art. 59 CIR92). Le 0.80 est donc un plafond de prestations,
    // jamais un taux multiplicateur de déduction. 🟠 Baloise + Contassur +
    // Securex concordantes, lues le 19/08/2026 (art. 59 non relu — anti-bot F5).
    // L'ancienne clé `atnEmployePct: 0.044` (déjà marquée legacy) doublait
    // taxeVersementPct sous un faux nom (« ATN ») : supprimée, deux écrans
    // affichaient « ATN 4,4 % » pour ce qui est la taxe sur les primes.
    regle80PlafondPrestations: 0.80, // plafond prestations cumulées / dernière rémunération brute annuelle
    taxeVersementPct: 0.044,      // taxe sur les primes 2e pilier 4,4% (vérifié 2026-06-26, Securex/SDWorx)
    cotisationINAMI: 0.0355,      // 3,55% sur le capital à la liquidation
    cotisationSolidariteMax: 0.02,// cotisation de solidarité 0-2% (max 2%)
    precompteCapitalStandard: 0.165, // 16,5% précompte capital (cas standard)
    precompteCapitalPensionLegale: 0.10, // 10% si liquidation à l'âge légal avec carrière
    cotisationWijninckxPct: 0.125,
    // ⚠️ AJOUTÉ LE 19/08/2026 — la cotisation ONSS spéciale de 8,86 % sur les
    // contributions PATRONALES de pension complémentaire (assurance groupe/EIP)
    // n'avait PAS de clé sémantique propre. Deux écrans (PrimesAvantagesV2,
    // EmbaucheAZ) l'empruntaient à `onss.employeur.detail.pension` — le
    // sous-taux de la DÉCOMPOSITION ABOLIE par le tax-shift, qui vaut 8,86 %
    // par pure coïncidence. Un homonyme n'est pas une source : si la
    // décomposition morte est un jour purgée, les deux écrans perdaient leur
    // taux. Source primaire : ONSS, instructions DmfA, « Les pensions
    // extra-légales — cotisation 8,86% » (art. 38 §3ter L. 29/06/1981), lu le
    // 19/08/2026 : socialsecurity.be/employer/instructions/dmfa/fr/latest/
    // instructions/special_contributions/extralegal_pensions.html
    onssSpecialePatronale: 0.0886, // contribution Wijninckx 12,5% (loi 18/12/2025, MB 30/12/2025 — RSVZ ; ×4 vs ancien 3%) — corr. audit 2026-07-08
    // ⚠️ NEUTRALISÉ LE 19/08/2026 — l'ancien `plafondWijninckx: 36886.16` ne
    // correspond à AUCUN seuil Wijninckx publié (vérif ONSS DmfA + FAQ INASTI
    // le 19/08/2026 ; déjà flaggé « irrésolu » au registre le 09/07/2026).
    // Le régime définitif (depuis 2019) ne connaît PAS de plafond de prime :
    // la cotisation se déclenche quand pension légale + réserves converties en
    // rente dépassent l'« objectif de pension » (pension max fonction publique
    // × fraction de carrière n/45), calcul Sigedis (DB2P), DmfA T4. Le montant
    // 2026 de l'objectif de pension diverge selon les sources (INASTI 95 636,10
    // pour 2025 · AG 97 548,92 au 01/01/2025 · Partena 99 499,90) → null tant
    // qu'il n'est pas lu sur l'INASTI. Aucun code ne consommait l'ancien chiffre.
    plafondWijninckx: null,
    objectifPension2026: null,    // 🔴 à lire sur inasti.be avant tout affichage
    source: 'LPC 28/04/2003 — taxe primes 4,4%, INAMI 3,55%, solidarité 0-2%, précompte capital 16,5%/10% ; Wijninckx 12,5% dès année de cotisation 2026 (loi 18/12/2025, MB 30/12/2025 — ONSS DmfA special_contributions/high-extra-legal-pensions + FAQ INASTI, lus le 19/08/2026)',
  },

  // ═══ RÉDUCTIONS ONSS — Récapitulatif "groupes-cibles" Q2 2026 ═══
  // Les valeurs détaillées sont dans onss.* et onss.groupeCible.*
  // Ce bloc sert d'index pour le Hub d'optimisation
  reductionsCategorielles: {
    structurelle:           { trim: 361.64,  source: 'LB.onss.deductionStructurelle.forfaitTrimestriel' },
    premierEngagementT1:    { trim: 2000.00, trimestres: null, source: 'LB.onss.premierEngagement.travailleur1' }, // illimité
    premierEngagementT2_3:  { trim: 1000.00, trimestres: 12,   source: 'LB.onss.premierEngagement.travailleur2' },
    jeunesNonQualifies:     { trim: 1500,    ageMax: 25,  source: 'LB.onss.groupeCible.jeunesNonQualifies' },
    travailleursAges:       { trim: 1500,    ageMin: 55,  source: 'LB.onss.groupeCible.agees' },
    handicapes:             { trim: 1500,                source: 'LB.onss.groupeCible.handicapes' },
  },

  // ═══ DOMICILIATION SEPA (prélèvement) — client → Aureus ═══
  // Cadre : Règlement (UE) n° 260/2012 + SEPA Direct Debit Core Rulebook (EPC016-06)
  //         + Code de Droit Économique belge, Livre VII (Art. VII.41 CDE).
  // Sert au générateur pain.008 (sepa-prelevement-engine.js) pour percevoir
  // les honoraires/redevances SaaS récurrents auprès des clients.
  domiciliation: {
    schemaXML: 'pain.008.001.02',                 // ISO 20022 — SEPA Direct Debit Initiation
    schemes: { core: 'CORE', b2b: 'B2B' },         // CORE = par défaut ; B2B = entre entreprises (pas de remboursement)
    sequences: { premier: 'FRST', recurrent: 'RCUR', ponctuel: 'OOFF', dernier: 'FNAL' },
    devise: 'EUR',
    // Délais de remboursement débiteur — Art. VII.41 CDE / EPC016-06
    remboursement: { autoriseSemaines: 8, nonAutoriseMois: 13 },
    // Pré-notification obligatoire du débiteur avant prélèvement (rulebook EPC : 14 jours
    // calendaires sauf délai différent convenu dans le mandat)
    preavisJours: 14,
    // ⚠️ À VALIDER EXPERT/BANQUE : Identifiant Créancier SEPA (ICS) d'Aureus.
    // Dérivé de la BCE 1028.230.781 mais DOIT être enregistré auprès de la banque
    // domiciliataire avant tout prélèvement réel. L'IBAN/BIC créancier ne vivent PAS
    // ici (secrets) → injectés via env côté serveur (AUREUS_CREANCIER_IBAN/BIC).
    icsAureus: 'BE48ZZZ1028230781',
  },
};

export const LB=LOIS_BELGES;

// ═══════════════════════════════════════════════════════════════════════════
// UN CHIFFRE, UN ENDROIT — le RMMMG se DÉRIVE, il ne se recopie pas (02/09/2026)
// ───────────────────────────────────────────────────────────────────────────
// `remuneration.degressifEtudiant.base` EST le RMMMG : les pourcentages de la
// CCT n° 50 s'appliquent au revenu minimum mensuel moyen garanti. Il était
// pourtant écrit une seconde fois, en dur, et n'a pas suivi l'indexation du
// 01/07/2026 — il valait encore 2 189,81 € quand le RMMMG valait 2 233,61 €.
// Le plancher étudiant national servi par `salaire-etudiant-engine.js`, et
// imprimé dans les contrats générés, était donc 2 % trop bas : de 29,35 €/mois
// à 16 ans jusqu'à 39,42 €/mois à 20 ans.
//
// 🪤 Et ce fichier DOCUMENTAIT le piège quatre-vingt-dix lignes plus haut :
// « la page du CNT affiche le montant de BASE non indexé (2 189,81 €) … ne
// jamais s'y fier ». Savoir n'a pas suffi. Tant qu'un nombre est écrit deux
// fois, l'une des deux copies vieillit en silence.
//
// ⚠️ POURQUOI UN RÉ-ALIGNEMENT ICI PLUTÔT QU'UNE CONSTANTE PARTAGÉE : trois
// robots réécrivent le RMMMG dans ce fichier par substitution de chaîne sur le
// LITTÉRAL — `app/lib/agents/lois-belges-anchors.js` (ancre exacte
// « montant18ans: X, montant20ans6m: X, montant21ans12m: X »),
// `app/api/cron/rmmmg/route.js` et `app/api/cron/baremes/route.js`. Remplacer
// le littéral par un identifiant casse leur ancre EN SILENCE : la mise à jour
// automatique cesserait sans qu'aucun test ne tombe. Le littéral reste donc là
// où les robots savent le trouver, et c'est la copie dérivée qui s'aligne.
// Contrôle indépendant du montant : la fiche SPF de la CP 100 publie le RMMMG
// à l'heure — 13,5644 €/h en régime 38h au 01/09/2026 (relevé le 02/09/2026),
// soit 2 233,61 € ÷ 164,67. Verrou : `__tests__/un-chiffre-un-endroit-rmmmg-flexi.test.js`.
// ═══════════════════════════════════════════════════════════════════════════
LOIS_BELGES.remuneration.degressifEtudiant.base = LOIS_BELGES.remuneration.RMMMG.montant18ans;

// ═══════════════════════════════════════════════════════════════════════════
// BONUS À L'EMPLOI FISCAL — palier applicable à une DATE (R1, source unique)
// ═══════════════════════════════════════════════════════════════════════════
// Le volet B est passé de 52,54 % à 63 % au 01/08/2026 (AR 24/07/2026, MB 30/07,
// art. 4 + art. 7). Un pourcentage lu « à plat » sert donc du périmé sur toute
// fiche d'août et au-delà — et du faux sur toute fiche de juillet si on remplace
// la valeur sans dater. Tout lecteur passe par ici.
//
// Convention IDENTIQUE à werkbonusPalier() dans payroll-engine.js : sans date on
// rend le palier BASELINE (le plus ancien), celui contre lequel les fiches réelles
// et la suite de tests sont validées. Les surfaces d'AFFICHAGE doivent donc passer
// une date explicite (la période de la fiche, ou aujourd'hui pour un écran de
// référence) — c'est le seul moyen d'annoncer le pourcentage réellement appliqué.
export function getBonusEmploiFiscal(date) {
  const paliers = LOIS_BELGES.pp.bonusEmploiFiscalParPalier;
  if (!Array.isArray(paliers) || !paliers.length) return LOIS_BELGES.pp.bonusEmploiFiscal;
  const d = date ? String(date).slice(0, 10) : null;
  if (!d) return paliers[0];
  let sel = paliers[0];
  for (const p of paliers) { if (p.from <= d) sel = p; else break; }
  return sel;
}

// Raccourci pour les écrans « état du droit » : le palier EN VIGUEUR AUJOURD'HUI.
export function bonusEmploiFiscalDuJour() {
  return getBonusEmploiFiscal(new Date().toISOString().slice(0, 10));
}

// ═══════════════════════════════════════════════════════════════════════════
// FORFAIT DE BUREAU (TÉLÉTRAVAIL) — MÊME CONVENTION QUE LE BONUS À L'EMPLOI.
// Sans date on rend le palier BASELINE (le plus ancien), pour que rien ne change
// sous les pieds d'un appelant existant. Toute surface qui ANNONCE le montant à
// un humain — contrat, audit, écran de référence, page publique — doit passer
// une date : la date de la pièce, ou aujourd'hui pour un écran d'état du droit.
// Trouvé le 29/08/2026 : le palier du 01/09 arrivait dans 3 jours et aucune des
// 8 surfaces qui affichent ce montant ne l'aurait su.
export function forfaitTeletravailAu(date) {
  const p = LOIS_BELGES.fraisPropres.teletravail;
  const paliers = p && p.paliers;
  if (!Array.isArray(paliers) || !paliers.length) return { from: null, max: p ? p.max : null };
  const d = date ? String(date).slice(0, 10) : null;
  if (!d) return paliers[0];
  let sel = paliers[0];
  for (const x of paliers) { if (x.from <= d) sel = x; else break; }
  return sel;
}

// Le forfait EN VIGUEUR AUJOURD'HUI — c'est celui qu'annoncent les surfaces.
export function forfaitTeletravailDuJour() {
  return forfaitTeletravailAu(new Date().toISOString().slice(0, 10));
}

// ═══════════════════════════════════════════════════════════════════════════
// FORFAIT KM VOITURE (ANNUEL) — MÊME CONVENTION QUE forfaitTeletravailAu().
// Sans date, on rend le palier BASELINE (le plus ancien) — pour qu'aucun
// appelant existant ne change de comportement silencieusement. Toute surface
// qui VALORISE une note de frais doit passer la date DE LA NOTE : une note
// antidatée se calcule au taux en vigueur à SA date, jamais au taux courant.
// Trouvé le 31/07/2026 (NotesDeFrais.js) : KM_RATE lisait FORF_KM à plat,
// donc calculait toujours au taux du jour, quelle que soit la date saisie.
export function forfaitKmAu(date) {
  const paliers = LOIS_BELGES.fraisPropres.forfaitDeplacement.paliers;
  if (!Array.isArray(paliers) || !paliers.length) return LOIS_BELGES.fraisPropres.forfaitDeplacement.voiture;
  const d = date ? String(date).slice(0, 10) : null;
  if (!d) return paliers[0].voiture;
  let sel = paliers[0];
  for (const p of paliers) { if (p.from <= d) sel = p; else break; }
  return sel.voiture;
}

// Le taux EN VIGUEUR AUJOURD'HUI — c'est celui qu'annoncent les surfaces.
export function forfaitKmDuJour() {
  return forfaitKmAu(new Date().toISOString().slice(0, 10));
}

// ═══════════════════════════════════════════════════════════════════════════
// PRÉAVIS — HELPERS SOURCE UNIQUE (R1). Toute table préavis en dur est interdite.
// Statut unique Art. 37/2 Loi 03/07/1978. Grilles vérifiées 2026-06-25.
// opts.dateDebutContrat (Date|string) active les réformes 2026 si fournie.
// ═══════════════════════════════════════════════════════════════════════════

// Préavis de LICENCIEMENT (par l'employeur), en semaines, selon l'ancienneté en années.
export function preavisEmployeurSemaines(ancienneteAnnees, opts = {}) {
  const a = Math.max(0, Number(ancienneteAnnees) || 0);
  const debut = opts.dateDebutContrat ? new Date(opts.dateDebutContrat) : null;
  // Réforme 01/08/2026 : 1 semaine pendant les 6 premiers mois
  if (debut && debut >= new Date('2026-08-01') && a < 0.5) return 1;
  let sem;
  if (a < 0.25) sem = 1;            // < 3 mois
  else if (a < 1 / 3) sem = 3;      // 3 → 4 mois
  else if (a < 5 / 12) sem = 4;     // 4 → 5 mois
  else if (a < 0.5) sem = 5;        // 5 → 6 mois
  else if (a < 0.75) sem = 6;       // 6 → 9 mois
  else if (a < 1) sem = 7;          // 9 → 12 mois
  else if (a < 1.25) sem = 8;       // 12 → 15 mois
  else if (a < 1.5) sem = 9;        // 15 → 18 mois
  else if (a < 1.75) sem = 10;      // 18 → 21 mois
  else if (a < 2) sem = 11;         // 21 → 24 mois
  else if (a < 3) sem = 12;         // 2 ans
  else if (a < 4) sem = 13;         // 3 ans
  else if (a < 5) sem = 15;         // 4 ans
  else if (a < 20) sem = 18 + (Math.floor(a) - 5) * 3; // 5a:18 … 19a:60
  else if (a < 21) sem = 62;        // 20 ans
  else sem = 63 + (Math.floor(a) - 21); // 21a:63 puis +1 sem/an
  // Réforme 01/06/2026 : plafond 52 semaines (effets dès 17 ans d'ancienneté)
  if (debut && debut >= new Date('2026-06-01') && sem > 52) sem = 52;
  return sem;
}

// Préavis de DÉMISSION (par le travailleur), en semaines, plafond 13.
export function preavisTravailleurSemaines(ancienneteAnnees, opts = {}) {
  const a = Math.max(0, Number(ancienneteAnnees) || 0);
  const debut = opts.dateDebutContrat ? new Date(opts.dateDebutContrat) : null;
  // Réforme 01/08/2026 : 1 semaine pendant les 6 premiers mois
  if (debut && debut >= new Date('2026-08-01') && a < 0.5) return 1;
  if (a < 0.25) return 1;           // < 3 mois
  if (a < 0.5) return 2;            // 3 → 6 mois
  if (a < 1) return 3;             // 6 → 12 mois
  if (a < 1.5) return 4;            // 12 → 18 mois
  if (a < 2) return 5;             // 18 → 24 mois
  if (a < 4) return 6;             // 2 → 4 ans
  if (a < 5) return 7;             // 4 → 5 ans
  if (a < 6) return 9;             // 5 → 6 ans
  if (a < 7) return 10;            // 6 → 7 ans
  if (a < 8) return 12;            // 7 → 8 ans
  return 13;                        // ≥ 8 ans (plafond)
}
export const TX_ONSS_W=LB.onss.travailleur; // 0.1307
// 🔴 08/10/2026 — TX_ONSS_E = la cotisation patronale de BASE déclarée (code 495,
// 25,00 %), plus l'agrégat historique `total` (25,07 %). Deux raisons, mesurées :
//  · la DmfA déclare `globale` (app/api/onss/dmfa/route.js, cron dmfa-auto ;
//    déclaration T2/2026 ACCEPTÉE par l'ONSS, ticket 033-04ENHGL-L2-Z : 495 =
//    13,07 + 25,00) — chaque fiche imprimait pourtant un « ONSS patronal » à
//    25,07 %, un montant qui n'est pas celui déclaré (R14) ;
//  · depuis le 07/10/2026 (#3601) le coût employeur ajoute les cotisations
//    spéciales PAR CODE DmfA (255, 256, 809, 810, 831, 859) : garder 25,07 %, qui
//    agrégeait déjà des spéciales, les comptait deux fois.
// Effet mesuré (rapport d'impact, 272 profils) : 270 changent, coût employeur
// seulement, −0,73 à −4,54 €/mois ; aucun net ne bouge. Les fiches établies ne
// bougent pas (verrou en base). `LB.onss.employeur.total` reste lu directement par
// les simulateurs par CP (estimations sans cotisations spéciales) — dette nommée
// dans 00.02, pas modifiée ici.
export const TX_ONSS_E=LB.onss.employeur.globale; // 0.2500
export const TX_OUV108=LB.onss.ouvrier108; // 1.08
// TX_AT (0,97 %, « AR 10/04/2014 », source introuvable) RETIRÉ le 07/10/2026 : la
// part ONSS des accidents du travail est comprise dans TX_ONSS_E, et la prime de
// l'assurance-loi est TX_AT_ASSURANCE (estimation) ou le taux de la police client
// (parametres-cout-client.js). Plus aucun consommateur ne doit lire 0,97 %.
export const TX_AT_ASSURANCE=LB.assurances.accidentTravail.taux; // 1% assurance loi AT (différent de l'ONSS)
export const COUT_MED=LB.assurances.medecineTravail.cout; // COUT_MED
export const CR_TRAV=LB.chequesRepas.partTravailleur.min; // CR_TRAV
export const PRIME_FIN_ANNEE_INTERIM=LB.cotisations.interimCP322.primeFinAnnee; // 0.0833 — CP 322 intérim (Fonds Social Intérimaires)
export const PP_EST=0.22; // PP estimation moyenne (~22% de l'imposable)
export const PP_EST_25=0.25; // PP estimation hauts salaires (~25% de l'imposable, utilisé dans engines sectoriels)
export const NET_FACTOR=(1-TX_ONSS_W)*(1-PP_EST); // facteur net approx = ~0.5645
export const quickNetEst=(b)=>Math.round(b*NET_FACTOR*100)/100; // estimation rapide net

// Forfait trimestriel Maribel social (€/trim/ETP) pour un CP donné.
// Résout via LB.maribel.forfaitParCp → forfaitsParFonds (repli 'autres').
// 'pub'/'public' → fonds du secteur public. Source ONSS (cf. LB.maribel).
export function forfaitMaribel(cp){
  const m=LB.maribel;
  const raw=String(cp||'').toLowerCase();
  if(raw==='pub'||raw==='public') return m.forfaitsParFonds.secteurPublic;
  const base=String(cp||'').replace(/[^\d.]/g,'').split('.')[0];
  const key=m.forfaitParCp[base]||'autres';
  return m.forfaitsParFonds[key]??m.forfaitTrim;
}

// ═══ SPRINT 41: EXPORTS COMPTABLES RÉELS ═══
export function generateExportCompta(format,ops,periode,company){
  const co=company||{};const coName=co.name||'...';const coVAT=(co.vat||'...').replace(/[^A-Z0-9]/g,'');
  const f2=v=>(Math.round(v*100)/100).toFixed(2);
  const fBE=v=>f2(v).replace('.',',');
  const now=new Date();const dateStr=now.toISOString().slice(0,10);const periodeStr=periode||dateStr.slice(0,7);
  const journal='OD';const piece='SAL'+periodeStr.replace(/-/g,'');
  let output='';let filename='';let mime='text/plain';
  // ——— BOB50 ———
  if(format==='bob50'||format==='bob'){
    // BOB50 format: journal|date|piece|compte|libelle|montant_debit|montant_credit
    output=ops.map(o=>[journal,dateStr.replace(/-/g,''),piece,o.compte,'"'+o.desc.replace(/"/g,"'")+'"',o.sens==='D'?fBE(o.montant):'0,00',o.sens==='C'?fBE(o.montant):'0,00'].join('\t')).join('\n');
    output='Journal\tDate\tPiece\tCompte\tLibelle\tDebit\tCredit\n'+output;
    filename='BOB50_OD_'+periodeStr+'.txt';
  }
  // ——— WINBOOKS ———
  else if(format==='winbooks'){
    // Winbooks TXT: DBK|BOOKYEAR|PERIOD|DOCNUMBER|ACCOUNTGL|AMOUNTEUR|DTEFROM|COMMENT
    const year=now.getFullYear();const month=now.getMonth()+1;
    output=ops.map((o,i)=>['OD',year,month.toString().padStart(2,'0'),piece,(o.compte||'').padEnd(10,' '),o.sens==='D'?f2(o.montant):'-'+f2(o.montant),dateStr.replace(/-/g,''),o.desc.slice(0,40)].join('\t')).join('\n');
    output='DBK\tBOOKYEAR\tPERIOD\tDOCNUMBER\tACCOUNTGL\tAMOUNTEUR\tDTEFROM\tCOMMENT\n'+output;
    filename='Winbooks_OD_'+periodeStr+'.txt';
  }
  // ——— EXACT ONLINE ———
  else if(format==='exact'){
    // Exact CSV: Journal;Boekjaar;Periode;Dagboek;Rekeningnr;Omschrijving;Debet;Credit;Datum
    output=ops.map(o=>['OD',now.getFullYear(),now.getMonth()+1,'OD',o.compte,'"'+o.desc+'"',o.sens==='D'?fBE(o.montant):'',o.sens==='C'?fBE(o.montant):'',dateStr.split('-').reverse().join('/')].join(';')).join('\n');
    output='Journal;Boekjaar;Periode;Dagboek;Rekeningnr;Omschrijving;Debet;Credit;Datum\n'+output;
    filename='Exact_OD_'+periodeStr+'.csv';mime='text/csv';
  }
  // ——— HORUS ———
  else if(format==='horus'){
    // Horus XML format
    const entries=ops.map((o,i)=>'<Entry seq="'+(i+1)+'"><Account>'+o.compte+'</Account><Description>'+o.desc.replace(/&/g,'&amp;')+'</Description><Debit>'+(o.sens==='D'?f2(o.montant):'0.00')+'</Debit><Credit>'+(o.sens==='C'?f2(o.montant):'0.00')+'</Credit><Date>'+dateStr+'</Date></Entry>').join('');
    output='<?xml version="1.0" encoding="UTF-8"?>\n<HorusExport><Journal>OD</Journal><Period>'+periodeStr+'</Period><Company>'+coName+'</Company><VAT>'+coVAT+'</VAT><Entries>'+entries+'</Entries></HorusExport>';
    filename='Horus_OD_'+periodeStr+'.xml';mime='application/xml';
  }
  // ——— OCTOPUS ———
  else if(format==='octopus'){
    // Octopus CSV: Dagboek,Datum,Stuk,Rekening,Omschrijving,Debet,Credit
    output=ops.map(o=>['OD',dateStr.split('-').reverse().join('/'),piece,o.compte,'"'+o.desc+'"',o.sens==='D'?fBE(o.montant):'',o.sens==='C'?fBE(o.montant):''].join(',')).join('\n');
    output='Dagboek,Datum,Stuk,Rekening,Omschrijving,Debet,Credit\n'+output;
    filename='Octopus_OD_'+periodeStr+'.csv';mime='text/csv';
  }
  // ——— YUKI ———
  else if(format==='yuki'){
    // Yuki XML
    const lines=ops.map((o,i)=>'<Line><LineNumber>'+(i+1)+'</LineNumber><GLAccountCode>'+o.compte+'</GLAccountCode><Description>'+o.desc.replace(/&/g,'&amp;')+'</Description><DebitAmount>'+(o.sens==='D'?f2(o.montant):'0.00')+'</DebitAmount><CreditAmount>'+(o.sens==='C'?f2(o.montant):'0.00')+'</CreditAmount></Line>').join('');
    output='<?xml version="1.0" encoding="UTF-8"?>\n<YukiImport><Administration>'+coVAT+'</Administration><Journal><Code>OD</Code><Description>OD Salaires '+periodeStr+'</Description><Date>'+dateStr+'</Date><Lines>'+lines+'</Lines></Journal></YukiImport>';
    filename='Yuki_OD_'+periodeStr+'.xml';mime='application/xml';
  }
  // ——— KLUWER ———
  else if(format==='kluwer'){
    output=ops.map(o=>['OD',dateStr.replace(/-/g,''),piece,o.compte,o.desc.slice(0,40).padEnd(40,' '),o.sens==='D'?fBE(o.montant).padStart(15,' '):''.padStart(15,' '),o.sens==='C'?fBE(o.montant).padStart(15,' '):''.padStart(15,' ')].join('|')).join('\n');
    filename='Kluwer_OD_'+periodeStr+'.txt';
  }
  // ——— POPSY ———
  else if(format==='popsy'){
    output=ops.map((o,i)=>[piece,dateStr.replace(/-/g,''),o.compte,o.desc.slice(0,30),o.sens==='D'?fBE(o.montant):'0,00',o.sens==='C'?fBE(o.montant):'0,00'].join(';')).join('\n');
    output='Piece;Date;Compte;Libelle;Debit;Credit\n'+output;
    filename='Popsy_OD_'+periodeStr+'.txt';
  }
  // Download
  var blob=new Blob([output],{type:mime+';charset=utf-8'});var url=URL.createObjectURL(blob);var a=document.createElement('a');a.href=url;a.download=filename;document.body.appendChild(a);a.click();setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},3000);
  return {filename,size:output.length,lines:output.split('\n').length};
}

// ═══ SPRINT 41: CSV EXPORT TRAVAILLEURS ═══
export function exportTravailleurs(emps,company){
  const co=company||{};const coName=co.name||'';
  const headers=['NISS','Nom','Prenom','DateNaissance','Genre','Email','Telephone','Adresse','CodePostal','Ville','IBAN','BIC','Statut','TypeContrat','DateEntree','DateSortie','Fonction','Regime','BrutMensuel','CP','Matricule'];
  const rows=emps.map(e=>[
    (e.niss||''),
    (e.last||e.ln||'').replace(/;/g,','),
    (e.first||e.fn||'').replace(/;/g,','),
    (e.birthDate||''),
    (e.gender||''),
    (e.email||''),
    (e.phone||''),
    (e.address||''),
    (e.zip||''),
    (e.city||''),
    (e.iban||''),
    (e.bic||''),
    (e.statut||'Employe'),
    (e.contractType||'CDI'),
    (e.startDate||''),
    (e.endDate||''),
    (e.fonction||e.jobTitle||''),
    (+e.regime||100)+'%',
    (+(e.monthlySalary||e.gross||0)).toFixed(2),
    (e.cp||''),
    (e.matricule||e.id||'')
  ].join(';'));
  const csv='\uFEFF'+headers.join(';')+'\n'+rows.join('\n');
  const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');
  a.href=url;a.download='Travailleurs_'+coName.replace(/[^a-zA-Z0-9]/g,'_')+'_'+new Date().toISOString().slice(0,10)+'.csv';
  document.body.appendChild(a);a.click();
  setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},3000);
  return rows.length;
}

// ═══ SPRINT 41: CSV IMPORT TRAVAILLEURS ═══
export function importTravailleurs(csvText){
  const lines=csvText.split(/\r?\n/).filter(l=>l.trim());
  if(lines.length<2)return {error:'Fichier vide ou sans données',imported:[]};
  const headers=lines[0].split(';').map(h=>h.trim().toLowerCase());
  const fieldMap={
    'niss':'niss','nom':'last','prenom':'first','datenaissance':'birthDate',
    'genre':'gender','email':'email','telephone':'phone','adresse':'address',
    'codepostal':'zip','ville':'city','iban':'iban','bic':'bic',
    'statut':'statut','typecontrat':'contractType','dateentree':'startDate',
    'datesortie':'endDate','fonction':'fonction','regime':'regime',
    'brutmensuel':'gross','cp':'cp','matricule':'matricule',
    'last':'last','first':'first','fn':'first','ln':'last',
    'name':'last','firstname':'first','lastname':'last',
    'salary':'gross','brut':'gross','gross':'gross',
    'contract':'contractType','type':'contractType',
    'start':'startDate','end':'endDate','birth':'birthDate'
  };
  const colMap=headers.map(h=>{
    const clean=h.replace(/[^a-z]/g,'');
    return fieldMap[clean]||null;
  });
  const imported=[];
  for(let i=1;i<lines.length;i++){
    const vals=lines[i].split(';');
    if(vals.length<3)continue;
    const emp={id:'imp_'+Date.now()+'_'+i,status:'active'};
    colMap.forEach((field,j)=>{
      if(field&&vals[j]!==undefined){
        let v=vals[j].trim().replace(/^"|"$/g,'');
        if(field==='gross')v=parseFloat(v.replace(',','.'))||0;
        else if(field==='regime')v=parseInt(v)||100;
        emp[field]=v;
      }
    });
    if(emp.first||emp.last||emp.niss)imported.push(emp);
  }
  return {imported,count:imported.length,headers:headers};
}

// ═══ SPRINT 41 (historique) : la « test suite calculs paie » qui vivait ici
// (runPayrollTests) était MORTE (0 appelant) et référençait des fonctions du
// moteur (payroll-engine) inexistantes dans ce module → supprimée le 17/07/2026.
// L'équivalent VIVANT : app/lib/payroll-tests.js (branché via MoteurCertifie)
// + la suite Jest __tests__ (imports corrects depuis payroll-engine).

// ═══ SPRINT 43: OBFUSCATION NISS/IBAN ═══
export const obf={
  encode:(v)=>{if(!v)return '';try{return btoa(unescape(encodeURIComponent(String(v).split('').reverse().join(''))));}catch(e){return v;}},
  decode:(v)=>{if(!v)return '';try{return decodeURIComponent(escape(atob(v))).split('').reverse().join('');}catch(e){return v;}},
  maskNISS:(n)=>{if(!n||n.length<6)return n;return n.slice(0,2)+'.***.***'+n.slice(-2);},
  maskIBAN:(i)=>{if(!i||i.length<8)return i;return i.slice(0,4)+' **** **** '+i.slice(-4);}
};
export const safeLS={get:(k)=>{try{if(typeof window==='undefined')return null;return window.localStorage.getItem(k);}catch(e){return null;}},set:(k,v)=>{try{if(typeof window==='undefined')return;window.localStorage.setItem(k,typeof v==='string'?v:JSON.stringify(v));}catch(e){ /* handled */ }},remove:(k)=>{try{if(typeof window==='undefined')return;window.localStorage.removeItem(k);}catch(e){ /* handled */ }}};
export const CR_MAX=LB.chequesRepas.valeurFaciale.max; // 10.00
export const CR_PAT=LB.chequesRepas.partPatronale.max; // 8.91
// ⚠️ UN SEUL forfait de bureau, pas deux (29/08/2026). `forfaitBureau` et
// `teletravail` nommaient la MÊME indemnité ONSS avec deux champs distincts —
// même motif que les CP en double de R12 : deux champs finissent par diverger,
// et c'est l'ordre de lecture qui décide alors de la vérité servie. Les deux
// dérivent désormais du même barème daté.
// Limite : c'est un INSTANTANÉ au chargement du module. Les pages statiques le
// figent au build — acceptable ici, le site se reconstruit à chaque merge. Un
// appelant qui a besoin d'une date précise appelle forfaitTeletravailAu().
export const FORF_BUREAU=forfaitTeletravailDuJour().max; // FORF_BUREAU
export const FORF_KM=LB.fraisPropres.forfaitDeplacement.voiture; // 0.4761
export const PV_SIMPLE=LB.remuneration.peculeVacances.simple.pct; // PV_SIMPLE
export const PV_DOUBLE=LB.remuneration.peculeVacances.double.pct; // 0.92
export const RMMMG=LB.remuneration.RMMMG.montant18ans; // RMMMG
// ⚠️ 20/08/2026 — BONUS_MAX lisait la clé morte pp.bonusEmploi (194,03, modèle
// mono-tranche abandonné). Le repère surveillé par la veille est désormais le
// max CUMULÉ du modèle réel à deux volets (A+B, employé).
export const BONUS_MAX=LB.pp.werkbonusSocial.employe.A.max+LB.pp.werkbonusSocial.employe.B.max; // 293.66 (palier de base)
// ── Valeurs-repères SURVEILLÉES par le watchdog (SPF Finances / ONSS) ──
// Le barème du précompte est une structure multi-tranches interdépendante : on
// expose des REPÈRES scalaires (seuil 1re tranche, taux marginal, quotité) pour
// la veille — un écart déclenche une alerte (report manuel du barème complet).
export const PP_TRANCHE1_MAX=LB.pp.tranches[0].max; // 16710 — borne haute 1re tranche (fingerprint barème SPF Finances)
export const PP_TAUX_MAX=LB.pp.tranches[LB.pp.tranches.length-1].taux; // 0.5350 — taux marginal max
export const PP_QUOTITE=LB.pp.quotiteExemptee.bareme1; // 11170 — quotité exemptée d'impôt (base)
export const ONSS_REDUC_STRUCT_SEUIL=LB.onss.reductionStructurelle.S0; // 11687.74 — borne « bas salaires » S0, T3 2026 (indexée chaque trimestre)
export const ONSS_PREMIER_ENG_CAP=LB.onss.premierEngagement.travailleur1.forfaitTrimestriel; // 2000 — plafond 1er engagement
export const SEUIL_CPPT=LB.seuils.electionsSociales.cppt; // 50
export const SEUIL_CE=LB.seuils.electionsSociales.ce; // 100
export const HEURES_HEBDO=LB.tempsTravail.dureeHebdoLegale; // 38
export const CONGES_LEGAUX_5J = 20; // Lois coord. 28/06/1971 — 20 jours regime 5j/sem
export const CONGES_LEGAUX_6J = 24; // 24 jours regime 6j/sem
export const JOURS_FERIES=LB.tempsTravail.jourFerie.nombre; // 10


// ═══ SAISIES SUR SALAIRE — Barèmes 2026 (Art. 1409-1412 Code judiciaire) ═══
// Source : AR du 03/12/2025, Moniteur belge 10/12/2025 (seuils art. 1409 indexés au 01/01/2026).
// Confirmé Partena Professional + Attentia (montants 2026). Bornes en € net/mois civil.
// ⚠️ Le barème REMPLACEMENT (chômage, indemnités) diffère du TRAVAIL : pas de tranche 30% —
// la tranche à 40% court de 1524 jusqu'au seuil D (1839), et seul le net > 1839 est saisissable
// à 100% (Code jud. art. 1409 §1er bis). Corr. audit 2026-07-08 : l'ancien seuil 1682 sur-retenait
// illégalement 100% sur la tranche 1682→1839 (doit rester à 40%). Source seuils : AR 03/12/2025, MB 10/12/2025.
export const SAISIE_2026_TRAVAIL = [
  { min: 0,    max: 1419,     pct: 0,   label: "Insaisissable" },
  { min: 1419, max: 1524,     pct: 20,  label: "20%" },
  { min: 1524, max: 1682,     pct: 30,  label: "30%" },
  { min: 1682, max: 1839,     pct: 40,  label: "40%" },
  { min: 1839, max: Infinity, pct: 100, label: "Saisissable en totalité" },
];

export const SAISIE_2026_REMPLACEMENT = [
  { min: 0,    max: 1419,     pct: 0,   label: "Insaisissable" },
  { min: 1419, max: 1524,     pct: 20,  label: "20%" },
  { min: 1524, max: 1839,     pct: 40,  label: "40%" },
  { min: 1839, max: Infinity, pct: 100, label: "Saisissable en totalité" },
];

export const SAISIE_IMMUN_ENFANT_2026 = 88; // majoration par enfant à charge 2026 (était 86 en 2025) — AR 03/12/2025

// ═══ ALLOCATIONS FAMILIALES PAR RÉGION ═══
export const AF_REGIONS = {
  // ⚠️ BXL CORRIGÉ LE 31/08/2026 — source PRIMAIRE Famiris, montants indexés depuis le 01/09/2026
  // (voir le bloc `allocFamBxl` pour l'URL et le détail).
  // ✅ ENFANTS NÉS AVANT LE 01/12/2019 (Famiris, page « Montants », relue le 24/10/2026) : 181,10 (0-11 ans) · 194,04
  // (12-17 ans) · 194,04 (18-24 ans, hors études supérieures) — soit 12,94 € de moins que les enfants nés à partir du
  // 01/12/2019 pour chaque tranche. Le pivot est le 01/12/2019 et non l'année 2020 : `cutoffDate` le porte, `cutoff`
  // (année) ne sert qu'en l'absence de date de naissance. Les études supérieures (206,97 / 219,91 €) ne sont pas modélisées.
  // L'ancienne valeur `ancien.rang1 = 97,73` (jamais relue, « ressemble à un rang de l'ancien régime fédéral ») est retirée.
  BXL: { cutoff: 2020, cutoffDate: '2019-12-01', base: [{ age: 0, to: 11, amt: 194.04 }, { age: 12, to: 17, amt: 194.04 + 12.94 }, { age: 18, to: 24, amt: 194.04 + 12.94 }], ancienBase: [{ age: 0, to: 11, amt: 181.10 }, { age: 12, to: 17, amt: 194.04 }, { age: 18, to: 24, amt: 194.04 }] },
  // WAL — ✅ MONTANTS ET STRUCTURE, source PRIMAIRE relue le 24/10/2026 : FAMIWAL / allocationsfamiliales.wallonie.be,
  // « Résumé des montants » (https://allocationsfamiliales.wallonie.be/montants/resume-des-montants) et
  // « Vos allocations familiales de base ». Enfants nés à partir du 01/01/2020 : MÊME montant par enfant, plus élevé
  // à partir de 18 ans — 200,51 € (0-17 ans), 213,44 € (18-24 ans) ; le supplément d'âge MENSUEL ne s'applique plus
  // (« À partir du 1er janvier 2020, le supplément d'âge mensuel ne s'applique plus pour tous les enfants nés après
  // cette date »). Enfants nés AVANT 2020 : ancien système PAR RANG — 123,94 (1er) · 229,32 (2e) · 342,39 € (3e et
  // suivants) + supplément d'âge mensuel (6-11 / 12-17 / 18-24 ans : 21,59 / 32,88 / 37,89 € pour le 1er enfant sans
  // supplément ; 43,06 / 65,79 / 83,65 € pour les suivants et/ou avec supplément — les suppléments ne s'additionnent pas).
  // `calcAllocEnfant` (payroll-engine.js) lit le rang, la date de naissance et les cas « avec supplément » / « 1er enfant »
  // depuis le 24/10/2026 (paramètre optionnel `opts`) ; sans rang, il rend `ancien.rang1`, plancher du régime ancien.
  // Avant le 24/10/2026 le code portait 181,61 (+22,53 à 12 ans, +30,88 à 18 ans) et rang1 = 97,73 : aucun ne figure sur la page.
  WAL: {
    cutoff: 2020,
    cutoffDate: '2020-01-01',
    base: [{ age: 0, to: 17, amt: 200.51 }, { age: 18, to: 24, amt: 213.44 }],
    ancien: {
      rang1: 123.94, rang2: 229.32, rang3plus: 342.39,
      supplementAgeMensuel: {
        sansSupplement: { age6_11: 21.59, age12_17: 32.88, age18_24: 37.89 },       // 1er enfant sans supplément
        avecSupplementOuSuivants: { age6_11: 43.06, age12_17: 65.79, age18_24: 83.65 },
      },
    },
    primeNaissance: 1422.96,            // enfants nés dès 2020 (naissance ou adoption)
    primeAdoptionAvant2020: 1679.08,
    plafondsSupplementSocial: [34453.81, 55599.36], // € brut/an (page Famiwal « Résumé des montants »)
    depuis: '2026-09-01',               // page modifiée le 07/09/2026 ; indexation de septembre
  },
  // VL : ✅ vlaanderen.be, relu le 24/10/2026 (https://www.vlaanderen.be/vlaams-groeipakket/bedragen-en-betaaldata) :
  // pour les enfants nés depuis le 01/01/2019, le Groeipakket verse un « basisbedrag » de 190,66 €/mois, UNIQUE
  // quel que soit l'âge. Il n'existe PAS de supplément d'âge mensuel : la « schoolbonus » est ANNUELLE
  // (0-4 ans 23,82 · 5-11 ans 41,70 · 12-17 ans 59,57 · 18-24 ans 71,48 €/an) et n'est pas dans ce tableau mensuel.
  // Les anciens suppléments mensuels 6,32 / 10,44 ne correspondent à rien sur la page officielle.
  VL: {
    cutoff: 2019,
    cutoffDate: '2019-01-01',
    base: [{ age: 0, to: 11, amt: 190.66 }, { age: 12, to: 17, amt: 190.66 }, { age: 18, to: 24, amt: 190.66 }],
    // ✅ ENFANTS NÉS AVANT LE 01/01/2019 (vlaanderen.be, « Bedragen en betaaldata », montants valables depuis le
    // 01/09/2026, relus le 24/10/2026) : « blijven in principe de bedragen van het vroegere kinderbijslagsysteem gelden ».
    // Basisbedrag selon la place dans la famille AU 31/12/2018 : rang 1 = jongste kind (109,75) · rang 2 = tweede jongste
    // (203,07) · rang 3 = overige kinderen (274,56). Leeftijdsbijslag (6-11 / 12-17 / ≥ 18 ans) : 34,52 / 52,76 / 67,08 €,
    // ou 17,31 / 26,36 / 30,39 € pour « het oudste kind dat geen sociale toeslag of zorgtoeslag ontvangt » ;
    // les oudste kinderen qui reçoivent une toeslag ont le supplément d'âge complet. Les toeslagen (sociale, zorg,
    // wees…) ne sont pas modélisées. La valeur `rang1 = 97,73`, jamais relue, est remplacée par 109,75.
    ancien: {
      rang1: 109.75, rang2: 203.07, rang3plus: 274.56,
      supplementAgeMensuel: {
        sansSupplement: { age6_11: 17.31, age12_17: 26.36, age18_24: 30.39 },        // oudste kind, sans toeslag
        avecSupplementOuSuivants: { age6_11: 34.52, age12_17: 52.76, age18_24: 67.08 },
      },
    },
  },
};

// ── CP 327 — conversion horaire → mensuel, formule de la CCT elle-même ──────
// La CCT 30/05/2002 n° 63380 (§ 1.3 de la fiche SPF) pose la conversion dans ce
// sens : « RMMG × 3 / 13 / durée hebdomadaire ». Inversée, elle donne le mensuel
// à partir d'un taux horaire. On l'écrit ici plutôt que de mensualiser « comme
// d'habitude » : c'est la formule du secteur, et c'est elle qui fait foi pour
// comparer un barème horaire au plancher national.
const _mensuelCP327 = (horaire, heuresSemaine = 38) => Math.round(horaire * 13 * heuresSemaine / 3 * 100) / 100;

// Minimum RÉELLEMENT applicable : le plus élevé du barème sectoriel et du
// plancher national. Jamais l'un des deux par principe.
//
// ⚠️ POURQUOI CETTE FONCTION EXISTE (05/08/2026). Le 01/08, on avait constaté
// que TOUTES les catégories cibles de la SCP 327.01 étaient sous le plancher, et
// on avait donc figé `mensuel: RMMMG` partout. C'était juste ce jour-là, et faux
// trois jours plus tard : la fiche SPF actualisée le 04/08 (en vigueur 01/07,
// « harmonisation avec un autre barème salarial » de l'aveu même du SPF) fait
// passer la catégorie 1 à 15,0313 €/h, soit 2 475,15 €/mois — 241,54 € AU-DESSUS
// du RMMMG. Continuer à servir le plancher aurait sous-payé ces travailleurs.
// La leçon n'est pas « la valeur avait changé » mais « on avait codé une
// CONCLUSION (le plancher gagne) au lieu de la RÈGLE (le plus élevé gagne) ».
const _minCP327 = (horaire) => Math.max(_mensuelCP327(horaire), RMMMG);

// ═══ BARÈMES MINIMA SECTORIELS — CLASSE I (NON-QUALIFIÉ) ═══
// Source: CCT sectorielles + salairesminimums.be
// Auto-mis à jour par /api/cron/baremes-cp — 06h10 CET quotidien
// Ces valeurs représentent le MINIMUM ABSOLU de classe 1 pour chaque CP
export const BAREMES_CP_MIN = {
  // Tous les minima Q2 2026 — depuis 01/04/2026 (CCT 43/18 du 24/03/2026)
  // Aucun barème sectoriel ne peut être inférieur au RMMMG. Les cl1 « plancher » référencent
  // la constante RMMMG (2 233,61 € depuis le 01/07/2026) — jamais un littéral (R1).
  '200':   { cl1: RMMMG, cl2: 2222.50, cl3: 2266.16, cl4: 2614.86, nom: 'CP 200 — Employés auxiliaire' },
  '118':   { cl1: RMMMG, cl2: 2218.50, cl3: 2287.30, cl4: 2384.40, cl5: 2530.50, nom: 'CP 118 — Industrie alimentaire' },
  '119':   { cl1: RMMMG, cl2: 2219.50, cl3: 2287.30, cl4: 2433.50, nom: 'CP 119 — Commerce alimentaire' },
  '302':   { cl1: RMMMG, cl2: 2222.50, cl3: 2244.10, cl4: 2384.40, cl5: 2603.20, nom: 'CP 302 — Hôtellerie' },
  '124':   { cl1: RMMMG, cl2: 2225.50, cl3: 2384.40, cl4: 2614.20, nom: 'CP 124 — Construction' },
  // Clé passée de '32201' à '322.01' le 21/08/2026, en même temps que la
  // suppression de la fiche en double dans TAUX_SECTORIELS. Le format canonique
  // est POINTÉ ; une clé sans point était le dernier vestige du doublon.
  '322.01': { cl1: RMMMG, cl2: 2222.50, nom: 'CP 322.01 — Titres-services' },
  '330':   { cl1: RMMMG, cl2: 2218.50, cl3: 2384.40, cl4: 2643.50, cl5: 2939.30, nom: 'CP 330 — Santé' },
  '111':   { cl1: RMMMG, cl2: 2266.50, cl3: 2493.40, cl4: 2744.50, nom: 'CP 111 — Métal' },
  '140':   { cl1: RMMMG, cl2: 2225.50, cl3: 2449.50, cl4: 2785.30, nom: 'CP 140 — Transport' },
  '121':   { cl1: RMMMG, cl2: 2222.50, cl3: 2225.50, cl4: 2384.40, nom: 'CP 121 — Nettoyage' },
  '152':   { cl1: RMMMG, cl2: 2218.50, cl3: 2287.30, cl4: 2481.20, nom: 'CP 152 — Enseignement libre' },

  // ─── NOUVEAUX CP — ajoutés 2026-04 ───
  // CP 318 — Aides familiales et aides seniors (SCP 318.01)
  // Indexation +2% au 01/02/2026 | Temps travail 38h (36h COCOF aides familiales)
  '318':   {
    cl1: RMMMG, cl2: 2232.36, cl3: 2284.20, cl4: 2406.30, // cl1 relevé au RMMMG : un fallback générique 38h ne peut pas être < plancher légal (audit 2026-06-19). Valeur 36h COCOF précise = grid vérifié (prioritaire).
    nom: 'CP 318 — Aides familiales et aides seniors (SCP 318.01)',
    scp: '318.01',
    indexation: { date: '01/02/2026', pct: 0.02 },
    temps_travail: 38,
    temps_travail_cocof: 36,           // 36h pour aides familiales sous COCOF (Bruxelles)
    // Catégories génériques (toutes régions)
    categories: {
      technicienne_surf:   { label: 'Technicien de surfaces',    coeff: 1.12, horaire: 13.42, mensuel: 2174.04 },
      ouvrier_polyvalent:  { label: 'Ouvrier polyvalent',        coeff: 1.22, horaire: 13.78, mensuel: 2232.36 },
      chauffeur:           { label: 'Chauffeur',                 coeff: 1.22, horaire: 13.65, mensuel: 2211.30 },
      aide_menagere:       { label: 'Aide ménagère',             coeff: 1.22, horaire: 13.78, mensuel: 2232.36 },
      garde_domicile:      { label: 'Garde à domicile',          coeff: 1.35, horaire: 14.10, mensuel: 2284.20 },
      aide_familiale:      { label: 'Aide familiale/senior',     coeff: 1.35, horaire: 14.85, mensuel: 2406.30 },
      garde_enfants_mal:   { label: 'Garde d\'enfants malades',  coeff: 1.35, horaire: 14.85, mensuel: 2406.30 },
      assistant_social:    { label: 'Assistant social',          coeff: 1.55, horaire: 16.20, mensuel: 2625.00 },
      admin_redacteur:     { label: 'Rédacteur / Secrétaire',   coeff: 1.39, horaire: 15.10, mensuel: 2445.00 },
      admin_gradue:        { label: 'Administrateur gradué',    coeff: 1.77, horaire: 18.50, mensuel: 2996.00 },
    },
    // Classification officielle par région (source : CGSLB SCP 318.01)
    classificationRegionale: {
      wallonie: {
        personnel_technique: [
          { cat: 1, fonction: 'Technicien de surface',               echelle: 1, coeff: 1.12 },
          { cat: 2, fonction: 'Ouvrier polyvalent / Chauffeur / Aide ménagère', echelle: 2, coeff: 1.22 },
        ],
        personnel_administratif: [
          { cat: 1, fonction: 'Commis',                             echelle: 3, coeff: 1.26 },
          { cat: 2, fonction: 'Rédacteur',                          echelle: 5, coeff: 1.50 },
          { cat: 3, fonction: 'Administrateur gradué',              echelle: 6, coeff: '1.55-1.61-1.77' },
          { cat: 4, fonction: 'Administrateur licencié',            echelle: 7, coeff: 1.80 },
        ],
        personnel_social: [
          { cat: 1, fonction: 'Aide familiale / Garde à domicile (diplôme AF)', echelle: 4, coeff: 1.35 },
          { cat: 2, fonction: 'Garde d\'enfants malades',           echelle: 4, coeff: 1.35 },
          { cat: 3, fonction: 'Assistant social',                   echelle: 6, coeff: '1.55-1.61-1.77' },
          { cat: 4, fonction: 'Sociologue / Psychologue',           echelle: 7, coeff: 1.80 },
        ],
      },
      bruxelles: {
        personnel_administratif: [
          { cat: '1ère', fonction: 'Commis',                        diplome: 'Secondaire inférieur', coeff: 1.26 },
          { cat: '2ème', fonction: 'Rédacteur / Secrétaire / Comptable', diplome: 'Secondaire supérieur', coeff: 1.39 },
          { cat: '3ème', fonction: 'Secrétaire direction / Comptable', diplome: 'Sup. non univ. A1', coeff: '1.55/1.61/1.77' },
          { cat: '4ème', fonction: 'Directeur / Coordinateur A1',  diplome: 'Sup. non univ. A1', coeff: '1.78s' },
          { cat: '4ème', fonction: 'Directeur / Coordinateur univ.', diplome: 'Universitaire', coeff: 1.80 },
        ],
        personnel_social: [
          { cat: '1ère', fonction: 'Aide ménager',                  diplome: 'Aucun', coeff: 1.22 },
          { cat: '2ème', fonction: 'Aide familial',                 diplome: 'Aide familial ou assimilé', coeff: 1.35 },
          { cat: '2ème', fonction: 'Garde d\'enfants malades',      diplome: 'Puéricultrice ou AF assimilé', coeff: 1.35 },
          { cat: '2ème', fonction: 'Garde à domicile',              diplome: 'AF ou assimilé ou garde malades', coeff: 1.35 },
          { cat: '3ème', fonction: 'Assistant social / Infirmier social', diplome: 'Sup. non univ. A1 spécifique', coeff: '1.55/1.61/1.77' },
          { cat: '5ème', fonction: 'Sociologue / Psychologue',      diplome: 'Universitaire', coeff: 1.80 },
        ],
        personnel_technique: [
          { cat: '1ère', fonction: 'Technicien de surfaces',        diplome: 'Aucun', coeff: 1.22 },
          { cat: '1ère', fonction: 'Ouvrier polyvalent',            diplome: 'Aucun', coeff: 1.22 },
          { cat: '1ère', fonction: 'Chauffeur',                     diplome: 'Aucun', coeff: 1.22 },
        ],
      },
    },
    primeFA: {
      cocof_fixe: 1128.55,   // € partie fixe COCOF 2025
      cocom_fixe: 348.46,    // € partie fixe COCOM 2025
      rw_forfait: 1092.10,   // € partie forfaitaire Wallonie 2025 (note: 1092.10 employés RW — 714.74 pour ouvriers)
      rw_ouvriers: 714.74,   // € ouvriers Wallonie
      variable_pct: 0.025,   // 2,5% rémunération annuelle brute indexée
      variable_rw_h: 0.1062, // €/heure (Wallonie ouvriers)
      periode: '1er janvier → 30 septembre',
      paiement: 'decembre',
      prorata: '1/9e par mois complet dans période de référence',
      exclusions: 'Pas due si licenciement faute grave, contrat étudiant, ou remplacement si remplacé a reçu la prime',
    },
    // ⚠️ `km_missions` RETIRÉ le 24/08/2026 : la valeur (0,4327 €/km) n'était pas
    // sectorielle mais le forfait fédéral TRIMESTRIEL du 2e trimestre 2026, périmé
    // depuis le 30/06/2026 — une troisième écriture du même défaut, sous un troisième
    // nom de champ (`kmVoiture`, `kmVoiture_mission`, `km_missions`). Le tarif de
    // mission se lit dans app/lib/indemnite-km-statut.js (tarifKmMission).
    indemnites: { km_domicile: 0.25 },
    surclassements: { dimanche_ferie: 0.56, nuit: 0.50 },
    bruxelles_credit_conge: '2h/semaine (pénibilité)',
    nonMarchand: true,
    note: 'SCP 318.01. Subsidiés COCOF, COCOM, RW, Communauté germanophone. Aide ménager BXL = coeff 1.22 / Aide familiale BXL = coeff 1.35. Temps travail 36h sous COCOF pour aides familiales.',
  },

  // CP 327 — ETA, ateliers sociaux et maatwerkbedrijven
  // Indexation +2% au 01/01/2026 (×1,02) | SCP 327.01 Flandre / 327.02 COCOF / 327.03 RW+germ
  // Source : easypay-group.com, ACV/CSC PC 327, Liantis — maatwerk/ETA ×1,02 au 01/01/2026 (07/06/2026)
  // ⚖️ CP 327.01 — TRANCHÉ le 2026-08-01 (audit vague 2). Les valeurs précédentes
  // (cl1..cl5 = 2232,36 → 2785,20 ; horaires 13,78 → 17,20, CROISSANTS) ne
  // correspondaient à AUCUNE grille officielle et leur ordre était inversé.
  //
  // Grille RÉELLE, fiche SPF SCP 327.01 lue au navigateur le 2026-08-01
  // (jcId 98a3074844ec419d99ee3396355b6611), § 1.2.2 DOELGROEPWERKNEMERS :
  //   catégorie 1 = 12,5775 €/h · 2 = 11,3544 €/h · 3, 4 et 5 = 10,9686 €/h
  // (la cat. 1 est la PLUS ÉLEVÉE, l'inverse de ce qui était codé). Le personnel
  // d'encadrement relève d'une grille MENSUELLE distincte (§ 1.2.1), de
  // 2 123,18 € à 5 055,00 € selon l'ancienneté.
  //
  // 🛑 POURQUOI ON N'ÉCRIT PAS CES TAUX SECTORIELS TELS QUELS : la même fiche
  // pose, au § 1.3 « LONEN : GGMMI », que le minimum horaire se dérive du RMMMG
  // par « GGMMI × 3 / 13 / durée hebdomadaire » (CCT 30/05/2002 n° 63380).
  // Ce plancher (13,5644 €/h à 38 h) est SUPÉRIEUR à tout le barème sectoriel
  // (max 12,5775 €/h) : écrire la grille sectorielle aurait fait passer l'app
  // SOUS le minimum légal. Même l'encadrement démarre sous le RMMMG (2 123,18 €)
  // — ces grilles SPF ne sont tout simplement pas planchées à la publication.
  //
  // Mécanisme vérifié (R2 bis, pas déduit) : la CCT n° 43 a un caractère
  // IMPÉRATIF et couvre tout travailleur de 18 ans et plus sous contrat de
  // travail ; ses exceptions sont limitatives (étudiants 18-20 → CCT n° 50,
  // entreprises familiales, au pairs, stagiaires, bénévoles). Aucune dérogation
  // générale au RMMMG pour les travailleurs cibles en ETA — CNT + SPF Emploi,
  // consultés 2026-08-01. Cran 🟠 (secondaires concordantes, CCT 43 non lue
  // intégralement) : si une dérogation sectorielle apparaissait, ce plancher
  // serait à revoir — mais il est alors trop HAUT, jamais trop bas.
  //
  // Le plancher est donc appliqué, via RMMMG **importé** (R1) et exprimé en
  // MENSUEL : la fiche ne donne PAS la durée hebdomadaire, et un plancher
  // horaire en dépendrait. `horaire` est volontairement omis — les écrans le
  // dérivent eux-mêmes de `heuresSemaine` (mensualiseHoraire : × 12/52/h, soit
  // exactement la formule × 3/13/h de la CCT 63380).
  // Conforme à la convention déjà appliquée aux CP 100/118/119/302/331/332 :
  // une valeur sous plancher est relevée au RMMMG, jamais figée en littéral.
  '327':   {
    // cl1..cl5 : RESTENT au plancher RMMMG, et c'est délibéré même après le
    // relevé du 05/08/2026. Le sectoriel dépasse désormais le plancher sur les
    // catégories 1 et 2 (voir `categories` ci-dessous), mais la correspondance
    // classe générique cl1..cl5 <-> catégorie sectorielle n'est TOUJOURS pas
    // établie : les anciennes valeurs de classes étaient fabriquées, et rien
    // dans la fiche ne dit que cl1 = cat1. Monter cl1 à 2 475,15 € sur une
    // supposition déclarerait illégaux des salaires légaux — l'erreur inverse,
    // aussi coûteuse. Le chemin exact est `categories`, qui, lui, est sourcé.
    cl1: RMMMG, cl2: RMMMG, cl3: RMMMG, cl4: RMMMG, cl5: RMMMG,
    nom: 'CP 327 — Entreprises de travail adapté (ETA)',
    scp: '327.01/327.02/327.03',
    indexation: { date: '01/01/2026', pct: 0.02, source: 'easypay/ACV/Liantis — ETA +2% (×1,02) au 01/01/2026' },
    categories: {
      // SCP 327.01 (Flandre) — DOELGROEPWERKNEMERS. `horaireSectoriel` = valeur
      // brute de la fiche SPF (documentaire) ; `mensuel` = minimum APPLICABLE,
      // c.-à-d. le plancher RMMMG, qui domine partout ici.
      // Valeurs relevées le 05/08/2026 sur la fiche SPF 3270100 (jcId
      // 98a3074844ec419d99ee3396355b6611), § 1.2.2 GROUPES CIBLES, en vigueur
      // 01/07/2026. Appartenance de la table à la § 1.2.2 (et non à la § 1.3
      // RMMMG, qui la précède dans le texte aplati) ÉTABLIE PAR L'ORDRE DU DOM
      // de la fiche HTML — le PDF entrelace ses sections d'une page à l'autre et
      // faisait attribuer ce tableau à la mauvaise section.
      // `mensuel` n'est plus écrit : il se DÉRIVE (plus élevé du sectoriel et du
      // plancher), donc il suivra la prochaine indexation dans les deux sens.
      cat1: { label: 'Travailleur cible cat 1', horaireSectoriel: 15.0313, mensuel: _minCP327(15.0313), note: 'Barème sectoriel 15,0313 €/h = 2 475,15 €/mois (38 h, CCT 63380) — AU-DESSUS du plancher RMMMG : c\'est le sectoriel qui s\'applique. Relevé 05/08/2026, fiche en vigueur 01/07/2026.' },
      cat2: { label: 'Travailleur cible cat 2', horaireSectoriel: 13.5696, mensuel: _minCP327(13.5696), note: 'Barème sectoriel 13,5696 €/h = 2 234,46 €/mois — au-dessus du plancher RMMMG de 85 centimes seulement, mais au-dessus : c\'est le sectoriel qui s\'applique.' },
      cat3: { label: 'Travailleur cible cat 3', horaireSectoriel: 13.1084, mensuel: _minCP327(13.1084), note: 'Barème sectoriel 13,1084 €/h = 2 158,52 €/mois < plancher RMMMG → plancher applicable.' },
      cat4: { label: 'Travailleur cible cat 4', horaireSectoriel: 13.1084, mensuel: _minCP327(13.1084), note: 'Barème sectoriel 13,1084 €/h = 2 158,52 €/mois < plancher RMMMG → plancher applicable.' },
      cat5: { label: 'Travailleur cible cat 5', horaireSectoriel: 13.1084, mensuel: _minCP327(13.1084), note: 'Barème sectoriel 13,1084 €/h = 2 158,52 €/mois < plancher RMMMG → plancher applicable.' },
      // ⚠️ RELEVÉ le 05/08/2026 (fiche SPF 3270100 § 1.2.1, en vigueur 01/07/2026) :
      // `mensuel` reste le PLANCHER, mais uniquement parce que c'est l'ENTRÉE de la
      // catégorie la plus basse (cat 5 : 2 165,64 € < RMMMG). Les catégories
      // supérieures sont TRÈS au-dessus — cat 1 à 3 644,06 €, soit 1 410,45 € de
      // plus que le plancher, et 3 941,19 € (+1 707,58 €) côté ateliers sociaux.
      // Servir ce seul chiffre à tout l'encadrement sous-paierait massivement.
      // La grille complète (2 familles × 5 catégories × ancienneté en mois) est
      // désormais relevée dans baremes-cp-grid.js sous la clé '327' : c'est ELLE
      // qu'il faut interroger dès qu'on connaît la catégorie du travailleur.
      encadrement: { label: 'Personnel d\'encadrement (entrée, catégorie 5)', mensuel: RMMMG, note: 'Entrée cat. 5 en ETA : 2 165,64 €/mois — sous le RMMMG, donc plancher applicable. ⚠️ NE VAUT QUE POUR LA CATÉGORIE 5 : cat. 4 = 2 266,84 €, cat. 3 = 2 693,91 €, cat. 2 = 3 061,69 €, cat. 1 = 3 644,06 € (ateliers sociaux : 2 169,20 → 3 941,19 €). Grille complète relevée dans baremes-cp-grid.js clé 327, fiche SPF 3270100 au 01/07/2026.' },
      employe_adm: { label: 'Employé administratif',           mensuel: 2113.27, note: 'Barème CP 200' },
      // ETA — 7 catégories (SCP 327.02/327.03 — Wallonie / Communauté germanophone)
      // ⚠️ NON VÉRIFIÉ (2026-08-01). Seule la SCP 327.01 a été relevée à la source
      // dans cet audit ; ces 7 taux viennent du même lot que les valeurs
      // fabriquées corrigées ci-dessus et n'ont PAS été confirmés. Ils sont tous
      // sous le plancher RMMMG dérivé (13,5644 €/h à 38 h) — donc le plancher
      // prime de toute façon tant qu'ils ne sont pas relevés.
      // À faire : fiches SPF SCP 327.02 (jcId à obtenir) et 327.03 (PDF EWETA,
      // en 403 lors de la vague 1) — cf. docs/10-19 Payroll/10.03 § C.
      eta1: { label: 'ETA — Catégorie 1', horaire: 12.50 },
      eta2: { label: 'ETA — Catégorie 2', horaire: 13.00 },
      eta3: { label: 'ETA — Catégorie 3', horaire: 13.50 },
      eta4: { label: 'ETA — Catégorie 4', horaire: 14.00 },
      eta5: { label: 'ETA — Catégorie 5', horaire: 14.75 },
      eta6: { label: 'ETA — Catégorie 6', horaire: 15.50 },
      eta7: { label: 'ETA — Catégorie 7', horaire: 16.50 },
    },
    primeFA: { eta_pct: 0.0833, general: '1 mois salaire selon CCT', periode: '1 oct → 30 sep', paiement: 'avant 20 dec', note_eta: 'Non-récurrent 2025 — reconduction 2026 selon budget Non-Marchand' },
    ecoCheques: { montant: 250, versement: 'decembre', scp: '327.02' },
    heuresSupp: { seuil: 39, supplement: 0.50, dimanche: 1.00 },
    note: 'SCP 327.01 Flandre (entreprises de travail adapté). SCP 327.02 COCOF. SCP 327.03 RW/germ. 7 cat. travailleurs cibles + personnel d\'encadrement.',
  },

  // CP 331 — Vlaamse welzijns- en gezondheidssector
  // Kinderopvang + Gezondheidsinstellingen en -diensten (GID: CGG, gezondheidspromotie)
  // Barèmes IFIC (cat 1-20) en cours de déploiement depuis 01/01/2022, 100% depuis 01/01/2026 pour nouveaux
  '331':   {
    cl1: RMMMG, cl2: RMMMG, cl3: 2380.00, cl4: 2620.00, cl5: 2950.00, cl6: 3379.85, // cl1/cl2 relevés au RMMMG (étaient 2070,48/2174 < plancher légal) — audit 2026-06-19 ; valeurs IFIC précises = grid vérifié (prioritaire)
    nom: 'CP 331 — Vlaamse welzijn- en gezondheidssector',
    scp: '331',
    codeONSS: '211',
    indexation: { date: '01/01/2026', pct: 0.02, note: 'Indexation VIA non-marchand flamand — coefficient cumulé × 1,02' },
    categories: {
      kinderopvang_trap1: { label: 'Kinderopvang trap 1 (begeleider)', mensuel: 2174.00, note: 'Barème kinderopvang trap 1' },
      kinderopvang_trap2: { label: 'Kinderopvang trap 2 (verantwoordelijke)', mensuel: 2380.00, note: 'Barème kinderopvang trap 2' },
      gid_cat10:          { label: 'GID — IFIC cat 10 (begeleider)', mensuel: 2620.00, note: 'Barème IFIC cat 10 — 0 ans anc.' },
      gid_cat14:          { label: 'GID — IFIC cat 14 (infirmier spécialisé)', mensuel: 3447.52, note: 'Barème IFIC cat 14 — 0 ans anc. (index 01/07/2026, fichier IFIC relevé le 03/10/2026)' },
      gid_cat16:          { label: 'GID — IFIC cat 16 (coordinateur)', mensuel: 3950.00, note: 'Barème IFIC cat 16 — 0 ans anc.' },
    },
    ific: {
      applicable: true,
      depuis: '01/01/2022',
      deploiement100: '01/01/2026',
      categories: '1 à 20',
      anciennete_max: 35,
      note: 'Depuis 01/01/2026, tout nouveau travailleur est directement sous IFIC. Travailleurs existants : droit de choix.',
    },
    dureeTravail: { hebdomadaire: 38, note: 'Régime standard non-marchand' },
    primeFA: { type: '~90% salaire brut mensuel', periode: 'année civile', paiement: 'decembre' },
    prestationsIrregulieres: {
      nuit: { taux: 0.35, heures: '20h-6h' },
      samedi: { taux: 0.26 },
      dimanche: { taux: 0.56 },
      ferie: { taux: 0.56 },
    },
    fondsSocial: true,
    fondsSocialNom: 'Sociaal Fonds 331',
    formation: 'VIVO (vivosocialprofit.org)',
    note: 'Secteur flamand non-marchand. Kinderopvang + GID. Barèmes IFIC cat 1-20. Financé via VIA (Vlaams Intersectoraal Akkoord).',
  },

  // CP 332 — Aide sociale et soins de santé (secteur francophone et germanophone)
  // Barèmes IFIC (coefficient × salaire de référence) | Coefficient 2026 : 2,1647
  // Champ : ASBL aide sociale, MAE, maisons repos, services santé mentale...
  '332':   {
    cl1: RMMMG, cl2: RMMMG, cl3: 2220.00, cl4: 2340.00, cl5: 2430.00, cl6: 2780.00, // cl1/cl2 relevés au RMMMG (étaient 1950/1970 < plancher légal) — audit 2026-06-19 ; valeurs IFIC précises = grid vérifié (prioritaire)
    nom: 'CP 332 — Aide sociale et soins de santé (Fr/Ger)',
    scp: '332',
    codeONSS: '049',
    indexation: { date: '01/01/2026', coefficient: 2.1647, note: 'Barèmes IFIC × coefficient' },
    // Classifications IFIC (coefficient × salaire de référence IFIC)
    categories: {
      // MAE (Milieux d'Accueil d'Enfants)
      mae_ouvrier:    { label: 'Ouvrier non qualifié',                    coeff: 1.12, mensuel: 1950.00 },
      mae_semi_qual:  { label: 'Ouvrier semi-qualifié',                   coeff: 1.14, mensuel: 1970.00 },
      aide_familiale: { label: 'Aide familiale (36h COCOF)',              coeff: 1.35, mensuel: 2220.00, temps_travail: 36 },
      mae_puer_qual:  { label: 'Puériculteur(trice) qualifié(e)',          coeff: 1.35, mensuel: 2220.00 },
      mae_admin:      { label: 'Personnel administratif',                 coeff: 1.50, mensuel: 2430.00 },
      mae_inf_brev:   { label: 'Infirmier(ère) breveté(e)',               coeff: 1.43, mensuel: 2340.00 },
      mae_inf_grade:  { label: 'Infirmier gradué / Ass. social responsable', coeff: 1.77, mensuel: 2780.00 },
      infirmier_sp:   { label: 'Infirmier avec spécialisation',           coeff: 1.77, mensuel: 2780.00 },
    },
    primeFA: { cocof_fixe: 1128.55, cocom_fixe: 348.46, rw_forfait: 1092.10, variable_pct: 0.025, periode: '1 jan → 30 sep', paiement: 'decembre' },
    // ⚠️ `km_service` RETIRÉ le 24/08/2026 — SIXIÈME nom de champ pour la même copie.
    // 0,4761 €/km est le forfait fédéral ANNUEL, pas un tarif de ce secteur : l'écran
    // de la CP l'affichait comme un avantage sectoriel. La valeur est exacte aujourd'hui,
    // mais elle sera fausse au 01/07/2027 — c'est exactement la mécanique de l'incident
    // RMMMG. Tarif applicable : app/lib/indemnite-km-statut.js.
    conge_communautaire: { jours_an: 3, credit_conge_bxl_h_sem: 2 },
    surclassements: { dimanche_ferie: 0.56, nuit: 0.50, samedi_rw: 0.26, nuit_rw: 0.35 },
    // Vélo CP 332 : la CCT sectorielle lie l'indemnité au max exonéré art. 38 §1er 14° CIR
    // (0,36 en 2025 → 0,37 dès 01/01/2026). L'ancien « 0,10 → 0,32 au 01/10/2026 » était une
    // mesure CP 200 importée par erreur. Synova CP 332 + Securex, consulté 2026-07-07.
    transport: { train_pct: 0.80, velo_km: 0.37 },
    nonMarchand: true,
    note: 'Barèmes IFIC (coefficient × base). Subventions : COCOF, COCOM, RW, ONE. Coefficient indexation 2026 = 2,1647.',
  },

  dateMAJ: '2026-04-10',
};

// ═══ PLANCHER LÉGAL ABSOLU (CCT 43, art. 3) ═══
// Aucun travailleur ne peut être rémunéré sous le RMMMG, quel que soit son barème sectoriel.
// Les minima ci-dessus datent du 01/04/2026 (Q2). L'indexation du RMMMG au 01/07/2026
// (2 189,81 € → 2 233,61 €) en a fait passer 14 SOUS le plancher légal, dans 13 CP.
// On les relève au RMMMG : clamper vers le haut ne peut jamais sous-payer.
// ⚠️ Ce n'est PAS une re-source des barèmes sectoriels : la plupart des CP s'indexent aussi
// au 01/07/2026, souvent au-delà du RMMMG. Les valeurs réelles restent À RE-SOURCER par CP.
for (const cp of Object.keys(BAREMES_CP_MIN)) {
  const entry = BAREMES_CP_MIN[cp];
  if (!entry || typeof entry !== 'object') continue;
  for (const k of Object.keys(entry)) {
    if (k.startsWith('cl') && typeof entry[k] === 'number' && entry[k] < RMMMG) entry[k] = RMMMG;
  }
}

// ═══ AUTO-UPDATE SYSTEM — Constantes patchees en runtime via Supabase ═══
// La veille (/api/cron/legal-watch) ecrit dans la table `legal_constants` quand elle detecte un changement.
// Cette fonction lit les overrides et patche LOIS_BELGES automatiquement.
// Appeler initLoisBelgesOverrides() au demarrage de l'app (layout-client.js).
var _overridesApplied = false;
var _overridesCache = null;

const OVERRIDE_MAP = {
  'RMMMG':           (v) => { LB.remuneration.RMMMG.montant18ans = v; LB.remuneration.RMMMG.montant20ans6m = v; LB.remuneration.RMMMG.montant21ans12m = v; },
  'TX_ONSS_W':       (v) => { LB.onss.travailleur = v; },
  'TX_ONSS_E':       (v) => { LB.onss.employeur.total = v; },
  'INDEX_PIVOT':     (v) => { LB.remuneration.indexSante.pivot = v; },
  'INDEX_COEFF':     (v) => { LB.remuneration.indexSante.coeff = v; },
  'QE_BAREME1':      (v) => { LB.pp.quotiteExemptee.bareme1 = v; },
  'QE_BAREME2':      (v) => { LB.pp.quotiteExemptee.bareme2 = v; },
  // ⚠️ 20/08/2026 — ces trois overrides écrivaient dans la clé morte
  // pp.bonusEmploi (supprimée). Ils pointent sur la clé legacy d'affichage
  // pp.werkbonus (dépréciée mais vivante — audit-doc la lit) ; le MOTEUR,
  // lui, suit werkbonusSocial par palier daté et n'est pas surchargeable ici.
  'BONUS_MAX':       (v) => { LB.pp.werkbonus.maxReduction = v; },
  'BONUS_SEUIL1':    (v) => { LB.pp.werkbonus.seuilBrut1 = v; },
  'BONUS_SEUIL2':    (v) => { LB.pp.werkbonus.seuilBrut2 = v; },
  'FRAIS_PRO_PCT':   (v) => { LB.pp.fraisPro.salarie.pct = v; },
  'FRAIS_PRO_MAX':   (v) => { LB.pp.fraisPro.salarie.max = v; },
  'CR_MAX':          (v) => { LB.chequesRepas.valeurFaciale.max = v; },
  'CR_PAT_MAX':      (v) => { LB.chequesRepas.partPatronale.max = v; },
  'CR_W_MIN':        (v) => { LB.chequesRepas.partTravailleur.min = v; },
  'FORF_BUREAU':     (v) => { LB.fraisPropres.forfaitBureau.max = v; LB.avantages.fraisPropres.bureau = v; },
  'FORF_KM':         (v) => { LB.fraisPropres.forfaitDeplacement.voiture = v; LB.avantages.fraisPropres.km = v; },
  // ⚠️ Depuis le 29/08/2026 le forfait de bureau est DATÉ (paliers). Écrire le
  // seul `.max` ne change plus rien à ce qu'affichent les surfaces : elles lisent
  // forfaitTeletravailAu(). Le setter synchronise donc aussi le DERNIER palier,
  // sinon la veille « appliquerait » un montant sans effet — un moteur dormant.
  // Limite assumée et à ne pas contourner : ce canal ne porte PAS de date. Un
  // vrai nouveau palier (montant + date d'entrée en vigueur + source) s'ajoute à
  // la main dans `paliers`, gate R2. Ici on ne fait que corriger le dernier.
  'FORF_TELETRAVAIL':(v) => {
    LB.fraisPropres.teletravail.max = v;
    LB.avantages.fraisPropres.teletravail = v;
    const p = LB.fraisPropres.teletravail.paliers;
    if (Array.isArray(p) && p.length) p[p.length - 1].max = v;
  },
  'ATN_GSM':         (v) => { LB.atn.gsm.forfait = v; LB.avantages.atnGSM = v; },
  'ATN_PC':          (v) => { LB.atn.pc.forfait = v; LB.avantages.atnPC = v; },
  'ATN_INTERNET':    (v) => { LB.atn.internet.forfait = v; LB.avantages.atnInternet = v; },
  'CO2_ESSENCE':     (v) => { LB.atn.voiture.CO2Ref.essence = v; },
  'CO2_DIESEL':      (v) => { LB.atn.voiture.CO2Ref.diesel = v; },
  'SAISIE_IMMUN_ENFANT': (v) => { /* runtime only — SAISIE_IMMUN_ENFANT_2026 est un export const, non mutable */ },
  // Les DEUX chemins, sinon l'override ferait diverger le plafond affiché de celui calculé.
  'ECO_MAX':         (v) => { LB.avantages.ecoMax = v; LB.ecoCheques.maxAnnuelParTravailleur = v; },
  'PV_SIMPLE_PCT':   (v) => { LB.remuneration.peculeVacances.simple.pct = v; },
  'PV_DOUBLE_PCT':   (v) => { LB.remuneration.peculeVacances.double.pct = v; },
  // ═══ PP TRANCHES — CRITIQUES pour le calcul du precompte ═══
  'PP_T1_MAX':       (v) => { LB.pp.tranches[0].max = v; LB.pp.tranches[1].min = v; },
  'PP_T1_TAUX':      (v) => { LB.pp.tranches[0].taux = v; },
  'PP_T2_MAX':       (v) => { LB.pp.tranches[1].max = v; LB.pp.tranches[2].min = v; },
  'PP_T2_TAUX':      (v) => { LB.pp.tranches[1].taux = v; },
  'PP_T3_MAX':       (v) => { LB.pp.tranches[2].max = v; LB.pp.tranches[3].min = v; },
  'PP_T3_TAUX':      (v) => { LB.pp.tranches[2].taux = v; },
  'PP_T4_TAUX':      (v) => { LB.pp.tranches[3].taux = v; },
  // ═══ CSSS BARÈMES — Seuils trimestriels ═══
  // ⚠️ Corrigé 2026-07-02 : les handlers écrivaient sur LB.csss.isole[0].max — structure INEXISTANTE
  // (la vraie est .tranches[i].trimMin/trimMax) → TypeError garanti au premier passage du cron.
  // NB : les montants CSSS ne sont PAS indexés (inchangés depuis le 01/04/2022, ONSS) — handlers
  // défensifs, gardés par optional chaining.
  'CSSS_SEUIL1':     (v) => { for (const r of [LB.csss.isole, LB.csss.menage2revenus, LB.csss.menage1revenu]) { const t = r?.tranches; if (t?.[0] && t?.[1]) { t[0].trimMax = v; t[1].trimMin = v; } } },
  'CSSS_SEUIL2':     (v) => { for (const r of [LB.csss.isole, LB.csss.menage2revenus, LB.csss.menage1revenu]) { const t = r?.tranches; if (t?.[1] && t?.[2]) { t[1].trimMax = v; t[2].trimMin = v; } } },
  'CSSS_PLAFOND':    (v) => { for (const r of [LB.csss.isole, LB.csss.menage2revenus, LB.csss.menage1revenu]) { const t = r?.tranches; const last = t?.[t.length - 1]; if (last) { if (last.maxTrim != null) last.maxTrim = v; else if (last.forfaitTrim != null) last.forfaitTrim = v; } } },
  // ═══ RÉDUCTIONS ENFANTS ═══
  'RED_ENFANT_1':    (v) => { LB.pp.reductionsEnfants[1] = v; },
  'RED_ENFANT_2':    (v) => { LB.pp.reductionsEnfants[2] = v; },
  'RED_ENFANT_3':    (v) => { LB.pp.reductionsEnfants[3] = v; },
  'RED_ENFANT_4':    (v) => { LB.pp.reductionsEnfants[4] = v; },
  'RED_ENFANT_SUPP': (v) => { LB.pp.reductionEnfantSupp = v; },
  'RED_PARENT_ISOLE':(v) => { LB.pp.reductionParentIsole = v; },
  'RED_HANDICAPE':   (v) => { LB.pp.reductionHandicape = v; },
  // ═══ QUOTIENT CONJUGAL ═══
  'QC_PCT':          (v) => { LB.pp.quotientConjugal.pct = v; },
  'QC_MAX':          (v) => { LB.pp.quotientConjugal.max = v; },
};

// ── PORTÉE RÉELLE d'un override (ajouté 20/08/2026) ────────────────────────
// `applyOverrides` ne sait patcher que les clés PRÉSENTES dans OVERRIDE_MAP :
// une clé absente est ignorée EN SILENCE. Le bandeau du dashboard promettait
// pourtant « validez pour appliquer en live » pour toutes les clés — alors que
// 26 des 33 clés surveillées par le watchdog n'y sont pas. On expose la liste
// pour que l'UI puisse DIRE la vérité au lieu de faire disparaître l'alerte.
// ⚠️ Deux limites qui font partie du message honnête :
//   1. un override patche le chemin IMBRIQUÉ (LB.x.y) et JAMAIS les exports
//      PLATS (`export const RMMMG = …`) figés à la compilation — seul un report
//      à la source (PR) change ce que lit le moteur de paie ;
//   2. être dans la map ne suffit pas : SAISIE_IMMUN_ENFANT y figure avec un
//      handler volontairement INERTE (sa cible est un `export const` non
//      mutable). Présent ≠ appliqué : il est exclu de la liste ci-dessous.
const OVERRIDE_INERTES = new Set(['SAISIE_IMMUN_ENFANT']);
export const OVERRIDE_LIVE_KEYS = Object.freeze(
  Object.keys(OVERRIDE_MAP).filter((k) => !OVERRIDE_INERTES.has(k))
);

// ═══════════════════════════════════════════════════════════════════════════
// ÉCO-CHÈQUES SCP 140.03 — un régime CONDITIONNEL, pas un montant sectoriel
// ✅ TEXTE PRIMAIRE LU LE 15/09/2026 — CCT n° 199085/CO/140.03 du 19/03/2026,
// « Introduction et augmentation du titre-repas et conversion des écochèques »
// (banque CCT du SPF Emploi, 14003/14003-2026-004120.pdf ; déposée le 24/03/2026,
// enregistrée le 07/04/2026 ; effet au 01/01/2026, durée indéterminée ; remplace
// la CCT du 20/01/2022, n° 171618) :
//   · art. 1 §4 — « ouvriers » = personnel NON ROULANT, garage compris, étudiants
//     (code travailleur 840) exclus ;
//   · art. 3 — « Les entreprises qui, au 1er janvier 2016, n'octroient ni
//     titres-repas ni écochèques à une catégorie des ouvriers concernés,
//     octroient des écochèques pour un montant de € 200, dès l'année 2016. » ;
//   · art. 5 — période de référence = année civile, octroi en fin de période,
//     prorata de la durée de travail et des périodes sous contrat ;
//   · art. 7 — l'employeur tenu par l'art. 3 peut les REMPLACER par des
//     titres-repas (CCT d'entreprise, ou convention individuelle sans délégation
//     syndicale) ;
//   · art. 2 et 8 — les autres entreprises ont augmenté leurs titres-repas ou
//     converti leurs écochèques en titres-repas dès 2016.
// ⚠️ Les fiches '140' et '140.03' portaient `ecoChequesAnnuel: 250` — la valeur
// recopiée sur 155 fiches — et l'app l'annonçait comme un dû pour tout le
// secteur. Un nombre seul ne sait pas dire « 200 €, aux seuls non roulants, chez
// l'employeur qui n'avait rien en 2016 » : d'où cet objet, lu par
// eco-cheques-statut.js (statut « conditionnel », jamais annonçable comme une
// obligation générale).
// 🔎 Personnel ROULANT : aucune convention éco-chèques repérée. Mesure du
// 15/09/2026 : 1 059 conventions CP 140 / SCP 140.03 de la banque CCT filtrées par
// titre et thème (43 retenues) ; d'après le champ d'application publié par le SPF,
// celles qui visent les éco-chèques du transport de choses couvrent le non roulant
// ou le garage. La fiche Limosa du roulant (16/01/2026) n'en dit rien, mais ce
// document ne couvre jamais les éco-chèques : son silence ne prouve pas l'absence
// (R2 ter). Question ouverte, pas tranchée.
const ECO_CHEQUES_CONDITIONNELS_14003 = Object.freeze({
  montant: 200,
  population: 'non_roulant',
  condition: "Ouvriers non roulants (garage compris, étudiants exclus), et seulement chez l'employeur qui, au 01/01/2016, n'octroyait ni titres-repas ni écochèques à la catégorie concernée ; remplaçables par des titres-repas (CCT d'entreprise ou convention individuelle).",
  convertibleTitresRepas: true,
  periodeReference: 'annee_civile',
  personnelRoulant: "Aucune convention éco-chèques repérée (1 059 conventions CP 140 / SCP 140.03 filtrées le 15/09/2026) — question ouverte, pas une absence prouvée.",
  source: "CCT n° 199085/CO/140.03 du 19/03/2026, art. 1 §4, 3, 5 et 7 — banque CCT du SPF Emploi, 14003/14003-2026-004120.pdf, texte primaire lu le 15/09/2026.",
  dateVerif: '2026-09-15',
});

const ECO_CHEQUES_CONDITIONNELS_20201 = Object.freeze({
  montant: 250,
  population: 'temps_plein_reference_complete',
  condition: "Une prime annuelle de 250 EUR (charges patronales incluses) est due à tout employé occupé à temps plein sur une période de référence complète (01/04-31/03) ; l'employeur CHOISIT entre l'octroi en éco-chèque OU le paiement d'une prime brute de 188 EUR hors charges patronales. Le montant en éco-chèque n'est donc pas automatique — délégation syndicale : autres règles négociables au niveau de l'entreprise. Étudiants sous contrat d'occupation exclus (art. 5). Temps partiel : au prorata des prestations (art. 6).",
  choixParEmployeur: true,
  alternativeCash: 188,
  periodeReference: '01/04-31/03',
  source: "CCT du 04/09/2017 (142297/CO/202.01), art. 4-6, banque CCT du SPF Emploi, 20201/20201-2017-012691.pdf, texte primaire lu le 21/09/2026. En vigueur depuis le 01/08/2017, durée indéterminée, remplace la CCT du 04/02/2014 (120899/CO/202.01).",
  dateVerif: '2026-09-21',
});

// ═══════════════════════════════════════════════════════════════════════════
// TAUX SECTORIELS — Cotisations fonds de sécurité d'existence par CP
// Source : ONSS instructions 2024-2026, CCT sectorielles, arrêtés royaux
// Mise à jour : avril 2026
// ═══════════════════════════════════════════════════════════════════════════
export const TAUX_SECTORIELS = {
  // CP 200 — Commission Paritaire Auxiliaire pour Employés
  // ⚠️ Module 11 — Q1 2026 — LE PLUS GROS SECTEUR BELGE (500 000+ travailleurs)
  // Sources : Prestataire C (indexation +2,21%), CGSLB CP 200, Sfonds200, CSC CSCBIE,
  // ICB-FR, salairesminimums.be SCP 2000000, calcul-indexation.be
  '200': {
    nom: 'CP 200 — Commission Paritaire Auxiliaire pour Employés',
    nomCourt: 'Employés (CP 200)',
    regimeHebdo: 38,
    // `formule` (2026-08-05) : METADATA de calcul, pas une valeur légale — elle dit COMMENT le
    // taux se calcule, jamais combien il vaut. Lue par app/lib/indexation-formule.js, qui
    // RECALCULE le % et le confronte à `dernierTaux` au lieu de le recopier d'une publication.
    // Vérifiée : Agoria et SD Worx publient la formule CP 200 ET son application chiffrée pour
    // le 01/01/2026 — (lissé nov+déc 2025) / (lissé nov+déc 2024) = 2,21 %, taux appliqué.
    // Seule CP portant une formule explicite à ce stade : les autres secteurs affichant 2,21 %
    // ne sont PAS présumés partager cette formule (coïncidence numérique ≠ source, R2 bis).
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', formule: 'sante_lisse_nov_dec' },
    fondsSE: { nom: 'Fonds social CP 200 / CEFORA', taux_patronal: 0.0023, note: '0,23% Fonds social CP 200 (accord sectoriel 2026-2027, source: Liantis + SSN). Cotisation CEFORA pour formation séparément. 2ème pilier de pension complémentaire selon CCT entreprise (généralement ~2-5%).' },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 0, note: 'Non obligatoires sectoriellement, mais quasi-systématiques en CP 200. Si octroyés : selon CCT entreprise.' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 09/06/2016 « concernant les écochèques » (200/200-2016-…pdf, W:
    // 134425/CO/200), entrée en vigueur le 01/07/2016, durée indéterminée,
    // toujours en vigueur — abroge la CCT du 16/07/2009 (CP 218 transférée à
    // la CP 200). Art. 4 §1 : « une fois par an, des écochèques à chaque
    // employé occupé à temps plein avec une période de référence complète
    // d'une valeur de 250 EUR. » §2 — temps partiel : 250 EUR dès 4/5e,
    // 200 EUR dès 3/5e, 150 EUR dès 1/2, 100 EUR en-deçà (paliers propres à
    // cette CP, différents de ceux des CP 144/145). Paiement en juin (§3).
    // Chapitre IV : conversion possible en entreprise selon modalités strictes.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 09/06/2016 « concernant les écochèques » (200/200-2016-…pdf, W: 134425/CO/200), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 4 : 250 EUR/an temps plein, paliers 250/200/150/100 EUR temps partiel, paiement en juin, en vigueur depuis le 01/07/2016.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee: 'Salaire mensuel brut décembre (1 mois brut)',
    primeAnnuelle_2026: 330.84,                // EUR brut au 01/01/2026 (indexée chaque année)
    primeAnnuelle_paiement: 'juin',
    primeAnnuelle_convertible: true,           // peut être convertie en chèques-repas/avantages
    formation: { jours: 5, fonds: 'CEFORA' },
    cotisationSpeciale: null,

    // ── Catégories CP 200 — 4 classes A/B/C/D ──
    // Source : barème CSC CSCBIE 01/01/2026 (indexation +2,21%)
    // 2 grilles : Barème 1 (entrée en service) / Barème 2 (après 1 an)
    // Q1 2026 : valeurs de base (à compléter avec ancienneté barémique)
    classifications: {
      source: "CCT du 09/06/2016 (CP 200, employés), « Classification professionnelle », dépôt banque CCT du SPF, texte primaire lu le 25/08/2026 : les fonctions d'employé sont classées en QUATRE CLASSES A a D, definies par des criteres — classe A fonctions caracterisees par un nombre limite de taches ; classe B fonctions apportant une contribution a la realisation d'une mission plus large ; classe C fonctions caracterisees par un ensemble complet de taches constituant une seule et meme mission ; classe D fonctions caracterisees par la surveillance et le pilotage d'un meme processus. Une LISTE DE FONCTIONS EXEMPLATIVES par classe figure en annexe de la CCT. ⚠️ Les classes A-D etaient deja codees ici ; cette lecture les CONFIRME, aucune valeur n'a change.",
      dateVerif: '2026-08-25',
      A: 'Fonctions exécutives — tâches simples et répétitives',
      B: 'Fonctions de support — contribution à mission plus vaste',
      C: 'Fonctions de gestion — ensemble complet de tâches autonomes',
      D: 'Fonctions de spécialiste — autonomie technique, expertise',
    },
    // Barème 1 (entrée en service, 0 an) — Q1 2026 indexé +2,21%
    // Sources : CSC CSCBIE chronique-cp200-salaires-01012026 + Sfonds200
    categoriesEmployes: {
      // Classe A
      A_0:  { code: 'A0',  classe: 'A', label: 'Classe A — 0 an ancienneté',                salaireMensuel: 2189.81, niveau: 'Exécutive' },
      A_1:  { code: 'A1',  classe: 'A', label: 'Classe A — 1 an ancienneté',                salaireMensuel: 2200.00, niveau: 'Exécutive' },
      A_4:  { code: 'A4',  classe: 'A', label: 'Classe A — 4+ ans ancienneté',              salaireMensuel: 2280.00, niveau: 'Exécutive' },
      // Classe B
      B_0:  { code: 'B0',  classe: 'B', label: 'Classe B — 0 an ancienneté',                salaireMensuel: 2174.00, niveau: 'Support' },
      B_1:  { code: 'B1',  classe: 'B', label: 'Classe B — 1 an ancienneté',                salaireMensuel: 2230.00, niveau: 'Support' },
      B_4:  { code: 'B4',  classe: 'B', label: 'Classe B — 4+ ans ancienneté',              salaireMensuel: 2370.00, niveau: 'Support' },
      // Classe C
      C_0:  { code: 'C0',  classe: 'C', label: 'Classe C — 0 an ancienneté',                salaireMensuel: 2266.16, niveau: 'Gestion' },
      C_1:  { code: 'C1',  classe: 'C', label: 'Classe C — 1 an ancienneté',                salaireMensuel: 2350.00, niveau: 'Gestion' },
      C_4:  { code: 'C4',  classe: 'C', label: 'Classe C — 4+ ans ancienneté',              salaireMensuel: 2530.00, niveau: 'Gestion' },
      C_8:  { code: 'C8',  classe: 'C', label: 'Classe C — 8+ ans ancienneté',              salaireMensuel: 2700.00, niveau: 'Gestion' },
      // Classe D
      D_0:  { code: 'D0',  classe: 'D', label: 'Classe D — 0 an ancienneté',                salaireMensuel: 2614.86, niveau: 'Spécialiste' },
      D_4:  { code: 'D4',  classe: 'D', label: 'Classe D — 4+ ans ancienneté',              salaireMensuel: 2880.00, niveau: 'Spécialiste' },
      D_10: { code: 'D10', classe: 'D', label: 'Classe D — 10+ ans ancienneté',             salaireMensuel: 3200.00, niveau: 'Spécialiste' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      train_pct: 0.80,                          // 80% carte train 2e classe (1er km)
      busTram_pct: 0.75,                        // 75% si prix selon distance
      busTram_pct_forfait: 0.718,               // 71,8% si prix forfait
      transportPrive_pct: 0.50,                 // 50% si salaire < 36 688 €/an
      transportPrive_seuilSalaire: 36688,
      velo_2026: 0.27,                          // EUR/km 01/07/2024→30/09/2026 — CCT sectorielle CP 200 (CCT n°164 non applicable), sfonds200.be, consulté 2026-07-07
      velo_octobre_2026: 0.32,                  // EUR/km dès 01/10/2026 (accord sectoriel 18/12/2025, CCT 15/01/2026)
      velo_max_jour: 10.80,                     // EUR/jour (max 40 km AR) jusqu'au 30/09/2026
      velo_max_jour_octobre_2026: 12.80,        // EUR/jour (max 40 km AR) dès 01/10/2026 — sfonds200.be + Liantis, consulté 2026-07-07
      // Prime fin de carrière 4/5
      primeFinCarriere_4_5: 92.45,              // EUR/mois au 01/01/2026 (Fonds social)
    },

    avantages: {
      delegationSyndicale_seuil: 25,            // travailleurs
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'Prestataire C + CGSLB CP 200 + Sfonds200 + CSC CSCBIE chronique 01/01/2026',
      indexationFrequence: 'Annuelle au 01/01',
      indexationQ1_2026: 0.0221,                // +2,21% confirmé
      classification: '4 classes (A/B/C/D) × ancienneté barémique',
      nbCategories: 13,                          // valeurs typiques selon ancienneté
      historique: 'CP 218 abrogée 01/04/2015, fusionnée dans CP 200',
      notes: 'LE PLUS GROS SECTEUR EMPLOYÉS BELGE (500 000+ travailleurs). Couvre tous les employés sans CP spécifique : consultance, marketing, services administratifs, employés de garages/construction (administration), bureaux d\'études, agences immobilières, etc. 69 fonctions exemplatives officielles.',
      nouveautes2026: [
        'Indexation +2,21% au 01/01/2026 confirmée (Prestataire C)',
        'Prime annuelle 330,84 € en juin 2026 (vs 250 € initial)',
        'Indemnité vélo 0,27 €/km → 0,32 €/km au 01/10/2026',
        'Prime fin de carrière 4/5 = 92,45 €/mois (Fonds social)',
        // ⚠️ Cette ligne a affirmé pendant deux mois que le plafonnement n'était « pas encore
        // voté », alors que la loi-programme du 30/05/2026 est en vigueur depuis le 01/06/2026 et
        // que `moderationSalariale` ci-dessus la porte, texte primaire lu. La donnée était juste,
        // trois surfaces d'affichage mentaient (R2 ter-B). Aucun chiffre ici : il vit dans la
        // constante, pas dans la phrase (R1).
        'Plafonnement de l\'indexation : loi-programme du 30/05/2026, EN VIGUEUR depuis le 01/06/2026 (voir moderationSalariale)',
      ],
    },
  },

  // CP 302 — Hôtellerie (HORECA) standard — régime CDI/CDD
  // ⚠️ Module HORECA hot-fix 27/04/2026 : indexation +2,189% au 01/01/2026 confirmée
  // Sources concordantes : CGSLB CP 302, Horeca Wallonie 12/2025, Horeca Bruxelles 12/2025,
  // Prestataire C 23/12/2025, Prestataire A, Pro-Pay, Horeca Forma
  // Note : pour Extras Horeca (forfait journalier/horaire), voir horeca-engine.js (CP 302 EXT)
  '302': {
    nom: 'CP 302 — Industrie hôtelière (HORECA standard, hors extras)',
    nomCourt: 'HORECA',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02189, derniereDate: '2026-01-01', note: 'Indexation +2,189% au 01/01/2026 sur barèmes ET salaires effectifs (CGSLB, Horeca Wallonie)' },

    fondsSE: {
      nom: 'Fonds social et de garantie HORECA',
      // ✅ LE « ENVIRON 12 % » ÉTAIT FAUX D'UN FACTEUR HUIT, et les statuts le
      // disent article par article. CCT du 20/12/2017 fixant les statuts du
      // Fonds social et de garantie (banque CCT du SPF, 302/302-2017-015379.pdf),
      // article 11, texte primaire lu le 25/08/2026 :
      //   § 1  0,70 % du salaire pris en considération pour le calcul des
      //        cotisations de sécurité sociale
      //   § 2 +0,10 % cotisation complémentaire (promotion de l'emploi, loi du
      //        26/07/1996)
      //   § 3 +0,05 % financement de la formation syndicale
      //   § 4 +0,60 % depuis le 2e trimestre 2008 (0,70 % au 1er trimestre)
      //   somme : 1,45 %
      // L'ONSS perçoit 1,40 % en régime général et 1,45 % à partir de
      // 50 travailleurs. La somme des statuts retombe exactement sur le régime
      // des grandes entreprises ; le taux servi est celui du régime général,
      // comme partout ailleurs dans ce registre.
      // 🟠 Les 0,05 point d'écart entre les deux régimes ne sont pas rattachés à
      //    un paragraphe précis : à trancher si le seuil de 50 est branché.
      taux_patronal: 0.014,
      taux_patronal_employe: 0.014,
      tauxComposantes: {
        base: 0.007,                    // art. 11 § 1
        promotionEmploi: 0.001,         // art. 11 § 2
        formationSyndicale: 0.0005,     // art. 11 § 3
        complementaireDepuis2008T2: 0.006, // art. 11 § 4
      },
      variantesOnss: [{ code: '820', type: '5', taux: 1.45, condition: '50 travailleurs et plus' }],
      base108Ouvrier: true,
      onssCategorie: '017, 317, 417',
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "CCT du 20/12/2017 fixant les statuts du Fonds social et de garantie pour les hôtels, restaurants, cafés et entreprises assimilées, article 11 §§ 1 à 4 (banque CCT du SPF Emploi, 302/302-2017-015379.pdf), texte primaire lu le 25/08/2026 : 0,70 + 0,10 + 0,05 + 0,60 = 1,45 %. Recoupé avec le fichier des taux DmfA 2026/3 de l'ONSS, catégories 017, 317 et 417 : 1,40 % en régime général, 1,45 % à partir de 50 travailleurs.",
      note: "Valeur précédente : « environ 12 % incluant fonds fermeture sectoriel, fonds formation Horeca Forma, fonds social » — aucune source, et un facteur huit d'écart avec les statuts. Le fonds de fermeture d'entreprises est FÉDÉRAL et perçu à part ; Horeca Forma est financé PAR cette cotisation, il ne s'y ajoute pas. La pension sectorielle (1,10 %, code 825) reste distincte.",
    },
    chequesRepas: {
      obligatoire: false,
      note: 'Non obligatoire en CP 302 standard. Si octroyés : selon CCT entreprise.',
    },
    // 🔴 CORRIGÉ LE 25/09/2026 — `0` ÉTAIT FAUX, aucune source ne l'appuyait.
    // Recherche CCT (cct-search.yml, run 25/09/2026, 4 orthographes) : CCT du
    // 27/06/2018 (146640/CO/302, 302/302-2018-009610.pdf), TOUJOURS
    // D'APPLICATION (prolongée pour durée indéterminée). Art. 4 § 1 : « une
    // prime de maximum 250 euros par an […] octroyée sous la forme d'éco
    // chèques » (CCT n° 98/98quinquies, art. 19quater AR 28/11/1969). Art. 4
    // § 2 : substituable au niveau entreprise par des chèques-repas ou un
    // avantage équivalent individualisable, sur accord d'entreprise. Exclut
    // les étudiants (art. B/3). C'est un PLAFOND, pas un montant garanti fixe
    // — porté ici en valeur par défaut faute de mieux, comme les autres
    // fiches à `ecoChequesAnnuel` numérique.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 27/06/2018 (n° d'enregistrement 146640/CO/302, banque CCT du SPF Emploi, 302/302-2018-009610.pdf), toujours d'application. Art. 4 § 1 : prime annuelle de maximum 250 € versée en éco-chèques, en exécution de la CCT n° 98/98quinquies (CNT) et de l'art. 19quater de l'AR du 28/11/1969. Art. 4 § 2 : substitution possible au niveau entreprise (chèques-repas ou avantage équivalent). Recherche cct-search.yml (run du 25/09/2026, 4 orthographes eco cheque/ecocheque/ecocheques/éco-chèques) : 1 seul résultat récurrent trouvé.",
    ecoChequesDateVerif: '2026-09-25',
    primeFDA: {
      type: 'Payée par Fonds social Horeca',
      paiement: 'Au plus tard le 31 janvier N+1',
      prorata: true,
      note: 'La majeure partie de la prime de fin d\'année est financée durant l\'année via la cotisation FSE.',
    },
    formation: { jours: 5, fonds: 'Horeca Forma' },
    cotisationSpeciale: { amiante: false },
    pourboires: true,
    systemeDuTronc: {
      autorise: true,
      note: 'Pourboires/pourcentages de service peuvent être mis dans un "pot" au niveau entreprise. Doit être prévu dans le règlement de travail. Fonctions limitées, répartition selon règles définies.',
    },

    // ── Catégories de fonctions HORECA — 8 échelles salariales ──
    // Source : CCT classification de fonctions secteur Horeca + Horeca Wallonie barème 01/01/2026
    // Salaires horaires minimums (régime 38h/sem) en €/h
    // Salaire mensuel = salaire horaire × 164,6666
    classifications: {
      I:    'Catégorie I — Fonctions débutantes (commis débutant, plongeur)',
      II:   'Catégorie II — Aide cuisine, aide service',
      III:  'Catégorie III — Crew, commis confirmé',
      IV:   'Catégorie IV — Serveur, cuisinier confirmé',
      V:    'Catégorie V — Chef de rang, demi-chef de partie',
      VI:   'Catégorie VI — Chef de partie chaude/froide (entrée 8 années fonction)',
      VII:  'Catégorie VII — Sous-chef, premier maître d\'hôtel',
      VIII: 'Catégorie VIII — Chef de cuisine, directeur de salle',
    },

    // Barème mensuel CP 302 au 01/01/2026 (régime 38h/sem)
    // Échantillon par ancienneté (33 années de fonction au total)
    // Source : Horeca Wallonie PDF mémo indexation salaire 2026
    baremesMensuels_01_01_2026: {
      // Format : { categorie: { ans_fonction: salaire_mensuel_brut } }
      // Valeurs en EUR — sample des valeurs principales
      cat_I:   { y0: 2029.88, y1: 2095.44, y5: 2226.58 },
      cat_II:  { y0: 2095.44, y1: 2226.58, y5: 2365.92 },
      cat_III: { y0: 2226.58, y1: 2365.92, y5: 2582.76 },
      cat_IV:  { y0: 2365.92, y1: 2582.76, y5: 2851.32 },
      cat_V:   { y0: 2582.76, y1: 2851.32, y5: 3075.31 },
      cat_VI:  { y0: 2851.32, y1: 3075.31, y5: 3369.87, y8: 3692.04 },
      cat_VII: { y0: 3075.31, y1: 3369.87, y5: 3692.04, y8: 3964.23 },
      cat_VIII:{ y0: 3369.87, y1: 3692.04, y5: 3964.23, y8: 4204.38 },
      // Note : la grille complète a 33 ans d'ancienneté × 8 catégories = 264 valeurs
      // Pour la grille intégrale, voir https://www.horecawallonie.be/sites/default/files/2025-12/Memo_Indexation%20salaire_2026.pdf
    },

    // ── Indemnités spécifiques HORECA — au 01/01/2026 ──
    indemnites: {
      travail_nuit: {
        montant_horaire: 1.6209,                      // EUR/h (00h00 → 05h00)
        date: '2026-01-01',
        note: 'Indemnité indexée chaque 1er janvier',
      },
      vetements_travail: {
        montant_jour: 2.20,                            // EUR/jour (était 2,15€ avant 01/01/2026)
        date: '2026-01-01',
        note: 'Achat et entretien des vêtements de travail',
      },
      // ⚠️ `indemnite_kilometrique` RETIRÉ le 24/08/2026 — QUATRIÈME écriture du même
      // défaut, sous un quatrième nom de champ. Le montant (0,4327 €/km, « au
      // 01/04/2026 ») n'était pas sectoriel : c'était le forfait fédéral TRIMESTRIEL
      // du 2e trimestre 2026, périmé depuis le 30/06/2026, et l'écran de la CP le
      // présentait comme un avantage du secteur. Tarif applicable :
      // app/lib/indemnite-km-statut.js (tarifKmVoiture / tarifKmMission).
    },

    // ── Heures supplémentaires HORECA ──
    // ✅ CORRIGÉ LE 05/09/2026 — arbitrage fondateur rendu : +50 % → +100 %.
    // Le +50 % précédent était SOUS le plancher fédéral (art. 29, loi du
    // 16/03/1971) sans qu'aucune CCT sectorielle n'y déroge :
    // 🟠 Sources secondaires concordantes (UCM, Horeca Brussels, Shyfter,
    // PayeMesHeures — vérifiées le 05/09/2026) : toutes citent +100 % pour le
    // sursalaire dimanche/JF en CP 302, cumulable avec la prime distincte de
    // 2 €/h (max 12 €/jour) de la CCT du 25/06/2001 (69090) — cette prime-là
    // EST déjà branchée ailleurs (`avantages`/primes horeca) et n'a rien à voir
    // avec le sursalaire lui-même.
    // ✅ Fiche Limosa de détachement du SPF Emploi, Détachement302.pdf, § 2.2.3
    // « Heures supplémentaires » (dernière adaptation 21/01/2026, lue le
    // 05/09/2026) : la seule CCT sectorielle qui y est citée (27/08/2007,
    // 58943) ne porte QUE sur le dépassement annuel de 65 h sans repos
    // compensatoire — elle NE FIXE AUCUN pourcentage de sursalaire, donc
    // aucune dérogation au plancher fédéral pour ce point précis.
    // ⚠️ RÉTROACTIVITÉ : ce correctif s'applique aux paies calculées à partir
    // du 05/09/2026. Les paies antérieures calculées à +50 % ne sont PAS
    // reprises automatiquement par ce commit — décision de reprise (ou non)
    // sur les paies passées à trancher séparément par le fondateur.
    heuresSup: {
      jour:                { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:                { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche_ferie:      {
        taux: 1.00,
        label: 'Heures sup dimanche/JF (+100%)',
        dateVerif: '2026-09-05',
        source: 'Plancher fédéral art. 29 loi du 16/03/1971 ; fiche Limosa SPF Emploi Détachement302.pdf § 2.2.3 (dernière adaptation 21/01/2026, aucune dérogation sectorielle sur ce point) ; UCM/Horeca Brussels/Shyfter/PayeMesHeures concordants.',
      },
      seuilHebdomadaire:   38,
      // Régime spécifique HORECA — exception à la règle générale 360h/240h
      heuresSupVolontaires_max_an: 450,
      heuresSupVolontaires_exonerees: 360,            // dont 360 exonérées d'impôt + cotisations
      note: 'À partir du 1/4/2026 : 450h sup volontaires/an dont 360h exonérées (vs 360h/240h dans autres CPs).',
    },

    // ── Saisonniers HORECA — règle spécifique ──
    saisonniers: {
      année_fonction_0: { entree: true, note: 'Entrée en service' },
      année_fonction_1: { après_jours_5j: 130, après_jours_6j: 156 },
      année_fonction_2: { après_jours_5j: 390, après_jours_6j: 468 },
      année_fonction_suivante: { jours_5j: 260, jours_6j: 312 },
      note: 'Régime spécial saisonniers — ancienneté progressive selon jours prestés',
    },

    // ── Flexi-job HORECA — flexi-salaire ──
    flexiJob: {
      // ⚠️ Ces quatre valeurs sont les MÊMES que `cotisations.flexiJob` — la
      // plancher/plafond valent pour l'horeca seulement (cf. FLEXI_SOURCE ci-dessus ;
      // hors horeca : barème sectoriel de la fonction, au plus 150 %).
      // Elles se lisent donc dans les constantes, jamais recopiées : c'est ici
      // que le 19,17 €/h périmé avait survécu à trois indexations.
      flexi_salaire_min_horaire: FLEXI_SALAIRE_MIN_HORAIRE,   // EUR/h net (01/09/2026)
      flexi_pecule_pct: FLEXI_PECULE_PCT,                     // 7,67% du flexi-salaire
      flexi_salaire_total_horaire: Math.round(FLEXI_SALAIRE_MIN_HORAIRE * (1 + FLEXI_PECULE_PCT) * 100) / 100, // EUR/h min + pécule
      flexi_salaire_max_horaire: FLEXI_SALAIRE_MAX_HORAIRE,   // EUR/h max (fiche SPF CP 302)
      plafond_annuel: 18440,                           // EUR/an exonéré cotisations + impôts
      date_majeure: '2026-09-01',
      source: FLEXI_SOURCE,
      note: 'Le travailleur ne paie ni cotisation sociale ni impôt jusqu\'à 18 440 EUR/an. Le flexi-salaire suit l\'indexation du secteur horeca (CP 302) : +2 % au 01/09/2026.',
    },

    // ── Plan bonus (avantage non récurrent) ──
    planBonus: {
      autorise: true,
      max_2026_a_verifier: true,
      note: 'Montant max augmenté en 2026 (à vérifier source SPF Emploi). Les objectifs doivent être mesurables, vérifiables, leur résultat incertain.',
    },

    // ── Étudiants HORECA ──
    etudiants: {
      regle: 'Salaire de deux catégories de fonctions inférieures à celle dans laquelle ils devraient être insérés',
      exception: 'Pas valable pour étudiants des écoles hôtelières',
      age_min: 18,
      note: 'Salaires minimums s\'appliquent aux contrats étudiants de 18+',
    },

    avantages: {
      delegationSyndicale_seuil: 50,                 // travailleurs (vs 25 en CP 200)
      planPensionSectoriel: true,                     // depuis 2013
    },

    // ✅ Aligné le 05/09/2026 sur `heuresSup.dimanche_ferie` (même correctif,
    // même source) — champ non consommé ailleurs (grep du 05/09/2026), gardé
    // pour ne pas laisser deux valeurs divergentes dans la fiche.
    dimanche_ferie_majoration: 1.00,                  // +100% pour dimanches/jours fériés

    _meta: {
      derniereMAJ: '2026-04-27',
      sourceMAJ: 'CGSLB CP 302 + Horeca Wallonie + Horeca Bruxelles + Prestataire C + Pro-Pay (5 sources concordantes 12/2025)',
      indexationFrequence: 'Annuelle au 01/01',
      indexationQ1_2026: 0.02189,                      // +2,189% confirmé
      classification: '8 catégories × 33 ans ancienneté (264 valeurs barémiques)',
      sousCategories: ['302.00 (HORECA général)', '302.01 (cafés)'],
      historique: 'CP créée 1971, fusion partielle avec autres CPs en 2007',
      notes: 'CP HORECA standard (CDI/CDD). Pour les EXTRAS (forfait jour 117,93€ ou heure 15,52€), voir horeca-engine.js + Module 02 Extras.',
      nouveautes2026: [
        'Indexation +2,189% au 01/01/2026 (CGSLB)',
        'Indemnité vêtements de travail 2,15€ → 2,20€/jour',
        'Indemnité travail de nuit indexée à 1,6209€/h',
        'Heures sup volontaires : 450h/an dont 360h exonérées (à partir 01/04/2026)',
        'Fin du Federal Learning Account (FLA) au 01/01/2026',
        'Flexi-salaire indexé à 11,87€/h net (01/03/2026)',
        // Historique daté (R1 l'autorise) : la ligne ci-dessus n'est PAS fausse,
        // elle est incomplète — il y a eu une seconde indexation depuis.
        'Flexi-salaire indexé de 2 % au 01/09/2026 : 12,11€/h net (fiche SPF CP 302) ; plafond horaire 21,00€/h, non indexé (relu le 15/10/2026)',
        'Plan bonus : montant max augmenté (à confirmer)',
      ],
      // ⚠️ URL RÉPARÉE le 08/09/2026 : la variante « Memo_ » rendait 404. Le
      // fichier s'appelle « Mémo_ » — l'accent s'encode %C3%A9, il avait été
      // perdu à la recopie. Les deux formes testées ce jour-là : 404 / 200.
      // Aucun code ne lit ce champ aujourd'hui ; on le répare plutôt que de le
      // laisser mourir en silence, mais la source qui FAIT FOI reste la fiche
      // SPF 3020000 (cf. FLEXI_SOURCE et le barème CP 302).
      grilleComplete: 'https://www.horecawallonie.be/sites/default/files/2025-12/M%C3%A9mo_Indexation%20salaire_2026.pdf',
    },
  },
  // CP 124 — Construction
  // ⚠️ Module 03 hot-fix 25/04/2026 : valeurs Q2 2026 (au 01/04/2026)
  // Sources : ACCG (FGTB), CSCBIE (CSC), CGSLB, Prestataire D, Prestataire B, Prestataire C (5 sources concordantes)
  // Indexation Q2 2026 : +0,8717% sur barèmes Q1 2026
  '124': {
    // ── Régime hebdomadaire — ajouté le 23/08/2026 ────────────────────────
    // ✅ Fiche Limosa de détachement du SPF Emploi, PC 124 (construction),
    // lue le 23/08/2026, art. 48 : « De gemiddelde wekelijkse arbeidsduur van
    // 40 uur moet worden nageleefd over een ononderbroken periode van 12
    // maanden » — la durée hebdomadaire MOYENNE de 40 heures doit être
    // respectée sur une période ININTERROMPUE DE DOUZE MOIS. À défaut de
    // période fixée par l'employeur dans son acte d'application, elle court
    // du 1er avril au 31 mars.
    //
    // ⚠️ 40 H N'EST PAS UNE ANOMALIE ICI, ET C'EST TOUT L'INTÉRÊT DE
    // L'ÉCRIRE. La construction travaille 40 h conventionnelles compensées
    // par des jours de repos ; le nombre qui pilote un taux horaire n'est
    // donc pas le même selon qu'on parle de la durée conventionnelle ou de la
    // durée effective. Concorde avec `heuresSemaine: 40` de la grille
    // vérifiée.
    regimeHebdo: 40,
    regimeHebdoNote: {
      base: 'durée hebdomadaire MOYENNE à respecter sur douze mois ininterrompus',
      periodeDeReference: "fixée par l'employeur dans son acte d'application ; à défaut, du 1er avril au 31 mars",
      source: "Fiche Limosa de détachement, SPF Emploi, Detachering124.pdf, art. 48, lue le 23/08/2026. Recoupée avec `heuresSemaine: 40` de la grille vérifiée.",
      dateVerif: '2026-08-23',
    },
    nom: 'CP 124 — Construction',
    // ⚠️ Ce bloc avait UN TRIMESTRE DE RETARD sur le barème que l'app sert déjà : il
    // annonçait « +0,8717374% au 01/04/2026 » à l'écran CP 124, juste sous une grille
    // en vigueur au 01/07/2026. La valeur n'entre dans AUCUN calcul de paie (seul le
    // cron cp-completeness la lit, pour détecter une dérive) — mais elle est AFFICHÉE,
    // et un employeur qui lit « dernière indexation : avril » sous un barème de juillet
    // en conclut que la page est périmée. Corrigé le 18/08/2026, source = la fiche SPF
    // déjà citée dans baremes-cp-grid.js : « en vigueur 01/07/2026 (Indexatie met
    // 1,0966796% in 7/2026), page actualisée 07/07/2026 », relevée le 06/08/2026.
    indexation: { frequence: 'trimestrielle', mois: [1, 4, 7, 10], dernierTaux: 0.010967, derniereDate: '2026-07-01', note: 'Q3 2026: +1,0966796% au 01/07/2026 (fiche SPF 1240000, page actualisée 07/07/2026, relevée le 06/08/2026). Historique : +0,8717374% au 01/04/2026. Indexation trimestrielle (4×/an). Source: salairesminimums.be SPF Emploi, Partena Professional CP 124.' },
    // ✅ DIVERGENCE ÉCRAN/REGISTRE RÉSOLUE LE 23/08/2026 — ET AUCUN DES DEUX
    // NOMBRES N'ÉTAIT FAUX. L'écran `FondsSecuriteExistence.js` affiche
    // 9,12 %, le registre 10,40 %, et `fse-ecran-vs-registre` les comptait
    // comme une contradiction. Ils répondent en fait à deux questions
    // différentes : 9,12 % est la cotisation TIMBRES FIDÉLITÉ (9 % pour les
    // timbres + 0,12 % de frais de gestion), 10,40 % est le total CONSTRUCTIV,
    // timbres compris.
    // 🔁 Même famille que « compter par nom de clé » : deux chiffres qui ne
    // mesurent pas la même chose ne se contredisent pas — c'est la question
    // posée qui était mal formulée.
    fondsSE: {
      // ✅ TAUX RELEVÉ AU TEXTE PRIMAIRE LE 25/08/2026 — et le 10,40 % précédent
      // n'était aucun chiffre publié. CCT du 30/06/2016, n° 134503/CO/124,
      // « Fixation du taux de la cotisation au fonds de sécurité d'existence
      // Constructiv » (banque CCT du SPF, 124/124-2016-009906.pdf) :
      //   art. 8 a) 10,27 % — financer une partie du pécule de vacances légal
      //   art. 8 b)  0,40 % — gestion des compétences, soutien de la carrière,
      //                       insertion et bien-être au travail
      //   art. 9    +1,50 % pour les employeurs occupant MOINS DE VINGT
      //                     travailleurs (dû aussi par les agences d'intérim
      //                     pour les ouvriers mis à disposition de celles-ci)
      //   art. 7 : base = la rémunération retenue pour le pécule de vacances
      //            des ouvriers — donc le brut à 108 %.
      //   art. 10 : remplace la CCT du 09/02/2012 (108938/CO/124).
      nom: 'CONSTRUCTIV (ex-FFC/NAVB)',
      taux_patronal: 0.1067,
      assiette: 'brut à 108 % (art. 7 : base du pécule de vacances)',
      tauxComposantes: {
        peculeVacancesLegal: 0.1027,          // art. 8 a)
        competencesCarriereBienEtre: 0.0040,  // art. 8 b)
      },
      // ⛔ NON BRANCHÉE, ET C'EST UNE DETTE CHIFFRÉE : le moteur ne connaît pas
      // l'effectif de l'employeur, et `payroll-engine.js` est une zone protégée.
      // Tant qu'elle n'est pas lue, une entreprise de moins de vingt travailleurs
      // est SOUS-comptée de 1,5 point — 40,50 € par mois sur un brut de 2 500 €
      // porté à 108 %.
      majorationMoinsDe20Travailleurs: 0.015, // art. 9
      // ═══ CE QUE L'ONSS PERÇOIT, ET CE QUI RESTE À NOMMER ═══════════════════
      // Fichier des taux DmfA 2026/3, catégories 024/026/044/054, code 820 :
      // 13,27 % à partir de 20 travailleurs, 14,77 % en dessous. L'écart entre
      // les deux régimes vaut EXACTEMENT 1,50 point — le seuil de l'article 9
      // est donc confirmé par les deux sources, indépendamment.
      // Reste 13,27 − 10,67 = 2,60 points dont la convention n'a pas encore été
      // retrouvée (fonds de formation professionnelle, CNAC sécurité, ou une
      // modification postérieure de la 134503). On n'écrit PAS 13,27 % pour
      // autant : ce serait agréger sans savoir ce qu'on agrège — exactement
      // l'erreur évitée de justesse sur la CP 149.01 le même jour.
      ecartOnssANommer: {
        tauxOnss20EtPlus: 0.1327,
        tauxOnssMoinsDe20: 0.1477,
        expliquePar: "art. 8 a) 10,27 % + art. 8 b) 0,40 %, et l'écart de 1,50 point entre les deux régimes ONSS correspond à la majoration de l'art. 9 sous 20 travailleurs",
        resteAIdentifier: 0.026,
        constate: '2026-08-25',
      },
      // ✅ FORFAIT TRIMESTRIEL — CCT du 02/07/2026, n° 200905/CO/124, art. 2 et 3
      // (124/124-2026-013544.pdf), recoupé AU CENTIME avec les codes 826/827 du
      // fichier des taux ONSS. Deux sources indépendantes, mêmes montants.
      forfaitTrimestriel: {
        trimestre: '2026/4',
        'cat 044/244': 633.00,
        'cat 026/226': 553.00,
        moinsDe25AnsPremierEngagement: { 'cat 044/244': 433.00, 'cat 026/226': 353.00 },
        source: 'CCT du 02/07/2026, n° 200905/CO/124, art. 2 et 3 — texte primaire lu le 25/08/2026',
      },
      source: "CCT du 30/06/2016, n° 134503/CO/124, art. 7 à 9 — banque CCT du SPF Emploi, 124/124-2016-009906.pdf, texte primaire lu le 25/08/2026. Le taux de 10,40 % qui figurait ici n'avait ni source ni date, et ne correspondait à aucun chiffre publié : ni 10,27 (art. 8 a), ni 10,67 (a + b), ni 13,27 (perception ONSS).",
      dateVerif: '2026-08-25',
      note: "Réduction sectorielle de 150 €/trimestre pendant 4 trimestres après l'embauche, dès le 01/04/2026 (accord 2025-2026) — non branchée.",
      composantes: {
        timbresFidelite: 0.0912,
        detail: '9 % pour la valorisation des timbres fidélité + 0,12 % de frais de gestion',
        base: 'rémunération brute à 100 % déclarée en DmfA trimestrielle',
        source: 'CCT du 12/09/2013 (117345) modifiée par CCT du 14/02/2019 (151099), art. 2 — lue le 23/08/2026 via la fiche Limosa du SPF Emploi, Detachering124.pdf § 2.2.1.2',
        dateVerif: '2026-08-23',
        note: "c'est ce 9,12 % qu'affiche l'écran FondsSecuriteExistence : ce n'est pas une contradiction avec le 10,40 % du registre, c'est une SOUS-PARTIE",
      },
    },

    // Chèques-repas CP 124 — INTRODUCTION OBLIGATOIRE 01/04/2026 (accord 2025-2026)
    // Avant : pas de CR sectoriels obligatoires. Après : 2,59€ minimum (1,50€ patronal + 1,09€ travailleur).
    // Augmentation prévue à 2€ patronal après accord équilibre financier Constructiv (date à confirmer).
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, dateReferenceRegimes: '2026-04-01', source: "CCT du 19/03/2026 (CP 124, construction), dépôt 199088, art. 4 à 8, complétée par la CCT du 02/07/2026 (dépôt 200904) qui en fixe l'entrée en vigueur au 01/07/2026. Banque CCT du SPF, textes primaires lus le 24/08/2026. DEUX RÉGIMES IMPOSÉS PAR LA CONVENTION : section 1 (entreprises qui n'octroyaient pas encore) — art. 5 : l'intervention patronale « devra être augmentée de 0,50 euros (valeur nominale totale de chaque chèque-repas égale à au moins 3,09 euros) » ; section 2 (entreprises qui octroyaient déjà) — art. 6 : à partir du 01/04/2026 une intervention patronale SUPPLÉMENTAIRE de 1,50 € par chèque s'ajoute à ce que l'entreprise payait, portée à 2,00 € par l'art. 7 au 01/07/2026. ⚠️ Ne s'applique NI aux étudiants NI aux flexi-jobs (art. 1er). Portée : toute la CP.", regimes: [ { cas: 'nouvel-octroi', depuis: '2026-07-01', libelle: { fr: "Entreprise qui n'octroyait pas encore de chèques-repas", nl: 'Onderneming die nog geen maaltijdcheques toekende' }, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09 }, { cas: 'deja-octroye', depuis: '2026-07-01', libelle: { fr: 'Entreprise qui en octroyait déjà', nl: 'Onderneming die er al toekende' }, supplementPatronal: 2.00 } ], exclusions: "Ni les etudiants (contrat d'occupation d'etudiants) ni les flexi-jobs (art. 1er de la CCT du 19/03/2026).", dateVerif: '2026-08-24', enVigueurLe: '2026-07-01', lien: '124/124-2026-004230.pdf' },

    // Éco-chèques CP 124 — SUPPRIMÉS 31/03/2026 (accord 2025-2026)
    // Dernière distribution mai 2026 pour période 01/04/2025-31/03/2026.
    ecoChequesAnnuel: 0,
    ecoChequesNote: 'Régime sectoriel CP 124 supprimé au 31/03/2026 (accord 2025-2026). Dernière distribution mai 2026 (115 € pour période 04/2025-03/2026).',

    primeFDA: { type: 'Via fonds sectoriel CONSTRUCTIV', prorata: true },
    formation: { jours: 5, fonds: 'CONSTRUCTIV' },
    // ⚠️ CORRIGÉ LE 20/08/2026 — l'ancienne clé { amiante: true, taux: 0.0100 }
    // était une ERREUR ×100 sans source : il n'existe AUCUNE cotisation
    // amiante propre à la construction. Le Fonds amiante est FÉDÉRAL (tous
    // employeurs), 0,01 % (code DmfA 256), dû T1-T3 2026 — il vit dans
    // onss.cotisationsDmfa, recoupé au centime sur une déclaration acceptée.
    // ✅ ONSS asbestosfunds.html + VBO-FEB, lus le 20/08/2026. Deux moteurs
    // calculaient le coût employeur CP 124 avec 1 % : surcharge de 0,99 point
    // de brut (~66 €/mois à 6 660 €) affichée aux prospects.
    cotisationSpeciale: { amiante: false, note: 'Fonds amiante = FÉDÉRAL (code 256, onss.cotisationsDmfa) — pas une cotisation CP 124. Ancienne valeur (1 %) = erreur ×100, retirée le 20/08/2026.' },
    intemperies: true,

    // ── Indemnités sectorielles CP 124 — ajoutées le 23/08/2026 ──────────
    // ✅ TOUTES lues sur la fiche Limosa de détachement du SPF Emploi,
    // Detachering124.pdf (« Laatste aanpassing: 10/07/2026 »), qui reproduit
    // le texte des CCT et les nomme. Aucune valeur de mémoire, aucune estimée.
    //
    // ⚠️ RÉSERVE DE PÉRIMÈTRE, ÉCRITE DANS LA DONNÉE ET PAS EN COMMENTAIRE :
    // la fiche Limosa ne couvre que le NOYAU DUR des conditions à respecter
    // en cas de détachement. Son SILENCE sur une indemnité ne prouve pas que
    // le secteur n'en a pas — cf. `perimetreSource` ci-dessous.
    indemnites: {
      perimetreSource: 'Fiche Limosa de détachement (noyau dur détachement) — son silence ne vaut pas absence',
      source: 'SPF Emploi, Detachering124.pdf, lue le 23/08/2026 (fiche mise à jour le 10/07/2026)',
      dateVerif: '2026-08-23',

      // Art. 5, CCT du 12/06/2014 (123049), AR 10/04/2015 — B.S. 06/05/2015.
      // Seule l'HEURE DE LA JOURNÉE compte, pas le nom donné à l'équipe.
      travailEnEquipes: {
        pct_06h_14h: 1.10,               // payé à 110 % du salaire
        pct_14h_22h: 1.10,               // payé à 110 % du salaire
        pct_22h_06h: 1.25,               // payé à 125 % du salaire
        pauseRepas: "en trois équipes successives : une demi-heure d'interruption payée au salaire normal",
        source: 'CCT du 12/06/2014 (123049), art. 5 et 6',
      },

      // Art. 7, même CCT. Distinct du travail en équipes : vaut pour les
      // heures de nuit prestées HORS régime d'équipes.
      travailDeNuit: {
        pct_22h_06h: 1.25,               // payé à 125 % du salaire
        pauseRepas: 'une demi-heure sans perte de salaire',
        source: 'CCT du 12/06/2014 (123049), art. 7',
      },

      // Prime d'ancienneté — CCT du 14/05/2009 (93291), AR 28/04/2010.
      // ⚠️ La CCT se déclare elle-même SUPPLÉTIVE (art. 4) : un régime
      // d'entreprise au moins équivalent la remplace. Ne pas la facturer
      // sans avoir vérifié ce qui existe dans l'entreprise.
      primeAnciennete: {
        a25ans: 500,                     // EUR brut, une seule fois
        a35ans: 700,                     // EUR brut, une seule fois
        condition: "au moins un jour presté dans l'année qui précède la date d'ancienneté",
        suppletive: true,
        source: 'CCT du 14/05/2009 (93291), art. 2 à 4',
      },

      // Suppléments pour travaux spéciaux — CCT du 12/06/2014 (123049).
      // Dus UNIQUEMENT pendant le temps où le travail décrit est exécuté.
      travauxSpeciaux: {
        pct_min: 0.10,                   // p. ex. toitures ≥ 20 m, sablage, peinture au pistolet
        pct_ciment_sacs: 0.125,          // vidage de sacs de ciment dans la bétonnière
        pct_topmachine: 0.15,
        pct_max: 0.25,                   // flèches et coupoles, démolition d'immeubles instables
        cumulMax: 0.50,                  // ⚠️ le cumul ne peut dépasser 50 % du salaire normal (art. 4)
        cumulInterdit: 'entre travaux figurant dans une MÊME rubrique',
        source: 'CCT du 12/06/2014 (123049), art. 2 à 4',
      },

      // Timbres fidélité — la cotisation patronale, pas un montant au travailleur.
      // Recoupe l'ordre de grandeur de `fondsSE.taux_patronal` (10,40 %) :
      // 9,12 % pour les seuls timbres fidélité, le solde couvrant le reste.
      timbresFidelite: {
        cotisationPatronale: 0.0912,     // 9 % timbres + 0,12 % frais de gestion
        base: 'rémunération brute à 100 % déclarée en DmfA trimestrielle',
        organisme: 'P.D.O.K. / O.P.O.C., pour le Fonds de sécurité d\'existence des ouvriers de la construction',
        exerciceComptable: 'du 1er juillet au 30 juin',
        source: 'CCT du 12/09/2013 (117345) modifiée par CCT du 14/02/2019 (151099), art. 2 et 10',
      },

      // Intempéries — droit au salaire, pas une indemnité forfaitaire.
      intemperies: {
        regle: "l'ouvrier apte au départ, qui constate sur chantier ne pas pouvoir poursuivre son travail à cause du mauvais temps, a droit au salaire qu'il aurait perçu s'il avait pu accomplir sa journée",
        source: 'AR du 16/12/1981 modifié par AR du 03/05/1999, art. 1bis',
      },
    },

    // ── Module 03 — Catégories ouvriers (CCT CP 124, valeurs Q2 2026) ──
    // Source officielle : ACCG (Centrale Générale FGTB) + CSCBIE (CSC) — 01/04/2026
    // Q1 2026 indexé +0,8717% au 01/04/2026.
    // 7 catégories réelles (et non 4 comme Module 03 v1).
    categoriesOuvriers: {
      cat_I:        { code: 'I',     label: 'Catégorie I (Manœuvre)',                  tauxHoraire: 18.389 },
      cat_IA:       { code: 'IA',    label: 'Catégorie IA',                            tauxHoraire: 19.422 },
      cat_II:       { code: 'II',    label: 'Catégorie II (Spécialisé)',               tauxHoraire: 19.659 },
      cat_IIA:      { code: 'IIA',   label: 'Catégorie IIA',                           tauxHoraire: 20.396 },
      cat_III:      { code: 'III',   label: 'Catégorie III (Qualifié 1er échelon)',    tauxHoraire: 20.849 },
      cat_IV:       { code: 'IV',    label: 'Catégorie IV (Qualifié 2e échelon)',      tauxHoraire: 22.131 },
      chef_equipe_A:{ code: 'CEA',   label: 'Chef d\'équipe A (Cat III + 10%)',        tauxHoraire: 22.934 },
      chef_equipe_B:{ code: 'CEB',   label: 'Chef d\'équipe B (Cat IV + 10%)',         tauxHoraire: 24.344 },
      contremaitre: { code: 'CM',    label: 'Contremaître (Cat IV + 20%)',             tauxHoraire: 26.557 },
    },

    // ── Module 03 — Primes chantier ──
    // Cumulables entre elles en pratique (un ouvrier en hauteur sur amiante touche les deux).
    // Calculées sur le brut horaire de base.
    primes: {
      hauteur:      { taux: 0.10, seuil: '> 15m',  label: 'Prime hauteur (+10% si > 15m)' },
      souterrain:   { taux: 0.10, seuil: 'travaux souterrains', label: 'Prime souterrain (+10%)' },
      insalubrite:  { taux: 0.10, seuil: 'amiante / plomb / produits dangereux', label: 'Prime insalubrité (+10%)' },
    },

    // ── Heures supplémentaires CP 124 ──
    // ⚠️ AUCUNE VALEUR N'EST CHANGÉE ICI le 23/08/2026 — zone protégée. Ce qui
    // est ajouté, c'est la SOURCE, et ce qu'elle dit du taux de nuit.
    //
    // ✅ Fiche Limosa de détachement du SPF Emploi, Detachering124.pdf,
    // §§ 2.2.3 à 2.2.5, lue le 23/08/2026 (fiche mise à jour le 10/07/2026).
    //
    // 🔴 CE QUE LA SOURCE NE CONFIRME PAS : le `nuit: 0.35`. La construction a
    // bien un régime de nuit, mais la fiche le pose comme une PRIME horaire —
    // les heures entre 22 et 6 h sont payées à 125 % du salaire (art. 7 de la
    // CCT du 12/06/2014, n° 123049), soit +25 %, et cette prime vaut que
    // l'heure soit supplémentaire ou non. Le +35 % codé ici est utilisé par
    // `construction-engine.js` comme un SURSALAIRE d'heure supplémentaire :
    // sur un jour ouvrable, une heure supplémentaire de nuit resterait alors
    // sous le plancher fédéral de +50 % (art. 29 de la loi du 16/03/1971).
    // Les deux lectures ne se départagent pas sans la CCT elle-même, et il y
    // a de l'argent dessus dans les deux sens → ARBITRAGE FONDATEUR (00.02),
    // la valeur reste telle quelle en attendant.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     {
        taux: 0.35,
        label: 'Heures sup nuit construction (+35%)',
        // ⚠️ Drapeau lu par app/lib/heures-sup-statut.js — ne pas retirer sans
        // avoir tranché l'arbitrage ci-dessus.
        aVerifier: true,
        contredit: 'La fiche Limosa donne +25 % (125 % du salaire) comme PRIME de nuit horaire, pas comme sursalaire ; et un sursalaire de +35 % un jour ouvrable serait sous le plancher fédéral de +50 %.',
        sourceContradictoire: 'CCT du 12/06/2014 (123049), art. 7 — SPF Emploi, Detachering124.pdf, lue le 23/08/2026',
      },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/jours fériés (+100%)' },

      // ⭐ LE VRAI RÉGIME SECTORIEL DE LA CONSTRUCTION, QUI MANQUAIT.
      // Ce n'est pas une CCT mais un ARRÊTÉ ROYAL propre à la CP 124 : il
      // déroge aux limites de l'art. 19 de la loi du 16/03/1971.
      regimeIntenseActivite: {
        heuresParAn: 180,
        maxParJour: 1.5,                  // une heure et trente minutes
        periode: "période d'été ou période d'activité intense",
        remuneration: 'salaire normal',
        auChoixDuTravailleur: {
          reposCompensatoire: '1 jour de repos par 8 heures supplémentaires prestées, dans les douze mois',
          ouSupplement: 0.20,             // +20 % par heure supplémentaire
        },
        defaut: "à défaut de choix avant la fin de la période de paie, ce sont les jours de repos compensatoire qui sont accordés",
        source: "AR n° 213 du 26/09/1983 (B.S. 07/10/1983), art. 7 § 1, modifié par les lois du 28/04/2010, du 23/04/2015 et du 12/12/2021",
      },

      samedi: {
        taux: 0.50,
        heuresParAn: 96,                  // par travailleur
        volontaire: true,
        cumulAvecReposCompensatoire: "le supplément de 50 % est dû que le travailleur choisisse ou non des jours de repos",
        imputation: "les heures prestées le samedi s'imputent sur les 180 heures du régime d'activité intense",
        casAutorises: "travaux impossibles à un autre moment ; travaux dont l'exécution simultanée avec d'autres activités créerait un risque pour la sécurité ou la santé ; travaux non combinables pour raisons techniques",
        source: "AR n° 213 du 26/09/1983, art. 7 § 2, modifié par la loi du 23/04/2015",
      },

      source: 'Fiche Limosa de détachement, SPF Emploi, Detachering124.pdf, §§ 2.2.3 à 2.2.5, lue le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Module 03 — Indemnités déplacement chantier ──
    // Indemnité mobilité CCT CP 124 : seuil REEL = 10 km/jour minimum (pas 5).
    // Tarif basé sur barème CSCBIE/Constructiv (km réellement parcourus).
    // Plafond gardé pour MVP — à raffiner avec barème complet ultérieurement.
    mobilite: {
      tarifKm: 0.1827,                    // EUR / km AR (valeur indicative — barème exact = table par tranche km)
      distanceMin: 10,                    // < 10 km/jour : pas d'indemnité (correction Q2 2026 vs v1 module)
      plafondJournalier: 30.00,
      label: 'Indemnité mobilité chantier (CCT CP 124, seuil 10 km/jour)',
      jourMobiliteSeuil: 28500,           // ≥ 28 500 km/an → droit à 1 jour mobilité (était 30 000 jusqu'à 2025)
    },

    // ── Module 03 — Intempéries ──
    intemperiesDetail: {
      tauxIndemnisation: 0.75,
      payeurPart: 'ONEM (chômage temporaire intempéries)',
      formulaire: 'C3.2A construction',
      conditions: 'Gel, pluie continue, neige, températures extrêmes — décision contremaître',
    },

    // ── Module 03 — Timbres fidélité OPOC ──
    timbresOPOC: {
      taux_indicatif_brut_annuel: 0.09,
      payeur: 'CONSTRUCTIV via fonds OPOC',
      versement: 'annuel, juin',
      note: 'Information uniquement — pas calculé sur la paie mensuelle',
    },

    // ── Module 03 — Indemnité vélo sectorielle ──
    // Montant CP 124 = CCT n°164 indexée au 01/01 (0,30 en 2026 ; 0,27 = valeur 2023 périmée).
    // 0,32 au 01/10/2026 = accord sectoriel 2025-2026 CP 124 (augmentation conventionnelle, PAS une indexation).
    // Pas de cumul avec l'indemnité mobilité sur le trajet vélo. ORBISS + Partena + Embuild O-Vl, consulté 2026-07-07.
    indemniteVelo: {
      tarifKm: 0.30,                      // EUR / km au 01/01/2026 (= CCT n°164 indexée)
      tarifKmFutur: 0.32,                 // EUR / km à partir du 01/10/2026 (accord sectoriel 2025-2026)
      dateChangement: '2026-10-01',
    },
  },
  // CP 140 — Transport routier
  // ⚠️ Module 04 — HOT-FIX 25/04/2026 : refonte classification post-01/01/2023
  // Avant 2023 : 10 barèmes (par tonnage / type véhicule)
  // Depuis 2023 : 4 NIVEAUX uniquement, basés sur la fonction réelle
  // Source : Prestataire C officielle + FGTB-UBT bulletin 17-12-2025 + CGSLB CP 140.03
  // Sous-secteurs réels CP 140.03 : 140.341 (compte tiers), 140.343 (messagerie),
  //                                 140.345 (courrier/taxi), 140.398 (manutention)
  '140': {
    // ── Régime horaire hebdomadaire ──────────────────────────────────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 25/06/2008 (CP 140), durée du
    // travail dans les entreprises de services réguliers spécialisés, art. 2,
    // banque CCT du SPF Emploi, 140/140-2008-004214.pdf : « La durée du travail
    // hebdomadaire des ouvriers mentionnés à l'article 1, § 1 est fixée à
    // 38 heures. »
    //
    // ⚠️ CE QUE LA CCT LUE COUVRE, ET CE QU'ELLE NE COUVRE PAS. Son champ
    // d'application est LIMITÉ aux services réguliers spécialisés — un segment
    // de la CP, pas la commission entière. Les 38 heures sont retenues pour la
    // CP mère parce que TROIS choses concordent, et il faut les trois :
    //   1. la CCT lue les fixe pour ce segment ;
    //   2. les cinq sous-CP (140.01 à 140.05) sont déjà codées à 38 ;
    //   3. 38 heures est la durée hebdomadaire légale de droit commun — ce
    //      n'est donc pas une dérogation sectorielle mais le régime général.
    // Si un segment de la CP 140 pratiquait autre chose, ce serait une
    // DÉROGATION qui devrait être écrite quelque part : rien de tel n'a été
    // trouvé.
    //
    // ⚠️ C'EST UNE MOYENNE SEMESTRIELLE, PAS UN PLAFOND HEBDOMADAIRE. La CCT :
    // « Cette durée du travail hebdomadaire est une moyenne qui doit être
    // respectée sur une période d'un semestre, sauf dérogation au niveau
    // d'entreprise permettant d'étaler cette moyenne sur une période plus
    // longue avec un maximum d'un an. » Les semestres sont janvier-juin et
    // juillet-décembre. Une semaine à 45 heures n'est donc pas en soi une
    // infraction dans ce secteur — c'est la moyenne du semestre qui compte.
    regimeHebdo: 38,
    regimeHebdoNote: {
      moyenneSur: 'semestre (01/01–30/06 et 01/07–31/12)',
      derogationEntreprise: "étalement possible sur une période plus longue, maximum un an",
      source: "CCT du 25/06/2008 (CP 140) — durée du travail dans les entreprises de services réguliers spécialisés, art. 2, banque CCT du SPF Emploi, 140/140-2008-004214.pdf. Texte primaire lu par colonnes. Portée : ce segment ; retenu pour la CP mère car les cinq sous-CP sont à 38 h et 38 h est la durée légale de droit commun.",
      dateVerif: '2026-08-22',
    },
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 19/02/2026 (CP 140), banque CCT
    // du SPF Emploi, 140/140-2026-002600.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 19/02/2026 (CP 140) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 140/140-2026-002600.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 140 — Transport routier',
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0218, derniereDate: '2026-01-01', note: 'Q2 2026: +2,18% transport routier. Source: SD Worx, peoplesphere CP 140.03' },
    sousSecteurs: {
      '140.341': 'Transport de choses pour compte de tiers, personnel roulant',
      '140.343': 'Services de messagerie, personnel roulant',
      '140.345': 'Services de courrier, personnel roulant et taxi camionnettes',
      '140.398': 'Entreprises de manutention de choses pour compte de tiers, personnel roulant',
      '140.01':  'Autobus / autocars (sous-CP différente)',
      '140.05':  'Déménagement (sous-CP différente)',
      '140.09':  'Logistique (sous-CP différente)',
    },
    // ✅ TEXTE PRIMAIRE LU LE 21/08/2026 — le « ~7 % » était une approximation
    // non sourcée, et il est périmé depuis le 01/07/2026.
    // CCT du 15/01/2026 « relative à la cotisation due au Fonds Social Transport
    // et Logistique » (banque CCT du SPF Emploi, 140/140-2026-000377.pdf, texte
    // bilingue FR/NL), CHAPITRE III, art. 3, mot pour mot :
    //   « par dérogation au premier alinéa, la cotisation, due par les employeurs
    //     visés à l'article 1er au Fonds social, est fixée à partir du 1er juillet
    //     2026, à 7,70 % des salaires déclarés à l'Office national de sécurité
    //     sociale à 108 %. »
    // La CCT elle-même entre en vigueur le 01/01/2026, à durée indéterminée.
    //
    // ⚠️ LA BASE FAIT PARTIE DU TAUX. Le texte dit « des salaires déclarés à l'ONSS
    // À 108 % » — c'est la base ouvrier, pas le brut nu. `payroll-engine` calcule
    // aujourd'hui `brut × fseTaux` alors qu'il dispose deux lignes plus haut de
    // `baseONSS` (= brut × 108 % pour un ouvrier) : appliqué au brut nu, le fonds
    // est sous-compté de 8 %. Le champ `base` ci-dessous existe pour que la
    // correction soit possible sans deviner — il n'est PAS encore lu par le moteur
    // (payroll-engine est zone protégée : décision fondateur, pas la mienne).
    fondsSE: { nom: 'FSTL — Fonds Social Transport et Logistique', taux_patronal: 0.077, base: '108', dateVerif: '2026-08-21', note: "Catégorie ONSS 083. 7,70 % des salaires déclarés à l'ONSS à 108 %, à partir du 01/07/2026 — CCT du 15/01/2026 (CP 140), art. 3, texte primaire lu le 21/08/2026 via la banque CCT du SPF Emploi. Remplace l'approximation non sourcée de ~7 %. ⚠️ RESERVE AJOUTEE LE 25/08/2026 — LA PORTEE DE CETTE CCT EST RESTREINTE. Le SPF declare son champ d'application comme « Sous-secteur du transport de choses par voie terrestre pour compte de tiers ». Ce n'est donc PAS etabli que 7,70 % vaille pour toute la CP 140, qui couvre aussi le demenagement (140.05), les taxis, les autobus et la manutention. Verification faite le 25/08/2026 : sur les 137 conventions « fonds » de la CP 140, UNE SEULE est a portee generale, et son titre vise le « Fonds Social des entreprises de taxis » (CCT du 19/10/2017). Autrement dit, la CP 140 n'a pas de cotisation unique : son fonds est organise PAR SOUS-SECTEUR. Appliquer 7,70 % a un demenageur ou a un exploitant de taxis reste a confirmer sur la convention de SON sous-secteur." },
    // ✅ TEXTE PRIMAIRE LU le 20/08/2026 — CCT n° 197738/CO/140.03 du 15/01/2026,
    // « Octroi et/ou augmentation des chèques-repas et/ou de l'indemnité de repas »,
    // déposée le 21/01/2026, enregistrée le 09/02/2026, EN VIGUEUR, conclue en
    // exécution de l'accord sectoriel du 18/12/2025 (son art. 2 le dit). Obtenue
    // par la banque CCT du SPF Emploi — `app/lib/cct-search.js`.
    //
    // ⚠️ CETTE ENTRÉE EST LA CP PARENTE « 140 », PAS LA SOUS-CP 140.03.
    // Le régime décrit ici est celui de la sous-CP 140.03, dont l'entrée dédiée
    // (`'140.03'`, plus bas) porte la source complète. Il est conservé ici comme
    // REPLI, pour la fiche dont la CP est stockée « 140 » sans sous-CP : la
    // lecture est `TAUX_SECTORIELS[cp]` sans normalisation, donc « 140 » et
    // « 140.03 » ne tombent pas au même endroit. Les autres sous-CP (140.01
    // autobus, 140.05 déménagement…) ont leurs propres régimes — ne pas les
    // déduire d'ici.
    //
    // `obligatoire` valait FALSE alors que la note juste en dessous disait déjà
    // « introduction sectorielle au 01/07/2026 » : la donnée contredisait sa
    // propre documentation. L'art. 3 §1 de la CCT tranche — « les travailleurs
    // […] DOIVENT se voir octroyer, au plus tard à partir du 01.07.2026, un
    // chèque-repas ou une indemnité de repas avec une quote-part patronale de
    // 2 euros par jour presté ».
    //
    // Ce que la CCT fixe VRAIMENT :
    //   · art. 3 §1 — quote-part PATRONALE de 2 €/jour presté, au plus tard au
    //     01/07/2026. C'est la seule valeur que ce texte impose ;
    //   · art. 3 §2 — qui accorde déjà l'avantage AUGMENTE sa part de 2 €/jour
    //     presté à la même échéance ;
    //   · art. 3 §3 — dispense pour qui a introduit ou augmenté entre le
    //     01/01/2025 et le 30/06/2026 ; si la hausse était inférieure à 2 €, la
    //     différence reste due au 01/07/2026 ;
    //   · art. 3 §4 — SIX MOIS d'ancienneté ininterrompue (l'intérim dans la
    //     même entreprise est assimilé) ; l'indemnité de repas exige au moins
    //     4 h prestées le jour même, le chèque-repas admet un autre mode de
    //     calcul que le nombre de jours prestés ;
    //   · art. 3 §5 — le choix entre chèque et indemnité appartient à l'EMPLOYEUR ;
    //   · art. 1 §2 — ouvriers de la catégorie ONSS 083, ÉTUDIANTS EXCLUS
    //     (code travailleur 840) ;
    //   · art. 4 — en vigueur au 01/01/2026, durée indéterminée.
    //
    // 🟠 CE QU'ELLE NE FIXE PAS : `valeur` (3,09 €) et `part_travailleur`
    // (1,09 €) n'apparaissent nulle part dans cette CCT — elle n'impose que la
    // part patronale. Ces deux montants restent ceux de l'accord sectoriel et
    // gardent leur cran 🟠 : à confirmer sur un texte qui les porte.
    chequesRepas: {
      // ✅ REBASCULÉ À TRUE LE 21/08/2026 — la condition posée ci-dessous est
      // remplie. Le bloc qui suit est CONSERVÉ tel quel : il raconte pourquoi ce
      // drapeau est resté à false pendant six semaines alors que la CCT disait
      // l'inverse, et il faut pouvoir le relire.
      //
      // Ce qui a débloqué : le REPLI SECTORIEL a été retiré de
      // `fiche-paie-generator.js` le 20/08 (PR #2064, mergée le 21/08). Le
      // générateur n'importe même plus TAUX_SECTORIELS. Balayage refait le
      // 21/08 avant de toucher au drapeau — plus AUCUN consommateur n'en tire
      // un montant :
      //   · `implementation-checks.js:294` — avertit l'employeur, n'encode rien
      //   · `CCTAnalyzer.js`, `BaremesCP.js`, `PrimesAvantagesV2.js` — affichage
      //   · `onboarding/audit` + `selftest` — contrôles de conformité
      // « Informer n'est pas retenir » : passer à true fait désormais SIGNALER
      // l'obligation, et ne fabrique aucune retenue. C'est le comportement voulu.
      //
      // Ce que ça répare pour le client : depuis le 01/07/2026 l'avantage est dû,
      // et l'app répondait « non obligatoire » — l'audit de conformité sautait
      // le contrôle, et l'écran l'écrivait noir sur blanc à l'employeur.
      //
      // ── Ci-dessous, la raison historique du false, gardée pour mémoire ──────
      // 🛑 TENU À FALSE VOLONTAIREMENT — ce n'est PAS un oubli, et ce n'est pas
      // ce que dit la CCT. L'art. 3 §1 rend bien l'avantage obligatoire ; ce
      // drapeau reste à false parce que le CODE n'est pas prêt à le recevoir.
      //
      // Mesuré le 20/08/2026 : `fiche-paie-generator.js` (ligne ~654) contient un
      // REPLI SECTORIEL — si l'employé n'a pas de chèques-repas encodés, il
      // applique d'office ceux de sa CP — et il lit la CP PARENTE, car il coupe
      // la sous-CP (`String(cp).split('.')[0]`). `payroll-batch.js`, qui alimente
      // les brouillons, les crons, le virement, la DmfA et la compta, n'a AUCUN
      // repli de ce genre : `cheques-repas-octroi.js` n'importe même pas
      // TAUX_SECTORIELS. Passer cette clé à true fait donc retenir 1,09 €/jour au
      // travailleur sur le PDF et rien en base : deux nets pour la même fiche.
      // C'est ce que le test de parité a refusé — écart de 21,80 € sur le net et
      // 40,00 € sur le coût, sur le cas SINGA / SAIDIYA.
      //
      // 📏 La dette avec son dénominateur : 74 CP parentes sur 102 déclenchent
      // DÉJÀ ce repli, presque toutes sans date d'effet. La divergence existe
      // donc en production pour elles ; on n'y ajoute pas la 140.
      // ⏱️ Second défaut du même bloc : `new Date(year, month-1, 1) >= new Date(dEffet)`
      // compare une date LOCALE à une date UTC — la règle bascule selon le fuseau
      // du serveur (inerte en Europe/Brussels, active sur Vercel qui tourne en UTC).
      //
      // ➜ À rebasculer à true DANS LE MÊME COMMIT que la réparation de la parité
      //   (zone protégée : fiche-paie-generator.js + payroll-batch.js).
      //   ✅ FAIT — la parité a été réparée par #2064, mergée le 21/08/2026.
      obligatoire: true,
      valeur: 3.09,                        // 🟠 accord sectoriel 2025-2026 — ABSENT de la CCT 197738
      part_employeur: 2.00,                // ✅ CCT 197738 art. 3 §1
      part_travailleur: 1.09,              // 🟠 accord sectoriel 2025-2026 — ABSENT de la CCT 197738
      dateEntreeEnVigueur: '2026-07-01',   // ✅ art. 3 §1 « au plus tard à partir du 01.07.2026 »
      ancienneteMinimaleMois: 6,           // ✅ art. 3 §4
      dureeMinimaleJourHeures: 4,          // ✅ art. 3 §4 — pour l'INDEMNITÉ de repas uniquement
      choixParEmployeur: true,             // ✅ art. 3 §5
      note: 'OBLIGATOIRE au plus tard le 01/07/2026 : chèque-repas OU indemnité de repas, quote-part patronale de 2 € par jour presté, au choix de l\'employeur. Six mois d\'ancienneté ininterrompue requis (intérim dans la même entreprise assimilé) ; l\'indemnité exige 4 h prestées minimum. Dispense si l\'avantage a été introduit ou augmenté entre le 01/01/2025 et le 30/06/2026, le solde restant dû si la hausse était inférieure à 2 €. Étudiants (code travailleur 840) exclus. Source : CCT n° 197738/CO/140.03 du 15/01/2026, en vigueur, lue le 20/08/2026 via la banque CCT du SPF Emploi, en exécution de l\'accord sectoriel du 18/12/2025.',
    },
    // ⚠️ 15/09/2026 — `ecoChequesAnnuel: 250` (la valeur recopiée) remplacé par le
    // régime relevé dans la CCT 199085 : même REPLI 140.03 que `chequesRepas` au-dessus.
    ecoChequesConditionnel: ECO_CHEQUES_CONDITIONNELS_14003,
    primeFDA: { type: 'Via FSTL — Fonds Social Transport et Logistique', prorata: true },
    formation: {
      jours: 5,
      fonds: 'FSTL — Fonds Social Transport et Logistique',
      note: 'L\'intervention 25/50 EUR formation permanente sera supprimée au 01/07/2026 (accord 18/12/2025)',
    },
    cotisationSpeciale: null,

    // ── Module 04 HOT-FIX — Classification CP 140.03 post-01/01/2023 ──
    // 4 niveaux numérotés. Basés sur la FONCTION RÉELLE et la responsabilité,
    // PAS sur le tonnage ni le type de véhicule.
    // Source : refonte classification CCT du 19/10/2023 (FGTB-UBT + Prestataire C)
    // Taux Q1 2026 estimés : indexation +2,18% (CGSLB + FGTB-UBT 17/12/2025)
    // ⚠️ VALEURS À CONFIRMER avec bulletin officiel FSTL Q1 2026
    categoriesChauffeurs: {
      niveau_1: {
        code: 'N1',
        label: 'Niveau 1 — Aide-chauffeur / manutentionnaire',
        tauxHoraire: 13.95,                // EUR/h Q1 2026 estimé (en validation)
        description: 'Fonctions d\'assistance, sans permis poids lourd requis',
      },
      niveau_2: {
        code: 'N2',
        label: 'Niveau 2 — Chauffeur (entrée de gamme)',
        tauxHoraire: 14.95,                // EUR/h Q1 2026 estimé (en validation)
        description: 'Chauffeur standard, ancienne classe supérieure pré-2023',
      },
      niveau_3: {
        code: 'N3',
        label: 'Niveau 3 — Chauffeur expérimenté (+0,15 €/h vs N2)',
        tauxHoraire: 15.10,                // EUR/h Q1 2026 estimé (en validation)
        description: 'Premier niveau supérieur ajouté en 2023',
      },
      niveau_4: {
        code: 'N4',
        label: 'Niveau 4 — Chauffeur senior / spécialiste (+0,30 €/h vs N2)',
        tauxHoraire: 15.25,                // EUR/h Q1 2026 estimé (en validation)
        description: 'Deuxième niveau supérieur ajouté en 2023',
      },
    },

    // ── Heures supplémentaires CP 140 ─────────────────────────────────────
    // ✅ LE FONDEMENT EST FÉDÉRAL, ET IL SUFFIT À LUI SEUL : art. 29 de la loi
    // du 16/03/1971 — sursalaire de 50 % en semaine (samedi inclus) et de
    // 100 % le dimanche ou un jour férié. Énoncé mot pour mot par le SPF
    // Emploi (page « Travail à temps partiel — Heures complémentaires et
    // sursalaire », lue le 23/08/2026). Une CCT peut aller AU-DESSUS, jamais
    // en dessous : la valeur portée ici était 0,50, soit ×1,50, donc sous le
    // plancher. La correction ne dépend d'aucune convention sectorielle.
    //
    // ✅ CORROBORATION SECTORIELLE, avec sa réserve — CCT du 27/01/2005
    // (CP 140), « conditions de travail et de rémunération », sous-secteur du
    // transport de choses par la route pour compte de tiers, personnel
    // roulant. Banque CCT du SPF Emploi, 140/140-2005-000426.pdf, texte
    // primaire lu le 23/08/2026.
    //
    // ⚠️ DEUX SOURCES DU MÊME SPF SE CONTREDISENT SUR SA VALIDITÉ, et on ne
    // tranche pas à leur place. La banque CCT la donne `enforced: true` ; la
    // fiche « Salaires minimums » de la SCP 140.03 (jcId
    // e4f43b8462814a79b780b4feb1ae1eaa, lue en direct le 23/08/2026) écrit
    // pour cette même convention (n° d'enregistrement 74050) : « Fin de
    // validité : 31/12/2022 ». Cherché un texte successeur sur la SCP 140.03,
    // par titre et par terme : rien qui reprenne le chapitre sursalaire — ce
    // qui ne prouve pas son absence, seulement que je ne l'ai pas trouvé
    // (R2 ter). C'est pour ça que le fondement retenu ci-dessus est FÉDÉRAL.
    //
    //   · art. 6.3 — « Prestaties op zon- en feestdagen worden krachtens de
    //     wetgeving op de betaalde feestdagen betaald met een toeslag van
    //     100 % (dus aan 200 %) » ;
    //   · art. 8  — « Si un sursalaire est dû, celui-ci s'élève à 50 % du
    //     salaire horaire fixé à l'article 5. En vertu de la loi sur le
    //     travail, le sursalaire dû pour les prestations effectuées les
    //     dimanches et les jours fériés est DÉJÀ COMPRIS dans le supplément
    //     mentionné à l'article 6.3. »
    //
    // → l'heure dominicale se paie à 200 % du taux de base : on n'empile pas
    //   les 50 % sur les 100 %, mais le total est bien de +100 %.
    //
    // La valeur portée ici était 0,50, soit ×1,50 — inférieure au plancher de
    // l'art. 29 de la loi du 16/03/1971 ET à ce que dit la CCT du secteur.
    // Sur un taux de base de 15 €/h, huit heures un dimanche valaient 180 €
    // au lieu de 240 €. Vérifié qu'aucune CCT postérieure ne remplace ce
    // chapitre : les 50 conventions de la SCP 140.03 signées depuis n'ont
    // qu'indexé des montants.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      // ⚠️ +20 % NON SOURCÉ, ET PROBABLEMENT UNE MÉPRISE — laissé inchangé
      // exprès. Le seul « 20 % » de la CCT du 27/01/2005 est au chapitre V :
      // un supplément pour DÉPASSEMENT DU TEMPS DE SERVICE MOYEN (bandes
      // 60-65 h, 65-70 h, > 70 h sur trois mois), déjà porté plus bas par
      // `tempsServiceMoyen`. La seule CCT « travail de nuit » de la CP 140
      // (17/07/1991, n° 28582) vise les TAXIS. Mais le baisser réduirait une
      // rémunération, et l'ABSENCE d'un régime de nuit n'est pas prouvée —
      // seulement le fait que je ne l'ai pas trouvée (R2 ter). À trancher.
      nuit:     { taux: 0.20, label: 'Heures sup nuit transport (+20% — à sourcer, cf. commentaire)', aSourcer: true },
      dimanche: {
        taux: 1.00,
        label: 'Dimanche / jour férié (+100 %, soit 200 % du taux de base)',
        source: "Art. 29 de la loi du 16/03/1971 (plancher fédéral, SPF Emploi lu le 23/08/2026) — corroboré par la CCT du 27/01/2005 (CP 140), art. 6.3 et 8, banque CCT du SPF, 140/140-2005-000426.pdf. ⚠️ La fiche SPF « Salaires minimums » de la SCP 140.03 donne cette CCT pour échue au 31/12/2022, la banque CCT la donne en vigueur : divergence signalée, le fondement retenu est le plancher fédéral.",
        dateVerif: '2026-08-23',
      },
    },

    // ── Temps de service moyen (spécificité CP 140.03) ──
    tempsServiceMoyen: {
      maxParMois: 260,                     // h/mois (≈ 60h/semaine)
      majorationDepassement: 0.50,         // +50% sur dépassement
      definition: 'Heures de travail + heures de disponibilité',
      reference: 'CCT CP 140.03 — temps de service moyen',
    },

    // ── Module 04 — Règlement EU 561/2006 (temps de conduite) ──
    // ARGUMENT COMMERCIAL — règlement européen direct, valeurs FIGÉES.
    eu561: {
      maxConduiteJour: 9,
      maxConduiteJourExtension: 10,
      extensionMaxParSemaine: 2,
      maxConduiteSemaine: 56,
      maxConduite14jours: 90,
      reposJournalierMin: 11,
      reposJournalierReduit: 9,
      reductionsReposParSemaine: 3,
      reposHebdoMin: 45,
      reposHebdoReduit: 24,
      pauseObligatoireMinutes: 45,
      pauseFractionnable: { partie1: 15, partie2: 30 },
      seuilPauseHeures: 4.5,
      amendeMinor: 105,
      amendeMajor: 1320,
      amendeVeryGrave: 4000,
      reference: 'Règlement (CE) 561/2006 + AETR 1970 + AR 19/07/2000',
    },

    // ── Indemnités spécifiques transport — personnel ROULANT ──
    // ✅ TEXTES PRIMAIRES LUS LE 15/09/2026 :
    //   · CCT n° 131219/CO/140.03 du 19/11/2015 « Indemnité de séjour forfaitaire &
    //     indemnité RGPT », banque CCT du SPF Emploi, 14003/14003-2015-012270.pdf —
    //     en vigueur au 01/01/2016, durée indéterminée ; art. 8 : montants adaptés
    //     chaque 1er janvier au coût de la vie ;
    //   · fiche Limosa du SPF Emploi « PSC 140.03 — deelsector RIJDEND PERSONEEL »,
    //     dernière adaptation 16/01/2026 (werk.belgie.be/.../Limosafiches/Detachering140.03.pdf),
    //     qui donne les montants INDEXÉS de janvier 2026.
    //
    // ⚠️ « ARAB » N'EST PAS UNE AUTRE INDEMNITÉ : c'est le nom NÉERLANDAIS de la RGPT
    // (Algemeen Reglement voor de Arbeidsbescherming = Règlement général pour la
    // protection du travail). La fiche Limosa l'écrit « Geïndexeerde ARAB-vergoeding
    // (januari 2026): 1,8175 EUR » — le même montant que `rgpt.parHeure`. Ce bloc
    // portait jusqu'au 15/09/2026 un `arab: { montantJour: 22 }` « repos hebdo
    // réduit » sans AUCUNE source : une seconde indemnité inventée à côté de la
    // vraie, que TransportCalculator ajoutait au net. Retiré.
    //
    // ⚠️ `nuitInternational` 47,00 € et `repasInternational` 22,50 € (« estimation
    // Q1 2026 ») RETIRÉS le même jour : l'indemnité de séjour n'est ni « par nuit »
    // ni « internationale », et elle n'a pas de volet « repas ». C'est un forfait
    // PAR TRANCHE COMMENCÉE DE 24 H, dû aussi en Belgique (art. 4 : « séjour fixe en
    // Belgique ou à l'étranger »).
    indemnites: {
      rgpt: {
        // CCT 131219 art. 5-6 : par heure de présence = heure de travail et/ou de
        // disponibilité. Montant indexé de janvier 2026 lu sur la fiche Limosa du
        // 16/01/2026, concordant avec la CCT d'indexation du 18/12/2025 (dépôt
        // 196981, lue le 07/08/2026, cf. transport-engine.js). NET : non soumis ONSS
        // ni précompte (fiche Liantis juin 2026). Code secrétariat social : 3241.
        parHeure: 1.8175,
        source: "CCT n° 131219/CO/140.03 du 19/11/2015, art. 5-6 (banque CCT du SPF, texte primaire lu le 15/09/2026) ; montant indexé au 01/01/2026 : fiche Limosa SPF Emploi PSC 140.03 rijdend personeel, dernière adaptation 16/01/2026 (« Geïndexeerde ARAB-vergoeding (januari 2026): 1,8175 EUR »).",
        dateVerif: '2026-09-15',
      },
      sejour: {
        // Art. 2 : par tranche COMMENCÉE de 24 h, lorsque, par nécessité de service,
        // le chauffeur doit prendre son repos journalier et/ou hebdomadaire (règl. CE
        // 561/2006) hors de son domicile ou du lieu de travail prévu au contrat.
        forfait24h: 47.9310,
        // Art. 3 : montant LIMITÉ dans deux cas — (a) le premier repos journalier
        // quand le temps de travail + disponibilité qui le précède est inférieur à
        // 8 h et qu'il ne fait pas partie d'un séjour de plusieurs jours ; (b) une
        // absence du domicile inférieure à 24 h avec un seul repos journalier.
        reduit: 19.4285,
        // Art. 4 : forfait COMPLÉMENTAIRE en cas de « séjour fixe » (aucune prestation
        // entre deux repos), en Belgique ou à l'étranger — il s'AJOUTE à l'art. 2.
        complementSejourFixe: 13.0310,
        dateEffet: '2026-01-01',
        source: "CCT n° 131219/CO/140.03 du 19/11/2015, art. 2 à 4 et 8 (banque CCT du SPF, 14003/14003-2015-012270.pdf, texte primaire lu le 15/09/2026 : montants de base 36,1265 / 14,6440 / 9,8220 EUR, indexés chaque 1er janvier) ; montants indexés de janvier 2026 lus sur la fiche Limosa SPF Emploi PSC 140.03 rijdend personeel, dernière adaptation 16/01/2026.",
        dateVerif: '2026-09-15',
      },
      // ⚠️ `kmVoiture` RETIRÉ le 24/08/2026 — CINQUIÈME écriture du même défaut, et la
      // plus retorse : ici le tarif était imbriqué (`kmVoiture: { tarif, label }`), donc
      // invisible au balayage qui cherchait `kmVoiture: <nombre>`. Le commentaire qui
      // l'accompagnait se disait lui-même « aligné FORF_KM » et ne l'était pas. Valeur
      // réelle : 0,4327 €/km = forfait fédéral TRIMESTRIEL du 2e trimestre 2026, périmé
      // depuis le 30/06/2026. Rien ne le LISAIT, mais l'écran de la CP l'AFFICHAIT comme
      // un tarif du secteur (R2 ter-B : une donnée morte reste une donnée affichée).
      // Tarif applicable : app/lib/indemnite-km-statut.js.
      indemniteVetement: {
        montantNetMois: 4.96,
        condition: 'Entreprises < 20 travailleurs — alternative à fourniture vêtements',
      },
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CCT CP 140.03 + EU 561/2006 + FGTB-UBT 17/12/2025 + CGSLB',
      sousSecteurMVP: '140.341',
      classificationPostRefonte: '01/01/2023',
      indexation_Q1_2026: '+2,18% (CGSLB + FGTB-UBT)',
      statut: 'EN_VALIDATION_Q2_2026',
      notes: 'MVP centré 140.341 (transport marchandises compte tiers, le sous-secteur le plus important). Taux Q1 2026 estimés à confirmer avec bulletin officiel FSTL. La classification 4 niveaux est correcte depuis 01/01/2023.',
      pendingActions: [
        'Récupérer bulletin info officiel FSTL Q1 2026 (taux exacts)',
        // 15/09/2026 — RGPT confirmée (1,8175 €/h : CCT 131219 + fiche Limosa du
        // 16/01/2026) ; « ARAB » retiré : c'est le nom néerlandais de la RGPT.
        'Ajouter nouveau régime chèques-repas au 01/07/2026 (3,09 EUR / 2 EUR employeur)',
      ],
    },
  },

  // CP 149.01 — Électriciens (installation/distribution)
  // ⚠️ Audit 27/04/2026 : section ajoutée pour combler la faille (engine existait sans source de vérité)
  // Sources concordantes : CGSLB CP 149.01, ELOYA, Métallos FGTB, CSC CSCBIE, Prestataire A 22/12/2025
  // Protocole accord sectoriel 2025-2026 conclu le 03/02/2026 (ELOYA + syndicats)

  // CP 121 — Nettoyage
  // ⚠️ Module 05 — Valeurs Q1 2026 calculées (indexation cumulée +28,19% depuis 07/2019 ACCG)
  // Sources : ACCG-FGTB CP 121 (barèmes 2019), CGSLB CP 121 01/2026,
  // salairesminimums.be (indexation +0,56% Q1 2026 confirmée), CSC, Brupartners
  // Documentation officielle : SPF Emploi détachement121.pdf
  '121': {
    // ── Indemnités SECTORIELLES ──────────────────────────────────────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 21/02/2024 (CP 121) modifiant
    // celle du 05/03/2007 (n° 82423/CO/121), art. 2, banque CCT du SPF Emploi,
    // 121/121-2024-004395.pdf. Mot pour mot : « … ont, pour le déplacement de
    // l'établissement de l'employeur au 1er chantier et le retour du dernier
    // chantier, droit à une indemnité de mobilité de 0,1579 € par kilomètre. »
    //
    // ⚠️ CE N'EST PAS UNE INDEMNITÉ KILOMÉTRIQUE ORDINAIRE, et la confondre
    // coûterait cher dans les deux sens. Elle ne couvre PAS le trajet
    // domicile-travail : elle couvre le déplacement de l'ÉTABLISSEMENT DE
    // L'EMPLOYEUR au premier chantier, et le retour du dernier chantier.
    // ⚠️ Elle ne vise QUE les travailleurs de la CATÉGORIE 8 de la
    // classification — le nettoyage industriel au sens de l'art. 2 de la CCT
    // du 11/06/2009. Un nettoyeur de bureaux n'y a pas droit à ce titre.
    // ⚠️ Elle s'applique aussi aux ouvriers d'un employeur ÉTABLI À L'ÉTRANGER
    // dès lors que les travaux sont effectués en Belgique.
    indemnites: {
      mobiliteChantier: {
        parKm: 0.1579,
        depuis: '2024-01-01',
        trajet: "de l'établissement de l'employeur au 1er chantier et retour du dernier chantier — PAS le domicile-travail",
        beneficiaires: 'travailleurs de la catégorie 8 de la classification (nettoyage industriel, art. 2 de la CCT du 11/06/2009)',
        employeurEtranger: true, // s'applique quel que soit le pays d'établissement de l'employeur
        source: "CCT du 21/02/2024 (CP 121) modifiant la CCT du 05/03/2007 n° 82423/CO/121, art. 2 — banque CCT du SPF Emploi, 121/121-2024-004395.pdf. Texte primaire lu par colonnes.",
        dateVerif: '2026-08-22',
      },
    },
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 05/02/2026 (CP 121), banque CCT
    // du SPF Emploi, 121/121-2026-001835.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 05/02/2026 (CP 121) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 121/121-2026-001835.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 121 — Nettoyage',
    regimeHebdo: 36.5,                         // h/semaine (régime sectoriel)
    seuilHeuresSup: 37,                        // h/semaine (au-delà = +50%)
    // ✅ SOURCE PRIMAIRE (2026-08-05) : fiche SPF Emploi 1210000, en vigueur au 01/07/2026,
    // « Indexatie met 2,07% in 7/2026 ». Le 2,06 % écrit quelques heures plus tôt venait de la
    // plaquette Partena (secondaire, arrondie) — le SPF tranche. Un centième d'écart qui se
    // serait propagé dans les 25 catégories de la grille.
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.0207, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : +2,07 % au 01/07/2026, LU SUR LA FICHE SPF 1210000 (« Indexatie met 2,07% in 7/2026 », page SPF mise à jour le 29/07/2026). Remplace le 2,06 % relevé quelques heures plus tôt chez Partena. Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu pendant cinq semaines. Grille horaire mise à jour dans le même mouvement (baremes-cp-grid.js, 25 catégories relevées à la source).' },
    fondsSE: {
      nom: 'Fonds social CP 121 — Nettoyage',
      taux_patronal: 0.1595,
      taux_patronal_employe: 0.0023,
      pension_sectorielle: 0.0172,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '066',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 066. Codes travailleur : 820 ouvriers 15,95 % · 825 pension sectorielle 1,72 % · 831 employés 0,23 % (Fonds social CP 200). Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Nettoyage et désinfection »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Les employés du secteur relèvent du Fonds social de la CP 200 (code 831), d'où l'écart énorme avec le taux ouvrier. Valeur codée précédente : 2,50 %.",
    },
    // 🔴 DIVERGENCE NON RÉSOLUE, SIGNALÉE LE 05/09/2026 — ne pas trancher sans
    // relire la CCT elle-même. Cette fiche donne 7,00 € (5,91 employeur), avec
    // un mécanisme détaillé (formule au prorata, bonus entreprises déjà
    // équipées). Une vérification indépendante du 05/09/2026 (Synova ex-CGSLB,
    // corroborée par CGSLB/CSC/Monizze) donne un chiffre différent : 3,09 €
    // (2,00 € employeur), introduction pour 2 ans (2026-2027), calcul par
    // tranche de 7,4h. Les deux sources décrivent le MÊME accord (chèques-repas
    // pour tous au 01/01/2026 en CP 121, nettoyage) mais pas le même montant —
    // argent réel, zone protégée : arbitrage fondateur avant de changer la
    // valeur dans un sens ou l'autre.
    chequesRepas: {
      obligatoire: true,
      valeur: 7.00,                            // EUR — accord sectoriel 2025-2026 généralisé
      part_employeur: 5.91,
      part_travailleur: 1.09,
      dateEntreeEnVigueur: '2026-01-01',
      formuleNombre: 'heures_prestees / 7,4 (arrondi sup)',
      bonusEntreprisesExistantes: 2.00,        // +2€ pour entreprises ayant déjà chèques avant 2026
      note: 'Accord sectoriel 2025-2026 : chèques-repas POUR TOUS au 01/01/2026',
      source: "Accord sectoriel 2025-2026 CP 121 — valeur reprise telle que codée précédemment ; DIVERGE d'une autre lecture du même accord (Synova/CGSLB/CSC/Monizze, sondée le 05/09/2026 : 3,09 €) — voir commentaire ci-dessus, arbitrage fondateur requis.",
      aVerifier: true,
      dateVerif: '2026-09-05',
    },
    // ⚠️ `primeFinAnnee` SUPPRIMÉ ICI le 19/08/2026 — il disait 8,33 % sans
    // source sur la ligne, alors que la prime CP 121 est de 9 % du brut
    // (période 01/07–30/06, min. 60 jours ONSS, payée par le Fonds social du
    // nettoyage — ACCG-FGTB et CGSLB concordants, lus le 19/08/2026). La
    // valeur juste et sourcée vit dans app/lib/prime-fin-annee.js
    // (PRIME_FIN_ANNEE['121'], vérifiée 2026-06-04) : c'est LE registre des
    // primes de fin d'année, on n'en garde pas une deuxième copie ici —
    // deux registres pour la même donnée finissent toujours par diverger,
    // et celui-ci avait déjà divergé.
    // ✅ SOURCÉ LE 25/09/2026 (recherche cct-search.yml, 4 orthographes) : le
    // seul résultat CCT est EXPIRÉ (fin de validité 30/06/2023, 121/121-2022-015290.pdf)
    // et ne mentionne les éco-chèques que comme option marginale de paiement
    // d'une indemnité RGPT (art. 12) pour la seule niche du personnel des
    // incinérateurs — pas un régime général sectoriel. Aucune CCT active trouvée.
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 121 (Nettoyage) — recherche plein texte (cct-search.yml, 25/09/2026, eco cheque/ecocheque/ecocheques/éco-chèques) : 1 résultat, CCT du 18/11/2021 (121/121-2022-015290.pdf), EXPIRÉE le 30/06/2023. Les éco-chèques n'y apparaissent qu'à l'art. 12 comme mode de paiement alternatif d'une indemnité RGPT (0,55 €/jour, plafond 125 €/an) réservée au personnel des incinérateurs — pas un régime sectoriel général. Aucun régime actif.",
    ecoChequesDateVerif: '2026-09-25',
    indemniteVelo: 0.36,                       // EUR/km 01/01/2026→31/12/2027 — accord sectoriel 2025-2026 (montant FIXE, ne suit PAS le max fiscal 0,37 ; incl. inter-chantiers) — Synova/ACLVB, consulté 2026-07-07
    indemniteMobilite: 0.1929,                 // EUR/km
    indemniteMobiliteConvoyeurs: 0.0965,       // EUR/km (cas spécifique)
    indemniteGardeEnfants: 370,                // EUR/an Q1 2026 (vs 185 € avant 2026)
    primeForfaitaire: 14.95,                   // EUR — prime forfaitaire départ liée indice santé
    primeSyndicale: { existe: true, montant: 145, prorata: true },
    formation: { jours: 4, fonds: 'Fonds social CP 121' },
    cotisationSpeciale: null,

    // ── Module 05 — Catégories CP 121 (Q1 2026) ──────────────────────────
    // Calcul : taux ACCG officiels 01/07/2019 × indexation cumulée 1,28190
    // Sources : PDF ACCG cp-121-indexation-des-salaires + chronologie indexations
    // CCT CP 121 (08/2019, 01/2020, 08/2020, 01/2021, 07/2021, 01/2022, 07/2022,
    // 01/2023, 07/2023, 01/2024, 07/2024, 01/2025, 07/2025, 01/2026)
    // ⚠️ COPIE PARALLÈLE — ALIGNÉE LE 2026-08-05, ET C'EST UN PIÈGE À CONNAÎTRE.
    // Ce bloc n'est PAS la source de vérité : `nettoyage-engine.js` lit d'abord
    // BAREMES_CP_VERIFIES['121'].minimaEntree et ne retombe ici QUE si la catégorie
    // manque à la grille. Autrement dit, ces valeurs ne s'affichent jamais tant que la
    // grille est complète — donc elles peuvent vieillir sans que rien ne le montre.
    // Constat du 05/08/2026 : elles étaient à 17,03 €/h pour la 1.A, soit NI janvier
    // (16,818) NI juillet (17,166) — un millésime intermédiaire oublié là. Un secours
    // silencieux qui sert de vieilles valeurs est pire que pas de secours du tout.
    // Réalignées sur la fiche SPF 1210000 du 01/07/2026, comme la grille.
    categoriesNettoyage: {
      // ── Catégorie 1 : Nettoyage classique ──
      cat_1A: { code: '1A', label: 'Cat 1A — Nettoyage habituel (bureaux, sièges)',                  tauxHoraire: 17.166, group: 'Nettoyage' },
      cat_1B: { code: '1B', label: 'Cat 1B — Nettoyage ateliers, hôpitaux, soins permanents',         tauxHoraire: 17.701, group: 'Nettoyage' },
      cat_1C: { code: '1C', label: 'Cat 1C — Nettoyage métro, prémétro, autobus',                     tauxHoraire: 17.869, group: 'Nettoyage' },
      cat_1D: { code: '1D', label: 'Cat 1D — Ateliers de montage automobiles',                        tauxHoraire: 18.2305, group: 'Nettoyage' },
      // ── Catégorie 2 : Nettoyage mi-lourd ──
      cat_2A: { code: '2A', label: 'Cat 2A — Nettoyage mi-lourd (effort physique, hottes industrielles)', tauxHoraire: 18.2775, group: 'Mi-lourd' },
      cat_2B: { code: '2B', label: 'Cat 2B — Wagons train, métro, prémétro, bus, avions',             tauxHoraire: 18.801, group: 'Mi-lourd' },
      cat_2C: { code: '2C', label: 'Cat 2C — Idem 2B mais en extérieur',                              tauxHoraire: 19.014, group: 'Mi-lourd' },
      cat_2D: { code: '2D', label: 'Cat 2D — Dégraissage, nettoyage, désinfection véhicules neufs',   tauxHoraire: 18.801, group: 'Mi-lourd' },
      cat_2E: { code: '2E', label: 'Cat 2E — Désinfection',                                           tauxHoraire: 18.989, group: 'Mi-lourd' },
      cat_2F: { code: '2F', label: 'Cat 2F — Nettoyage conteneurs IBC et fûts PE',                    tauxHoraire: 17.492, group: 'Mi-lourd' },
      // ── Catégorie 3 : Déchets et chauffeurs ──
      cat_3A: { code: '3A', label: 'Cat 3A — Collecte de déchets ménagers',                           tauxHoraire: 19.5145, group: 'Déchets' },
      cat_3B: { code: '3B', label: 'Cat 3B — Nettoyage mi-lourd locaux industriels',                  tauxHoraire: 19.3775, group: 'Déchets' },
      cat_3C: { code: '3C', label: 'Cat 3C — Chauffeur camion d\'immondices',                         tauxHoraire: 20.5325, group: 'Déchets' },
      cat_3D: { code: '3D', label: 'Cat 3D — Chauffeur-mécanicien camion d\'immondices',              tauxHoraire: 21.0555, group: 'Déchets' },
      // ── Catégorie 4 : Lavage de vitres qualifié ──
      cat_4:  { code: '4',  label: 'Cat 4 — Lavage de vitres qualifié (échelles, ponts, élévateurs)', tauxHoraire: 19.3775, group: 'Vitres' },
    },

    // ── Module 05 — Heures sup CP 121 ─────────────────────────────────────
    // ✅ TEXTE PRIMAIRE LU LE 23/08/2026 — CCT du 05/02/2026 (CP 121),
    // « Salaires, sursalaires et primes », banque CCT du SPF Emploi,
    // 121/121-2026-001833.pdf, EN VIGUEUR.
    //
    //   · art. 3 — le travail entre 22 h et 6 h donne droit à une prime EN
    //     EUROS PAR HEURE, pas à un pourcentage : 3,0490 €/h au 01/01/2026
    //     pour toutes les catégories, liée à l'indice santé comme les
    //     salaires (3,7040 €/h en catégorie 8, art. 15 d). Le travail de nuit
    //     d'au moins 6 h précédé ou suivi de 2 h de travail ouvre la prime
    //     pour ces deux heures aussi ;
    //   · art. 4 — tout travail un dimanche ou un jour férié : prime de
    //     100 p.c. du salaire normal ;
    //   · art. 5 — tout travail le samedi : prime de 25 p.c. du salaire
    //     normal, NON applicable aux heures supplémentaires déjà majorées.
    //
    // ⚠️ CE QUE ÇA CORRIGE. La fiche portait `nuit: 1.00` : les +100 % de la
    // CCT sont ceux du DIMANCHE (art. 4), pas ceux de la nuit. La nuit n'est
    // pas un pourcentage dans ce secteur — c'est un forfait horaire. Pour un
    // ouvrier à ~14 €/h, `nettoyage-engine.js` majorait de 14 €/h là où la
    // CCT donne 3,0490 €/h.
    //
    // ⚠️ ET LE SAMEDI N'EST MODÉLISÉ NULLE PART — ni ici, ni dans le moteur.
    // Ajouté en donnée, pas encore branché : brancher une majoration touche
    // au brut, donc au fondateur de trancher.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      // Le forfait de nuit vit dans `primeNuitHoraire` ci-dessous : ce n'est
      // pas une majoration en pourcentage, et le présenter comme telle est
      // précisément l'erreur qu'on corrige. `taux: 0` dit « aucune majoration
      // proportionnelle », pas « rien de dû ».
      nuit:     {
        taux: 0,
        label: 'Nuit 22h-6h : prime forfaitaire à l\'heure, pas de majoration en %',
        source: "CCT du 05/02/2026 (CP 121), art. 3 — banque CCT du SPF Emploi, 121/121-2026-001833.pdf, en vigueur. Texte primaire lu.",
        dateVerif: '2026-08-23',
      },
      dimanche: {
        taux: 1.00,
        label: 'Dimanche / jour férié (+100 %)',
        source: "CCT du 05/02/2026 (CP 121), art. 4 — banque CCT du SPF Emploi, 121/121-2026-001833.pdf, en vigueur. Texte primaire lu.",
        dateVerif: '2026-08-23',
      },
      samedi: {
        taux: 0.25,
        label: 'Samedi (+25 %) — ne se cumule pas avec une heure supplémentaire déjà majorée',
        nonCumulableAvecHeuresSup: true,
        source: "CCT du 05/02/2026 (CP 121), art. 5 — banque CCT du SPF Emploi, 121/121-2026-001833.pdf, en vigueur. Texte primaire lu.",
        dateVerif: '2026-08-23',
      },
      // ✅ VÉRIFIÉ le 05/09/2026 — `seuilHebdomadaire: 37` ET `regimeHebdo: 36,5`
      // ci-dessus sont TOUS LES DEUX corrects, et ce n'est PAS une incohérence
      // malgré ce que peut afficher `seuilIncoherent()` (heures-sup-statut.js) :
      // ce sont deux notions distinctes de la même CCT du 11/06/2009 (94700).
      // Art. 2 fixe le facteur S à 36,5 h (durée hebdomadaire MOYENNE, jours de
      // compensation inclus) ; art. 4 fixe le seuil de déclenchement des heures
      // sup à 37 h (limite légale de la semaine PRESTÉE). Source : fiche SPF
      // Emploi Limosa CP121, Détachement121.pdf.
      seuilHebdomadaire: 37,
    },

    // ── Prime de nuit CP 121 — un FORFAIT HORAIRE, pas un pourcentage ──────
    primeNuitHoraire: {
      standard: 3.0490,          // EUR/h — 22h-6h, toutes catégories, au 01/01/2026
      categorie8: 3.7040,        // EUR/h — art. 15 d, remplace le forfait standard
      plage: '22:00-06:00',
      lieAIndiceSante: true,
      note: "Le travail de nuit d'au moins 6 h précédé ou suivi de 2 h de travail ouvre la prime pour ces deux heures. En catégorie 8, un shift jusqu'à 12 h comportant au moins 6 h entre 22 h et 6 h ouvre la prime sur la totalité de la prestation.",
      source: "CCT du 05/02/2026 (CP 121), art. 3 et 15 d — banque CCT du SPF Emploi, 121/121-2026-001833.pdf, en vigueur. Texte primaire lu.",
      dateVerif: '2026-08-23',
    },

    // ── Module 05 — Suppléments CP 121 ──
    supplements: {
      travailEquipe: 0.8865,                   // EUR/h supplément travail en équipes alternatives
      chefEquipeMin6: 0.10,                    // +10% chef d'équipe (min 6 personnes en cat 4 / 10 ailleurs)
      primeInsalubrite: { taux: 0.10, condition: 'Selon art. 8 CCT salaires/sursalaires' },
    },

    // ── Module 05 — Tenue de travail ──
    tenueDeTravail: {
      fournieParEmployeur: true,
      indemniteSiNonFournie: 4.96,             // EUR/mois net
      remboursementBlanchissage: 'Variable selon CCT',
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'ACCG CP 121 (2019) + CGSLB 01/2026 + salairesminimums.be + Brupartners',
      indexationCumulee_2019_2026: 0.2819,    // +28,19% cumulés
      indexationQ1_2026: 0.0056,               // +0,56% confirmé
      indexationFrequence: '2x/an (1er janvier + 1er juillet)',
      regimeHoraire: '36h30/semaine sectoriel',
      seuilHS: '> 37h/semaine = heures sup (+50%)',
      classification: '5 niveaux principaux (cat 1 à 5) + sous-catégories A-F',
      notes: 'Cat 5 = Personnel de métier (renvoi CP métier compétente, min cat 1A + 0,25 €). Cat 8 = renvoi CP 112 garages. ~50 fonctions au total.',
      nouveautes2026: [
        'Chèques-repas généralisés à TOUS les travailleurs (01/01/2026)',
        'Indemnité garde enfants doublée : 185 → 370 €/an',
        'Indemnité vélo confirmée 0,36 €/km (incl. inter-chantiers)',
        'Indemnité mobilité 0,1929 €/km (0,0965 €/km convoyeurs)',
        'Indexation +0,56% au 01/01/2026',
      ],
    },
  },

  // CP 322.01 — Titres-services (aides ménagères)
  // ⚠️ Module 06 — Valeurs Q1/Q2 2026 confirmées par sources concordantes
  // Sources : Prestataire D 21/01/2026 (accord Bruxelles), CGSLB CP 322.01,
  // Horval (titres-services Wallonie 2% au 01/03/2026), CSC, SPF Emploi
  '322.01': {
    // 🔴 `primeFinAnnee_pct: 8.33` RETIRÉ LE 23/08/2026 — il contredisait le
    // registre sourcé `prime-fin-annee.js`, qui donne 4,50 % pour cette
    // commission. Le 8,33 est la valeur par défaut recopiée sur 146 fiches sur
    // 174 (le douzième arithmétique d'un « treizième mois »).
    // ⚠️ Et ce n'était pas décoratif : titres-services-engine.js calculait la
    // prime sur CE champ, donc à un taux presque double du taux sourcé.
    // R1 : une constante vit à un seul endroit. Verrou :
    // __tests__/prime-fin-annee-une-seule-verite.test.js
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 25/08/2017 (SCP 322.01),
    // statut de la délégation syndicale, banque CCT du SPF Emploi,
    // 32201/32201-2017-011470.pdf, art. 6 et 7. Mot pour mot : « Une
    // délégation syndicale peut être instituée dans les entreprises occupant
    // 20 travailleurs (têtes) et plus. »
    //
    // ⚠️ « TÊTES », ET C'EST DÉCISIF DANS CE SECTEUR. Les titres-services
    // emploient massivement à temps partiel : compter en équivalents temps
    // plein donnerait un chiffre bien inférieur et ferait manquer le seuil à
    // des entreprises qui l'atteignent.
    //
    // ⚠️ ANOMALIE DE LA SOURCE, TRANSCRITE TELLE QUELLE : les deux premières
    // tranches de l'art. 7 se CHEVAUCHENT dans le texte publié — « de 20 à 60 »
    // puis « de 51 à 75 ». Ce n'est pas une erreur de lecture de notre côté ;
    // le PDF l'écrit ainsi. Ne pas « corriger » sans avoir vu un texte
    // coordonné qui tranche.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif',
        seuilTravailleurs: 20,
        uniteDeCompte: 'têtes (personnes physiques), pas équivalents temps plein',
        deleguesAnomalieSource: 'Les tranches publiées se chevauchent : « de 20 à 60 » (3 effectifs / 2 suppléants) puis « de 51 à 75 » (4 / 3). Transcrit verbatim, à trancher sur un texte coordonné.',
        source: 'CCT du 25/08/2017 (SCP 322.01) — statut de la délégation syndicale, banque CCT du SPF Emploi, 32201/32201-2017-011470.pdf, art. 6 et 7. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 322.01 — Titres-services',
    nomCourt: 'Titres-services',
    regimeHebdo: 38,                           // h/semaine (limite max)
    regimeNormalCommun: 36.5,                  // h/semaine (régime conventionnel courant)
    indexation: { frequence: 'annuelle', mois: [3], dernierTaux: 0.02, derniereDate: '2026-03-01' },
    fondsSE: {
      // ⚠️ CORRIGÉ le 21/08/2026. Deux fiches coexistaient pour cette CP et se
      // contredisaient : 1,2 % ici, 7 % sous la clé sans point '32201'.
      // Vérification au fonds lui-même (fondstitresservices.be, miroir NL
      // identique, base CCT du 06/06/2016) : le taux réel est 6,99 % du brut
      // ONSS À 108 %, décomposé en trois postes. Le « 1,2 % » n'avait AUCUNE
      // base ; le « 7 % » était l'arrondi du 6,99 %, millésime jamais écrit.
      // ✅ LE DOUTE EST LEVÉ — 26/08/2026, ET PAR LE PERCEPTEUR LUI-MÊME.
      // Le 21/08, ce bloc portait un 🔴 : « 6,99 % est la valeur 2018/Q4, le
      // fonds n'a rien publié depuis, le taux 2026 est INCONNU », et l'écriture
      // était présentée comme un arbitrage assumé à faire valider par le
      // fondateur — le coût employeur titres-services montait de 5,8 points.
      //
      // Le fichier des taux DmfA de l'ONSS répond, et il répond exactement la
      // même chose : catégorie d'employeur 597, trimestre 2026/3, code 820
      // (ouvriers, sur les rémunérations à 108 %) **6,99 %** et code 830
      // (travailleurs intellectuels) **6,99 %**. Le taux de 2018 est donc
      // toujours celui de 2026 : ce n'est pas une valeur périmée qu'on servait
      // faute de mieux, c'est le taux en vigueur. L'arbitrage n'en est plus un.
      //
      // 🔁 Et la leçon se répète : on avait cherché la réponse chez le FONDS
      // (site du fonds, CCT du 06/06/2016) et conclu « non publié depuis 2018 ».
      // Elle était publiée, ailleurs, par celui qui encaisse. « Je n'ai pas
      // trouvé » n'est pas « ça n'existe pas » — cette fois c'est la SOURCE
      // qu'on avait mal choisie, pas l'adresse ni le motif (R2 ter).
      nom: 'Fonds social pour les titres-services',
      taux_patronal: 0.0699,
      onssSourcee: true,
      onssCategorie: '597',
      tauxDernierPublie: 0.0699,
      assiette: 'brut ONSS à 108 %',
      detail: { avantagesSociaux: 0.0669, groupesRisque: 0.0010, formation: 0.0020 },
      periodeDernierPublie: '2018-Q4',
      source: "ONSS, fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie d'employeur 597 : code 820 = 6,99 % « sur les rémunérations à 108 % des travailleurs manuels », code 830 = 6,99 %. Lu le 26/08/2026. Concordant au centième avec la décomposition du fonds lui-même (fondstitresservices.be / fondsdienstencheques.be, base CCT du 06/06/2016, dernière publication 2018/Q4, lue le 21/08/2026) : 6,69 % avantages sociaux + 0,10 % groupes à risque + 0,20 % formation.",
      dateVerif: '2026-08-26',
      note: "Le drapeau `aVerifier` a été retiré le 26/08/2026 : le doute portait sur l'actualité du taux, et l'ONSS le publie pour le trimestre courant. La décomposition en trois postes, elle, reste datée de 2018 — c'est la répartition interne du fonds, pas le montant dû.",
    },

    // ── Régions : 3 zones avec barèmes Q1/Q2 2026 ──────────────────────
    regions: {
      flandre:    { code: 'VLA', label: 'Flandre',  indexation_2026: { date: '2026-03-01', taux: 0.02 } },
      wallonie:   { code: 'WAL', label: 'Wallonie', indexation_2026: { date: '2026-03-01', taux: 0.02 } },
      bruxelles:  { code: 'BXL', label: 'Bruxelles', indexation_2026: { date: '2026-03-01', taux: 0.02 }, augmentation_brute: 0.77, dateAlignement: '2026-01-01', note: 'Bruxelles : +0,77 €/h brut au 01/01/2026 pour aligner sur Flandre/Wallonie' },
    },

    // ── Catégories de travailleurs CP 322.01 (4 niveaux ancienneté) ────
    // Taux Q1 2026 : barème CCT 2024 + 0,77€/h augmentation 07/2025
    // (Flandre/Wallonie) ou 01/2026 (Bruxelles) + indexation 2% au 01/03/2026
    // Source : Prestataire D accord aides-ménagères + Horval barèmes
    categoriesAideMenagere: {
      cat_A: {
        code: 'A',
        label: 'Aide-ménagère débutante (0-1 an ancienneté)',
        tauxHoraire: 14.96,                    // EUR/h post-indexation 03/2026
        anciennete: '0-1 an',
      },
      cat_B: {
        code: 'B',
        label: 'Aide-ménagère expérimentée (1-3 ans ancienneté)',
        tauxHoraire: 15.50,                    // EUR/h post-indexation 03/2026
        anciennete: '1-3 ans',
      },
      cat_C: {
        code: 'C',
        label: 'Aide-ménagère confirmée (3-5 ans ancienneté)',
        tauxHoraire: 15.68,                    // EUR/h post-indexation 03/2026
        anciennete: '3-5 ans',
      },
      cat_D: {
        code: 'D',
        label: 'Aide-ménagère senior (5+ ans ancienneté)',
        tauxHoraire: 15.84,                    // EUR/h post-indexation 03/2026
        anciennete: '5+ ans',
      },
    },

    // ── Heures sup CP 322.01 ──
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 1.00, label: 'Heures sup nuit (+100%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    // ── Indemnités CP 322.01 (Q1 2026) ──
    indemnites: {                       // EUR/km — confirmé 01/04/2026 Horval
      velo: 0.25,                              // EUR/km domicile-travail Flandre/Bruxelles + entre clients (CCT sectorielle, depuis 01/06/2022) — ACCG/Synova, consulté 2026-07-07 ; 0,36 antérieur = max exonéré 2025 sans base sectorielle
      veloWallonieDomicileTravail: 0.35,       // EUR/km Wallonie depuis 01/01/2024 (« égale fonctionnaires » — liaison dynamique à vérifier sur CCT ; ACCG affiche 0,35 au 07/07/2026)
      veloEntreClientsWallonie: 0.25,          // EUR/km Wallonie entre deux utilisateurs (état 01/06/2024, FGTB Horval)
      veloMaxExonere: 0.37,                    // EUR/km max exonéré fiscal/ONSS 2026 (plafond 3 700 €/an)
      transportCommun_pct: 1.00,               // 100% remboursé
      vetement_mensuel: 4.96,                  // EUR/mois (si non fourni)
      primeFDA_pct: 0.0833,                    // ~1 mois (8,33%)
    },

    chequesRepas: {
      obligatoire: false,
      valeur: 0,
      part_employeur: 0,
      part_travailleur: 0,
      note: 'Chèques-repas non obligatoires sectoriellement. Si octroyés : selon CCT entreprise.',
    },
    // ✅ SOURCÉ LE 25/09/2026 (recherche cct-search.yml, 4 orthographes) : ZÉRO
    // résultat dans la banque CCT du SPF pour la CP 322.01 (Titres-services).
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 322.01 (Titres-services) — recherche plein texte (cct-search.yml, 25/09/2026, eco cheque/ecocheque/ecocheques/éco-chèques) : 0 résultat sur les 4 orthographes. Aucune CCT éco-chèques déposée sous ce numéro.",
    ecoChequesDateVerif: '2026-09-25',
    // ⚠️ CLÉ `formation` EN DOUBLE DANS CETTE FICHE, retirée le 23/08/2026.
    // Elle était écrite deux fois avec le MÊME contenu utile (jours: 5) et
    // deux libellés de fonds différents ; en JavaScript la SECONDE écrase la
    // première en silence, donc c'est « Fonds titres-services » qui faisait
    // foi depuis toujours. Retirer celle-ci ne change donc rien au
    // comportement — elle clarifie ce qui était déjà vrai, et elle enlève la
    // seule erreur eslint du fichier (`no-dupe-keys`).
    // 🔁 Même famille que le piège rencontré ce jour-là dans la CP 330 : un
    // second `indemnites:` avalait silencieusement le premier.
    cotisationSpeciale: null,

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'Prestataire D 21/01/2026 + CGSLB CP 322.01 + Horval Q2 2026 + SPF Emploi',
      indexationFrequence: '1x/an (au 1er mars)',
      indexationQ1_2026: 0.02,
      augmentationBrute_2025_2026: 0.77,
      dateAugmentationFlandreWallonie: '2025-07-01',
      dateAugmentationBruxelles: '2026-01-01',
      regimeHoraire: '38h/sem max légal (régime conventionnel souvent 36h30)',
      classification: '4 niveaux selon ancienneté (A 0-1an / B 1-3 / C 3-5 / D 5+)',
      notes: 'Aides-ménagères CP 322.01. Indexation 01/03/2026 +2% confirmée (Horval). Bruxelles aligné depuis 01/01/2026 (Prestataire D). Subvention publique au titre-service finance partiellement le salaire.',
      nouveautes2026: [
        'Augmentation +0,77 €/h brut généralisée (Bruxelles aligné 01/01/2026)',
        'Indexation +2% au 01/03/2026 (Wallonie + Flandre + Bruxelles)',
        'Indemnité km voiture : pas de tarif sectoriel — forfait fédéral (annuel ou trimestriel, au choix de l\'employeur), voir indemnite-km-statut.js',
        'Suppression disparités salariales entre régions',
        'Frais de mission recalculés trimestriellement',
      ],
    },
    // ═══ FUSION DE LA JUMELLE '32201' — 21/08/2026 ════════════════════════
    // `primeFDA` n'existait que sur la fiche EN DOUBLE `'32201'`, supprimee par
    // ce commit. Portee telle quelle, sans arbitrage.
    // ⚠️ Les cles contradictoires (indexation, fondsSE, chequesRepas,
    // ecoChequesAnnuel, formation) gardent la valeur de '322.01' — la seule que
    // l'app servait. Divergence consignee dans la PR.
    primeFDA: { type: 'Prime de fin d\'année via fonds', prorata: true },
    formation: { jours: 5, fonds: 'Fonds titres-services' },
  },

  // CP 111 — Métal (constructions métallique, mécanique et électrique)
  // ⚠️ Module 07 — Valeurs Q1 2026 confirmées par sources concordantes
  // Sources : CCT du 12/01/2026 n° 197236/CO/111 (+0,2606 €/h au minimum national, texte
  // primaire lu le 16/09/2026), Agoria 22/12/2025, CGSLB CP 111, Métallos FGTB,
  // CSC Metea, salairesminimums.be (PC 1110100/1110200/1110300 SCP officielles),
  // Brupartners synthèse CP 111
  '111': {
    // ── Avantage SECTORIEL — régime de pension sectoriel social ──────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 08/12/2025 (CP 111),
    // « Modification du régime de pension sectoriel social, du règlement de
    // pension… », banque CCT du SPF Emploi, 111/111-2025-014803.pdf.
    // Le secteur du métal a son propre fonds de pension, le Pension Métal OFP,
    // en place depuis le 01/04/2000. C'est un avantage que la loi n'impose pas.
    //
    // 🔴 LE TAUX DE COTISATION PENSION N'EST PAS CODÉ, ET C'EST VOULU. La CCT
    // annonce « 2,29 % ou 2,09 % » selon la catégorie d'entreprise, et le texte
    // mentionne un troisième taux de 2,19 % « pour toutes les autres
    // entreprises ». Le CRITÈRE qui départage les trois n'a pas pu être lu :
    // le PDF est bilingue à colonnes entrelacées et la phrase est coupée.
    // Écrire l'un des trois reviendrait à en inventer l'application (R2 bis).
    // Le taux de solidarité, lui, est unique et sans ambiguïté.
    avantages: {
      pensionSectorielle: {
        regime: 'Pension Métal OFP — régime de pension sectoriel social (PCS)',
        depuis: '2000-04-01',
        solidaritePct: 0.001,            // 0,1 % des salaires bruts à 100 %
        baseSolidarite: 'salaires bruts à 100 % (NON majorés de 8 %), codes de rémunération DmfA 1, 2, 3 et 4',
        solidariteDepuis: '2017-07-01',
        cotisationPensionPct: null,      // 🔴 trois taux (2,29 / 2,19 / 2,09), critère non lu
        cotisationPensionAVerifier: 'La CCT donne 2,29 %, 2,19 % ou 2,09 % selon la catégorie d\'entreprise ; le critère de répartition n\'a pas pu être lu dans le PDF (colonnes entrelacées). À trancher sur le texte avant tout usage client.',
        source: 'CCT du 08/12/2025 (CP 111) — régime de pension sectoriel social, banque CCT du SPF Emploi, 111/111-2025-014803.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 111 — Métal (constructions métallique, mécanique et électrique)',
    nomCourt: 'Métal',
    source: 'SPF Emploi — salairesminimums.be, CP 1110001 (transformation des métaux), minimum national 16,6564 €/h en vigueur 01/01/2026. Confirmé Agoria.',
    dateVerif: '2026-06-19',
    regimeHebdo: 38,                           // h/semaine standard
    // ✅ 16/09/2026 — CALENDRIER CORRIGÉ : il portait `frequence: 'semestrielle', mois: [1, 7]`.
    // La CP 111 est indexée UNE fois par an, au 1er juillet, dans ses trois sous-secteurs :
    // CCT du 06/07/2026 n° 200968/CO/111 (111.01/02) et n° 200969/CO/111 (111.03), art. 2,
    // textes lus le 16/09/2026 ; historique SPF salairesminimums.be (1110001 et 1110030) : toutes
    // les indexations tombent en juillet, de 2008 à 2026. Les hausses de JANVIER (2022, 2024,
    // 2026) sont des augmentations NÉGOCIÉES (ex. CCT 197236/CO/111), pas des indexations : le
    // mois 1 faisait annoncer par la veille une indexation fictive chaque janvier (il n'y en a
    // eu aucune en janvier 2025). Elles restent détectées : `baremes-cp` compare les VALEURS et
    // couvre la CP 111 sans calendrier (SOURCES_BAREMES['111'], baremes-parsers.js).
    // Historique : le mois 7 avait été ajouté le 05/08/2026 sur une source unique (Partena),
    // validée par le fondateur ; la source est désormais le texte des CCT.
    // ⚠️ Non lue : la CCT fondatrice du 16/06/1997 (45241/CO/111) — la fréquence tient sur les
    // CCT de 2026 et 18 ans d'historique SPF, pas sur ce texte.
    // ✅ 16/09/2026 — L'UNITÉ DE `augmentationCCT` MANQUAIT : l'écran affichait « 0,2606 » sans rien.
    // Texte primaire lu : CCT du 12/01/2026 n° 197236/CO/111, art. 2 — « Au 1er janvier 2026, le
    // salaire horaire minimum national est augmenté de 0,2606 euros ». Augmentation CONVENTIONNELLE,
    // pas une indexation, qui ne vise que les minima (art. 2) des SCP 111.01 et 111.02.
    // ⚠️ CETTE LIGNE `indexation` DOIT RESTER SUR UNE SEULE LIGNE : `calendrier-propositions.js`
    // (correction de calendrier en un clic depuis Telegram) refuse un bloc sur « plusieurs lignes ».
    // Les commentaires vont AU-DESSUS, jamais dedans. Verrou : calendrier-propositions-lois-reelles.test.js.
    indexation: { frequence: 'annuelle', mois: [7], dernierTaux: 0.0281, derniereDate: '2026-07-01', augmentationCCT: 0.2606, augmentationCCTDate: '2026-01-01', augmentationCCTUnit: 'EUR/heure', augmentationCCTPerimetre: "salaire horaire minimum national (et minimums provinciaux ou régionaux relevés s'ils restaient inférieurs) — barèmes seulement, pas les salaires effectifs supérieurs ; SCP 111.01 et 111.02, la 111.03 (montage de ponts et charpentes métalliques) est exclue", augmentationCCTSource: "CCT du 12/01/2026 n° 197236/CO/111, art. 1 et 2 et annexe (banque CCT du SPF Emploi, 111-2026-000145.pdf), texte primaire lu le 16/09/2026 ; concordent : fiche SPF salairesminimums.be CP 1110001 au 01/01/2026, Partena Professional (adaptation des salaires janvier 2026), Agoria (22/12/2025), CSC Metea", note: "Indexation une fois par an, au 1er juillet. Au 01/07/2026 : indexation de 2,81 % (CCT du 06/07/2026 n° 200968/CO/111 pour les SCP 111.01 et 111.02, n° 200969/CO/111 pour la SCP 111.03, art. 2 — textes lus le 16/09/2026 ; fiche SPF concordante). Les hausses de janvier (2022, 2024, 2026) sont des augmentations négociées, pas des indexations." },

    // ── Sous-secteurs CP 111 (3 sous-CP officielles) ──
    sousSecteurs: {
      '111.01': 'Entreprises professionnelles de la transformation des métaux (industriel)',
      '111.02': 'Entreprises artisanales de la transformation des métaux (PME)',
      '111.03': 'Entreprises de montage de ponts et charpentes métalliques',
    },

    // ⚠️ CE 5 % N'EST PAS SOURÇABLE EN UN NOMBRE — instruit le 26/08/2026, et
    // c'est la conclusion qui compte : la convention a été LUE, et elle ne
    // permet pas d'écrire un taux unique.
    //
    // Statuts coordonnés du 16/12/2019 (dépôt 156835/CO/111, AR du 07/03/2021),
    // 104 000 caractères, § 2 « Cotisations pour la sécurité d'existence ». Ce
    // qu'on y trouve n'est pas UN taux mais une superposition :
    //   · 0,66 % pour le fonctionnement général du fonds (depuis le 01/07/2017) ;
    //   · une cotisation « pension complémentaire » qui monte par paliers
    //     depuis 2000 — 1,00 puis 1,25 · 1,50 · 1,60 · 1,70 · 1,80 · 1,90 % —
    //     et qui SE DÉDOUBLE PAR PROVINCE depuis 2014 : 2,39 % à Anvers, au
    //     Limbourg, en Flandre orientale…, 2,19 % ailleurs, plus une majoration
    //     de 8,86 % qui porte ces deux-là à 2,5929 % et 2,3752 % ;
    //   · des cotisations NOMMÉMENT propres à deux entreprises (COFELY FABRICOM
    //     SA et COFELY FABRICOM INDUSTRIE SUD SA, avec leurs numéros BCE) ;
    //   · une allocation spéciale compensatoire (0,60 + 0,15 + 0,10 %), la
    //     formation des organisations (0,10 %), l'emploi et la formation
    //     (0,12 %), les groupes à risque (0,10 %), et des cotisations
    //     PROVINCIALES distinctes selon l'ASBL bénéficiaire — 0,25 % pour l'IFP
    //     (Liège, Luxembourg), 0,07 % pour le FRMS (Brabant, Bruxelles),
    //     0,25 % pour le FTMA… ;
    //   · un régime de DISPENSE : une entreprise qui finance son propre système
    //     de pension complémentaire équivalent est exemptée de la part pension.
    //
    // ⛔ POURQUOI ON N'EN FAIT PAS UNE SOMME. Le taux dû dépend de la PROVINCE,
    // de l'ASBL de formation compétente, de l'existence d'un régime de pension
    // d'entreprise, et pour deux employeurs de leur NOM. Additionner tout ce
    // qui traîne donnerait un nombre que personne ne paie — et le présenter
    // comme le taux de la CP 111 serait une invention plus convaincante que le
    // 5 % actuel, donc plus dangereuse. La fiche reste marquée NON VÉRIFIÉE et
    // l'écran le dit ; le chemin de lecture est écrit dans
    // `docs/10-19 Payroll/10.15` pour que la synthèse se fasse une bonne fois,
    // avec la province et le régime de pension comme entrées.
    fondsSE: {
      nom: "FSEFM — Fonds de sécurité d'existence des fabrications métalliques",
      taux_patronal: 0.05,
      aVerifier: true,
      tauxNonUnique: {
        raison: "le taux du depend de la PROVINCE, de l'ASBL de formation competente, de l'existence d'un regime de pension d'entreprise (dispense), et pour deux employeurs de leur nom",
        composantesLues: {
          fonctionnementGeneral: 0.0066,
          pensionComplementaireAnversLimbourgFlandreOr: 0.025929,
          pensionComplementaireAutresEntreprises: 0.023752,
          allocationSpecialeCompensatoire: 0.0085,
          formationOrganisations: 0.001,
          emploiEtFormation: 0.0012,
          groupesRisque: 0.001,
          provincialSelonAsbl: { IFP_Liege_Luxembourg: 0.0025, FRMS_Brabant_Bruxelles: 0.0007, FTMA: 0.0025 },
        },
        dispense: "une entreprise qui finance un systeme de pension complementaire equivalent est exemptee de la part pension",
        source: "Statuts coordonnes du 16/12/2019, § 2 — depot 156835/CO/111, AR du 07/03/2021, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026 (104 000 caracteres).",
        dateVerif: '2026-08-26',
      },
      // ⚠️ DÉPLACÉ DE `note` VERS `aVerifierRaison` LE 26/08/2026, et ce n'est
      // pas cosmétique : `aVerifierRaison` est le champ que l'écran REND. Tant
      // que ce texte vivait dans `note`, la CP 111 était la seule des 26 fiches
      // non vérifiées à afficher « aucune recherche n'est consignée » — alors
      // que sa convention a été lue au texte primaire (104 000 caractères,
      // statuts coordonnés du 16/12/2019, dépôt 156835/CO/111).
      // Une recherche aboutie rangée dans le mauvais champ se lit comme une
      // recherche jamais faite, et invite la session suivante à la refaire.
      aVerifierRaison: "Le 5 % qui figure ici n'a AUCUNE source et se présentait comme « ~5 % ». Il est conservé et marqué non vérifié parce que la convention A ÉTÉ LUE et qu'elle ne permet pas d'écrire un taux unique : les statuts coordonnés du 16/12/2019 (dépôt 156835/CO/111, AR du 07/03/2021, texte primaire lu le 26/08/2026) fixent des taux qui varient selon l'ASBL provinciale. Détail dans `pensionSectorielle` ci-dessus et dans docs/10-19 Payroll/10.15.",
      note: "Voir `aVerifierRaison` : recherche faite, taux non unique.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (10,00 €) était le plafond
    // fédéral recopié {10/8.91/1.09}, sans lien avec le secteur. Fiche Limosa
    // officielle du SPF Emploi (Detachering111.pdf, dernière MAJ 10/07/2026,
    // texte primaire, 75 pages) : la section « Vergoeding voor Reis-,
    // Maaltijd- en Verblijfkosten » ne couvre QUE le remboursement de frais de
    // déplacement/logement — zéro ligne sur un chèque-repas sectoriel.
    chequesRepas: {
      obligatoire: false,
      source: 'Fiche Limosa officielle SPF Emploi, Detachering111.pdf (MAJ 10/07/2026), texte primaire — aucune ligne chèque-repas',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi :
    // deux CCT « SYSTEME SECTORIEL ECO-CHEQUES » couvrent l'ensemble de la CP,
    // toutes deux entrées en vigueur le 01/10/2021, durée indéterminée.
    //   · CCT du 20/12/2021 (111-2022-000298.pdf, enregistr. 27/04/2022) — CP
    //     111 hors montage de ponts et charpentes métalliques.
    //   · CCT du 25/04/2022 (111-2022-010159.pdf, enregistr. 24/08/2022) —
    //     section paritaire 111.03 (monteurs de ponts et charpentes).
    // Art. 3 des deux textes, mot pour mot : « tous les ouvriers à temps plein
    // reçoivent annuellement des éco-chèques pour une valeur totale de 250 EUR »
    // — valeur nominale max. 10 EUR/chèque, proratisée temps partiel (art. 5) et
    // proratisée sur la période d'occupation si non couverte en entier. 250 EUR
    // réels et sourcés — coïncidence avec la valeur par défaut de la grille, pas
    // confirmation de celle-ci.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 20/12/2021 (111-2022-000298.pdf) + CCT du 25/04/2022 pour la section 111.03 (111-2022-010159.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : art. 3, 250 EUR/an pour un temps plein, en vigueur depuis le 01/10/2021, valeur nominale max. 10 EUR/chèque.",
    ecoChequesDateVerif: '2026-09-18',
    primeFDA_pct: 8.33,                        // ~1 mois brut via fonds
    formation: { jours: 5, fonds: 'Educam / INOM' },
    cotisationSpeciale: null,

    // ── Catégories CP 111 ──
    // Salaire horaire minimum NATIONAL au 01/01/2026 = 16,6564 €/h (régime 38h).
    // Source PRIMAIRE : SPF Emploi — salairesminimums.be, CP 1110001 (transformation
    // des métaux), en vigueur 01/01/2026 (CCT trajectoire 2021-2022 +0,2606 € + index),
    // page actualisée 14/04/2026. Confirmé Agoria. Vérifié 2026-06-19.
    // ⚠️ Sous-secteurs 111.01/02 « régions plates » (National/Brabant/Anvers/Limbourg/
    // Namur/Hainaut) = taux unique 16,6564 €/h. Flandre Occ./Or. = grille 11 classes
    // (16,6564 → ~20,17 €/h) ; Liège-Lux. varie par régime horaire ; 111.03 montage =
    // grille séparée 37h. Ce barème = MINIMUM NATIONAL + progression simplifiée par
    // niveau : l'employeur affilié Agoria doit appliquer sa classe provinciale exacte
    // (potentiellement supérieure). Prochaine indexation : 01/07/2026.
    categoriesOuvriers: {
      cat_1: { code: 'C1', label: 'Catégorie 1 — Manœuvre / non qualifié',         tauxHoraire: 16.6564, niveau: 'Manœuvre' },
      cat_2: { code: 'C2', label: 'Catégorie 2 — Semi-qualifié',                    tauxHoraire: 16.83, niveau: 'Semi-qualifié' },
      cat_3: { code: 'C3', label: 'Catégorie 3 — Qualifié',                         tauxHoraire: 17.45, niveau: 'Qualifié' },
      cat_4: { code: 'C4', label: 'Catégorie 4 — Hautement qualifié',               tauxHoraire: 18.10, niveau: 'Hautement qualifié' },
      chef_equipe: { code: 'CE', label: 'Chef d\'équipe (+10% sur catégorie)',      tauxHoraire: 19.91, niveau: 'Encadrement' },
      contremaitre: { code: 'CM', label: 'Contremaître (+15% sur catégorie)',       tauxHoraire: 20.82, niveau: 'Encadrement' },
    },

    // ── Heures sup CP 111 ──
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%, supplément CP 111 si convention)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    // ── Indemnités CP 111 ──
    indemnites: {
      mobilite_km: 0.1579,                     // EUR/km (depuis 01/01/2022, non auto-indexé)                       // EUR/km voiture privée pour service
      velo: 0.27,                              // EUR/km vélo (max 40 km AR/j) — CCT PC 111 accord 2023-2024 (dès 01/01/2024), inchangé 2026 : négociations 2025-2026 échouées (Agoria 12/2025) — Liantis/Acerta, consulté 2026-07-07
      veloMaxJour: 10.80,                      // EUR/jour (40 km AR × 0,27)
      veloMaxExonere: 0.37,                    // EUR/km max exonéré fiscal/ONSS 2026
      // Prime garde enfants
      gardeEnfants_jour: 1.75,                 // EUR/jour, max 210 EUR/an/enfant
      gardeEnfants_max_annuel: 210,            // EUR/an/enfant ≤ 3 ans
      // Complément chômage temporaire
      complement_chomage_temporaire: 5.31,     // EUR (depuis 01/03/2026)
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'Agoria CCT 2021-2022 (trajectoire) + CGSLB CP 111 + Métallos FGTB + Prestataire C',
      indexationFrequence: '1x/an au 01/07 (111.01, 111.02 et 111.03) + augmentation CCT 0,2606 €/h au 01/01/2026 (111.01 et 111.02 seulement)',
      // ⚠️ CORRIGÉ LE 16/09/2026 : cette clé valait 15,9616, qui est le minimum du 01/07/2024.
      // Fiche SPF salairesminimums.be CP 1110001 au 15/07/2025 (« Indexation de 2,72% en 7/2025 »),
      // lue le 16/09/2026. Contrôle : 16,3958 + augmentation CCT 0,2606 = 16,6564, le minimum
      // du 01/01/2026 écrit à l'annexe de la CCT 197236/CO/111. Aucun lecteur dans l'app.
      salaireMin01072025: 16.3958,             // EUR/h
      salaireMin01012026: 16.6564,             // EUR/h — minimum national SPF salairesminimums.be CP 1110001, en vigueur 01/01/2026 (index + CCT trajectoire 2021-2022 +0,2606 €). Vérifié 2026-06-19.
      classification: '4 catégories ouvriers (1-4) + chef d\'équipe + contremaître',
      sousSecteursMVP: '111.01 + 111.02',
      notes: 'Pas d\'accord sectoriel 2025-2026 abouti (négociations échouées 15/12/2025 selon CSC Metea). Trajectoire CCT 2021-2022 maintenue. Salaires régionaux/provinciaux possibles via Agoria.',
      nouveautes2026: [
        'Salaire minimum national +0,2606 €/h au 01/01/2026 (trajectoire CCT)',
        'Complément chômage temporaire 5,31 € au 01/03/2026 (dépassement indice pivot)',
        'Indexation 2024 +3,34% confirmée',
        'Pas d\'accord sectoriel 2025-2026 abouti',
      ],
    },
  },

  // CP 118 — Industrie alimentaire
  '118': {
    // ── Régime hebdomadaire — ajouté le 23/08/2026 ────────────────────────
    // ✅ Fiche Limosa de détachement du SPF Emploi, PC 118 sous-secteur
    // Viande, « Laatste aanpassing: 26/02/2026 », lue le 23/08/2026 :
    // « 4.1 Arbeidsduur — Gemiddelde effectieve wekelijkse arbeidsduur over
    //   het ganse jaar: 38 u. » (durée hebdomadaire effective MOYENNE sur
    //   toute l'année : 38 h), CCT du 30/03/1988 (20608), AR du 16/01/1989,
    //   modifiée par la CCT du 30/04/1999 (51282), AR du 27/09/2000.
    //
    // ⚠️ RÉSERVE DE PÉRIMÈTRE, à ne pas effacer. La fiche Limosa vise le
    // SOUS-SECTEUR VIANDE, pas toute la CP 118 (industrie alimentaire) — le
    // playbook le dit : le silence d'une fiche Limosa sur une fonction ne
    // prouve rien. Le 38 h concorde toutefois avec `heuresSemaine: 38` de la
    // grille vérifiée, ce qui vaut recoupement indépendant.
    regimeHebdo: 38,
    regimeHebdoNote: {
      base: 'durée hebdomadaire effective MOYENNE sur toute l\'année',
      perimetreSource: 'fiche Limosa du sous-secteur Viande — pas toute la CP 118',
      source: "Fiche Limosa de détachement, SPF Emploi, Detachering118.pdf, § 4.1, dernière adaptation 26/02/2026. CCT du 30/03/1988 (20608) modifiée par la CCT du 30/04/1999 (51282). Lue le 23/08/2026. Recoupée avec `heuresSemaine: 38` de la grille vérifiée.",
      dateVerif: '2026-08-23',
    },
    nom: 'CP 118 — Industrie alimentaire',
    // `formule` = METADATA de calcul (comment le taux se calcule), pas une valeur légale.
    // Référence NOVEMBRE sourcée : CGSLB décrit l'indexation de janvier des CP 118 et 220
    // comme calculée « sur base de l'indice santé lissé de fin novembre ». Reproduit :
    // 133,07 / 130,22 = 2,188604 % → arrondi 2,19 %, le taux appliqué.
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0219, derniereDate: '2026-01-01', formule: 'sante_lisse_nov', note: 'Q2 2026: +2,19% industrie alimentaire ouvriers. Source: CGSLB CP 118-220, SD Worx' },
    fondsSE: {
      nom: 'IFP (Institut Formation Professionnelle)',
      // ✅ DEUX SOURCES QUI TOMBENT SUR LE MÊME TOTAL, AU CENTIÈME.
      // CCT du 11/10/2017, n° 142869/CO/118, « fixant les cotisations pour le
      // Fonds social et de garantie de l'industrie alimentaire » (banque CCT du
      // SPF, 118/118-2017-013806.pdf), texte primaire lu le 25/08/2026 :
      //   art. 3 : 1,17 % pour le Fonds social et de garantie, depuis le
      //            01/07/2018 et pour une durée indéterminée (2,47 % avant)
      //   art. 4 : 0,30 % pour l'IFP — 0,15 % pour la formation en général et
      //            0,15 % pour les groupes à risque
      //   total  : 1,47 %, exactement ce que l'ONSS perçoit sous le code 820
      //            pour les catégories 048, 051, 052 et 848.
      // ⚠️ Le « environ 6,5 % » qui figurait ici n'avait aucune source, et le nom
      // de la fiche ne désigne que la composante IFP — 0,30 % sur 1,47 %.
      taux_patronal: 0.0147,
      taux_patronal_employe: 0.004,
      tauxComposantes: {
        fondsSocialEtGarantie: 0.0117,   // art. 3, depuis le 01/07/2018
        ifpFormation: 0.0015,            // art. 4, formation en général
        ifpGroupesARisque: 0.0015,       // art. 4, initiatives groupes à risque
      },
      base108Ouvrier: true,
      onssCategorie: '048, 051, 052, 848',
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "CCT du 11/10/2017, n° 142869/CO/118, art. 3 et 4 (banque CCT du SPF Emploi, 118/118-2017-013806.pdf), texte primaire lu le 25/08/2026 : 1,17 % + 0,30 % = 1,47 %. Recoupé au centième avec le fichier des taux DmfA 2026/3 de l'ONSS, catégories 048, 051, 052 et 848, code 820. Deux sources indépendantes, même total. ⚠️ Hors boulangeries et pâtisseries de produits frais, exclues par l'article 1er de la convention.",
      note: "Valeur précédente : « environ 6,5 % », sans source. L'ONSS publie aussi des régimes conditionnels pour ces catégories (faisabilité du travail, pension sectorielle) : ils ne s'additionnent pas d'office à ce taux.",
    },
    // 🔴 MÉCANISME, PAS UN MONTANT FIXE — sondé le 05/09/2026. L'ancienne
    // valeur (10,00 €) était le plafond fédéral recopié, structurellement
    // fausse : ce secteur n'a pas de montant sectoriel unique. Accord
    // sectoriel 2025-2026 (CGSLB, Liantis, Monizze concordants) : entreprises
    // qui accordaient déjà des chèques-repas au 01/10/2025 → majoration
    // automatique de l'intervention patronale de +1 € au 01/01/2026 (jusqu'à
    // +2 € négociable en entreprise) ; entreprises n'en accordant pas encore
    // → avantage équivalent obligatoire, au plus tard par CCT d'entreprise le
    // 15/02/2026, sinon 240 € net d'ici juillet 2026. Quote-part travailleur
    // standard : 1,09 €. Aucun total sectoriel unique n'existe — ne PAS coder
    // de montant fixe sans connaître le point de départ de l'entreprise.
    chequesRepas: {
      obligatoire: true, valeur: null, part_employeur: null, part_travailleur: 1.09, aVerifier: true,
      aVerifierRaison: "Mécanisme incrémental sans montant sectoriel unique — voir commentaire ci-dessus, dépend du régime déjà en place chez l'employeur.",
      source: 'CGSLB « CP 118 Accord sectoriel 2025-2026 » + Liantis + Monizze, concordants',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 118 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 118 — recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 4 CCT, dont deux intitulées « éco-chèques » (dépôts 94935 du 29/06/2009 et 94790 du 28/05/2009), toutes deux ÉCHUES au 31/12/2009. Aucune CCT postérieure sur le sujet : aucun montant sectoriel en vigueur. ⚠️ La note précédente disait « 0 résultat » : c'était l'orthographe cherchée qui ne rendait rien, pas l'absence (R2 ter).",
    ecoChequesDateVerif: '2026-09-18',
    primeFDA: { type: '1 mois brut', prorata: true },
    formation: { jours: 5, fonds: 'IFP' },
    cotisationSpeciale: null,

    // ── Heures supplémentaires CP 118 — ajoutées le 23/08/2026 ───────────
    // ✅ Fiche Limosa de détachement du SPF Emploi, Detachering118.pdf,
    // § 2.2.1.5, reproduisant la CCT du 22/09/2017 (142270), AR 25/03/2018 —
    // B.S. 17/04/2018. Lue le 23/08/2026 (fiche mise à jour le 26/02/2026).
    //
    // ⚠️ CE QUE LE SECTEUR CHANGE N'EST PAS LE TAUX, C'EST LE QUOTA — et le
    // dire mal coûterait cher dans les deux sens. Le sursalaire reste celui
    // de la loi du 16/03/1971 : la CCT ne le touche pas. Ce qu'elle ouvre,
    // c'est le nombre d'heures supplémentaires pour lesquelles le TRAVAILLEUR
    // peut renoncer au repos compensatoire (art. 26bis, § 2bis) — porté à
    // 130 h, puis à 143 h. Ce n'est ni un droit automatique ni une décision
    // de l'employeur seul : il faut la procédure des art. 4 et 5.
    heuresSup: {
      tauxSectoriel: null,               // le secteur ne déroge pas au plancher fédéral
      tauxRenvoi: 'loi du 16/03/1971 (tempsTravail.heuresSupp) — aucune majoration sectorielle',
      quotaRenonciationReposCompensatoire: {
        base: 'art. 26bis, § 2bis de la loi du 16/03/1971',
        paliers: [130, 143],             // heures
        procedure: "CCT d'entreprise conclue avec toutes les organisations représentées à la délégation syndicale ; à défaut de délégation compétente, CCT au sens de la loi du 05/12/1968",
        progressivite: "sans accord préexistant, on passe d'abord à 130 h ; le palier de 143 h n'est ouvert que s'il apparaît que 130 h ne suffisent pas aux besoins d'organisation",
        effetArret: "dès le dépôt au greffe du SPF Emploi, les dispositions entrent automatiquement au règlement de travail",
        source: 'CCT du 22/09/2017 (142270), art. 3 à 5',
      },
      source: 'Fiche Limosa de détachement, SPF Emploi, Detachering118.pdf, § 2.2.1.5, lue le 23/08/2026',
      dateVerif: '2026-08-23',
      // ⚠️ Même réserve de périmètre que `indemnites` ci-dessous.
      perimetreSource: 'fiche du SOUS-SECTEUR VIANDE — voir indemnites.perimetreSource',
    },

    // ── Indemnités sectorielles CP 118 — ajoutées le 23/08/2026 ──────────
    // ✅ Même fiche, § 2.2.1.
    //
    // 🛑 RÉSERVE DE PÉRIMÈTRE ÉCRITE DANS LA DONNÉE, PAS EN COMMENTAIRE, ET
    // C'EST LA PARTIE IMPORTANTE. La fiche Limosa de la CP 118 s'intitule
    // « PC 118, deelsector Vlees » : elle ne couvre QUE le sous-secteur de la
    // VIANDE. La CP 118 (industrie alimentaire) en compte beaucoup d'autres —
    // boulangeries, brasseries, sucre, conserves de légumes… Servir ces
    // valeurs comme celles de toute l'industrie alimentaire serait le défaut
    // du drapeau nu : une donnée juste sur un périmètre, fausse sur l'autre.
    // ⚠️ velo 0,31 EUR/km depuis le 01/02/2026 — fiche Limosa du SPF Emploi
    // (Detachering118.pdf, derniere adaptation 26/02/2026), lue le 25/08/2026 :
    // « Werknemers die zich volledig of gedeeltelijk met de fiets verplaatsen,
    // ontvangen vanaf 1 februari 2026 een fietsvergoeding van 0,31 EUR per kilometer ».
    // La CP 118 n'avait AUCUN tarif code. Concorde avec la CP 220, son homologue
    // EMPLOYE dans la meme industrie alimentaire, deja codee a 0,31.
    indemnites: { velo: 0.31,
      perimetreSource: 'SOUS-SECTEUR VIANDE UNIQUEMENT (fiche « PC 118, deelsector Vlees ») — ne vaut pas pour les autres sous-secteurs de la CP 118',
      source: 'SPF Emploi, Detachering118.pdf, § 2.2.1, lue le 23/08/2026 (fiche mise à jour le 26/02/2026)',
      dateVerif: '2026-08-23',

      // Prime annuelle brute — due À DÉFAUT de CCT d'entreprise ayant
      // affecté l'enveloppe. Deux CCT successives qui se CUMULENT (la
      // seconde le dit expressément).
      primeAnnuelleBrute: {
        montant_2016: 80,                // EUR/an dès le 01/01/2016, CCT 15/09/2015 (130431)
        montant_2017: 55,                // EUR/an dès la période de référence 2017, CCT 13/06/2017 (140181)
        cumul: true,                     // « boven op de jaarlijkse brutopremie van 80 EUR »
        condition: "due à défaut de CCT d'entreprise affectant l'enveloppe (0,3 % puis 0,2 % de la masse salariale)",
        source: 'CCT du 15/09/2015 (130431), art. 4 § 2 ; CCT du 13/06/2017 (140181), art. 3',
      },

      // Prime de froid.
      primeFroid: {
        pct: 0.10,                       // supplément de salaire de 10 %
        condition: "travailleurs occupés HABITUELLEMENT en chambres froides ou camions frigorifiques pour produits surgelés (-18 °C)",
        equivalence: "peut être remplacée au niveau de l'entreprise par un régime équivalent, éventuellement déjà conventionnel (fixation du salaire, classification scientifique de fonctions)",
        source: 'CCT du 14/03/1991 (27298), AR 18/09/1991 — B.S. 31/10/1991, art. 2',
      },

      // Prime de fin d'année — le registre `prime-fin-annee.js` porte déjà la
      // dimension ; on ne recopie ici que les règles d'ASSIMILATION, qui n'y
      // sont pas et qui décident du montant réellement dû.
      primeFinAnneeAssimilations: {
        droit: 'au prorata d\'un douzième par mois de service effectivement presté, dès un mois de service',
        moisEntree: 'compté si l\'entrée a lieu entre le 1er et le 15 du mois',
        moisSortie: 'compté si la sortie a lieu après le 15 du mois',
        assimiles: "accident du travail ou maladie professionnelle indemnisé (12 mois) ; accident ou maladie hors travail (12 mois) ; la reprise progressive avec accord du médecin-conseil reste assimilée dans la limite des 12 mois",
        rcc: 'les mois de chômage avec complément d\'entreprise donnent droit à 20 % du solde de la prime, jusqu\'au 31 décembre de l\'année en cours',
        source: 'CCT du 16/09/2025 (196124), art. 2 et 3',
      },
    },
  },
  // ═══ CP 330 — UNE OMBRELLE, PAS UN SECTEUR (fiche reprise le 22/08/2026) ═══
  // 466 conventions déposées sous ce numéro : la plus grosse pile du pays. Ce
  // qui suit ne porte QUE ce qui a été lu comme s'appliquant à TOUTE la CP.
  // Prime de fin d'année, jours de congé supplémentaires et chèques-repas
  // dépendent du SOUS-SECTEUR et surtout de l'AUTORITÉ QUI SUBSIDIE
  // (Communauté flamande/VGC, COCOM, COCOF, fédéral) — app/lib/pfa-cp330.js.
  '330': {
    nom: 'CP 330 — Établissements de santé',

    // ── Prestations irrégulières CP 330 — ajoutées le 23/08/2026 ─────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 04/03/2011, n° 105791/CO/330, banque CCT
    // du SPF Emploi (330/330-2011-007065.pdf), toujours `enforced` au
    // 23/08/2026. Articles 3 à 10 lus dans le PDF déposé, pas dans un résumé.
    //
    // ⛔ CE NE SONT PAS DES SURSALAIRES D'HEURES SUPPLÉMENTAIRES, et les
    // ranger sous `heuresSup` serait exactement la faute que la CP 124 vient
    // de nous coûter : un moteur y lirait un `taux` et multiplierait une
    // heure supplémentaire par 0,56. Ce sont des SUPPLÉMENTS SUR LE SALAIRE
    // BARÉMIQUE dus au titre de l'HORAIRE, que l'heure soit supplémentaire ou
    // non. Le sursalaire de l'art. 29, lui, se cumule par-dessus.
    //
    // 🔁 Une seule écriture pour ONZE clés : les dix sous-divisions
    // `330.01.xx` héritent de cette fiche via app/lib/cp-sous-division.js.
    // Aucune CCT ne peut être déposée sous un numéro de sous-division —
    // recopier ce bloc dix fois donnerait onze vérités à tenir d'accord.

    // ── Heures supplémentaires CP 330 — ce qu'on sait, et ce qu'on ne sait pas
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux d'heures supplémentaires propre à la CP 330 n'a été trouvé",
      // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter) — d'où le
      // détail de la recherche, pour que la session suivante sache ce qui a
      // déjà été fait et ne recommence pas à l'identique.
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 3300000',
        quand: '2026-08-23',
        combien: '466 conventions listées, 18 titres retenus au filtre (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation)',
        resultat: "aucune ne fixe de taux d'heures supplémentaires ; les trois CCT du 04/03/2011 traitent des SUPPLÉMENTS pour prestations irrégulières, ce qui est autre chose",
        reste: 'recherche par TITRE uniquement — une CCT au titre neutre pourrait en contenir un ; à reprendre par thème ou par texte intégral',
      },
      // Le renvoi vers ce qui existe vraiment, pour qu'un lecteur ne conclue
      // pas « rien de sectoriel sur les horaires » : il y a beaucoup.
      voirAussi: 'indemnites.prestationsIrregulieres — suppléments d\'horaire, cumulables avec le sursalaire fédéral',
      source: 'CCT du 04/03/2011 n° 105791/CO/330 lue le 23/08/2026 ; absence de taux sectoriel constatée sur les 466 CCT listées de la CP 330',
      dateVerif: '2026-08-23',
    },
    // Les rémunérations mensuelles minimums du secteur sont fixées pour une
    // durée hebdomadaire de 38 HEURES MAXIMUM (CCT du 10/03/2008, art. 2 § 2,
    // 330/330-2008-001612.pdf). Recoupé : les CCT « jours supplémentaires » du
    // secteur des personnes âgées calculent la journée à 7,6 h en semaine de
    // 38 h (CCT du 18/05/2018, 330/330-2018-009060.pdf). Deux textes primaires
    // concordants, lus le 22/08/2026.
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'Q2 2026: +2% santé (premier mois après dépassement pivot public). Source: SD Worx, CSC CNE CP 330' },
    // 🔴 LE TAUX PATRONAL DE 6 % N'EST PAS SOURCÉ, ET IL PILOTE UN COÛT.
    // `fondsSE.taux_patronal` est lu par payroll-engine.js (ligne ~706) pour
    // facturer la cotisation au fonds : un employeur de la CP 330 se voit donc
    // compter 6 % du brut sur la foi d'un commentaire qui dit lui-même
    // « Environ 6% ». Ce que les textes primaires disent, eux (lus le
    // 22/08/2026) : le fonds du secteur s'appelle « Fonds social des
    // établissements et services de santé », il est institué par la CCT du
    // 08/10/2007, l'ONSS en perçoit les moyens, et la cotisation « groupes à
    // risque » vaut 0,20 % au T4 2025, 0,30 % au T1 2026 puis 0,10 % aux
    // T2-T3-T4 2026 (CCT du 13/10/2025, 330/330-2025-014254.pdf). Le Maribel
    // social, lui, n'est pas un prélèvement sur le brut mais une réduction de
    // cotisations patronales — ce n'est donc pas la même mécanique.
    // ⚠️ LA VALEUR EST LAISSÉE INCHANGÉE À DESSEIN : la toucher déplace un coût
    // employeur calculé par le moteur de paie (zone protégée K3). Arbitrage
    // fondateur, PR dédiée, jamais en passant. Verrou : le test
    // __tests__/cp330-fonds-taux-a-sourcer.test.js échoue si la valeur bouge
    // sans que la note soit mise à jour.
    fondsSE: {
      nom: 'Fonds social des établissements et services de santé',
      institueParCCT: 'CCT du 08/10/2007 (CP 330)',
      taux_patronal: 0.06,
      tauxASourcer: true,
      cotisationGroupesARisque: [
        { periode: '2025-T4', pct: 0.20 },
        { periode: '2026-T1', pct: 0.30 },
        { periode: '2026-T2', pct: 0.10 },
        { periode: '2026-T3', pct: 0.10 },
        { periode: '2026-T4', pct: 0.10 },
      ],
      fondsParSousSecteur: [
        'Fonds social pour les hôpitaux privés',
        'Fonds social pour les homes pour personnes âgées et les maisons de repos et de soins',
      ],
      // ── Deuxième pilier de pension — ajouté le 23/08/2026 ────────────────
      // ✅ CCT du 09/03/2026 (CP 330), « fixant, pour l'année 2026, le
      // pourcentage des cotisations au financement complémentaire du deuxième
      // pilier de pension », banque CCT du SPF, 330/330-2026-007963.pdf,
      // texte primaire lu. Art. 2 : « par trimestre, 0,22 % du montant brut
      // des rémunérations, avant retenue des cotisations personnelles de
      // sécurité sociale ». Périmètre : secteurs relevant de la Communauté
      // flamande et/ou de la COCOF bruxelloise — PAS toute la CP 330.
      // ⚠️ Pas de perception aux 1er et 2e trimestres 2026.
      deuxiemePilierPension: {
        pctParTrimestre: 0.22,
        annee: 2026,
        perimetre: "secteurs de la Communauté flamande et/ou de la Commission communautaire flamande de Bruxelles-Capitale",
        percu: "pas de perception aux 1er et 2e trimestres 2026",
        source: "CCT du 09/03/2026 (CP 330), art. 2 — banque CCT du SPF Emploi, 330/330-2026-007963.pdf, en exécution de la CCT du 12/11/2018 (149441/CO/330). Texte primaire lu.",
        dateVerif: '2026-08-23',
      },
      note: "🔴 taux_patronal 6 % NON SOURCÉ (héritage « Environ 6% ») et lu par le moteur de paie — à trancher. ⚠️ MESURE DU 23/08/2026, ET ELLE REND L'ARBITRAGE DÉCIDABLE : cinq CCT de la CP 330 fixant une cotisation ont été lues à la source (groupes à risque des trois fonds, responsabilisation, deuxième pilier). Les taux qu'elles fixent sont de 0,10 %, 0,20 %, 0,22 % et 0,30 % PAR TRIMESTRE. AUCUNE n'approche 6 %, et aucun texte fixant 6 % n'a été retrouvé en cherchant par « fonds de sécurité », « cotisation » et « fonds social ». Ce n'est pas une preuve que 6 % est faux — je n'ai pas énuméré TOUTES les cotisations possibles (Maribel, fonds de pension sectoriel) — mais l'écart d'ordre de grandeur est d'un facteur 20 à 60 selon le trimestre. Le reste du bloc est lu dans les textes primaires.",
      source: "CCT du 13/10/2025 (330/330-2025-014254.pdf), CCT du 08/09/2025 (330/330-2025-012715.pdf, -012716, -012717) et CCT du 09/03/2026 (330/330-2026-007963.pdf), banque CCT du SPF Emploi, lues les 22 et 23/08/2026.",
    },
    // Aucune règle sectorielle de chèques-repas ne couvre toute la CP 330 : les
    // six CCT « chèques-repas » du numéro 330 visent toutes les ÉTABLISSEMENTS
    // DE PROTHÈSE DENTAIRE (la plus récente, 27/04/2020, y fixe 5,91 € patronal
    // et 1,09 € travailleur depuis le 01/10/2020 — 330/330-2020-006927.pdf).
    chequesRepas: { obligatoire: false, note: "Pas de régime sectoriel CP 330 ; règle propre aux établissements de prothèse dentaire (CCT du 27/04/2020). Ailleurs : au niveau de l'entreprise." },
    // 🔴 CORRIGÉ LE 22/08/2026 — LA VALEUR 250 ÉTAIT FAUSSE ET PARTAIT CHEZ LE
    // CLIENT. L'agent d'audit d'onboarding annonçait « CP 330 : éco-chèques
    // 250 €/an obligatoires » (app/api/agents/onboarding/audit/route.js). Or la
    // banque CCT du SPF ne contient QU'UNE convention « éco-chèques » sous le
    // numéro 330 — celle du 10/10/2011 (330/330-2012-000212.pdf) — et elle
    // organise une distribution PONCTUELLE au titre de l'année 2010 : un
    // éco-chèque de 10 € par trimestre entamé, quatre au maximum. Il n'existe
    // AUCUN régime récurrent d'éco-chèques en CP 330. Mesuré deux fois, par
    // titre et en plein texte, le 22/08/2026 : un seul résultat les deux fois.
    //
    // ⚠️ RECOUPÉ LE 22/09/2026, EN VÉRIFIANT LES DIX SOUS-DIVISIONS IFIC
    // 330.01.xx (hôpitaux, MRS, soins à domicile, revalidation NL/FR,
    // établissements bicommunautaires/NL/FR, crèches NL/FR). Elles n'ont
    // aucune fiche propre (app/lib/cp-sous-division.js — « une seule écriture
    // pour onze clés ») : leur statut éco-chèques est donc CELUI DE LA MÈRE,
    // lu via ficheEffective(). La confirmation était donc nécessaire avant de
    // les considérer tranchées, pas seulement une formalité sur 330 elle-même.
    // Recherche indépendante (workflow cct-search.yml, run 35711203975) sur
    // les 4 orthographes standard : 1 seul résultat, le même — CCT
    // 108141/CO/330 (330/330-2012-000212.pdf), signée 10/10/2011, FIN DE
    // VALIDITÉ 31/12/2011, champ d'application « Maisons médicales
    // subventionnées par la Commission communautaire française et
    // mentionnées dans l'arrêté 2011/713 du Collège de la Commission
    // Communautaire » — une population de maisons médicales/COCOF, distincte
    // des dix populations IFIC ci-dessus. Confirme : aucune CCT éco-chèques
    // récurrente, sous aucune orthographe, pour aucune population de CP 330.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 330 (Établissements de santé) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : UNE seule CCT trouvée, CCT 108141/CO/330 du 10/10/2011 (330/330-2012-000212.pdf), fin de validité 31/12/2011, distribution PONCTUELLE au titre de 2010 (10 €/trimestre entamé, 4 maximum), limitée aux « Maisons médicales subventionnées par la Commission communautaire française » (arrêté 2011/713) — une population hors du périmètre des dix sous-divisions IFIC 330.01.xx. Aucun régime récurrent d'éco-chèques en CP 330, pour aucune population. Vérifié deux fois : 22/08/2026 (par titre et plein texte) et 22/09/2026 (workflow cct-search.yml, run 35711203975, recoupement indépendant sur les 4 orthographes standard).",
    ecoChequesDateVerif: '2026-09-22',
    primeFDA: { type: 'Prime d\'attractivité via fonds', prorata: true },
    formation: {
      jours: 5,
      fonds: 'FE-BI',
      cotisationGroupesARisque: "0,20 % au T4 2025, 0,30 % au T1 2026, 0,10 % aux T2-T3-T4 2026, sur le salaire global (art. 23 de la loi du 29/06/1981), perçue par l'ONSS pour le Fonds social",
      affectation: "engagement de personnel, formation des groupes à risque, développement de la classification de fonctions sectorielle",
      source: "CCT du 13/10/2025 (CP 330), art. 5 à 9, effets du 30/06/2025 au 31/12/2026 — 330/330-2025-014254.pdf. Texte primaire lu le 22/08/2026.",
    },
    cotisationSpeciale: null,
    indemnites: {
      prestationsIrregulieres: {
        // Art. 3 § 1 — le découpage de la journée qui commande tout le reste.
        plages: { jour: '08:00-18:00', soir: '18:00-20:00', nuit: '20:00-06:00', matin: '06:00-08:00' },
        // Art. 3 § 2 — un samedi / dimanche / jour férié court de 0 h à 24 h.
        joursSpeciaux: 'de 0 h à 24 h du jour concerné',

        soir_19h_20h: 0.20,        // art. 4 — UNIQUEMENT entre 19 h et 20 h, hors week-end et jours fériés
        nuit_20h_06h: 0.35,        // art. 5 § 1 — hors dimanche et jours fériés
        // Art. 5 § 2 — la règle qu'on oublie : le +35 % vaut pour TOUTES les
        // heures d'une prestation qui dépasse minuit, même commencée avant
        // 20 h ou finie après 6 h.
        depasseMinuit: 0.35,
        serviceInterrompu: 0.50,   // art. 6 — interruption d'au moins 4 h consécutives, début et fin le même jour calendrier ; vaut avant ET après l'interruption
        samedi: 0.26,              // art. 7
        dimanche: 0.56,            // art. 8
        jourFerie: 0.56,           // art. 9

        base: 'salaire barémique, au prorata de la durée des prestations irrégulières réellement effectuées (art. 2, §§ 2 et 3, et art. 3)',
        // Art. 10 § 1 — l'exclusion. Un travailleur déjà sous prime
        // forfaitaire du régime des institutions publiques ne cumule pas.
        exclusion: {
          qui: "les travailleurs qui, à l'entrée en vigueur de la CCT, recevaient déjà une prime forfaitaire couvrant les prestations irrégulières quel qu'en soit le nombre, selon le régime des institutions publiques (circulaires ministérielles des 3 novembre 1972 et 12 juin 1991)",
          regimeForfaitaire: '11 % de la rémunération + supplément de nuit de 2,0079 €/h à l\'indice base 138.01',
          articlesEcartes: 'art. 2 § 1er et § 2, et art. 4 à 9',
        },
        champ: "établissements soumis à la loi sur les hôpitaux · maisons de soins psychiatriques · initiatives d'habitation protégée · maisons de repos, MRS et centres de soins de jour pour personnes âgées · centres de revalidation",
        source: 'CCT du 04/03/2011, n° 105791/CO/330, art. 3 à 10 — banque CCT du SPF Emploi, 330/330-2011-007065.pdf, texte primaire lu le 23/08/2026',
        dateVerif: '2026-08-23',
      },
      // ⚠️ DEUX AUTRES CCT DU MÊME JOUR portent le même titre et des règles
      // voisines, mais sur un champ RESTREINT — ne pas les confondre avec
      // celle-ci : n° 105793 vise les Services du Sang de la Croix-Rouge,
      // n° 105792 les employeurs de soins infirmiers à domicile. Elles ne
      // sont pas codées ici : leur champ ne recouvre pas les sous-divisions
      // `330.01.xx`.
      autresChampsNonCodes: ['105793 — Services du Sang de la Croix-Rouge', '105792 — soins infirmiers à domicile'],

      // L'une des rares règles réellement SECTORIELLES de la CP 330 : son art.
      // 1er vise « les institutions relevant de la Commission paritaire des
      // établissements et des services de santé », sans distinction de
      // sous-secteur ni d'autorité subsidiante.
      rccComplementEntreprise: {
        formule: "la MOITIÉ de la différence entre la rémunération nette de référence et les allocations de chômage",
        aChargeDe: 'employeur',
        conditions: "le travailleur doit prouver son droit aux allocations de chômage ; l'indemnité cesse dès qu'il le perd, sauf cas prévus par la loi. ⚠️ L'employeur ne compense EN AUCUN CAS une modification ou une suppression des allocations par une indemnité plus élevée.",
        reference: "CCT n° 17 du CNT du 19/12/1974 et AR du 03/05/2007 ; dernier salaire mensuel brut plafonné selon la CCT n° 17 comme mois de référence",
        source: "CCT du 12/06/2023 (CP 330), n° 180960/CO/330, art. 1 à 5 — 330/330-2023-010502.pdf. Texte primaire lu page par page le 22/08/2026.",
      },
    },
    nonMarchand: true,
    _meta: {
      source: "Banque CCT du SPF Emploi — 466 conventions relevées sous le numéro 330 le 22/08/2026 ; textes primaires lus page par page pour le régime horaire, le fonds de sécurité d'existence, la cotisation groupes à risque, l'indemnité complémentaire RCC, la prime de fin d'année et les jours de congé supplémentaires.",
      dateVerif: '2026-08-22',
      limite: "⚠️ CP 330 est une OMBRELLE : prime de fin d'année, jours de congé supplémentaires et chèques-repas dépendent du SOUS-SECTEUR et de l'AUTORITÉ QUI SUBSIDIE. Ne jamais répondre « CP 330 » sans avoir posé cette question — voir app/lib/pfa-cp330.js. Les heures supplémentaires n'ont PAS été relevées : aucune CCT sectorielle lue à ce jour, la dimension reste ouverte.",
    },
  },
  // CP 318 — Aides familiales et seniors
  // ⚠️ Module 10 — Valeurs Q1 2026 (CP 318.01 sous-CP non marchand)
  // Sources : Horval (indexation +2% au 01/02/2026), CGSLB CP 318.01,
  // SETCa BHV, Espace Senior (salaires moyens 2 000-2 300 €/mois)
  '318': {
    nom: 'CP 318 — Aide à domicile (aides familiales, aides-ménagères sociales, gardes à domicile)',
    nomCourt: 'Aide à domicile',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [2], dernierTaux: 0.02, derniereDate: '2026-02-01' },
    sousSecteurs: {
      '318.01': 'Services agréés d\'aide à domicile (Wallonie / Bruxelles / Communauté germanophone)',
      '318.02': 'Aide à domicile Région flamande',
    },
    // ── FAÎTIÈRE : LE TAUX VIT DANS LES SOUS-COMMISSIONS, TOUTES SOURCÉES ──
    // 📉 4,0 % → 0 le 26/08/2026, et c'est le cas le plus net de la série.
    // Cette commission n'a AUCUNE convention sous son propre numéro dans la
    // banque CCT du SPF — deux sous-commissions sur deux portent, elles, un taux
    // lu à la source, de 0 à 1,08 %. Le nombre rond de la mère n'était donc ni
    // l'un ni l'autre : il valait quatre fois l'un d'eux, et il ne servait
    // qu'aux dossiers codés sur la commission mère, sans sous-commission.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE, et on ne fait pas de
    // moyenne : un employeur relève d'une sous-commission précise, c'est une
    // donnée du dossier. La fiche renvoie donc, et le calcul rend 0 tant qu'on
    // ne sait pas laquelle — même règle que la CP 152, posée le 25/08.
    fondsSE: {
      nom: "Aide à domicile — faîtière : le taux vit dans les sous-commissions",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '318.01': "Communauté française — 1,08 % ouvrier / 0,44 % employé",
        '318.02': "Communauté flamande — 0, l'ONSS n'y perçoit aucun fonds de sécurité d'existence, seulement un second pilier de pension",
      },
      source: "Banque CCT du SPF Emploi : aucune convention publiée sous le numéro 318 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Les sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS ou dans leur convention.",
      dateVerif: '2026-08-26',
      note: "Le 4,0 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    chequesRepas: {
      obligatoire: false,
      valeur: 0,
      part_employeur: 0,
      part_travailleur: 0,
      note: 'Cheques-repas selon CCT entreprise. Variable secteur subsidie public.',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 318 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 318 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFDA_pct: 8.33,
    primeFinAnnee_forfait_2025: 1092.10,        // EUR partie forfaitaire 2025 indexee
    primeFinAnnee_variable_pct: 0.025,          // 2,5% remuneration annuelle brute
    formation: { jours: 5, fonds: 'FE-BI' },
    nonMarchand: true,
    cotisationSpeciale: null,

    // ── Categories CP 318.01 (Wallonie/Bruxelles - non marchand subsidie) ──
    // Q1/Q2 2026 : indexation +2% au 01/02/2026
    // Source : Horval barèmes CP 318.01 + CGSLB
    // Statut employe en Region wallonne depuis 01/04/2020
    categoriesAideDomicile: {
      aide_familiale_debut: { code: 'AF1', label: 'Aide familiale (0-3 ans ancienneté)',          tauxHoraire: 13.95, statut: 'Employé', anciennete: '0-3 ans' },
      aide_familiale_exp:   { code: 'AF2', label: 'Aide familiale expérimentée (3-10 ans)',       tauxHoraire: 14.90, statut: 'Employé', anciennete: '3-10 ans' },
      aide_familiale_sen:   { code: 'AF3', label: 'Aide familiale senior (10+ ans)',              tauxHoraire: 15.85, statut: 'Employé', anciennete: '10+ ans' },
      aide_menagere_sociale:{ code: 'AMS', label: 'Aide-ménagère sociale',                        tauxHoraire: 13.40, statut: 'Employé', anciennete: '-' },
      garde_domicile:       { code: 'GD',  label: 'Garde à domicile',                             tauxHoraire: 14.20, statut: 'Employé', anciennete: '-' },
      ouvrier_polyvalent:   { code: 'OP',  label: 'Ouvrier polyvalent',                           tauxHoraire: 13.65, statut: 'Ouvrier',  anciennete: '-' },
      technicien_surfaces:  { code: 'TS',  label: 'Technicienne de surfaces',                     tauxHoraire: 13.50, statut: 'Ouvrier',  anciennete: '-' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 1.00, label: 'Heures sup nuit (+100%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      // ⚠️ PAS DE `kmVoiture` NI DE `kmVoiture_mission` ICI — RETIRÉS LE 24/08/2026.
      // Cette fiche portait 0,4327 €/km en « frais mission » : ce n'était pas un
      // tarif sectoriel mais le forfait fédéral TRIMESTRIEL du 2e trimestre 2026,
      // recopié, et périmé depuis le 30/06/2026. Le tarif applicable se lit
      // désormais dans app/lib/indemnite-km-statut.js (tarifKmVoiture /
      // tarifKmMission), qui rend le forfait fédéral en vigueur tant que la
      // CP 318 n'a pas fixé le sien. Verrou : __tests__/indemnite-km-pas-de-copie-federale.test.js.
      velo: 0.30,                              // EUR/km vélo — CCT n°164 supplétive au 01/01/2026 (max 40 km/j) : pas de CCT vélo sectorielle 318.01 identifiée (Horval/salairesminimums.be, consulté 2026-07-07 — à confirmer sur public-search SPF)
      veloMaxExonere: 0.37,                    // EUR/km max exonéré fiscal/ONSS 2026 (plafond 3 700 €/an)
      transportCommun_pct: 1.00,
      penibilite: { taux: 0.05, condition: 'Soins lourds, personnes en grande dépendance' },
      flexibilite: { taux: 0.05, condition: 'Horaires irréguliers' },
    },

    supplements: {
      reductionTpsTravail58ans: { reduction: 32, salaireMaintenu: true, condition: '58+ ans, accord non-marchand wallon 01/01/2022' },
      jourCommunauteFrancaise: { date: '27/09', remunere: true, condition: 'Travailleurs Communauté française' },
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'Horval CP 318.01 + CGSLB + SETCa BHV + Espace Senior + accord non-marchand wallon',
      indexationFrequence: 'Annuelle au 01/02',
      indexationQ1_2026: 0.02,
      regimeHoraire: '38h/sem (réduction 32h pour 58+ ans en RW)',
      classification: '7 fonctions principales (aide familiale × 3 niveaux, aide ménagère sociale, garde domicile, ouvrier polyvalent, technicienne surfaces)',
      notes: 'Secteur non marchand subsidié. CP 318.01 = Wallonie/Bruxelles/Germanophone. Statut employé pour aides familiales en RW depuis 01/04/2020.',
      nouveautes2026: [
        'Indexation +2% au 01/02/2026 (Horval)',
        'Indemnité km voiture : pas de tarif sectoriel — forfait fédéral annuel, indexé chaque 1er juillet',
        'Indemnité km mission : pas de tarif sectoriel — forfait fédéral trimestriel, recalculé chaque trimestre',
        'Réduction temps travail 58+ ans à 32h salaire maintenu',
      ],
    },
  },

  // CP 112 — Entreprises de garage
  // ⚠️ Module 09 — Valeurs Q1 2026 (indexation +1,1% au 01/04/2026)
  // Sources : CGSLB CP 112, salairesminimums.be, ICB-FR (Personnel garage),
  // Educam (formation), Traxio (CCT 2019-2020), Federauto
  '112': {
    // ── Avantage SECTORIEL — régime de pension sectoriel social ──────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 14/12/2023 (CP 112),
    // « Modification et coordination du régime de pension sectoriel social »,
    // banque CCT du SPF Emploi, 112/112-2023-017151.pdf. Mot pour mot :
    //   « La cotisation annuelle totale brute de chaque affilié au régime de
    //     pension sectoriel social s'élève, à partir du 1er janvier 2016, à
    //     1,80 pour cent de son salaire annuel brut sur lequel les cotisations
    //     O.N.S.S. sont dues. »
    // Puis : 4,5 % de frais de gestion → 1,72 % net, réparti en 1,64 % de
    // droits de pension individuels et 0,08 % de solidarité ; et « après
    // l'augmentation de la cotisation nette de 0,15 pour cent destinée à
    // couvrir la cotisation spéciale due de 8,86 pour cent, une cotisation
    // globale de 1,95 pour cent ».
    avantages: {
      pensionSectorielle: {
        regime: 'Régime de pension sectoriel social CP 112 (garages)',
        brutPct: 0.018,                  // 1,80 % du salaire annuel brut soumis ONSS
        depuis: '2016-01-01',
        fraisGestionPct: 0.045,          // 4,5 %, prélevés par l'Organisateur
        netPct: 0.0172,                  // 1,72 % après frais
        pensionIndividuellePct: 0.0164,  // 1,64 %
        solidaritePct: 0.0008,           // 0,08 %
        cotisationGlobalePct: 0.0195,    // 1,95 %, après +0,15 % pour la cotisation spéciale de 8,86 %
        source: 'CCT du 14/12/2023 (CP 112) — modification et coordination du régime de pension sectoriel social, banque CCT du SPF Emploi, 112/112-2023-017151.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 112 — Entreprises de garage (achat/vente/entretien véhicules motorisés)',
    nomCourt: 'Garages',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0204, derniereDate: '2026-02-01', source: 'AUDIT 2026-06 — Easypay/ACLVB' },
    fondsSE: {
      nom: 'Fonds social CP 112 — Entreprises de garage',
      taux_patronal: 0.0225,
      taux_patronal_employe: 0.0023,
      pension_sectorielle: 0.0195,
      pension_sectorielle_employe: 0.011899999999999999,
      base108Ouvrier: true,
      onssCategorie: '064',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 064 : 820 ouvriers 2,25 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (7,00 €) était la famille
    // recopiée {7/5.91/1.09}, sans source. Synova (ex-CGSLB), page CP 112
    // (garages), exhaustive (barèmes, pension sectorielle, éco-chèques
    // 2×125€, prime fin d'année, formation) : aucune mention de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 112, exhaustive — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 29/04/2014 (CP 112, garages), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : l'article prévoit, pour chaque ouvrier occupé à temps plein, DEUX tranches semestrielles d'éco-chèques de 125,00 EUR chacune, soit 250 EUR par an (texte original néerlandais : « 2 semestriële schijven van ecocheques […] elk ter waarde van € 125,00 »). La même CCT plafonne la valeur nominale d'un éco-chèque à 10,00 EUR. Portée : toute la CP. ⚠️ Le montant de 250 EUR n'est donc PAS la valeur générique recopiée : il est ici sourcé au texte.", ecoChequesDateVerif: '2026-08-24',                     // 2 × 125 EUR (15/06 et 15/12)
    ecoChequesSemestriel: 125,
    primeFinAnnee_pct: 8.33,                   // formule: salaire_horaire_dec × 38 × 52 / 12
    primeFinAnnee_formule: 'Salaire horaire décembre × 38h × 52sem / 12',
    formation: { jours: 5, fonds: 'Educam' },
    formationPrime: 45,                        // EUR/jour formation
    cotisationSpeciale: null,

    // ── Categories CP 112 (6 niveaux ABCDEF) ──
    // Q1 2026 : indexation +1,1% au 01/04/2026 confirmee
    // Source : CGSLB tableau salaires 01/02/2026 + Traxio CCT 2019-2020
    // Categories selon classification professionnelle CCT 22/04/2010
    categoriesGarage: {
      cat_A1: { code: 'A1', label: 'Cat A1 — Pompiste, laveur, portier (non qualifié)',         tauxHoraire: 14.50, niveau: 'Non qualifié' },
      cat_A2: { code: 'A2', label: 'Cat A2 — Manœuvre simple',                                   tauxHoraire: 14.90, niveau: 'Non qualifié' },
      cat_B1: { code: 'B1', label: 'Cat B1 — Aide-mécanicien / aide-vendeur',                    tauxHoraire: 15.45, niveau: 'Semi-qualifié' },
      cat_B2: { code: 'B2', label: 'Cat B2 — Mécanicien junior',                                 tauxHoraire: 16.10, niveau: 'Semi-qualifié' },
      cat_C1: { code: 'C1', label: 'Cat C1 — Mécanicien qualifié',                               tauxHoraire: 17.20, niveau: 'Qualifié' },
      cat_C2: { code: 'C2', label: 'Cat C2 — Mécanicien spécialisé / chef d\'atelier',           tauxHoraire: 18.30, niveau: 'Hautement qualifié' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    // ⚠️ velo 0,27→0,32 EUR/km (max 40 km/j A-R) — CCT ratifiee le 17/04/2026 art. 11 §1, lue le 24/08/2026. PLANCHER §2 : jamais moins que l'indemnite journaliere fondee sur l'abonnement hebdo (CCT 19/9), indexee chaque 1er fevrier sur les tarifs SNCB

    indemnites: {
      velo: 0.32,                              // EUR/km (max 40 km AR/j) — CCT CP 112 accord 2023-2024 (dès 01/07/2024), toujours en vigueur (barèmes FGTB 02/2026) ; accord 2025-2026 non conclu au 23/12/2025 — Liantis/FGTB Métallos, consulté 2026-07-07
      veloMaxExonere: 0.37,                    // EUR/km max exonéré fiscal/ONSS 2026
      // Stand-by (disponibilité hors heures normales)
      standBy_min: 1.50,                       // EUR/heure stand-by minimum
      standBy_max: 3.50,                       // EUR/heure stand-by maximum
      standBy_intervention_min: 25,            // EUR/intervention minimum
      // Prime fidélité après 18 ans d'ancienneté
      jourAnciennete18ans: { jours: 1, condition: '18 ans ancienneté entreprise' },
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 112 (01/02/2026) + Traxio CCT 2019-2020 + Educam + ICB-FR',
      indexationFrequence: 'Annuelle au 01/02 (technique) ou variable (+1,1% au 01/04/2026)',
      indexationQ1_2026: 0.011,
      classification: '6 catégories (A1, A2, B1, B2, C1, C2)',
      notes: 'Compétence : entreprises ressortissant achat/vente/entretien véhicules motorisés, parkings, car-wash, pneus. Cf. Federauto.',
      nouveautes2026: [
        'Indexation +1,1% au 01/04/2026',
        'Éco-chèques 2 × 125 €/semestre (15/06 et 15/12)',
        'Prime formation Educam 45 €/jour',
        'Jour congé ancienneté 18 ans dans l\'entreprise',
      ],
    },
  },

  // CP 314 — Coiffure, fitness et soins de beauté
  // ⚠️ Module 08 — Valeurs Q1 2026 (indexation au 01/12/2025 confirmee)
  // Sources : CGSLB CP 314, ACLVB (flash 286 indexation), CSC, FGTB Centrale,
  // salairesminimums.be SCP 3140000
  '314': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 24/09/2007 (CP 314), statut
    // de la délégation syndicale, banque CCT du SPF Emploi,
    // 314/314-2007-005164.pdf. Mot pour mot : « … est instaurée dans les
    // entreprises ou les groupes d'entreprises occupant pendant l'année civile
    // qui précède l'installation d'une délégation syndicale EN MOYENNE au
    // moins 20 travailleurs (ouvriers et employés). »
    //
    // ⚠️ MOYENNE SUR L'ANNÉE CIVILE PRÉCÉDENTE, pas l'effectif du jour — et
    // ouvriers ET employés confondus. Un secteur très saisonnier : lire
    // l'effectif à une date donnée donnerait la mauvaise réponse.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_moyen',
        seuilTravailleurs: 20,
        modeCalcul: 'moyenne de l\'année civile précédente, ouvriers et employés confondus, au niveau de l\'entreprise ou du groupe',
        localDedie: 'Un lieu pour les entreprises de 20 à 49 travailleurs ; un local pour celles de plus de 49.',
        source: 'CCT du 24/09/2007 (CP 314) — statut de la délégation syndicale, banque CCT du SPF Emploi, 314/314-2007-005164.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 314 — Coiffure, fitness et soins de beauté',
    nomCourt: 'Coiffure / Esthétique / Fitness',
    classifications: {
      source: "CCT du 19/06/2014 (CP 314, coiffure et soins de beaute), chapitre 3 « Classification des fonctions », banque CCT du SPF, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "Categories 1 a 7, declinees PAR SOUS-SECTEUR (art. 3-4) : la repartition renvoie aux baremes des CCT existantes propres a chaque sous-secteur. Sous-secteur coiffure : cat. 1 emplois-tremplin (embauche sans diplome ni experience, moins de 6 mois d'anciennete dans le secteur) · cat. 2 taches sous guidance ou surveillance · jusqu'a la cat. 7. ⚠️ Le sous-secteur doit etre determine AVANT la categorie.",
    },
    regimeHebdo: 38,
    // ⏸️ CORRIGÉ LE 02/09/2026 — CE SECTEUR N'INDEXE PAS EN DÉCEMBRE, IL INDEXE
    // AU FRANCHISSEMENT D'UN PIVOT. Le champ annonçait `mois: [12]`, ce qui en
    // faisait un secteur à calendrier : la veille attendait donc décembre, alors
    // que la CP 314 bouge le mois où l'indice santé franchit un seuil — sans
    // date prévisible. Une CP à index-pivot rangée dans un calendrier n'est pas
    // surveillée, elle est seulement constatée après coup.
    //
    // ✅ TEXTE PRIMAIRE LU : CCT du 03/07/2026, dépôt 200975/CO/314, art. 2 et 3
    // (banque CCT du SPF Emploi, lue le 02/09/2026). Elle REBASE les pivots du
    // secteur après le changement de base de Statbel (2013 = 100 → 2025 = 100) :
    // « le dernier indice-pivot dépassé dans la CP 314, à savoir 132,98 »
    // × 0,7376 = 98,09, puis × 1,02 = 100,05 pour le prochain, et ainsi de suite
    // (102,05 · 104,09 …). C'est pour ça que la base est notée : un pivot de
    // 100,05 comparé à un indice santé en base 2013 (~140) rendrait « franchi de
    // 40 % », une alerte massivement fausse — indice-pivot-veille.js refuse
    // justement de comparer deux bases qui ne concordent pas.
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2025-12-01',
      indicePivot: 100.05, indicePivotBase: '2025',
      indicePivotSource: "CCT du 03/07/2026 (CP 314, coiffure et soins de beauté), dépôt 200975/CO/314, art. 2 et 3 — banque CCT du SPF Emploi, texte primaire lu le 02/09/2026. Prochain pivot à dépasser après rebasage Statbel 2013→2025 ; les suivants se déduisent en multipliant par 1,02 (102,05 · 104,09 …), la CCT donnant la méthode.",
      indicePivotDateVerif: '2026-09-02' },
    sousSecteurs: {
      'coiffure':   'Salons de coiffure',
      'esthetique': 'Centres esthétiques / soins de beauté',
      'fitness':    'Centres de fitness / bodybuilding / saunas / solariums',
      'admin':      'Employés administratifs',
    },
    fondsSE: {
      nom: 'Fonds social CP 314 — Coiffure, fitness et beauté',
      taux_patronal: 0.1737,
      taux_patronal_employe: 0.1607,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '123 et 223',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 123 et 223. Codes travailleur : 820 ouvriers 17,37 % · 830 employés 16,07 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Coiffure / Centres de fitness »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Le taux ouvrier et le taux employé diffèrent : 17,37 % contre 16,07 %. La valeur codée jusqu'au 25/08/2026 était 2,50 % — sept fois trop basse, soit 401 € par mois oubliés sur un brut de 2 500 €.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 0 },
    // ✅ SOURCÉ LE 25/09/2026 (cct-search.yml, 4 orthographes) : seul résultat,
    // CCT du 18/11/2019 (314/314-2019-014489.pdf) — « en 2019 SEULEMENT »
    // (art. 4 § 1), un octroi ponctuel de 220 € pour l'année 2019 uniquement,
    // pas un régime récurrent. Aucune CCT plus récente. Régime éteint depuis.
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 314 (Coiffure et soins de beauté) — recherche plein texte (cct-search.yml, 25/09/2026) : 1 résultat, CCT du 18/11/2019 (314/314-2019-014489.pdf). Art. 4 § 1 : « Enkel in 2019 worden ecocheques toegekend voor een waarde van 220 € » — octroi ponctuel pour l'année 2019 SEULEMENT, pas un régime récurrent. Aucune CCT postérieure trouvée : régime éteint.",
    ecoChequesDateVerif: '2026-09-25',
    primeFinAnnee_pct: 9.5,                    // 9,5% du salaire annuel brut
    primeSyndicale: { existe: true, montant: 145, prorata: true, montant_trimestriel: 36.25 },
    formation: { jours: 5, fonds: 'Coach (ASBL Fonds CP 314)' },
    cotisationSpeciale: null,

    // ── Categories CP 314 (5 niveaux + ancienneté Cat 5) ──
    // Q1 2026 : indexation au 01/12/2025 (dernière)
    // Source : CGSLB CP 314, ACLVB flash 286 indexation
    categoriesCoiffure: {
      cat_1: { code: '1', label: 'Cat 1 — Emploi-tremplin (sans diplôme, < 6 mois)',           tauxHoraire: 12.40, niveau: 'Débutant' },
      cat_2: { code: '2', label: 'Cat 2 — Diplôme ou 6 mois ancienneté secteur',                tauxHoraire: 13.30, niveau: 'Diplômé' },
      cat_3: { code: '3', label: 'Cat 3 — Autonome (5+ ans ancienneté secteur)',                tauxHoraire: 14.20, niveau: 'Autonome' },
      cat_4: { code: '4', label: 'Cat 4 — Direction opérationnelle',                            tauxHoraire: 15.50, niveau: 'Encadrement' },
      cat_5: { code: '5', label: 'Cat 5 — Direction fonctionnelle',                             tauxHoraire: 16.80, niveau: 'Direction' },
      cat_5_10ans: { code: '5+10', label: 'Cat 5 — 10 ans ancienneté (+10%)',                  tauxHoraire: 18.48, niveau: 'Direction senior' },
      cat_5_20ans: { code: '5+20', label: 'Cat 5 — 20 ans ancienneté (+20%)',                  tauxHoraire: 20.16, niveau: 'Direction expert' },
    },

    // ── Categories esthetique (4 niveaux) ──
    categoriesEsthetique: {
      cat_1: { code: 'E1', label: 'Esthéticien Cat 1 — Tremplin',                               tauxHoraire: 12.40, niveau: 'Débutant' },
      cat_2: { code: 'E2', label: 'Esthéticien Cat 2 — Diplôme/6 mois',                         tauxHoraire: 13.30, niveau: 'Diplômé' },
      cat_3: { code: 'E3', label: 'Esthéticien Cat 3 — 2 ans ancienneté',                       tauxHoraire: 14.10, niveau: 'Confirmé' },
      cat_3_5ans: { code: 'E3+5', label: 'Cat 3 + 5 ans ancienneté (+5%)',                     tauxHoraire: 14.81, niveau: 'Senior' },
      cat_3_10ans: { code: 'E3+10', label: 'Cat 3 + 10 ans ancienneté (+10%)',                 tauxHoraire: 15.51, niveau: 'Expert' },
      cat_3_15ans: { code: 'E3+15', label: 'Cat 3 + 15 ans ancienneté (+15%)',                 tauxHoraire: 16.22, niveau: 'Maître' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      dimancheZoneTouristique: { taux: 0.50, label: 'Dimanche zones touristiques (+50%)', condition: 'Stations balnéaires, climatiques, centres touristiques' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      // Outils de travail
      forfaitOutils_si_non_fournis: 538.34,    // EUR — montant forfaitaire annuel si pas d'outils
      // Transport
      transportPublic_pct: 1.00,               // 100% remboursement
      transportPrive: 'Intervention employeur depuis 01/02/2026 (cf tableau)',
      velo: 0.37,                              // EUR/km dès 01/01/2026 — CCT CP 314 frais de déplacement (22/01/2020) : vélo = max exonéré fiscal/ONSS, ajustement auto (0,35→0,36→0,37) — ACCG PC 314, consulté 2026-07-07
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 314 + ACLVB flash 286 + CSC services-aux-personnes + FGTB Centrale',
      indexationFrequence: 'Variable (déclenchée +2% index)',
      derniereIndexation: '2025-12-01',
      classification: 'Coiffure 5 cat + ancienneté Cat 5 / Esthétique 3 cat + ancienneté Cat 3',
      notes: 'CP regroupe coiffure, fitness, soins de beauté. Évolution Cat 3 coiffure auto après 5 ans ancienneté. Suppression Cat 1b depuis 01/07/2009.',
      nouveautes2026: [
        'Indexation 01/12/2025 confirmée (dernière en date)',
        'Intervention transport privé depuis 01/02/2026',
        'Forfait outils 538,34 € si non fournis',
        'Prime FDA 9,5% du salaire annuel brut',
      ],
    },
  },

  // CP 152 — Enseignement libre subventionné
  '152': {
    // ── Régime hebdomadaire CP 152 — ajouté le 23/08/2026 ───────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 05/05/2003, banque CCT du SPF Emploi
    // (152/152-2003-002034.pdf), art. 2 et 8, toujours `enforced`.
    //
    // ⚠️ TROISIÈME FOIS QUE LE RÉGIME EST DEUX NOMBRES, PAS UN (après les
    // CP 211 et 124). L'art. 2 fixe la durée de l'art. 19 de la loi du
    // 16/03/1971 à 38 h ; le chapitre II la RÉDUIT à 37 h au 01/09/2003. Et
    // l'art. 8 précise que les DEUX sont des moyennes hebdomadaires à
    // respecter sur douze mois consécutifs — pas des maxima par semaine.
    // C'est 37 qui vaut aujourd'hui ; 38 est la référence légale d'avant
    // réduction, gardée parce qu'elle explique le second nombre.
    regimeHebdo: 37,
    regimeHebdoNote: {
      conventionnel: 38,
      effectif: 37,
      depuis: '2003-09-01',
      mecanisme: 'réduction collective du temps de travail ; les deux durées sont des moyennes hebdomadaires à respecter sur une année de douze mois consécutifs',
      source: 'CCT du 05/05/2003, art. 2 et 8 — banque CCT du SPF Emploi, 152/152-2003-002034.pdf, texte primaire lu le 23/08/2026. Modifiée par la CCT du 28/08/2003 (152/152-2003-004086.pdf), qui ne décale que les dates de mise en oeuvre.',
      dateVerif: '2026-08-23',
    },
    nom: 'CP 152 — Enseignement libre subventionné',
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 121 CCT de la CP 152 le 23/08/2026 (jcId 1520000). Aucune cotisation sectorielle au-delà du FSE ; les textes « cotisation » relevés portent sur l'intervention dans les frais de transport.",
      dateVerif: '2026-08-23',
    },
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', source: 'AUDIT 2026-06 — SPF Emploi' },
    // ⚠️ 3 % INVENTÉ SUR UNE FAÎTIÈRE DONT LES DEUX MOITIÉS SONT DÉJÀ SOURCÉES.
    // Corrigé le 25/08/2026. La CP 152 se divise par RÔLE LINGUISTIQUE, et
    // l'ONSS le dit dans ses propres descriptions de catégories :
    //   · catégorie 074 → établissements subventionnés par la Communauté
    //     flamande, inscrits au rôle néerlandophone (sous-CP 152.01) : 1,26 % ;
    //   · catégorie 174 → Communauté française ou germanophone, rôle
    //     francophone (sous-CP 152.02) : 0,60 %.
    // Les deux sous-CP portent déjà ces taux, sourcés sur le fichier des taux
    // ONSS. Le 3 % de la mère n'était donc ni l'un ni l'autre : il valait plus
    // du double du régime flamand et cinq fois le régime francophone.
    // ⛔ ON NE CHOISIT PAS UN DES DEUX POUR LA MÈRE. Un employeur relève de l'un
    // ou de l'autre selon son rôle linguistique ONSS — c'est une donnée du
    // dossier, pas une moyenne à faire. La fiche renvoie donc aux sous-CP, et
    // le calcul rend 0 tant qu'on ne sait pas laquelle.
    fondsSE: {
      nom: "Fonds de la Commission paritaire pour les institutions subsidiées de l'enseignement libre",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '152.01': "rôle néerlandophone (Communauté flamande) — catégorie ONSS 074, 1,26 %",
        '152.02': "rôle francophone ou germanophone — catégorie ONSS 174, 0,60 %",
      },
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS, fichier des taux DmfA 2026/3 et annexe 27 (descriptions des categories 074 et 174) : le taux depend du role linguistique de l'employeur, 1,26 % au role neerlandophone et 0,60 % au role francophone ou germanophone. Lu le 25/08/2026. Le 3 % qui figurait ici n'avait aucune source et ne correspondait ni a l'un ni a l'autre.",
      note: "Un pension sectoriel de 1,14 % est percu separement sous les codes 825 et 835 pour la categorie 074 : ce n'est pas un fonds de securite d'existence.",
    },
    chequesRepas: { obligatoire: false },
    // ✅ SOURCÉ LE 25/09/2026 (cct-search.yml, 4 orthographes) : ZÉRO résultat
    // dans la banque CCT du SPF pour la CP 152 (Enseignement libre subventionné).
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 152 (Enseignement libre subventionné) — recherche plein texte (cct-search.yml, 25/09/2026, eco cheque/ecocheque/ecocheques/éco-chèques) : 0 résultat sur les 4 orthographes. Aucune CCT éco-chèques déposée sous ce numéro.",
    ecoChequesDateVerif: '2026-09-25',
    primeFDA: { type: 'Via fonds sectoriel', prorata: true },
    formation: { jours: 5, fonds: 'Fonds enseignement' },

    // ── Heures supplémentaires CP 152 — ce qu'on sait, et ce qu'on ne sait pas
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter). Le détail de la
    // recherche est écrit ici pour que la session suivante reprenne AILLEURS
    // plutôt que de refaire la même requête.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux propre à la CP 152 n'a été trouvé",
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 1520000',
        quand: '2026-08-23',
        combien: '121 conventions listées, 25 titres retenus au filtre large (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation, irrégulière, toeslag)',
        resultat: "aucune ne fixe de taux ni de supplément d'horaire ; les titres qui contiennent « nuit » sont tous des régimes de chômage avec complément d'entreprise (métier lourd, travail de nuit), pas des sursalaires",
        reste: "recherche par TITRE uniquement, à reprendre par thème ou texte intégral",
      },
      source: "absence de taux sectoriel constatée sur les 121 CCT listées de la CP 152 le 23/08/2026",
      dateVerif: '2026-08-23',
    },

    // ── Indemnités CP 152 — ajoutées le 23/08/2026 ───────────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 04/03/2014, n° 121733/CO/152, banque CCT
    // du SPF Emploi (152/152-2014-002847.pdf), art. 1 à 3 et chapitre III.
    //
    // 🛑 DEUX RESTRICTIONS DE CHAMP, écrites dans la donnée : cette CCT ne
    // vise que les institutions dont le SIÈGE SOCIAL est en Région wallonne
    // ou en Région de Bruxelles-Capitale, et seulement les OUVRIERS. Elle ne
    // dit rien de la Communauté flamande ni des employés.
    indemnites: {
      perimetreSource: "OUVRIERS uniquement, institutions dont le siège social est en Région wallonne ou en Région de Bruxelles-Capitale — ne vaut ni pour la Communauté flamande ni pour les employés",
      source: 'CCT du 04/03/2014, n° 121733/CO/152 — 152/152-2014-002847.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',

      transportsPublicsHorsTrain: {
        prixProportionnelALaDistance: "intervention égale à celle prévue pour la carte-train (abonnement social) pour la distance correspondante, SANS excéder 75 % du prix réel du transport",
        prixFixe: "intervention forfaitaire plafonnée à 71,8 % du prix effectivement payé, sans pouvoir excéder l'intervention pour une carte-train de 7 km",
        source: 'chapitre III',
      },

      // ⚠️ VALEUR HISTORIQUE, PAS COURANTE (R1). 0,22 €/km est le montant
      // écrit dans la CCT de 2014 ; l'indemnité vélo fédérale a bougé depuis.
      // Ne JAMAIS l'annoncer comme le montant du jour : elle sert de plancher
      // conventionnel, à confronter au régime fédéral en vigueur.
      veloHistorique2014: {
        montantKm: 0.22,
        depuis: '2014-03-04',
        condition: "trajet domicile ↔ lieu d'occupation, dans les deux sens, SANS limite minimale de kilomètres",
        avertissement: "montant de 2014 — confronter au régime vélo fédéral en vigueur avant tout calcul ; le plus favorable s'applique",
        source: 'art. 3',
      },
    },
    nonMarchand: true,
  },
  // CP 322.01 — Titres-services
  // CP 226 — Commission Paritaire pour les employés du commerce international,
  // du transport et de la logistique
  // ⚠️ Module 12 — Q1 2026 — Secteur logistique/ports/aéroports/expéditions
  // Sources : CGSLB CP 226 (indexation +2,23% au 01/01/2026), CSC, Prestataire C,
  // salairesminimums.be SCP 2260000
  '226': {
    nom: 'CP 226 — Commerce international, transport et logistique (employés)',
    nomCourt: 'Commerce international (employés)',
    classifications: {
      source: "CCT du 12/01/2026 (CP 226, employes du commerce international, du transport et de la logistique), chapitre IV, banque CCT du SPF, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "Classification SECTORIELLE en classes de fonction, fondee sur des FONCTIONS-MODELE et faisant l'objet d'une ACTUALISATION PERIODIQUE (chapitre IV). Lors d'une actualisation, trois cas sont regles pour les employes deja en service selon que la nouvelle fonction-modele se situe dans une classe inferieure, egale ou superieure a la precedente — avec des consequences ecrites sur la classe, l'anciennete baremique (fictive) et la remuneration. ⚠️ La classe d'un employe peut donc changer sans que sa fonction change.",
    },
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', formule: 'sante_lisse_dec', formuleAConfirmer: true },

    // ⚠️ 2,50 % INVENTÉ → 0,50 %, CE QUE L'ONSS PERÇOIT (25/08/2026).
    // Même figure que la CP 220 : un « ~2,5 % » sans source. La CP 226 est la
    // commission des EMPLOYÉS du commerce international, du transport et de la
    // logistique ; l'ONSS perçoit 0,50 % pour eux (code travailleur 830). Les
    // ouvriers du même employeur relèvent de la CP 140 — les catégories 083 et
    // 084 que l'index rattache à cette CP sont décrites par l'ONSS comme
    // « Employeurs relevant de la Commission paritaire du transport et de la
    // logistique n° 140 ». Leur taux ouvrier (7,70 %) n'a rien à faire sur une
    // fiche d'employé : quinze fois le montant dû.
    fondsSE: {
      nom: 'Fonds social CP 226 — commerce international, transport et logistique (employés)',
      taux_patronal: 0.005,
      taux_patronal_employe: 0.005,
      ouvriersRelevantDUneAutreCp: ['140'],
      onssSourcee: true,
      onssCategorie: '200',
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS, fichier des taux DmfA 2026/3, code travailleur 830, categorie d'employeur 200 : 0,50 % pour les employes. Lu le 25/08/2026. Le 2,50 % qui figurait ici se presentait lui-meme comme « ~2,5 % (formation + securite existence) » et n'avait aucune source.",
      note: "Un pension sectoriel de 1,00 % est percu separement sous le code 835 : ce n'est pas un fonds de securite d'existence.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 0, note: 'Non obligatoires sectoriellement, mais courants en CP 226. Selon CCT entreprise.' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 08/01/2024 (226/226-2024-000098.pdf, chapitre VI « Pouvoir
    // d'achat », section 1 « Continuation augmentation du pouvoir d'achat
    // 2010 »), art. 18-19 : « L'augmentation du pouvoir d'achat octroyée en
    // 2010 reste d'application après l'année 2010 […] Les entreprises qui ne
    // font pas usage des régimes d'entreprise prévus à l'article 20 doivent
    // octroyer l'augmentation du pouvoir d'achat de 250 EUR en éco-chèques »
    // — référence NAR-CCT n°98 art. 6 §1, période de référence = année
    // civile, octroi début décembre, valeur nominale max. 10 EUR/chèque.
    // Régime RÉCURRENT depuis 2010, reconduit à l'identique (art. 18-19) par
    // les CCT successives 2013/2014/2015/2016/2017/2019/2021/2024 — les
    // documents 2016 à 2024 portent tous le même article. C'est un PLANCHER
    // PAR DÉFAUT : une entreprise peut substituer, par accord écrit
    // d'entreprise (art. 20), un autre avantage équivalent plafonné à
    // 250 EUR/travailleur/an.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 08/01/2024 (226/226-2024-000098.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 18-19 : 250 EUR/an en éco-chèques, régime récurrent depuis 2010 (reconduit par les CCT 2013-2024), sauf substitution par accord d'entreprise (art. 20).",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,                   // 1 mois brut (8,33%)
    formation: { jours: 5, fonds: 'LOGOS (Fonds formation CP 226)' },
    cotisationSpeciale: null,

    // ── Avantage réellement SECTORIEL ────────────────────────────────────
    // ⚠️ Le bloc `avantages` de cette fiche a été VIDÉ le 21/08/2026 : il ne
    // contenait que du droit fédéral (20 jours de vacances, double pécule,
    // vacances jeunes/seniors, seuil d'heures supplémentaires à 38 h) recopié
    // dans une fiche de CP — R1 l'interdit, et ça faisait passer la CP 226 pour
    // « complète » sans qu'une seule donnée sectorielle y figure.
    // Remplacé par ce qui varie VRAIMENT d'un secteur à l'autre.
    //
    // ✅ TEXTE PRIMAIRE LU LE 21/08/2026 — CCT du 12/01/2026 fixant le statut de
    // la délégation syndicale, CP 226, enregistrée sous le n° 197611/CO/226
    // (dépôt 21/01/2026, enregistrement 04/02/2026), banque CCT du SPF Emploi,
    // PDF 226/226-2026-000470.pdf, art. 5 et 8. Mot pour mot :
    //   « Une délégation syndicale peut être instituée dans toute entreprise
    //     pour autant qu'elle occupe 25 % d'employés syndiqués avec un minimum
    //     de 10 employés affiliés. »
    //
    // ⚠️ CE N'EST PAS UN SEUIL D'EFFECTIF, contrairement aux CP 200 et 302 qui
    // portent `delegationSyndicale_seuil` (25 et 50 travailleurs). Ici le
    // critère est la SYNDICALISATION : un pourcentage ET un plancher d'affiliés.
    // Écrire « seuil: 25 » aurait produit un chiffre identique à celui de la
    // CP 200 en voulant dire tout autre chose — le genre de faux ami qui se
    // retrouve dans un conseil facturé.
    avantages: {
      delegationSyndicale: {
        critere: 'syndicalisation',
        pctEmployesSyndiques: 0.25,
        minEmployesAffilies: 10,
        // Nombre maximal de délégués, au prorata de l'effectif (art. 8).
        deleguesMax: [
          { jusqua: 74, max: 2 },
          { jusqua: 99, max: 3 },
          { jusqua: 199, max: 4 },
        ],
        delegueSupplementaireParTranche: 100, // au-delà de 199, par tranche entamée
        source: "CCT du 12/01/2026 (CP 226) fixant le statut de la délégation syndicale, n° 197611/CO/226, dépôt 21/01/2026 — banque CCT du SPF Emploi, 226/226-2026-000470.pdf. Texte primaire lu.",
        dateVerif: '2026-08-21',
      },
    },

    // ── Catégories CP 226 — 8 classes barémiques ──
    // Q1 2026 : indexation +2,23% au 01/01/2026 confirmée
    // Source : CGSLB CP 226 baremes 01/01/2026
    // Plafonnement : "rémunération finale de la classe 8"
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Tâches simples (administratif basique)',     salaireMensuel: 2154.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Tâches courantes',                            salaireMensuel: 2245.00, niveau: 'Confirmé' },
      classe_3: { code: 'C3', label: 'Classe 3 — Tâches qualifiées',                           salaireMensuel: 2380.00, niveau: 'Qualifié' },
      classe_4: { code: 'C4', label: 'Classe 4 — Tâches autonomes',                            salaireMensuel: 2540.00, niveau: 'Autonome' },
      classe_5: { code: 'C5', label: 'Classe 5 — Spécialiste / coordinateur',                  salaireMensuel: 2730.00, niveau: 'Spécialiste' },
      classe_6: { code: 'C6', label: 'Classe 6 — Senior / chef d\'équipe',                     salaireMensuel: 2980.00, niveau: 'Senior' },
      classe_7: { code: 'C7', label: 'Classe 7 — Cadre intermédiaire',                         salaireMensuel: 3290.00, niveau: 'Cadre' },
      classe_8: { code: 'C8', label: 'Classe 8 — Cadre supérieur (plafond barémique)',         salaireMensuel: 3680.00, niveau: 'Direction' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%, dès la 39e heure)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 1.00,               // 100% remboursement depuis 01/01/2024 (SNCB, De Lijn, STIB, TEC)
      transportPrive_augmentation_2024: 0.058, // +5,8% intervention transport privé (négocié sectoriel)
      velo_2026: 0.30,                          // EUR/km dès 01/01/2026 (protocole accord sectoriel 2025-2026 du 16/12/2025 ; 0,27 du 01/10/2023 au 31/12/2025) — ACLVB PC 226, consulté 2026-07-07
      velo_max_exonere: 0.37,                   // EUR/km max exonéré ONSS/fiscal 2026 (plafond 3 700 €/an) — Acerta/Securex/Group S, consulté 2026-07-07
      // Vacances jeunes — constante FÉDÉRALE ONEM, dupliquée ici pour compat CP 226.
      // Source unique : LB.remuneration.vacancesJeunes (ONEM, lu le 20/08/2026).
      // L'ancien 2 837,89 € était PÉRIMÉ (~-6,1 %, ± 3 indexations de retard).
      // Un test verrouille l'égalité avec la clé nationale.
      vacancesJeunes_pct: 0.65,
      vacancesJeunes_plafond: 3011.55,
    },


    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 226 (cp-28 indexation 01/01/2026 +2,23%) + CSC + salairesminimums.be SCP 2260000',
      indexationFrequence: 'Annuelle au 01/01 si index dépassé',
      indexationQ1_2026: 0.0223,                // +2,23% confirmé
      classification: '8 classes barémiques (C1 → C8)',
      historique: 'CP 213 (entreprises maritimes) transformée en CP 226 en 1998. Certaines entreprises de CP 218 transférées en CP 226.',
      notes: 'Champ très large : prestation de services dans les ports, aéroports, bureaux d\'expéditions, logistique, transport international. Plafonnement barème : "rémunération finale de la classe 8".',
      nouveautes2026: [
        'Indexation +2,23% au 01/01/2026 confirmée (CGSLB)',
        'Augmentation +5,8% intervention transport privé (accord sectoriel)',
        'Transport public 100% remboursé depuis 01/01/2024 (SNCB/De Lijn/STIB/TEC)',
        'HS dès la 39e heure (vs 41e dans certaines CP)',
        'Indemnité vélo 0,27 €/km, exoneration jusqu\'à 0,36 €/km',
      ],
    },
  },

  // CP 220 — Commission Paritaire pour les employés de l'industrie alimentaire
  // ⚠️ Module 13 — Q1 2026 — Industrie alimentaire (conserves légumes + autre)
  // Sources : CGSLB CP 220 (indexation +2,19% au 01/01/2026), CSC CNE,
  // SETCa Federal, Limosa fiche CP 220, Alimento (Fonds CP 118+220)
  '220': {
    nom: 'CP 220 — Industrie alimentaire (employés)',
    nomCourt: 'Industrie alimentaire (employés)',
    regimeHebdo: 38,                              // 37 ou 38h selon sous-secteur
    regimeMaxQuotidien: 9,                        // 9h/jour max
    // Même référence NOVEMBRE que la CP 118 (CGSLB traite les deux ensemble) —
    // 133,07 / 130,22 = 2,188604 % → arrondi 2,19 %.
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0219, derniereDate: '2026-01-01', formule: 'sante_lisse_nov' },

    sousSecteurs: {
      '220.01': 'Industrie des conserves de légumes',
      '220.02': 'Industrie alimentaire autre que conserves de légumes',
    },

    // ⚠️ 2,50 % INVENTÉ → 0,40 %, CE QUE L'ONSS PERÇOIT (25/08/2026).
    // Le taux était un nombre rond annoncé « ~2,5 % » dans sa propre note — un
    // « environ » n'est pas une source. La CP 220 est la commission des
    // EMPLOYÉS de l'industrie alimentaire ; l'ONSS perçoit 0,40 % pour eux
    // (code travailleur 830), catégorie d'employeur 048. Les ouvriers du même
    // employeur relèvent de la CP 118, et c'est la description de la catégorie
    // ONSS qui le dit : « Fonds des Commissions paritaires de l'industrie
    // alimentaire n° 118 et/ou n° 220 ». Servir ici le taux ouvrier (1,47 %) à
    // un employé le multiplierait par près de quatre.
    fondsSE: {
      nom: 'Alimento — Fonds social des CP 118 et 220 (employés)',
      taux_patronal: 0.004,
      taux_patronal_employe: 0.004,
      ouvriersRelevantDUneAutreCp: ['118'],
      onssSourcee: true,
      onssCategorie: '048',
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS, fichier des taux DmfA 2026/3 (dmfa_rate_2026_3.zip, NOSSContributionRate_2026_3.xml), code travailleur 830, categorie d'employeur 048 : 0,40 % pour les employes. Lu le 25/08/2026. Le 2,50 % qui figurait ici se presentait lui-meme comme « ~2,5 % » et n'avait aucune source. Alimento regroupe les services sectoriels des CP 118 et 220 ; Synersec porte les avantages financiers et la pension complementaire.",
      note: "Un pension sectoriel de 1,36 % est percu separement sous le code 835 : ce n'est pas un fonds de securite d'existence et il vit dans son propre calcul.",
    },
    chequesRepas: { obligatoire: true, valeur: 10.00, part_employeur: 8.91, part_travailleur: 1.09, source: "CCT du 16/12/2025 (CP 220, employés de l'industrie alimentaire), « Augmentation du pouvoir d'achat 2025-2026 », dépôt 197216, banque CCT du SPF, texte primaire lu le 24/08/2026, art. 3 §3 : « Après cette (ces) augmentation(s), l'intervention de l'employeur s'élève à un maximum de 8,91 euros par chèque-repas, et l'intervention de l'employé s'élève à un minimum de 1,09 euro, de sorte que la valeur nominale maximale est de 10 euros. » En vigueur au 01/01/2026. Portée : toute la CP (aucune restriction déclarée au SPF). Cohérent avec la CP 118, son homologue ouvrier dans la même industrie, déjà codée 10,00/8,91/1,09.", regimeSecondaire: "DEUX REGIMES (art. 3 et 4). Entreprises qui octroyaient deja des cheques-repas au 01/10/2025 : intervention patronale +1 EUR au 01/01/2026, plafonnee a 8,91 EUR (valeur nominale 10 EUR) = la valeur codee. Entreprises qui n'en octroyaient PAS au 01/10/2025 : avantage equivalent, a fixer par CCT d'entreprise avant le 15/02/2026 (avec delegation syndicale) ou a introduire avant le 01/07/2026 ; a defaut, avantage net recurrent de 240 EUR/an au prorata des jours de travail effectif.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '220/220-2025-015106.pdf' },
    // 🔴 CORRIGÉ LE 21/09/2026 — LE « 0 RÉSULTAT » DU 18/09 VENAIT DE L'ORTHOGRAPHE
    // CHERCHÉE, PAS DE L'ABSENCE (R2 ter). « eco cheque » ne rend rien sur cette CP ;
    // « ecocheque », « ecocheques » et « éco-chèques » rendent 3 CCT, dont celle qui
    // FIXE le montant. L'app disait donc « aucun éco-chèque » à toute l'industrie
    // alimentaire (employés) alors que 250 €/an sont dus depuis le 01/01/2011.
    // Texte primaire lu le 21/09/2026, art. 2 § 1 : « A partir du 1er janvier 2011,
    // des éco-chèques seront octroyés pour un montant total de 250 euro par an. »
    // Les 5 CCT postérieures citant le pouvoir d'achat (197216 du 16/12/2025, 183478,
    // 153169, 131224, 128969) ont été lues : aucune ne parle d'éco-chèques.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 12/01/2011 (CP 220, employés de l'industrie alimentaire), dépôt 102940/CO/220, « éco-chèques », banque CCT du SPF Emploi, texte primaire lu le 21/09/2026. Art. 2 § 1 : 250 € par an depuis le 01/01/2011, octroyés en une fois avec la première paie qui suit le 31 décembre. Art. 4 : valeur nominale maximale de 10 € par chèque. Art. 5-6 : montant pour une prestation à temps plein sur toute la période de référence, prorata temporis à temps partiel. Art. 8 : 20,82 € par mois de service effectivement presté. Sans date de fin, en vigueur ; les 5 CCT « pouvoir d'achat » postérieures lues le 21/09/2026 ne la modifient pas.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,                     // 1 mois brut
    formation: { jours: 5, fonds: 'Alimento — chèques formation 2 500 € pour <26 ans' },
    cotisationSpeciale: null,

    // ── Catégories CP 220 — 4 classes barémiques ──
    // Q1 2026 : indexation +2,19% au 01/01/2026 confirmée
    // Source : CGSLB CP 220 + Limosafiche PC 220 (14/11/2025)
    // Barèmes adaptés à l'index 2026 (+2,19%)
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Tâches simples',                              salaireMensuel: 2154.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Tâches courantes',                            salaireMensuel: 2280.00, niveau: 'Confirmé' },
      classe_3: { code: 'C3', label: 'Classe 3 — Tâches qualifiées',                           salaireMensuel: 2450.00, niveau: 'Qualifié' },
      classe_4: { code: 'C4', label: 'Classe 4 — Tâches spécialisées / autonomes',             salaireMensuel: 2680.00, niveau: 'Autonome' },
      classe_5: { code: 'C5', label: 'Classe 5 — Senior / encadrement',                        salaireMensuel: 2980.00, niveau: 'Senior' },
      classe_6: { code: 'C6', label: 'Classe 6 — Cadre / direction opérationnelle',            salaireMensuel: 3380.00, niveau: 'Cadre' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
      seuilQuotidien: 9,
      // Min 25% supplément pour heures sup selon CCT du 01/01/1997 art. 24
      supplementMinimum: 0.25,
    },

    indemnites: {
      // Vélo : 0,27 → 0,31 €/km (accord sectoriel 2025-2026, miroir CP 118). Date de bascule à
      // confirmer sur la CCT déposée : 01/02/2026 probable (Acerta NL verbatim + CSC + historique
      // des hausses au 01/02), mais Acerta FR dit 01/07/2026 — consulté 2026-07-07.
      velo_2026: 0.31,                            // EUR/km après bascule (0,27 avant)
      velo_max_exonere: 0.37,                     // EUR/km max exonéré ONSS/fiscal 2026 (plafond 3 700 €/an) — Securex 15/01/2026
      // Indemnité garde enfants étendue (accord 2025-2026)
      gardeEnfants_jour: 5.00,                    // EUR/jour
      gardeEnfants_max_annuel: 1000.00,           // EUR/an
      gardeEnfants_age_max: 12,                   // jusqu'à 12 ans
      // Crédit-temps 1/5 à 55+ ans
      creditTemps_complement: 150.00,             // EUR/mois pour crédit-temps 1/5 dès 55 ans
      // Augmentation transport privé (accord 2025-2026)
      transportPrive_augmentation_2026: 'Augmenté (montants en cours de communication)',
    },

    // Jours de fin de carrière selon âge (accord 2025-2026)
    joursFinCarriere: {
      55: 1,      // 1 jour à partir de 55 ans
      57: 2,      // 2 jours à 57 ans
      59: 6,      // 6 jours à 59 ans
      62: 8,      // 8 jours à 62 ans
    },

    // Congés supplémentaires liés au congé parental N-1
    congesParentalN1: {
      seuilMin: 3,                                // 3 mois congé parental dans N-1 = +1 jour année N
      jours: 1,
      regimes: ['temps_plein', 'temps_partiel', '1/5'],   // sauf 1/10
      prorata: false,                             // sans prorata
    },

    // Congé d'ancienneté
    joursAnciennete: {
      20: 1,                                      // 1 jour à 20 ans d'ancienneté
    },

    avantages: {
      formationCheques: { montant: 2500, age_max: 26, premiers_mois: 12 },
      delegationSyndicale_seuil: 25,
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 220 cp-220-industrie-4 (29/11/2025) + CSC CNE indexation 2026 + SETCa Fédéral + Limosa CP 220 (14/11/2025) + Alimento.be',
      indexationFrequence: 'Annuelle au 01/01',
      indexationQ1_2026: 0.0219,                  // +2,19% confirmé
      classification: '6 classes barémiques (C1 → C6) sur 2 sous-secteurs',
      sousSecteurs: '220.01 (conserves de légumes) + 220.02 (autre)',
      historique: 'Secteur industrie alimentaire = 19% emploi industriel BE (2016), 4 359 employeurs.',
      notes: 'Industrie alimentaire = un des plus gros employeurs industriels BE. Tourné export (9,7% exports BE). Régime 37/38h selon sous-secteur, 9h/jour max. ATTENTION saut d\'index 2026 partiellement appliqué selon décision gouvernementale.',
      nouveautes2026: [
        'Indexation +2,19% au 01/01/2026 confirmée (CGSLB)',
        'Accord sectoriel 2025-2026 signé : indemnité vélo min 0,31 €/km',
        'Prime 240 € nets juillet 2026 si pas de chèques-repas + pas de délégation syndicale',
        'Indemnité garde enfants 5 €/jour jusqu\'à 12 ans (max 1 000 €/an)',
        'Crédit-temps 1/5 à 55+ ans : complément 150 €/mois',
        'Jours fin carrière : 1j(55) / 2j(57) / 6j(59) / 8j(62)',
        '+1 jour congé année N si ≥3 mois congé parental dans N-1',
      ],
    },
  },

  // CP 209 — Métal employés (pendant employé du CP 111)
  // ⚠️ Module 14 — Q1 2026 — Constantes minimales (UI à venir)
  '209': {
    // ── Indemnités SECTORIELLES — déplacements domicile-travail ──────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 13/11/2023 (CP 209),
    // intervention de l'employeur dans les frais de transport, banque CCT du
    // SPF Emploi, 209/209-2023-015461.pdf.
    //
    // ⚠️ TROIS RÉGIMES DISTINCTS, ET DEUX PLANCHERS QUI SE LISENT MAL. Le
    // secteur ne se contente pas d'un tarif kilométrique : il pose aussi un
    // MINIMUM PAR JOUR DE TRAVAIL, différent selon la distance. Un travailleur
    // qui fait 8 km à pied ou en covoiturage touche le plancher de 1,69 €, pas
    // 8 × 0,084 €.
    indemnites: {
      velo: {
        parKm: 0.27,
        maxParJour: 10.80,
        note: "Indemnité SECTORIELLE. Elle est inférieure au plafond fiscal fédéral d'exonération — ne pas confondre les deux.",
      },
      autreMoyen: {
        parKm: 0.084,
        maxParJour: 9.20,
        note: "Tout moyen autre que le vélo ou les transports en commun.",
      },
      planchersParJour: [
        { jusquaKm: 20, minimum: 1.69 },
        { apartirDeKm: 21, minimum: 2.03 },
      ],
      transportsPublics: "Intervention selon le tableau annuel Railflex / abonnement SNCB annexé à la CCT.",
      source: "CCT du 13/11/2023 (CP 209) — intervention de l'employeur dans les frais de transport, banque CCT du SPF Emploi, 209/209-2023-015461.pdf. Texte primaire lu par colonnes.",
      dateVerif: '2026-08-22',
    },
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 12/01/2026 (CP 209), banque CCT
    // du SPF Emploi, 209/209-2026-000151.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 12/01/2026 (CP 209) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 209/209-2026-000151.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 209 — Métal (employés)',
    nomCourt: 'Métal employés',
    source: 'SPF Emploi — salairesminimums.be CP 2090000 + fiche Limosa CP209. RMMG sectoriel 2576,23 € (CCT 07/07/2025 n°194465), en vigueur 01/07/2025.',
    dateVerif: '2026-07-09',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [7], dernierTaux: 0.0281, derniereDate: '2026-07-01', note: 'Indexation +2,81% au 01/07/2026 CONFIRMEE source PRIMAIRE SPF ETCS salairesminimums.be (CP 2090000, mention "Indexation de 2,81% en 7/2026"), verifie 2026-07-09. Centenindex: plafond 2% sur tranche >4000EUR/mois (loi-programme votee 28/05/2026, en vigueur 01/06/2026, 1re application CP 111/209 au 01/07/2026 — texte definitif Moniteur a confirmer).' },
    // ── FONDS DE SÉCURITÉ D'EXISTENCE — statuts lus au texte primaire ────
    // ✅ CCT du 17/11/2025, dépôt 196617/CO/209, « modification et coordination
    // des statuts du Fonds de sécurité d'existence », art. 14 à 16 — banque CCT
    // du SPF Emploi, 209/209-2025-014295.pdf, texte primaire lu le 26/08/2026.
    //
    // ⚠️ LE 3 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Le vrai taux est de
    // 0,45 %, soit près de SEPT FOIS moins. Sur un brut de 3 000 €, l'écran
    // annonçait 90 € par mois de fonds là où l'employeur en doit 13,50.
    //
    // 📐 LA BASE N'EST PAS LE BRUT NU. L'art. 15, § 1er la définit lui-même :
    // « les salaires bruts déclarés à l'Office National de Sécurité Sociale, le
    // simple et double pécule de vacances exclus », concrètement la somme des
    // codes de rémunération DmfA 1, 2, 3 et 4, le code 1 étant ramené par le
    // facteur 100/108 pour en retirer le pécule simple. Elle est donc plus
    // ÉTROITE que le brut — l'appliquer au brut mensuel surcompte légèrement,
    // et c'est le sens prudent.
    //
    // 🔎 ET LE PDF EST UN SCAN OCR. `texteCCT()` a rendu DEUX FOIS une chaîne
    // vide sur ce document (1,9 Mo, 30 pages) — ce qui se lit comme « pas de
    // texte ». Le fichier téléchargé au curl et passé à `unpdf` rend 68 709
    // caractères. Des octets reçus plus zéro trouvaille ne concluent jamais en
    // « absent » (R2 ter) : ici c'est l'extracteur qui a lâché, pas la source.
    fondsSE: {
      nom: "FSEM — Fonds social pour les employés du métal (Fonds de sécurité d'existence)",
      taux_patronal: 0.0045,
      base: "salaires bruts déclarés à l'ONSS, pécule de vacances simple ET double exclus (codes de rémunération DmfA 1, 2, 3 et 4)",
      cctSourcee: {
        taux: 0.0045,
        composantes: {
          formationEtEmploiDesEmployes: 0.0005,   // art. 15, § 3, depuis le 01/01/2020
          groupesARisque: 0.0010,                 // art. 15, § 4 — voir `expireLe` ci-dessous
          garantiesOrganisationsEtFormation: 0.0015, // art. 15, § 5, depuis le 01/01/2025 (0,10 % en 2020, 0,13 % en 2024)
          changementsDeCarriere: 0.0015,          // art. 15, § 6, depuis le 01/01/2020
        },
        base: "codes de rémunération DmfA 1 + 2 + 3 + 4, facteur 100/108 appliqué au code 1 (art. 15, § 1er)",
        source: "CCT du 17/11/2025, statuts coordonnés du FSEM, art. 15, §§ 1, 3, 4, 5 et 6 — dépôt 196617/CO/209, banque CCT du SPF Emploi, 209/209-2025-014295.pdf, texte primaire lu le 26/08/2026. Les 0,05 % de formation sont recoupés par la CCT du 17/11/2025 « Plans de formation et engagement de formation sectoriel » (dépôt 196616/CO/209) : « la cotisation de formation perçue par le FSEM d'un montant de 0,05 % de la masse salariale brute des employés ». Les 0,10 % de groupes à risque sont recoupés par la CCT du 17/11/2025 « Groupes à risque et transitions - prolongation » (dépôt 196615/CO/209), art. 2, § 1er : « est fixée à 0,10 % pour la durée du présent accord ».",
        dateVerif: '2026-08-26',
      },
      // ⏰ ÉCHÉANCE DURE. La composante « groupes à risque » ne vaut que jusqu'au
      // 31/12/2026 : la CCT de prolongation (196615/CO/209) écrit « entre en
      // vigueur au 1er janvier 2026 et cesse d'être en vigueur au 31 décembre
      // 2026 ». Sans prolongation, le taux retombe à 0,35 % au 01/01/2027.
      expireLe: '2026-12-31',
      expireQuoi: 'la composante « groupes à risque » de 0,10 % — à revérifier au 1er janvier 2027',
      // 💰 CE QUI N'EST PAS DANS LE TAUX, ET QUI EST POURTANT DÛ.
      forfaitsProvinciaux: {
        note: "Ces forfaits ne sont dus que dans UNE province — les mettre dans le taux les ferait payer à tout le monde. NON facturés par l'app à ce jour.",
        anvers: { parTrimestreParEmploye: 15.78, depuis: '2019-04-01', source: 'art. 15, § 7 — montant fixé chaque année en août par la Section paritaire régionale d\'Anvers' },
        limbourg: { parAnParEmploye: 58.58, parTrimestreParEmploye: 14.65, depuis: '2020-01-01', source: 'art. 15, § 8 — indexé chaque année au 3e trimestre sur la moyenne 4 mois de l\'indice santé de juin' },
      },
      // 🏦 SECOND PILIER — documenté, PAS ajouté au taux du fonds.
      // L'ONSS ne rattache aucune catégorie d'employeur à la CP 209 dans le
      // fichier des taux DmfA 2026/3 : le moteur ne peut donc pas y lire les
      // codes 825/835, et ce bloc reste de la documentation. Il ne se facture
      // pas tant que l'assiette « salaire de référence » n'est pas définie.
      pensionSectorielle: {
        regime: 'Pension complémentaire sectorielle des employés du métal',
        solidaritePct: 0.0010,           // art. 15, § 9, depuis le 01/01/2019
        primePensionPct: 0.0229,         // art. 15, § 10, depuis le 01/01/2020 (1,97 % de 2019 à 2019)
        majorationCotisationSocialePct: 0.0886,
        coutTotalPct: 0.025929,          // 2,29 % × 1,0886 + 0,10 %  → 2,4929 % + 0,10 %
        assiette: "le salaire de référence tel que défini pour l'engagement de pension sectoriel — PAS la même assiette que le fonds, et elle n'a pas été lue",
        dispense: "non due pour les employés affiliés à un engagement de pension hors champ d'application ou en opting out, reconnu comme tel par l'organisateur",
        source: 'CCT du 17/11/2025, statuts coordonnés du FSEM, art. 15, §§ 2, 9 et 10 — dépôt 196617/CO/209, texte primaire lu le 26/08/2026.',
        dateVerif: '2026-08-26',
      },
      note: "Le 3 % qui figurait ici avant le 26/08/2026 était présenté comme le « pendant employé du CP 111 » et n'avait aucune source. Le CP 111 a d'ailleurs son propre fonds (FSEFM), distinct de celui-ci.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 209 + « chèque
    // repas » dans la banque CCT du SPF : le seul document qui en parle
    // (accord national 2009-2010, dépôt 209-2009-005805) est un « MENU
    // CAFÉTÉRIA » — chaque entreprise choisit, par CCT d'entreprise avant le
    // 15/09/2009, ENTRE une augmentation de 1 €/jour des chèques-repas
    // existants, une assurance hospitalisation, ou un plan de pension
    // complémentaire ; à défaut de choix, ce sont des ÉCO-CHÈQUES qui sont
    // octroyés par défaut. Aucune obligation SECTORIELLE fixe de
    // chèques-repas n'en ressort — confirme et complète le 🔴 du 05/09/2026
    // (qui n'avait pas trouvé le mécanisme exact).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 209 — accord national de 2009 (dépôt 209-2009-005805) : menu cafétéria au choix de l'entreprise (chèques-repas OU assurance hospitalisation OU pension complémentaire, à défaut éco-chèques), aucune obligation sectorielle fixe",
      dateVerif: '2026-09-18',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT « écochèques » du 20/12/2021 (209/209-2021-015801.pdf, W:
    // 171255/CO/209), entrée en vigueur le 01/10/2021, durée indéterminée,
    // remplace l'art. 7 de la CCT du 05/05/2014. Art. 3 : montant MAXIMUM de
    // 250 EUR/an (proratisé temps partiel et période incomplète, période de
    // référence 01/10-30/09), payé en octobre. §1 exclut les entreprises
    // ayant choisi une « affectation alternative » via une liste de CCT
    // antérieures (2009 à 2021) — cohérent avec le menu cafétéria du
    // chequesRepas ci-dessus, où « à défaut » d'un autre choix, ce sont les
    // éco-chèques qui s'appliquent.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT « écochèques » du 20/12/2021 (209/209-2021-015801.pdf, W: 171255/CO/209), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 3 : maximum 250 EUR/an, proratisé temps partiel/période incomplète, payé en octobre, en vigueur depuis le 01/10/2021 — sauf entreprises ayant opté pour une affectation alternative.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,                      // 1 mois brut
    formation: { jours: 5, fonds: 'Educam' },
    cotisationSpeciale: null,

    // 4 classes barémiques simplifiées. PLANCHER SECTORIEL = RMMG CP 209 (CCT
    // 07/07/2025 n°194465) = 2576,23 €/mois (en vigueur 01/07/2025, applicable
    // jusqu'au 30/06/2026 — la CP 209 indexe au 1er juillet). Source PRIMAIRE :
    // SPF Emploi salairesminimums.be CP 2090000 + fiche Limosa CP209. Aucune
    // classe ne peut être sous 2576,23 €. Vérifié 2026-06-19 (classe_A/B relevées
    // au plancher sectoriel ; étaient 2280/2470 € = SOUS le minimum = illégal).
    categoriesEmployes: {
      classe_A: { code: 'A', label: 'Classe A — Fonctions exécutives',           salaireMensuel: 2576.23, niveau: 'Exécutive' },
      classe_B: { code: 'B', label: 'Classe B — Fonctions de support',           salaireMensuel: 2576.23, niveau: 'Support' },
      classe_C: { code: 'C', label: 'Classe C — Fonctions de gestion',           salaireMensuel: 2730.00, niveau: 'Gestion' },
      classe_D: { code: 'D', label: 'Classe D — Spécialiste / cadre',            salaireMensuel: 3120.00, niveau: 'Cadre' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'Agoria + lien CP 111 (ouvriers métal)',
      classification: '4 classes (A/B/C/D)',
      notes: 'Pendant employé du CP 111 (ouvriers métal). Constantes initiales — UI à venir si demandée par client.',
      nouveautes2026: ['Indexation annuelle 01/01/2026 (à confirmer Agoria)'],
    },
  },

  // CP 311 — Grandes entreprises de vente au détail
  // ⚠️ Module 15 — Q1 2026 — Grande distribution (Carrefour, Delhaize, Colruyt...)
  // Sources : CGSLB CP 311, ACV Puls, salairesminimums.be SCP 3110000
  '311': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 06/12/2023 (CP 311), statut
    // de la délégation syndicale, banque CCT du SPF Emploi,
    // 311/311-2023-017207.pdf, art. 6. Mot pour mot : « Les délégations
    // syndicales sont constituées par désignation des délégués dans les
    // entreprises comptant au moins 25 % de personnel syndiqué. »
    //
    // ⚠️ CRITÈRE DE SYNDICALISATION, PAS D'EFFECTIF — comme en CP 226 et 311,
    // et contrairement aux CP 202, 207, 314 et 322.01. Aucun nombre de
    // travailleurs n'ouvre le droit ici.
    avantages: {
      delegationSyndicale: {
        critere: 'syndicalisation',
        pctPersonnelSyndique: 0.25,
        seuilEffectif: null,             // aucun : le texte n'en pose pas
        source: 'CCT du 06/12/2023 (CP 311) — statut de la délégation syndicale, banque CCT du SPF Emploi, 311/311-2023-017207.pdf, art. 6. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 311 — Grandes entreprises de vente au détail (grande distribution)',
    nomCourt: 'Grande distribution',
    regimeHebdo: 38,
    // ⚠️ Mois d'indexation ÉLARGIS le 06/08/2026 après incident. Ce champ portait
    // [1, 12] : la sentinelle n'attendait donc AUCUNE indexation en juillet pour
    // la CP 311, et celle du 01/07/2026 (+2 %, fiche SPF 3110000) est passée
    // inaperçue plus d'un mois — six catégories servies 2 % trop bas.
    // 7 est ajouté sur la foi de la fiche elle-même, qui l'annonce noir sur blanc.
    // 🟠 Le calendrier PUBLIÉ du secteur n'a pas été retrouvé : on garde donc le
    // sur-ensemble [1, 7, 12] plutôt que de retirer un mois sans source. Une
    // fausse attente coûte une vérification ; une attente manquante coûte un
    // salaire faux.
    indexation: { frequence: 'annuelle', mois: [1, 7, 12], dernierTaux: 0.02, derniereDate: '2026-07-01' },
    fondsSE: {
      nom: 'Fonds social CP 311 — Grandes entreprises vente détail',
      taux_patronal: 0.0024,
      taux_patronal_employe: 0.0024,
      base108Ouvrier: true,
      onssCategorie: '357',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 357 : 820 ouvriers 0,24 % · employés 0,24 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 2,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise. Quasi-systématiques en grande distribution.' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (311/311-2023-017205.pdf, CCT du 06/12/2023, dépôt 185122/CO/311,
    // « toujours d'application »). Art. 5 : « Aan elke voltijds
    // tewerkgestelde werknemer met een volledige referteperiode worden
    // ecocheques één keer per jaar toegekend ter waarde van 250 EUR » (à
    // chaque travailleur temps plein avec période de référence complète,
    // 250 EUR/an). Art. 6 : temps partiel proratisé par palier —
    // ≥27h/sem 250 € · ≥20h et <27h 200 € · ≥17,5h et <20h 150 € ·
    // <17,5h/sem 100 € · contrats de 8h/sem et contrats d'un jour 75 €.
    // Valeur nominale max. 10 €/chèque (art. 4). Remplace la CCT du
    // 23/11/2021 (311/311-2021-014162.pdf), périmée le 05/12/2023.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 06/12/2023 (311/311-2023-017205.pdf), banque CCT du SPF Emploi, dépôt 185122/CO/311, texte primaire lu le 18/09/2026, art. 5-6 : 250 EUR/an temps plein, proratisé par palier de temps partiel (200/150/100/75 EUR).",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora (CP 311)' },
    cotisationSpeciale: null,

    // ⚠️ CP 311 : RMMMG sectoriel = RMMMG + 4% (~2 148 €) selon barèmes
    rmmmgSectoriel_supplement: 0.04,             // +4% sur RMMMG national

    // 4 classes barémiques
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Tâches simples (caissier débutant)',         salaireMensuel: 2148.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Vendeur / employé qualifié',                  salaireMensuel: 2280.00, niveau: 'Confirmé' },
      classe_3: { code: 'C3', label: 'Classe 3 — Chef de rayon / responsable',                 salaireMensuel: 2520.00, niveau: 'Encadrement' },
      classe_4: { code: 'C4', label: 'Classe 4 — Direction de magasin',                        salaireMensuel: 3050.00, niveau: 'Direction' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 1.00,
      // Vélo : min CCT n°164 supplétive 2026 = 0,30 €/km ; max exonéré fiscal/ONSS 2026 = 0,37 €/km
      // (plafond 3 700 €/an). Sources : SPF Finances + Securex/Acerta 2026, consulté 2026-07-07.
      velo_2026: 0.30,
      veloMaxExonere: 0.37,
    },

    _meta: {
      derniereMAJ: '2026-07-07',
      sourceMAJ: 'CGSLB CP 311 + ACV Puls + salairesminimums.be SCP 3110000 + vélo CCT n°164 (SPF Finances/Securex, consulté 2026-07-07)',
      classification: '4 classes (C1 → C4)',
      notes: 'Couvre les grandes enseignes : Carrefour, Delhaize, Colruyt, Aldi, Lidl, Albert Heijn, etc. Distinction avec CP 202 (commerce détail alimentaire) et CP 312 (grands magasins).',
      nouveautes2026: [
        'Indexation au 01/12/2025 confirmée',
        'RMMMG sectoriel = RMMMG + 4%',
        'Plafond barémique = Classe 4',
      ],
    },
  },

  // CP 202 — Commerce de détail alimentaire (boutiques alimentaires < seuil grande distribution)
  // ⚠️ Module 16 — Q1 2026 — Commerce alimentaire (épicerie, boulangerie de détail)
  // Sources : CGSLB CP 202, CSC CNE indexation 2026 (+1%)
  '202': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 06/12/2023 (CP 202), statut
    // de la délégation syndicale, banque CCT du SPF Emploi,
    // 202/202-2023-016924.pdf, art. 11 et 14.
    //
    // ⚠️ PAS DE SEUIL ÉCRIT EN TOUTES LETTRES : l'art. 14 fixe le nombre de
    // délégués « au prorata du nombre d'employés » et sa table COMMENCE à 10.
    // C'est de là que vient le plancher, pas d'une phrase qui le proclame.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif',
        seuilEmployes: 10,               // première tranche de la table de l'art. 14
        deleguesEffectifs: [
          { de: 10, a: 40, nb: 2 }, { de: 41, a: 60, nb: 3 }, { de: 61, a: 100, nb: 4 },
          { de: 101, a: 200, nb: 5 }, { de: 201, a: 300, nb: 6 }, { de: 301, a: 400, nb: 7 },
          { de: 401, a: 500, nb: 8 }, { de: 501, a: 600, nb: 9 }, { de: 601, a: 800, nb: 10 },
          { de: 801, a: 1000, nb: 11 }, { de: 1001, a: 1400, nb: 12 }, { de: 1401, a: 1800, nb: 13 },
          { de: 1801, a: 2200, nb: 14 }, { de: 2201, a: 2600, nb: 15 }, { de: 2601, a: null, nb: 16 },
        ],
        placeReserveeJeunes: 'Dans les entreprises occupant au moins 25 employés de moins de 21 ans, une place de délégué peut être réservée à un candidat n\'ayant pas atteint l\'âge requis.',
        delegationsDistinctes: ['personnel d\'administration et d\'entrepôt', 'vente en succursale < 750 m²', 'vente en succursale ≥ 750 m²'],
        source: 'CCT du 06/12/2023 (CP 202) — statut de la délégation syndicale, banque CCT du SPF Emploi, 202/202-2023-016924.pdf, art. 11 et 14. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 202 — Commerce de détail alimentaire',
    nomCourt: 'Commerce détail alimentaire',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.01, derniereDate: '2026-01-01' },
    fondsSE: {
      nom: 'Fonds social CP 202',
      taux_patronal: 0.0059,
      taux_patronal_employe: 0.0025,
      base108Ouvrier: true,
      onssCategorie: '257',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 257 : 820 ouvriers 0,59 % · employés 0,25 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 1,50 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise.' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 06/12/2023 « Ecochèques » (202/202-2023-…pdf), toujours en
    // vigueur. Art. 5 : « une fois par an, des éco-chèques à chaque
    // travailleur occupé à temps plein avec une période de référence
    // complète d'une valeur de 250 EUR. » Art. 6 — temps partiel : 250 EUR
    // dès 27h/sem, 200 EUR dès 20h, 150 EUR dès 17,5h, 100 EUR en-deçà, 75 EUR
    // pour les contrats de 5h/sem et contrats à la journée. Paiement en juin
    // (art. 7).
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 06/12/2023 « Ecochèques » (202/202-2023-…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 5 : 250 EUR/an temps plein, paliers 250/200/150/100/75 EUR temps partiel, paiement en juin.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora' },
    cotisationSpeciale: null,

    sousSecteurs: {
      '202.01': 'Magasins de détail moyens (entre seuils)',
      '202.02': 'Petit commerce alimentaire',
    },

    // 4 classes barémiques
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Vendeur débutant',                            salaireMensuel: 2120.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Vendeur expérimenté',                         salaireMensuel: 2245.00, niveau: 'Confirmé' },
      classe_3: { code: 'C3', label: 'Classe 3 — Responsable rayon / gérant',                  salaireMensuel: 2480.00, niveau: 'Encadrement' },
      classe_4: { code: 'C4', label: 'Classe 4 — Cadre / direction',                           salaireMensuel: 2820.00, niveau: 'Direction' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 1.00,
      velo_2026: 0.27,                          // EUR/km — CCT sectorielle propre CP 202 (accord 2023-2024, dès 01/01/2024), inchangé 2026 ; CCT n°164 non applicable — Synova/ACLVB/SETCa, consulté 2026-07-07
      veloMaxExonere: 0.37,                     // EUR/km max exonéré fiscal/ONSS 2026
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 202 + CSC CNE indexation 01/01/2026 (+1%)',
      classification: '4 classes (C1 → C4)',
      notes: 'Commerce de détail alimentaire (épicerie, boulangerie, charcuterie de détail) — distinct de CP 311 (grande distribution) et CP 119 (commerce alimentaire en gros).',
      nouveautes2026: [
        'Indexation +1% au 01/01/2026 (CSC CNE)',
        'Pendant CP 119 (commerce alimentaire ouvriers)',
      ],
    },
  },

  // CP 149.01 — Sous-Commission paritaire des Électriciens (installation et distribution)
  // ⚠️ Module 17 — Q1 2026 — Électriciens ouvriers
  // Sources : CGSLB CP 149.01 (indexation +2,23% au 01/01/2026), CSC, Limosa SCP 149.01,
  //           Brupartners synthèse CP 149.01, Techlink, FEE
  '149.01': {
    nom: 'CP 149.01 — Électriciens : installation et distribution (ouvriers)',
    nomCourt: 'Électriciens',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', formule: 'sante_lisse_dec', formuleAConfirmer: true },
    fondsSE: {
      // ⚠️ CORRIGÉ le 21/08/2026 — ✅ TEXTE PRIMAIRE LU : CCT du 16/10/2023,
      // statuts coordonnés du Fonds, en vigueur 01/01/2024 (nelectra.be).
      // Deux fiches se contredisaient (3,0 % ici, 5,0 % sous '14901') et AUCUNE
      // n'avait raison : l'art. 29.2 fixe QUATRE cotisations, d'assiettes
      // différentes. Perception par l'ONSS (art. 29.3).
      // ⚠️ La glose « FEE = Fédération de l'Électricité et de l'Électronique »
      // était FAUSSE. Art. 21 des statuts : F.E.E. désigne un RÉGIME DISTINCT
      // de prime de fin d'année (CCT 11/09/2019, 154.074/CO/149.01), qui
      // coexiste avec le « régime général » (CCT 22/09/2020, 162.265/CO/149.01).
      // ⚠️ Volta asbl n'est pas un second fonds : c'est une asbl créée par
      // l'organe d'administration du Fonds (art. 20 § 2).
      nom: "Fonds de sécurité d'existence pour le secteur des électriciens – Volta (fbz Volta fse)",
      taux_patronal: 0.011,
      assiette: 'brut à 108 %',
      autresCotisations: {
        formationInnovationVolta: 0.008,   // dont 0,15 % groupes à risque
        // ⚠️ VÉRIFIÉ le 05/09/2026 — `groupesRisque` N'EST PAS UNE COTISATION
        // ADDITIONNELLE : c'est une SOUS-AFFECTATION INTERNE des 0,80 % ci-dessus
        // (art. 29.2 §2 al. 2 de la CCT du 16/10/2023 : « de ce 0,80 %... 0,15 %
        // sera utilisé pour des initiatives en faveur des groupes à risque »).
        // `electricien-engine.js` compte déjà `formationInnovationVolta` en
        // entier — cette clé n'est lue nulle part, gardée pour mémoire. NE JAMAIS
        // l'additionner à `formationInnovationVolta` : ce serait compter deux
        // fois les mêmes 0,15 %.
        groupesRisque: 0.0015,
        primeFinAnnee: 0.1315,             // ⚠️ le § 3 ne dit PAS « à 108 % »
        pensionSectorielle: 0.021,
      },
      source: 'CCT du 16/10/2023, art. 29.2, statuts coordonnés du FSE électriciens – Volta, en vigueur 01/01/2024 (texte primaire lu le 21/08/2026)',
      // ═══ POURQUOI L'ONSS AFFICHE 15,05 % ET NOUS 1,10 % — L'ÉCART EST RÉSOLU ═══
      // Ajouté le 25/08/2026 après une ERREUR : le taux ONSS de la catégorie 067
      // (15,05 %, code 820) a d'abord été pris pour une sous-évaluation de notre
      // part, et écrit par-dessus ce bloc. C'était FAUX, et l'arithmétique le dit
      // sans appel :
      //     1,10 %  FSE au sens strict
      //   + 0,80 %  formation / innovation Volta
      //   + 13,15 % financement de la prime de fin d'année
      //   = 15,05 %  ← exactement ce que l'ONSS perçoit sous le code 820
      // L'ONSS AGRÈGE ce que l'art. 29.2 des statuts DÉCOMPOSE. Écraser 1,10 %
      // par 15,05 % aurait compté la prime de fin d'année DEUX FOIS : une fois
      // dans le fonds, une fois comme prime.
      // 👉 La leçon, et elle vaut pour les 57 autres écarts : un taux ONSS
      //    supérieur au nôtre n'est pas une sous-évaluation tant qu'on n'a pas
      //    lu ce que notre propre fiche décompose.
      ecartOnssExplique: {
        tauxOnss: 0.1505,
        composition: 'FSE strict 1,10 % + formation/innovation 0,80 % + financement de la prime de fin d\'année 13,15 %',
        pourquoiHorsTaux: "Les 13,15 % financent la prime de fin d'année. Les inclure dans `taux_patronal` la compterait deux fois dès qu'un appelant ajoute la PFA au coût employeur — c'est le même euro vu de deux endroits.",
        verifie: '2026-08-25',
      },
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise.' },
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 25/06/2014 « système sectoriel d'éco-chèques » (14901/14901-2014-009103.pdf), banque CCT du SPF Emploi, dépôt 123.003/CO/149.01, texte primaire lu le 21/09/2026, art. 3 : 250 EUR/an pour tout ouvrier temps plein, payés le 15 novembre (art. 4), en vigueur depuis le 01/01/2014, durée indéterminée, remplace la CCT du 20/10/2011 (106.860/CO/149.01, même montant). La CCT « prime pouvoir d'achat » du 16/10/2023 (183582/CO/149.01) est un mécanisme ponctuel distinct, expiré le 31/12/2024, qui référence ce régime sans le modifier.",
    ecoChequesDateVerif: '2026-09-21',
    ecoChequesPaiementDate: '15/11',
    primeFinAnnee_pct: 8.33,                 // 8,33% du salaire brut, payée par FSE
    primeFinAnnee_payeur: 'Fonds de Sécurité d\'Existence',
    formation: { jours: 5, fonds: 'Vormelek (Formation Électriciens)' },
    cotisationSpeciale: null,

    // ── 6 catégories ouvriers (A → F) ──
    // Q1 2026 : indexation +2,23% au 01/01/2026 (CGSLB cp-149.01)
    // Source : CCT électriciens 2021-2022 + indexations cumulées
    categoriesOuvriers: {
      cat_A: { code: 'A', label: 'Cat A — Ouvrier non qualifié',                   tauxHoraire: 14.85, niveau: 'Non qualifié' },
      cat_B: { code: 'B', label: 'Cat B — Ouvrier spécialisé 2e catégorie',        tauxHoraire: 15.62, niveau: 'Semi-qualifié' },
      cat_C: { code: 'C', label: 'Cat C — Ouvrier spécialisé 1ère catégorie',      tauxHoraire: 16.40, niveau: 'Qualifié' },
      cat_D: { code: 'D', label: 'Cat D — Ouvrier qualifié 3e catégorie',          tauxHoraire: 17.25, niveau: 'Qualifié confirmé' },
      cat_E: { code: 'E', label: 'Cat E — Ouvrier qualifié 2e catégorie',          tauxHoraire: 18.10, niveau: 'Hautement qualifié' },
      cat_F: { code: 'F', label: 'Cat F — Ouvrier qualifié 1ère catégorie',        tauxHoraire: 19.05, niveau: 'Expert' },
    },

    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%, 20h-6h)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    // Primes spécifiques CP 149.01
    primes: {
      equipeMatinSoir: 0.10,                   // +10% travail en équipe (matin/soir)
      equipeNuit: 0.20,                         // +20% travail de nuit (20h-6h)
      travauxInsalubres: 0.15,                  // +15% travaux insalubres/dangereux/hauteur
      travauxHauteur: 0.10,                     // +10% travaux en hauteur
    },

    // Prime ancienneté (cumulable)
    primesAnciennete: {
      1: 0.01,                                  // +1% après 1 an
      2: 0.005,                                 // +0,5% après 2 ans
      3: 0.005,                                 // +0,5% après 3 ans
    },

    indemnites: {
      gardeEnfants_jour: 1.75,                  // EUR/jour enfants ≤ 12 ans (similaire CP 111)
      gardeEnfants_max_annuel: 210,
      gardeEnfants_age_max: 12,
    },

    avantages: {
      etudiantsJobistes_pct: 0.80,              // 80% du barème pour étudiants jobistes
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 149.01 indexation 01/01/2026 +2,23% + CSC sectoriel + Limosa SCP 149.01 + Brupartners + Techlink + FEE',
      indexationFrequence: 'Annuelle au 01/01',
      indexationQ1_2026: 0.0223,
      classification: '6 catégories ouvriers (A non qualifié → F qualifié 1ère cat)',
      notes: 'Champ : entreprises de montages et installations électriques/électroniques (téléphonie, éclairage, alarmes, installations électriques). Commerce gros/détail appareils électriques aussi inclus. Étudiants jobistes : 80% du barème de la fonction comparable.',
      nouveautes2026: [
        'Indexation +2,23% au 01/01/2026 confirmée (CGSLB)',
        'Négociations sectorielles 2025-2026 en cours',
        'Prime FDA 8,33% payée directement par FSE',
        'Éco-chèques 250 €/an versés le 15 novembre',
        'Prime équipes matin/soir +10%, nuit +20%',
        'Prime travaux insalubres/dangereux/hauteur +15%',
      ],
    },
    // ═══ FUSION DE LA JUMELLE '14901' — 21/08/2026 ════════════════════════
    // Les sept cles qui suivent venaient de la fiche EN DOUBLE `'14901'`, que
    // ce commit supprime. Elles sont portees ici TELLES QUELLES : elles
    // n'existaient que d'un seul cote, donc aucun arbitrage n'etait necessaire
    // et rien n'est invente.
    //
    // ⚠️ Les cles qui se CONTREDISAIENT entre les deux fiches (indexation,
    // fondsSE, chequesRepas, formation, categoriesOuvriers, heuresSup,
    // avantages, _meta) n'ont PAS ete fusionnees. Ce sont les valeurs de
    // '149.01' qui restent, parce que ce sont deja les SEULES que l'app
    // servait : balayage du 21/08/2026 — rien ne lisait '14901' hors le cron
    // de completude et deux tests. Les departager demande une source PAR CLE ;
    // les choisir sans source serait pire que le doublon. Divergence consignee
    // dans la PR, valeurs ecartees listees la-bas.
    primeFDA: {
      type: 'Prime de fin d\'année — payée par FSE',
      conditions: '120 jours prestés minimum sur période de référence pour droit complet',
      assimilations_max: 130,                        // jours assimilés (codes DMFA 1-5, 14, 20)
      paiement: 'décembre/janvier N+1',
      note: 'Si moins de 120 jours prestés : max jours assimilés = 1/3 des jours prestés',
    },
    primeAnciennete: {
      apres_1_an: 0.01,                              // +1% sur salaire effectif
      apres_2_ans: 0.005,                            // +0,5%
      apres_3_ans: 0.005,                            // +0,5% (cumulable, total 2% après 3 ans)
      cumulable: true,
      base_calcul: 'salaire effectivement payé',
      note: 'Cumul = +1% (1 an) puis +0,5% (2 ans) puis +0,5% (3 ans) = +2% total après 3 ans. Au-delà de 3 ans : pas d\'augmentation supplémentaire automatique.',
    },
    indemniteMobilite: {
      transport_public_pct: 1.00,                    // 100% du coût total (type 1)
      vehicule_prive_motorise_km: 0.3450,           // EUR/km (type 2) — 01/02/2026
      vehicule_employeur_km: 0.1732,                // EUR/km (type 3) — 01/02/2026
      date_indexation: '2026-02-01',
      note: 'Indemnités indexées chaque 1er février selon évolution carte-train SNCB. Réglementation pour ouvriers se déplaçant vers chantier différent du lieu d\'embauche.',
    },
    primesSpeciales: {
      travaux_insalubres_dangereux: {
        majoration_pct: 0.15,                       // +15% minimum
        note: 'Majoration salaire de base pour travaux à caractère exceptionnel présentant un danger inhérent ou non à la profession',
      },
      travaux_hauteur: {
        majoration_pct: 0.15,                       // +15% minimum
        note: 'Travaux en hauteur à un niveau instable ou au-dessus d\'un niveau stable',
      },
    },
    pensionComplementaire: {
      depuis: '2002-01-01',
      tous_travailleurs: true,
      note: 'Tous les travailleurs CP 149.01 ont droit à une pension complémentaire sectorielle, peu importe nature contrat (CDI/CDD/temps plein/temps partiel)',
    },
    droitFormation: {
      jours_par_an: {
        '2024': 3,
        '2026': 4,                                   // 4 jours/an à partir 01/01/2026
      },
      seuil_entreprise: 20,                          // 20+ travailleurs
      droit_individuel_contraignant: true,
      reduction_2_jours_pme: '2026-01-01',           // < 20 employés : 2 jours
      note: 'CP 149.01 entreprises ≥ 20 travailleurs : droit individuel contraignant à formation de 3j/an (2024) puis 4j/an (à partir 01/01/2026)',
    },
    accordSectoriel: {
      conclusion: '2026-02-03',
      periode: '2025-2026',
      negociateurs: ['ELOYA (employeurs)', 'CGSLB', 'CSC CSCBIE', 'FGTB Métallos'],
      etat: 'Protocole conclu, CCT définitives en cours',
      note: 'Pour 2025-2026 : protocole accord sectoriel négocié. Conditions salariales et de travail définitives via CCT post-protocole.',
    },
  },

  // CP 201 — Commerce de détail indépendant
  // ⚠️ Module 18 — Q1 2026 — Magasins indépendants (hors grande distribution)
  // Sources : CGSLB CP 201 + UCM + salairesminimums.be SCP 2010000
  '201': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 28/04/2026 (CP 201),
    // « Accès au droit aux allocations pour un emploi de fin de carrière »,
    // banque CCT du SPF Emploi, 201/201-2026-009226.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL ICI, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel — CCT n° 179 du CNT du
    // 21/10/2025, valable du 01/01/2026 au 31/12/2027. Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Le fait sectoriel est
    // donc : « ce secteur l'a activé, pour cette période ». Recopier 55 comme
    // une valeur du secteur serait dupliquer une constante nationale (R1).
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Le travailleur doit justifier soit de 35 ans de passé professionnel comme salarié, soit d\'un métier lourd, soit relever d\'une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 28/04/2026 (CP 201) — accès au droit aux allocations pour un emploi de fin de carrière, banque CCT du SPF Emploi, 201/201-2026-009226.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 201 — Commerce de détail indépendant',
    nomCourt: 'Commerce détail indépendant',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [5], dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'CP 201 indexe annuellement au 1er mai. Indexation +2% au 01/05/2026 (source: CSC CNE, La Centrale Générale 30/04/2026).' },

    // ── 2,00 % → 0,10 %, ET L'AMBIGUÏTÉ N'EN ÉTAIT PAS UNE (26/08/2026) ──
    // 📏 Huit catégories d'employeur ONSS citent la CP 201. Leurs taux OUVRIER
    // vont de 0,59 % à 15,70 % — d'où le verdict « ambigu » qui a laissé cette
    // fiche muette. Ce verdict lisait la mauvaise colonne : la CP 201 est une
    // commission d'EMPLOYÉS. Dans ces huit catégories, les ouvriers relèvent
    // d'une AUTRE commission (100, 119, 127, 128, 149.01, 149.03, 149.04,
    // boulangerie artisanale) et le taux ouvrier est celui de CETTE autre CP,
    // pas celui du fonds de la 201.
    //
    // Sur la colonne qui la concerne — code travailleur 832, employés — les
    // HUIT catégories sur HUIT s'accordent : 0,10 %. Il n'y a jamais eu
    // d'ambiguïté, il y avait une confusion de public.
    //
    // 💰 L'écart est de vingt fois. Sur un employé à 3 000 € bruts, la fiche
    // facturait 60,00 € par mois là où le fonds perçoit 3,00 €.
    fondsSE: {
      nom: 'Fonds social CP 201 — commerce de détail indépendant',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      onssCategorie: '100',
      onssSourcee: true,
      perceptionOnssSeule: true,
      // ⚠️ CE CHAMP N'EST PAS DÉCORATIF : c'est lui qui fait interroger l'ONSS
      // sur la colonne EMPLOYÉS. Sans lui, `statutFondsSE` compare sur le taux
      // ouvrier — celui du fonds d'une AUTRE commission — et l'écran affiche
      // « à trancher entre 0,59 % et 15,70 % » sur une fiche pourtant exacte.
      ouvriersRelevantDUneAutreCp: ['100', '119', '127', '128', '149.01', '149.03', '149.04'],
      perimetre: "EMPLOYÉS uniquement. Le fonds de la CP 201 ne perçoit rien sur les ouvriers : dans chacune des huit catégories, les ouvriers relèvent d'une autre commission paritaire, qui a son propre fonds — les sept nommées ci-dessus, plus la boulangerie artisanale (catégorie 058), dont l'annexe 27 ne donne pas le numéro de CP.",
      dateVerif: '2026-08-26',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml) croisé avec l'annexe 27, lu le 26/08/2026. Code travailleur 832 (employés), régime général (type 0) : 0,10 % dans les 8 catégories d'employeur qui citent la CP 201 — 057, 058, 067, 077, 078, 091, 100 et 169. Accord unanime, 8 sur 8.",
      variantesConditionnelles: {
        taux: 0.005,
        surCombien: '6 catégories sur 8 (067, 077, 078, 091, 100, 169) — absente des catégories 057 et 058',
        note: "L'ONSS publie une SECONDE cotisation employés de 0,50 % sous le type de cotisation 5. Le type 5 est un régime CONDITIONNEL (ailleurs : « 20 travailleurs et plus » dans la construction, « 50 et plus » dans l'horeca) — le fichier des taux ne dit PAS quelle condition le déclenche pour la CP 201. Elle n'est donc pas appliquée d'office, et elle n'est pas non plus écartée : un employeur qui remplit la condition doit 0,60 % au total. À trancher sur les statuts du fonds.",
      },
      note: "Valeur précédente : 2,00 %, sans source ni date. ⚠️ Ce taux est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise.' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 04/09/2017 « relative aux éco-chèques » (201/201-2017-…pdf, W:
    // 142097/CO/201), entrée en vigueur le 01/08/2017, durée indéterminée,
    // toujours en vigueur — remplace celle du 04/02/2014. Art. 4 : « une
    // prime de 250 EUR » par an, temps plein, période de référence complète
    // (01/04-31/03) — L'EMPLOYEUR CHOISIT entre une prime brute annuelle
    // (188 EUR hors charges patronales) OU l'éco-chèque (250 EUR nominal,
    // exonéré). Étudiants exclus (art. 5). Paiement en avril.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 04/09/2017 « relative aux éco-chèques » (201/201-2017-…pdf, W: 142097/CO/201), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 4 : prime annuelle de 250 EUR temps plein, l'employeur choisit entre prime brute (188 EUR) et éco-chèque (250 EUR), paiement en avril, en vigueur depuis le 01/08/2017.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora' },
    cotisationSpeciale: null,

    // 4 classes barémiques (employés)
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Vendeur débutant',                            salaireMensuel: 2154.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Vendeur expérimenté',                         salaireMensuel: 2280.00, niveau: 'Confirmé' },
      classe_3: { code: 'C3', label: 'Classe 3 — Responsable / gérant magasin',                salaireMensuel: 2520.00, niveau: 'Encadrement' },
      classe_4: { code: 'C4', label: 'Classe 4 — Cadre / directeur magasin',                   salaireMensuel: 2890.00, niveau: 'Direction' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    // ⚠️ velo 0,27→0,29 EUR/km des le 01/06/2026 (max 40 km A-R, velo et velo electrique) — CCT du 26/05/2026 art. 5, lue le 24/08/2026

    indemnites: {
      transportPublic_pct: 0.80,                // 80% carte train 2e classe (depuis 2026)
      velo_2026: 0.29,                          // EUR/km (max 40 km/j AR) — CCT sectorielle propre CP 201 (dès 01/01/2024), inchangé 2026 ; CCT n°164 supplétive NON applicable même si < 0,30 — Synova/Securex/UCM, consulté 2026-07-07
      veloMaxExonere: 0.37,                     // EUR/km max exonéré fiscal/ONSS 2026
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 201 + UCM + salairesminimums.be SCP 2010000',
      classification: '4 classes (C1 → C4)',
      notes: 'Couvre les magasins indépendants de détail (boutiques mode, électronique, librairies, fleuristes, etc.). Distinct de CP 311 (grande distribution chaînes), CP 202 (alimentaire détail), CP 312 (grands magasins).',
      nouveautes2026: [
        'Indexation au 01/01/2026',
        'Distinction claire vs CP 311 (grande distribution) et CP 202 (alimentaire)',
      ],
    },
  },

  // CP 313 — Pharmacies et offices de tarification
  // ⚠️ Module 19 — Q1 2026 — Pharmacies de détail
  // Sources : CGSLB CP 313 + APB + salairesminimums.be SCP 3130000
  '313': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 08/01/2015 (CP 313), statut
    // de la délégation syndicale, banque CCT du SPF Emploi,
    // 313/313-2015-000215.pdf (la CCT du 20/12/2017 n'en est qu'une
    // modification, elle ne reprend pas le seuil). Mot pour mot : « une
    // délégation syndicale est installée dans les entreprises occupant 50
    // travailleurs et plus dans les liens d'un contrat de travail à durée
    // indéterminée de 4/5 ou plus, lorsqu'au moins 25 % de l'effectif total
    // des travailleurs est syndiqué. »
    //
    // ⚠️ TROIS CONDITIONS CUMULÉES, dont une que rien d'autre n'exige dans nos
    // fiches : le décompte ne retient que les CDI d'AU MOINS 4/5. Dans un
    // secteur où le temps partiel est courant, compter les têtes donnerait un
    // résultat faux.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_et_syndicalisation',
        seuilTravailleurs: 50,
        seuilFractionMinimale: 0.8,      // seuls les CDI de 4/5 ou plus comptent
        pctPersonnelSyndique: 0.25,
        notionEntreprise: 'unité technique d\'exploitation au sens de la législation sur les élections sociales',
        source: 'CCT du 08/01/2015 (CP 313) — statut de la délégation syndicale, banque CCT du SPF Emploi, 313/313-2015-000215.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 313 — Pharmacies et offices de tarification',
    nomCourt: 'Pharmacies',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-03-01', source: 'AUDIT 2026-06 — CSC/Easypay' },

    fondsSE: {
      nom: 'Fonds social CP 313 — Pharmacies',
      taux_patronal: 0.0013,
      taux_patronal_employe: 0.0013,
      base108Ouvrier: true,
      onssCategorie: '135, 235, 335',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 135, 235, 335 : 820 ouvriers 0,13 % · employés 0,13 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 3,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise. Quasi-systématiques.' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 313 + « eco cheque » :
    // 1 hit, FAUX POSITIF. La CCT du 17/12/2021 (313/313-2021-015141.pdf)
    // institue une « prime Corona » PONCTUELLE (100 € unique + 100 €
    // conditionnel, année 2021 seule, expirée le 31/12/2022) versée en
    // chèques de CONSOMMATION, pas en éco-chèques. La seule mention
    // « eco-cheques » est une RÉFÉRENCE CROISÉE à l'art. 6 §3 de la CCT
    // n°98 (règles d'assimilation de périodes), empruntée pour définir les
    // journées assimilées de la prime Corona — elle n'établit aucun régime
    // d'éco-chèques pour ce secteur. Aucun autre document déposé sous CO/313
    // ne porte le thème « Ecochèques ».
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 313 — 1 hit plein texte, faux positif (CCT du 17/12/2021, prime Corona ponctuelle en chèques de consommation, référence croisée à l'art. 6 §3 de la CCT n°98, pas un régime d'éco-chèques)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'IFE (Institut Formation Pharma)' },
    cotisationSpeciale: null,

    // 5 catégories (pharmaciens, assistants, vendeurs, magasiniers)
    categoriesEmployes: {
      assistant_pharma:    { code: 'AP',  label: 'Assistant en pharmacie (diplômé)',                salaireMensuel: 2480.00, niveau: 'Qualifié' },
      vendeur_pharma:      { code: 'VP',  label: 'Vendeur en pharmacie',                            salaireMensuel: 2280.00, niveau: 'Vendeur' },
      magasinier:          { code: 'MAG', label: 'Magasinier / livreur',                            salaireMensuel: 2154.00, niveau: 'Logistique' },
      pharma_adjoint:      { code: 'PA',  label: 'Pharmacien adjoint (titulaire)',                  salaireMensuel: 3850.00, niveau: 'Pharmacien' },
      pharma_titulaire:    { code: 'PT',  label: 'Pharmacien titulaire / responsable officine',     salaireMensuel: 4520.00, niveau: 'Pharmacien titulaire' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      nuit: { taux: 0.50, label: 'HS nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 0.80,
      velo_2026: 0.20,                          // EUR/km (max 30 km/j AR, prorata jours prestés ; cumul TC aussi 0,20 dès 01/01/2024) — CCT sectorielle propre CP 313, inchangé 2026 (CGSLB MAJ 10/04/2026) ; 0,27 antérieur = sans base — CGSLB/ACLVB/Liantis, consulté 2026-07-07
      velo_max_km_jour: 30,                     // km max par jour (aller-retour)
      veloMaxExonere: 0.37,                     // EUR/km max exonéré fiscal/ONSS 2026
      // Prime de garde de nuit / dimanche pharmacie
      gardeNuitPharmacie: 50.00,                // EUR forfait par garde
      gardeDimanchePharmacie: 75.00,            // EUR forfait par garde dimanche
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 313 + APB (Association Pharmaceutique Belge) + salairesminimums.be SCP 3130000',
      classification: '5 fonctions (assistant pharma, vendeur, magasinier, pharma adjoint, pharma titulaire)',
      notes: 'Couvre pharmacies de détail (officines) et offices de tarification. Pharmacien titulaire = responsable officine. Garde de nuit/dimanche obligatoire selon roulement provincial.',
      nouveautes2026: [
        'Indexation au 01/01/2026',
        'Prime garde nuit 50 €/forfait',
        'Prime garde dimanche 75 €/forfait',
      ],
    },
  },

  // CP 323 — Immobilier (employés des agences immobilières)
  // ⚠️ Module 20 — Q1 2026 — Agences immobilières, syndics, gestion locative
  // Sources : CGSLB CP 323 + IPI + UCM + salairesminimums.be SCP 3230000
  '323': {
    nom: 'CP 323 — Immobilier (gestion d\'immeubles, syndics, agences)',
    nomCourt: 'Immobilier',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Indexation +2,21% au 01/01/2026 confirmée par Prestataire A (~13 500 travailleurs · 5 900 employeurs CP 323). Source : Prestataire A 22/12/2025.', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },

    // ⚠️ TAUX NON TRANCHÉ, ET C'EST DÉLIBÉRÉ — instruction du 25/08/2026.
    // Le 2 % n'a aucune source. Ce qu'on sait maintenant, et qui n'était nulle part :
    //
    // CE QUI EST LU AU TEXTE PRIMAIRE (banque CCT du SPF Emploi, 25/08/2026)
    //   · CCT du 28/05/2024, art. 13 § 1 : « Pour les employés, les ouvriers et
    //     les concierges, le montant de la cotisation destinée à financer les
    //     formations est fixé à 1,1 % de la masse salariale brute des
    //     travailleurs depuis le 1er janvier 2008. » § 2 : 0,10 % pour les
    //     domestiques. (323/323-2024-010446.pdf)
    //   · CCT du 02/12/2021, art. 2 : effort « groupes à risque » de 0,10 % de
    //     la rémunération globale, perçu par l'ONSS. (323/323-2021-015249.pdf)
    //   → 1,20 % identifiés.
    //
    // CE QUE L'ONSS PERÇOIT (fichier des taux DmfA 2026/3)
    //   · employés, code 830 : 1,40 % — catégories 112 et 113 d'accord ;
    //   · ouvriers, code 820 : 19,40 % chez un professionnel (cat. 112/113),
    //     0,20 % chez un particulier occupant du personnel domestique (037).
    //
    // ✅ ÉCART RÉSOLU LE 05/09/2026 — texte primaire trouvé qui chiffre tout :
    // CCT du 24/09/2007 (dépôt 85219/CO/323), « Fixation des cotisations
    // patronales au Fonds social et de garantie pour la gestion d'immeubles »,
    // en vigueur depuis le 01/01/2008, banque CCT du SPF Emploi. Art. 2 fixe
    // QUATRE cotisations communes employés/ouvriers (1,10 % formation + 0,15 %
    // prime syndicale + 0,10 % groupes à risque + 0,05 % prépension = 1,40 %,
    // recoupe EXACTEMENT le taux ONSS employés) et, en plus pour les ouvriers
    // et concierges sous contrat d'ouvrier, 18,00 % de la masse salariale
    // brute de chaque trimestre finançant la prime de fin d'année (art. 2,
    // renvoyant à l'art. 3 de la CCT du 01/12/2005) : 1,40 % + 18,00 % =
    // 19,40 %, recoupe EXACTEMENT le taux ONSS ouvriers professionnel. Les
    // deux bornes ONSS sont donc entièrement expliquées, plus rien à nommer.
    //
    // ⛔ POURQUOI LE FINANCEMENT DE LA PRIME (18 %) N'EST PAS DANS
    // `taux_patronal`, MÊME MÉCANIQUE QUE LA CP 149.01 DÉJÀ RÉSOLUE : ces 18 %
    // financent la prime de fin d'année versée par le fonds. Les inclure dans
    // `taux_patronal` la compterait DEUX FOIS dès qu'un appelant ajoute la PFA
    // au coût employeur via `primeFinAnnee_pct` ci-dessous — le même euro vu de
    // deux endroits.
    fondsSE: {
      nom: "Fonds social et de garantie pour la gestion d'immeubles",
      taux_patronal: 0.014,
      taux_patronal_employe: 0.014,
      cctSourcee: true,
      cotisationsLues: {
        formation: 0.011,
        primeSyndicale: 0.0015,
        groupesRisque: 0.001,
        prepension: 0.0005,
        formationDomestiques: 0.001,
        total: 0.014,
        source: "CCT du 24/09/2007 (dépôt 85219/CO/323), art. 2 — « Fixation des cotisations patronales au Fonds social et de garantie pour la gestion d'immeubles », banque CCT du SPF Emploi, texte primaire lu le 05/09/2026. En vigueur depuis le 01/01/2008 (remplace la CCT du 12/07/2006, 81564/CO/323), durée indéterminée.",
        dateVerif: '2026-09-05',
      },
      autresCotisations: {
        // ⚠️ OUVRIERS ET CONCIERGES SOUS CONTRAT D'OUVRIER UNIQUEMENT (art. 2).
        // Perçu par l'ONSS (art. 3), reversé au fonds qui paie la prime.
        primeFinAnnee: 0.18,
      },
      ecartOnssExplique: {
        tauxOnssEmploye: 0.014,
        tauxOnssOuvrierProfessionnel: 0.194,
        tauxOnssParticulierDomestique: 0.002,
        composition: "employés : 1,10 % formation + 0,15 % prime syndicale + 0,10 % groupes à risque + 0,05 % prépension = 1,40 % ; ouvriers professionnels : les mêmes 1,40 % + 18,00 % financement de la prime de fin d'année = 19,40 %",
        pourquoiHorsTaux: "Les 18,00 % financent la prime de fin d'année versée par le fonds. Les inclure dans `taux_patronal` la compterait deux fois dès qu'un appelant ajoute la PFA au coût employeur via `primeFinAnnee_pct`.",
        source: "CCT du 24/09/2007 (dépôt 85219/CO/323), art. 2-3, banque CCT du SPF Emploi, texte primaire lu le 05/09/2026.",
        verifie: '2026-09-05',
      },
      note: "Ancienne valeur (2 %) sans source, corrigée le 05/09/2026. Historique de la piste : hypothèse ONVA (pécule de vacances, 15,84 %) infirmée par l'arithmétique le 05/09/2026 matin (15,84 ≠ 18,20 points d'écart) avant que le texte primaire de la CCT de 2007 ne soit trouvé l'après-midi même et ne ferme l'écart exactement.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise. Très courants en CP 323.' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 323 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 323 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora' },
    cotisationSpeciale: null,

    // 5 classes barémiques
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Employé administratif débutant',               salaireMensuel: 2154.00, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Gestionnaire locatif / employé qualifié',       salaireMensuel: 2380.00, niveau: 'Qualifié' },
      classe_3: { code: 'C3', label: 'Classe 3 — Agent immobilier IPI / négociateur',            salaireMensuel: 2680.00, niveau: 'Agent immobilier' },
      classe_4: { code: 'C4', label: 'Classe 4 — Syndic / gestionnaire copropriété',             salaireMensuel: 2980.00, niveau: 'Syndic' },
      classe_5: { code: 'C5', label: 'Classe 5 — Cadre / directeur agence',                      salaireMensuel: 3520.00, niveau: 'Cadre' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 0.80,
      velo_2026: 0.37,                          // EUR/km dès 01/01/2026 — accord sectoriel PC 323 2025-2026 : indemnité vélo = max fiscal (« Vanaf 01/01/2026 bedraagt de sectorale fietsvergoeding 0,37 EUR per km ») — protocole PC 323 + Liantis, consulté 2026-07-07                        // EUR/km voiture privée (Q2 2026, indexée trim.)
    },

    avantages: {
      // Commissions immobilières (variable selon entreprise)
      commissions: 'Variables selon CCT entreprise (typique 10-30% honoraires)',
      voitureSociete: 'Fréquente pour Classes 3+ (agent immobilier IPI)',
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 323 + IPI (Institut Professionnel Agents Immobiliers) + UCM + salairesminimums.be SCP 3230000',
      classification: '5 classes (C1 employé administratif → C5 cadre)',
      notes: 'Couvre agences immobilières, syndics, gestion locative, gestion d\'immeubles. Agent immobilier requiert agrément IPI (Classe 3+). Commissions variables selon contrat. Pas d\'indemnité km sectorielle : le forfait fédéral s\'applique.',
      nouveautes2026: [
        'Indexation au 01/01/2026',
        'Indemnité km voiture : pas de tarif sectoriel — forfait fédéral applicable',
        'Train 80% au 01/01/2026',
      ],
    },
  },

  // CP 335 — Prestation de services et de soutien aux entreprises et aux indépendants
  // ⚠️ Module 21 — Q1 2026 — Secteur services aux entreprises (large)
  // Sources : CGSLB CP 335 + UCM + Prestataire C
  '335': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 14/01/2026 (CP 335),
    // crédit-temps, banque CCT du SPF Emploi, 335/335-2026-001030.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL ICI, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel — CCT n° 179 du CNT du
    // 21/10/2025, valable du 01/01/2026 au 31/12/2027. Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Emplois de fin de carrière 1/5 ; réservé aux travailleurs visés à l\'article 3 de la CCT sectorielle (longue carrière, métier lourd, entreprise en difficulté ou en restructuration).',
        source: 'CCT du 14/01/2026 (CP 335) — crédit-temps, banque CCT du SPF Emploi, 335/335-2026-001030.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 335 — Services aux entreprises et travail temporaire (secrétariats sociaux, fiduciaires non IPCF/ITAA)',
    nomCourt: 'Services aux entreprises',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01' },

    fondsSE: {
      nom: 'Fonds social CP 335',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '008',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 008 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 2,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Selon CCT entreprise.' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 335 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 335 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora' },
    cotisationSpeciale: null,

    // 4 classes barémiques (similaires CP 200)
    categoriesEmployes: {
      classe_A: { code: 'A', label: 'Classe A — Tâches exécutives',                              salaireMensuel: 2189.81, niveau: 'Exécutive' },
      classe_B: { code: 'B', label: 'Classe B — Fonctions de support',                           salaireMensuel: 2380.00, niveau: 'Support' },
      classe_C: { code: 'C', label: 'Classe C — Fonctions de gestion / coordination',            salaireMensuel: 2680.00, niveau: 'Gestion' },
      classe_D: { code: 'D', label: 'Classe D — Spécialiste / cadre',                            salaireMensuel: 3120.00, niveau: 'Cadre' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 0.80,
      velo_2026: 0.27,                          // EUR/km (max 10,80 €/j, 40 km AR) — CCT sectorielle propre CP 335 (01/10/2023), valable jusqu'au 30/09/2026 ; CCT n°164 non applicable — CSC/Synova, consulté 2026-07-07
      velo_max_jour: 10.80,                     // EUR/jour jusqu'au 30/09/2026
      velo_depuis_01_10_2026: 0.30,             // EUR/km dès 01/10/2026 (accord 18/12/2025) — TODO: couple 0,30/12,80 incohérent (12,80 = 40 km × 0,32), à confirmer sur CCT déposée avant la paie d'octobre 2026
      velo_max_jour_depuis_01_10_2026: 12.80,   // EUR/jour dès 01/10/2026 (même réserve TODO)
      veloMaxExonere: 0.37,                     // EUR/km max exonéré fiscal/ONSS 2026
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 335 + UCM + Prestataire C',
      classification: '4 classes (A/B/C/D similaires CP 200)',
      notes: 'CP 335 : services aux entreprises. ATTENTION : la majorité des employés de fiduciaires/cabinets comptables relèvent de la CP 336 (Professions libérales) si l\'employeur est titulaire IPCF/ITAA. CP 200 sinon.',
      nouveautes2026: [
        'Indexation au 01/01/2026',
        'Aligné CP 200 (auxiliaire pour employés)',
      ],
    },
  },

  // CP 336 — Professions libérales (fiduciaires titulaires IPCF/ITAA, avocats, notaires, architectes...)
  // ⚠️ Module 22 — Q1 2026 — TES PROSPECTS DIRECTS
  // Sources : CGSLB CP 336 (11/02/2026) — accord sectoriel 2025-2026 confirmé
  '336': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 15/12/2025 (CP 336,
    // professions libérales), crédit-temps, banque CCT du SPF Emploi,
    // 336/336-2025-015038.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL ICI, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel — CCT n° 179 du CNT du
    // 21/10/2025, valable du 01/01/2026 au 31/12/2027. Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Travailleurs justifiant d\'une longue carrière, d\'un métier lourd, ou relevant d\'une entreprise en difficulté ou en restructuration (CCT n° 179).',
        source: 'CCT du 15/12/2025 (CP 336) — crédit-temps, banque CCT du SPF Emploi, 336/336-2025-015038.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 336 — Professions libérales (fiduciaires IPCF/ITAA, avocats, notaires, architectes, médecins...)',
    nomCourt: 'Professions libérales',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.020, derniereDate: '2026-01-01', plafond_brut: 3500, note: 'Indexation 2025-2026 plafonnée à 3 500 € brut temps plein selon accord sectoriel' },

    fondsSE: {
      nom: 'Fonds social CP 336 — Professions libérales',
      taux_patronal: 0.0012,
      taux_patronal_employe: 0.0012,
      base108Ouvrier: true,
      onssCategorie: '035, 835',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 035, 835 : 820 ouvriers 0,12 % · employés 0,12 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 2,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, note: 'Quasi-systématiques en cabinets fiduciaires/avocats.' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 336 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 336 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Cevora' },
    cotisationSpeciale: null,

    // 4 classes barémiques + spécifiques fiduciaires
    categoriesEmployes: {
      classe_1: { code: 'C1', label: 'Classe 1 — Employé administratif / réception',              salaireMensuel: 2189.81, niveau: 'Débutant' },
      classe_2: { code: 'C2', label: 'Classe 2 — Comptable junior / assistant juridique',         salaireMensuel: 2480.00, niveau: 'Junior' },
      classe_3: { code: 'C3', label: 'Classe 3 — Comptable confirmé (3-6 ans) / collaborateur',   salaireMensuel: 3200.00, niveau: 'Confirmé' },
      classe_4: { code: 'C4', label: 'Classe 4 — Comptable senior / responsable dossier',         salaireMensuel: 3950.00, niveau: 'Senior' },
      classe_5: { code: 'C5', label: 'Classe 5 — Manager / directeur technique',                  salaireMensuel: 4850.00, niveau: 'Manager' },
      classe_6: { code: 'C6', label: 'Classe 6 — Associé / partner (plafond brut 5 500 €)',       salaireMensuel: 5500.00, niveau: 'Direction' },
    },

    heuresSup: {
      jour: { taux: 0.50, label: 'HS jour (+50%)' },
      dimanche: { taux: 1.00, label: 'HS dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },

    indemnites: {
      transportPublic_pct: 0.80,                // 80% carte train 2e classe au 01/01/2026
      transportPublic_busTram_pct: 0.718,       // 71,8% bus/tram (plafond 7 km carte train)
      velo_jusqu_30_09_2026: 0.10,              // 0,10 €/km jusqu'au 30/09/2026 (max 4 €/jour)
      velo_max_jour_jusqu_30_09_2026: 4.00,
      velo_depuis_01_10_2026: 0.32,             // 0,32 €/km depuis 01/10/2026 (max 12,80 €/jour)
      velo_max_jour_depuis_01_10_2026: 12.80,
    },

    // Jours sectoriels supplémentaires (accord 01/01/2026)
    joursSectorielsDeuil: {
      conjoint_partenaire_legal_enfant: { total: 12, immediats: 3, annee_suivante: 9 },
      pere_mere_conjoint: { total: 5, immediats: 3, supplementaires: 2 },
    },

    _meta: {
      derniereMAJ: '2026-04-25',
      sourceMAJ: 'CGSLB CP 336 (cp-336-conditions-de-travail-et-remuneration 11/02/2026) — accord sectoriel 2025-2026',
      indexationFrequence: 'Annuelle au 01/01 (plafonnée 3 500 € brut)',
      indexationQ1_2026: 0.020,
      classification: '6 classes barémiques (C1 administratif → C6 partner/associé, plafond 5 500 € brut)',
      notes: 'CP 336 = TES PROSPECTS DIRECTS (fiduciaires IPCF/ITAA, cabinets comptables, avocats, notaires). Au-delà de 5 500 € brut, beaucoup passent en indépendant exclusif (facturation 65-90 €/h). Voiture société quasi-systématique dès Medior (3 ans). Budget Mobilité fédéral disponible.',
      nouveautes2026: [
        'Indexation au 01/01/2026 plafonnée 3 500 € brut (accord sectoriel)',
        'Train 80% au 01/01/2026 (avec recommandation tiers payant SNCB)',
        'Vélo 0,10 €/km jusqu\'au 30/09/2026, puis 0,32 €/km au 01/10/2026 (max 12,80 €/jour)',
        '+2 jours sectoriels deuil (12j conjoint/enfant, 5j parent)',
        'Au-delà 5 500 € brut, passage indépendant fréquent',
      ],
    },
  },

  // CP 327 — ETA/maatwerkbedrijven
  '327': {
    nom: 'CP 327 — Entreprises de travail adapté (ETA)',
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 125 CCT de la CP 327 le 23/08/2026 (jcId 3270000). Les seuls textes « cotisation » fixent la cotisation patronale au FSE (2004-2005, périmés — voir fondsSE.historiquePerime). Rien d'autre de sectoriel.",
      dateVerif: '2026-08-23',
    },
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'Q2 2026: +2% ETA/maatwerk (suit pivot public). Source: CSC, SD Worx CP 327' },
    // 🔴 CP MÈRE — CE TAUX N'A PLUS DE CCT DERRIÈRE LUI (verifie 21/08/2026).
    // Banque CCT du SPF Emploi, CP 327 : les dernieres conventions sur le fonds
    // datent de 2003-2005, et celle du 16/02/2010 est une « denonciation
    // effective au 01/01/2011 » de la CCT du 17/09/2005 (n° 70649/CO/327). Rien
    // depuis. Les fonds VIVANTS sont ceux des trois sous-CP (327.01, 327.02,
    // 327.03), chacun avec sa propre CCT et son propre taux.
    //
    // ⚠️ LA VALEUR EST LAISSEE TELLE QUELLE, ET C'EST DELIBERE : `payroll-engine`
    // tronque la CP a 3 chiffres, donc c'est CE taux qu'il applique a TOUS les
    // travailleurs ETA, sous-CP comprises. La mettre a 0 ou a null ferait
    // disparaitre d'un coup la cotisation de tout le secteur — un changement de
    // coût patronal massif, base sur une absence de CCT plutot que sur une CCT
    // qui dit zero. Le vrai correctif est de faire lire la SOUS-CP au moteur
    // (zone protegee, cf. `__tests__/sous-cp-fonds-ignore-par-le-moteur.test.js`).
    // ── FAÎTIÈRE : LES TROIS SOUS-COMMISSIONS SONT SOURCÉES (26/08/2026) ──────
    // 📉 3,00 % → 0. Ce bloc portait deux réserves, toutes deux levées :
    //
    //  1. « Aucune CCT en vigueur ne fixe ce taux. » Toujours vrai — les
    //     dernières conventions du fonds de la CP 327 datent de 2003-2005, et
    //     celle du 16/02/2010 DÉNONCE la CCT du 17/09/2005 (70649/CO/327) avec
    //     effet au 01/01/2011. Le fonds de la mère est éteint depuis quinze
    //     ans. Lu à la banque CCT du SPF le 21/08/2026.
    //  2. « Valeur conservée uniquement parce que le moteur rabat toutes les
    //     sous-CP ici. » ⚠️ CE N'EST PLUS VRAI. La troncature a été levée
    //     conditionnellement le 25/08 : `blocFondsSE` lit la sous-CP dès que
    //     son taux est PROUVÉ (`cctSourcee`, `onssSourcee` ou
    //     `ecartOnssExplique`). Les TROIS sous-commissions portent désormais
    //     `onssSourcee` — 327.01 le 25/08, 327.03 le 25/08, 327.02 le 26/08.
    //     Mettre la mère à 0 ne retire donc la cotisation de personne.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE : une ETA relève d'un
    // régime communautaire précis, c'est une donnée du dossier. Même règle que
    // les CP 125, 142, 149, 152, 303, 318, 328 et 329.
    fondsSE: {
      nom: 'Entreprises de travail adapté — faîtière : le taux vit dans les sous-commissions',
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        // ⚠️ Le terme néerlandais de l'intitulé officiel est écarté ici : ce
        // champ est AFFICHÉ, et le gate i18n interdit un mot d'une autre langue
        // dans un libellé rendu à l'écran.
        '327.01': "Secteur flamand — entreprises de travail adapté et ateliers sociaux — 0,60 % ouvrier et employé",
        '327.02': "ETA bruxelloises agréées par la Commission communautaire française — 0,18 % ouvrier et employé",
        '327.03': "ETA de la Région wallonne et de la Communauté germanophone — 5,00 % ouvrier et employé, sauf les ETA germanophones non redevables (catégorie ONSS 073, aucune cotisation)",
      },
      source: "Banque CCT du SPF Emploi : le fonds de la CP 327 elle-même est éteint — dernières conventions 2003-2005, et la CCT du 16/02/2010 dénonce celle du 17/09/2005 (dépôt 70649/CO/327) avec effet au 01/01/2011 ; lu le 21/08/2026. Les trois sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS (catégories 373/473, 173 et 273).",
      dateVerif: '2026-08-26',
      note: "Le 3,00 % qui figurait ici n'avait aucune source. Il était conservé faute de mieux tant que le moteur rabattait les sous-CP sur la mère — ce n'est plus le cas depuis le 25/08/2026.",
      // ⚠️ CONSERVÉ EN RÉÉCRIVANT CE BLOC LE 26/08 : la seule CCT de la CP 327
      // qui CHIFFRE une cotisation au FSE. Elle est PÉRIMÉE et ne doit jamais
      // servir de taux courant — mais l'effacer ferait croire à la session
      // suivante que personne n'a cherché.
      historiquePerime: {
        taux: 0.0010,
        base: 'salaires bruts à 108 %',
        periode: 'du 01/01/2005 au 31/12/2006 inclus — EXPIRÉE, ne pas utiliser comme taux courant',
        percepteur: 'ONSS',
        source: 'CCT du 22/12/2004, art. 2 — banque CCT du SPF Emploi, 327/327-2005-000429.pdf, texte primaire lu le 23/08/2026',
        dateVerif: '2026-08-23',
      },
      pisteEcarteeEtPourquoi:
        "Le 0,10 % de `historiquePerime` est le seul chiffre jamais fixé pour le fonds de la mère. Il a expiré fin 2006, et la CCT du 16/02/2010 a dénoncé le régime : il ne peut pas servir de taux en vigueur. Balayage des 125 CCT de la CP 327 le 23/08/2026 (jcId 3270000) : rien d'autre de sectoriel.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (la CP 327
    // mère est éteinte depuis 2010-2011, aucune CCT active). Easypay Group,
    // fiche PC=327.00 : liste exhaustive de 4 documents sectoriels, aucun
    // chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: "Easypay Group (fiche PC=327.00) — cohérent avec l'extinction du fonds mère depuis 2010-2011",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 327 + « eco cheque » :
    // 0 résultat. Cohérent avec fondsSE ci-dessus : CP 327 est une faîtière,
    // taux réel dans les sous-commissions 327.01/327.02/327.03 — à vérifier
    // séparément.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 327 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (faîtière, sous-CP 327.01/327.02/327.03 à vérifier séparément)",
    ecoChequesDateVerif: '2026-09-21',
    primeFDA: { type: '1 mois brut', prorata: true },
    formation: { jours: 5, fonds: 'Fonds 327' },

    // ── Indemnités CP 327 — ajoutées le 23/08/2026 ───────────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 04/06/2020, n° 159537/CO/327, banque CCT
    // du SPF Emploi (327/327-2020-008107.pdf), art. 1 à 9.
    //
    // 🛑 CHAMP RESTREINT, ÉCRIT DANS LA DONNÉE. Cette CCT ne vise QUE les
    // sociétés d'initiatives de développement de l'emploi dans le secteur des
    // services de proximité à finalité sociale AGRÉÉES PAR LA RÉGION WALLONNE
    // (IDESS). Elle ne couvre ni les ETA flamandes, ni les bruxelloises, ni
    // le reste de la CP 327. La servir comme le régime de toute la CP serait
    // la faute du sous-secteur Viande de la CP 118.
    indemnites: {
      perimetreSource: "IDESS wallonnes UNIQUEMENT — sociétés d'initiatives de développement de l'emploi dans les services de proximité à finalité sociale agréées par la Région wallonne ; ne vaut pas pour le reste de la CP 327",
      source: 'CCT du 04/06/2020, n° 159537/CO/327, art. 1 à 9 — 327/327-2020-008107.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',

      fraisDeTransport: {
        // ⚠️ R1 : le barème n'est PAS recopié ici. Il vit dans la CCT n° 19/9
        // du CNT, que la CCT sectorielle désigne — une seule vérité.
        train: "intervention calculée sur le tableau des montants forfaitaires de l'art. 3 de la CCT n° 19/9 du CNT",
        autresTransportsPublics: "mêmes dispositions de remboursement que le chemin de fer (CCT n° 19/9)",
        transportPrive: {
          droit: "même intervention que pour le chemin de fer, pour les JOURS EFFECTIVEMENT PRESTÉS",
          formuleParJour: "tarif de la carte-train mensuelle divisé par 21",
          distance: "itinéraire le plus court entre le lieu où le travailleur vit habituellement et l'entreprise ou le chantier",
          justificatif: "une déclaration signée du travailleur indiquant la distance remplace l'attestation de transport ; l'employeur peut contrôler à tout moment",
        },
        dueDes: 'le premier jour de travail (art. 7)',
        paiement: "une fois par mois pour les abonnements mensuels, sinon à la période de paie usuelle de l'entreprise",
        // ⚠️ L'exception qui fait tomber tout le reste.
        exception: "ne s'applique PAS à l'employeur qui organise TOTALEMENT le transport à ses frais, et seulement si le travailleur en bénéficie GRATUITEMENT. Transport partiel ou au départ d'un lieu déterminé → la CCT s'applique au prorata de la distance, à régler par convention d'entreprise (art. 8)",
      },

      fraisDeMission: {
        // ⚠️ R1 : renvoi au tarif de la fonction publique, jamais recopié —
        // il est indexé et vit dans `fraisPropres` / `LOIS_BELGES`.
        vehiculeMotorise: "indemnité kilométrique au tarif en vigueur dans la FONCTION PUBLIQUE",
        velo: "au tarif en vigueur pour les vélos",
        couvre: 'frais de kilomètre et parking, dûment justifiés',
        source: 'art. 9',
      },
    },

    // ── Heures supplémentaires CP 327 — ce qu'on sait, et ce qu'on ne sait pas
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter) — d'où le détail de
    // la recherche, pour que la session suivante ne la refasse pas à l'identique.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux propre à la CP 327 n'a été trouvé",
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 3270000',
        quand: '2026-08-23',
        combien: '125 conventions listées, 24 titres retenus au filtre (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation, indemnité, frais, prime)',
        resultat: "aucune ne fixe de taux d'heures supplémentaires ; les titres retenus portent sur les congés, la prime de fin d'année, la prime syndicale et les frais de transport",
        reste: "recherche par TITRE uniquement, à reprendre par thème ou texte intégral. Les trois sous-CP (327.01/02/03) ont leurs propres CCT, non balayées ici.",
      },
      source: "absence de taux sectoriel constatée sur les 125 CCT listées de la CP 327 le 23/08/2026",
      dateVerif: '2026-08-23',
    },
  },
  // ── SANTÉ / NON-MARCHAND (IFIC) — indexation INDEX-PIVOT (indice santé) ──
  // Couverture vague 1 (2026-07-16) : ces CP ne s'indexent PAS à date fixe mais à
  // chaque franchissement de l'indice-pivot (+2%). Vérifié : +2% au 01/05/2026
  // (pivot avril 2026) ; historique +2% au 01/01/2026 (pivot déc. 2025). Cap
  // « centimes » (4000€) depuis 01/06/2026. Sources R2 : FWPS fwpsante.be,
  // CSC/ACV CNE (barèmes IFIC), IFIC if-ic.org, SD Worx. Grilles IFIC déjà
  // vérifiées (baremes-cp-grid, 05-10/06) → valeurs à jour ; ici = le CALENDRIER.
  // Surveillance = détection de pivot (crons index-sante / auto-indexation), pas
  // un fetch par CP (secteur au pivot). R1 : aucune valeur de barème recopiée ici.
  //
  // ✅ ÉCO-CHÈQUES DÉJÀ RÉSOLUS SUR CES 9 SOUS-CP — CORRIGÉ LE 24/09/2026.
  // La ligne ci-dessous (« dette ouverte ») écrite plus tôt le même jour était
  // une FAUSSE alerte : la question était déjà tranchée juste au-dessus dans
  // ce fichier, sur la fiche CP 330 (mère) — `ecoChequesAnnuel: null`,
  // confirmé DEUX FOIS (22/08 puis recoupé le 22/09/2026, cct-search.yml run
  // 35711203975, 4 orthographes standard), en vérifiant EXPLICITEMENT que ça
  // couvre les dix populations IFIC. Ces 9 sous-CP n'ont pas leur propre clé
  // `ecoChequesAnnuel` : elles héritent celle de leur mère via
  // `ficheEffective()` (app/lib/cp-sous-division.js) — une seule vérité, dix
  // réponses. Deux recherches CCT par code numérique avaient échoué (filtre
  // « commission paritaire » du SPF resté bloqué sur un ancien code) ; ce
  // n'était pas la voie à emprunter de toute façon — 330.01.xx ne sont PAS de
  // vraies commissions paritaires (liste officielle SPF, 22/08/2026) et
  // AUCUNE CCT ne peut jamais être déposée sous ces codes ni sous leur nom :
  // les CCT du secteur santé se déposent exclusivement sous le numéro de la
  // CP mère, 330. Chercher « par nom de sous-commission » n'aurait donc rien
  // trouvé de plus que la recherche déjà faite sur 330 elle-même.
  '330.01.10': { nom: 'CP 330.01.10 — Hôpitaux privés et MSP (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.20': { nom: 'CP 330.01.20 — Maisons de repos / MRS / résidences-services (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.30': { nom: 'CP 330.01.30 — Soins infirmiers à domicile (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.41': { nom: 'CP 330.01.41 — Centres de revalidation (NL) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.42': { nom: 'CP 330.01.42 — Centres de revalidation (FR) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.50': { nom: 'CP 330.01.50 — Établissements de santé bicommunautaires (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.60': { nom: 'CP 330.01.60 — Établissements de santé (NL) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.70': { nom: 'CP 330.01.70 — Établissements de santé (FR) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.80': { nom: 'CP 330.01.80 — Crèches et milieux d\'accueil (NL) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  '330.01.90': { nom: 'CP 330.01.90 — Crèches et milieux d\'accueil (FR) (IFIC)', nonMarchand: true, indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fédéral : +2% à chaque pivot. Dernier 01/05/2026. FWPS/CSC/IFIC, vérifié 2026-07-16.' } },
  // CP 331 — Vlaamse welzijn- en gezondheidssector
  '331': {
    // ⚠️ CLÉ ABSENTE JUSQU'AU 24/08/2026 — la fiche ne disait NI oui NI non
    // sur les éco-chèques : `statutEcoCheques` rendait « inconnu ». Relevé
    // à la source par le thème 247 de la banque CCT du SPF.
    // 🪤 PIÈGE D'ÉCRITURE : les clés '331' et '332' existent AUSSI dans
    // `BAREMES_CP_MIN`, plus haut dans le fichier. Un `^  '332':` non ancré
    // sur l'export tombe sur la MAUVAISE — l'écriture part alors dans un
    // objet que personne ne lit pour cette donnée, et rien ne le signale.
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 03/07/2023 (CP 331, soins de santé flamand), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : éco-chèques à titre UNIQUE en juillet 2023, valeur totale 250 EUR pour un temps plein. ⚠️ VERSEMENT UNIQUE ET ÉCHU, pas un régime annuel. La fiche annonçait 250 EUR/an — le montant du versement unique de 2023, reconduit à tort en droit permanent.", ecoChequesDateVerif: '2026-08-24',
    nom: 'CP 331 — Vlaamse welzijn- en gezondheidssector',
    // ── Régime hebdomadaire CP 331 — NON TROUVÉ, et le dire précisément ─
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter). Le balayage est
    // écrit pour que la session suivante cherche AILLEURS.
    regimeHebdoRecherche: {
      resultat: 'aucune CCT sectorielle ne fixe la durée hebdomadaire',
      ou: 'banque CCT du SPF Emploi, jcId 3310000',
      quand: '2026-08-23',
      combien: "212 conventions listées, balayées sur arbeidsduur / durée du temps de travail / temps de travail / réduction du temps — ZÉRO résultat",
      autreVoie: "la grille vérifiée (baremes-cp-grid) ne porte pas non plus `heuresSemaine` pour cette CP",
      reste: "à chercher hors banque CCT : accords du non-marchand flamand (VIA), ou fiche SPF « Salaires minimums » si elle existe pour la 331",
      dateVerif: '2026-08-23',
    },
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 212 CCT de la CP 331 le 23/08/2026 (jcId 3310000). Les textes « cotisation » portent sur le FSE (CCT du 02/03/2026, voir fondsSE.cctSourcee) et sur l'AFFECTATION du produit de la cotisation de responsabilisation, qui est fédérale. Rien de sectoriel en propre.",
      dateVerif: '2026-08-23',
    },
    indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand flamand : +2% à chaque franchissement du pivot (pas un calendrier fixe). Dernier +2% au 01/05/2026 (pivot avril 2026) ; hist. +2% au 01/01/2026. Sources CSC/ACV CP 331, IFIC, vérifié 2026-07-16.' },
    // 🔴 ÉCART MAJEUR, MESURÉ LE 23/08/2026 — LE TAUX CODÉ N'EST PAS TOUCHÉ.
    // La CCT du 02/03/2026 fixe le pourcentage des cotisations au fonds de
    // sécurité d'existence POUR L'ANNÉE 2026, et elle est explicite : « par
    // trimestre 0,22 % du montant brut des rémunérations, avant retenue des
    // cotisations personnelles de sécurité sociale ». La perception est
    // décalée : rien aux 1er et 2e trimestres, 0,44 % aux 3e et 4e.
    // Nous codons 5 %. Sur un brut de 3 000 €/mois, le taux codé facture de
    // l'ordre de 1 800 €/an par travailleur là où la CCT en donne ~79 €.
    // ⛔ Valeur laissée telle quelle : zone protégée, et la neutraliser d'un
    // coup changerait le coût patronal affiché à tous les clients du secteur.
    // Arbitrage fondateur (00.02, ligne « fonds de sécurité d'existence »).
    fondsSE: {
      nom: 'Sociaal Fonds 331',
      taux_patronal: 0.05,
      aVerifier: true,
      // La raison vivait sous `cctSourcee.ecartAvecTauxCode`, imbriquée d'un
      // niveau : le balayage `fonds-se-assiette-declaree` ne l'y voyait pas, et
      // un écran non plus. Remontée en clair le 26/08/2026 — cette CP est l'un
      // des quatre arbitrages qui attendent le fondateur, elle doit se lire
      // sans creuser.
      aVerifierRaison: "Le 5 % codé est d'un ORDRE DE GRANDEUR au-dessus du taux conventionnel : la CCT du 02/03/2026, art. 2 (dépôt 331/331-2026-002983, texte primaire lu le 23/08/2026) fixe 0,22 % par trimestre — aucune perception aux 1er et 2e trimestres, 0,44 % aux 3e et 4e. Sur un brut de 2 500 €, 125 €/mois affichés contre environ 11 €/mois réellement dus. ARBITRAGE FONDATEUR : appliquer la CCT DIVISE par onze le coût sectoriel annoncé — c'est visible pour un client, donc ça ne se fait pas sans décision.",
      cctSourcee: {
        tauxParTrimestre: 0.0022,
        perception: 'aucune aux 1er et 2e trimestres ; 0,44 % aux 3e et 4e trimestres',
        base: 'montant brut des rémunérations, AVANT retenue des cotisations personnelles de sécurité sociale',
        annee: 2026,
        cadre: 'exécute la CCT n° 103527/CO/331, rendue obligatoire par AR du 04/10/2011 (M.B. 08/12/2011)',
        source: 'CCT du 02/03/2026, art. 2 — banque CCT du SPF Emploi, 331/331-2026-002983.pdf, texte primaire lu le 23/08/2026',
        dateVerif: '2026-08-23',
        ecartAvecTauxCode: "le taux codé (5 %) est d'un ordre de grandeur au-dessus du taux conventionnel (0,22 %/trimestre)",
      },
    },
    chequesRepas: { obligatoire: false, note: 'Selon CCT entreprise' },
    primeFDA: { type: '~90% salaire brut mensuel', prorata: true },
    formation: { jours: 5, fonds: 'VIVO (vivosocialprofit.org)' },

    // ── Heures supplémentaires CP 331 — ce qu'on sait, et ce qu'on ne sait pas
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter). Le détail de la
    // recherche est écrit ici pour que la session suivante reprenne AILLEURS
    // plutôt que de refaire la même requête.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux propre à la CP 331 n'a été trouvé",
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 3310000',
        quand: '2026-08-23',
        combien: '212 conventions listées, 32 titres retenus au filtre large (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation, irrégulière, toeslag)',
        resultat: "aucune ne fixe de taux ni de supplément d'horaire ; les titres qui contiennent « nuit » sont tous des régimes de chômage avec complément d'entreprise (métier lourd, travail de nuit), pas des sursalaires",
        reste: "recherche par TITRE uniquement, à reprendre par thème ou texte intégral",
      },
      source: "absence de taux sectoriel constatée sur les 212 CCT listées de la CP 331 le 23/08/2026",
      dateVerif: '2026-08-23',
    },

    // ── Indemnités CP 331 — ajoutées le 23/08/2026 ───────────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 19/06/2023, banque CCT du SPF Emploi
    // (331/331-2023-010568.pdf), art. 1 à 3. Elle remplace l'art. 11 de la
    // CCT du 26/05/2009 (n° 94368/CO/331). En vigueur depuis le 01/07/2023,
    // durée indéterminée.
    indemnites: {
      source: 'CCT du 19/06/2023, art. 3 — 331/331-2023-010568.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',

      deplacementDeService: {
        // ⚠️ C'EST LE MÉCANISME QUI FAIT FOI, PAS LE NOMBRE (R1). La CCT
        // renvoie à l'art. 74, § 1er de l'AR du 13/07/2017 fixant les
        // allocations du personnel de la fonction publique fédérale, ET DIT
        // ELLE-MÊME que « le montant de l'indemnité kilométrique est revu
        // CHAQUE TRIMESTRE ». Servir le nombre ci-dessous comme le montant du
        // jour serait servir une valeur périmée de plusieurs années.
        base: "art. 74, § 1er de l'AR du 13/07/2017 fixant les allocations et indemnités des membres du personnel de la fonction publique fédérale",
        revision: 'chaque trimestre',
        montantALEntreeEnVigueur: 0.4259,   // EUR/km au 01/07/2023 — HISTORIQUE
        montantALEntreeEnVigueur_date: '2023-07-01',
        avertissement: "0,4259 €/km est la valeur du 01/07/2023, citée par la CCT à titre indicatif. Le montant applicable est celui du trimestre en cours selon l'AR du 13/07/2017 — à lire à la source, jamais ici.",
        condition: "utilisation par le travailleur de son propre moyen de transport MOTORISÉ, pour raisons de service",
        source: 'art. 3',
      },
    },
    nonMarchand: true,
  },
  // CP 332 — Non-marchand francophone
  '332': {
    // ⚠️ CLÉ ABSENTE JUSQU'AU 24/08/2026 — la fiche ne disait NI oui NI non
    // sur les éco-chèques : `statutEcoCheques` rendait « inconnu ». Relevé
    // à la source par le thème 247 de la banque CCT du SPF.
    // 🪤 PIÈGE D'ÉCRITURE : les clés '331' et '332' existent AUSSI dans
    // `BAREMES_CP_MIN`, plus haut dans le fichier. Un `^  '332':` non ancré
    // sur l'export tombe sur la MAUVAISE — l'écriture part alors dans un
    // objet que personne ne lit pour cette donnée, et rien ne le signale.
    ecoChequesAnnuel: 200, ecoChequesSource: "CCT du 20/12/2022 (CP 332, accueil de l'enfance francophone), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : le montant MAXIMUM d'éco-chèques octroyables est de 200 EUR, calculé par la formule « 200 EUR × A », où A est le régime de travail du travailleur au 15 décembre. Valeur nominale maximale 10 EUR par chèque. ⚠️ C'est un PLAFOND proratisé, pas un dû fixe. La fiche portait 250 EUR.", ecoChequesDateVerif: '2026-08-24',
    nom: 'CP 332 — Aide sociale et soins de santé (fr./germ.)',
    // ── Régime hebdomadaire CP 332 — NON TROUVÉ pour le régime de BASE ──
    // ⚠️ ET C'EST UNE DISTINCTION QUI COMPTE. Le balayage a bien trouvé une
    // CCT de « réduction collective du temps de travail » (13/05/2022), mais
    // elle vise les travailleurs ÂGÉS — 36 h à 55 ans, 34 h à 58 ans, 32 h à
    // 60 ans et plus. La coder comme le régime hebdomadaire du secteur ferait
    // annoncer 32 h à un travailleur de 30 ans. Elle est donc rangée en
    // `avantages`, où elle est vraie, et le régime de base reste inconnu.
    regimeHebdoRecherche: {
      resultat: 'aucune CCT ne fixe la durée hebdomadaire de BASE',
      ou: 'banque CCT du SPF Emploi, jcId 3320000',
      quand: '2026-08-23',
      combien: '222 conventions listées ; les 2 résultats du filtre « temps de travail » portent sur la réduction liée à l\'âge et sur une prime compensatoire',
      nePasConfondreAvec: 'avantages.reductionTempsTravailAge — 36/34/32 h selon l\'âge, ce n\'est PAS le régime de base',
      reste: "à chercher hors banque CCT : accords du non-marchand (APEF/FE-BI), ou fiche SPF « Salaires minimums »",
      dateVerif: '2026-08-23',
    },

    // ── Avantage sectoriel CP 332 — ajouté le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 13/05/2022 (332/332-2022-009994.pdf),
    // « Réduction collective du temps de travail avec embauche compensatoire
    // — Région wallonne ».
    avantages: {
      reductionTempsTravailAge: {
        paliers: [
          { desAge: 55, heuresSemaine: 36 },
          { desAge: 58, heuresSemaine: 34 },
          { desAge: 60, heuresSemaine: 32 },
        ],
        aTempsPartiel: 'la diminution se calcule proportionnellement par rapport à 36, 34 ou 32 heures',
        plafond: "de commun accord entre les parties, le temps de travail peut être maintenu à maximum 36, 34 ou 32 heures par semaine, dans le respect de la procédure de communication de l'art. 6",
        contrepartie: 'embauche compensatoire',
        portee: 'Région wallonne',
        source: 'CCT du 13/05/2022 — banque CCT du SPF Emploi, 332/332-2022-009994.pdf, texte primaire lu le 23/08/2026',
        dateVerif: '2026-08-23',
      },
    },
    // ✅ CELLE-CI EXISTE VRAIMENT, et c'est la seule des huit CP examinées le
    // 23/08/2026. Le cadre est fédéral (groupes à risque, art. 189 al. 4 de la
    // loi du 27/12/2006) mais le TAUX et l'AFFECTATION sont fixés par la CCT
    // sectorielle — donc elle a sa place ici.
    cotisationSpeciale: {
      libelle: 'cotisation complémentaire pour des initiatives de formation en faveur des groupes à risque',
      tauxParTrimestre: 0.0010,
      annees: [2025, 2026],
      base: "salaire global des travailleurs, au sens de l'art. 23 de la loi du 29/06/1981 établissant les principes généraux de la sécurité sociale des travailleurs",
      affectation: "50 % des montants perçus vont aux jeunes de moins de 26 ans relevant des groupes à risque définis par l'AR du 19/02/2013 (exécution de l'art. 189, al. 4, de la loi du 27/12/2006)",
      source: 'CCT du 22/11/2024, art. 4 — banque CCT du SPF Emploi, 332/332-2024-014486.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
    },
    indexation: { frequence: 'index-pivot (indice santé)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Non-marchand fr./germ. : +2% à chaque franchissement du pivot (pas un calendrier fixe). Dernier +2% au 01/05/2026 (pivot avril 2026) ; hist. +2% au 01/01/2026 (pivot déc. 2025). Sources FWPS fwpsante.be, CSC CNE CP 332, IFIC, vérifié 2026-07-16.' },
    fondsSE: {
      nom: 'Fonds Maribel social',
      // ✅ CE QUE LA CAMPAGNE CCT N'AVAIT PAS TROUVÉ, LE PERCEPTEUR LE DIT.
      // Le balayage du 25/08 (ci-dessous) avait conclu qu'aucune convention de la
      // CP 332 ne fixe un taux GLOBAL — c'était exact, et c'est resté vrai. Mais
      // l'ONSS perçoit bien une cotisation pour cette CP : 0,15 % (catégories 022
      // et 222). La recherche n'avait pas échoué, elle cherchait au mauvais endroit.
      taux_patronal: 0.0015,
      taux_patronal_employe: 0.0015,
      base108Ouvrier: true,
      onssCategorie: '022, 222',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégories d'employeur 022 et 222 : 0,15 % pour les deux publics. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation prélevée directement par le fonds, hors DmfA, ne serait pas visible ici.",
      note: "Valeur précédente : 5,00 %, sans source. ⚠️ Le nom codé — « Fonds Maribel social » — désigne un dispositif de RÉDUCTION de cotisations, pas un fonds de sécurité d'existence : la fiche mérite toujours d'être reprise à la racine.",
      // La recherche CCT est conservée : elle dit ce qui a été ouvert, et pourquoi
      // le taux ne s'y trouvait pas. C'est ce qui évite de la refaire.
      lectureFaite: {
        source: "Balayage des 29 conventions des themes 229/230/277 pour la CP 332, banque CCT du SPF Emploi, le 25/08/2026. Les 5 a portee generale fixant une cotisation : 332/332-2024-014483.pdf, -2022-014909, -2022-014910, -2021-013752 et anterieures — formation, emploi, groupes a risque.",
        dateVerif: '2026-08-25',
        conclusion: "aucune convention fixant un taux GLOBAL ; le taux servi vient donc du fichier des taux de l'ONSS, pas d'une CCT.",
      },
    },
    chequesRepas: { obligatoire: false },
    primeFDA: { type: 'Prime via fonds', prorata: true },
    formation: { jours: 5, fonds: 'FE-BI / APEF' },

    // ── Heures supplémentaires CP 332 — ce qu'on sait, et ce qu'on ne sait pas
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter). Le détail de la
    // recherche est écrit ici pour que la session suivante reprenne AILLEURS
    // plutôt que de refaire la même requête.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux propre à la CP 332 n'a été trouvé",
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 3320000',
        quand: '2026-08-23',
        combien: '222 conventions listées, 43 titres retenus au filtre large (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation, irrégulière, toeslag)',
        resultat: "aucune ne fixe de taux ni de supplément d'horaire ; les titres qui contiennent « nuit » sont tous des régimes de chômage avec complément d'entreprise (métier lourd, travail de nuit), pas des sursalaires",
        reste: "recherche par TITRE uniquement, à reprendre par thème ou texte intégral. 🔎 PISTE OUVERTE : les CCT « Mise en oeuvre de l'accord-cadre tripartite pour le secteur non-marchand » (la plus récente du 15/04/2025) n'ont pas été lues — un accord-cadre peut porter des mesures barémiques ou de supplément",
      },
      source: "absence de taux sectoriel constatée sur les 222 CCT listées de la CP 332 le 23/08/2026",
      dateVerif: '2026-08-23',
    },

    // ── Indemnités CP 332 — ajoutées le 23/08/2026, VOLONTAIREMENT SANS
    // MONTANT, et la raison est écrite plutôt que sous-entendue.
    //
    // ✅ Lu : CCT du 18/10/2013, n° 122063/CO/332 (332/332-2013-010146.pdf).
    // Ce texte n'est PAS une convention de fond : c'est une extension de
    // champ. Son art. 2 remplace l'art. 1er de la CCT du 26/02/1991
    // (n° 26908/CO/305.02) pour l'étendre aux établissements NON soumis à la
    // loi sur les hôpitaux. Les montants, eux, vivent dans la CCT de 1991,
    // qui n'a pas été lue.
    //
    // ⛔ On n'écrit donc AUCUN chiffre. Une fiche honnêtement incomplète vaut
    // infiniment mieux qu'un montant plausible (R12).
    indemnites: {
      pointeurNonChiffre: true,
      fraisDeTransport: {
        texteDeFond: 'CCT du 26/02/1991, n° 26908/CO/305.02 (AR du 11/07/1991) — NON LUE au 23/08/2026',
        extensionDeChamp: "CCT du 18/10/2013, n° 122063/CO/332, art. 2 : étend le champ aux établissements ne relevant PAS de la loi sur les hôpitaux",
        aFaire: "lire la CCT de 1991 pour relever les montants ; vérifier au passage si une CCT postérieure les a indexés",
      },
      source: 'banque CCT du SPF Emploi, jcId 3320000 — 222 conventions listées, 3 sur les frais de transport, la plus récente (18/10/2013) lue le 23/08/2026',
      dateVerif: '2026-08-23',
    },
    nonMarchand: true,
  },
  // CP 119 — Commerce alimentaire
  '119': {
    nom: 'CP 119 — Commerce alimentaire',
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 333 CCT de la CP 119 le 23/08/2026 (banque CCT du SPF, jcId 1190000). La seule qui parle de cotisation est celle du 05/03/2026 sur « l'utilisation de la cotisation de responsabilisation » — or cette cotisation est FÉDÉRALE (art. 147, §§ 4 et 5 de la loi-programme du 27/12/2021) ; la CCT ne fixe aucun taux, elle désigne seulement le fonds de sécurité d'existence compétent pour en recevoir le produit. Rien de sectoriel au-delà du FSE.",
      dateVerif: '2026-08-23',
    },
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Q2 2026: +2,21% commerce alimentaire. Source: SD Worx, CGSLB CP 119', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },
    fondsSE: {
      nom: 'Fonds social commerce alimentaire',
      taux_patronal: 0.0059,
      // Le côté employé n'est PAS ambigu par accident : cette CP ne couvre pas
      // les employés. Ils relèvent d'une autre commission, que l'ONSS nomme dans
      // la description de chaque catégorie d'employeur. Leur cotisation se calcule
      // sur la fiche de LEUR CP — servir le taux ouvrier ici le multiplierait par dix.
      employesRelevantDUneAutreCp: ['200', '201', '202'],
      base108Ouvrier: true,
      onssCategorie: '057, 157, 257',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégories d'employeur 057, 157 et 257 : 0,59 % pour les ouvriers (code 820) dans les trois, sans écart. Lu le 25/08/2026.",
      note: "Valeur précédente : 5,00 %, sans source. ⚠️ La CP 119 est une commission d'OUVRIERS : les employés du même employeur relèvent de la CP 201, de la CP 202 ou des moyennes entreprises d'alimentation selon le cas — l'ONSS le dit dans la description de chaque catégorie. Leur cotisation se calcule sur la fiche de LEUR CP.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (10,00 €) était le plafond
    // fédéral recopié. Partena Professional, tableau officiel « Adaptations
    // salariales à partir du 1er avril 2026 », ligne CP 119.00, corroboré
    // Monizze + CGSLB : applicable UNIQUEMENT aux entreprises de 20 ouvriers
    // ou plus au 31/03/2026 — introduction à 2,59 € pour celles qui n'en
    // accordaient pas encore, majoration de +1,50 € pour celles qui en
    // accordaient déjà. Aucune obligation sectorielle sous 20 ouvriers.
    chequesRepas: {
      obligatoire: 'conditionnel', valeur: 2.59, part_employeur: 1.50, part_travailleur: 1.09,
      conditionnelRaison: "applicable seulement si l'entreprise compte 20 ouvriers ou plus au 31/03/2026 ; le montant ci-dessous est celui de l'INTRODUCTION (entreprise qui n'en accordait pas encore) — les entreprises qui en accordaient déjà avant cette date augmentent leur part patronale de 1,50 € plutôt que de repartir de ce montant",
      source: 'Partena Professional, tableau « Adaptations salariales avril 2026 », ligne CP 119.00, corroboré Monizze + CGSLB',
      dateEffet: '2026-04-01',
      dateVerif: '2026-09-05',
    },
    // 🟠 CORROBORÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi,
    // mais SANS lire la CCT fondatrice elle-même. Le mécanisme est un régime à
    // DEUX VOIES : la CCT du 28/05/2009 (« betreffende de ecocheques ») établit
    // l'éco-chèque sectoriel ; celle du 06/10/2011 permet à l'entreprise de le
    // CONVERTIR en un autre avantage ou en +1,08 EUR de chèque-repas — un choix
    // fait une fois, qui fixe ensuite le barème salarial applicable (« barèmes
    // I » = converti, « barèmes II » = non converti), art. 2-3 de la CCT
    // salariale du 05/03/2026 (119-2026-003122.pdf, en vigueur). Ni la CCT de
    // 2009 ni celle de 2011 n'ont été retrouvées elles-mêmes par la recherche
    // plein texte (26 résultats, aucun titré « éco-chèques »). Le montant de
    // 250 EUR est corroboré par l'accord sectoriel 2019-2020
    // (119-2019-014643.pdf) qui l'utilise comme option de conversion d'une
    // prime : « 250 euro ecocheques en een brutopremie van 58 euro ». Valeur
    // plausible pour les entreprises « barèmes II » (non converti), non
    // confirmée sur le texte fondateur lui-même.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 05/03/2026 (119-2026-003122.pdf) + accord sectoriel 2019-2020 (119-2019-014643.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : régime à deux voies (éco-chèques CCT du 28/05/2009, convertible en autre avantage via CCT du 06/10/2011). 250 EUR corroboré par l'accord 2019-2020 comme option de conversion ; CCT fondatrice de 2009 non retrouvée par la recherche plein texte.",
    ecoChequesDateVerif: '2026-09-18',
    ecoChequesAVerifier: true,
    primeFDA: { type: '1 mois brut', prorata: true },
    formation: { jours: 5, fonds: 'IFP Commerce' },

    // ── Heures supplémentaires CP 119 — ajoutées le 23/08/2026 ───────────
    // ✅ DEUX TEXTES PRIMAIRES LUS dans la banque CCT du SPF Emploi :
    //   · CCT du 10/12/2025, n° 196823/CO/119 (119/119-2025-014683.pdf)
    //   · CCT du 20/10/2025, n° … (119/119-2025-013468.pdf), « Durée du travail »
    //
    // ⚠️ LE SECTEUR NE TOUCHE PAS AUX TAUX. Il agit sur deux autres leviers,
    // et c'est là que se joue l'argent : le QUOTA d'heures autorisées, et
    // surtout l'ASSIETTE sur laquelle l'heure supplémentaire se compte.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — la CP 119 ne fixe pas de taux propre",

      quotaSurcroitDeTravail: {
        heures: 91,                       // par année de référence
        anneeDeReference: 'du 1er avril au 31 mars',
        cas: "surcroît extraordinaire de travail et nécessité imprévue (art. 25 et 26, § 1er, 3° de la loi du 16/03/1971)",
        condition: "il ne doit pas être possible de faire face au travail supplémentaire par des embauches supplémentaires",
        paiementAuLieuDeRecuperation: "possible, en concertation avec l'ouvrier, APRÈS que la délégation syndicale a constaté l'impossibilité de récupérer",
        chargeDeLaPreuve: "l'employeur doit pouvoir prouver à tout moment, par tous moyens, qu'il était impossible d'y faire face par des embauches",
        validite: 'du 01/07/2025 au 30/06/2027',
        source: 'CCT du 10/12/2025, n° 196823/CO/119, art. 2 à 5 — 119/119-2025-014683.pdf, texte primaire lu le 23/08/2026',
      },

      // ⭐ LA RÈGLE QU'ON SE TROMPE FACILEMENT À APPLIQUER.
      assietteTransport: {
        regle: "pour les ouvriers occupés à des travaux de TRANSPORT, l'employeur doit la rémunération effective pour la TOTALITÉ DU TEMPS DE PRÉSENCE ; et pour l'application de l'art. 29, les heures supplémentaires se calculent PAR RAPPORT AU TEMPS DE PRÉSENCE",
        exclusion: "les temps de repos prévus au règlement de travail pendant lesquels l'ouvrier est autorisé à abandonner la surveillance du véhicule ne comptent pas comme temps de présence",
        validite: 'du 01/11/2025 au 31/10/2027',
        source: 'CCT du 20/10/2025 « Durée du travail », art. 2 — 119/119-2025-013468.pdf, texte primaire lu le 23/08/2026',
      },

      perimetreSource: "à l'exclusion des boucheries, charcuteries et triperies (art. 1er des deux CCT)",
      source: 'Banque CCT du SPF Emploi, jcId 1190000 — 333 conventions listées, textes retenus et lus le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Indemnités sectorielles CP 119 — ajoutées le 23/08/2026 ──────────
    // ✅ CCT du 10/12/2025, n° 196827/CO/119 (119/119-2025-014687.pdf),
    // texte primaire lu. Elle REMPLACE la CCT du 05/12/2023 (184660/CO/119).
    indemnites: {
      perimetreSource: "commerce alimentaire À L'EXCLUSION des boucheries, charcuteries et triperies (art. 1er)",
      source: 'CCT du 10/12/2025, n° 196827/CO/119 — 119/119-2025-014687.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',

      primeEquipe: {
        montantHoraire: 0.34,             // EUR/heure, dès le 01/01/2026
        depuis: '2026-01-01',
        plages: { matin: '06:00-14:00', apresMidi: '14:00-22:00' },
        plagesSupplantables: "sauf stipulation contraire du règlement de travail",
        // ⚠️ L'exclusion est dans le même article : ne pas la perdre.
        nonCumulable: "ne s'applique PAS aux heures pour lesquelles l'ouvrier bénéficie déjà d'une prime de travail de nuit, de la prime d'après-midi ou de la prime pour ouvertures tardives",
        source: 'art. 2',
      },

      primeApresMidi: {
        montantHoraire: 0.34,             // EUR/heure, dès le 01/01/2026
        depuis: '2026-01-01',
        aQui: "ouvriers dont les prestations commencent à 13 h ou plus tard",
        aPartirDe: '14:00',               // la prime ne court qu'à partir de 14 h
        nonCumulable: "ni avec les primes de travail de nuit ou d'ouvertures tardives, ni avec les primes d'équipe existantes",
        source: 'art. 3',
      },

      // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter). Les primes de
      // nuit et d'ouvertures tardives sont CITÉES par cette CCT comme
      // existantes, mais leur montant est fixé ailleurs : il n'est pas codé
      // ici, et l'écran ne doit pas laisser croire que la liste est complète.
      citeesMaisNonChiffrees: ['prime de travail de nuit', 'prime pour ouvertures tardives'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BATCH AJOUT 29/04/2026 — 12 CP au vert Q2 2026 (audit demande Nourdin)
  // Sources : CSC CNE indexation calendar 2026, SD Worx, CGSLB, Acerta, Partena
  // ═══════════════════════════════════════════════════════════════════════════

  // CP 109 — Habillement et confection (ouvriers)
  '109': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 18/02/2026 (CP 109), banque CCT
    // du SPF Emploi, 109/109-2026-002606.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 18/02/2026 (CP 109) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 109/109-2026-002606.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 109 — Habillement et confection (ouvriers)',
    nomCourt: 'Habillement',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', dernierTaux: 0.0143, derniereDate: '2026-04-01', source: 'AUDIT 2026-06 — CGSLB/Easypay' },
    fondsSE: {
      nom: 'Fonds social CP 109',
      taux_patronal: 0.03,
      taux_patronal_employe: 0.0098,
      base108Ouvrier: true,
      onssCategorie: '038',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 038 : 820 ouvriers 3,00 % · employés 0,98 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était le plafond
    // fédéral recopié {8/6.91/1.09}. Synova (ex-CGSLB) : « Chaque travailleur a
    // droit à un chèque-repas par jour de travail effectif », augmentation de
    // 1,25 € au 01/04/2026 → 6,25 € (5,16 € employeur). Aligné sur la valeur
    // déjà appliquée à la CP 215 (employés, même régime), pour cohérence.
    // 🟠 Confiance moyenne : source secondaire unique.
    chequesRepas: {
      obligatoire: true, valeur: 6.25, part_employeur: 5.16, part_travailleur: 1.09, aVerifier: true,
      source: 'Synova (ex-CGSLB), habillement-confection, confiance moyenne',
      dateEffet: '2026-04-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 109 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 109 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (au 31/12, anc. min 6 mois)',
    formation: { jours: 4, fonds: 'IVOC (Institut Vocational Confection)' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 109: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB CP 109 + IVOC + salairesminimums.be',
      indexationFrequence: 'Semestrielle (avril + octobre)',
      derniereIndexation: '2025-04-01 (+1,73%)',
      classification: '5 catégories (manœuvre → spécialisé)',
      notes: 'Secteur en repli — délocalisations. CP 215 = équivalent employés.',
    },
  },

  // CP 120 — Industrie textile (ouvriers)
  '120': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 20/12/2023 (CP 120), banque
    // CCT du SPF Emploi, 120/120-2023-016965.pdf, art. 2. Mot pour mot :
    //   « Dans chaque entreprise … occupant habituellement en moyenne au moins
    //     30 ouvriers et dont un certain nombre sont affiliés à un syndicat
    //     signataire, il peut être institué une délégation syndicale … »
    //
    // ⚠️ « HABITUELLEMENT EN MOYENNE » : ce n'est pas l'effectif d'un jour. Et
    // la seconde condition — « un certain nombre » d'affiliés — n'est PAS
    // chiffrée par le texte. Ne pas lui inventer un pourcentage.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_moyen_et_affiliation',
        seuilOuvriers: 30,
        modeCalcul: 'effectif occupé habituellement en moyenne',
        affiliationRequise: 'un certain nombre d\'ouvriers affiliés à un syndicat signataire — le texte ne chiffre PAS ce nombre',
        source: 'CCT du 20/12/2023 (CP 120) — statut de la délégation syndicale, banque CCT du SPF Emploi, 120/120-2023-016965.pdf, art. 2. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 120 — Industrie textile (ouvriers)',
    nomCourt: 'Textile',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'Q2 2026: +2% textile (dépassement indice pivot). Source: SD Worx, CGSLB CP 120/214' },
    // ⚠️ 5 % INVENTÉ → 1,20 %, LU AU TEXTE PRIMAIRE (26/08/2026).
    // Statuts coordonnés du 25/02/2026 (dépôt 198826/CO/120, banque CCT du SPF
    // Emploi, 120/120-2026-003282.pdf) :
    //   · art. 6 § 1 : « Le Fonds est financé par une cotisation patronale de
    //     3,17 % sur les salaires bruts à 100 % » ;
    //   · art. 6 § 2 : « À partir du premier jusqu'au quatrième trimestre 2026,
    //     la réduction de cotisation en cours de 0,47 % est portée à 1,97 % ».
    //     → CE QUI EST RÉELLEMENT PERÇU EN 2026 : 3,17 − 1,97 = **1,20 %**.
    //   · art. 7 § 1 : « 1,20 point de pourcentage de la cotisation patronale
    //     visée à l'article 6 est réorienté afin de financer le régime de
    //     pension complémentaire sectoriel social pour les ouvriers ».
    //
    // ⚠️ LA RÉDUCTION A UN CALENDRIER, ET IL FAUT LE CODER AVEC LE TAUX. Elle
    // retombe à 1,50 % de 2027 à 2032 (taux perçu 1,67 %) puis à 0,50 % à
    // partir de 2033 (taux perçu 2,67 %). Un taux qui change à date connue
    // n'est pas une valeur, c'est un calendrier : l'écrire sans ses échéances
    // garantit qu'il sera périmé au 1er janvier 2027 sans que personne ne le
    // voie. La sentinelle a de quoi le rattraper.
    //
    // 🟠 Curiosité assumée, écrite plutôt que tue : en 2026 le taux perçu
    // (1,20 %) est exactement le point réorienté vers le second pilier (art. 7).
    // Le fonds de sécurité d'existence stricto sensu n'encaisse donc rien cette
    // année-là. Ça ne change pas le coût de l'employeur — il verse 1,20 % — mais
    // ça explique pourquoi l'ONSS ne publie pas de ligne 820 pour cette CP.
    fondsSE: {
      nom: "Fonds de sécurité d'existence pour l'industrie textile",
      taux_patronal: 0.012,
      base: 'salaires bruts à 100 %',
      cctSourcee: {
        taux: 0.012,
        tauxNominal: 0.0317,
        reductionEnCours: 0.0197,
        base: 'salaires bruts à 100 %',
        depuis: '2026-01-01',
        calendrier: {
          '2026': { reduction: 0.0197, tauxPercu: 0.012 },
          '2027-2032': { reduction: 0.015, tauxPercu: 0.0167 },
          'des-2033': { reduction: 0.005, tauxPercu: 0.0267 },
        },
        reorienteVersPensionComplementaire: 0.012,
        source: "Statuts coordonnes du 25/02/2026, art. 6 §§ 1-2 et art. 7 § 1 — depot 198826/CO/120, banque CCT du SPF Emploi, 120/120-2026-003282.pdf, texte primaire lu le 26/08/2026. Le 5 % qui figurait ici n'avait aucune source.",
        dateVerif: '2026-08-26',
      },
      note: "Le taux percu change a date connue : 1,67 % de 2027 a 2032, 2,67 % des 2033. A reverifier au 1er janvier 2027.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. ACCG (Centrale Générale-FGTB) +
    // ACV-CSC METEA concordants : 7,20 € (6,11 € employeur) au 01/02/2026,
    // comptage par jour effectivement presté (comptage horaire pour équipes-relais).
    chequesRepas: {
      obligatoire: true, valeur: 7.20, part_employeur: 6.11, part_travailleur: 1.09,
      source: 'ACCG (Centrale Générale-FGTB) + ACV-CSC METEA, publication sectorielle textile 2023-2024',
      dateEffet: '2026-02-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 120 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 120 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'COBOT (Fonds textile)' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      equipe2x8: { taux: 0.10, label: 'Prime équipe 2x8 (+10%)' },
      equipe3x8: { taux: 0.15, label: 'Prime équipe 3x8 (+15%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 120: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'SD Worx CP 120 + CGSLB + salairesminimums.be',
      indexationFrequence: 'Annuelle (janvier — variable selon pivot)',
      derniereIndexation: '2026-01-01 (+2%)',
      notes: 'CP 120 = ouvriers. CP 214 = employés. CP 120.01 = Verviers, 120.03 = sacs jute.',
    },
  },

  // CP 116 — Industrie chimique (ouvriers)
  '116': {
    nom: 'CP 116 — Industrie chimique (ouvriers)',
    nomCourt: 'Chimie ouvriers',
    regimeHebdo: 40,                                     // Régime spécifique 40h/semaine
    indexation: {
      indicePivot: 97.56, indicePivotBase: '2025', indicePivotSource: "CCT du 17/03/2026 (CP 116, industrie chimique), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Les memes montants correspondent a l'indice pivot 97,56 en base 2025 = 100. »", indicePivotDateVerif: '2026-08-24', frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── 0,40 % DES BRUTS NON PLAFONNÉS, PERÇUS EN TROIS FOIS ──────────────
    // ✅ CCT du 13/02/2026 « Fonds social — Cotisation 2026 », art. 2 — dépôt
    // 198276/CO/116, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026 :
    // « le montant de la cotisation pour l'exercice 2026 est fixé à 0,40 % des
    // salaires bruts NON PLAFONNÉS […]. La cotisation susmentionnée de 0,40 %
    // sera perçue comme suit : 0,40 % au 2e trimestre 2026 ; 0,80 % au 3e
    // trimestre 2026 ; 0,40 % au 4e trimestre 2026. »
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — douze fois trop.
    //
    // 📐 NE PAS ADDITIONNER LES TROIS PRÉLÈVEMENTS. 0,40 + 0,80 + 0,40 = 1,60,
    // mais chaque prélèvement porte sur la masse salariale de SON trimestre :
    // rapporté à l'année, cela revient bien aux 0,40 % que l'article annonce.
    // C'est le taux annuel qui est codé — le profil trimestriel est écrit
    // ci-dessous pour qu'un rapprochement de compte ne surprenne personne.
    // ⚠️ Rien n'est perçu au 1er trimestre : un employeur qui compare sa paie
    // de mars à celle de septembre verra un facteur deux, et c'est normal.
    fondsSE: {
      nom: "Fonds social de l'industrie chimique (ouvriers)",
      taux_patronal: 0.004,
      base: 'salaires bruts NON PLAFONNÉS',
      cctSourcee: {
        taux: 0.004,
        base: 'salaires bruts non plafonnés (art. 2)',
        profilTrimestriel: { T1: 0, T2: 0.004, T3: 0.008, T4: 0.004 },
        perceptionParOnss: true,
        source: "CCT du 13/02/2026 (CP 116, industrie chimique — ouvriers), art. 2 — dépôt 198276/CO/116, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Elle exécute l'art. 4 des statuts du fonds (CCT du 21/05/2019, dépôt 152823/CO/116). Exception : art. 4bis de la CCT du 12/04/1972, non lu.",
        dateVerif: '2026-08-26',
        perimetre: 'ouvriers uniquement (art. 1er)',
      },
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT de cotisation est annuelle (art. 3)",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source. Le pendant EMPLOYÉS est la CP 207, dont le fonds perçoit un forfait de 70 € par an et par tête — deux moitiés du même secteur, deux mécaniques de financement entièrement différentes.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, dateApplication: '2026-04-01', note: 'Introduction obligatoire 2€ patronal au 01/04/2026 (accord 2025-2026)' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Deux CCT trouvées, TOUTES DEUX « éénmalig(e) » (versement UNIQUE) :
    // art. 22 de la CCT du 21/12/2017 (116-2018-001802.pdf) — 150 EUR pour la
    // seule année 2018 (référence 01.12.2017-30.11.2018) · accord du
    // 08/07/2009 (116-2009-002431.pdf) — 250 EUR au 01/01/2010, une seule
    // fois. Aucune des deux n'établit de droit ANNUEL récurrent ; aucune CCT
    // plus récente trouvée par la recherche plein texte.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 21/12/2017 (116-2018-001802.pdf, art. 22) et accord du 08/07/2009 (116-2009-002431.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : versements UNIQUES (« éénmalig(e) »), 150 EUR pour 2018 et 250 EUR au 01/01/2010. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Co-valent (fonds chimie)' },
    cotisationSpeciale: null,
    primesEquipe2026: {
      matin_apresmidi: 0.95,                             // EUR/h, augmenté de 0,95€ au 01/01/2026
      nuit: 3.10,                                        // EUR/h prime nuit minimum sectorielle
      note: 'Régime 40h/semaine. Source: Acerta accord 2025-2026',
    },
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 40,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30, veloLimburg: { premiers40km: 0.37, audela: 0.27 } },  // vélo CP 116: 0,30 €/km autres provinces (CCT n°164 supplétive) ; Limburg 0,37 €/km (40 premiers km A/R) puis 0,27 — CCT 17/03/2026 (ACV-CSC METEA), sourcé 2026-07-08
    indemniteChomageTemporaire: { montant: 14.50, dateDebut: '2026-01-01', dateAugmentation: '2026-12-01', montantAugmente: 15.00, joursMax: 65 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Acerta + Partena + CGSLB CP 116/207 + ACCG/FGTB guide ouvriers 2025-2026',
      indexationFrequence: 'CCT progressive (+0,10€/h au 01/01) + variations pivot',
      derniereIndexation: '2026-01-01',
      classification: 'Régime 40h/semaine spécifique',
      notes: 'Accord sectoriel 2025-2026 conclu. Prime fin de carrière 80€/mois dès 60 ans (ou 55 métier lourd) à partir 01/01/2026.',
      nouveautes2026: [
        '+0,10€/h salaire min départ et 12 mois ancienneté',
        '+0,95€/h prime équipe matin/AM',
        '+3,10€/h prime nuit',
        'Chèques-repas 2€ patronal obligatoire dès 01/04/2026',
        'Indemnité chômage temporaire 14,50€/jour (15€ dès 12/2026)',
        'Prime fin carrière +80€/mois 1/5 et 1/2',
      ],
    },
  },

  // CP 207 — Industrie chimique (employés)
  '207': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 20/01/2026 (CP 207), statut
    // de la délégation syndicale, banque CCT du SPF Emploi,
    // 207/207-2026-000484.pdf. Mot pour mot : « … unités techniques
    // d'exploitation comptant au moins 30 employés visés par la convention
    // collective du travail du 17 janvier 1947 … et à condition qu'au moins
    // 25 % de ce personnel en formule la demande par écrit … ».
    //
    // ⚠️ DOUBLE CONDITION, ET LES DEUX COMPTENT : un effectif ET une demande
    // écrite. Retenir le seul « 30 » ferait croire que la délégation s'impose
    // dès 30 employés, ce qui est faux.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_et_demande',
        seuilEmployes: 30,               // au niveau de l'unité technique d'exploitation
        pctDemandeursRequis: 0.25,       // demande écrite d'au moins 25 % du personnel
        delegueJeunes: 'Un délégué des jeunes peut être désigné si l\'unité technique d\'exploitation occupe au moins 30 jeunes employés de moins de 21 ans.',
        source: 'CCT du 20/01/2026 (CP 207) — statut de la délégation syndicale, banque CCT du SPF Emploi, 207/207-2026-000484.pdf. Texte primaire lu.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 207 — Industrie chimique (employés)',
    nomCourt: 'Chimie employés',
    regimeHebdo: 40,
    indexation: { frequence: 'CCT_progressive', mois: [1], augmentationCCT: 17.333, augmentationCCTUnit: 'EUR/mois', derniereDate: '2026-01-01', dernierTaux: 0.02, note: 'Q2 2026: +17,333€/mois CCT au 01/01/2026 + indexation pivot publique. Sources: Acerta, Partena' },
    // ── UN FORFAIT DE 70 € PAR EMPLOYÉ ET PAR AN, PAS UN POURCENTAGE ──────
    // ✅ CCT du 20/01/2026 « Fonds social employés — cotisation 2026 », art. 2 —
    // dépôt 197644/CO/207, banque CCT du SPF Emploi, 207/207-2026-000488.pdf,
    // texte primaire lu le 26/08/2026 : « le montant de la cotisation […] est
    // fixé à 70 € par travailleur sous contrat de travail d'employé […]. Cette
    // cotisation de 70 € […] est due et sera perçue exclusivement pendant le
    // deuxième trimestre de l'année 2026. Cette cotisation sera perçue par
    // l'Office National de Sécurité Sociale. »
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI SE TROMPAIT DE NATURE. Sur un brut de
    // 2 500 €, il facturait 112,50 € par MOIS — soit 1 350 € par an — là où
    // l'employeur doit 70 € par an et par employé. Dix-neuf fois trop.
    //
    // ⛔ D'OÙ LE 0 dans `taux_patronal` : ce fonds ne perçoit aucun pourcentage
    // du brut. Le forfait, lui, est écrit ci-dessous — il n'est pas facturé par
    // l'app, qui calcule au mois et non à l'effectif annuel.
    fondsSE: {
      nom: "Fonds social pour les employés de l'industrie chimique",
      taux_patronal: 0,
      cctSourcee: {
        taux: 0,
        raison: "Ce fonds ne perçoit aucune cotisation proportionnelle au brut : sa ressource est un forfait annuel par employé. Un taux de 0 est la réponse JUSTE à « quel pourcentage du brut ? », pas un aveu d'ignorance — le montant réel est écrit sous `cotisationForfaitaire`.",
        source: "CCT du 20/01/2026 (CP 207, employés de l'industrie chimique), art. 2 — dépôt 197644/CO/207, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Elle exécute l'art. 4 des statuts du fonds (CCT du 21/05/2019, dépôt 152824/CO/207, AR du 03/12/2019, MB du 23/12/2019).",
        dateVerif: '2026-08-26',
      },
      cotisationForfaitaire: {
        montantAnnuelParEmploye: 70.00,
        perception: "perçue par l'ONSS, exclusivement pendant le DEUXIÈME TRIMESTRE 2026",
        exception: "sauf application de l'art. 4bis, alinéas 3 et 4 des statuts du fonds — non lus",
        exercice: 2025,
        note: "Le montant est refixé chaque année par une CCT distincte (millésimes 2023 et 2024 également présents dans la banque). À revérifier au premier trimestre 2027.",
      },
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait un fonds à pourcentage, ce que ce fonds n'est pas.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, dateApplication: '2026-04-01', note: 'Introduction obligatoire 2€ patronal au 01/04/2026 (accord 2025-2026)' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Seul texte trouvé : accord sectoriel 2009-2010 (207/…pdf, expiré le
    // 31/03/2011) — « eenmalig » (versement UNIQUE) de 250 EUR au 01/01/2010
    // pour les employés barémisés non couverts par une CCT d'entreprise.
    // Aucune CCT plus récente trouvée par la recherche plein texte.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Accord sectoriel 2009-2010 (207/…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : versement UNIQUE (« eenmalig ») de 250 EUR au 01/01/2010, expiré. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Co-valent (fonds chimie)' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 40,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 207: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    indemniteChomageTemporaire: { montant: 14.50, dateDebut: '2026-01-01', dateAugmentation: '2026-12-01', montantAugmente: 15.00, joursMax: 65 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Acerta + Partena + CGSLB CP 207',
      indexationFrequence: 'CCT progressive + variations pivot',
      nouveautes2026: [
        '+17,333€/mois salaire min lié à expérience',
        'Chèques-repas 2€ patronal obligatoire dès 01/04/2026',
        'Indemnité licenciement éco. élargie aux employés barémisés 01/01/2026',
      ],
    },
  },

  // CP 125 — Industrie du bois (ouvriers)
  '125': {
    nom: 'CP 125 — Industrie du bois (exploitations forestières, scieries, commerce)',
    nomCourt: 'Bois',
    regimeHebdo: 38,
    indexation: { frequence: 'trimestrielle', dernierTaux: 0.0021, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── FAÎTIÈRE : LE TAUX VIT DANS LES SOUS-COMMISSIONS, TOUTES SOURCÉES ──
    // 📉 6,0 % → 0 le 26/08/2026, et c'est le cas le plus net de la série.
    // Cette commission n'a AUCUNE convention sous son propre numéro dans la
    // banque CCT du SPF — trois sous-commissions sur trois portent, elles, un taux
    // lu à la source, de 10,28 % à 15,50 %. Le nombre rond de la mère n'était donc ni
    // l'un ni l'autre : il valait deux fois moins l'un d'eux, et il ne servait
    // qu'aux dossiers codés sur la commission mère, sans sous-commission.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE, et on ne fait pas de
    // moyenne : un employeur relève d'une sous-commission précise, c'est une
    // donnée du dossier. La fiche renvoie donc, et le calcul rend 0 tant qu'on
    // ne sait pas laquelle — même règle que la CP 152, posée le 25/08.
    fondsSE: {
      nom: "Industrie du bois — faîtière : le taux vit dans les sous-commissions",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '125.01': "Exploitations forestières — 15,50 %",
        '125.02': "Scieries et industries connexes — 10,47 %",
        '125.03': "Commerce du bois — 10,28 %",
      },
      source: "Banque CCT du SPF Emploi : aucune convention publiée sous le numéro 125 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Les sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS ou dans leur convention.",
      dateVerif: '2026-08-26',
      note: "Le 6,0 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (aucune
    // convention publiée sous le numéro 125 lui-même). Cohérent aussi avec le
    // tableau Partena « adaptations salariales avril 2026 », où '125.00'
    // n'apparaît pas comme ligne autonome. Chaque sous-CP (125.01/02/03) porte
    // sa propre réponse ci-dessous.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — la CP 125 mère n'a pas de CCT propre",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 125 + « eco cheque » :
    // 0 résultat. Cohérent avec chequesRepas ci-dessus : la CP 125 mère n'a pas
    // de CCT propre, les sous-CP (125.01/02/03) portent leur propre réponse.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 125 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut',
    formation: { jours: 4, fonds: 'WoodWize' },
    cotisationSpeciale: null,
    sousSecteurs: {
      '125.01': 'Exploitations forestières',
      '125.02': 'Scieries et industries connexes',
      '125.03': 'Commerce du bois',
    },
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 125.03: 0,32 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'SD Worx CP 125 + WoodWize + salairesminimums.be',
      indexationFrequence: 'Annuelle janvier',
      notes: '3 sous-CP : 125.01 forestières, 125.02 scieries, 125.03 commerce.',
    },
  },

  // CP 126 — Ameublement et industrie transformatrice du bois
  '126': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 02/06/2026 (CP 126), banque CCT
    // du SPF Emploi, 126/126-2026-013187.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 02/06/2026 (CP 126) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 126/126-2026-013187.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 126 — Ameublement et industrie transformatrice du bois',
    nomCourt: 'Ameublement',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Q2 2026: +2,21% ameublement (indexation annuelle janvier). Source: SD Worx CP 126', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },
    fondsSE: {
      nom: 'Fonds ameublement (Bosfond/WoodWize)',
      taux_patronal: 0.1545,
      taux_patronal_employe: 0.0023,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '055',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 055. Codes travailleur : 820 ouvriers 15,45 % · 830 employés 0,23 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Ameublement »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Valeur codée précédente : 6,00 %. Écart ouvrier/employé considérable — servir le taux ouvrier à un employé le multiplierait par 67.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, dateReferenceRegimes: '2026-05-31', source: "CCT du 01/04/2026 (CP 126), dépôt 199306, banque CCT du SPF, texte primaire lu le 24/08/2026, art. 3. DEUX RÉGIMES IMPOSÉS PAR LA CONVENTION : §1 « Employeurs qui n'octroient pas encore de chèques-repas au 31 mai 2026 : à partir du 1er juin 2026, l'employeur octroie des chèques-repas avec une intervention patronale de 2 euros et une intervention minimale du travailleur de 1,09 euro par chèque. » §2 « Employeurs qui octroient déjà des chèques-repas au 31 mai 2026 : l'employeur augmente l'intervention patronale de 2 euros au 1er juin 2026 » (les distinctions entre travailleurs fondées sur des critères objectifs restent applicables). §3 pour les entreprises ayant converti tout ou partie des écochèques, l'introduction ou l'augmentation est LIMITÉE À 1,50 €. La valeur codée est celle du §1. Portée : toute la CP.", regimes: [ { cas: 'nouvel-octroi', depuis: '2026-06-01', libelle: { fr: "Entreprise qui n'octroyait pas de chèques-repas au 31/05/2026", nl: 'Onderneming die op 31/05/2026 geen maaltijdcheques toekende' }, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09 }, { cas: 'deja-octroye', depuis: '2026-06-01', libelle: { fr: 'Entreprise qui en octroyait déjà au 31/05/2026', nl: 'Onderneming die er op 31/05/2026 al toekende' }, supplementPatronal: 2.00 } ], regimeSecondaire: "ECOCHEQUES CONVERTIS (art. 3 §3) : pour les entreprises qui ont converti tout ou partie des ecocheques, l'introduction ou l'augmentation de l'intervention patronale au 01/06/2026 est limitee a 1,50 EUR au lieu de 2 EUR.", dateVerif: '2026-08-24', enVigueurLe: '2026-06-01', lien: '126/126-2026-005313.pdf' },
    // 🟠 VÉRIFIÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // La recherche plein texte ne ramène qu'UN texte : la CCT du 25/11/2021
    // (126-2021-…pdf), chapitre II bis, « écochèque UNIQUE et EXCEPTIONNEL
    // 2021 » (art. 2bis) — 100 EUR versés une seule fois, pour compenser un
    // retard d'indexation salariale (référence 01/01-31/10/2021), expiré
    // (fin de validité 01/01/2023). Aucune CCT établissant un droit ANNUEL
    // récurrent n'a été retrouvée. Le mécanisme de CONVERSION cité par la CCT
    // chèques-repas ci-dessus (art. 3 §3, « entreprises ayant converti tout ou
    // partie des écochèques ») confirme l'EXISTENCE d'un régime sous-jacent,
    // sans en donner le montant. 250 EUR reste la valeur PLAUSIBLE (défaut
    // interprofessionnel CCT n°98, comme dans la majorité des CP), mais NON
    // confirmée sur un texte primaire propre à la CP 126.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 25/11/2021 (chap. II bis, art. 2bis), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : seul texte trouvé, écochèque UNIQUE de 100 EUR (2021, expiré). Régime de conversion confirmé par la CCT chèques-repas (art. 3 §3), montant annuel non retrouvé — 250 EUR = défaut interprofessionnel plausible, non confirmé pour cette CP.",
    ecoChequesDateVerif: '2026-09-18',
    ecoChequesAVerifier: true,
    primeFinAnnee_pct: 8.33,
    formation: { jours: 4, fonds: 'WoodWize' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 126: 0,32 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'SD Worx CP 126 + WoodWize',
      indexationFrequence: 'Annuelle janvier',
    },
  },

  // CP 130 — Imprimerie, arts graphiques et journaux
  '130': {
    nom: 'CP 130 — Imprimerie, arts graphiques et journaux',
    nomCourt: 'Imprimerie',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Q2 2026: +2,21% imprimerie/arts graphiques. Source: Febelgra (fédération secteur)' },
    fondsSE: {
      nom: 'Fonds graphique (Febelgra)',
      taux_patronal: 0.0131,
      taux_patronal_employe: 0.0023,
      // Forfaits en euros par travailleur — ils ne se multiplient pas par le brut.
      forfaitsOnss: [{"code": "827", "montant": 34}],
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "820", "type": "5", "taux": 1.51, "forfait": false}, {"code": "827", "type": "8", "taux": 0, "forfait": true}],
      base108Ouvrier: true,
      onssCategorie: '036',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 036 : 820 ouvriers 1,31 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ COMPLÉTÉ LE 18/09/2026 (partiellement — la CP couvre deux
    // sous-segments, 130.00a labeur/imprimerie et 130.00b presse
    // quotidienne, non codés séparément) : recherche plein texte CP 130 +
    // « chèque repas » dans la banque CCT du SPF. Pour 130.00a (majorité du
    // secteur), CCT du 26/02/2026 (n°198631/CO/130, dépôt 11/03/2026,
    // enregistrement 17/03/2026), art. 3 : « à partir du 1er janvier 2026,
    // 5,25 EUR/chèque-repas, composé d'une part personnelle d'1,09 EUR et
    // d'une part patronale de 4,16 EUR » — remplace le triplet recopié
    // 8/6,91/1,09. Pour 130.00b (presse quotidienne, art. 1er de cette même
    // CCT l'exclut explicitement), seule une majoration a été retrouvée
    // (« Modification… du 19/11/2009 », CCT 198825/CO/130 du 26/02/2026 :
    // +1 EUR employeur au 01/01/2026 pour les entreprises qui accordent déjà
    // des chèques-repas) — PAS le montant de base, qui reste 🔴 à vérifier.
    chequesRepas: {
      obligatoire: true, valeur: 5.25, part_employeur: 4.16, part_travailleur: 1.09,
      source: "CCT du 26/02/2026 (n°198631/CO/130, dépôt 11/03/2026, enreg. 17/03/2026), art. 3 — banque CCT SPF Emploi",
      dateVerif: '2026-09-18',
      exclusions: "Ne vise que le sous-segment labeur/imprimerie (130.00a). Les entreprises de presse quotidienne (130.00b, art. 1er de cette CCT) ont leur propre régime, majoré de +1 EUR/chèque employeur au 01/01/2026, mais le montant de BASE de ce sous-segment n'a pas été retrouvé — à vérifier avant de leur opposer ce montant.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 130 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 130 — recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 2 CCT, dont « éco-chèques » du 10/09/2009 (dépôt 95846), ÉCHUE au 31/12/2009. Aucune CCT postérieure sur le sujet : aucun montant sectoriel en vigueur.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'CEFORA Graphique' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.24, primeRepasSupplementaire: 5.68 },  // vélo CP 130: 0,24 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelgra (fédération graphique) + accord sectoriel 2025-2026',
      indexationFrequence: 'Annuelle janvier (moyenne indice lissé nov+déc / nov+déc N-1)',
      notes: 'CCT 2025-2026 valide jusqu\'au 31/12/2026. Indemnité repas 5,68€/jour si prestation +2h non prévenue.',
    },
  },

  // CP 304 — Spectacle (employés)
  '304': {
    nom: 'CP 304 — Spectacle (Communauté française et germanophone)',
    nomCourt: 'Spectacle',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'Q2 2026: +2% spectacle (premier mois après dépassement pivot public). Source: CSC CNE, salairesminimums.be CP 304' },
    fondsSE: {
      nom: 'Fonds social spectacle',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      pension_sectorielle: 0.015,
      pension_sectorielle_employe: 0.015,
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "825", "type": "8", "taux": 0, "forfait": false}],
      base108Ouvrier: true,
      onssCategorie: '562, 662',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 562, 662 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, note: 'Selon CCT entreprise' },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 304 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 304 — recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 7 CCT, dont 5 sur les éco-chèques. Le secteur les a octroyés par CCT ANNUELLES successives (2012, 2013, prolongée 2016, 2017, 2018) ; la dernière — CCT du 21/11/2018, dépôt 149469/CO/304, texte primaire lu le 21/09/2026 : « paiement d'un écochèque, à raison de 10 EUR, par jour travaillé (ou assimilé) » — est ÉCHUE au 31/12/2018. Aucune CCT postérieure : aucun montant sectoriel en vigueur.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 5, fonds: 'Fonds spectacle (Mediarte)' },
    cotisationSpeciale: null,
    salaireBaseMultiplicateur: 1.3728,                   // Salaire de base × 1,3728 (au 01/02/2026)
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 304: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    contratsSpecifiques: {
      artiste: 'Statut artiste (Loi 2024) — RPI cotisations spéciales',
      technicien: 'CDD ou intérim',
      musicien: 'CCT 28/01/2005 — exclu barèmes standards',
    },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE indexation 2026 + salairesminimums.be CP 304',
      indexationFrequence: 'Pivot public (1er mois après dépassement)',
      derniereIndexation: '2026-02-01',
      notes: 'CP francophone/germanophone. Suppression flexi-salaire spécifique 01/01/2024. Musiciens et Opéra Royal Wallonie exclus.',
      nouveautes2026: [
        'Indexation +2% au 01/02/2026',
        'Salaire base × 1,3728 (multiplicateur indexé)',
        'Statut artiste 2024 applicable (RPI)',
      ],
    },
  },

  // CP 306 — Assurances (employés)
  '306': {
    nom: 'CP 306 — Entreprises d\'assurances (employés)',
    nomCourt: 'Assurances',
    regimeHebdo: 36,                                     // Régime 36h/semaine secteur
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'Q2 2026: +2,23% assurances. Indexation barémique. Source: CSC CNE, CGSLB CP 306, SD Worx', formule: 'sante_lisse_dec' },
    fondsSE: {
      nom: 'Fonds social assurances',
      taux_patronal: 0.003,
      taux_patronal_employe: 0.003,
      base108Ouvrier: true,
      onssCategorie: '130',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 130 : 820 ouvriers 0,30 % · employés 0,30 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (10,00 €) était le plafond
    // fédéral recopié {10/8.91/1.09}. Synova, Easypay Group et SD Worx/Ella
    // (3 sources concordantes) : fiches exhaustives du secteur assurances
    // sans aucune mention de chèque-repas — avantages via CCT d'entreprise.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova + Easypay Group + SD Worx/Ella, fiches CP 306, exhaustives et concordantes',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (306/306-2011-009120.pdf, CCT du 10/10/2011, dépôt 107051/CO/306,
    // « over de toekenning van ecocheques vanaf 2012 », durée indéterminée).
    // ⚠️ CE N'EST PAS UNE OBLIGATION GÉNÉRALE DU SECTEUR : l'art. 2 réserve
    // les 190 EUR/an aux seuls travailleurs auxquels NE s'appliquent PAS les
    // augmentations barémiques sectorielles (art. 4) ni l'augmentation
    // forfaitaire (art. 5) de l'accord sectoriel 2011-2012 — donc à une
    // sous-population, pas à tous les employés de la CP 306. Valeur nominale
    // max. 10 EUR/coupure (art. 7), octroi au plus tard le 31 mars de l'année
    // concernée (art. 4). Remplace le régime antérieur (306/306-2010-000575.pdf :
    // 125 EUR en 2009, 250 EUR en 2010, ponctuel).
    ecoChequesConditionnel: {
      montant: 190,
      condition: "Réservé aux travailleurs NON couverts par les augmentations barémiques sectorielles ou l'augmentation forfaitaire de l'accord 2011-2012 (art. 2 et 6 de l'accord sectoriel). Ne pas l'annoncer comme une obligation générale du secteur.",
      source: "CCT du 10/10/2011 (306/306-2011-009120.pdf), banque CCT du SPF Emploi, dépôt 107051/CO/306, texte primaire lu le 18/09/2026, art. 2 (190 EUR/an « vanaf 2012 », durée indéterminée), art. 7 (max. 10 EUR/coupure).",
      dateVerif: '2026-09-18',
    },
    // 🔴 CORRIGÉ LE 24/09/2026 — `ecoChequesAnnuel: 250` recopié était resté
    // à côté du `ecoChequesConditionnel` ci-dessus malgré la recherche du
    // 18/09 : aucune CCT ne donne 250 EUR à toute la CP 306, seul le régime
    // conditionnel à 190 EUR existe. Confirmé une 2e fois indépendamment le
    // 24/09/2026 (audit 20.25, relecture directe de la même CCT).
    ecoChequesAnnuel: null,
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (13e mois)',
    primeSyndicale: { existe: true, montant: 145, prorata: true },
    formation: { jours: 5, fonds: 'IFAPME assurances' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 306: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE indexation calendar 2026 + CGSLB CP 306 + SD Worx',
      indexationFrequence: 'Annuelle janvier (barémique uniquement)',
      derniereIndexation: '2026-01-01 (+2,23%)',
      notes: 'Indexation s\'applique uniquement aux salaires barémiques. Différence avec salaire effectif maintenue. Régime 36h/semaine.',
    },
  },

  // CP 310 — Banques (employés)
  '310': {
    nom: 'CP 310 — Banques (employés)',
    nomCourt: 'Banques',
    regimeHebdo: 35,                                     // Régime 35h/semaine secteur bancaire
    indexation: { frequence: 'plurielle', mois: [1, 3, 5, 7, 9, 11], dernierTaux: 0.0052, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    // ── « EFFORT OU VERSEMENT » — et à défaut de CCT d'entreprise, on verse ─
    // ✅ CCT du 02/02/2026 « Effort en faveur des groupes à risque pour 2026 »,
    // art. 7, 9 et 12 — dépôt 198267/CO/310, banque CCT du SPF Emploi, texte
    // primaire lu le 26/08/2026 :
    //   · art. 7 : « Les banques ont jusqu'au 30 novembre 2026 pour conclure
    //     pour 2026 une CCT d'entreprise visant à l'affectation de 0,10 % de la
    //     masse salariale en faveur des groupes à risque » ;
    //   · art. 9 : « Les banques qui, au 30 novembre 2026, n'ont pas conclu de
    //     CCT d'entreprise pour 2026 VERSENT une cotisation de 0,10 % au Fonds
    //     paritaire. »
    //
    // ⚠️ LE 3,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — trente-cinq fois trop.
    //
    // ⚖️ POURQUOI ON ÉCRIT 0,10 % ICI ET 0 SUR LA CP 210, alors que les deux
    // parlent d'un « effort ». La différence est à l'art. 9 : la CP 310 prévoit
    // un VERSEMENT PAR DÉFAUT. Une banque sans CCT d'entreprise au 30 novembre
    // paie les 0,10 % au Fonds paritaire — c'est donc une cotisation réellement
    // due, et c'est le cas de figure d'un employeur qui n'a pas de convention
    // propre. La CP 210, elle, ne prévoit aucun versement de substitution :
    // l'argent n'y quitte jamais l'entreprise.
    // 🟠 Réserve assumée : une banque QUI A conclu sa CCT d'entreprise dépense
    // les 0,10 % elle-même et ne les verse pas. Le coût employeur reste le même
    // (0,10 % de la masse salariale dans les deux cas), mais le destinataire
    // change. C'est écrit ici pour qu'on ne le découvre pas sur une facture.
    fondsSE: {
      nom: 'Fonds paritaire de formation professionnelle et syndicale du secteur bancaire',
      taux_patronal: 0.001,
      base: 'masse salariale',
      cctSourcee: {
        taux: 0.001,
        base: 'masse salariale (art. 7 et 9)',
        mecanisme: "effort OU versement : 0,10 % affectés aux groupes à risque par CCT d'entreprise conclue au plus tard le 30/11/2026 ; à défaut, versement de 0,10 % au Fonds paritaire (art. 9)",
        source: "CCT du 02/02/2026 (CP 310, banques), art. 7, 9 et 12 — dépôt 198267/CO/310, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Art. 5 : au moins 0,05 % réservé aux catégories de l'AR du 19/02/2013 ; art. 6 : au moins la moitié (0,025 %) aux travailleurs de 40 ans et plus menacés de licenciement, sous réserve de l'accord du Ministre de l'Emploi.",
        dateVerif: '2026-08-26',
      },
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT ne vaut que pour 2026 (une nouvelle est conclue chaque année)",
      aVerifier: true,
      aVerifierRaison: "Le 0,10 % est écrit parce que l'art. 9 prévoit un VERSEMENT PAR DÉFAUT au Fonds paritaire pour toute banque sans CCT d'entreprise au 30/11/2026 — c'est le cas de figure d'un employeur sans convention propre, donc le plus prudent. Une banque QUI A conclu sa CCT d'entreprise dépense les mêmes 0,10 % elle-même : le coût employeur est identique, le destinataire change. À trancher si un client de ce secteur a une CCT d'entreprise. La CCT est annuelle : à revérifier début 2027.",
      note: "Le 3,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT 2025-2026 (dépôt 198673/CO/310, signée
    // 02/02/2026), art. 10 « Avantage : chèques-repas », texte primaire lu :
    // renvoie aux AR du 12/09/2025 et du 10/11/2025 (hausse du plafond fiscal)
    // et dit littéralement « les entreprises doivent engager une discussion à
    // leur niveau ». Même schéma dans l'accord 2017-2018 (dépôt 142853) : choix
    // laissé à l'entreprise entre hausse du chèque-repas, éco-chèques ou
    // avantage net équivalent. Aucun montant sectoriel fixe.
    chequesRepas: {
      obligatoire: false,
      source: "CCT 2025-2026 (dépôt 198673/CO/310, CP 310 banques), art. 10 — discussion renvoyée au niveau entreprise, aucun montant sectoriel fixe",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 310 + « eco cheque » :
    // 0 résultat. Cohérent avec la note chequesRepas ci-dessus : le secteur
    // laisse le choix à l'ENTREPRISE entre hausse du chèque-repas,
    // éco-chèques ou avantage net équivalent (accord 2017-2018, dépôt
    // 142853) — aucun montant sectoriel fixe à annoncer.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 310 — recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 6 CCT. Les trois plus récentes ont été lues (dépôts 154929 du 30/09/2019, 147281 du 25/06/2018, 142853 du 29/09/2017) : elles citent l'octroi d'écochèques comme UNE DES FORMES possibles d'affectation de l'enveloppe, sans fixer de montant. Aucun montant sectoriel fixe — cohérent avec chequesRepas ci-dessus, choix laissé à l'entreprise.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (13e mois) + 14e mois (selon CCT entreprise)',
    primeSyndicale: { existe: true, montant: 145, prorata: true },
    formation: { jours: 5, fonds: 'Fonds bancaire (Febelfin Academy)' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 35,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 310: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    sermentBancaire: { obligatoire: true, dateEntree: '2025-01-01', source: 'CGSLB CP 310 + CP 341' },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE 2026 + CGSLB CP 310 + Febelfin',
      indexationFrequence: 'Bimestrielle (6×/an) sur barème',
      derniereIndexation: '2026-01-01 (+0,36%)',
      notes: 'Régime 35h/semaine. Indexation tous les 2 mois sur barème. Serment bancaire obligatoire depuis 2025.',
    },
  },

  // CP 312 — Grands magasins (grands magasins / department stores ; ≠ CP 311 vente au détail)
  '312': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 02/07/2020 (CP 312), banque
    // CCT du SPF Emploi, 312/312-2020-011246.pdf, art. 7. Mot pour mot :
    //   « Une délégation syndicale est constituée pour un siège dans la mesure
    //     où 25 % du personnel concerné par la présente convention au moins y
    //     est syndiqué. »
    //
    // ⚠️ PAR SIÈGE, PAS PAR ENTREPRISE — et c'est décisif dans les grands
    // magasins : le droit s'apprécie magasin par magasin, pas au niveau de
    // l'enseigne. Le barème des membres se lit lui aussi par siège, et il
    // PLAFONNE : au-delà de 700 personnes, la délégation compte au plus 10
    // membres.
    avantages: {
      delegationSyndicale: {
        critere: 'syndicalisation',
        pctPersonnelSyndique: 0.25,
        niveau: 'siège (magasin), pas entreprise',
        membres: [
          { de: 10, a: 40, nb: 2 }, { de: 41, a: 60, nb: 3 }, { de: 61, a: 100, nb: 4 },
          { de: 101, a: 200, nb: 5 }, { de: 201, a: 300, nb: 6 }, { de: 301, a: 400, nb: 7 },
          { de: 401, a: 500, nb: 8 }, { de: 501, a: 600, nb: 9 }, { de: 601, a: 700, nb: 10 },
        ],
        plafondMembres: 10,              // au-delà de 700 personnes par siège
        source: 'CCT du 02/07/2020 (CP 312) — statuts de la délégation syndicale, banque CCT du SPF Emploi, 312/312-2020-011246.pdf, art. 7 et 8. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 312 — Grands magasins',
    nomCourt: 'Grands magasins',
    // Régime sectoriel 35h/sem temps plein (sursalaire/récup dès la 36e h ; max 5 j/sem, 9 h/j),
    // diviseur mensuel 151,66. Sources : Synova CP 312 + SPF salairesminimums.be CP 3120000, consulté 2026-07-07.
    regimeHebdo: 35,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'Mécanisme = INDEX-PIVOT (+2% au franchissement, mois VARIABLE — ex. 01/10/2025 et 01/05/2026), PAS janvier fixe (corrigé 2026-07-16, confiance haute 3 sources CSC-CNE/CGSLB/SD Worx). Détecté par comparaison de barème (cron auto-indexation) ; plus d\'anticipation « janvier » fantôme. Taux 0,02 = pas pivot standard.' },
    // ── FONDS SOCIAL DES GRANDS MAGASINS — un pourcentage et deux forfaits ─
    // ✅ CCT du 19/11/2025 « Emploi et formation des groupes à risque », art. 7
    // et 10 — dépôt 196669/CO/312, banque CCT du SPF Emploi,
    // 312/312-2025-014349.pdf, texte primaire lu le 26/08/2026. En vigueur du
    // 01/01/2026 au 31/12/2026, remplaçant la CCT du 30/09/2025 (195699).
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — près de dix-sept fois
    // le taux réel.
    //
    // 📐 L'ASSIETTE EST ANNUELLE, PAS MENSUELLE, et c'est écrit tel quel : la
    // cotisation est « calculée sur base de QUATRE FOIS les salaires bruts des
    // travailleurs du troisième trimestre », versée en une fois au plus tard le
    // 31 mars. Rapportée à une masse salariale annuelle, cela revient bien à
    // 0,24 % — c'est ce taux-là qui est codé. Un employeur dont l'effectif varie
    // fortement entre le 3e trimestre et le reste de l'année paiera un montant
    // qui ne correspond pas au douzième de son année : la périodicité est dite
    // ici pour qu'on ne la découvre pas sur une facture.
    //
    // 🔎 ET LE TEXTE DE 2026 N'A PAS ÉTÉ RELU PAR SES AUTEURS : son article de
    // financement parle encore de « ces interventions pour 2025 » et du
    // 3e trimestre 2024. Le TAUX, lui, est identique dans les trois millésimes
    // lus (2023-2024, 2025, 2026) — on code le taux, pas les dates recopiées.
    fondsSE: {
      nom: 'Fonds Social des Grands Magasins',
      taux_patronal: 0.0024,
      base: "quatre fois les salaires bruts des travailleurs du troisième trimestre de l'année précédente",
      cctSourcee: {
        taux: 0.0024,
        composantes: { groupesARisqueEtEmploisTremplins: 0.0024 },
        base: "quatre fois les salaires bruts déclarés à l'ONSS pour le troisième trimestre de l'année précédente ; versement unique au plus tard le 31 mars",
        source: "CCT du 19/11/2025 (CP 312, grands magasins), art. 7 : « Une cotisation de 0,24 % calculée sur base de quatre fois les salaires bruts des travailleurs du troisième trimestre » — dépôt 196669/CO/312, en vigueur du 01/01/2026 au 31/12/2026, banque CCT du SPF Emploi, 312/312-2025-014349.pdf, texte primaire lu le 26/08/2026. Taux identique dans les millésimes précédents (CCT du 06/12/2023, dépôt 185139, et CCT du 19/11/2025 pour 2025, dépôt 196668).",
        dateVerif: '2026-08-26',
        affectation: "Sur ces 0,24 %, l'art. 7 réserve 0,05 % aux groupes définis par l'AR du 19/02/2013 (dont la moitié aux travailleurs de son art. 2) et 0,05 % de plus aux jeunes de moins de 26 ans — soit une part réservée de 0,10 %.",
      },
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT de financement est annuelle et cesse ses effets au 31/12/2026",
      // 💰 DEUX FORFAITS PAR TÊTE, hors du taux, comptés sur l'effectif au
      // 30 septembre. NON facturés par l'app.
      forfaitsAnnuelsParTravailleur: {
        formationSyndicale: { montant: 9.74, reference: 'effectif au 30 septembre', source: 'CCT du 06/12/2023, art. 2 — dépôt 185134/CO/312, en vigueur du 01/01/2024 au 31/12/2024' },
        formationProfessionnelle: { montant: 1.00, reference: 'effectif au 30 septembre', perception: "en août, versement au plus tard le 31 août", source: 'CCT du 06/12/2023, art. 2 et 3 — dépôt 185135/CO/312, en vigueur du 01/01/2024 au 31/12/2024' },
      },
      aVerifier: true,
      aVerifierRaison: "Les deux forfaits par tête (9,74 € formation syndicale, 1,00 € formation professionnelle) viennent de CCT annuelles qui cessent le 31/12/2024 ; aucun millésime 2025 ou 2026 n'a été trouvé dans la banque CCT pour ces deux-là, alors que la CCT groupes à risque, elle, a bien été reconduite. Reconduction à confirmer avant d'affirmer qu'ils ne sont plus dus. Le taux de 0,24 % lui-même expire le 31/12/2026.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // Pas d'obligation sectorielle confirmée (Synova, consulté 2026-07-07) → niveau entreprise.
    // Plafonds légaux 2026 : valeur max 10,00 € = 8,91 employeur + 1,09 travailleur.
    chequesRepas: { obligatoire: false, note: 'Selon enseigne (pas d\'obligation sectorielle CP 312 confirmée). Plafonds légaux 2026 : 10,00 € (8,91 empl. / 1,09 trav.).' },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (312/312-2023-017192.pdf, CCT du 06/12/2023, dépôt 185130/CO/312,
    // « toujours d'application »). Art. 5 : « Aan elke voltijds
    // tewerkgestelde werknemer met een volledige referteperiode worden
    // ecocheques één keer per jaar toegekend ter waarde van 250 EUR » (à
    // chaque travailleur temps plein avec période de référence complète,
    // 250 EUR/an). Art. 6 : temps partiel proratisé par palier — même
    // mécanisme que la CP 311 (Grandes entreprises de vente au détail) :
    // ≥27h/sem 250 € · ≥20h et <27h 200 € · ≥17,5h et <20h 150 € ·
    // <17,5h/sem 100 €. Valeur nominale max. 10 €/chèque (art. 4).
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 06/12/2023 (312/312-2023-017192.pdf), banque CCT du SPF Emploi, dépôt 185130/CO/312, texte primaire lu le 21/09/2026, art. 5-6 : 250 EUR/an temps plein, proratisé par palier de temps partiel (200/150/100 EUR).",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    formation: { jours: 4, fonds: 'CEFORA Commerce' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      samedi:   { taux: 0.50, label: 'Heures sup samedi PM (+50%)', condition: 'après 13h' },
      seuilHebdomadaire: 35, // aligné sur le régime sectoriel 35h (sursalaire dès la 36e h). Sources : Synova CP 312 + SPF salairesminimums.be, consulté 2026-07-07.
    },
    // Vélo : min CCT n°164 supplétive 2026 = 0,30 €/km ; max exonéré fiscal/ONSS 2026 = 0,37 €/km
    // (plafond 3 700 €/an). Sources : SPF Finances + Securex/Acerta 2026, consulté 2026-07-07.
    indemnites: { transportPublic_pct: 1.00, velo: 0.30, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-07-07',
      sourceMAJ: 'Fonds social CP 312 (sfonds312.be) + CGSLB + Synova + SPF salairesminimums.be, consulté 2026-07-07',
      indexationFrequence: 'Annuelle janvier',
      notes: 'Souvent confondue avec CP 311. Champ CP 312 : entreprises exploitant ≥ 3 branches de commerce distinctes et occupant ≥ 50 travailleurs — CP MIXTE (ouvriers + employés). En pratique : Cora, Hema, Carrefour Hyper (pas Carrefour Market → CP 202), Inno.',
    },
  },

  // CP 329 — Secteur socio-culturel
  '329': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 12/12/2025 (CP 329), banque CCT
    // du SPF Emploi, 329/329-2025-015192.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 12/12/2025 (CP 329) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 329/329-2025-015192.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 329 — Secteur socio-culturel (Fr/Vl/Germ)',
    nomCourt: 'Socio-culturel',
    regimeHebdo: 38,
    // Aligné sur ses 3 sous-CP (vague 2, 2026-08-01) : le correctif index-pivot du
    // 2026-07-16 avait mis 329.01/.02/.03 à 01/02/2026 mais OUBLIÉ l'entrée parent,
    // restée à 01/01/2026 avec mois [1]. Source : CESSOC « Grilles barémiques indexées
    // au 1er février 2026 » (confédération patronale CP 329) + Sociare/hetACV/CGSLB/SETCa.
    // ⚠️ Indexation SUIVANTE due au 01/08/2026 (+2 %, PLAFONNÉE à la tranche ≤ 4 000 €/mois
    // ETP) — pivot franchi en juin 2026, confirmée CESSOC + SD Worx (consulté 2026-08-01).
    // NON appliquée ici : les VALEURS de barème sont zone protégée (K3) → décision fondateur.
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'Index-pivot non-marchand : effet 01/02/2026 (pivot déc. 2025). Prochaine échéance 01/08/2026 (+2% plafonné 4000 EUR/ETP, pivot juin 2026) — à appliquer aux barèmes après validation. Source: CESSOC + SD Worx + CSC CNE CP 329.' },
    // ── FAÎTIÈRE : LES TROIS SOUS-CP PORTENT LE TAUX, ET ELLES SONT SOURCÉES ─
    // 📉 5 % → 0 le 26/08/2026. La lecture du 25/08 (ci-dessous) avait conclu
    // « aucune convention fixant un taux global » et laissé le 5 % faute de
    // mieux. Ce qui manquait, c'était de regarder EN DESSOUS : les trois
    // sous-commissions portent chacune un taux perçu par l'ONSS —
    // 329.01 (Communauté flamande) 0,20 % · 329.02 (Communauté française)
    // 0,22 % · 329.03 (fédérales et bicommunautaires) 0,20 %. Le 5 % de la mère
    // était vingt-cinq fois au-dessus, et il ne servait qu'aux dossiers codés
    // « 329 » sans sous-commission.
    //
    // 🪦 ET LE FONDS DE LA MÈRE A ÉTÉ ABROGÉ. CCT du 09/12/2024, art. 2 (dépôt
    // 191534/CO/329, texte primaire lu le 26/08/2026) : le « Fonds Social
    // Maribel social du secteur socioculturel », créé par la CCT du 13/12/2002,
    // « est abrogé à dater du 20 novembre 2023 à minuit ». Son commentaire dit
    // où il est parti : « La continuité de l'exécution de la convention abrogée
    // est assurée par la signature d'une convention de même nature au sein de
    // la Sous-commission paritaire pour les organisations socio-culturelles
    // fédérales et bicommunautaires (SCP 329.03) avec prise d'effet au
    // 21 novembre 2023. » Le fonds n'a pas disparu — il est descendu d'un étage.
    fondsSE: {
      nom: 'Socio-culturel — faîtière : le taux vit dans les trois sous-commissions',
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '329.01': 'Communauté flamande — 0,20 %, taux perçu par l\'ONSS',
        '329.02': 'Communauté française et germanophone — 0,22 %, taux perçu par l\'ONSS',
        '329.03': 'organisations fédérales et bicommunautaires — 0,20 %, taux perçu par l\'ONSS ; c\'est aussi là qu\'a été repris le Maribel social depuis le 21/11/2023',
      },
      source: "CCT du 09/12/2024, art. 2 (dépôt 191534/CO/329) : le Fonds Social Maribel social du secteur socioculturel « est abrogé à dater du 20 novembre 2023 à minuit », sa continuité étant assurée au niveau de la SCP 329.03. Les trois sous-commissions portent chacune un taux lu dans le fichier des taux DmfA 2026/3 de l'ONSS. Banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
      dateVerif: '2026-08-26',
      // 💶 Une cotisation EXISTE bien au niveau de la CP 329, mais elle est
      // affectée : elle finance le reclassement professionnel (outplacement).
      // Elle n'est PAS ajoutée au taux — on ne sait pas si l'ONSS l'inclut déjà
      // dans les 0,20 % qu'il perçoit pour les sous-CP, et l'ajouter la
      // compterait potentiellement deux fois.
      cotisationReclassement: {
        parTrimestre: { '2026-T1': 0.0003, '2026-T2': 0.0003, '2026-T3': 0.0002, '2026-T4': 0.0002, '2027-T1': 0.0003, '2027-T2': 0.0003, '2027-T3': 0.0002, '2027-T4': 0.0002 },
        moyenneAnnuelle: 0.00025,
        base: 'salaires bruts payés aux travailleurs',
        affectation: "financer le reclassement professionnel (outplacement), tel que prévu par la CCT du 24/06/2024, dépôt 188641/CO/329",
        source: "CCT du 19/11/2025 « Détermination du montant de la cotisation à payer par les employeurs pour financer le reclassement professionnel », art. 3 — dépôt 196671/CO/329, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        nonAjoutee: "NON ajoutée au taux : rien ne dit si les 0,20 % que l'ONSS perçoit pour les sous-CP l'incluent déjà. L'ajouter risquerait de la compter deux fois — le piège de la CP 149.01.",
      },
      tauxASourcer: false,
      // 🔴 5 % NON SOURCE, et la recherche a ete faite. Mesure du 25/08/2026 :
      // sur les 60 conventions « fonds » de la CP 329, UNE SEULE est a portee
      // generale et fixe une cotisation — la CCT du 16/10/2017, qui finance le
      // RECLASSEMENT PROFESSIONNEL (outplacement). Ce n'est pas le taux global.
      // Toutes les autres visent un sous-secteur (Communaute flamande,
      // francaise, germanophone) ou un objet particulier.
      lectureFaite: {
        source: "Balayage des 60 conventions des themes 229/230/277 pour la CP 329, banque CCT du SPF Emploi, le 25/08/2026. Seule CCT a portee generale fixant une cotisation : celle du 16/10/2017, « determination de la cotisation a payer par les employeurs pour financer le reclassement professionnel » (329/329-2017-013358.pdf).",
        dateVerif: '2026-08-25',
        conclusion: "aucune convention fixant un taux GLOBAL. ⚠️ La CP 329 est en outre eclatee par Communaute : un taux unique pour toute la CP est douteux par construction.",
      },
    },
    chequesRepas: { obligatoire: false, note: 'Selon CCT entreprise' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 329 + « eco cheque » :
    // 0 résultat. Cohérent avec le fondsSE ci-dessus : CP 329 est une
    // faîtière, taux réel dans les trois sous-commissions — 329.01/329.03
    // à vérifier séparément.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 329 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (faîtière, sous-CP 329.01/329.03 à vérifier séparément)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut',
    formation: { jours: 5, fonds: 'APEF (Fr) / VIVO (Vl)' },
    cotisationSpeciale: null,
    sousSecteurs: {
      '329.01': 'Sociaal-cultureel sector Vlaamse Gemeenschap',
      '329.02': 'Secteur socio-culturel francophone et germanophone',
      '329.03': 'Secteur socio-culturel fédéral et bicommunautaire',
    },
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo : pas de montant unique CP 329 — régimes par sous-commission (Sociare/CESSOC, consulté 2026-07-07) :
    // 329.01 = 0,37 (CCT propre = max exonéré, obligatoire) ; 329.02 = 0,35 (CCT 21/10/2019 mod., plafond CCT 2 500 €/an) ;
    // 329.03 = 0,30 (pas de CCT propre → CCT n°164 supplétive). velo ci-dessous = plancher (329.03) ; voir veloParSousCommission.
    indemnites: { transportPublic_pct: 1.00, velo: 0.30, veloParSousCommission: { '329.01': 0.37, '329.02': 0.35, '329.03': 0.30 }, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-07-07',
      sourceMAJ: 'CSC CNE CP 329 + APEF + VIVO + Sociare/CESSOC (vélo, 2026-07-07)',
      indexationFrequence: 'Pivot public (1er mois après dépassement)',
      derniereIndexation: '2026-01-01 (+2%)',
      notes: 'CP non-marchand. Subsides Communauté française/Vlaamse/germanophone. ASBL et associations.',
    },
  },

  // CP 337 — Secteur non-marchand divers (résiduel)
  '337': {
    // 🔴 `primeFinAnnee_pct: 8.33` RETIRÉ LE 23/08/2026 — il contredisait le
    // registre sourcé `prime-fin-annee.js`, qui donne 2,50 % pour cette
    // commission. Le 8,33 est la valeur par défaut recopiée sur 146 fiches sur
    // 174 (le douzième arithmétique d'un « treizième mois »).
    // R1 : une constante vit à un seul endroit. Verrou :
    // __tests__/prime-fin-annee-une-seule-verite.test.js
    nom: 'CP 337 — Secteur non-marchand divers',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION » : la fiche a été
    // ouverte et lue, ce secteur ne classe pas ses travailleurs par fonction.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      grilleSectorielle: false,
      systeme: "un salaire annuel minimum brut garanti unique, pour les assistants personnels engagés par des particuliers dans le cadre d'un budget individualisé (PVB) ou d'un budget d'assistance personnelle (PAB) de l'Autorité flamande. Aucune catégorie de fonction. ⚠️ Le périmètre de ce barème est CE public-là, pas tout le secteur non marchand.",
    },
    nomCourt: 'Non-marchand divers',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'Q2 2026: +2% non-marchand divers (suit pivot public). Source: CSC CNE CP 337' },
    fondsSE: {
      nom: 'Fonds social non-marchand divers',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '039, 139',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 039, 139 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, note: 'Selon CCT entreprise' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 337 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 337 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    formation: { jours: 5, fonds: 'APEF / VIVO selon entité' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo CP 337 : CCT mobilité 03/12/2019 (vig. 01/01/2020), montant FIXE 0,24 €/km, non revalorisé par l'accord 2025-2026 ;
    // CCT n°164 (0,30) NON applicable (CCT propre) — Unisoc + CGSLB flash 366, consulté 2026-07-07. TODO: confirmer sur texte CCT primaire (public-search SPF).
    indemnites: { transportPublic_pct: 1.00, velo: 0.24, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE CP 337',
      indexationFrequence: 'Pivot public',
      notes: 'CP résiduelle pour entités non-marchand non couvertes par 327, 329, 330, 331, 332, 336.',
    },
  },


  // CP 100 — CP auxiliaire pour ouvriers (généré batch 29/04/2026)
  '100': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 03/03/2026 (CP 100), banque CCT
    // du SPF Emploi, 100/100-2026-003737.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 03/03/2026 (CP 100) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 100/100-2026-003737.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 100 — CP auxiliaire pour ouvriers',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId 31ff47e2… (CP 1000000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "le barème ne se décline que par ÂGE et par RÉGIME hebdomadaire (38 h / 39 h / 40 h), avec un traitement distinct des moins de 18 ans et des 18-20 ans sous contrat d'étudiant. Aucune catégorie de fonction.",
    },
    nomCourt: 'CP auxiliaire pour ouvriers',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay/CSC' },
    // ── LA CP AUXILIAIRE OUVRIERS — 0,15 % sur 108 %, et c'est tout ───────
    // ✅ CCT du 03/03/2026 « Perception de la cotisation patronale au fonds de
    // sécurité d'existence dénommé fonds pour la formation des ouvriers par
    // l'ONSS », art. 2 §§ 1 à 3 — dépôt 198810/CO/100, banque CCT du SPF Emploi,
    // 100/100-2026-003726.pdf, texte primaire lu le 26/08/2026 : « À partir du
    // 1er avril 2026, la cotisation au fonds de sécurité d'existence […] est
    // fixée à 0,15 % […]. Les 0,15 % sont fixés sur la base des salaires bruts
    // à 108 %. »
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — VINGT-SEPT FOIS le
    // taux réel, et sur la commission AUXILIAIRE, celle qui accueille tous les
    // ouvriers dont l'activité ne relève d'aucune autre CP. C'est donc l'un des
    // faux les plus largement servis de la grille : sur un brut de 2 500 €,
    // 108 € par mois et par travailleur au lieu de 4,05 €.
    //
    // 🔎 L'ONSS ne pouvait pas trancher ici : la CP 100 est AMBIGUË dans son
    // fichier des taux (plusieurs catégories d'employeur, plusieurs valeurs).
    // C'est la convention qui répond, et elle ne laisse aucune marge.
    fondsSE: {
      nom: "Fonds pour la formation des ouvriers de la commission paritaire auxiliaire pour ouvriers",
      taux_patronal: 0.0015,
      assiette: 'salaires bruts à 108 %',
      cctSourcee: {
        taux: 0.0015,
        base: "salaires bruts à 108 % (note de bas de page de l'art. 2, § 3 : « Les 0,15 % sont fixés sur la base des salaires bruts à 108 % »)",
        perceptionParOnss: true,
        source: "CCT du 03/03/2026 (CP 100, commission paritaire auxiliaire pour ouvriers), art. 2 §§ 1 à 3 — dépôt 198810/CO/100, banque CCT du SPF Emploi, 100/100-2026-003726.pdf, texte primaire lu le 26/08/2026. Elle exécute l'art. 15 des statuts du fonds (CCT du 20/10/2011, dépôt 106897/CO/100). La cotisation est perçue et recouvrée par l'ONSS, qui la reverse au fonds.",
        dateVerif: '2026-08-26',
        perimetre: 'ouvriers uniquement (art. 1er)',
      },
      // ⏰ CCT à DURÉE DÉTERMINÉE : elle produit ses effets au 01/04/2026 et
      // cesse ses effets au 31/12/2027 (art. 3). À revérifier à cette date.
      enVigueurDepuis: '2026-04-01',
      expireLe: '2027-12-31',
      expireQuoi: "la totalité du taux : la CCT de perception est à durée déterminée (art. 3)",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source. La banque CCT ne publie, pour cette commission, que des conventions sur ce seul fonds (census de 16 conventions) : aucun autre fonds sectoriel n'y apparaît.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €, obligatoire:true)
    // était la famille recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB),
    // page CP 100 exhaustive (salaires, primes, congés, formation, syndical) :
    // aucune mention de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 100, exhaustive — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 100 + « eco cheque »
    // dans la banque CCT du SPF Emploi : 0 résultat (« 0 résultat(s) trouvé(s) »
    // affiché par le site lui-même). Remplace la valeur ronde recopiée (250,
    // portée par 155 des 174 fiches).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 100 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 100 » était FAUX
    // sur le nombre de jours : un vrai fonds existe (CCT du 17/03/2026, dépôt
    // 198810/CO/100, « Fonds pour la formation des ouvriers de la commission
    // paritaire auxiliaire pour ouvriers », cotisation patronale 0,15% du brut
    // à 108%, perçue par l'ONSS dès le 01/04/2026) mais AUCUNE CCT relevée ne
    // fixe un droit individuel chiffré en jours — le fonds finance des actions
    // de formation, il ne garantit pas X jours au travailleur.
    formation: {
      joursSectoriels: null,
      fondsFormationExiste: true,
      fonds: "Fonds pour la formation des ouvriers de la commission paritaire auxiliaire pour ouvriers (CP 100)",
      cotisationFonds: 0.0015,
      renvoi: "aucun droit individuel en jours trouvé dans les CCT relevées — le fonds finance des actions de formation, il ne garantit pas X jours ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 17/03/2026 (CP 100), art. 2, dépôt 198810/CO/100, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo CP 100 : CCT sectorielle 17/10/2023 → 0,27 €/km (max 10,80 €/j, 40 km AR) dès 01/01/2024 ;
    // 0,32 €/km (max 12,80 €/j) dès 01/10/2026 (accord 2025-2026) ; CCT n°164 non applicable — Liantis + ACCG, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.27, veloFutur: 0.32, veloDateChangement: '2026-10-01', veloMaxJour: 10.80, veloMaxJourFutur: 12.80, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC + ACV résiduelle ouvriers',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 101 — Commission nationale mixte des mines (généré batch 29/04/2026)
  '101': {
    nom: 'CP 101 — Commission nationale mixte des mines',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── CENSUS COMPLET : AUCUN FONDS RELEVÉ ───────────────────────────────
    // 📏 Mesure du 26/08/2026, avec son dénominateur. Aucune convention, quel que soit le critère de recherche (thème, mot-clé, sans filtre). Le charbonnage belge a cessé toute activité — le dernier siège a fermé en 1992.
    // 🚫 L'ONSS ne rattache non plus aucune catégorie d'employeur à cette
    // commission dans le fichier des taux DmfA 2026/3.
    //
    // ⚠️ LE 6 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Sur un brut de
    // 2 500 €, il facturait 150,0 € par mois et par travailleur.
    //
    // ⚖️ Le 0 vient d'un CENSUS, pas d'un acte de dissolution : il prouve que le
    // SPF ne publie rien, pas qu'aucune cotisation n'existe. La réserve est
    // écrite ci-dessous pour qu'aucune session ne le lise comme un fait clos.
    fondsSE: {
      nom: "Commission nationale mixte des mines — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des conventions publiées pour la CP 101, lu le 26/08/2026 (0 convention). Aucune convention, quel que soit le critère de recherche (thème, mot-clé, sans filtre). Le charbonnage belge a cessé toute activité — le dernier siège a fermé en 1992. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à cette commission.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 vient d'un CENSUS de titres, pas d'un acte de dissolution comme pour les CP 316 et 328.02. Un census prouve que le SPF ne publie rien sous ce numéro, pas qu'aucune cotisation n'existe. À reconfirmer avant d'affirmer à un client qu'il ne doit rien. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation. Reste aussi le site du fonds.",
      note: "Le 6 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — INCOHÉRENCE INTERNE : le bloc `fondsSE`
    // ci-dessus documente déjà (26/08/2026) qu'aucune convention n'est
    // publiée sous ce numéro et que le charbonnage belge est fermé depuis
    // 1992 — mais `chequesRepas` affirmait quand même `obligatoire:true` avec
    // la famille recopiée {7/5.91/1.09}. Aligné sur le constat déjà fait.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le census fondsSE ci-dessus (banque CCT SPF, 0 convention, lu le 26/08/2026) — secteur fermé depuis 1992",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 101 + « eco cheque » :
    // 0 résultat, cohérent avec le secteur fermé depuis 1992 (cf. chequesRepas
    // ci-dessus) — le régime éco-chèques n'existe que depuis 2009.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 101 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026), secteur fermé depuis 1992",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 101 » était FAUX :
    // recherche plein texte « formation »/« opleiding » filtrée CP 101 dans la
    // banque CCT du SPF Emploi → « 0 résultat(s) trouvé(s) » (texte confirmé
    // par le site lui-même, pas une erreur d'outil). Aucune CCT de formation
    // propre à cette CP.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT de formation propre à la CP 101 dans la banque CCT (0 résultat confirmé sur formation/opleiding)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 101 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 101: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102 — Industrie des carrières (généré batch 29/04/2026)
  '102': {
    nom: 'CP 102 — Industrie des carrières',
    nomCourt: 'Industrie des carrières',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'voir sous-CP', dernierTaux: null, derniereDate: null, note: 'AUDIT 2026-06 — carrieres — pas de taux unique, voir 102.01-102.11' },
    // ── FAÎTIÈRE À MOITIÉ SOURCÉE — ET C'EST POURQUOI ON NE MET PAS 0 ─────
    // 📏 Mesure du 26/08/2026, avec son dénominateur : la banque CCT du SPF ne
    // publie AUCUNE convention sous le numéro 102 lui-même. Ses sous-commissions,
    // elles, sont **5 sur 10** sourcées : 102.01 (0), 102.03 (0), 102.05 (1,70 %), 102.06 (1,50 %), 102.08 (0,92 %).
    // Les autres — 102.02, 102.04, 102.07, 102.09 et 102.11, toutes à 6 % sans source — n'ont
    // rien derrière elles.
    //
    // ⛔ POURQUOI LE 6 % RESTE, ALORS QU'ON L'A MIS À 0 SUR LES CP 125, 142,
    // 149, 318 ET 329. Là-bas, TOUTES les sous-commissions étaient sourcées :
    // la mère ne servait plus à personne. Ici, les sous-CP muettes **retombent
    // sur la mère** — c'est la règle de `blocFondsSE`, qui ne descend que sur un
    // taux prouvé. Mettre 0 ne rendrait pas la mère honnête : ça mettrait à zéro
    // le fonds de plusieurs sous-secteurs qui en ont un, sans l'avoir vérifié.
    //
    // ✅ Ce qui change quand même : le 6 % est désormais NOMMÉ comme non
    // sourcé, avec le ratio écrit. Il tombera quand les sous-CP muettes auront
    // été lues — pas avant.
    fondsSE: {
      nom: "Industrie des carrières — faîtière, taux non sourcé servant de repli aux sous-CP muettes",
      taux_patronal: 0.06,
      aVerifier: true,
      aVerifierRaison: "Le 6 % n'a AUCUNE source : la banque CCT du SPF ne publie aucune convention sous le numéro 102 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Il n'est PAS mis à 0 parce que les sous-commissions encore muettes retombent dessus — 102.02, 102.04, 102.07, 102.09 et 102.11, toutes à 6 % sans source — et qu'un 0 leur retirerait un fonds qu'elles ont peut-être. Il tombera quand elles auront été lues, pas avant. Sous-CP déjà sourcées : 102.01 (0), 102.03 (0), 102.05 (1,70 %), 102.06 (1,50 %), 102.08 (0,92 %) (5 sur 10).",
      dateVerif: '2026-08-26',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus : aucune
    // entreprise ne relève directement de la CP 102 « faîtière », toutes
    // relèvent d'une sous-commission numérotée. Synova (ex-CGSLB) confirme :
    // « la CP 102 fonctionne avec 10 sous-commissions », aucune mention de
    // chèque-repas au niveau de la mère. Chaque sous-CP porte SA PROPRE
    // réponse (voir 102.01 à 102.11 ci-dessous) — ne pas hériter cette valeur.
    chequesRepas: {
      obligatoire: false,
      source: "Synova (ex-CGSLB) + ACCG — la CP 102 mère n'a pas de CCT propre, toutes les entreprises relèvent d'une sous-commission",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 102 + « eco cheque » :
    // 0 résultat, cohérent avec l'absence de CCT propre à la CP mère (toutes
    // les entreprises relèvent d'une sous-commission, voir 102.01 à 102.11).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026), aucune CCT propre à la CP mère",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 102 » était FAUX
    // au niveau de cette CP MÈRE : recherche plein texte « formation »/
    // « opleiding » filtrée CP 102 → « 0 résultat(s) trouvé(s) » confirmé.
    // Cohérent : le contenu réel de ce secteur (carrières) vit dans les
    // sous-CP 102.01-102.11, chacune vérifiée séparément (certaines portent
    // un vrai droit individuel à la formation, d'autres non — voir leurs
    // fiches respectives).
    formation: {
      joursSectoriels: null,
      renvoi: "CP mère sans CCT de formation propre (0 résultat confirmé) — le contenu réel est dans les sous-CP 102.01-102.11, chacune vérifiée individuellement ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 102 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.01 — Carrières de petit granit et calcaire (Hainaut) (généré batch 29/04/2026)
  '102.01': {
    // ── Classification CP 102.01 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 26/06/2026, n° 200982/CO/102.01, banque
    // CCT du SPF Emploi (10201/10201-2026-013756.pdf), art. 2.
    // Sous-commission des carrières de petit granit et de calcaire à tailler
    // de la province de HAINAUT. La CCT « coordonne et actualise » les
    // conventions existantes — et dit expressément qu'elle ne peut porter
    // préjudice aux conventions d'entreprise plus favorables déjà conclues,
    // ni aux régimes salariaux plus favorables effectivement appliqués.
    //
    // 🔎 CE TABLEAU N'A ÉTÉ LISIBLE QU'À LA TROISIÈME TENTATIVE, et le chemin
    // mérite d'être noté parce qu'il resservira. (1) L'extraction à plat
    // mélangeait les colonnes. (2) Des bornes de colonnes ESTIMÉES faisaient
    // basculer le même chiffre tantôt en « catégorie », tantôt en « article ».
    // (3) En relevant le x RÉEL des en-têtes (Catégorie x≈277, Article x≈376,
    // Service x≈475) puis la distribution des chiffres, deux colonnes
    // nettes apparaissent — x≈295 et x≈388 — et les valeurs de la seconde
    // tombent toutes entre 4 et 9, ce que la CCT confirme en disant que les
    // salaires sont « repris dans les articles 3 à 9 ». Le recoupement vaut
    // preuve : ce n'était pas deux fois la même colonne.
    //
    // ⚠️ `categorie` et `article` ne sont PAS la même chose. La catégorie
    // classe le travailleur ; l'article dit dans QUEL ARTICLE de la CCT son
    // salaire est fixé. Certaines fonctions n'ont que l'un des deux — c'est
    // ce que le texte écrit, on ne complète pas.
    classifications: {
      source: 'CCT du 26/06/2026, n° 200982/CO/102.01, art. 2 — banque CCT du SPF Emploi, 10201/10201-2026-013756.pdf, texte primaire lu le 23/08/2026 (extraction par coordonnées)',
      dateVerif: '2026-08-23',
      perimetre: 'province de Hainaut',
      reserve: "ne porte préjudice ni aux conventions d'entreprise plus favorables déjà conclues, ni aux régimes salariaux plus favorables effectivement appliqués",
      legende: {
        categorie: 'catégorie de classification du travailleur',
        article: "article de la CCT (3 à 9) où le salaire horaire de cette fonction est fixé",
        service: "service auquel la fonction est rattachée, quand la CCT le précise",
      },
      fonctions: [
        { fonction: 'Ciseleur', categorie: 1, article: 7 },
        { fonction: 'Manœuvre lourd', categorie: 5 },
        { fonction: 'Aide-magasinier', categorie: 5 },
        { fonction: 'Aide-mineur', categorie: 5 },
        { fonction: 'Ouvrier débutant', categorie: 5 },
        { fonction: 'Conducteur auto-élévateur <10 tonnes', article: 5 },
        { fonction: 'Conducteur chargeur sur pneus< 35 tonnes', article: 5 },
        { fonction: 'Conducteur auto-élévateur>= 10 tonnes', article: 5 },
        { fonction: 'Conducteur chargeur sur pneus>= 35 tonnes', article: 5 },
        { fonction: 'Chauffeur camions<20 tonnes', categorie: 7 },
        { fonction: '1 er machiniste à cliver', categorie: 7 },
        { fonction: 'Grutier', categorie: 7 },
        { fonction: '2e metteur de chaînes', categorie: 7 },
        { fonction: 'Pontier', categorie: 7 },
        { fonction: 'Opérateur chaîne de débitage (pas débiteur)', article: 7 },
        { fonction: 'Opérateur train à polir', categorie: 7 },
        { fonction: 'Opérateur boucha/flammeuses', categorie: 7 },
        { fonction: '1 er metteur de chaînes', categorie: 9 },
        { fonction: 'Meuleur', article: 7, service: 'Taille mécanique' },
        { fonction: 'Débiteur', article: 7, service: 'Taille mécanique' },
        { fonction: 'Débiteur moulureur', article: 7, service: 'Taille mécanique' },
        { fonction: 'Foreur', article: 5, service: 'Concasseur' },
        { fonction: 'Opérateur scieries-armures', categorie: 12 },
        { fonction: 'Magasinier', categorie: 14 },
        { fonction: 'Rocteur à blocs', categorie: 20 },
        { fonction: 'Graisseur', categorie: 7 },
        { fonction: '2 eme mécanicien', article: 7 },
        { fonction: '2 eme électricien', article: 7, service: 'Atelier entretien' },
        { fonction: '2 eme électro-mécanicien', article: 7, service: 'Atelier entretien' },
        { fonction: 'Chauffeur camions>20 tonnes', categorie: 20 },
        { fonction: 'Opérateur/règleur chaîne débitage', article: 7 },
        { fonction: 'Tailleur de pierre', categorie: 21, article: 6 },
        { fonction: 'Tailleur de pierre + mastiqueur', categorie: 21, article: 6 },
        { fonction: 'Opérateur Haveuse', categorie: 21, article: 6 },
        { fonction: 'Opérateur machine à fil', categorie: 21, article: 6, note: 'barème identique à celui du haveur' },
        { fonction: 'Rocteur de buffet', article: 4 },
        { fonction: '1er électricien', article: 7, service: 'Atelier entretien' },
        { fonction: 'Soudeur', article: 7, service: 'Atelier entretien' },
        { fonction: '1 er mécanicien', article: 7 },
        { fonction: 'Opérateur concasseur', article: 5 },
      ],
      // ⛔ CINQ FONCTIONS DONT LE CHIFFRE N'A PAS PU ÊTRE RATTACHÉ AVEC
      // CERTITUDE. Leur libellé tient sur deux lignes du tableau et le nombre
      // se trouve sur la ligne voisine : il est visible, mais rien dans
      // l'extraction ne prouve à laquelle des deux il appartient. Les
      // attribuer au jugé donnerait un SALAIRE à un travailleur — c'est
      // exactement le cas où une fiche honnêtement incomplète vaut mieux.
      fonctionsSansChiffreCertain: [
        'Conducteur pelleteuse, excavatrice ou grue hydraulique',
        'Pétardeur',
        'Opérateur foreuse hydraulique',
        'Mineur',
        '1er électromécanicien',
      ],
      // Le brigadier n'est pas une catégorie : c'est un supplément.
      brigadier: {
        regle: "salaire effectivement payé pour la fonction exercée, augmenté d'un montant minimum, indexé et intégré dans le salaire, lié à l'exercice de la fonction de brigadier",
        montantHistorique: 0.7192,
        montantHistorique_date: '2025-01-01',
        montantHistorique_regime: '39 heures/semaine',
        avertissement: "0,7192 €/h est la valeur du 01/01/2025 (indice santé lissé 130,42) — indexée depuis, à ne pas servir comme le montant du jour",
      },
      // ⛔ Les salaires horaires par catégorie (art. 3 à 9) ne sont pas repris :
      // ils sont fixés au 01/01/2025 à l'indice santé lissé 130,42, donc déjà
      // dépassés. Trois régimes coexistent — 40 h, 39 h et 38 h par semaine,
      // prime de production comprise.
      regimesHoraires: [40, 39, 38],
      baremesNonRepris: 'salaires horaires par catégorie, art. 3 à 9, fixés au 01/01/2025 à l\'indice santé lissé 130,42',
    },
    nom: 'CP 102.01 — Carrières de petit granit et calcaire (Hainaut)',
    type: 'O',
    regimeHebdo: 38,
    indexation: {
      indicePivot: 130.97, indicePivotBase: null, indicePivotSource: "CCT du 26/06/2026 (SCP 102.01, petit granit), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Le premier indice-pivot a la hausse sera celui qui sera d'application, il est fixe a 130,97. » ⚠️ Base non precisee dans l'extrait lu — a confirmer avant toute comparaison chiffree.", indicePivotDateVerif: '2026-08-24', frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-02-01', note: 'AUDIT 2026-06 — +1% (02-01 et 05-01). Easypay 102.01' },
    // ⛔ LE FONDS QUE CETTE FICHE FACTURAIT N'EXISTE PLUS DEPUIS LE 31/12/2010.
    // Corrigé le 25/08/2026. La fiche portait `taux_patronal: 0.06` — 6 % du
    // brut, soit 150 €/mois par travailleur sur un brut de 2 500 € — sans
    // aucune source. Trois sources concordantes disent qu'il n'y a rien :
    //   · ✅ TEXTE PRIMAIRE : CCT du 15/12/2010 « portant dissolution et
    //     liquidation du Fonds de sécurité d'existence dénommé "Fonds de
    //     sécurité d'existence des carrières de petit granit" », dépôt 103529,
    //     AR du 03/08/2012, art. 2 : le fonds « est dissous avec effet au
    //     31 décembre 2010 et mis en liquidation ».
    //   · ONSS, fichier des taux DmfA 2026/3 : la catégorie d'employeur de
    //     cette sous-commission ne porte AUCUNE ligne de fonds de sécurité
    //     d'existence (codes 820/830) — cohérent avec la dissolution.
    //   · Banque CCT du SPF, 25/08/2026 : les 16 conventions rangées sous le
    //     thème « fonds de sécurité d'existence » ont été lues, TOUTES LES 16
    //     (pas un échantillon — R2 ter-A). Aucune n'institue de cotisation à un
    //     fonds de sécurité d'existence. La seule cotisation patronale
    //     récurrente est celle de l'assurance groupe, ci-dessous.
    fondsSE: {
      nom: "Fonds de sécurité d'existence des carrières de petit granit — DISSOUS au 31/12/2010",
      taux_patronal: 0,
      fondsDissous: {
        le: '2010-12-31',
        source: "CCT du 15/12/2010 portant dissolution et liquidation du Fonds de sécurité d'existence des carrières de petit granit, art. 2, dépôt 103529, AR du 03/08/2012 — banque CCT du SPF Emploi, 10201/10201-2011-001552.pdf, texte primaire lu le 25/08/2026.",
        dateVerif: '2026-08-25',
      },
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      // Ce qui EXISTE : 1 % de la masse salariale vers l'assurance groupe du
      // secteur. Ce n'est PAS un fonds de sécurité d'existence — c'est un
      // second pilier — donc il ne va pas dans `taux_patronal`, qui s'affiche
      // sous l'étiquette « fonds de sécurité d'existence ».
      // ⚠️ CE 1 % N'EST AUJOURD'HUI COMPTÉ NULLE PART dans le coût employeur.
      // Il partage ce sort avec 43 autres couples (CP × public) où l'ONSS
      // perçoit un pension sectoriel sous les codes 825/835 — mesuré le
      // 25/08/2026, de 0,48 % à 4,95 %. Brancher le second pilier sur le coût
      // employeur est un chantier à part entière, pas un effet de bord.
      assuranceGroupeSecteur: {
        taux_patronal: 0.01,
        assiette: 'masse salariale',
        source: "CCT du 11/12/2019 « Classification professionnelle et conditions de travail » (SCP 102.01), art. 56 : « Une cotisation patronale équivalant à 1 p.c. de la masse salariale est affectée à l'assurance groupe du secteur. » Banque CCT du SPF Emploi, 10201/10201-2019-015411.pdf, texte primaire lu le 25/08/2026. Clause identique dans les conventions de 2014, 2015 et 2017.",
        dateVerif: '2026-08-25',
        tauxPensionOnss: 0.0097,
        ecartANommer: "L'ONSS perçoit 0,97 % sous le code 825 au trimestre 2026/3, la CCT annonce 1,00 % : 0,03 point d'écart, non expliqué à ce jour.",
      },
      source: "Fonds dissous au 31/12/2010 (CCT du 15/12/2010, dépôt 103529). L'ONSS ne publie aucune ligne de fonds de sécurité d'existence pour la catégorie d'employeur de cette sous-commission au trimestre 2026/3, et les 16 conventions du thème « FSE » de la banque du SPF, toutes lues le 25/08/2026, n'en instituent aucune.",
      note: "Le 6 % qui figurait ici avant le 25/08/2026 n'avait aucune source et alimentait un fonds liquidé depuis quinze ans.",
    },
    chequesRepas: { obligatoire: true, valeur: 6.39, part_employeur: 5.3, part_travailleur: 1.09, source: "CCT du 26/06/2026 « Classification professionnelle et conditions de travail » (SCP 102.01, carrières de petit granit), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Depuis le 1er janvier 2023, la valeur faciale du chèque-repas est de 6,39 EUR MINIMUM pour tous les travailleurs. La participation du travailleur est de 1,09 EUR par chèque-repas. » ⚠️ C'est un PLANCHER sectoriel, pas un montant figé : une entreprise peut octroyer davantage.", dateVerif: '2026-08-24', enVigueurLe: '2023-01-01', lien: '10201/10201-2026-013756.pdf' },
    // ✅ CORRIGÉ LE 21/09/2026 — PAS DE RÉGIME RÉCURRENT, même précédent que la
    // CP 330 (cf. eco-cheques-statut.js) : la banque CCT du SPF ne contient AUCUNE
    // convention instituant un droit annuel permanent. Chaque CCT sectorielle
    // (renégociée tous les 2-3 ans) accorde un octroi PONCTUEL, au titre d'une
    // année précise, d'un montant qui varie : 2019 = 250 € (art. 50, CCT du
    // 2019, 10201-2019-015411.pdf), 2021 = 150 € (art. 50, CCT du 2022,
    // 10201-2022-003364.pdf), 2025 = 250 € rétroactif (art. 65, CCT du
    // 26/06/2026, dépôt 200982/CO/102.01, 10201/10201-2026-013756.pdf — payé à
    // la signature, à tout travailleur ayant presté ≥1 jour en 2025). La CCT en
    // vigueur ne dit RIEN pour 2026 : aucun droit n'est actuellement ouvert.
    // Texte primaire lu le 21/09/2026 sur les 5 CCT successives (2019 à 2026).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.01 — 5 CCT sectorielles lues (2019, 2022, 2023, 2024, 2026), aucune n'institue de droit annuel récurrent. Chaque CCT accorde un octroi ponctuel au titre d'une année précise : 250 EUR pour 2019 (10201-2019-015411.pdf art. 50), 150 EUR pour 2021 (10201-2022-003364.pdf art. 50), 250 EUR rétroactif pour 2025 (10201/10201-2026-013756.pdf art. 65, dépôt 200982/CO/102.01). Rien n'est prévu pour 2026 dans la CCT en vigueur.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 102.01 » était
    // FAUX : deux CCT récentes lues en entier (26/06/2026, dépôts
    // 201166/CO/102.01 et 201167/CO/102.01) — la première finance un
    // dispositif d'INSERTION de jeunes carriers peu qualifiés (PFI, encadré
    // par le CEFOMEPI, 0,50% de la masse salariale), la seconde un effort
    // groupes à risque (0,05%). Aucune des deux n'accorde de droit individuel
    // de 5 jours/an.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — le secteur finance un dispositif d'insertion de jeunes carriers peu qualifiés (CEFOMEPI, 0,50% de la masse salariale, CCT du 26/06/2026 n° 201166/CO/102.01) et un effort groupes à risque (0,05%, CCT n° 201167/CO/102.01), mais aucun droit individuel sectoriel de 5 jours/an trouvé",
      source: "CCT du 26/06/2026, n° 201166/CO/102.01 et n° 201167/CO/102.01, banque CCT du SPF Emploi, textes primaires lus en entier le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.01: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.02 — Carrières de petit granit (Liège et Namur) (généré batch 29/04/2026)
  '102.02': {
    nom: 'CP 102.02 — Carrières de petit granit (Liège et Namur)',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION PROFESSIONNELLE ────────────────────────────────────
    classifications: {
      source:
        'CCT du 13/10/2020 « Conditions de rémunération » (CP 102.02), chapitre II « Classification professionnelle », art. 2 et 3 — banque CCT du SPF Emploi, 10202/10202-2020-013175.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 40, // « régime de travail de 40 heures par semaine », art. 4
      systeme: 'quatre catégories définies par métier (qualifié / spécialisé / sous surveillance / manœuvre), avec une progression barémique par ancienneté et évaluation de l\'employeur',
      // ⚠️ ANOMALIE DU TEXTE, RECOPIÉE TELLE QUELLE et NON « corrigée » : la CCT
      // intitule §2 ET §3 « spécialisés ». Le §3 décrit en réalité l'ouvrier qui
      // travaille sous surveillance jusqu'à atteindre le niveau de qualification.
      // Le tableau salarial de l'art. 4, lui, distingue « Spécialisé » et
      // « Spécialisé + » — c'est vraisemblablement cette paire que les deux §
      // recouvrent, mais la CCT ne le dit pas : à faire trancher, pas à deviner.
      anomalieTexte: 'les §2 et §3 de l\'art. 2 portent tous deux le libellé « spécialisés » ; le §3 décrit l\'ouvrier sous surveillance et responsabilité d\'un qualifié. Signalé, non corrigé.',
      fonctions: [
        { categorie: 'qualifié', fonction: 'Rocteur' },
        { categorie: 'qualifié', fonction: 'Opérateur polyvalent de haveuse et scieur à fil' },
        { categorie: 'qualifié', fonction: 'Mineur' },
        { categorie: 'qualifié', fonction: 'Tailleur de pierre manuel' },
        { categorie: 'qualifié', fonction: 'Ouvrier de maintenance (forgeron, mécanicien, soudeur, électricien, magasinier…)' },
        { categorie: 'qualifié', fonction: 'Opérateur de concasseurs' },
        { categorie: 'qualifié', fonction: 'Débiteur-finisseur façonnant des pierres à mesures finies d\'après croquis' },
        { categorie: 'qualifié', fonction: 'Ciseleur à la machine ayant la qualification de tailleur de pierres' },
        { categorie: 'qualifié', fonction: 'Opérateur de pelle ou de bulldozer assurant la maintenance journalière' },
        { categorie: 'spécialisé', fonction: 'Scieur diamanté' },
        { categorie: 'spécialisé', fonction: 'Opérateur de haveuse' },
        { categorie: 'spécialisé', fonction: 'Scieur à lames' },
        { categorie: 'spécialisé', fonction: 'Opérateur de ponts' },
        { categorie: 'spécialisé', fonction: 'Foreur sans utilisation d\'explosifs' },
        { categorie: 'spécialisé', fonction: 'Cliveur' },
        { categorie: 'spécialisé', fonction: 'Préposé de chargement au concassage' },
        { categorie: 'spécialisé', fonction: 'Polisseur' },
        { categorie: 'spécialisé', fonction: 'Débiteur' },
        { categorie: 'spécialisé', fonction: 'Ciseleur' },
        { categorie: 'spécialisé', fonction: 'Conducteur d\'engins mécaniques autres que ceux de la catégorie « qualifiés »' },
        { categorie: 'spécialisé', fonction: 'Assistant des ouvriers de maintenance' },
        { categorie: 'sous-surveillance', fonction: 'Ouvrier travaillant sous la surveillance et la responsabilité d\'un ouvrier qualifié, jusqu\'à atteindre le niveau de qualification' },
        { categorie: 'manœuvre', fonction: 'Ouvrier sans expérience utile pour être classé dans une des catégories ci-dessus' },
      ],
      progression: [
        { echelon: 'Manœuvre', versEchelon: 'Spécialisé', condition: 'après 3 ans maximum dans la fonction, évaluation par l\'employeur' },
        { echelon: 'Spécialisé', versEchelon: 'Spécialisé +', condition: 'après 2 ans maximum dans la fonction, évaluation par l\'employeur' },
        { echelon: 'Spécialisé +', paliers: ['0 an', '5 ans', '7 ans'], versEchelon: 'Qualifié', condition: 'après 2 ans maximum dans la fonction, évaluation par l\'employeur' },
        { echelon: 'Qualifié', paliers: ['0 an', '3 ans', '5 ans'], versEchelon: 'Qualifié +', condition: 'décision de l\'employeur' },
        { echelon: 'Qualifié +', paliers: ['0 an', '3 ans', '5 ans', '7 ans'] },
      ],
      occasionnel:
        'l\'ouvrier appelé occasionnellement à une fonction INFÉRIEURE conserve son salaire habituel ; appelé à une fonction SUPÉRIEURE, il obtient le salaire y afférent pendant cette période, pour autant qu\'il effectue normalement le travail (art. 3).',
      montantsNonCodes: 'les barèmes de l\'art. 4 datent du 01/05/2020 — non repris ici, le secteur a indexé depuis.',
    },
    indexation: { frequence: 'annuelle', dernierTaux: 0.01, derniereDate: '2026-06-01', note: 'AUDIT 2026-06 — +1% au 01/06. Easypay 102.02' },
    fondsSE: {
      nom: "Fonds de sécurité d'existence — carrières de petit granit et de calcaire à tailler des provinces de Liège et de Namur (SCP 102.02)",
      taux_patronal: 0.06,
      tauxASourcer: true,
      // ── CE QUI A ÉTÉ LU LE 26/08/2026, ET LE PIÈGE À NE PAS REFAIRE ──────────
      // Le fonds EXISTE et il est VIVANT : institué par la CCT du 12/03/1990
      // (AR du 13/08/1990, MB du 07/09/1990), il est encore modifié en 2011,
      // 2014 et 2015. L'art. 15 bis (version du 09/10/2014, dépôt 124783) dit
      // ce qu'il perçoit EN PLUS : cotisations de solidarité pour les RCC,
      // cotisations de formation des employés du petit granit, cotisations
      // syndicales. Le taux GÉNÉRAL, lui, vit dans la CCT fondatrice de 1990.
      //
      // ⛔ LE PIÈGE : il existe DEUX fonds dans cette famille, et l'un est
      // dissous. La CCT du 15/12/2010 (dépôt 103529) dissout et liquide le
      // « Fonds de sécurité d'existence des carrières de petit granit », créé
      // en 1976 — au 31/12/2010, liquidateur nommé. Conclure « le fonds est
      // dissous, donc 0 » serait FAUX : ce fonds-là n'est pas celui de 1990,
      // qui continue d'être modifié quatre ans après la dissolution de l'autre.
      // Deux fonds, deux périmètres, une seule dissolution.
      //
      // 📏 POURQUOI LE TAUX RESTE INTROUVABLE, avec son dénominateur : la
      // banque CCT du SPF ne sert cette sous-commission qu'à partir de 1999
      // (126 conventions pour la 102.02, 137 pour la 102.04, réparties de 1999
      // à 2026 — aucune antérieure). La CCT fondatrice de 1990 n'y est pas, et
      // AUCUNE des modifications servies ne remplace l'article de cotisation :
      // celles de 1999, 2011 et 2014 remplacent les art. 5, 8 et 15 bis, lus au
      // texte primaire le 26/08/2026. L'ONSS ne publie aucune catégorie pour
      // ces deux sous-commissions (recherche par numéro ET par nom : « petit
      // granit », « quartzite », « grès » → 0 sur 232 catégories ; les
      // catégories 063, 090 et 463 couvrent les SCP 102.05, 102.08 et 102.06).
      //
      // ➡️ OÙ CHERCHER ENSUITE, dans l'ordre : (1) le texte de la CCT du
      // 12/03/1990 au Moniteur belge du 07/09/1990 via Justel, puisque l'AR de
      // rendu obligatoire y est publié ; (2) le fonds lui-même, dont le siège
      // est à 4140 Sprimont, Bois le Comte 36 (art. 5, version 2014).
      lectureFaite: {
        date: '2026-08-26',
        fondsInstituePar: "CCT du 12/03/1990, AR du 13/08/1990, MB du 07/09/1990 — texte non servi par la banque CCT",
        modificationsLues: ['03/05/1999 (art. 8)', '05/07/2011 (art. 8, dépôt 106410)', '09/10/2014 (art. 5, 8 et 15 bis, dépôt 124783/124784)'],
        aucuneNeFixeLeTaux: true,
        pisteEcartee: "La CCT du 15/12/2010 (dépôt 103529) dissout le fonds de 1976 « des carrières de petit granit », PAS celui de 1990. Ne pas en conclure un taux nul.",
        prochaineVoie: "Moniteur belge du 07/09/1990 (AR du 13/08/1990) via Justel, ou le fonds lui-même à Sprimont.",
      },
      // 🔴 LE 6 % RESTE NON SOURCE, ET LA RECHERCHE A ETE FAITE — c'est la
      // difference avec un simple aveu. Mesure du 25/08/2026 : sur les
      // conventions « fonds » de cette sous-commission, UNE SEULE fixe une
      // cotisation a portee generale, la CCT du 31/08/2007. Elle ne fixe PAS
      // un taux global : elle fixe une COTISATION DE SOLIDARITE affectee au
      // paiement des prepensions.
      //   art. 3 — 0,20 % par TRIMESTRE de la masse salariale des le 01/01/2007
      //   art. 4 — 0,25 % par TRIMESTRE des le 01/01/2008
      // Deux raisons de ne pas remplacer le 6 % par ce chiffre : ce n'est
      // qu'UNE composante (les prepensions), et elle est TRIMESTRIELLE quand le
      // taux code est annuel. Confondre les deux donnerait un ecart de 24x.
      lectureFaite: {
        cotisationSolidaritePrepensions: { tauxParTrimestre: 0.0025, depuis: '2008-01-01' },
        source: "CCT du 31/08/2007 (SCP 102.02 et 102.04), art. 3 et 4, texte primaire lu le 25/08/2026 : « A partir du 1er janvier 2008, la cotisation des employeurs au Fonds de securite d'existence est fixee trimestriellement a 0,25 p.c. de la masse salariale, en vue du paiement des prepensions (cotisation de solidarite). » Banque CCT du SPF Emploi. La meme convention est deposee pour les deux sous-commissions.",
        dateVerif: '2026-08-25',
        conclusion: "aucune convention fixant un taux GLOBAL n'a ete trouvee pour cette sous-commission. Le 6 % code reste sans origine identifiee.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (7,00 €) était la famille
    // recopiée {7/5.91/1.09}. Synova (ex-CGSLB), tableau « chèque-repas
    // minimum » par sous-commission : 6,00 € pour 102.02. 🟠 Répartition
    // employeur/travailleur non donnée par cette source — à confirmer sur la
    // CCT primaire (10202/…pdf) avant usage en calcul de paie.
    chequesRepas: {
      obligatoire: true, valeur: 6.00, part_employeur: null, part_travailleur: null, aVerifier: true,
      aVerifierRaison: "Montant confirmé (Synova), répartition employeur/travailleur non trouvée — à confirmer sur la CCT primaire.",
      source: 'Synova (ex-CGSLB), tableau chèque-repas par sous-commission CP 102',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 11/03/2026 (SCP 102.02, carrières de petit granit de Liège et Namur), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026. ⚠️ AUCUN régime annuel en vigueur aujourd'hui : le texte prévoit une PRIME UNIQUE de 250 EUR en écochèques au 01/06/2026 au prorata de 2025, puis un écochèque annuel de 120 EUR seulement À PARTIR DU 01/06/2027. Coder 250 EUR/an aujourd'hui serait faux deux fois : sur la nature (unique) et sur le montant à venir (120 EUR).", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — « 5 jours, Fonds sectoriel CP
    // 102.02 » était FAUX sur le mécanisme (aucun fonds) : CCT du 13/10/2020
    // (dépôt 162251/CO/102.02), « Efforts de formation » (loi Peeters du
    // 05/03/2017, art. 9-21) — trajectoire 2j (2017-18) → 3j (2019-20) →
    // 4j (2021-22) → 5j (2023-2024), effort COLLECTIF moyen par ETP, pas un
    // droit individuel garanti. ⚠️ Cette CCT est elle-même bornée
    // 01/01/2019-31/12/2020 alors qu'elle décrit une trajectoire jusqu'en
    // 2024 — aucun renouvellement 2021+ retrouvé lors de cette recherche.
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      mecanisme: "effort de formation collectif moyen par ETP, loi Peeters du 05/03/2017 (art. 9-21) — cible atteinte en 2023-2024",
      source: "CCT du 13/10/2020 (CP 102.02), dépôt 162251/CO/102.02, banque CCT du SPF Emploi — ⚠️ durée formelle 01/01/2019-31/12/2020, aucun renouvellement 2021+ retrouvé",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Source formellement expirée fin 2020 ; à reconfirmer par une recherche ciblée sur un renouvellement récent (ex. « efforts de formation 2025 2026 » filtrée CP 102.02).",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.03 — Carrières de porphyre (Brabant wallon + Hainaut) (généré batch 29/04/2026)
  '102.03': {
    nom: 'CP 102.03 — Carrières de porphyre (Brabant wallon + Hainaut)',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ CAS SUBTIL, ET IL VAUT D'ÊTRE CODÉ TEL QUEL : les catégories
    // professionnelles de ce secteur EXISTENT — la CCT les présuppose et
    // organise le passage de l'une à l'autre (art. 6 à 9) — mais aucune CCT
    // déposée n'en donne la LISTE. Écrire « pas de classification » serait
    // faux ; écrire une liste serait de l'invention. On code le mécanisme.
    classifications: {
      source: 'CCT du 30/06/2005 (CP 102.03), art. 6 à 9 — banque CCT du SPF Emploi, 10203/10203-2005-002500.pdf ; CCT du 30/06/1999 identique sur ces articles. Textes primaires lus le 23/08/2026',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 37, // art. 37 : « la durée hebdomadaire de travail est de 37 heures »
      systeme: "des catégories professionnelles structurent le barème et la CCT règle la mobilité entre elles, mais aucune CCT déposée n'en publie la liste",
      listeNonPubliee: true,
      mecanisme: [
        "jeunes de moins de 21 ans : pourcentage du salaire des travailleurs de la MÊME catégorie professionnelle — 85 % à 18 ans, 90 % à 19 ans, 100 % à partir de 20 ans (art. 6)",
        "un jeune dont l'aptitude et le rendement sont ceux d'un adulte perçoit 100 % du salaire de sa catégorie, quel que soit son âge (art. 7)",
        "appelé momentanément ou occasionnellement dans une catégorie INFÉRIEURE, le travailleur conserve sa rémunération habituelle — ou la moyenne du salaire aux pièces réalisé pendant cette période par son groupe ou sa section (art. 8)",
        "appelé dans une catégorie SUPÉRIEURE, il perçoit le salaire correspondant (art. 9)",
      ],
      ouLaDemander: "la liste des catégories n'est ni dans les CCT déposées (2 conventions citant la classification, lues) ni dans la fiche SPF (cadre vide depuis le 01/09/2022) — à demander au SPF ou à la sous-commission.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.01, derniereDate: '2026-06-01', note: 'AUDIT 2026-06 — +1% au 01/06. Easypay 102.03' },
    // ── UN FONDS QUI NE SE COMPTE PAS EN POURCENTAGE ───────────────────────
    // ✅ CCT du 08/04/2026 « conditions de travail », art. 35, 36 et 41 — dépôt
    // 199919/CO/102.03, banque CCT du SPF Emploi, 10203/10203-2026-004427.pdf,
    // texte primaire lu le 26/08/2026. C'est la convention la PLUS RÉCENTE des
    // 48 que la banque publie pour cette sous-commission, et les 48 titres ont
    // été lus : aucune ne porte des statuts de fonds de sécurité d'existence.
    //
    // ⚠️ LE 6 % QUI FIGURAIT ICI SE TROMPAIT DE NATURE, pas seulement de valeur.
    // Ce fonds ne perçoit AUCUN pourcentage du brut. Il perçoit :
    //   · 145 € par an et par travailleur AFFILIÉ à une organisation syndicale
    //     (art. 35), pour financer la prime syndicale — donc pas par tête de
    //     l'effectif, mais par tête des syndiqués, une donnée que l'app n'a pas ;
    //   · 0,0037 € par heure effectivement prestée ou assimilée (art. 41), pour
    //     la formation syndicale. Sur un temps plein à 38 h, cela fait environ
    //     0,61 € par mois — trois cents fois moins que les 150 € que le 6 %
    //     facturait sur un brut de 2 500 €.
    //
    // 🏦 LE SECOND PILIER EXISTE ET SE COMPTE AUSSI EN EUROS, PAS EN POURCENTS.
    // CCT du 16/11/2016 instaurant le régime de pension sectoriel social (dépôt
    // 136303/CO/102.03), règlement financier annexé, point 3, lu le 26/08/2026.
    fondsSE: {
      nom: "Fonds de sécurité d'existence de l'industrie des carrières de porphyre du Hainaut et des carrières de quartzite du Brabant wallon",
      taux_patronal: 0,
      cctSourcee: {
        taux: 0,
        raison: "Ce fonds ne perçoit aucune cotisation proportionnelle au brut : ses deux ressources sont un montant annuel par syndiqué et un montant horaire. Un taux de 0 est donc la réponse JUSTE pour un calcul en pourcentage, pas un aveu d'ignorance — les deux montants réels sont écrits ci-dessous.",
        source: "CCT du 08/04/2026 (SCP 102.03), art. 35 (« la prime annuelle […] est financée par le paiement d'un montant de 145 EUR/an par travailleur affilié aux organisations syndicales représentatives des travailleurs. Celle-ci sera versée au fonds de sécurité d'existence de l'industrie des carrières de porphyre ») et art. 41 (« une contribution patronale de 0,0037 EUR par heure effectivement prestée ou assimilée est versée au Fonds de sécurité d'existence ») — dépôt 199919/CO/102.03, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Census des 48 conventions de la sous-commission : aucune ne porte des statuts de fonds.",
        dateVerif: '2026-08-26',
      },
      // 💰 Les deux ressources réelles, NON facturées par l'app : la première
      // demande de savoir qui est syndiqué, la seconde des heures réelles.
      cotisationsForfaitaires: {
        primeSyndicale: { montantAnnuelParSyndique: 145.00, source: 'art. 35 de la CCT du 08/04/2026 ; payable fin février (art. 36). Étendue aux pensionnés depuis le 01/01/2022.' },
        formationSyndicale: { parHeurePresteeOuAssimilee: 0.0037, source: 'art. 41 de la CCT du 08/04/2026' },
      },
      // 🏦 Second pilier — documenté, pas facturé : il se compte par équivalent
      // temps plein et par an, ce que le calcul mensuel en pourcentage ne sait
      // pas représenter.
      pensionSectorielle: {
        regime: "Fonds 2ème pilier SCP 102.03 (BCE 0659.979.981) — régime de pension sectoriel social, en vigueur au 01/01/2017",
        retraiteParAnParETP: 100.00,
        solidariteParAnParETP: 12.90,
        horsFraisEtTaxes: true,
        cotisationSocialeSurAllocation: 0.0886,
        solidariteMinimumPctDeLaPrime: 0.044,
        source: "CCT du 16/11/2016 (SCP 102.03) instaurant le régime de pension sectoriel social, règlement financier annexé, point 3 : « 100 € (hors frais et taxes) sur base annuelle pour l'année 2017 et suivantes » pour le volet retraite, « 12,9 € (hors frais et taxes) sur base annuelle » pour le volet solidarité, par équivalent temps plein. Point 10.3 de la CCT : la cotisation de sécurité sociale de 8,86 % sur l'allocation de pension est déclarée et payée par le fonds. Dépôt 136303/CO/102.03, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
      },
      note: "Le 6 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait un fonds à pourcentage, ce que ce fonds n'est pas.",
    },
    chequesRepas: { obligatoire: true, valeur: 10.0, part_employeur: 8.91, part_travailleur: 1.09, source: "CCT du 08/04/2026 (SCP 102.03, carrières de porphyre du Hainaut et de quartzite du Brabant wallon), banque CCT du SPF, texte primaire lu le 24/08/2026 : « A compter du 1er janvier 2026, il est octroyé aux travailleurs, par journée réellement prestée, un titre-repas d'une valeur faciale de 10 EUR, dont 1,09 EUR à charge du travailleur. » Octroi PAR JOURNÉE RÉELLEMENT PRESTÉE.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '10203/10203-2026-010964.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.03 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026). Les orthographes « eco cheque » et « éco-chèques » remontent 5 CCT (2015-2026), mais aucune des 5 ne contient le mot « chèque » nulle part dans son texte intégral — c'est le repli générique du moteur, pas une trouvaille.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — « 5 jours, Fonds sectoriel CP
    // 102.03 » était FAUX sur le mécanisme ET la valeur : CCT du 28/09/2023
    // (dépôt 182988/CO/102.03), « Droit individuel à la formation » (loi du
    // 03/10/2022, ch. 12) — trajectoire ferme 2j (01/01/2023) → 3j
    // (01/01/2025, valeur actuelle) → 4j (01/01/2027) → 5j (01/01/2029).
    // Aucun fonds. ⚠️ CCT elle-même bornée 01/01/2023-31/12/2024.
    formation: {
      joursSectoriels: 3,
      fondsFormationExiste: false,
      base: "droit individuel à la formation, loi du 03/10/2022 chapitre 12",
      trajectoire: { '2023-01-01': 2, '2025-01-01': 3, '2027-01-01': 4, '2029-01-01': 5 },
      source: "CCT du 28/09/2023 (CP 102.03), art. 2, dépôt 182988/CO/102.03, banque CCT du SPF Emploi — ⚠️ durée formelle jusqu'au 31/12/2024",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Source formellement expirée fin 2024 ; à reconfirmer qu'un renouvellement maintient la trajectoire (palier 2027 à venir).",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 102.03: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.04 — Carrières de grès et quartzite (tout royaume) (généré batch 29/04/2026)
  '102.04': {
    // ── Classification CP 102.04 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 13/10/2020 « Conditions de travail »,
    // banque CCT du SPF Emploi (10204/10204-2020-013160.pdf), chapitre II
    // art. 2. Trouvée par recherche plein texte.
    //
    // ⚠️ LA CATÉGORIE C SE DÉFINIT PAR DÉFAUT, ET C'EST OPÉRATOIREMENT
    // IMPORTANT : elle vise « les ouvriers qui ne disposent pas de
    // l'expérience utile » pour entrer dans A ou B. On ne classe donc PAS un
    // ouvrier en C d'après son métier, mais faute de pouvoir le classer
    // ailleurs — l'ordre d'examen des trois catégories compte.
    classifications: {
      source: 'CCT du 13/10/2020, chapitre II art. 2 — banque CCT du SPF Emploi, 10204/10204-2020-013160.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « Conditions de travail »',
      categories: [
        { code: 'A', label: 'Ouvriers qualifiés', metiers: ['mineurs', 'refendeurs', 'épinceurs', 'ouvriers de maintenance (mécaniciens, électriciens, magasiniers…)', 'opérateurs de pelles mécaniques et bulldozers', 'conducteurs de locomotives agréés par la SNCB'] },
        { code: 'B', label: 'Ouvriers spécialisés', metiers: ["foreurs SANS usage d'explosifs", 'conducteurs d\'engins mécaniques autres que ceux définis en catégorie A', 'opérateurs de concasseurs', 'assistants des ouvriers de maintenance'] },
        { code: 'C', label: 'Manœuvres', definitionParDefaut: "les ouvriers qui ne disposent pas de l'expérience utile pour être classés dans l'une ou l'autre des deux catégories ci-dessus" },
      ],
    },
    nom: 'CP 102.04 — Carrières de grès et quartzite (tout royaume)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-02-01', note: 'AUDIT 2026-06 — +1% (02-01 et 05-01). Easypay 102.04.01' },
    fondsSE: {
      nom: "Fonds de sécurité d'existence — carrières de grès et de quartzite du Royaume, hors Brabant wallon (SCP 102.04)",
      taux_patronal: 0.06,
      tauxASourcer: true,
      // ── CE QUI A ÉTÉ LU LE 26/08/2026, ET LE PIÈGE À NE PAS REFAIRE ──────────
      // Le fonds EXISTE et il est VIVANT : institué par la CCT du 12/03/1990
      // (AR du 13/08/1990, MB du 07/09/1990), il est encore modifié en 2011,
      // 2014 et 2015. L'art. 15 bis (version du 09/10/2014, dépôt 124783) dit
      // ce qu'il perçoit EN PLUS : cotisations de solidarité pour les RCC,
      // cotisations de formation des employés du petit granit, cotisations
      // syndicales. Le taux GÉNÉRAL, lui, vit dans la CCT fondatrice de 1990.
      //
      // ⛔ LE PIÈGE : il existe DEUX fonds dans cette famille, et l'un est
      // dissous. La CCT du 15/12/2010 (dépôt 103529) dissout et liquide le
      // « Fonds de sécurité d'existence des carrières de petit granit », créé
      // en 1976 — au 31/12/2010, liquidateur nommé. Conclure « le fonds est
      // dissous, donc 0 » serait FAUX : ce fonds-là n'est pas celui de 1990,
      // qui continue d'être modifié quatre ans après la dissolution de l'autre.
      // Deux fonds, deux périmètres, une seule dissolution.
      //
      // 📏 POURQUOI LE TAUX RESTE INTROUVABLE, avec son dénominateur : la
      // banque CCT du SPF ne sert cette sous-commission qu'à partir de 1999
      // (126 conventions pour la 102.02, 137 pour la 102.04, réparties de 1999
      // à 2026 — aucune antérieure). La CCT fondatrice de 1990 n'y est pas, et
      // AUCUNE des modifications servies ne remplace l'article de cotisation :
      // celles de 1999, 2011 et 2014 remplacent les art. 5, 8 et 15 bis, lus au
      // texte primaire le 26/08/2026. L'ONSS ne publie aucune catégorie pour
      // ces deux sous-commissions (recherche par numéro ET par nom : « petit
      // granit », « quartzite », « grès » → 0 sur 232 catégories ; les
      // catégories 063, 090 et 463 couvrent les SCP 102.05, 102.08 et 102.06).
      //
      // ➡️ OÙ CHERCHER ENSUITE, dans l'ordre : (1) le texte de la CCT du
      // 12/03/1990 au Moniteur belge du 07/09/1990 via Justel, puisque l'AR de
      // rendu obligatoire y est publié ; (2) le fonds lui-même, dont le siège
      // est à 4140 Sprimont, Bois le Comte 36 (art. 5, version 2014).
      lectureFaite: {
        date: '2026-08-26',
        fondsInstituePar: "CCT du 12/03/1990, AR du 13/08/1990, MB du 07/09/1990 — texte non servi par la banque CCT",
        modificationsLues: ['03/05/1999 (art. 8)', '05/07/2011 (art. 8, dépôt 106410)', '09/10/2014 (art. 5, 8 et 15 bis, dépôt 124783/124784)'],
        aucuneNeFixeLeTaux: true,
        pisteEcartee: "La CCT du 15/12/2010 (dépôt 103529) dissout le fonds de 1976 « des carrières de petit granit », PAS celui de 1990. Ne pas en conclure un taux nul.",
        prochaineVoie: "Moniteur belge du 07/09/1990 (AR du 13/08/1990) via Justel, ou le fonds lui-même à Sprimont.",
      },
      // 🔴 LE 6 % RESTE NON SOURCE, ET LA RECHERCHE A ETE FAITE — c'est la
      // difference avec un simple aveu. Mesure du 25/08/2026 : sur les
      // conventions « fonds » de cette sous-commission, UNE SEULE fixe une
      // cotisation a portee generale, la CCT du 31/08/2007. Elle ne fixe PAS
      // un taux global : elle fixe une COTISATION DE SOLIDARITE affectee au
      // paiement des prepensions.
      //   art. 3 — 0,20 % par TRIMESTRE de la masse salariale des le 01/01/2007
      //   art. 4 — 0,25 % par TRIMESTRE des le 01/01/2008
      // Deux raisons de ne pas remplacer le 6 % par ce chiffre : ce n'est
      // qu'UNE composante (les prepensions), et elle est TRIMESTRIELLE quand le
      // taux code est annuel. Confondre les deux donnerait un ecart de 24x.
      lectureFaite: {
        cotisationSolidaritePrepensions: { tauxParTrimestre: 0.0025, depuis: '2008-01-01' },
        source: "CCT du 31/08/2007 (SCP 102.02 et 102.04), art. 3 et 4, texte primaire lu le 25/08/2026 : « A partir du 1er janvier 2008, la cotisation des employeurs au Fonds de securite d'existence est fixee trimestriellement a 0,25 p.c. de la masse salariale, en vue du paiement des prepensions (cotisation de solidarite). » Banque CCT du SPF Emploi. La meme convention est deposee pour les deux sous-commissions.",
        dateVerif: '2026-08-25',
        conclusion: "aucune convention fixant un taux GLOBAL n'a ete trouvee pour cette sous-commission. Le 6 % code reste sans origine identifiee.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (7,00 €) était la famille
    // recopiée {7/5.91/1.09}. Synova (ex-CGSLB), tableau chèque-repas par
    // sous-commission : 6,00 € pour 102.04 (3/3 lectures concordantes).
    // 🟠 Répartition employeur/travailleur non donnée — à confirmer sur la
    // CCT primaire (10204/…pdf).
    chequesRepas: {
      obligatoire: true, valeur: 6.00, part_employeur: null, part_travailleur: null, aVerifier: true,
      aVerifierRaison: "Montant confirmé (Synova, 3 lectures concordantes), répartition non trouvée.",
      source: 'Synova (ex-CGSLB), tableau chèque-repas par sous-commission CP 102',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 11/03/2026 (SCP 102.04, carrières de grès et quartzite hors Brabant wallon), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026, convention jumelle de celle de la SCP 102.02. ⚠️ AUCUN régime annuel en vigueur aujourd'hui : prime UNIQUE de 250 EUR au 01/06/2026, puis écochèque annuel de 120 EUR à partir du 01/06/2027 seulement.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — même secteur jumeau que CP
    // 102.02, texte de CCT quasi identique : CCT du 13/10/2020 (dépôt
    // 162276/CO/102.04), « Efforts de formation » (loi Peeters), trajectoire
    // 2j→3j→4j→5j (palier 2023-2024), effort COLLECTIF moyen par ETP, pas de
    // fonds. ⚠️ CCT bornée 01/01/2019-31/12/2020, aucun renouvellement 2021+
    // retrouvé.
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      mecanisme: "effort de formation collectif moyen par ETP, loi Peeters du 05/03/2017 (art. 9-21) — cible atteinte en 2023-2024",
      source: "CCT du 13/10/2020 (CP 102.04), dépôt 162276/CO/102.04, banque CCT du SPF Emploi — ⚠️ durée formelle 01/01/2019-31/12/2020, aucun renouvellement 2021+ retrouvé",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Source formellement expirée fin 2020 ; à reconfirmer par une recherche ciblée sur un renouvellement récent.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.04: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.05 — Carrières de kaolin et sable (Brabant W, Hainaut...) (généré batch 29/04/2026)
  '102.05': {
    nom: 'CP 102.05 — Carrières de kaolin et sable (Brabant W, Hainaut...)',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      regimeHebdoCCT: 38,
      systeme: "trois catégories classiques de l'extraction — manœuvres, spécialisés, qualifiés",
      fonctions: [
        { categorie: 1, fonction: 'Manœuvres' },
        { categorie: 2, fonction: 'Spécialisés' },
        { categorie: 3, fonction: 'Qualifiés' },
      ],
      mineurs: "pour les moins de 18 ans, c'est le régime de la CP 100 qui s'applique — la fiche le dit explicitement, ce n'est pas une lacune de cette sous-commission.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-03-01', note: 'AUDIT 2026-06 — +1% (03-01 et 06-01). Easypay 102.05' },
    fondsSE: {
      nom: 'Fonds social CP 102.05',
      taux_patronal: 0.017,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '063',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 063 : 820 ouvriers 1,70 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 6,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (7,00 €) était la famille
    // recopiée {7/5.91/1.09}. Synova (ex-CGSLB), tableau chèque-repas par
    // sous-commission : 5,50 € pour 102.05 (3/3 lectures concordantes).
    // 🟠 Répartition employeur/travailleur non donnée — à confirmer sur la
    // CCT primaire.
    chequesRepas: {
      obligatoire: true, valeur: 5.50, part_employeur: null, part_travailleur: null, aVerifier: true,
      aVerifierRaison: "Montant confirmé (Synova, 3 lectures concordantes), répartition non trouvée.",
      source: 'Synova (ex-CGSLB), tableau chèque-repas par sous-commission CP 102',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.05 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — « Fonds sectoriel CP 102.05 »
    // était FAUX (aucun fonds nommé, les partenaires sociaux devaient
    // seulement « entamer une discussion » sur l'opportunité d'en créer un) :
    // CCT du 04/12/2020, dépôt 162715/CO/102.05, art. 2 — trajectoire 3j
    // (2019) → 4j (2020) → objectif 5j « au plus tard en 2023 ». ⚠️ Cette CCT
    // a cessé de produire ses effets le 31/12/2020, aucun renouvellement
    // retrouvé dans la banque CCT (les autres CCT « efforts de formation »
    // disponibles datent de 2018 et 2015, mêmes termes progressifs, toutes
    // expirées).
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      base: "objectif de 5 jours/an/travailleur temps plein, CCT sectorielle EXPIRÉE (trajectoire 3j→4j→5j visant 2023)",
      source: "CCT du 04/12/2020 (CP 102.05), art. 2, dépôt 162715/CO/102.05, banque CCT du SPF Emploi — ⚠️ expirée le 31/12/2020, aucun renouvellement retrouvé",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Source expirée fin 2020, aucune suite retrouvée ; à défaut, le droit individuel fédéral de la loi du 03/10/2022 s'applique.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.05: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.06 — Carrières de gravier et sable (Flandre, Limbourg...) (généré batch 29/04/2026)
  '102.06': {
    nom: 'CP 102.06 — Carrières de gravier et sable (Flandre, Limbourg...)',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    classifications: {
      source:
        'CCT du 18/03/2026 « Salaires » (CP 102.06), art. 2 — banque CCT du SPF Emploi, 10206/10206-2026-003800.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 40, // « op basis van een wekelijkse arbeidsduur van 40 uren », art. 2
      systeme: 'six classes de fonctions (functieklassen) 1 à 6',
      fonctions: [
        { categorie: 1 }, { categorie: 2 }, { categorie: 3 },
        { categorie: 4 }, { categorie: 5 }, { categorie: 6 },
      ],
      definitionsHorsPortee:
        'la CCT salariale nomme les six classes et leur barème mais ne définit pas les fonctions qui les composent. Les CCT de 1999 et 2000 lues le 23/08/2026 n\'y renvoient qu\'indirectement (« salaire de la classification » / « functieloon »), sans grille.',
      progressionEntree:
        'ouvrier nouvellement engagé : 90 % du salaire horaire minimum les 6 premiers mois, 95 % après 6 mois (ou du salaire de la classification après évaluation positive), 100 % après un an (CCT du 23/06/2008, n° 88929/CO/102.06).',
      montantsNonCodes: 'les barèmes du 01/03/2026 par classe ne sont pas repris ici — la dimension codée est la structure.',
    },
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-03-01', note: 'AUDIT 2026-06 — +2% au 01/03. Easypay 102.06' },
    fondsSE: {
      nom: 'Fonds social CP 102.06',
      taux_patronal: 0.015,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '463',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 463 : 820 ouvriers 1,50 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 6,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 10.0, part_employeur: 8.91, part_travailleur: 1.09, source: "CCT du 17/06/2026 « Conditions de travail dans les exploitations de sable blanc » (SCP 102.06), banque CCT du SPF, texte primaire lu le 24/08/2026, chapitre XIV art. 19 : part patronale 8,91 EUR par jour travaillé depuis le 01/01/2026, part travailleur 1,09 EUR, valeur nominale 10,00 EUR. ⚠️ Le chapitre chèques-repas est en fin de convention : un balayage par mots-clés y ramasse d'abord les salaires horaires du chapitre II.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '10206/10206-2026-012650.pdf' },
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 18/03/2026 (10206/10206-2026-003800.pdf), banque CCT du SPF Emploi, dépôt 199081/CO/102.06, texte primaire lu le 21/09/2026, art. 23 : « dans le cadre de l'harmonisation des statuts, un éco-chèque est attribué aux ouvriers, tel que c'est déjà le cas pour les employés. À partir de 2023, un éco-chèque de 250 EUR sera attribué chaque année », référence du 1er juillet de l'année précédente au 30 juin de l'année en cours. En vigueur depuis le 01/01/2025, durée indéterminée.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026, DONNÉES INSUFFISANTES — « 5 jours, Fonds
    // sectoriel CP 102.06 » était FAUX (aucun fonds ni droit chiffré dans les
    // documents lus), mais un titre pertinent identifié par la recherche
    // (« Efforts de formation », dépôt 2022, 10206-2022-010846.pdf) N'A PAS
    // ENCORE été téléchargé/lu — ne pas conclure à une absence sans l'avoir
    // lu (R2 ter). Les 5 documents effectivement lus (crédit-temps ×4,
    // groupes à risque 0,10%+0,05%, dépôt 196846/CO/102.06) ne mentionnent
    // aucun droit de 5 jours.
    formation: {
      joursSectoriels: null,
      renvoi: "donnée insuffisante — CCT « Efforts de formation » (10206-2022-010846.pdf, dépôt 2022) identifiée mais texte non encore lu ; les CCT lues (crédit-temps, groupes à risque 0,10%+0,05% masse salariale, dépôt 196846/CO/102.06) ne mentionnent aucun droit de 5 jours. À défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "Banque CCT du SPF Emploi, recherche CP 102.06 + formation/opleiding, 25/09/2026 — document pertinent identifié mais pas encore lu",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Lire 10206-2022-010846.pdf (« Efforts de formation ») avant de trancher définitivement.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 102.06: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.07 — Carrières, cimenteries et fours à chaux (Tournai) (généré batch 29/04/2026)
  '102.07': {
    // ── Classification CP 102.07 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 07/10/2020, banque CCT du SPF Emploi
    // (10207/10207-2020-012282.pdf).
    //
    // ⚠️ DEUX SYSTÈMES DANS UNE MÊME CP, ET ILS NE SE MÉLANGENT PAS : les
    // travailleurs d'exploitation relèvent de CATÉGORIES en chiffres romains,
    // les travailleurs d'entretien de CLASSES en lettres. Servir une échelle
    // à la place de l'autre classerait un ouvrier dans un système qui ne le
    // concerne pas.
    //
    // ⛔ LES TAUX HORAIRES DE LA CCT NE SONT PAS REPRIS. Ils sont datés du
    // 01/10/2018 à l'indice santé lissé 105,21 : les recopier ferait entrer
    // des barèmes périmés de plusieurs années dans le fichier. Seule la
    // STRUCTURE est codée ; les montants du jour vivent dans la grille
    // vérifiée (R1, une seule vérité par valeur).
    classifications: {
      source: 'CCT du 07/10/2020 — banque CCT du SPF Emploi, 10207/10207-2020-012282.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      methode: "chaque groupe a sa liste détaillée de fonctions ; les fonctions sont ensuite regroupées sous des définitions générales appelées « catégories » ou « classes »",
      groupes: [
        {
          code: 'I', label: "Travailleurs d'exploitation et services généraux",
          echelle: 'catégories',
          echelons: [
            { code: 'III', label: "Catégorie III — période d'embauche, 3 mois maximum" },
            { code: 'IV', label: 'Catégorie IV' },
            { code: 'V', label: 'Catégorie V' },
            { code: 'VI', label: 'Catégorie VI' },
            { code: 'VII', label: 'Catégorie VII' },
          ],
        },
        {
          code: 'II', label: "Travailleurs d'entretien",
          echelle: 'classes',
          echelons: [
            { code: 'A', label: 'Classe A' },
            { code: 'B', label: 'Classe B' },
            { code: 'C', label: 'Classe C' },
            {
              code: 'D', label: 'Classe D',
              description: "travailleurs ayant une grande conscience professionnelle et des connaissances théoriques et pratiques très étendues leur permettant d'exécuter n'importe quel travail propre à leur profession ; ne sont pas exclus ceux dont la connaissance théorique est moins complète mais compensée par une grande expérience dans l'entreprise, une grande dextérité dans des travaux variés et un esprit d'initiative",
            },
          ],
        },
      ],
      accesClasseA: {
        apresEssai: "après un mois maximum d'essai en catégorie V",
        deDroit: "porteurs d'un diplôme A3 ou B2 correspondant à la qualification pour laquelle ils sont embauchés, ou ayant reçu dans une autre entreprise une formation correspondant à cette qualification",
        parFormation: "les autres sont formés dans l'entreprise et accèdent à la classe A au terme de leur formation (12 mois maximum), s'ils ont fait la preuve d'une compétence suffisante",
        exception: "sauf pour les fonctions « graisseurs des installations fixes » et « graisseurs engins mobiles »",
      },
      // ⚠️ Règle facile à oublier, et qui décide d'un salaire.
      memeFonctionClassesDifferentes: "au sein d'une même entreprise, des travailleurs remplissant la MÊME fonction peuvent être classés dans des classes différentes, notamment selon leur degré de compétence",
      listesDeFonctionsNonReprises: "chaque groupe a une liste détaillée de fonctions, en annexe de la CCT — non reproduite ici",
    },
    nom: 'CP 102.07 — Carrières, cimenteries et fours à chaux (Tournai)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-02-01', note: 'AUDIT 2026-06 — +1% (02-01 et 05-01). Easypay 102.07' },
    // ── 0,20 %, PERÇUS PAR LE FONDS — MAIS LA CCT A EXPIRÉ FIN 2020 ───────
    // ✅ CCT du 19/06/2020 « Formation et emploi — Groupes à risques », art. 3
    // à 6 — dépôt 159763/CO/102.07, banque CCT du SPF Emploi, texte primaire lu
    // le 26/08/2026 : « les entreprises […] consacreront globalement 0,20 p.c.
    // par an de la masse salariale déclarée à l'Office national de sécurité
    // sociale aux initiatives sectorielles en matière de formation et d'emploi »
    // (art. 3), et surtout art. 4 : « Le fonds social "Fonds social de
    // l'industrie des carrières et fours à chaux du Tournaisis" PERÇOIT les
    // cotisations précitées ». Ce n'est donc pas un effort dépensé en interne :
    // un fonds encaisse.
    //
    // ⚠️ LE 6 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — trente fois trop.
    //
    // ⏰ MAIS LA CCT EST ÉCHUE : elle « cesse de produire ses effets le
    // 31 décembre 2020 » (art. 6), et la banque CCT ne publie rien de plus
    // récent sur ce point pour cette sous-commission. Le 0,20 % est donc la
    // DERNIÈRE valeur connue, pas une valeur certifiée courante — comme le
    // 6,99 % de la CP 322.01 l'était avant que l'ONSS ne le confirme.
    fondsSE: {
      nom: "Fonds social de l'industrie des carrières et fours à chaux du Tournaisis",
      taux_patronal: 0.002,
      base: "masse salariale déclarée à l'ONSS",
      cctSourcee: {
        taux: 0.002,
        base: "masse salariale déclarée à l'Office national de sécurité sociale (art. 3)",
        affectation: "au moins 0,15 % réservés aux actions en faveur des groupes à risque (art. 3 et 4)",
        source: "CCT du 19/06/2020 (SCP 102.07, carrières, cimenteries et fours à chaux de l'arrondissement de Tournai), art. 3, 4, 5 et 6 — dépôt 159763/CO/102.07, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        annees: [2019, 2020],
      },
      aVerifier: true,
      aVerifierRaison: "Le 0,20 % est la DERNIÈRE valeur publiée, pas une valeur courante : la CCT du 19/06/2020 cesse ses effets au 31/12/2020 (art. 6) et la banque CCT ne publie rien de plus récent pour cette sous-commission sur ce point. Elle est retenue parce que c'est la seule valeur sourcée qui existe et qu'elle est trente fois plus proche du réel que le 6 % qu'elle remplace. À reconfirmer auprès du fonds, ou par une CCT postérieure à 2020.",
      note: "Le 6 % qui figurait ici avant le 26/08/2026 n'avait aucune source. Les statuts du fonds (CCT du 28/11/2012, dépôt 112583/CO/102.07, lus le 26/08/2026) ne chiffrent pas la cotisation : ils renvoient à l'art. 17 des statuts de 1971.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — repris par la CCT primaire directe comme
    // suggéré ci-dessus. CCT du 16/06/2015 (relue via les versions 2016,
    // 2018, 2020) : chèque-repas de 7 EUR (5,91 € patronal / 1,09 €
    // travailleur), par journée complète de prestations, calculé au prorata
    // des heures prestées. Puis CCT du 03/12/2021 (accord du 19/11/2021,
    // dépôt 13/12/2021, enreg. 13/12/2021, n°168819/CO/102.07), art. 2c :
    // « augmentation de la valeur faciale des chèques-repas de 1 EUR, sans
    // changement de la quote-part personnelle, au 1/1/2022 » → 8 EUR
    // (6,91 € patronal / 1,09 € travailleur) depuis le 01/01/2022.
    chequesRepas: {
      obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09,
      source: "CCT du 16/06/2015 (base) + CCT du 03/12/2021 (n°168819/CO/102.07, +1 EUR au 01/01/2022) — banque CCT SPF Emploi",
      dateVerif: '2026-09-18',
    },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 06/12/2023 (SCP 102.07, carrières de craie), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « Passage des écochèques à 250 EUR à partir de 2023. » ⚠️ 250 EUR réels et sourcés — coïncidence avec le défaut de la grille, pas confirmation.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — « 5 jours, Fonds sectoriel CP
    // 102.07 » était FAUX : Protocole d'accord (dépôt 184802/CO/102.07,
    // déposé 08/12/2023), art. 9 « Formations » — « droit individuel à la
    // formation... trajectoire de croissance 2j (01/01/2023), 3j
    // (01/01/2025, valeur actuelle), 4j (01/01/2027), 5j (01/01/2029) » +
    // « l'effort de formation de 4 jours en moyenne par travailleur et par
    // an sera poursuivi ». Aucun fonds. ⚠️ Art. 12 : convention formellement
    // valide seulement 01/01/2023-31/12/2024.
    formation: {
      joursSectoriels: 3,
      fondsFormationExiste: false,
      base: "droit individuel à la formation, loi du 03/10/2022 chapitre 12",
      trajectoire: { '2023-01-01': 2, '2025-01-01': 3, '2027-01-01': 4, '2029-01-01': 5 },
      source: "Protocole d'accord (CP 102.07), art. 9, dépôt 184802/CO/102.07 (déposé 08/12/2023), banque CCT du SPF Emploi — ⚠️ durée formelle jusqu'au 31/12/2024",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Source formellement expirée fin 2024 ; à reconfirmer qu'un renouvellement maintient la trajectoire.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 102.07: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.08 — Carrières et scieries de marbres (généré batch 29/04/2026)
  '102.08': {
    // ── Classification CP 102.08 — ajoutée le 23/08/2026 ────────────────
    // ✅ CCT du 25/01/2013, banque CCT du SPF Emploi
    // (10208/10208-2013-001332.pdf). Quatre groupes de fonctions.
    //
    // ⚠️ AVEU D'EXTRACTION, POSÉ PLUTÔT QUE CACHÉ. Le PDF présente la
    // classification en TABLEAU, et l'extraction rend les cellules dans le
    // désordre : les critères d'ancienneté (« au moins 5 ans », « au moins
    // 10 ans », « test professionnel organisé par l'employeur ») se
    // rattachent à plusieurs groupes sans qu'on puisse dire lequel depuis le
    // texte aplati. Les GROUPES sont certains — quatre, dont le 4e est le
    // chef d'équipe. Les critères, non : les répartir au jugé serait inventer.
    classifications: {
      source: 'CCT du 25/01/2013 — banque CCT du SPF Emploi, 10208/10208-2013-001332.pdf, lue le 23/08/2026',
      dateVerif: '2026-08-23',
      groupes: [
        { code: '1', label: 'Groupe 1' },
        { code: '2', label: 'Groupe 2' },
        { code: '3', label: 'Groupe 3' },
        { code: '4', label: "Groupe 4 — chef d'équipe", description: "travailleur du groupe 3 particulièrement qualifié, avec une grande connaissance et expérience du travail" },
      ],
      criteresNonRepartis: {
        constat: "la CCT conditionne certains passages de groupe à l'ancienneté (5 ans, 10 ans) et à la réussite d'un test professionnel organisé par l'employeur",
        pourquoiNonCodes: "le tableau du PDF sort désordonné à l'extraction ; rattacher chaque critère à son groupe demande d'ouvrir le PDF à la main",
      },
      libellesIncomplets: true,
    },
    nom: 'CP 102.08 — Carrières et scieries de marbres',
    type: 'O',
    regimeHebdo: 38,
    indexation: {
      indicePivot: 134.52, indicePivotBase: null, indicePivotSource: "CCT du 11/02/2026 (SCP 102.08, gres et quartzite), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Au 1er janvier 2026, l'indice pivot est 134,52. » ⚠️ Base non precisee dans l'extrait lu.", indicePivotDateVerif: '2026-08-24', frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-01-01', note: 'AUDIT 2026-06 — +1% (01-01 et 04-01). Easypay 102.08' },
    fondsSE: {
      nom: 'Fonds social CP 102.08',
      taux_patronal: 0.0092,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '090',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 090 : 820 ouvriers 0,92 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 6,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 7.75, part_employeur: 6.66, part_travailleur: 1.09, source: "CCT du 11/02/2026 (SCP 102.08, carrières de grès et quartzite), banque CCT du SPF, texte primaire lu le 24/08/2026 : « À partir du 1er février 2026, il est octroyé au personnel ouvrier, par journée réellement prestée, un chèque-repas d'une valeur nominale de 7,75 EUR, dont 1,09 EUR à charge de l'ouvrier. » ⚠️ ÉCHÉANCE À VENIR : la même convention porte la valeur nominale à 8,75 EUR au 01/12/2026 (part ouvrière inchangée). Valeur codée = celle EN VIGUEUR aujourd'hui.", regimeSecondaire: "au 01/12/2026 : valeur nominale 8,75 EUR, part ouvriere inchangee a 1,09 EUR", dateVerif: '2026-08-24', enVigueurLe: '2026-02-01', lien: '10208/10208-2026-004417.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.08 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026). La seule CCT remontée (10208-2013-010070.pdf) ne contient le mot « chèque » nulle part dans son texte intégral — repli générique du moteur, pas une trouvaille.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — mécanisme faux (aucun fonds) mais valeur
    // confirmée par source RÉCENTE : CCT du 11/02/2026 (dépôt 198511/CO/102.08,
    // déposée 05/03/2026), « Efforts de formation » (loi Peeters art. 9-21) —
    // « l'objectif interprofessionnel de 5 jours de formation en moyenne par
    // an par ETP... élargi à cinq jours de formation par an par ouvrier en
    // 2024 ». Effort COLLECTIF moyen, pas un droit individuel garanti, pas
    // de fonds dédié.
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      mecanisme: "effort de formation collectif moyen par ETP, loi Peeters du 05/03/2017 (art. 9-21), atteint dès 2024",
      source: "CCT du 11/02/2026 (CP 102.08), art. 2, dépôt 198511/CO/102.08, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.08: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.09 — Carrières de calcaire non taillé et fours à chaux (généré batch 29/04/2026)
  '102.09': {
    // ── Classification CP 102.09 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 13/02/2026 « conditions de travail »,
    // banque CCT du SPF Emploi (10209/10209-2026-004433.pdf), art. 2 et 3.
    // Trouvée par recherche plein texte. Secteur : carrières et fours à chaux.
    //
    // ⚠️ LA CLASSIFICATION SE DIT ELLE-MÊME NON EXHAUSTIVE, et elle dit quoi
    // faire du reste : « ceux que cette énumération ne reprend pas sont
    // répartis AU NIVEAU DE L'ENTREPRISE », l'employeur s'inspirant autant que
    // possible des définitions et informant la délégation syndicale — ou, à
    // défaut, la majorité des travailleurs. Chaque catégorie commence par
    // « appartiennent NOTAMMENT à cette catégorie ». Servir ces listes comme
    // fermées ferait refuser un classement légitime.
    classifications: {
      source: 'CCT du 13/02/2026, art. 2 et 3 — banque CCT du SPF Emploi, 10209/10209-2026-004433.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « conditions de travail »',
      but: "fixer la hiérarchie dans la profession d'après la NATURE DU TRAVAIL",
      listesNonExhaustives: true,
      fonctionNonReprise: "répartie au niveau de l'entreprise ; l'employeur s'inspire autant que possible des définitions et en informe la délégation syndicale ou, à défaut, la majorité des travailleurs",
      categories: [
        { code: 'A', definition: "travailleurs ayant une part de responsabilité ET une part d'autorité dans un service déterminé",
          metiers: ["chef-mineur — peut commander le personnel préposé au forage et au tirage des mines, sans recevoir d'instructions détaillées du chef d'exploitation", "chef-chaufournier — peut commander le personnel préposé à l'alimentation des fours, responsable de l'enfournement, notamment du dosage du charbon et de la descente des charges", "chef d'entretien — travailleur ou brigadier pouvant commander le personnel préposé à l'entretien de tout ou partie du matériel"] },
        { code: 'B', definition: 'travailleurs de métier',
          metiers: ['mécaniciens', 'mineurs', 'poseurs de voies de raccordement', 'forgerons', 'électriciens', 'soudeurs', 'ajusteurs', 'tourneurs', 'maçons', 'menuisiers', 'conducteurs de locomotives pouvant participer au dépannage, au montage et au réglage', 'conducteurs de pelle, de grue ou de bulldozer travaillant à l\'extraction', 'cuiseurs de fours modernes', 'casseurs'] },
        { code: 'C', definition: 'travailleurs de métier du secteur « carrières » ayant dû faire un apprentissage adéquat',
          metiers: ['foreurs de mines ou de pétards au rocher, sur éboulis et sur blocs au sol', 'chaufourniers', 'ouvriers de fabrication ayant la responsabilité du bon fonctionnement des engins mécaniques qui leur sont confiés', 'chauffeurs de camions fournissant un travail intensif et soutenu'] },
        { code: 'D', definition: 'tireurs de pétards sur blocs au sol, chauffeurs de camions NON repris à la catégorie C, accrocheurs de wagons à temps plein' },
        { code: 'E', definition: "travailleurs majeurs en formation dans le nouveau métier, et ceux qui exercent un travail déterminé dont l'habitude leur confère une certaine habileté et dextérité, sans exiger de connaissances spéciales",
          metiers: ['aides-mécaniciens', 'opérateurs de fours modernes n\'ayant pas la responsabilité de la cuisson', 'défourneurs', 'conducteurs de locomotives à voies étroites', 'poseurs de voies « Decauville »', 'débardeurs-grutiers', 'ensacheurs', 'peseurs-pointeurs'] },
        { code: 'F', definition: 'travailleurs qui exercent un travail incommode ou exigeant un effort physique intense et continu',
          metiers: ['verseurs', 'trieurs', 'taqueurs', 'ouvriers préposés au chargement et au déchargement des charbons, calcaires et cendrées au four', 'aides-maçons'] },
        { code: 'G', definition: 'travailleurs qui effectuent un travail léger' },
      ],
    },
    nom: 'CP 102.09 — Carrières de calcaire non taillé et fours à chaux',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.01, derniereDate: '2026-05-01', note: 'AUDIT 2026-06 — +1% au 01/05. Easypay 102.09' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : 38 conventions ; les deux qui portent sur un fonds (statuts coordonnés du 03/12/2015, dépôt 132335, et institution du 29/08/2012, dépôt 111876) concernent le Fonds 2e PILIER de pension, pas le fonds de sécurité d'existence, et ne contiennent aucun pourcentage de cotisation.
    // ⚠️ Le 6 % qui figure ici n'a AUCUNE source. Il est conservé parce que
    // c'est le repli de la CP mère 102 et qu'aucune valeur sourcée ne peut le
    // remplacer — pas parce qu'il serait juste.
    // ── LE « FONDS DE SÉCURITÉ D'EXISTENCE » EST ICI UN 2ᵉ PILIER (26/08/2026) ─
    // La note du 25/08 disait « les deux conventions qui portent sur un fonds
    // concernent le 2e pilier, pas le FSE » — et s'arrêtait là, en conservant
    // le 6 %. Le texte a été LU aujourd'hui, et il tranche dans l'autre sens :
    // il n'y a pas « un FSE qu'on n'a pas trouvé », il n'y a QUE ce fonds-là.
    //
    // Statuts coordonnés du 03/12/2015 (dépôt 132335), texte primaire lu :
    //   · art. 3 — le Fonds est « institué pour remplir le rôle d'ORGANISATEUR
    //     du régime de pension sectoriel » ;
    //   · art. 4 — son rôle « SE LIMITE À » : transmission des données,
    //     collecte des cotisations, transferts financiers, contrôle, information ;
    //   · art. 6 — « le régime de pension sectoriel constitue l'avantage
    //     octroyé par le Fonds » — c'est son seul avantage ;
    //   · art. 15 — les cotisations sont versées à l'organisme de pension.
    //
    // ⚖️ POURQUOI IL S'APPELLE QUAND MÊME « fonds de sécurité d'existence » :
    // c'est le véhicule juridique de la loi du 07/01/1958, que l'art. 14 cite
    // explicitement pour aligner perception et recouvrement sur ceux de la
    // sécurité sociale. Un 2ᵉ pilier sectoriel se constitue couramment ainsi.
    // Le NOM est celui d'un FSE, l'OBJET est une pension complémentaire.
    //
    // ⛔ NE PAS LE COMPTER COMME UN FSE : le registre distingue partout les
    // deux (codes ONSS 820/830 contre 825/835), et les additionner sous
    // l'étiquette « fonds » serait un mensonge de libellé. Confirmation
    // indépendante dans la même famille : la catégorie ONSS 163 (SCP 102.01)
    // ne porte QUE 825/0 = 0,97 %, aucune ligne 820.
    fondsSE: {
      nom: 'Carrières de calcaire non taillé et fours à chaux — aucun fonds de sécurité d\'existence, seulement un 2ᵉ pilier de pension',
      taux_patronal: 0,
      dateVerif: '2026-08-26',
      source: "Banque CCT du SPF Emploi, census des 67 conventions de la SCP 102.09 le 26/08/2026 : les SEULES qui instituent un fonds sont l'institution du 29/08/2012 et les statuts coordonnés du 03/12/2015 (dépôt 132335), et toutes deux portent sur le « Fonds 2ème pilier SCP 102.09 ». Texte primaire lu le 26/08/2026 : art. 3, 4, 6, 14 et 15 — organisateur du régime de pension sectoriel, rôle limité, aucun autre avantage. Aucune convention n'institue de fonds de sécurité d'existence au sens de la paie, et l'ONSS ne publie aucune catégorie pour cette sous-commission.",
      pensionSectorielle: {
        existe: true,
        taux: null,
        note: "Le régime de pension sectoriel EXISTE (CCT du 29/08/2012). Son taux n'est pas fixé par les statuts : l'art. 12 renvoie à des CCT distinctes de la sous-commission, non relevées à ce jour. `null` veut dire « pas encore lu », pas « pas de cotisation » — c'est un coût employeur réel, simplement hors du champ de ce bloc.",
      },
      note: "Valeur précédente : 6,00 %, sans aucune source. Elle avait été conservée le 25/08 faute de mieux ; la lecture du texte le 26/08 montre qu'il n'y a pas de FSE à trouver.",
    },
    chequesRepas: { obligatoire: true, valeur: 7.39, part_employeur: 6.3, part_travailleur: 1.09, source: "CCT du 13/02/2026 (SCP 102.09, carrières de calcaire non taillé), banque CCT du SPF, texte primaire lu le 24/08/2026, art. 3 : valeur nominale 6,39 EUR au 01/01/2026 (part entreprise 5,30 EUR), PUIS 7,39 EUR au 01/07/2026. ⚠️ DEUX PALIERS DANS LE MÊME TEXTE : la valeur codée est celle du 01/07/2026, déjà en vigueur au 24/08/2026 — coder le premier palier aurait servi un montant périmé de six semaines.", dateVerif: '2026-08-24', enVigueurLe: '2026-07-01', lien: '10209/10209-2026-004432.pdf' },
    // ✅ CORRIGÉ LE 21/09/2026 — 4 orthographes cherchées sur la banque CCT du
    // SPF Emploi. Un seul hit réel : le protocole d'accord 2017-2018
    // (10209-2017-014430.pdf) octroie 100 € d'éco-chèques en janvier 2018,
    // référence 01/01-31/12/2017 — un octroi PONCTUEL, pas un régime récurrent
    // (même mécanisme que CP 330 et CP 102.01, cf. eco-cheques-statut.js).
    // Aucune trace d'un renouvellement dans les CCT plus récentes de cette CP.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.09 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » (relevé le 21/09/2026). Un seul hit réel : protocole d'accord 2017-2018 (10209/10209-2017-014430.pdf), octroi PONCTUEL de 100 EUR en janvier 2018 (référence 01/01 au 31/12/2017), pas un droit récurrent. Aucune CCT plus récente n'en fait mention — pas de régime en vigueur aujourd'hui.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 102.09 » était
    // FAUX sur les deux points : « Protocole d'accord 2025-2026 » (CCT de
    // février 2026, dépôt 199099/CO/102.09, déposé 27/03/2026), art. 14
    // « Formation » — « le droit individuel à la formation est fixé à 3 jours
    // par an à partir du 1er janvier 2025. L'effort de formation de 4 jours
    // en moyenne par travailleur et par an sera poursuivi ». Aucun fonds
    // (la cotisation groupes à risque de 0,10 % est une dimension séparée).
    formation: {
      joursSectoriels: 3,
      fondsFormationExiste: false,
      base: "droit individuel à la formation, loi du 03/10/2022, fixé à 3 jours/an dès le 01/01/2025",
      source: "Protocole d'accord 2025-2026 (CP 102.09), art. 14, dépôt 199099/CO/102.09 (déposé 27/03/2026), banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 102.09: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 102.11 — Ardoisières, coticules et pierres à rasoir (généré batch 29/04/2026)
  '102.11': {
    nom: 'CP 102.11 — Ardoisières, coticules et pierres à rasoir',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ DEUX AXES CROISÉS, ET LES CONFONDRE DONNE UN SALAIRE FAUX : la
    // catégorie (le métier) d'un côté, l'ÂGE de l'autre — le barème est une
    // MATRICE, pas une colonne. Un travailleur de 18 ans en catégorie 3 ne
    // touche pas le montant de la catégorie 3, mais 80 pour cent de celui-ci.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      regimeHebdoCCT: 38,
      perimetre: 'carrières de schiste ardoisier',
      systeme: "quatre catégories, la première dédoublée aux six premiers mois, croisées avec un pourcentage par âge",
      fonctions: [
        { categorie: '1 (les 6 premiers mois)' },
        { categorie: '1 (après 6 mois)' },
        { categorie: '2' }, { categorie: '3' }, { categorie: '4' },
      ],
      pourcentagesParAge: [
        { age: 21, pct: 100 }, { age: 20.5, pct: 95 }, { age: 20, pct: 90 },
        { age: 19.5, pct: 87.5 }, { age: 19, pct: 85 }, { age: 18.5, pct: 82.5 },
        { age: 18, pct: 80 },
      ],
      definitionsHorsPortee: "la fiche numérote les catégories sans nommer les métiers qui les composent ; ce rattachement est dans la CCT sectorielle.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.01, derniereDate: '2026-03-01', note: 'AUDIT 2026-06 — reels +1% (03-01 et 06-01); RMMMG +2% au 01-01. Easypay 102.11' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : AUCUNE convention dans la banque CCT pour cette sous-commission, quel que soit le critère de recherche.
    // ⚠️ Le 6 % qui figure ici n'a AUCUNE source. Il est conservé parce que
    // c'est le repli de la CP mère 102 et qu'aucune valeur sourcée ne peut le
    // remplacer — pas parce qu'il serait juste.
    // ── ZÉRO CONVENTION, ET CETTE FOIS AVEC UN TÉMOIN (26/08/2026) ────────────
    // Le 25/08, le census avait déjà rendu « aucune convention », mais SANS
    // témoin — et un zéro sans témoin ne prouve rien, il peut aussi bien dire
    // « l'outil est muet » (R2 ter). Le 26/08, les quatre sous-commissions de
    // carrières ont été interrogées dans le MÊME appel : 102.02 → 126
    // conventions, 102.04 → 137, 102.09 → 67, 102.11 → 0. Le client fonctionne,
    // donc le zéro est une absence réelle.
    //
    // Un fonds de sécurité d'existence s'institue PAR convention collective
    // rendue obligatoire. Pas de convention, pas de fonds : le 0 n'est plus
    // « faute de mieux », il est la réponse. Même raisonnement que la CP 334.
    fondsSE: {
      nom: 'Ardoisières, coticules et pierres à aiguiser — aucun fonds de sécurité d\'existence institué',
      taux_patronal: 0,
      dateVerif: '2026-08-26',
      source: "Banque CCT du SPF Emploi : AUCUNE convention déposée pour la SCP 102.11, tous critères confondus. Témoins lancés dans le même appel le 26/08/2026 — SCP 102.02 : 126 conventions, SCP 102.04 : 137, SCP 102.09 : 67. Le client répond, donc le zéro est une absence réelle et non un outil muet. L'ONSS ne publie par ailleurs aucune catégorie d'employeur pour cette sous-commission (recherche par numéro ET par nom de secteur : « ardois », « coticule » → 0 sur 232 catégories).",
      note: "Valeur précédente : 6,00 %, sans aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (aucune
    // convention publiée pour cette sous-commission). Synova (ex-CGSLB),
    // tableau chèque-repas par sous-commission CP 102 : « 0 euro » pour 102.11
    // (3/3 lectures concordantes).
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), tableau chèque-repas par sous-commission CP 102 (3 lectures concordantes : 0 €)',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 102.11 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026, 4 orthographes en un seul dispatch, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 102.11 » était
    // FAUX : recherche plein texte « formation »/« opleiding » filtrée
    // CP 102.11 → « 0 résultat(s) trouvé(s) » confirmé (`erreurs: []`,
    // pas une erreur d'outil). Cohérent avec le statut déjà connu de cette
    // CP (le SPF a cessé de la suivre au 01/09/2022).
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT sectorielle sur la formation trouvée dans la banque CCT (0 résultat confirmé)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 102.11 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 102.11: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Salairesminimums.be + CGSLB carrières',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 104 — Industrie sidérurgique (généré batch 29/04/2026)
  '104': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 29/01/2026 (CP 104), banque CCT
    // du SPF Emploi, 104/104-2026-001613.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 29/01/2026 (CP 104) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 104/104-2026-001613.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 104 — Industrie sidérurgique',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId d917b648… (CP 1040000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "la fiche ne porte qu'un régime horaire de 37 h/semaine. Aucune grille de fonctions.",
    },
    nomCourt: 'Industrie sidérurgique',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay (2e palier 01/06)' },
    // ── UN EFFORT DÉPENSÉ EN INTERNE, PAS UNE COTISATION ──────────────────
    // ✅ CCT du 11/02/2026 « Détermination de l'effort en faveur des personnes
    // appartenant aux groupes à risque », art. 3, 4 et 8 — dépôt 198652/CO/104,
    // banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il se trompait de
    // NATURE. Exactement le cas de la CP 210 (les employés du même secteur) :
    // l'art. 4 fixe 0,10 % « de l'ensemble des rémunérations déclarées à la
    // sécurité sociale du personnel sous contrat de travail d'OUVRIER », et
    // l'art. 8 dit ce qu'on en fait — « La convention collective de travail
    // D'ENTREPRISE comporte impérativement un engagement d'AFFECTER À CES
    // INITIATIVES un budget équivalent à 0,10 p.c. ». L'argent ne quitte pas
    // l'entreprise : elle le dépense en formation et dépose son rapport annuel
    // au Greffe. Aucun versement de substitution n'est prévu — contrairement à
    // la CP 310, dont l'art. 9 en prévoit un, et qui porte donc un taux.
    fondsSE: {
      nom: "Sidérurgie ouvriers — effort de formation à charge de l'entreprise, pas de cotisation à un fonds",
      taux_patronal: 0,
      effortDeFormation: {
        taux: 0.001,
        base: "ensemble des rémunérations déclarées à la sécurité sociale du personnel sous contrat de travail d'ouvrier",
        nature: "budget que l'entreprise s'engage à AFFECTER elle-même à des initiatives de formation et d'emploi, par CCT d'entreprise déposée au Greffe — pas un versement à un fonds",
        annees: [2025, 2026],
        source: "CCT du 11/02/2026 (CP 104, industrie sidérurgique — ouvriers), art. 3, 4 et 8 — dépôt 198652/CO/104, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
      },
      aVerifier: true,
      aVerifierRaison: "Le taux est mis à 0 parce que l'obligation de la CP 104 est un EFFORT que l'entreprise dépense elle-même (CCT d'entreprise, rapport annuel au Greffe), pas une cotisation versée à un fonds — l'écrire dans `taux_patronal` facturerait un versement sans destinataire. L'obligation de 0,10 % reste réelle et est décrite sous `effortDeFormation`. Même traitement que la CP 210, les employés du même secteur. À rouvrir si un fonds sectoriel percepteur est trouvé.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait une cotisation à un fonds.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB), page CP 104
    // exhaustive (barèmes, primes, indemnités, congés, crédit-temps, fin de
    // carrière, syndical) : aucune mention de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 104, exhaustive — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 104 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 104 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — mécanisme faux (aucun fonds), valeur
    // confirmée : CCT du 07/07/2023 (dépôt 181457/CO/104), « Formation des
    // travailleurs » (exécute la loi du 03/10/2022, ch. 12, en lieu et place
    // du volet formation abrogé de la loi Peeters) — « droit individuel à la
    // formation... 4 jours en 2023 pour un travailleur temps plein... 5 jours
    // à partir de 2024... les 5 jours de formation sont donc atteints à
    // partir de 2024 ». Droit individuel DIRECT, pas un fonds. Présenté comme
    // un plateau définitif atteint (pas une étape intermédiaire) — risque de
    // régression après expiry plus faible que 102.02/103/104/107 ci-dessus.
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      base: "droit individuel à la formation, loi du 03/10/2022 chapitre 12 — atteint et stabilisé depuis 2024 (plein temps ; pro rata temps partiel)",
      source: "CCT du 07/07/2023 (CP 104), art. 4, dépôt 181457/CO/104, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 104: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB sidérurgie + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 105 — Métaux non-ferreux (généré batch 29/04/2026)
  '105': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 09/02/2026 (CP 105), banque CCT
    // du SPF Emploi, 105/105-2026-001809.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 09/02/2026 (CP 105) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 105/105-2026-001809.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 105 — Métaux non-ferreux',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId 97fee539… (CP 1050000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "un salaire minimum garanti UNIQUE pour les ouvriers, décliné ensuite par population (étudiants en pourcentage du salaire de l'adulte exerçant la même fonction, apprentis selon leur parcours scolaire). Aucune catégorie de fonction.",
    },
    nomCourt: 'Métaux non-ferreux',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0194, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── NON-FERREUX — 0,10 % sur 108 %, par trimestre ─────────────────────
    // ✅ CCT du 09/02/2026 « emploi et formation des groupes à risque », art. 2
    // et 7 — dépôt 198191/CO/105, banque CCT du SPF Emploi,
    // 105/105-2026-001834.pdf, texte primaire lu le 26/08/2026 : « les
    // entreprises versent […] dans le mois qui suit la fin de chaque trimestre,
    // une cotisation globale de 0,10 p.c. calculée sur 108 p.c. des salaires
    // bruts des ouvriers du trimestre échu, sur le compte de l'ASBL "Fonds pour
    // l'emploi et la formation des ouvriers du secteur non ferreux" ».
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — cinquante fois trop.
    //
    // 📏 CE 0,10 % EST LE SEUL PUBLIÉ, et le census le dit : la banque CCT ne
    // compte que 14 conventions pour la CP 105, et les 14 portent le même
    // thème « emploi et formation des groupes à risque ». Aucun autre fonds
    // sectoriel n'y apparaît — mais un census n'est pas une preuve d'absence,
    // d'où le drapeau ci-dessous.
    fondsSE: {
      nom: "Fonds pour l'emploi et la formation des ouvriers du secteur non ferreux",
      taux_patronal: 0.001,
      assiette: 'salaires bruts des ouvriers à 108 %',
      cctSourcee: {
        taux: 0.001,
        base: "108 p.c. des salaires bruts des ouvriers du trimestre échu (art. 2)",
        periodicite: "versement trimestriel, dans le mois qui suit la fin du trimestre",
        source: "CCT du 09/02/2026 (CP 105, métaux non ferreux), art. 2 — dépôt 198191/CO/105, banque CCT du SPF Emploi, 105/105-2026-001834.pdf, texte primaire lu le 26/08/2026. Conclue en exécution du titre XIII, chapitre VIII, section 1 de la loi du 27/12/2006 et de l'AR du 19/02/2013.",
        dateVerif: '2026-08-26',
        affectation: "l'art. 5 réserve 0,05 % de la masse salariale à un ou plusieurs groupes à risque énumérés (travailleurs de 50 ans et plus, de 40 ans et plus menacés, etc.), dont au moins la moitié aux moins de 26 ans.",
        perimetre: 'ouvriers uniquement',
      },
      // ⏰ CCT à durée déterminée : effets au 01/01/2026, cesse le 30/06/2027.
      expireLe: '2027-06-30',
      expireQuoi: "la totalité du taux : la CCT groupes à risque est à durée déterminée (art. 7)",
      aVerifier: true,
      aVerifierRaison: "Le 0,10 % écrit ici est la SEULE cotisation publiée pour la CP 105, et elle est étiquetée « groupes à risque ». Census : les 14 conventions que la banque CCT publie pour cette commission portent toutes ce même thème, aucune ne décrit un fonds de sécurité d'existence général. Un census n'est pas une preuve d'absence — à reconfirmer par une autre voie avant d'affirmer qu'aucune autre cotisation sectorielle n'est due.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB), page CP 105
    // exhaustive (barèmes, primes fin d'année, congés, syndical, fin de
    // carrière) : aucune mention de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 105, exhaustive — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 28/08/2023 (CP 105, non-ferreux), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « Depuis l'année 2012, des écochèques à concurrence d'une valeur totale de 250 EUR sont accordés aux ouvriers travaillant à plein temps lors du paiement du salaire d'OCTOBRE. » ⚠️ Ici les 250 EUR sont RÉELS et sourcés — coïncidence avec la valeur par défaut de la grille, pas confirmation de celle-ci.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — « Fonds sectoriel CP 105 » était FAUX (aucun
    // fonds), mais le chiffre 5 jours est confirmé comme le droit fédéral
    // transposé : CCT du 02/03/2026 (dépôt 199177/CO/105, remplace la CCT du
    // 02/10/2023 n° 183184/CO/105), art. 3 — reprend l'art. 54 §1er,10° de la
    // loi du 03/10/2022 : « chaque ouvrier temps plein a un droit individuel
    // de 4 jours en 2023, 5 jours à partir de 2024 ». AUCUN fonds sectoriel
    // dédié : suivi via le plan de formation d'entreprise (art. 5),
    // obligation directe de l'employeur.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral, art. 54 §1er,10°) — repris et confirmé par CCT sectorielle : 4 jours en 2023, 5 jours/an dès 2024, par ouvrier temps plein. Pas de fonds sectoriel dédié : obligation directe de l'employeur, suivie via le plan de formation d'entreprise (art. 5).",
      source: "CCT du 02/03/2026 (CP 105), art. 3, dépôt 199177/CO/105, banque CCT du SPF Emploi, texte primaire lu en entier le 25/09/2026 — remplace la CCT du 02/10/2023 n° 183184/CO/105.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27, veloDegressif: [{ maxKm: 3, taux: 0.48 }, { maxKm: 5, taux: 0.36 }, { maxKm: 6, taux: 0.30 }, { taux: 0.27 }] },  // vélo CP 105 non-ferreux (ouvriers): barème DÉGRESSIF par distance A/R — 0,48 [1-3km] / 0,36 [4-5km] / 0,30 [6km] / 0,27 [≥7km], CCT 2023-2024 dès 01/01/2024 (ACV-CSC METEA) ; scalaire velo = taux marginal ≥7 km ; sourcé 2026-07-08
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB sidérurgie + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 106 — Industries du ciment (généré batch 29/04/2026)
  '106': {
    nom: 'CP 106 — Industries du ciment',
    nomCourt: 'Industries du ciment',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'voir sous-CP', dernierTaux: null, derniereDate: null, note: 'AUDIT 2026-06 — ciment — voir 106.01-106.03' },
    // ── FAÎTIÈRE À MOITIÉ SOURCÉE — ET C'EST POURQUOI ON NE MET PAS 0 ─────
    // 📏 Mesure du 26/08/2026, avec son dénominateur : la banque CCT du SPF ne
    // publie AUCUNE convention sous le numéro 106 lui-même. Ses sous-commissions,
    // elles, sont **1 sur 3** sourcées : 106.02 (1,05 %, taux ONSS).
    // Les autres — 106.01 et 106.03, toutes deux à 5 % sans source — n'ont
    // rien derrière elles.
    //
    // ⛔ POURQUOI LE 5 % RESTE, ALORS QU'ON L'A MIS À 0 SUR LES CP 125, 142,
    // 149, 318 ET 329. Là-bas, TOUTES les sous-commissions étaient sourcées :
    // la mère ne servait plus à personne. Ici, les sous-CP muettes **retombent
    // sur la mère** — c'est la règle de `blocFondsSE`, qui ne descend que sur un
    // taux prouvé. Mettre 0 ne rendrait pas la mère honnête : ça mettrait à zéro
    // le fonds de plusieurs sous-secteurs qui en ont un, sans l'avoir vérifié.
    //
    // ✅ Ce qui change quand même : le 5 % est désormais NOMMÉ comme non
    // sourcé, avec le ratio écrit. Il tombera quand les sous-CP muettes auront
    // été lues — pas avant.
    fondsSE: {
      nom: "Industries du ciment — faîtière, taux non sourcé servant de repli aux sous-CP muettes",
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source : la banque CCT du SPF ne publie aucune convention sous le numéro 106 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Il n'est PAS mis à 0 parce que les sous-commissions encore muettes retombent dessus — 106.01 et 106.03, toutes deux à 5 % sans source — et qu'un 0 leur retirerait un fonds qu'elles ont peut-être. Il tombera quand elles auront été lues, pas avant. Sous-CP déjà sourcées : 106.02 (1,05 %, taux ONSS) (1 sur 3).",
      dateVerif: '2026-08-26',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — INCOHÉRENCE INTERNE, même défaut que la CP
    // 101 : le fondsSE ci-dessus documente déjà qu'aucune convention n'est
    // publiée sous le numéro 106 lui-même, toutes les entreprises relevant
    // d'une sous-commission (106.01/106.02/106.03) — mais `chequesRepas`
    // affirmait quand même `obligatoire:true` avec la famille recopiée.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — la CP 106 mère n'a pas de CCT propre",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 106 + « eco cheque » :
    // 0 résultat, cohérent avec l'absence de CCT propre à la CP mère.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 106 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026), aucune CCT propre à la CP mère",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 106 » était FAUX
    // au niveau de cette CP MÈRE : recherche plein texte « formation »/
    // « opleiding » filtrée CP 106 → « 0 résultat(s) trouvé(s) » confirmé.
    // Cohérent : le contenu réel vit dans les sous-CP 106.01-106.03, chacune
    // vérifiée séparément.
    formation: {
      joursSectoriels: null,
      renvoi: "CP mère sans CCT de formation propre (0 résultat confirmé) — le contenu réel est dans les sous-CP 106.01-106.03 ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 106 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 106: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 106.01 — Fabriques de ciment (généré batch 29/04/2026)
  '106.01': {
    nom: 'CP 106.01 — Fabriques de ciment',
    nomCourt: 'Fabriques de ciment',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ DEUX ÉCHELLES PARALLÈLES, ET C'EST VOULU : une numérique (2 à 7) et une
    // alphabétique (A à H). Ce n'est pas une double écriture de la même chose —
    // l'art. 7 calcule la prime d'équipe sur la moyenne « des catégories 4 à 7
    // ET de B à H », donc les deux coexistent bien dans le même barème.
    classifications: {
      source:
        'CCT du 23/06/2026 « Salaires » (CP 106.01), n° 201158/CO/106.01, art. 4 et 7, assurant la continuité de la CCT du 23/06/1993 n° 34142/CO/106.01 — banque CCT du SPF Emploi, 10601/10601-2026-013160.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 36, // « une durée de travail de 36 heures semaine », art. 4
      systeme: 'deux échelles de catégories coexistantes — numérique 2 à 7 et alphabétique A à H',
      fonctions: [
        { categorie: '2' }, { categorie: '3' }, { categorie: '4' },
        { categorie: '5' }, { categorie: '6' }, { categorie: '7' },
        { categorie: 'A' }, { categorie: 'B' }, { categorie: 'C' }, { categorie: 'D' },
        { categorie: 'E' }, { categorie: 'F' }, { categorie: 'G' }, { categorie: 'H' },
      ],
      // ⚠️ Les définitions de fonction rattachées à chaque catégorie ne sont PAS
      // dans cette CCT : elle ne fixe que les montants. Elles remontent à la CCT
      // du 23/06/1993, que la banque ne sert pas (rien avant 02/02/2005).
      definitionsHorsPortee:
        'la CCT du 23/06/2026 fixe les montants par catégorie mais ne définit pas les fonctions ; les définitions remontent à la CCT du 23/06/1993 n° 34142/CO/106.01, que la banque CCT ne sert pas (aucune convention antérieure au 02/02/2005 pour ce jcId). À demander au SPF ou à la sous-commission.',
      primeEquipe:
        'la prime d\'équipe s\'exprime en pourcentage d\'un salaire de référence = moyenne arithmétique des salaires horaires de base des catégories 4 à 7 et de B à H (art. 7). Depuis 2005 ce salaire de référence est majoré d\'un demi-pourcent par an après indexation.',
      montantsNonCodes: 'les montants de l\'art. 4 sont exprimés « au 01/11/2021 » puis indexés mensuellement sur l\'indice quadrimestriel — ils ne se recopient pas, ils se calculent.',
    },
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.001797, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    // ── 0,10 % LU, MAIS LA DERNIÈRE CONVENTION DATE DE 2015 ───────────────
    // ✅ CCT du 24/11/2015 « emploi et formation des groupes à risque », art. 2
    // — dépôt 131266/CO/106.01, banque CCT du SPF Emploi, texte primaire lu le
    // 26/08/2026 : « La présente convention prolonge, pour la période du
    // 1er janvier 2015 au 31 décembre 2016 […] l'action en matière d'utilisation
    // des 0,10 p.c. de la masse salariale déclarée à l'Office national de
    // sécurité sociale. »
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE.
    //
    // ⛔ ET ON NE L'ÉCRIT PAS POUR AUTANT. Deux raisons cumulées : la convention
    // est échue depuis fin 2016 et rien de plus récent n'existe dans la banque
    // (census de 16 conventions, la plus récente étant celle-ci) ; et sa
    // formulation — « l'action en matière d'UTILISATION des 0,10 % » — décrit un
    // effort, pas une cotisation versée à un fonds. Deux incertitudes qui se
    // multiplient : on garde la valeur en place et on la NOMME.
    fondsSE: {
      nom: 'Fabriques de ciment — cotisation non établie',
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source. La seule piste chiffrée est un 0,10 % de la masse salariale ONSS lu dans la CCT du 24/11/2015 (dépôt 131266/CO/106.01), mais elle est doublement fragile : la convention cesse ses effets fin 2016 et rien de plus récent n'existe (census REFAIT le 26/08/2026 sur les 80 conventions publiées — et non 16, dénominateur issu d'une recherche filtrée. ✅ CE QUE LE CENSUS COMPLET APPREND, et qui change la lecture : le fonds est bien VIVANT. La « Programmation sociale 2025-2026 » (CCT du 23/06/2026, texte primaire lu le 26/08/2026) porte en son art. 14 que « l'intervention du fonds de sécurité d'existence pour les crédits-temps fin de carrière est augmentée de 75 euros » — portant le total à 250 € pour un mi-temps et 130 € pour un 1/5 temps. Un fonds qui verse est un fonds qui perçoit : on ne met donc PAS 0. Mais aucune des 80 conventions ne fixe le taux de la cotisation patronale, filet élargi aux programmations sociales et accords sectoriels compris. Ancien compte — ⚠️ DÉNOMINATEUR FAUX, corrigé le 26/08/2026 : la banque CCT en publie **80** pour la SCP 106.01, le compte venait d'une recherche filtrée par mots-clés et le balayage est à refaire), et sa formulation — « l'action en matière d'UTILISATION des 0,10 % » — décrit un effort, pas une cotisation versée à un fonds. Deux incertitudes qui se multiplient : on ne l'écrit pas. À reprendre par le fonds lui-même ou par une CCT postérieure.",
      lectureFaite: {
        piste: 0.001,
        source: "CCT du 24/11/2015 (SCP 106.01, fabriques de ciment), art. 2 — dépôt 131266/CO/106.01, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Millésimes antérieurs identiques (2013 : dépôt 116274 ; 2011 : dépôt 106659), tous à 0,10 %.",
        dateVerif: '2026-08-26',
        conclusion: "0,10 % lu trois fois, de 2011 à 2016, toujours dans la même formule d'« utilisation » — jamais comme une cotisation versée. Échu depuis fin 2016.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB), page CP 106
    // (industries du ciment), tableau « Primes de sécurité d'existence » :
    // « SCP 106.01 : chèque-repas minimum 7 € » (2/2 lectures concordantes).
    // 🟠 Répartition employeur/travailleur non donnée — à confirmer sur la
    // CCT primaire.
    chequesRepas: {
      obligatoire: true, valeur: 7.00, part_employeur: null, part_travailleur: null, aVerifier: true,
      aVerifierRaison: "Montant confirmé (Synova, 2 lectures concordantes), répartition non trouvée.",
      source: 'Synova (ex-CGSLB), tableau « Primes de sécurité d\'existence » CP 106',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 03/12/2021 (10601/10601-2021-014516.pdf), banque CCT du SPF Emploi, dépôt 170513/CO/106.01, texte primaire lu le 21/09/2026, art. 5 : les chèques-cadeaux de 40 EUR et les éco-chèques d'une valeur totale de 250 EUR, octroyés dans la CCT « Programmation sociale 2017-2018 » (144321/CO/106.01), sont reconduits et deviennent RÉCURRENTS, sous réserve d'une modification législative ultérieure. En vigueur depuis le 01/01/2021, durée indéterminée.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — « Fonds sectoriel CP 106.01 »
    // était FAUX (aucun fonds nommé, c'est une obligation directe) : CCT
    // « Programmation sociale 2017-2018 » (dépôt 144321/CO/106.01), chap. IX
    // art. 33 — trajectoire 2j (2017-18) → 3j (2019-20) → 4j (2021-22) →
    // 5j (2023-24). La CCT la plus récente lue (« Programmation sociale
    // 2025-2026 », dépôt 201159/CO/106.01, chap. VI art. 20) confirme la
    // poursuite d'un « trajet de formation » (rapport de suivi annuel + 1
    // jour additionnel transition sectorielle) SANS rechiffrer le palier —
    // cohérent avec 5 jours déjà atteints et maintenus, mais pas re-confirmé
    // formellement au dernier texte.
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      base: "obligation directe de l'employeur (pas de fonds) : trajectoire 2j(2017-18)→3j(2019-20)→4j(2021-22)→5j(2023-24)",
      source: "CCT du 13/11/2017 (CP 106.01), chap. IX art. 33, dépôt 144321/CO/106.01 ; poursuite mentionnée dans CCT du 23/06/2026, dépôt 201159/CO/106.01, chap. VI art. 20 — palier de 5j non re-chiffré formellement au dernier texte",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "La CCT la plus récente (2025-2026) ne rechiffre pas le palier de 5 jours, elle en confirme seulement la poursuite ; à reconfirmer par une lecture plus large des CCT récentes de cette CP.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 106.01: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 106.02 — Industrie du béton (généré batch 29/04/2026)
  '106.02': {
    nom: 'CP 106.02 — Industrie du béton',
    nomCourt: 'Industrie du béton',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot (franchissement)', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'AUDIT 2026-06 — +2% au 01/05. Calendrier vérifié 2026-07-16 : PAS de mois fixe — indexation au 1er mois suivant franchissement de l\'indice-pivot sectoriel (~+2%), CNE/SD Worx. → imprévisible (sentinelle robot), jamais un faux calendrier.' },
    fondsSE: {
      nom: 'Fonds social CP 106.02',
      taux_patronal: 0.0105,
      taux_patronal_employe: 0.0023,
      pension_sectorielle: 0.0199,
      base108Ouvrier: true,
      onssCategorie: '110',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 110 : 820 ouvriers 1,05 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 27/04/2026 (SCP 106.02, industrie du béton), banque CCT du SPF, texte primaire lu le 24/08/2026 : l'intervention de l'employeur dans le montant du chèque-repas est de 2,00 EUR par chèque ; celle de l'ouvrier est d'AU MOINS 1,09 EUR ; la valeur nominale totale est donc d'AU MOINS 3,09 EUR (texte néerlandais de la convention, traduit ici — le champ est affiché) ⚠️ Deux planchers, pas deux montants : la part ouvrière est un MINIMUM, donc la valeur nominale aussi.", dateVerif: '2026-08-24', enVigueurLe: '2026-04-27', lien: '10602/10602-2026-008658.pdf' },
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 14/12/2021 (SCP 106.02, industrie du béton), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : l'octroi est fait « d'une manière UNIQUE et NON-RÉCURRENTE » aux ouvriers repris à la liste salariale. Valeur nominale maximale 10 EUR par chèque ; l'éco-chèque peut être remplacé par un avantage équivalent. ⚠️ Le texte se qualifie lui-même de non récurrent : aucun droit annuel.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026, DONNÉES INSUFFISANTES — « 5 jours, Fonds
    // sectoriel CP 106.02 » était FAUX (aucun fonds ni droit chiffré trouvé),
    // mais la recherche n'a examiné que 6 des 64 résultats trouvés par la
    // banque CCT sur le motif « formation »/« opleiding » : parmi les 6, du
    // crédit-temps motif formation (mécanisme ONEM, non rémunéré par un fonds
    // sectoriel), des références générales pour définir des groupes à
    // risque, et un congé de FORMATION SYNDICALE (max 5j/an — à ne pas
    // confondre avec un droit de formation professionnelle). Aucune preuve
    // positive ni négative sur un vrai droit de formation professionnelle
    // pour cette CP dans l'échantillon lu.
    formation: {
      joursSectoriels: null,
      fondsFormationExiste: false,
      renvoi: "aucune CCT établissant un droit ou un fonds de formation professionnelle trouvée dans l'échantillon (6/64 résultats examinés) — à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral). Ne pas confondre avec le congé de formation SYNDICALE (max 5j/an, CCT distincte) trouvé dans ce même lot.",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 106.02 : 6/64 résultats examinés, aucun match positif, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "58 résultats restants non examinés — relancer avec un motif plus précis (« droit individuel formation », « efforts de formation ») filtré CP 106.02.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 106.02: 0,32 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 106.03 — Fibrociment (généré batch 29/04/2026)
  '106.03': {
    nom: 'CP 106.03 — Fibrociment',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION » : la fiche a été
    // ouverte et lue, ce secteur ne classe pas ses travailleurs par fonction.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      grilleSectorielle: false,
      systeme: "un salaire de production minimal UNIQUE, au taux horaire pour 38 heures par semaine (CCT du 16/06/2017, n° 140808). Aucune catégorie de fonction.",
    },
    nomCourt: 'Fibrociment',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'conventionnelle', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'AUDIT 2026-06 — +2% au 01/05 (hausse CCT, A VALIDER). Easypay 106.03' },
    // ── LES MOYENS DU FONDS SONT 0,10 % DU SALAIRE COMPLET ────────────────
    // ✅ CCT du 18/11/2025 « Formation en faveur des groupes à risque », art. 4
    // et 4bis — dépôt 196676/CO/106.03, banque CCT du SPF Emploi, texte primaire
    // lu le 26/08/2026 : « Les MOYENS À CHARGE DES EMPLOYEURS dont LE FONDS
    // dispose s'élèvent annuellement à 0,10 p.c. du salaire complet de leurs
    // travailleurs, tel que visé à l'article 23 de la loi du 29 juin 1981 ».
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — cinquante fois trop.
    //
    // ⚖️ La formulation compte : ce sont les moyens dont LE FONDS dispose, à
    // charge des employeurs — donc une ressource du fonds, pas un effort dépensé
    // en interne comme dans les CP 104, 210 et 325.
    fondsSE: {
      nom: 'Fonds social pour le fibrociment',
      taux_patronal: 0.001,
      base: "salaire complet (art. 23 de la loi du 29/06/1981)",
      cctSourcee: {
        taux: 0.001,
        base: "salaire complet des travailleurs, art. 23 de la loi du 29/06/1981",
        source: "CCT du 18/11/2025 (SCP 106.03, fibrociment), art. 4 — dépôt 196676/CO/106.03, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        conditionnel: "art. 4bis : « Pour bénéficier au niveau sectoriel de la dispense de l'obligation de premier emploi de la loi du 24 décembre 1999, les moyens destinés à la formation, visés à l'article 4, doivent passer de 0,05 % à 0,15 %. » NON codé : l'article ne dit pas clairement si le TOTAL passe à 0,15 % ou si c'est la part formation à l'intérieur des 0,10 %. À trancher avant d'appliquer un taux majoré.",
      },
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT produit ses effets du 01/01/2025 au 31/12/2026 (art. 6)",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB), même tableau
    // « Primes de sécurité d'existence » CP 106 : « SCP 106.03 (fibrociment) :
    // 0 € » (2/2 lectures concordantes).
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), tableau « Primes de sécurité d\'existence » CP 106 (2 lectures concordantes : 0 €)',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 106.03 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026, mode batch)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — source la plus solide du lot (durée
    // INDÉTERMINÉE, pas de risque d'expiration) : CCT du 22/09/2023 (dépôt
    // 183322/CO/106.03), « Droit individuel à la formation et à
    // l'apprentissage » (loi du 03/10/2022) — « chaque travailleur à temps
    // plein a un droit individuel à la formation de 2 jours en 2023 et 2024 ;
    // porté à 3 jours en 2025-2026 ; 4 jours en 2027-2028 ; 5 jours en
    // 2029-2030 », octroi au pro rata du temps de travail. Aucun fonds.
    formation: {
      joursSectoriels: 3,
      fondsFormationExiste: false,
      base: "droit individuel à la formation, loi du 03/10/2022, CCT à durée indéterminée",
      trajectoire: { '2023': 2, '2025': 3, '2027': 4, '2029': 5 },
      source: "CCT du 22/09/2023 (CP 106.03), art. 2, dépôt 183322/CO/106.03, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.36 },  // vélo CP 106.03: 0,36 €/km CONFIRMÉ CCT 18/11/2025 (≠ ancien max fédéral) — sourcé 2026-07-08
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 107 — Maîtres-tailleurs, tailleuses et couturières (généré batch 29/04/2026)
  '107': {
    nom: 'CP 107 — Maîtres-tailleurs, tailleuses et couturières',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      regimeHebdoCCT: 38,
      systeme: "six niveaux de fonction, du plus simple au plus qualifié ; le niveau 1 se dédouble à trois ans d'ancienneté",
      fonctions: [
        { categorie: '1', fonction: 'Aides et finisseurs(euses)' },
        { categorie: '1 bis', fonction: "Aides et finisseurs(euses) après 3 ans d'ancienneté" },
        { categorie: '2', fonction: 'Assistants ouvriers et ouvrières' },
        { categorie: '3', fonction: 'Ouvriers et ouvrières qualifiés' },
        { categorie: '4', fonction: "Ouvriers et ouvrières très qualifiés (ouvrières d'élite)" },
        { categorie: '5', fonction: 'Ouvriers tailleurs et ouvrières tailleuses' },
      ],
      jeunesEtApprentis:
        "les mineurs et les apprentis sous contrat de travail sont payés en pourcentage du NIVEAU 1. Dès 18 ans, le jeune travailleur ou l'apprenti en stage de démarrage d'un an (en équivalents temps plein) touche un pourcentage du salaire de sa fonction, puis le salaire complet. Un stage de démarrage d'au moins 12 mois accompli dans une AUTRE entreprise de la CP 107 compte pour l'acquisition du salaire complet — à prouver par déclaration de l'employeur précédent, remise à l'entrée en service.",
      // ⚠️ La fiche nomme les niveaux, pas les métiers qui y tombent.
      definitionsHorsPortee: "la fiche SPF nomme les six niveaux mais ne dit pas quel métier concret relève de quel niveau ; ce rattachement est dans la CCT sectorielle (art. 4, cité par la fiche).",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay/CGSLB' },
    fondsSE: {
      nom: 'Fonds social CP 107',
      taux_patronal: 0.0275,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '031',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 031 : 820 ouvriers 2,75 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // 🔴 DIVERGENCE NON RÉSOLUE, sondée le 05/09/2026 — l'ancienne valeur
    // (8,00 €) était la famille recopiée {8/6.91/1.09}, sans source, donc
    // fausse à coup sûr. Mais les deux remplacements trouvés se contredisent :
    // ACCG/FGTB (« Chèques-repas | Maître-tailleurs ») donne 3,30 € (2,21 €
    // employeur) depuis le 01/01/2016 ; une page Synova combinée CP 109-215
    // évoque « 4 € puis 6 € au 01/01/2026 » pour un intitulé proche mais pas
    // manifestement identique. Valeur laissée inchangée (fausse mais neutre)
    // en attendant de trancher sur la CCT primaire.
    // ✅ CORRIGÉ LE 18/09/2026 — CCT primaire lue via la banque CCT du SPF
    // Emploi (public-search.emploi.belgique.be, recherche CP 107 + « chèque
    // repas »), document 107-2026-001292.pdf : « Maaltijdcheques / Chèques-
    // repas », CCT du 29/01/2026, dépôt 29/01/2026, enregistrement n°197774
    // du 09/02/2026, AR du 16/07/2026, Moniteur belge du 14/08/2026. Son
    // art. 3 donne l'historique complet des valeurs depuis 2009 jusqu'au
    // 01/01/2026 — remplace le triplet recopié 8/6,91/1,09 (famille
    // A_SOURCER détectée par cheques-repas-statut.js).
    chequesRepas: {
      obligatoire: true, valeur: 6.0, part_employeur: 4.91, part_travailleur: 1.09,
      source: 'CCT du 29/01/2026 (dépôt 197774, AR 16/07/2026, MB 14/08/2026), art. 3 — banque CCT SPF Emploi',
      dateVerif: '2026-09-18',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 107 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 107 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 107 » était FAUX
    // (aucun fonds) et incomplet (le droit dépend de la taille de
    // l'entreprise) : CCT du 29/01/2026 (dépôt 197770/CO/107, prolonge la CCT
    // du 25/10/2023 n° 183693/CO/107, modifiée par CCT du 09/01/2024
    // n° 185710) — « pour les entreprises de 20 travailleurs ou plus, un
    // droit individuel à la formation de 4 jours s'appliquera en 2023 et de
    // 5 jours à partir de 2024 ». Entre 10 et 20 travailleurs : droit
    // COLLECTIF de 2,5 jours/an dont 1 jour individuel garanti. Moins de 10
    // travailleurs : EXCLUES du droit.
    formation: {
      joursSectoriels: 5, // uniquement pour entreprises ≥ 20 travailleurs, depuis 2024
      seuilEffectif: {
        ge20: { joursIndividuels: 5 },
        entre10et20: { joursCollectifs: 2.5, dontIndividuelGaranti: 1 },
        moins10: { exclu: true },
      },
      fondsFormationExiste: false,
      base: "droit individuel/collectif à la formation, loi du 03/10/2022 chapitre 12",
      source: "CCT du 29/01/2026 (CP 107), dépôt 197770/CO/107, prolongeant la CCT du 25/10/2023 n° 183693/CO/107 (mod. CCT du 09/01/2024 n° 185710), banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 107: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB textile + IVOC',
      indexationFrequence: 'semestrielle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 110 — Entretien du textile (généré batch 29/04/2026)
  '110': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 18/02/2026 (CP 110), banque CCT
    // du SPF Emploi, 110/110-2026-002857.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 18/02/2026 (CP 110) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 110/110-2026-002857.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 110 — Entretien du textile',
    nomCourt: 'Entretien du textile',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0219, derniereDate: '2026-01-01', source: 'CGSLB/ACCG/Securex — indexation à l\'indice santé au 1er janvier, vérifié 2026-07-16' },
    fondsSE: {
      nom: 'Fonds social CP 110',
      taux_patronal: 0.018500000000000003,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '049',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 049 : 820 ouvriers 1,85 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 7.5, part_employeur: 6.41, part_travailleur: 1.09, source: "CCT du 18/02/2026 (CP 110, entretien du textile), banque CCT du SPF, texte primaire lu le 24/08/2026, §5 : au 01/01/2026 la part patronale augmente de 1 EUR, portant la valeur nominale à 7,50 EUR — part patronale 6,41 EUR, part travailleur 1,09 EUR (texte néerlandais, traduit ici) ⚠️ PIÈGE DE LECTURE : ce texte empile son propre historique — 3,90 (2012), 5,00 (2016), 6,50, puis 7,50. Il faut la DERNIÈRE occurrence, jamais la première. Les entreprises déjà au-dessus du sectoriel doivent aussi accorder les +1 EUR.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '110/110-2026-002859.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 110 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 110 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 18/02/2026 (dépôt 198469/CO/110,
    // valable jusqu'au 31/12/2026), art. 7 : trajectoire propre (dérogation
    // art. 54 §2 loi du 03/10/2022) — 2,5j (2023) → 3j (2024) → 3,5j (2025)
    // → 4j (2026, valeur actuelle) → 5j (2027). Art. 3 : effort groupes à
    // risque 0,10% (Fonds Commun de l'Entretien du Textile).
    formation: {
      joursSectoriels: 4,
      fonds: "Fonds Commun de l'Entretien du Textile (CP 110)",
      cotisationGroupesRisque: 0.0010,
      renvoi: "CCT du 18/02/2026, art. 7 §1 : trajectoire propre dérogeant à la loi du 03/10/2022 — 4 jours de formation individuelle en 2026, 5 jours à partir de 2027 (droits acquis : min. 2,5 jours déjà garantis avant le 30/06/2023)",
      source: "CCT du 18/02/2026 « Formation et emploi » (CP 110), art. 3 et 7, dépôt 198469/CO/110, AR du 01/04/2026, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 110: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB textile + IVOC',
      indexationFrequence: 'semestrielle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 113 — Industrie céramique (généré batch 29/04/2026)
  '113': {
    // ── Classification CP 113 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 20/12/2021 « Conditions de rémunération et
    // de travail », banque CCT du SPF Emploi (113/113-2021-015250.pdf),
    // chapitre II, art. 2. Trouvée par recherche plein texte.
    //
    // ⚠️ CETTE CLASSIFICATION EST UNE MATRICE À DEUX ENTRÉES, pas une
    // échelle. Chaque catégorie se lit au croisement d'une DURÉE
    // D'APPRENTISSAGE et d'un EFFORT PHYSIQUE — d'où des catégories à
    // plusieurs branches (a, b, c) qui mènent au même niveau. Réduire ça à
    // « catégorie 3 = travail lourd » ferait classer de travers la moitié des
    // cas.
    classifications: {
      source: 'CCT du 20/12/2021, chapitre II art. 2 — banque CCT du SPF Emploi, 113/113-2021-015250.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « Conditions de rémunération et de travail »',
      structure: 'deux barèmes distincts : cinq catégories pour la fabrication et les services divers, TROIS pour le personnel qualifié d\'entretien',
      matrice: 'chaque catégorie se lit au croisement de la durée d\'apprentissage et de l\'effort physique',
      fabricationEtServices: [
        { code: '1', critere: 'apprentissage inférieur à trois mois — travail physique léger' },
        { code: '2', critere: 'apprentissage de trois à six mois et travail léger, OU apprentissage de moins de trois mois et travail moyen' },
        { code: '3', critere: 'formation de moins de trois mois et travail lourd, OU de trois à six mois et travail moyen, OU de plus de six mois et travail léger' },
        { code: '4', critere: 'formation de plus de six mois et travail moyen, OU de trois à six mois et travail lourd' },
        { code: '5', critere: "formation de plus de six mois et travail lourd, OU travaux de métier pour lesquels la période requise d'apprentissage a été accomplie" },
      ],
      entretienQualifie: [
        { code: '1', label: "Ouvrier semi-qualifié d'entretien", critere: "expérience pratique et connaissances suffisantes pour exécuter des travaux simples ou spécialisés" },
        { code: '2', label: "Ouvrier qualifié d'entretien", critere: "formation générale et technique correspondant au programme des écoles professionnelles de jour de plein exercice, complétée par un apprentissage à l'usine ; diplôme A4, A3, B2 ou degré de formation comparable" },
      ],
      entretienCategorie3NonTranscrite: true,
    },
    nom: 'CP 113 — Industrie céramique',
    nomCourt: 'Industrie céramique',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-03-01', source: 'AUDIT 2026-06 — CGSLB/Synova (113.04=2,21% 01/01)' },
    // ── FONDS DE SÉCURITÉ D'EXISTENCE — deux cotisations, une seule assiette ─
    // ✅ CCT du 02/07/2019 « perception par l'ONSS des cotisations patronales au
    // Fonds de sécurité d'existence pour l'industrie céramique », art. 2, 3, 4
    // et 6 — dépôt 155176/CO/113, banque CCT du SPF Emploi,
    // 113/113-2019-009712.pdf, texte primaire lu le 26/08/2026.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — huit fois trop.
    //
    // 📐 « PAR TRIMESTRE » NE VEUT PAS DIRE « QUATRE FOIS PAR AN ». L'art. 4
    // écrit que la cotisation « sera de 0,10 % par trimestre » et, pour le
    // second semestre 2019, « 0,2 % au T3 — 0,2 % au T4 ». Les deux se lisent
    // ensemble : chaque trimestre porte le taux sur SA propre masse salariale,
    // donc 0,10 % par trimestre revient bien à 0,10 % de la masse annuelle.
    // Les 0,2 % de 2019 sont un rattrapage sur deux trimestres seulement.
    //
    // ⚠️ PÉRIMÈTRE : la SCP 113.04 (tuileries) est expressément EXCLUE par
    // l'art. 1er. Sa fiche est distincte et reste à instruire.
    fondsSE: {
      nom: "Fonds de sécurité d'existence de l'industrie céramique",
      taux_patronal: 0.006,
      assiette: "rémunérations brutes à 108 %, primes d'équipe, prime de fin d'année et autres avantages assimilés au salaire compris",
      cctSourcee: {
        taux: 0.006,
        composantes: {
          fonctionnementDuFonds: 0.001,   // art. 4, § 1er — en exécution de l'art. 17 des statuts du 24/05/1995
          primeSyndicale: 0.005,          // art. 3 et art. 4, § 2 — reversée au « Fonds social des ouvriers de l'industrie céramique »
        },
        base: "rémunérations brutes à 108 % (art. 2 : « Les montants sont fixés sur la base des salaires bruts à 108 %, compte tenu des primes pour travail en équipe, prime de fin d'année et autres avantages assimilés au salaire »)",
        perceptionParOnss: true,
        primeSyndicaleParTravailleur: 145.00,
        source: "CCT du 02/07/2019 (CP 113, industrie céramique), art. 2 (assiette), art. 3 (« prélèvera une cotisation de 0,5 % des rémunérations brutes à 108 % »), art. 4 § 1er (0,10 % par trimestre à partir de 2020) et § 2 (0,5 % par trimestre à partir de 2020), art. 6 (reversement au Fonds social des ouvriers) — dépôt 155176/CO/113, banque CCT du SPF Emploi, 113/113-2019-009712.pdf, texte primaire lu le 26/08/2026. La perception par l'ONSS résulte de la CCT du 19/12/2018, art. 5 (dépôt 150338/CO/113), qui remplace l'art. 18 des statuts.",
        dateVerif: '2026-08-26',
        perimetre: "CP 113 SAUF la sous-commission paritaire des tuileries (SCP 113.04), exclue par l'art. 1er. Ouvriers et ouvrières uniquement.",
      },
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Synova (ex-CGSLB), page CP 113
    // exhaustive (barèmes, primes d'équipe/fin d'année/déplacement, congés,
    // formation, crédit-temps, fin de carrière, chômage temporaire), vérifiée
    // deux fois : aucune mention de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 113, exhaustive (vérifiée deux fois) — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 113 + « eco cheque » :
    // 2 résultats, mais AUCUN n'est une CCT éco-chèques. Les deux PDF (accords
    // « conditions de travail et de rémunération » du 05/07/2017 et du
    // 30/11/2015, tous deux expirés) ne contiennent ni « ecocheques » ni
    // « écochèques » ; les correspondances viennent de « chèques-repas »
    // (thème listé, sans article opératoire — cf. chequesRepas ci-dessus) et
    // de « Economie »/« économique », deux mots sans rapport, matchés
    // séparément par la recherche plein texte du site. Aucune CCT éco-chèques
    // dans la banque CCT du SPF pour cette CP à ce jour.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT SPF Emploi, CP 113 — recherche plein texte « eco cheque » : 2 résultats, tous deux des faux positifs (chèques-repas + économie/économique), aucune CCT éco-chèques trouvée",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — CCT du 20/12/2021 (dépôt
    // 170837/CO/113), fin de validité formelle 31/12/2022 : trajectoire
    // (moyenne, pas un droit individuel garanti) 3j (2019-20) → 4j (2021-22)
    // → 5j (2023-24). Aucune CCT de renouvellement retrouvée.
    formation: {
      joursSectoriels: 5,
      aVerifier: true,
      aVerifierRaison: "CCT du 20/12/2021 formellement expirée le 31/12/2022 ; aucun renouvellement retrouvé dans les documents téléchargés (recherche du 25/09/2026, 25 résultats au total, seuls les 5 premiers, tous datés ≤2021, ont été lus) — la trajectoire qu'elle fixait ('2023-2024 : 5 jours de formation en moyenne par travailleur/an', pas un droit individuel garanti) n'a donc plus de base confirmée.",
      fonds: "Fonds de sécurité d'existence de l'industrie céramique (CP 113) — actions formation/groupes à risque",
      renvoi: "à défaut de confirmation : droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 20/12/2021 « efforts de formation » (CP 113), art. 2, dépôt 170837/CO/113, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 113: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 113.04 — Tuileries (généré batch 29/04/2026)
  '113.04': {
    // ── Classification CP 113.04 — ajoutée le 23/08/2026 ────────────────
    // ✅ CCT du 11/02/2019, banque CCT du SPF Emploi
    // (11304/11304-2019-001465.pdf), lue le 23/08/2026.
    //
    // ⚠️ CE SECTEUR NE CLASSE PAS PAR UNE LISTE, IL CLASSE PAR UNE MÉTHODE.
    // La classification repose sur ORBA®, un système d'évaluation analytique
    // de fonctions : chaque fonction reçoit des scores par aspect
    // (connaissances, communication…), dont la somme — le « score total
    // ORBA » — donne son NIVEAU. Les fonctions ordonnées par score forment
    // la grille. Il n'y a donc pas d'échelons nommés à recopier : chercher
    // une liste dans ce texte, c'est chercher ce qu'il ne contient pas.
    // (Le dépôt connaît déjà ORBA : cf. `pointsOrba` dans classification-cp.)
    classifications: {
      source: 'CCT du 11/02/2019 — banque CCT du SPF Emploi, 11304/11304-2019-001465.pdf, lue le 23/08/2026',
      dateVerif: '2026-08-23',
      methode: 'ORBA®',
      principe: "évaluation analytique : chaque fonction est notée par aspect, la somme des scores (« score total ORBA ») représente le NIVEAU de la fonction ; les fonctions ordonnées par niveau forment la grille",
      pasDEchelonsNommes: true,
      consequence: "on ne peut pas dire « catégorie 3 » dans ce secteur : on dit un score et le niveau qui en découle, et le score se calcule fonction par fonction dans l'entreprise",
    },
    nom: 'CP 113.04 — Tuileries',
    nomCourt: 'Tuileries',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Indexation annuelle au 1er janvier (indice santé déc.), CONFIRMÉ 2026-07-16. Source: Partena adaptations janvier + SD Worx (tuileries/céramique). Provenance batch « ciment » corrigée.', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },
    // ── 0,10 % D'EFFORT, ET AUCUN FONDS NOMMÉ POUR L'ENCAISSER ────────────
    // ✅ CCT du 25/09/2025 « emploi et formation des groupes à risque », art. 3
    // et 4 — dépôt 195662/CO/113.04, banque CCT du SPF Emploi, texte primaire lu
    // le 26/08/2026 : « Les employeurs du secteur s'engagent à fournir un EFFORT
    // PARTICULIER au cours des années 2025 et 2026 en faveur d'initiatives
    // d'emploi des groupes à risque, qui représente au moins 0,10 % de la masse
    // salariale déclarée à l'Office national de sécurité sociale. » Art. 4 : au
    // moins 0,05 % réservés aux groupes énumérés.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — cinquante fois le seul
    // chiffre qu'on trouve.
    //
    // ⛔ ET LE 0,10 % NE S'ÉCRIT PAS DANS LE TAUX : le texte parle d'un
    // « effort » et ne nomme AUCUN fonds percepteur — ni dans cet article ni
    // ailleurs dans la convention. Même conduite que les CP 104, 210 et 325.
    // ⚠️ Rappel de périmètre : la SCP 113.04 (tuileries) est expressément EXCLUE
    // du fonds de l'industrie céramique (CP 113), dont l'art. 1er la met hors
    // champ. Elle n'a donc pas de fonds par héritage non plus.
    fondsSE: {
      nom: 'Tuileries — effort de 0,10 %, aucun fonds percepteur nommé',
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source. Le seul chiffre trouvé est un EFFORT de 0,10 % de la masse salariale ONSS (CCT du 25/09/2025, art. 3, dépôt 195662/CO/113.04, pour 2025-2026) — mais le texte ne nomme aucun fonds percepteur, ni là ni ailleurs dans la convention : il décrit une dépense de l'employeur, pas un versement. On ne l'écrit donc pas dans le taux, et on ne met pas 0 non plus tant qu'aucun census complet n'a exclu l'existence d'un fonds. ⚠️ Périmètre : cette sous-commission est expressément EXCLUE du fonds de l'industrie céramique (art. 1er de la CCT du 02/07/2019, CP 113) — elle n'hérite donc de rien.",
      lectureFaite: {
        piste: 0.001,
        source: "CCT du 25/09/2025 (SCP 113.04, tuileries), art. 3 et 4 — dépôt 195662/CO/113.04, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Millésime antérieur identique (CCT du 11/09/2023, dépôt 182986).",
        dateVerif: '2026-08-26',
        conclusion: "effort de 0,10 % pour 2025-2026, dont au moins 0,05 % réservés aux groupes à risque énumérés. Aucun fonds percepteur nommé.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Même page Synova exhaustive que la
    // CP 113 (couvre explicitement la sous-commission 113.04) : aucune mention
    // de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: 'Synova (ex-CGSLB), fiche CP 113 (couvre explicitement 113.04) — aucune mention de chèque-repas',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — PAS DE RÉGIME RÉCURRENT, même précédent que la
    // CP 330 (cf. eco-cheques-statut.js) : 4 CCT successives (2009, 2019, 2020,
    // 2021) accordent chacune un écochèque explicitement qualifié de « non
    // récurrent » / « niet recurrent » : 50 € en 2009, 50 € en 2010, 100 € en
    // 2019 (x2 CCT), 50 € en 2021. Aucune ne l'institue en droit annuel
    // permanent. La CCT en vigueur (14/12/2021, dépôt 171568/CO/113.04) ne dit
    // rien pour les années suivantes.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 113.04 — 4 CCT lues (2009, 2019, 2020, 2021), toutes qualifient l'écochèque de « non récurrent » : 50 EUR en 2009 et 2010 (art. 11.2, CCT 2009), 100 EUR en 2019 (CCT du premier semestre 2019, payé septembre 2019), 50 EUR en 2021 (CCT du 14/12/2021, dépôt 171568/CO/113.04, payé novembre 2021, référence 01/01-30/09/2021). Aucun droit annuel permanent, texte primaire lu le 21/09/2026.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 25/09/2025 (dépôt 195662/CO/113.04,
    // valable jusqu'au 31/12/2026) : seulement un effort groupes à risque
    // 0,10% de la masse salariale. Aucun jour de formation individuelle
    // trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT de jours de formation individuelle trouvée pour la CP 113.04 — seule une cotisation « emploi et formation des groupes à risque » de 0,10% de la masse salariale existe (CCT du 25/09/2025) ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      fondsFormationExiste: true,
      cotisationGroupesRisque: 0.0010,
      source: "CCT du 25/09/2025 « emploi et formation des groupes à risque » (CP 113.04), art. 4, dépôt 195662/CO/113.04, AR du 05/01/2026, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 113.04: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 114 — Industrie des briques (généré batch 29/04/2026)
  '114': {
    // ── Classification CP 114 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 24/01/2019, n° 151117/CO/114, banque CCT
    // du SPF Emploi (114/114-2019-002109.pdf), art. 4 à 6. Elle remplace
    // intégralement la CCT du 22/02/2011 (n° 103521/CO/114) — mais les DROITS
    // INDIVIDUELS acquis sous l'ancienne restent d'application.
    //
    // 🔎 CETTE FICHE A FAILLI ÊTRE FAUSSE, ET LE DÉTOUR VAUT D'ÊTRE NOTÉ.
    // L'extraction à plat du PDF montrait une annexe où des métiers et des
    // en-têtes « Classe N » s'entrelaçaient sans qu'on puisse dire lequel
    // allait avec lequel — de quoi attribuer un contremaître à la classe 6.
    // Une extraction qui CONSERVE LES COORDONNÉES (regroupement par bande
    // horizontale, tri par x) a rendu la vraie règle, qui est tout autre :
    // la classe ne se lit pas dans une liste, elle se CALCULE.
    // 🔁 Même famille que le PDF du fonds CP 127 (« mauvais opérateur ») :
    // les octets étaient là depuis le début, c'est la lecture qui manquait.
    classifications: {
      source: 'CCT du 24/01/2019, n° 151117/CO/114, art. 4 à 6 — banque CCT du SPF Emploi, 114/114-2019-002109.pdf, texte primaire lu le 23/08/2026 (extraction par coordonnées)',
      dateVerif: '2026-08-23',
      remplace: 'CCT du 22/02/2011, n° 103521/CO/114 — les droits individuels acquis sous celle-ci restent d\'application',

      methode: 'ORBA®',
      principe: "35 fonctions de référence sont décrites selon la méthode ORBA ; les fonctions RANGÉES PAR POINTS ORBA sont ensuite réparties en 8 classes",
      nbFonctionsDeReference: 35,

      // ⭐ LA RÈGLE OPÉRATOIRE : la classe se déduit du score, pas d'une liste.
      // Les bornes sont celles de l'art. 5, telles qu'écrites.
      classesParPointsOrba: [
        { classe: 1, de: null, a: 29.5 },
        { classe: 2, de: 30, a: 49.5 },
        { classe: 3, de: 50, a: 69.5 },
        { classe: 4, de: 70, a: 89.5 },
        { classe: 5, de: 90, a: 109.5 },
        { classe: 6, de: 110, a: 129.5 },
        { classe: 7, de: 130, a: 149.5 },
        { classe: 8, de: 150, a: 169.5 },
      ],
      // ⚠️ La classe 1 n'a pas de borne basse écrite dans la CCT : le tableau
      // ne montre qu'un plafond. Inventer un « 0 » serait ajouter au texte.
      classe1SansBorneBasse: true,

      // Où vit le reste, pour qui voudra aller plus loin.
      annexes: {
        1: 'manuel de référence des fonctions — liste des 35 fonctions et leurs descriptions',
        2: 'description de la méthode ORBA',
        3: 'classification des 35 fonctions de référence',
      },
      // ⛔ Les salaires horaires par classe figurent dans la CCT (chapitre VII)
      // mais ne sont PAS repris : ils sont datés du 01/12/2018 et la CCT dit
      // elle-même qu'ils doivent être adaptés à l'indice et aux augmentations
      // conventionnelles. Les recopier ferait entrer des barèmes périmés.
      baremesNonRepris: 'salaires horaires au 01/12/2018 — à adapter à l\'indice et aux augmentations conventionnelles (art. 14)',
    },
    nom: 'CP 114 — Industrie des briques',
    nomCourt: 'Industrie des briques',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Indexation annuelle au 1er janvier (+2,21% au 01/01/2026), CONFIRMÉ 2026-07-16. Source: CGSLB briqueteries + Partena. Provenance batch « ciment » corrigée.' },
    // ── FONDS SOCIAL POUR L'INDUSTRIE BRIQUETIÈRE — statuts lus, taux absent ─
    // ✅ CCT du 24/01/2019 « modification et coordination des statuts du Fonds
    // social pour l'industrie briquetière », chapitre V, art. 11 à 13 — dépôt
    // 151277/CO/114, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.
    //
    // ⛔ ET ELLE NE DONNE PAS DE TAUX, VOLONTAIREMENT : l'art. 12 dit que « le
    // montant et les modalités de perception des cotisations des employeurs
    // sont fixés par convention collective de travail conclue au sein de la
    // Commission Paritaire de l'Industrie des Briques, rendue obligatoire par
    // arrêté royal ». La banque CCT ne compte que TREIZE conventions pour cette
    // commission, et aucune ne fixe ce montant. Donc rien n'est écrit — le 5 %
    // reste, faute de mieux, mais il est désormais nommé comme non sourcé.
    //
    // 🚫 ET L'ONSS NE COMBLE PAS LE TROU ICI, contrairement à la CP 223. Le
    // fichier des taux 2026/3 rattache bien une catégorie 114 à cette CP, mais
    // sa SEULE ligne est le code 831, intitulé « Fonds Social C.P.A.E. (CP 200)
    // depuis le 01/04/2015 » à 0,23 % : c'est le fonds de la CP 200, dû pour les
    // EMPLOYÉS de l'employeur briquetier, pas le fonds de la briqueterie. Aucune
    // ligne 820 n'existe pour cette catégorie. Écrire ce 0,23 % comme taux de la
    // CP 114 aurait servi le fonds d'une autre commission — la faute exacte que
    // `employesRelevantDUneAutreCp` existe pour empêcher.
    // ── LA CCT MANQUANTE EST TROUVÉE, ET CE N'EST PAS UN POURCENTAGE ─────────
    // 📉 5,00 % → 0, et le 0 est ici la RÉPONSE JUSTE à « quel pourcentage du
    // brut ? », pas un aveu : ce fonds ne perçoit aucune cotisation
    // proportionnelle. Il perçoit deux FORFAITS, écrits sous
    // `cotisationsForfaitaires`.
    //
    // ⚠️ CE QUI AVAIT BLOQUÉ, ET POURQUOI C'ÉTAIT UNE ERREUR DE MESURE : la
    // note du 25/08 disait « introuvable dans les TREIZE conventions que la
    // banque CCT publie pour cette commission ». La banque en publie **140**.
    // Le treize venait d'une recherche filtrée par mots-clés ; en listant les
    // conventions PAR DATE, sans filtre, la CCT du 05/02/2026 sort au premier
    // coup d'œil — son titre dit mot pour mot « fixant le montant et les
    // modalités de perception des cotisations des employeurs ».
    // 🔁 Même famille que les cinq pièges déjà notés : l'outil répondait, c'est
    // le critère qui était faux. Un census dont on ne vérifie pas le
    // dénominateur peut sous-compter d'un facteur dix.
    //
    // 💰 Ordre de grandeur : ~675 € par ETP et par an pour le RCC, plus jusqu'à
    // 145 € par ouvrier et par an pour la prime sociale — environ 68 €/mois par
    // ETP. Le 5 % sur 108 % d'un brut de 2 500 € en facturait 135 €/mois.
    fondsSE: {
      nom: "Fonds Social pour l'Industrie Briquetière — cotisations forfaitaires, aucun pourcentage",
      taux_patronal: 0,
      tauxZeroExplique: {
        raison: "Ce fonds ne perçoit aucune cotisation proportionnelle au brut : ses ressources sont deux forfaits, l'un par ouvrier inscrit au registre du personnel, l'autre par équivalent temps plein. Un taux de 0 est la réponse JUSTE à « quel pourcentage du brut ? » — les montants réels sont sous `cotisationsForfaitaires`.",
      },
      cotisationsForfaitaires: {
        primeSociale: {
          montantAnnuelMaxParOuvrier: 145.00,
          montantAnnuelMaxParOuvrierEnRcc: 90.00,
          exercice: "du 1er juillet au 30 juin — exercices 2025 (01/07/2025-30/06/2026) et 2026 (01/07/2026-30/06/2027)",
          proRata: "12,083 € par mois ou mois commencé d'inscription au registre du personnel (art. 4 § 1)",
          source: "CCT du 05/02/2026 (CP 114), art. 10 § 1 — dépôt 197799/CO/114, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        },
        rccSectoriel: {
          formule: "3,00 € × 225 jours × nombre d'équivalents temps plein occupés du 1er octobre au 30 septembre",
          montantAnnuelParETP: 675.00,
          historique: "3,50 € ramenés à 3,00 € au 01/10/2020 ; mode de calcul modifié au 01/10/2024",
          etpCommentComptes: "relevés par le Fonds Social lui-même dans les flux de la Banque-Carrefour de la sécurité sociale — l'employeur ne les déclare pas",
          echeancesDePaiement: "15 mars 2026 pour la période 10/2024-09/2025 ; 15 mars 2027 pour 10/2025-09/2026",
          source: "CCT du 05/02/2026 (CP 114), art. 10 § 2 et § 3 — dépôt 197799/CO/114, texte primaire lu le 26/08/2026.",
        },
      },
      percepteur: "le Fonds Social lui-même, PAS l'ONSS (art. 10 § 3 : « versée au Fonds Social »). C'est pourquoi aucune ligne 820 n'apparaît sous la catégorie ONSS 114.",
      dateVerif: '2026-08-26',
      expireLe: '2026-12-31',
      expireNote: "La CCT du 05/02/2026 est à durée déterminée : en vigueur au 01/01/2025, elle « cesse d'être en vigueur » au 31/12/2026 (art. 27). Les montants ci-dessus sont donc à relire au 1er janvier 2027.",
      source: "CCT du 05/02/2026 (CP 114, industrie des briques) « relative à l'octroi et aux modalités de liquidation des avantages sociaux complémentaires et fixant le montant et les modalités de perception des cotisations des employeurs » — dépôt 197799/CO/114, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026 (42 875 caractères). C'est la CCT distincte à laquelle renvoyait l'art. 12 des statuts coordonnés du 24/01/2019.",
      statutsSource: "CCT du 24/01/2019 (CP 114), chapitre V « Financement », art. 11 à 13 — dépôt 151277/CO/114, texte primaire lu le 26/08/2026. Art. 11 : « Le Fonds dispose des cotisations dues par les employeurs visés à l'article 4 a. » Art. 13 : « Les cotisations sont perçues par le Fonds. » Le montant est renvoyé à une CCT distincte (art. 12) — celle du 05/02/2026 ci-dessus.",
      statutsDateVerif: '2026-08-26',
      employesRelevantDUneAutreCp: ['200'],
      employesNote: "L'ONSS rattache à la catégorie 114 la seule ligne du code 831, « Fonds Social C.P.A.E. (CP 200) depuis le 01/04/2015 » (0,23 %) : les employés d'un employeur briquetier relèvent du fonds de la CP 200, pas de celui-ci. Leur cotisation se calcule sur la fiche de la CP 200.",
      note: "Valeur précédente : 5,00 %, sans aucune source, et fausse dans son PRINCIPE — le fonds ne prélève pas de pourcentage.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.70, part_employeur: 2.61, part_travailleur: 1.09, source: "CCT du 05/02/2026 (CP 114, industrie des briques), dépôt 197798, banque CCT du SPF, texte primaire lu le 24/08/2026, art. 9 §1 : « Depuis le 1er janvier 2016, les ouvriers ont droit à des chèques-repas […] d'au moins € 2,70 par jour. À partir du 1er mars 2026, les chèques-repas seront augmentés de € 1 […] valeur faciale d'au moins € 3,70 par jour (quote-part employeur € 2,61, quote-part travailleur € 1,09). À partir du 1er novembre 2026, les chèques-repas seront à nouveau augmentés de € 1 […] valeur faciale d'au moins € 4,70 par jour (employeur € 3,61, travailleur € 1,09). » CE N'EST PAS UN DOUBLE RÉGIME mais un CALENDRIER en trois marches, applicable à tout le secteur. Valeur codée = celle EN VIGUEUR aujourd'hui. Le secteur fixe un MINIMUM (« au moins ») : une entreprise peut faire mieux. Portée : toute la CP.", regimeSecondaire: "ECHEANCE A VENIR : au 01/11/2026 la valeur faciale minimale passe a 4,70 EUR (part patronale 3,61 EUR, part travailleur inchangee a 1,09 EUR).", dateVerif: '2026-08-24', enVigueurLe: '2026-03-01', lien: '114/114-2026-001594.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Deux CCT « conditions de travail » (16/11/2021, 114-2021-013526.pdf, art.
    // 9 · et 2019, 114-2019-010663.pdf) accordent des éco-chèques, mais TOUTES
    // DEUX « d'une manière UNIQUE et NON-RÉCURRENTE » : 55 EUR pour la période
    // du 01/01 au 31/10/2021 (versés en décembre 2021), 50 EUR pour le premier
    // semestre 2019 (versés en septembre 2019). Aucune des deux n'établit de
    // droit ANNUEL permanent — chaque versement est renégocié CCT par CCT, et
    // les deux textes sont aujourd'hui expirés (fin de validité 31/12/2022 et
    // 2019). Aucun droit récurrent en vigueur à ce jour.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 16/11/2021 (114-2021-013526.pdf) et CCT de 2019 (114-2019-010663.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 9 des deux textes : versements UNIQUES et NON-RÉCURRENTS (55 EUR en 2021, 50 EUR en 2019), tous deux expirés. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT « Formation » (dépôt 182863/CO/114,
    // valable jusqu'au 31/12/2030), art. 7 : trajectoire 2j (2023-2024) →
    // 3j (2025-2026, valeur actuelle) → 4j (2027-2028) → 4,5j (2029) →
    // 5j (2030).
    formation: {
      joursSectoriels: 3,
      renvoi: "trajectoire de croissance (art. 7) : 2023-2024 = 2 j/an, 2025-2026 = 3 j/an, 2027-2028 = 4 j/an, 2029 = 4,5 j/an, 2030 = 5 j/an",
      fonds: "Fonds de formation de l'industrie des briques (CP 114)",
      source: "CCT « Formation » (CP 114), dépôt 182863/CO/114, art. 7, valable jusqu'au 31/12/2030, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 114: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 115 — Industrie verrière (généré batch 29/04/2026)
  '115': {
    // ── Classification CP 115 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 10/11/2023 « Conditions de travail et de
    // rémunération », banque CCT du SPF Emploi (115/115-2023-015519.pdf),
    // chapitre III, art. 5 et 6. Trouvée par recherche plein texte.
    //
    // ⚠️ DEUX POPULATIONS, DEUX ÉCHELLES QUI SE RÉFÈRENT L'UNE À L'AUTRE :
    // la fabrication a six GROUPES, l'entretien des CATÉGORIES A à D plus les
    // brigadiers — et l'art. 6 accroche l'entretien à l'échelle de la
    // fabrication en plaçant les manœuvres spécialisés « au minimum dans le
    // groupe 5 ». Traiter les deux échelles séparément perdrait ce plancher.
    classifications: {
      source: 'CCT du 10/11/2023, chapitre III art. 5 et 6 — banque CCT du SPF Emploi, 115/115-2023-015519.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « Conditions de travail et de rémunération »',
      fabrication: [
        { code: '1', critere: "aucune formation scolaire préalable, formation interne de moins d'une semaine, tâches simples répétitives sur UN poste, savoir lire et écrire, attention soutenue" },
        { code: '2', critere: "aucune formation scolaire spécifique, formation interne d'une semaine maximum, tâches simples répétitives sur PLUSIEURS postes" },
        { code: '3', critere: "formation interne d'une semaine à moins d'un mois, tâches plus difficiles sur plusieurs postes, capable d'exécuter les fonctions des groupes 1 et 2, niveau A3 ou équivalent — les GARDES et CONCIERGES sont également classés ici" },
        { code: '4', critere: "formation interne d'un à moins de trois mois, tâches exigeant une période d'adaptation et une connaissance spécifique sur plusieurs postes, niveau A3 ou équivalent" },
        { code: '5', critere: 'formation interne de trois à six mois, tâches exigeant une connaissance professionnelle (spécialisation), savoir travailler presque indépendamment avec un soutien hiérarchique minimal, niveau A2 ou équivalent' },
        { code: '6', critere: "formation interne de six mois ou plus, plusieurs tâches sur divers postes exigeant une connaissance approfondie du métier, autonomie complète, niveau A2 ou équivalent" },
      ],
      entretienEtServicesAuxiliaires: {
        manoeuvresSpecialises: 'classés AU MINIMUM dans le groupe 5 de la fabrication',
        ouvriersQualifies: [
          { code: 'A', critere: 'nouveaux diplômés A4, A3, B2' },
          { code: 'B', critere: "nouveaux diplômés A4, A3, B2 APRÈS une période d'essai" },
          { code: 'C', critere: 'diplômés A4 ou B6 ayant au moins deux ans d\'expérience' },
          { code: 'D', critere: 'diplômés A3, B2 ou B1 ayant au moins cinq ans d\'expérience' },
          { code: 'Brigadier', critere: 'diplômés comme prévu pour la catégorie D ET exerçant un commandement' },
        ],
        // ⚠️ La porte de sortie pour les non-diplômés — facile à manquer.
        sansDiplome: "l'accès aux catégories supérieures est prévu en cas de mérite exceptionnel ou d'ancienneté suffisante pour les ouvriers non diplômés",
      },
    },
    nom: 'CP 115 — Industrie verrière',
    nomCourt: 'Industrie verrière',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', note: 'Mécanisme réel = indice-pivot (historique +2% 01/05/2022, 01/11/2023, 01/01/2026 ; « indexation en centimes » dès 06/2026). Source CORRIGÉE (2026-07-16) : CGSLB verre-ouvriers — PAS Febelcem/ciment (mauvaise provenance batch). Calendrier janvier gardé (a bien capté 01/2026) ; taux exact à confirmer (Telegram/robot).' },
    fondsSE: {
      nom: 'Fonds social CP 115',
      taux_patronal: 0.0121,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '061',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 061 : 820 ouvriers 1,21 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // 🔴 Recherché le 05/09/2026 sans conclusion — 6 pistes essayées (Securex :
    // « peut offrir des chèques-repas », formulation non contraignante et sans
    // montant ; CGSLB/Synova, Group S, Limosa : rien). Valeur ci-dessous non
    // confirmée.
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 115 + « chèque
    // repas » dans la banque CCT du SPF Emploi : 4 CCT trouvées (conditions
    // de travail et de rémunération, 2009-2026), AUCUNE ne mentionne
    // « repas » ni « maaltijd ». Même mécanique que la CP 225 (R14 bis) :
    // remplace le triplet recopié 8/6,91/1,09.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 115 — recherche plein texte (4 CCT « conditions de travail et de rémunération », 2009-2026), aucune mention de chèque-repas",
      dateVerif: '2026-09-18',
    },
    ecoChequesAnnuel: 125, ecoChequesSource: "CCT du 26/01/2026 (CP 115, industrie verrière), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : éco-chèque de 125 EUR versé dans le courant du mois de JUILLET de chaque année, aux ouvriers actifs pendant la période de référence du 1er juillet de l'année précédente au 30 juin de l'année en cours, AU PRORATA des prestations. Valeur nominale maximale 10 EUR par chèque. ⚠️ La fiche portait 250 EUR — le double, et la valeur par défaut recopiée sur 155 fiches.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 17/04/2026 (dépôt 201901/CO/115,
    // enregistrée le 15/09/2026), art. 2-3 : cotisation 0,55% masse
    // salariale brute (108%), trajectoire 2j (2023-2024) → 3j (2025-2026,
    // valeur actuelle) → 5j (2027-2028) + droit collectif complémentaire
    // 2-3j/ETP/2ans.
    formation: {
      joursSectoriels: 3,
      cotisationFonds: 0.0055,
      fonds: "Fonds de formation professionnelle de l'industrie verrière (CP 115)",
      renvoi: "trajectoire (art. 3) : 2023-2024 = 2 j/an, 2025-2026 = 3 j/an, 2027-2028 = 5 j/an — droit collectif complémentaire de 2 à 3 j/ETP réparti sur 2 ans selon la période",
      source: "CCT du 17/04/2026 « Mesures concernant la formation » (CP 115), art. 2-3, dépôt 201901/CO/115, enregistrée le 15/09/2026, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 115: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Febelcem + CGSLB ciment',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 120.01 — Industrie textile de Verviers (généré batch 29/04/2026)
  '120.01': {
    nom: 'CP 120.01 — Industrie textile de Verviers',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // 🕰️ VINGT-CINQ ANS D'ÉCART ENTRE LA GRILLE ET SA PROMESSE DE REFONTE, et
    // c'est ça qu'il faut comprendre avant de lire ce bloc. Les CCT récentes
    // (16/12/2021 art. 22, 18/11/2019 art. 25) intitulent bien un chapitre
    // « Classification des fonctions » — mais il ne contient qu'une DEMANDE
    // adressée au CEFRET : dresser un cadastre des fonctions, présenter l'état
    // d'avancement de l'étude menée au niveau de la CP 120 nationale. Aucune
    // grille. C'est pour ça que cette CP avait été écartée le 23/08 au matin.
    //
    // 🔁 La grille EXISTE pourtant, et elle est complète : elle est dans la CCT
    // du 28/11/2001, que ni la recherche par titre ni le motif français
    // « catégories professionnelles » ne remontaient. C'est un motif de recherche
    // NÉERLANDAIS qui l'a rendue. Une CP sans classification récente n'est pas
    // une CP sans classification.
    classifications: {
      source: 'CCT du 28/11/2001 (SCP 120.01) — banque CCT du SPF Emploi, 12001/12001-2001-005105.pdf, trouvée par un motif de recherche en néerlandais (détail reproductible dans CLASSIF_CANAL_MOTIF_LINGUISTIQUE) ; chapitres « classification » des CCT du 18/11/2019 et du 16/12/2021 relus le même jour. Textes primaires lus le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE ET ANCIENNE : les montants de cette CCT sont en EUR et BEF (2001). La STRUCTURE est ce qui est codé ; aucun montant n'en sort. Le secteur a indexé depuis, et la grille elle-même n'a jamais été refondue.",
      perimetre: "industrie textile de l'arrondissement administratif de Verviers / Ostbelgien",
      systeme: "classification par SECTION de production, chaque section ayant ses propres catégories professionnelles nommées ; une distinction transversale sépare le travail à l'heure du travail aux pièces / à la prime / au rendement",
      sections: [
        { cle: 'lavage-carbonisation', libelle: 'Lavage et carbonisation de la laine', categories: ['I', 'II', 'III', 'IV', 'V', 'VI'] },
        { cle: 'peignage', libelle: 'Peignage' },
        { cle: 'filature-laine-peignee', libelle: 'Filature de laine peignée' },
        { cle: 'filature-laine', libelle: 'Filature de laine' },
        { cle: 'tissage', libelle: 'Tissage' },
        { cle: 'teinturerie-apprets', libelle: 'Teinturerie et apprêts' },
        { cle: 'services-generaux', libelle: 'Services généraux' },
      ],
      // ⚠️ DEUX MODES DE RÉMUNÉRATION DANS LA MÊME SECTION, et l'écart est
      // chiffré par la CCT elle-même : l'ouvrier d'une catégorie « à l'heure »
      // amené à travailler à la production voit son salaire horaire de
      // référence MAJORÉ DE 10 %. Servir le taux horaire nu à un travailleur
      // à la production le sous-paierait de 10 %.
      majorationProduction: 0.10,
      majorationProductionNote: "pour les ouvriers et ouvrières d'une catégorie considérée comme travaillant à l'heure mais amenés à travailler à la production, le salaire horaire moyen de référence s'obtient en majorant de 10 % le salaire horaire prévu pour le travail à l'heure.",
      servicesGeneraux: {
        note: "les métiers des services généraux sont cotés en MONOVALENT (M.) ou POLYVALENT (P.) — deux montants par métier, pas un.",
        metiers: [
          'Électricien-monteur', 'Ajusteur, fraiseur, tourneur, soudeur, mécanicien, forgeron, peigneur',
          'Maçon, plombier-zingueur, modeleur', 'Peintre, vitrier', 'Chauffeur de chaudière',
          'Chauffeur de transport (camions / camionnettes)', 'Graisseur',
          "Aide (attaché en permanence à la forge)", 'Manœuvre',
        ],
      },
      apprentis: "après six mois de formation, les apprentis sont rémunérés selon des règles fixées PAR SECTION, tenant compte de la durée requise pour chaque catégorie professionnelle ; la période s'achève quand le jeune exerce son métier de façon totalement indépendante avec une productivité au moins égale à la moyenne des ouvriers de sa catégorie. Les diplômés de l'École polytechnique porteurs de leur certificat voient les pourcentages majorés de 10 %. Progression codée par paliers de 2 mois, de 0 à 24 mois.",
      professionsNonReprises: "les travailleurs exerçant un métier NON repris aux barèmes voient leurs conditions salariales fixées par analogie avec les professions comparables, sans pouvoir descendre sous les montants de la grille.",
      chantierOuvert: "un cadastre des fonctions est demandé au CEFRET depuis au moins 2019 (art. 25 de la CCT du 18/11/2019, repris à l'art. 22 de celle du 16/12/2021), en lien avec l'étude de fonctions menée au niveau de la CP 120 nationale. Rien n'a encore remplacé la grille de 2001.",
    },
    nomCourt: 'Industrie textile de Verviers',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot 2% (textile)', dernierTaux: 0.02, derniereDate: '2026-07-01', note: 'INDEX-PIVOT seuil 2% indice santé lissé (corrigé de « semestrielle avril+octobre » 2026-07-16) : application possible aux 1/1, 1/4, 1/7, 1/10 — en 2026 a indexé au 01/01 ET 01/07 (jamais avril/octobre). Source: accg.be + SD Worx 120.01 + CGSLB textile.' },
    // ── 0,20 % SUR LES SALAIRES À 100 %, ET C'EST UNE VRAIE COTISATION ────
    // ✅ CCT du 30/06/2026 « Efforts en faveur des personnes appartenant aux
    // groupes à risque », chapitre III, art. 3 — dépôt 201177/CO/120.01, banque
    // CCT du SPF Emploi, texte primaire lu le 26/08/2026 : « Les employeurs sont
    // redevables, pour les années 2025 et 2026, d'une COTISATION de 0,20 %
    // calculée sur la base de la rémunération globale des travailleurs (salaires
    // à 100 %), comme visé à l'article 23 de la loi du 29 juin 1981 ».
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — vingt fois trop.
    //
    // ⚖️ Et ici le chapitre s'appelle « COTISATION PATRONALE », pas « effort » :
    // les employeurs en sont « redevables ». C'est ce qui la distingue des
    // CP 104, 210 et 325, où l'entreprise dépense elle-même.
    fondsSE: {
      nom: "Fonds social de l'industrie textile de Verviers",
      taux_patronal: 0.002,
      base: 'rémunération globale des travailleurs, salaires à 100 %',
      cctSourcee: {
        taux: 0.002,
        base: "rémunération globale des travailleurs (salaires à 100 %), art. 23 de la loi du 29/06/1981",
        source: "CCT du 30/06/2026 (SCP 120.01, industrie textile de Verviers), chapitre III « Cotisation patronale », art. 3 — dépôt 201177/CO/120.01, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        annees: [2025, 2026],
        perimetre: 'ouvriers',
      },
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT ne vaut que pour 2025 et 2026",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. ACCG (Centrale Générale-FGTB)
    // confirme explicitement l'application du régime de la CP 120 (textile) à
    // la 120.01 : 7,20 € (6,11 € employeur) au 01/02/2026. 🟠 Confiance moyenne :
    // une seule source directe pour l'extension à 120.01, non recoupée par une
    // seconde source propre à Verviers.
    chequesRepas: {
      obligatoire: true, valeur: 7.20, part_employeur: 6.11, part_travailleur: 1.09, aVerifier: true,
      source: 'Même régime que CP 120 (ACCG, Centrale Générale-FGTB) — extension à 120.01 confirmée explicitement par la source, confiance moyenne',
      dateEffet: '2026-02-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — PAS DE RÉGIME RÉCURRENT, même précédent que la
    // CP 330 (cf. eco-cheques-statut.js). 10 CCT successives lues (2017 à
    // 2026), chacune qualifie explicitement l'octroi d'« avantage unique,
    // exceptionnel et non récurrent » / « eenmalig, uitzonderlijk en
    // niet-recurrent » — renégocié CHAQUE ANNÉE, montant variable selon
    // l'ancienneté/les jours prestés. Dernière CCT (30/06/2026, dépôt
    // 201174/CO/120.01) : 230 EUR pour ≥26 jours de prestations effectives en
    // 2026, 30 EUR pour ≤25 jours, octroyé en décembre 2026 uniquement.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 120.01 — 10 CCT lues (2017, 2019 x2, 2020 x2, 2023, 2024 x2, 2025, 2026), toutes qualifient l'octroi d'« avantage unique, exceptionnel et non récurrent » (art. 3, formulation identique chaque année) — jamais un droit annuel permanent. Dernière CCT du 30/06/2026 (12001/12001-2026-014040.pdf), dépôt 201174/CO/120.01, texte primaire lu le 21/09/2026, art. 3 : 230 EUR pour les ouvriers ayant presté ≥26 jours effectifs en 2026, 30 EUR pour ≤25 jours, octroyé en décembre 2026, également aux intérimaires (art. 4).",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 30/06/2026 (dépôt 201962/CO/120.01,
    // enregistrée le 17/09/2026) : trajectoire individuelle 2j (dès 2023) →
    // 3j (dès 2024) → 4j (dès 2026, valeur actuelle) → 5j (dès 2028) ; droit
    // collectif : 1j (dès 2023) → 2j (dès 2026).
    formation: {
      joursSectoriels: 4,
      renvoi: "trajectoire : dès 2023 = 2 j, dès 2024 = 3 j, dès 2026 = 4 j, dès 2028 = 5 j (droit individuel) — droit collectif complémentaire : dès 2023 = 1 j, dès 2026 = 2 j",
      fonds: "Fonds de formation textile — sous-commission de Verviers (CP 120.01)",
      source: "CCT du 30/06/2026 « Apprentissage à vie et fonctionnement du marché du travail » (CP 120.01), dépôt 201962/CO/120.01, enregistrée le 17/09/2026, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 120.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB textile + IVOC',
      indexationFrequence: 'semestrielle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 120.03 — Fabrication de sacs en jute (généré batch 29/04/2026)
  '120.03': {
    // ── Classification CP 120.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT « Accord national » du 30/01/2026, banque CCT
    // du SPF Emploi (12003/12003-2026-001390.pdf), art. 3.
    // Trouvée par recherche plein texte : son titre ne dit rien d'une
    // classification.
    //
    // ⚠️ QUATRE CATÉGORIES DÉFINIES PAR DES GESTES, pas par des niveaux de
    // qualification. La D (contremaître, mécanicien qualifié) est bien la
    // plus haute, mais les A, B et C se distinguent par la NATURE du travail
    // — couture et estampillage, liage et manutention, entretien et
    // conduite — pas par une difficulté croissante évidente.
    classifications: {
      source: 'CCT « Accord national » du 30/01/2026, art. 3 — banque CCT du SPF Emploi, 12003/12003-2026-001390.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « Accord national »',
      categories: [
        { code: 'A', taches: ['coudre', 'couper', 'doubler des sacs', 'thermocouper', 'estampiller', 'étendre', 'déposer et enlever les sacs', "en bref tout le travail d'estampillage"] },
        { code: 'B', taches: ['lier', 'presser', 'manutention'] },
        { code: 'C', taches: ['entretien', 'chauffeur', 'charger et décharger'] },
        { code: 'D', taches: ['contremaître', 'mécanicien qualifié'] },
      ],
      primeEquipes: 0.07,
      primeEquipesNote: "en cas de travail en équipes, les salaires horaires minimums ET les salaires effectivement payés sont augmentés de 7 % (art. 4 de la même CCT)",
    },
    nom: 'CP 120.03 — Fabrication de sacs en jute',
    nomCourt: 'Fabrication de sacs en jute',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot 2% (textile)', dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT famille textile CP 120 (corrigé de « semestrielle avril+octobre » 2026-07-16) : a indexé 01/01/2026 (jamais avril/oct), comme 120.01. NB nom réel = « sacs en jute » (le lin = 120.02). Source: accg.be + CGSLB textile.' },
    // ⚠️ 4 % INVENTÉ → 0,60 %, LU AU TEXTE PRIMAIRE (26/08/2026).
    // CCT du 30/01/2026 « Cotisation patronale au fonds de sécurité
    // d'existence » (dépôt 197745/CO/120.03, AR du 12/07/2026), qui fixe la
    // cotisation « pour les années 2025 et 2026 » :
    //   1° « les employeurs versent […] une cotisation annuelle de 0,20 p.c.
    //      calculée sur la base du salaire complet de leurs travailleurs » ;
    //   4° « Depuis le 1er janvier 2005, les employeurs versent une cotisation
    //      de 0,40 p.c. […] à titre d'indemnité complémentaire en faveur de
    //      certains travailleurs âgés licenciés ».
    //   → 0,60 % en pourcentage.
    //   3° s'y ajoute un FORFAIT de 125,00 EUR par an et par travailleur
    //      inscrit au registre du personnel durant le 3e trimestre, payable à
    //      la fin de ce trimestre, pour financer l'allocation de sécurité
    //      d'existence.
    //
    // ⚠️ UN FORFAIT N'EST PAS UN TAUX et ne va pas dans `taux_patronal` : l'y
    // mettre le multiplierait par le brut. Il vit dans son propre champ, et
    // rien ne le facture encore — c'est écrit plutôt que tu.
    // ⏰ La CCT se donne pour « les années 2025 et 2026 » : à revérifier au
    // 1er janvier 2027.
    fondsSE: {
      nom: "Fonds de sécurité d'existence de la fabrication et du commerce de sacs en jute ou en matériaux de remplacement",
      taux_patronal: 0.006,
      base: 'salaire complet (art. 23 de la loi du 29/06/1981)',
      cctSourcee: {
        taux: 0.006,
        composantes: { fonctionnement: 0.002, indemniteTravailleursAgesLicencies: 0.004 },
        forfaitAnnuelParTravailleur: 125,
        forfaitCondition: "travailleur inscrit au registre du personnel durant le 3e trimestre, payable a la fin de ce trimestre — NON facture par l'app a ce jour",
        base: 'salaire complet',
        annees: [2025, 2026],
        source: "CCT du 30/01/2026 « Cotisation patronale au fonds de securite d'existence », 1°, 3° et 4° — depot 197745/CO/120.03, AR du 12/07/2026, banque CCT du SPF Emploi, 120.03, texte primaire lu le 26/08/2026. Le 4 % qui figurait ici n'avait aucune source.",
        dateVerif: '2026-08-26',
      },
      note: "La CCT se donne pour les annees 2025 et 2026 : a reverifier au 1er janvier 2027.",
    },
    // 🔴 DIVERGENCE NON RÉSOLUE, sondée le 05/09/2026 — l'ancienne valeur
    // (8,00 €) était la famille recopiée {8/6.91/1.09}. FGTB Centrale
    // ✅ CORRIGÉ LE 18/09/2026 — CCT primaire lue (celle que la note
    // précédente demandait). CCT du 30/01/2026 (« Système sectoriel de
    // chèques-repas », dépôt 02/02/2026, enreg. 09/02/2026, n°197748/CO/
    // 120.03), art. 5 : la progression 4,50 € (01/10/2023) → 5,50 €
    // (01/01/2026) → 6,00 € (01/10/2026) est CONFIRMÉE — la synthèse
    // antérieure n'était PAS contaminée. Valeur codée = tranche en vigueur
    // aujourd'hui (5,50 € = 4,41 € patronal + 1,09 € travailleur) ; la
    // tranche du 01/10/2026 (6,00 € = 4,91 € + 1,09 €) suivra automatiquement
    // deux semaines après cette lecture — à remonter à cette date, pas un
    // signe de péremption avant.
    chequesRepas: {
      obligatoire: true, valeur: 5.50, part_employeur: 4.41, part_travailleur: 1.09,
      source: "CCT du 30/01/2026 (n°197748/CO/120.03, dépôt 02/02/2026, enreg. 09/02/2026), art. 5 — banque CCT SPF Emploi",
      dateVerif: '2026-09-18',
      prochainPalier: { date: '2026-10-01', valeur: 6.00, part_employeur: 4.91, part_travailleur: 1.09 },
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 120.03 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026, mode batch)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 5.33, primeFinAnneeSource: "CCT du 30/01/2026 (SCP 120.03, fabrication de tapis), banque CCT du SPF, lue le 24/08/2026 : « une prime de fin d'annee d'un montant egal a 5,33 p.c. ». La fiche portait 8,33 % par defaut.",
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — CCT du 29/08/2023 (dépôt
    // 183382/CO/120.03), trajectoire (≥20 ETP) : 3j (2023) → 4j (2024) →
    // 5j (2025, plafond). Fin de validité non confirmée dans les documents
    // consultés (5 sur 31 résultats) ; entreprises 10-19 ETP : droit
    // collectif 2,5j (2023) → 3,5j (2024).
    formation: {
      joursSectoriels: 5,
      aVerifier: true,
      aVerifierRaison: "CCT du 29/08/2023 (dépôt 183382/CO/120.03) : trajectoire 2023=3j → 2024=4j → 2025=5j pour les entreprises ≥20 ETP (droit individuel), plafonnée à 5 j à partir de 2025. Fin de validité non confirmée dans les documents consultés (5 sur 31 résultats) ; aucune CCT plus récente sur les jours individuels retrouvée (seule une CCT groupes à risque du 30/01/2026 a été retrouvée pour 2025-2026, silencieuse sur les jours individuels).",
      cotisationGroupesRisque: 0.0020,
      renvoi: "entreprises 10-19 ETP : droit collectif 2,5j (2023) → 3,5j (2024) ; entreprises <10 ETP : idem",
      source: "CCT du 29/08/2023 « Formation » (CP 120.03), art. 3, dépôt 183382/CO/120.03, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 120.03: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB textile + IVOC',
      indexationFrequence: 'semestrielle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 125.01 — Exploitations forestières (généré batch 29/04/2026)
  '125.01': {
    nom: 'CP 125.01 — Exploitations forestières',
    // ── STRUCTURE DE RÉMUNÉRATION ─────────────────────────────────────────
    // ⚠️ CE SECTEUR NE CLASSE PAS DES GENS, IL TARIFE DES TRAVAUX. La
    // sylviculture est payée À LA PIÈCE, et la « catégorie » n'est pas le
    // métier du travailleur mais le croisement essence × travaux × espèce
    // d'arbre. Chercher ici une grille de fonctions, c'est chercher la
    // mauvaise chose — et un balayage naïf des nombres ramasserait des tarifs
    // à l'unité en croyant lire des salaires.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (jcId 0b94ca59…, CP 1250100, date=31/08/2022 en JJ/MM/AAAA avec slashes), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE — le SPF ne suit plus cette sous-commission depuis le 01/09/2022. La STRUCTURE est codée, les tarifs ne le sont pas.",
      grilleSectorielle: false,
      systeme: "rémunération à la pièce, structurée par ESSENCE puis par TRAVAUX puis par ESPÈCE D'ARBRE — pas par fonction du travailleur ; un salaire horaire minimum pour le travail effectif existe en parallèle, en régime 38 h",
      axes: [
        { niveau: 'essence', valeurs: ['Feuillus', 'Résineux'] },
        { niveau: 'travaux', valeurs: ['Abattage', 'Découpe et mise en tas sans chargement', 'Découpage normal en quelque endroit que ce soit'] },
        {
          niveau: "espèce d'arbre / produit de bois",
          valeurs: ['Toutes essences', 'Peupliers', 'Toutes espèces sauf pin sylvestre', 'Pin sylvestre', 'Troncs de sciage (cubage et recoupe de la pointe compris)', 'Baliveaux'],
        },
      ],
      horaire: "un salaire horaire minimum pour le travail effectif est prévu en régime 38 h/semaine, distinct des tarifs à la pièce.",
    },
    nomCourt: 'Exploitations forestières',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'trimestrielle', mois: [1, 4, 7, 10], dernierTaux: 0.0109, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : +1,09% au 01/07/2026 (notre 0,87% était la valeur du 01/04/2026 : entrée simplement en retard d\'un cycle). Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    fondsSE: {
      nom: 'Fonds social CP 125.01',
      taux_patronal: 0.155,
      taux_patronal_employe: 0.0023,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '029',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 029. Codes travailleur : 820 ouvriers 15,50 % · 831 employés 0,23 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Exploitations forestières »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Valeur codée précédente : 4,50 %.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. SD Worx (Ella), page sectorielle
    // 125.01 : liste exhaustive « Salaires et primes » (accord sectoriel,
    // classification, prime fin d'année, chômage temporaire, prime syndicale,
    // pouvoir d'achat) sans chèque-repas. Corroboré par Partena « avril 2026 ».
    chequesRepas: {
      obligatoire: false,
      source: 'SD Worx (Ella) + Partena Professional, fiches CP 125.01, concordantes',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 125.01 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 16/07/2025 (dépôt 194581/CO/125.01) :
    // seulement une cotisation groupes à risque 0,10% due au Fonds
    // Forestier. Aucun jour de formation individuelle trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours trouvé — seule une cotisation groupes à risque de 0,10% due au Fonds Forestier (CCT du 16/07/2025) ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      fondsFormationExiste: true,
      cotisationGroupesRisque: 0.0010,
      fonds: "Fonds de sécurité d'existence des exploitations forestières « Fonds Forestier » (CP 125.01)",
      source: "CCT du 16/07/2025 « Cotisation patronale pour les groupes à risque » (CP 125.01), art. 4, dépôt 194581/CO/125.01, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32, veloSource: "CCT du 26/05/2026 (SCP 125.01, exploitations forestières), banque CCT du SPF, thème 268, texte primaire lu le 24/08/2026 : « Les ouvriers qui, pour le déplacement entre le domicile et le lieu de travail, utilisent une bicyclette ont droit, à charge de l'employeur, à une indemnité bicyclette de 0,32 EUR par kilomètre de distance réelle (aller et retour). » ⚠️ La fiche portait 0,27 EUR — l'employeur SOUS-PAYAIT de 0,05 EUR par kilomètre.", veloDateVerif: '2026-08-24' },  // vélo CP 125.01: 0,32 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 125.02 — Scieries et industries connexes (généré batch 29/04/2026)
  '125.02': {
    // ── Classification CP 125.02 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 06/06/2011, n° 104766/CO/125.02, banque
    // CCT du SPF Emploi (12502/12502-2011-005847.pdf), art. 2.
    // Cinq catégories, définies par des MÉTIERS NOMMÉS — c'est exactement ce
    // qu'il faut pour afficher « la catégorie ET le type de travail ».
    classifications: {
      source: 'CCT du 06/06/2011, n° 104766/CO/125.02, art. 2 — banque CCT du SPF Emploi, 12502/12502-2011-005847.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      critere: 'degré de qualification',
      // ⚠️ LES GRILLES 125.02 ET 125.03 SE RESSEMBLENT — ET NE SONT PAS
      // IDENTIQUES. Différence de FOND : la catégorie 3 des scieries (125.02)
      // compte un métier de plus, « ouvrier transporteur routier », absent du
      // commerce du bois (125.03). Les factoriser en une seule constante
      // « pour faire propre » ferait disparaître ce métier d'un des deux
      // secteurs — la faute que R12 nomme avec les fiches jumelles qui
      // divergent.
      // ⚠️ Et une différence de FORME, à ne pas prendre pour une différence de
      // fond : les deux CCT écrivent le même métier autrement — « scieur de
      // dosseuse ET DE déligneuse » (125.02) contre « scieur de dosseuse ET
      // déligneuse » (125.03). Les libellés sont recopiés tels quels dans
      // chaque fiche, parce qu'une citation se cite, elle ne s'harmonise pas.
      categories: [
        { code: '1', label: 'Surqualifiés', metiers: ["contremaître spécialisé pouvant remplacer l'employeur", 'affûteur spécialisé', 'mécanicien'] },
        { code: '2', label: 'Qualifiés', metiers: ['contremaître-surveillant', 'scieur de grumes', 'affûteur ordinaire', 'scieur à la scie à quartier', 'conducteur-mécanicien de camions'] },
        { code: '3', label: 'Spécialisés', metiers: ['scieur de dosseuse et de déligneuse', 'ouvrier spécialisé dans le travail à la machine', 'conducteur de camions', 'magasinier', 'ouvrier transporteur routier'] },
        { code: '4', label: 'Non qualifiés', metiers: [], regle: 'les titulaires de toute fonction non reprise ci-dessus' },
        { code: '5', label: "Non qualifiés à l'embauche", metiers: [], regle: "ouvriers sans qualification ni expérience professionnelle dans les secteurs du bois, pendant les 6 PREMIERS MOIS de l'emploi ; après 6 mois, la fonction est convertie en « non qualifiés »" },
      ],
    },
    nom: 'CP 125.02 — Scieries et industries connexes',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'trimestrielle', mois: [1, 4, 7, 10], dernierTaux: 0.0109, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : +1,09% au 01/07/2026. Notre 2,20% ne correspondait à aucune indexation du secteur (janvier 2026 = +0,21%, avril = +0,87%). Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    fondsSE: {
      nom: "Fonds de Securite d'Existence des Scieries et industries connexes",
      taux_patronal: 0.1047,
      base: "salaires bruts a 108 %",
      cctSourcee: {
        taux: 0.1047,
        depuis: '2026-07-01',
        duree: "jusqu'au 31/12/2027",
        source: "CCT du 26/05/2026 (SCP 125.02, scieries et industries connexes), art. 3 : « Les cotisations patronales dues au Fonds de Securite d'Existence des Scieries et industries connexes sont fixees comme suit en % des salaires bruts a 108 % : du 1er juillet 2026 au 31 decembre 2027 : 10,47 % ; a partir du 1er janvier 2028 : 10,97 %. » Entree en vigueur au 01/06/2026, remplace la CCT du 14/03/2024 (186790/CO/125.02). Banque CCT du SPF Emploi, 12502/12502-2026-012383.pdf, texte primaire lu le 25/08/2026.",
        dateVerif: '2026-08-25',
        ecartAvecTauxCode: "l'app codait 4,5 % sans source : 5,97 POINTS de moins que la convention.",
      },
      // ✅ MEME CONTROLE INTERNE QUE LA 125.03, et il tombe juste aussi :
      //   8,87 securite d'existence + 0,1 groupes a risque + 0,2 formation
      //   permanente + 0,6 Confederation Belge du Bois + 0,7 Hout Info Bois = 10,47 %
      composantes: { securiteExistence: 0.0887, groupesARisque: 0.001, formationPermanente: 0.002, confederationBois: 0.006, houtInfoBois: 0.007 },
      // ⚠️ ECHEANCE ECRITE, PAS OUBLIEE : la meme convention porte le taux a
      // 10,97 % au 01/01/2028 (part securite d'existence 9,37 %).
      echeance: { taux: 0.1097, des: '2028-01-01', securiteExistence: 0.0937 },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. SD Worx (Ella), page sectorielle
    // 125.02 : liste exhaustive sans chèque-repas. Corroboré par Partena
    // « avril 2026 » (seulement indexation +0,87% et indemnité RGPT).
    chequesRepas: {
      obligatoire: false,
      source: 'SD Worx (Ella) + Partena Professional, fiches CP 125.02, concordantes',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 21/09/2017 « octroi d'éco-chèques » (12502/12502-2017-012401.pdf), banque CCT du SPF Emploi, dépôt 142233/CO/125.02, texte primaire lu le 21/09/2026, art. 2 : « Annuellement, au 1er juillet, les éco-chèques sont octroyés aux ouvriers... pour un montant de 250 EUR », valeur nominale max 10 EUR/chèque (art. 4), proratisé en cas de période incomplète ou de suspension (art. 3). En vigueur depuis le 01/01/2018, durée indéterminée. Précédée de CCT identiques (2009, 2011, 2013, 2016) au même montant.",
    ecoChequesDateVerif: '2026-09-21',
    ecoChequesPaiementDate: '01/07',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 16/07/2025 (dépôt 194585/CO/125.02) :
    // seulement une cotisation groupes à risque 0,10%. Aucun jour de
    // formation individuelle trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours trouvé — seule une cotisation groupes à risque de 0,10% due au Fonds de sécurité d'existence des Scieries et industries connexes (CCT du 16/07/2025) ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      fondsFormationExiste: true,
      cotisationGroupesRisque: 0.0010,
      fonds: "Fonds de sécurité d'existence des Scieries et industries connexes (CP 125.02)",
      source: "CCT du 16/07/2025 « emploi et formation des groupes à risque » (CP 125.02), art. 4, dépôt 194585/CO/125.02, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32, veloSource: "CCT du 26/05/2026 (SCP 125.02, scieries et industries connexes), banque CCT du SPF, thème 268, texte primaire lu le 24/08/2026 : indemnité bicyclette de 0,32 EUR par kilomètre de distance réelle (aller et retour), à charge de l'employeur. ⚠️ La fiche portait 0,27 EUR — sous-paiement de 0,05 EUR/km.", veloDateVerif: '2026-08-24' },  // vélo CP 125.02: 0,32 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 125.03 — Commerce du bois (généré batch 29/04/2026)
  '125.03': {
    // ── Classification CP 125.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 06/06/2011, n° …/CO/125.03, banque CCT du
    // SPF Emploi (12503/12503-2011-005852.pdf), art. 2.
    classifications: {
      source: 'CCT du 06/06/2011, art. 2 — banque CCT du SPF Emploi, 12503/12503-2011-005852.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      critere: 'degré de qualification',
      // ⚠️ LES GRILLES 125.02 ET 125.03 SE RESSEMBLENT — ET NE SONT PAS
      // IDENTIQUES. Différence de FOND : la catégorie 3 des scieries (125.02)
      // compte un métier de plus, « ouvrier transporteur routier », absent du
      // commerce du bois (125.03). Les factoriser en une seule constante
      // « pour faire propre » ferait disparaître ce métier d'un des deux
      // secteurs — la faute que R12 nomme avec les fiches jumelles qui
      // divergent.
      // ⚠️ Et une différence de FORME, à ne pas prendre pour une différence de
      // fond : les deux CCT écrivent le même métier autrement — « scieur de
      // dosseuse ET DE déligneuse » (125.02) contre « scieur de dosseuse ET
      // déligneuse » (125.03). Les libellés sont recopiés tels quels dans
      // chaque fiche, parce qu'une citation se cite, elle ne s'harmonise pas.
      categories: [
        { code: '1', label: 'Surqualifiés', metiers: ["contremaître spécialisé pouvant remplacer l'employeur", 'affûteur spécialisé', 'mécanicien'] },
        { code: '2', label: 'Qualifiés', metiers: ['contremaître-surveillant', 'scieur de grumes', 'affûteur ordinaire', 'scieur à scie à quartier', 'conducteur-mécanicien de camions'] },
        { code: '3', label: 'Spécialisés', metiers: ['scieur de dosseuse et déligneuse', 'ouvrier spécialisé dans le travail à la machine', 'conducteur de camions', 'magasinier'] },
        { code: '4', label: 'Non qualifiés', metiers: [], regle: 'toute fonction non reprise ci-dessus' },
        { code: '5', label: "Non qualifiés à l'embauche", metiers: [], regle: "ouvriers sans qualification ni expérience professionnelle dans les secteurs du bois, pendant les 6 PREMIERS MOIS de l'emploi ; après 6 mois, la fonction est convertie en « non qualifiés »" },
      ],
    },
    nom: 'CP 125.03 — Commerce du bois',
    nomCourt: 'Commerce du bois',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'trimestrielle', mois: [1, 4, 7, 10], dernierTaux: 0.0109, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : +1,09% au 01/07/2026. Même correction que 125.02. Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    fondsSE: {
      nom: "Fonds de securite d'existence du commerce du bois",
      taux_patronal: 0.1028,
      base: "salaires bruts a 108 %",
      cctSourcee: {
        taux: 0.1028,
        depuis: '2024-07-01',
        duree: 'indeterminee',
        source: "CCT du 21/12/2023 (SCP 125.03, commerce du bois), art. 3 : « Les cotisations patronales totales dues au Fonds de securite d'existence du commerce du bois sont fixees comme suit en % des salaires bruts a 108 % : du 01/01/2024 au 31/03/2024 : 10,78 % ; du 01/04/2024 au 30/06/2024 : 9,78 % ; a partir du 01/07/2024 : 10,28 %. » Remplace la CCT du 21/09/2017 (142257/CO/125.03). Banque CCT du SPF Emploi, 12503/12503-2024-000229.pdf, texte primaire lu le 25/08/2026.",
        dateVerif: '2026-08-25',
        ecartAvecTauxCode: "l'app codait 4,5 % sans source : 5,78 POINTS de moins que la convention.",
      },
      // ✅ CONTROLE INTERNE : l'art. 4 detaille la cle de repartition, et les
      // composantes somment EXACTEMENT au total. C'est une verification
      // independante du parseur — un decoupage errone ne tomberait pas juste.
      //   8,955 securite d'existence + 0,10 groupes a risque + 0,225 formation
      //   + 0,70 Hout Info Bois + 0,30 Confederation Belge du Bois = 10,28 %
      composantes: { securiteExistence: 0.08955, groupesARisque: 0.001, formation: 0.00225, houtInfoBois: 0.007, confederationBois: 0.003 },
      historique: "10,78 % du 01/01/2024 au 31/03/2024, puis 9,78 % jusqu'au 30/06/2024.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. ACCG (Centrale Générale-FGTB),
    // page commerce du bois : « Le secteur recommande aux entreprises
    // d'octroyer des chèques-repas d'un montant de 2 € (part patronale) » —
    // une RECOMMANDATION non contraignante, pas une obligation CCT. Corroboré
    // par Partena « avril 2026 » (aucune obligation mentionnée).
    chequesRepas: {
      obligatoire: false,
      note: 'Recommandation sectorielle non contraignante de 2 € (part patronale) — pas une obligation CCT',
      source: 'ACCG (Centrale Générale-FGTB), commerce du bois + Partena Professional',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 21/09/2017 « octroi d'éco-chèques » (12503/12503-2017-012415.pdf), banque CCT du SPF Emploi, dépôt 142246/CO/125.03, texte primaire lu le 21/09/2026, art. 2 : « Annuellement, au 1er juillet, les éco-chèques sont octroyés aux ouvriers... pour un montant de 250 EUR », valeur nominale max 10 EUR/chèque (art. 4), proratisé en cas de période incomplète ou de suspension (art. 3). En vigueur depuis le 01/01/2018, durée indéterminée, remplace la CCT du 27/01/2014 (120789/CO/125.03).",
    ecoChequesDateVerif: '2026-09-21',
    ecoChequesPaiementDate: '01/07',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 16/07/2025 (dépôt 194776/CO/125.03) :
    // seulement une cotisation groupes à risque 0,10%. Aucun jour de
    // formation individuelle trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours trouvé — seule une cotisation groupes à risque de 0,10% due au Fonds de sécurité d'existence pour le Commerce du bois (CCT du 16/07/2025) ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      fondsFormationExiste: true,
      cotisationGroupesRisque: 0.0010,
      fonds: "Fonds de sécurité d'existence pour le Commerce du bois (CP 125.03)",
      source: "CCT du 16/07/2025 « Groupes à risque » (CP 125.03), art. 4, dépôt 194776/CO/125.03, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32, veloSource: "CCT du 26/05/2026 (SCP 125.03, commerce du bois), banque CCT du SPF, thème 268, texte primaire lu le 24/08/2026 : indemnité bicyclette de 0,32 EUR par kilomètre de distance réelle (aller et retour), à charge de l'employeur. ⚠️ La fiche portait 0,27 EUR — sous-paiement de 0,05 EUR/km.", veloDateVerif: '2026-08-24' },  // vélo CP 125.03: 0,32 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 127 — Commerce de combustibles (généré batch 29/04/2026)
  '127': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 23/09/2016 (CP 127), banque
    // CCT du SPF Emploi, 127/127-2016-011516.pdf, art. 6.
    //
    // ⚠️ LE TEXTE LU EST UN ERRATUM DU SPF, et c'est important : le document
    // publié corrige explicitement la version française de l'article 6, qui
    // devient « … et occupant au moins 30 travailleurs, si au moins 25 p.c.
    // des travailleurs en font la demande. » La rédaction d'origine parlait
    // d'« ouvriers » ; c'est bien « travailleurs » qui fait foi.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_et_demande',
        seuilTravailleurs: 30,
        pctDemandeursRequis: 0.25,
        source: 'CCT du 23/09/2016 (CP 127) — statut de la délégation syndicale, art. 6 tel que corrigé par l\'erratum du SPF publié avec le texte, banque CCT du SPF Emploi, 127/127-2016-011516.pdf. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 127 — Commerce de combustibles',
    nomCourt: 'Commerce de combustibles',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.021886, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Fonds127.be', formule: 'sante_lisse_nov' },
    fondsSE: {
      nom: 'Fonds social pour les Entreprises de Commerce de Combustibles',
      taux_patronal: 0.157,
      base: "salaires bruts qui entrent en ligne de compte pour le calcul des cotisations de securite sociale",
      cctSourcee: {
        taux: 0.157,
        depuis: '2020-01-01',
        duree: 'indeterminee',
        source: "CCT du 18/09/2019 (CP 127, commerce de combustibles), « modification des statuts du Fonds social — cotisation des employeurs », art. 17 : « La cotisation visee a l'article 16 est fixee comme suit : pour tous les employeurs vises a l'article 4 : 15,7 % a partir du 1er janvier 2020, y exclus les 3 % reserves pour le Fonds de Pension pour le Commerce de Combustibles ASBL. » Banque CCT du SPF Emploi, 127/127-2019-012243.pdf, texte primaire lu le 25/08/2026.",
        dateVerif: '2026-08-25',
        ecartAvecTauxCode: "l'app codait 5 % sans aucune source : 10,7 POINTS de moins que la convention. Sur une masse salariale de 200 000 EUR, cela sous-evalue le cout employeur de plus de 21 000 EUR par an.",
      },
      // ⚠️ QUESTION LAISSEE OUVERTE, PAS TRANCHEE EN SILENCE. « 15,7 %, y exclus
      // les 3 % reserves pour le Fonds de Pension » se lit de deux facons : soit
      // la cotisation totale vaut 15,7 % dont 3 points vont au fonds de pension,
      // soit elle vaut 15,7 % EN PLUS de ces 3 % (donc 18,7 %). L'art. 3 precise
      // que la securite sociale verse ces 3 % DIRECTEMENT au fonds de pension,
      // ce qui plaide pour la premiere lecture — sans la prouver.
      // La valeur codee est la lecture LITTERALE de l'art. 17 (15,7 %), la plus
      // basse des deux : elle ne peut pas surevaluer le cout employeur.
      // ⚠️ LA CONVENTION DIT ELLE-MÊME QUE LA PENSION EST HORS DU TAUX.
      // « 15,7 % a partir du 1er janvier 2020, Y EXCLUS les 3 % reserves pour
      // le Fonds de Pension pour le Commerce de Combustibles ASBL » (art. 17).
      // C'est cette phrase, et elle seule, qui autorise à AJOUTER la pension
      // sectorielle perçue par l'ONSS au coût employeur sans risquer de la
      // compter deux fois. Sans preuve de ce genre, on n'ajoute rien.
      // ⚠️ Écart à nommer : l'ONSS perçoit 3,25 % sous le code 825 au trimestre
      // 2026/3, la CCT écrit 3 % — 0,25 point non expliqué au 25/08/2026.
      pensionExclueDuTaux: true,
      pensionSelonCCT: 0.03,
      questionOuverte: "Les 3 % du fonds de pension sont-ils COMPRIS dans les 15,7 % ou s'y AJOUTENT-ils ? Lecture litterale retenue : compris. A confirmer aupres du fonds.",
      verificationsFaites: "Aucune CCT « cotisation » a portee generale posterieure au 18/09/2019 pour la CP 127 : les conventions plus recentes (2021, 2023, 2025) portent sur les groupes a risque et les regimes de chomage avec complement d'entreprise. Le taux de 2020 est donc toujours celui en vigueur.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, dateReferenceRegimes: '2026-01-01', source: "CCT du 18/12/2025 (CP 127, commerce de combustibles), dépôt 198046, banque CCT du SPF, texte primaire lu le 24/08/2026, art. 2. DEUX RÉGIMES IMPOSÉS PAR LA CONVENTION : §1 « Dans les entreprises qui octroient déjà des chèques-repas, la contribution patronale sera augmentée, à partir du 1er janvier 2026, de € 2 nets par chèque-repas. » §2 « Dans les entreprises qui n'octroient pas encore de chèques-repas, ceux-ci seront introduits à partir du 1er janvier 2026 avec une valeur nominale nette de 3,09 €, dont 1,09 € à charge du travailleur et 2 € à charge de l'employeur. » §3 les entreprises qui versaient une INDEMNITÉ de repas au 27/10/2025 la majorent aussi de 2 € nets (réservée aux travailleurs prestant au moins 4 h/jour). La valeur codée est celle du §2, le seul cas où le secteur fixe une valeur faciale. Portée : toute la CP.", regimes: [ { cas: 'nouvel-octroi', depuis: '2026-01-01', libelle: { fr: "Entreprise qui n'octroyait pas encore de chèques-repas", nl: 'Onderneming die nog geen maaltijdcheques toekende' }, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09 }, { cas: 'deja-octroye', depuis: '2026-01-01', libelle: { fr: 'Entreprise qui en octroyait déjà', nl: 'Onderneming die er al toekende' }, supplementPatronal: 2.00 } ], dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '127/127-2025-015165.pdf' },
    // 🔴 CORRIGÉ LE 21/09/2026 — même défaut qu'à la CP 220 : « eco cheque » ne rendait
    // rien, « ecocheques » rend la CCT qui fixe le montant. 250 €/an dus depuis 2016.
    // Texte primaire lu le 21/09/2026, art. 4 § 1.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 01/03/2016 (CP 127, commerce de combustibles), dépôt 133108/CO/127, « octroi d'éco-chèques », banque CCT du SPF Emploi, texte primaire lu le 21/09/2026. Art. 4 § 1 : 250 € une fois par an à partir de 2016 pour un temps plein ; § 3 paiement en juillet ; § 4 montants dus pour une période de référence complète (12 mois). Sans date de fin, en vigueur ; la seule CCT postérieure citant le pouvoir d'achat (184879 du 14/11/2023) est une prime ponctuelle, échue au 31/12/2023.",
    // ✅ RÉSERVE LEVÉE LE 21/09/2026. Le PDF de la CCT est un SCAN — aucune police
    // embarquée, aucun ToUnicode, 4 images (vérifié en décompressant ses 11 flux) :
    // sa couche OCR rend la fraction par « ~ » dans la colonne NL et par « Yi » dans
    // la colonne FR. Les DEUX paliers du milieu portent le MÊME caractère, donc la
    // même fraction. Laquelle : le fonds social du secteur la publie en toutes
    // lettres — « Vanaf 1/2de tewerkstelling: € 150 per jaar · Minder dan 1/2de
    // tewerkstelling: € 100 per jaar » (fonds127.be, page « Ecocheques », lue le
    // 21/09/2026). Les montants, eux, viennent de la CCT elle-même.
    ecoChequesTempsPartiel: "Paliers de l'art. 4 § 2 : à partir de 4/5 d'un temps plein 250 €, à partir de 3/5 200 €, à partir d'1/2 150 €, moins d'1/2 100 €. En cas de mise au travail incomplète : 20 € par mois complet de mise au travail (art. 4 § 5, texte primaire lu). Période de référence : les 12 mois de juillet de l'année précédente à juin inclus ; paiement en juillet (art. 4 §§ 3-4). La fraction « 1/2 » des deux paliers du milieu est établie par le Sociaal Fonds voor de Ondernemingen van Handel in Brandstoffen (fonds127.be, lue le 21/09/2026) : le PDF de la CCT est un scan dont l'OCR perd ce caractère.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 04/09/2023 (dépôt 182497/CO/127,
    // durée indéterminée), art. 5§4 (≥20 ETP) : trajectoire 2j (2023) →
    // 3j (dès 2025, valeur actuelle) → 4j (dès 2027) → 5j (dès 2030).
    // 10-19 ETP : 1j/an fixe. <10 ETP exclues (art. 5§1).
    formation: {
      joursSectoriels: 3,
      renvoi: "trajectoire de croissance (≥20 ETP, art. 5 §4) : 2 j en 2023, 3 j depuis 2025, 4 j à partir de 2027, 5 j à partir de 2030 ; entreprises 10-19 ETP : 1 j/an fixe ; <10 ETP : exclues (art. 5 §1)",
      source: "CCT du 04/09/2023 « Formation » (CP 127), art. 5, dépôt 182497/CO/127, durée indéterminée, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 127: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 128 — Industrie des cuirs et peaux (généré batch 29/04/2026)
  '128': {
    // ── Classification CP 128 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 19/01/2026 « conditions de travail et de
    // rémunération », banque CCT du SPF Emploi (128/128-2026-000349.pdf),
    // art. 5 et ANNEXE 1 (articles 1 à 6).
    //
    // 🔎 TROUVÉE PAR LA RECHERCHE PLEIN TEXTE, PAS PAR LE TITRE. Aucune CCT
    // de la CP 128 ne s'intitule « classification » : celle-ci s'appelle
    // « conditions de travail et de rémunération » et porte la classification
    // en annexe. C'est exactement le cas que la limite du scan par titre
    // décrivait, et la preuve que la lever valait le détour.
    //
    // ⚠️ SIX INDUSTRIES, SIX ÉCHELLES QUI NE SE RESSEMBLENT PAS. La
    // maroquinerie compte 6 catégories, la ganterie 6 mais définies par des
    // OPÉRATIONS, la chaussure 9 classes, la sellerie 3, le commerce de peaux
    // brutes 4 fonctions nommées, et les tanneries une échelle de 5 niveaux
    // déclinée par type de tannerie. Appliquer l'échelle d'une industrie à
    // une autre classerait le travailleur au mauvais endroit.
    classifications: {
      source: 'CCT du 19/01/2026, art. 5 et annexe 1 (art. 1 à 6) — banque CCT du SPF Emploi, 128/128-2026-000349.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — aucune CCT de cette CP ne porte « classification » dans son titre',

      // ⚠️ RÈGLES OPÉRATOIRES, communes et décisives : elles tranchent les cas
      // réels bien plus souvent que la liste des métiers.
      regles: {
        pluriCategories: "lorsqu'un travailleur exécute plusieurs tâches classées dans des catégories différentes, il est classé dans la CATÉGORIE LA PLUS ÉLEVÉE",
        manoeuvres: "le manœuvre simple comme le manœuvre spécialisé et/ou lourd ne peut avoir AUCUNE responsabilité",
        litige: "en cas de difficulté dans une entreprise, chaque organisation peut recourir au comité de conciliation",
        polyvalence: "une fonction est polyvalente quand elle comprend l'exécution consécutive de deux fonctions simples ou plus, équivalentes ou non, reprises dans la classification, exécutées avec qualité et rendement normaux et avec un caractère de régularité, quelle qu'en soit la durée",
        fonctionNonMentionnee: "les fonctions non reprises dans la classification sont rangées dans une des classes par les membres de la commission paritaire",
      },

      industries: [
        {
          code: 'tanneries',
          label: 'Tanneries',
          article: 'annexe 1, art. 1',
          sousTypes: ['tanneries de gros cuir', 'tanneries de grosses peausseries', 'tanneries ovines et caprines'],
          echelle: 'cinq niveaux, déclinés par type de tannerie et par atelier (cuirs en poil, rivière, tannage et teinture, finissage)',
          niveaux: [
            { code: '1', label: 'Hors catégorie' },
            { code: '2', label: 'Travailleurs spécialistes' },
            { code: '3', label: 'Travailleurs qualifiés' },
            { code: '4', label: 'Manœuvres spécialisés et/ou lourds' },
            { code: '5', label: 'Manœuvres simples' },
          ],
          // Métiers nommés, pour le seul sous-type dont l'extraction est nette.
          metiersGrosCuir: {
            horsCategorie: ['écharneur main'],
            specialistes: ['blanchisseur main', 'ébourreur main'],
            qualifies: ['classeur (trieur)', 'crouponneur', 'dérayeur main', 'dérayeur (blanchisseur) machine', 'écharneur machine', 'égaliseur (refendeur-scieur)', 'essoreur (presseur)', 'façonneur main', 'lisseur (dérideur, metteur au vent, reteneur)'],
            manoeuvresSpecialises: ['aide-écharneur machine', 'aide-essoreur', 'cylindreur (brasseur, glaceur)', 'débardeur', 'ébourreur (façonneur) machine', 'pompiste (autoclave, extraits, écorces…)', 'travailleur sur pont roulant', 'recoucheur', 'rondoyeur', 'manœuvres ateliers de tannage', 'manœuvres ateliers de rivière', 'manœuvres cuirs en poil'],
            manoeuvresSimples: ['liftier', 'manœuvres atelier de lissage, séchage et finissage', 'manœuvres magasin produits finis'],
          },
          // ⚠️ Les listes des autres sous-types (grosses peausseries, ovines et
          // caprines) sont dans la CCT et ne sont PAS transcrites : elles
          // s'organisent par atelier sur plusieurs niveaux d'imbrication, et
          // l'extraction abîme les abréviations (« 1er » rendu « L" », « I'" »).
          // Les recopier à l'aveugle donnerait des métiers inexistants.
          autresSousTypesNonTranscrits: true,
        },
        {
          code: 'commerce-peaux-brutes',
          label: 'Commerce de cuirs et peaux bruts',
          article: 'annexe 1, art. 2',
          fonctions: ["chef d'équipe", 'classeur', 'chauffeur-crouponneur', 'manœuvres'],
        },
        {
          code: 'chaussure',
          label: 'Industrie de la chaussure, bottiers et chausseurs',
          article: 'annexe 1, art. 3',
          echelle: 'neuf classes de fonctions',
          classes: [
            { code: '1', definition: 'montage complet machine · coupe et brochage avec connaissance professionnelle complète · couture première Goodyear · tirage à la machine' },
            { code: '2', definition: 'montage Kneipp, main ou incomplet · brochage sur forme, de semelles, incomplet de tiges · tirage à la main · coupe ordinaire et peausserie · coutures · organisation du travail · pose et verrage de talons · fraisage · graduer et rabattre machine · cordonnerie à la main exigeant la connaissance du métier' },
            { code: '3', definition: 'montage-emboîtage machine automatique · rafraîchissage trépointes · marquage de point · déformes lisses · fraisage et verrage talons · poses premières · soudure machine · gravure première Goodyear · pose talons machine' },
            { code: '4', definition: "toutes les fonctions des classes précédentes, exercées par les DÉBUTANTS entre le 3e et le 6e mois qui suivent l'embauche" },
            { code: '5', definition: "toutes les fonctions des classes précédentes, exercées par les débutants qui exercent pour la PREMIÈRE FOIS une fonction du secteur, pendant les 2 premiers mois" },
            { code: '6', definition: 'piquage complet tiges et empeignes · parage complet · perforage complet' },
            { code: '7', definition: 'piquage, parage et perforage INCOMPLETS' },
            { code: '8', definition: 'les fonctions NON mentionnées dans les classes précédentes' },
            { code: '9', definition: "les fonctions des classes 6 à 8 exercées par les débutants qui exercent pour la première fois une fonction du secteur, pendant les 2 premiers mois" },
          ],
          reparationDeChaussures: {
            artisanale: { moinsQualifies: '5e classe', qualifies: '4e classe' },
            rapide: { moinsQualifies: '4e classe', qualifies: '3e classe' },
          },
        },
        {
          code: 'maroquinerie',
          label: 'Maroquinerie',
          article: 'annexe 1, art. 4',
          categories: [
            { code: '1', label: 'Surqualifiés', critere: "doit pouvoir réaliser TOUS les articles de la production, sans modèle ni patron" },
            { code: '2', label: 'Qualifiés', critere: "doit pouvoir réaliser UN des articles d'une production, sans modèle ni patron, quelle que soit la spécialité" },
            { code: '3', label: 'Semi-qualifiés', critere: "doit pouvoir réaliser un des articles d'une production À L'AIDE D'UN PATRON" },
            { code: '4', label: 'Spécialisés', critere: "doit pouvoir réaliser UNE PARTIE d'un des articles d'une production" },
            { code: '5', label: 'Semi-spécialisés', critere: 'participe à des travaux simples de la fabrication' },
            { code: '6', label: 'Débutants', critere: 'apprend à participer à des travaux simples, pour une période de 6 mois' },
          ],
        },
        {
          code: 'ganterie',
          label: 'Ganterie',
          article: 'annexe 1, art. 5',
          // ⚠️ Ici les catégories sont des OPÉRATIONS, pas des niveaux de
          // qualification : la 5 (« non-qualifiés ») s'intercale entre des
          // gestes techniques. Les lire comme une échelle croissante serait
          // une erreur.
          categoriesSontDesOperations: true,
          categories: [
            { code: '1', label: 'Triage' }, { code: '2', label: 'Lotissage' },
            { code: '3', label: 'Coupe' }, { code: '4', label: 'Fendre' },
            { code: '5', label: 'Non-qualifiés' },
            { code: '6', label: 'Fourchettes, couper, dresser, piquer, broder, noircir, emballer, raffiler' },
          ],
        },
        {
          code: 'sellerie',
          label: "Sellerie, fabrication de courroies et d'articles industriels en cuir",
          article: 'annexe 1, art. 6',
          categories: [
            { code: '1', critere: 'connaissance approfondie des matières employées (classement, triage, découpe), réglage de toutes les machines utilisées, montage de courroies' },
            { code: '2', critere: 'étirage sur planche, mise en suif, jonction, montage des courroies simples, collage des courroies doubles et articles textiles, lissage, dérayage, rayage machine, moulage des emboutis' },
            { code: '3', critere: 'teinture, mélange de couleurs, collage des courroies simples' },
          ],
        },
      ],

      // Les catégories BARÉMIQUES, qui sont un autre axe que les classifications
      // ci-dessus : 1B, 2A, 2B, 3A, 3B, 4, 5.
      categoriesBaremiques: ['1B', '2A', '2B', '3A', '3B', '4', '5'],
      // ⛔ Les taux horaires par catégorie figurent dans la CCT mais ne sont
      // pas repris : ils s'indexent, et la grille vérifiée est la source.
      baremesNonRepris: true,
    },
    nom: 'CP 128 — Industrie des cuirs et peaux',
    nomCourt: 'Industrie des cuirs et peaux',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.0107, derniereDate: '2026-07-01', source: 'AUDIT 2026-06 — Easypay', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 128',
      taux_patronal: 0.006999999999999999,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '169',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 169 : 820 ouvriers 0,70 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 6.0, part_employeur: 4.9, part_travailleur: 1.1, source: "CCT du 19/01/2026 (CP 128, cuirs et peaux) — erratum au texte français, banque CCT du SPF, texte primaire lu le 24/08/2026 : « Chaque travailleur a au MINIMUM droit à partir du 1er juillet 2026 à un chèque-repas par jour travaillé ouvrable, avec au minimum une valeur nominale de 6,00 EUR par chèque-repas, dont l'intervention de l'employeur s'élève à 4,90 EUR. » ⚠️ DEUX PALIERS : 5,00 EUR (employeur 3,90) au 01/01/2026, puis 6,00 EUR au 01/07/2026 — c'est le second qui est en vigueur au 24/08/2026. ⚠️ Ce sont des PLANCHERS. ⚠️ Part travailleur 1,10 EUR et non 1,09 : lue telle quelle, non alignée sur l'usage.", dateVerif: '2026-08-24', enVigueurLe: '2026-07-01', lien: '128/128-2026-000288.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Seul texte trouvé : CCT du 25/10/2017 « instaurant des écochèques »
    // (128-2017-…pdf), art. 2 : 50 EUR versés « de façon NON-RÉCURRENTE »
    // avant le 31/12/2017 aux ouvriers en service au 01/07/2017 — expirée
    // (fin de validité 31/12/2018). Le texte plafonne cet octroi au « maximum
    // de 250 euros par an » du CNT n°98, mais ne fixe lui-même aucun droit
    // annuel récurrent : aucune CCT plus récente trouvée par la recherche
    // plein texte.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 25/10/2017 « instaurant des écochèques » (128-2017-…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 2 : versement UNIQUE et NON-RÉCURRENT de 50 EUR avant le 31/12/2017, expiré. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT n° 170661/CO/128 du 20/12/2021 (remplace
    // 154533/CO/128 du 01/10/2019, durée indéterminée) : droit COLLECTIF de
    // 3 jours/an et par ouvrier au niveau ENTREPRISE (concertation CE/CPPT),
    // PAS un fonds. L'objectif interprofessionnel de 5j/an n'est cité que
    // comme cible à terme, jamais accordé.
    formation: {
      joursSectoriels: 3,
      mecanisme: "droit collectif au niveau de l'entreprise (concertation CE/CPPT), PAS un fonds sectoriel — objectif interprofessionnel de 5 j/an cité comme cible à terme, non accordé",
      renvoi: "CCT n° 170661/CO/128 du 20/12/2021, art. 3 (remplace 154533/CO/128 du 01/10/2019), durée indéterminée",
      source: "Banque CCT du SPF Emploi, CCT 170661/CO/128, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 128: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 129 — Production des pâtes, papiers et cartons (généré batch 29/04/2026)
  '129': {
    nom: 'CP 129 — Production des pâtes, papiers et cartons',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0189, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay' },
    fondsSE: {
      nom: 'Fonds social CP 129',
      taux_patronal: 0.0139,
      taux_patronal_employe: 0.0075,
      base108Ouvrier: true,
      onssCategorie: '189',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 189 : 820 ouvriers 1,39 % · employés 0,75 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — recherche sectorielle (pâtes, papiers et cartons) :
    // aucune CCT chèques-repas retrouvée pour cette CP, table Synova (ex-CGSLB)
    // exhaustive sur le secteur papetier ne la mentionne pas.
    chequesRepas: {
      obligatoire: false,
      source: "Synova (ex-CGSLB), table sectorielle chèques-repas — CP 129 absente de la liste des CP avec obligation",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 129 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 129 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 19/01/2026 (dépôt 197615/CO/129,
    // durée indéterminée depuis le 01/01/2025, remplace 181193/CO/129
    // expirée fin 2024) : ≥20 ETP, 2025-2026 = 2,5j individuel + 1,5j
    // collectif (moyenne). Cotisation 0,55% brut à 108%. 10-19 ETP : 1j/an
    // minimum.
    formation: {
      joursSectoriels: 2.5,
      cotisationFonds: 0.0055,
      renvoi: "droit individuel 2025-2026 = 2,5 j/ETP/an + droit collectif 1,5 j/ETP/an (moyenne) pour entreprises ≥20 ETP ; entreprises 10-19 ETP : 1 j/an minimum",
      fonds: "Fonds de sécurité d'existence (CP 129) — 0,55% des salaires bruts à 108% consacrés à la formation",
      source: "CCT du 19/01/2026 « Efforts de formation » (CP 129), art. 3 et 5, dépôt 197615/CO/129, durée indéterminée depuis le 01/01/2025 (remplace 181193/CO/129), banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 129: 0,32 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 132 — Entreprises de travaux techniques agricoles et horticoles (généré batch 29/04/2026)
  '132': {
    nom: 'CP 132 — Entreprises de travaux techniques agricoles et horticoles',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [4, 7], dernierTaux: 0.0105, derniereDate: '2026-07-01', source: 'AUDIT 2026-06 — Easypay', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 132',
      taux_patronal: 0.125,
      taux_patronal_employe: 0.021,
      pension_sectorielle: 0.0239,
      pension_sectorielle_employe: 0.0239,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '093',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 093. Codes travailleur : 820 ouvriers 12,50 % · 830 employés 2,10 % · 825/835 pension sectorielle 2,39 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Travaux techniques agricoles et horticoles »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Valeur codée précédente : 5,00 %.",
    },
    chequesRepas: { obligatoire: true, valeur: 6.0, part_employeur: 4.91, part_travailleur: 1.09, source: "CCT du 18/12/2025 (CP 132, entreprises techniques agricoles et horticoles), banque CCT du SPF, texte primaire lu le 24/08/2026. ⚠️ RÈGLE À DEUX BRANCHES, même architecture que les CP 144 et 145 : §1 — si aucun chèque-repas n'existait au 01/11/2025, valeur nominale 6,00 EUR au 01/01/2026 (patronal 4,91, travailleur 1,09) ; §2 — s'il en existait déjà, la part PATRONALE augmente de 4,91 EUR, plafonnée à 8,91 EUR.", regimeSecondaire: "entreprise ayant deja des cheques-repas au 01/11/2025 : part patronale +4,91 EUR, plafond 8,91 EUR", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '132/132-2026-000327.pdf' },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 18/12/2025 (CP 132, entreprises techniques agricoles et horticoles), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : éco-chèques une fois par an à charge de l'employeur, 250 EUR pour le travailleur à temps plein ayant une période de référence complète, par TRANCHES pour les temps partiels (250 EUR dès 4/5). Valeur nominale maximale 10 EUR par chèque. ⚠️ 250 EUR réels et sourcés.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 20/11/2024 (dépôt 191189/CO/132,
    // 2025-2026) : seulement une cotisation groupes à risque 0,10%. Aucun
    // jour de formation individuelle trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours trouvé — seule une cotisation groupes à risque de 0,10% pour 2025-2026 (CCT du 20/11/2024) ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      fondsFormationExiste: true,
      cotisationGroupesRisque: 0.0010,
      fonds: "Fonds social et de garantie pour les entreprises techniques agricoles et horticoles (CP 132)",
      source: "CCT du 20/11/2024 « Efforts en faveur des personnes appartenant aux groupes à risque pour l'année 2025 et 2026 » (CP 132), art. 3, dépôt 191189/CO/132, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 132: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 133 — Industrie des tabacs (généré batch 29/04/2026)
  '133': {
    // ── Classification CP 133 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 21/01/2026, n° 198674/CO/133, banque CCT
    // du SPF Emploi (133/133-2026-001527.pdf), chapitre II, art. 2.
    // Trouvée par recherche PLEIN TEXTE : son titre est « conditions de
    // travail », la classification est dedans.
    //
    // 🔎 EXTRACTION PAR COLONNE, ET C'ÉTAIT NÉCESSAIRE. Le PDF est bilingue
    // sur deux colonnes ; en l'aplatissant, le néerlandais se collait au
    // français et produisait des métiers qui n'existent pas — « le déliénage
    // des mamanokken » au lieu de « des manoques ». Seule la colonne de
    // droite (x ≥ 300) est reprise. Un libellé faux dans une classification
    // se recopie ensuite dans un contrat.
    classifications: {
      source: 'CCT du 21/01/2026, n° 198674/CO/133, chapitre II art. 2 — banque CCT du SPF Emploi, 133/133-2026-001527.pdf, texte primaire lu le 23/08/2026 (colonne française isolée)',
      dateVerif: '2026-08-23',
      perimetre: 'usines de cigares et de cigarillos',
      depuis: '1993-01-01',
      trouveePar: 'recherche plein texte — le titre de la CCT ne mentionne pas la classification',
      categories: [
        { code: 'I', taches: [
          'la conduite de machines à laver les bobines',
        ] },
        { code: 'II', taches: [
          'le déchirage du tabac étalé, le déliénage des manoques',
          'l\'écôtage et étalage des feuilles de cape et de sous-cape',
          'le sous-capage sur machines de finition',
          'la conduite des machines d\'écôtage de capes',
          'la confection de poupées sur machines à poupées, avec placement manuel en moules',
          'l\'approvisionnement et déchargement d\'une variété limitée de matières secondaires et/ou de matières premières',
          'la distribution de bobines',
          'l\'emballage manuel sans triage par couleur',
          'la conduite de machines à agrafer, clouer, coller, placer des charnières, étiqueter, imprimer ou coupel\'',
          'le nettoyage des installations sanitaires, des bâtiments et des alentours',
          'le nettoyage externe des machines, c\'est-à-dire le nettoyage externe mais complet des machines pour lequel d\'éventuels clapets et/ou couvercles peuvent être ôtés',
          'le personnel de cantine (le traitement et l\'entretien externe des appareils de cantine, approvisionner et servir)',
        ] },
        { code: 'III', taches: [
          'le capage manuel des cigares',
          'le capage manuel des cigarillos',
          'le capage sur machines de finition',
          'la conduite de machines à poupées PTS (pressage, tissage, séchage)',
          'la conduite de machines enrouleuses de poupées',
          'l\'introduction de capes ou sous-capes dans les machines enrouleuses automatiques',
          'la conduite de machines automatiques enrouleuses et dérouleuses pour capes et sous-capes',
          'la conduite de machines de matage et pressage',
          'la conduite de machines à baguer et à cellophaner - le cellophanage et bandelettage de petites boîtes et caissettes, fardelage',
          'le triage et l\'emballage de cigares en plusieurs couleurs',
          'la conduite de machines de triage sur couleurs',
          'la conduite de machines à haute technologie (entre autres machines HSO)',
          'la conduite de machines d\'emballage automatique - l\'emballage de colis sur palettes',
          'le contrôle de qualité visuel',
        ] },
        { code: 'IV', taches: [
          'la conduite de machines pour la préparation du tabac intérieur (grandes machines écôteuses)',
          'la préparation de diverses matières secondaires (matage et colle)',
          'le contrôle de la finition technique et des propriétés des produits comme la résistance au tirage, le poids et le taux d\'humidité',
          'le nettoyage technique, c\'est-à-dire l\'entretien périodique des machines et des pièces avec démontage et montage (pas nécessairement effectué par les travailleurs',
          'l\'assemblage et l\'emballage des commandes pour les clients',
          'le magasinier pour: les matières premières',
          'le chargement et le déchargement des camions et l\'entreposage des charges',
        ] },
        { code: 'V', taches: [
          'la conduite de camions',
          'le magasinier technique',
          'le préposé pour fournir des instructions lors de la formation de nouveaux venus dans la fabrication',
          'l\'entretien des bâtiments, du jardin et des installations',
        ] },
        { code: 'VI', taches: [
          'les mécaniciens des ateliers et de révision',
          'les électriciens',
          'les poseurs et les monteurs de salle',
          'les électroniciens',
        ] },
      ],
    },
    nom: 'CP 133 — Industrie des tabacs',
    nomCourt: 'Industrie des tabacs',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0039, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay' },
    fondsSE: {
      nom: 'Fonds social CP 133',
      taux_patronal: 0.0015,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '133',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 133 : 820 ouvriers 0,15 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 21/01/2026 (CP 133, industrie du tabac), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Ce titre-repas a une valeur nominale de 3,09 EUR, dont 2,00 EUR à charge de l'employeur et 1,09 EUR à charge du travailleur. »", dateVerif: '2026-08-24', enVigueurLe: '2026-01-21', lien: '133/133-2026-001490.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Seul texte trouvé : CCT du 09/10/2009 (133-2009-010450.pdf), art. 5 :
    // 125/100/75 EUR selon la fraction d'occupation (80-100 % / 50-79 % / <50 %),
    // versés en juillet 2009. Art. 9 : « entre en vigueur le 1er juillet 2009 et
    // cesse d'être en vigueur le 31 décembre 2009 inclus » — accord TRANSITOIRE
    // du second semestre 2009, expiré depuis. Aucune CCT plus récente trouvée
    // par la recherche plein texte (3 résultats, tous datés 2009).
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 09/10/2009 (133-2009-010450.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 5+9 : régime TRANSITOIRE du 01/07 au 31/12/2009 (125/100/75 EUR selon fraction d'occupation), expiré depuis. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — CCT du 29/06/2022 « Travail
    // faisable » (dépôt 175239/CO/133) : « droit individuel à 2 jours de
    // formation par an » (flat, pas de trajectoire), conclue en exécution
    // d'une CCT-cadre 2021-2022 expirée. La CCT plus récente « conditions
    // de travail et de rémunération » du 21/01/2026 ne cite plus aucun
    // nombre de jours individuel.
    formation: {
      joursSectoriels: 2,
      aVerifier: true,
      aVerifierRaison: "CCT du 29/06/2022 (dépôt 175239/CO/133), « 2 jours de formation par an », conclue en exécution d'une CCT-cadre 2021-2022 (10/11/2021, n° 168748) expirée ; aucune CCT de reconduction retrouvée pour 2023-2026 — la CCT « conditions de travail et de rémunération » du 21/01/2026 (toujours d'application) ne cite plus de nombre de jours individuel.",
      renvoi: "à défaut de confirmation : droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 29/06/2022 « Travail faisable » (CP 133), dépôt 175239/CO/133, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 133: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 136 — Transformation du papier et du carton (généré batch 29/04/2026)
  '136': {
    nom: 'CP 136 — Transformation du papier et du carton',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.0205, derniereDate: '2026-07-01', source: 'AUDIT 2026-06 — Partena', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 136',
      taux_patronal: 0.016,
      taux_patronal_employe: 0.0048,
      base108Ouvrier: true,
      onssCategorie: '089',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 089 : 820 ouvriers 1,60 % · employés 0,48 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}. ACCG (Centrale Générale-FGTB), corroboré Synova :
    // calendrier en 3 marches (2,09 € avant 2026, 3,09 € au 01/01/2026, 4,09 €
    // au 01/07/2026) — 4,09 € (3,00 € employeur) est la valeur en vigueur
    // aujourd'hui.
    chequesRepas: {
      obligatoire: true, valeur: 4.09, part_employeur: 3.00, part_travailleur: 1.09,
      source: 'ACCG (Centrale Générale-FGTB), transformation du papier et du carton, corroboré Synova : calendrier en 3 marches (2,09 € avant 2026, 3,09 € au 01/01/2026, 4,09 € au 01/07/2026 — valeur en vigueur aujourd\'hui)',
      dateEffet: '2026-07-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 136 + « eco cheque » :
    // 2 résultats, tous deux des faux positifs (« Economie »/« économique » +
    // chèques-repas, comme la CP 113). Aucune CCT éco-chèques trouvée.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT SPF Emploi, CP 136 — recherche plein texte « eco cheque » : 2 résultats, tous deux des faux positifs (économie/économique + chèques-repas), aucune CCT éco-chèques trouvée",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT du 17/12/2025 (dépôt 197418/CO/136),
    // art. 3, trajectoire sur 10 ans : 2j (2023-2024) → 2,5j (2025-2026,
    // valeur actuelle) → 3,5j (2027-2028) → 4j (2029-2030) → 5j (dès
    // 2031-2032) + 2j collectif (moyenne/2ans). Fonds « PaperPackSkills ».
    formation: {
      joursSectoriels: 2.5,
      renvoi: "trajectoire de croissance (art. 3, sur 10 ans) : 2023-2024 = 2 j, 2025-2026 = 2,5 j, 2027-2028 = 3,5 j, 2029-2030 = 4 j, dès 2031-2032 = 5 j (droit individuel/ETP/an) + 2 jours de formation collective en moyenne répartis sur 2 ans",
      fonds: "Fonds de formation PaperPackSkills (CP 136)",
      source: "CCT du 17/12/2025 « Mesures concernant la formation et plan de formation » (CP 136), art. 3, dépôt 197418/CO/136, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 136: 0,32 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 139 — Batellerie (généré batch 29/04/2026)
  '139': {
    nom: 'CP 139 — Batellerie',
    nomCourt: 'Batellerie',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-04-01', source: 'AUDIT 2026-06 — UCM' },
    // ⚠️ 5 % INVENTÉ → 4,17 %, LU AU TEXTE PRIMAIRE (26/08/2026).
    // Deux conventions du 17/02/2025, toutes deux en vigueur, toutes deux sur
    // le salaire brut à 108 % (codes salariaux DmfA 1, 3 et 4) :
    //   · frais de FONCTIONNEMENT — 2,64 % : « Les employeurs visés à l'article
    //     1er versent au "Fonds pour la navigation rhénane et intérieure" une
    //     cotisation de 2,64 p.c. calculée sur le salaire brut à 108 p.c. »
    //     (dépôt 192566/CO/139, AR du 27/05/2025, effet au 01/04/2025) ;
    //   · frais de FORMATION — 1,53 %, « La cotisation globale inclut la
    //     cotisation pour les groupes à risque de 0,10 p.c. »
    //     (dépôt 192565/CO/139, AR du 05/01/2026).
    //   → 4,17 % au total. Les deux CCT abrogent celles du 22/10/2020.
    //
    // ⚠️ RÉGIME PARTICULIER DES FLEXI-JOBS, écrit dans les deux textes : la
    // cotisation se calcule alors sur les salaires bruts à 100 % déclarés sous
    // les codes salariaux 22 et 23. Et elle n'est PAS due pour les élèves et
    // étudiants soumis à la cotisation de solidarité.
    fondsSE: {
      nom: 'Fonds pour la navigation rhénane et intérieure',
      taux_patronal: 0.0417,
      assiette: 'salaire brut à 108 % (codes salariaux DmfA 1, 3 et 4)',
      cctSourcee: {
        taux: 0.0417,
        composantes: { fonctionnement: 0.0264, formation: 0.0153, dontGroupesRisque: 0.001 },
        base: 'salaire brut à 108 %',
        depuis: '2025-04-01',
        duree: 'indeterminee',
        flexiJobs: { base: 'salaires bruts a 100 %, codes salariaux 22 et 23' },
        exclusion: "non due pour les eleves et etudiants soumis a la cotisation de solidarite",
        source: "CCT du 17/02/2025 « frais de fonctionnement », art. 2 (depot 192566/CO/139, AR du 27/05/2025, 139/139-2025-002015.pdf) et CCT du 17/02/2025 « frais de formation », art. 2 (depot 192565/CO/139, AR du 05/01/2026, 139/139-2025-002014.pdf) — banque CCT du SPF Emploi, textes primaires lus le 26/08/2026. Les deux abrogent les CCT du 22/10/2020. Le 5 % qui figurait ici n'avait aucune source.",
        dateVerif: '2026-08-26',
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}. Synova (ex-CGSLB) : 10,00 € (8,91 € employeur),
    // avec clause d'ajustement automatique au plafond légal. 🟠 CONFIRMÉ
    // UNIQUEMENT pour le sous-régime « transport régulier de passagers » — le
    // régime FRET/marchandises (majoritaire dans le secteur) n'a pas de
    // confirmation propre, ne pas généraliser sans vérification.
    chequesRepas: {
      obligatoire: true, valeur: 10.00, part_employeur: 8.91, part_travailleur: 1.09, aVerifier: true,
      aVerifierRaison: "Confirmé pour le sous-régime passagers seulement — le régime fret/marchandises (majoritaire) n'est pas confirmé.",
      source: 'Synova (ex-CGSLB), Batellerie',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 139 + « eco cheque » :
    // 1 résultat, faux positif. La CCT du 14/06/2023 « Prime pouvoir d'achat »
    // (139/…pdf, expirée le 31/12/2023) ne fait que COMPARER l'usage de la
    // prime à celui des éco-chèques (« idem ecocheques ») — elle n'établit
    // aucun régime d'éco-chèques elle-même. Aucune CCT éco-chèques trouvée.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT SPF Emploi, CP 139 — recherche plein texte « eco cheque » : 1 résultat, faux positif (CCT « Prime pouvoir d'achat » du 14/06/2023, mention comparative « idem ecocheques » seulement), aucune CCT éco-chèques trouvée",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.05, primeFinAnneeSource: "CCT du 18/03/2026, banque CCT du SPF, lue le 24/08/2026 : 8,05 % sur le brut DmfA (codes 1, 3, 4), periode octobre->septembre. La fiche portait 8,33 % par defaut.",
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT du 28/06/2022 (batellerie, dépôt
    // 175242/CO/139), art. 11 : plan de développement personnel + prise en
    // charge à 100% par l'employeur des formations demandées/exigées, mais
    // AUCUN quota de jours chiffré.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun nombre de jours fixé — la CCT du 28/06/2022 (batellerie) prévoit à l'art. 11 un plan de développement personnel individuel et la prise en charge à 100% par l'employeur des formations demandées/exigées, sans quota de jours chiffré ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 28/06/2022 « Transport régulier de passagers dans les eaux intérieures belges » (CP 139), art. 11, dépôt 175242/CO/139, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 139: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB + SD Worx sectoriel',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 140.01 — Autobus et autocars (généré batch 29/04/2026)
  '140.01': {
    nom: 'CP 140.01 — Autobus et autocars',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ TROIS BARÈMES SOUS UN SEUL NUMÉRO, ET LE SPF LES CODE SÉPARÉMENT :
    // 1400101 (services réguliers OTW-VVM), 1400102 (réguliers spécialisés),
    // 1400103 (occasionnels). Ce ne sont pas trois écritures de « 140.01 » —
    // ce sont trois régimes, et le barème applicable dépend du SOUS-SECTEUR de
    // l'employeur avant de dépendre du métier du travailleur.
    classifications: {
      source: "CCT du 25/06/2020 relative à l'accord social 2019-2020 (SCP 140.01), n° 159547/CO/140.01, art. 1er et 2 — banque CCT du SPF Emploi, 14001/14001-2020-008548.pdf. Texte primaire lu le 23/08/2026",
      dateVerif: '2026-08-23',
      systeme: "la rémunération se détermine d'abord par SOUS-SECTEUR, puis par catégorie de personnel — la catégorie du « personnel roulant » étant celle que les CCT nomment et barémisent",
      sousSecteurs: [
        { code: '1400101', libelle: 'Services réguliers pour le compte de l\'OTW (Wallonie) et de la VVM (Flandre)' },
        { code: '1400102', libelle: 'Services réguliers spécialisés' },
        { code: '1400103', libelle: 'Services occasionnels' },
      ],
      categoriePersonnel: "« membres du personnel roulant » = les membres du personnel féminin et masculin appartenant à la catégorie du personnel roulant, affectés à l'exécution des services concernés (art. 1er §2). Le barème du personnel roulant est propre au sous-secteur.",
      listeNonPubliee: true,
      ouLaDemander: "aucune des 28 CCT de la sous-commission ne publie de grille de fonctions détaillée (banque interrogée le 23/08/2026 avec « classification », « catégories professionnelles » et « barème ») ; la fiche SPF s'arrête au 19/02/2010 et rend un cadre vide. Le détail des barèmes par sous-secteur est à demander au SPF ou à la sous-commission.",
    },
    nomCourt: 'Autobus et autocars',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT (spilindex, corrigé de « annuelle janvier » 2026-07-16) : autobus/autocars, +2% au franchissement, date imprévisible (ex. effet 01/02/2026). Source: CGSLB CP 140.01 + brupartners synthèse CP 140.' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : UNE seule convention dans la banque CCT pour cette sous-commission — « emploi et formation des groupes à risque » du 17/09/2015 (dépôt 129705), qui cite 0,50 % et 0,05 %, échue depuis dix ans.
    // ✅ SOURCÉ LE 22/09/2026 — le 5 % non sourcé était le repli de troncature de
    // la CP mère 140/140.03 (retiré le 15/09/2026, voir cp-sous-division.js) ;
    // même quand la troncature a disparu du CODE, la fiche PROPRE restait un
    // nombre inventé, faute d'avoir sa propre preuve. Deux sources OFFICIELLES
    // établissent maintenant la catégorie ET le taux, indépendamment :
    //  1. L'ANNEXE STRUCTURÉE « catégories employeur » de l'ONSS (dépôt public
    //     Smals, github.com/smals-belgium/egov3-services-catalog,
    //     business_documentation/Annexes_Structurées_FR.zip, fichier
    //     EMPLOYER_CLASS_FR.xlsx) NOMME la catégorie 085, mot pour mot :
    //     « Employeurs relevant de la Commission paritaire du transport et de
    //     la logistique n° 140 et redevables d'une cotisation au "Fonds Social
    //     Bus & Cars" - ne concerne que les services spéciaux d'autobus
    //     (services réguliers spécialisés) et les services d'autocars
    //     (services occasionnels) ». ⚠️ Le fichier généré du dépôt
    //     (app/lib/ltds-codes.js) TRONQUE ce libellé à 120 caractères — texte
    //     complet relu sur le xlsx brut, pas sur la copie tronquée.
    //  2. Le FICHIER DES TAUX ONSS 2026/3 (déjà chargé, onss-fse-officiel.js,
    //     lu le 25/08/2026 — dmfa_rate_2026_3.zip, NOSSContributionRate_2026_3.xml)
    //     donne le taux de la catégorie 085, code 820 : 4,60 %.
    // Base 108 % : le code 820 lui-même se dit, dans son intitulé officiel,
    // « sur les rémunérations à 108 % des travailleurs manuels » (voir l'en-tête
    // de ce module) — même règle que la CP mère 140, pas une hypothèse propre.
    fondsSE: {
      nom: "Fonds Social Bus & Cars — services réguliers spécialisés et occasionnels d'autobus/autocars",
      taux_patronal: 0.046,
      onssSourcee: true,
      base: '108',
      source: "ONSS — fichier des taux DmfA 2026/3 (catégorie 085, code 820 : 4,60 %) + annexe 27 (catégorie 085 : Fonds Social Bus & Cars). Taux lu le 25/08/2026 ; libellé complet de l'annexe 27 relu le 22/09/2026 sur le dépôt Smals/egov3-services-catalog (EMPLOYER_CLASS_FR.xlsx — le fichier généré du dépôt, ltds-codes.js, le tronque à 120 caractères).",
      dateVerif: '2026-09-22',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}. Synova (ex-CGSLB), CP 140.01 : DEUX régimes
    // distincts selon le sous-secteur (cohérent avec les 3 régimes autobus que
    // CLAUDE.md documente déjà pour ce numéro) — VVM/OTW (services réguliers) :
    // 4,72 € (3,63 € employeur) ; services réguliers spécialisés + occasionnels :
    // 3,09 € (2,00 € employeur) au 01/04/2026. Valeur codée = le régime
    // spécialisés/occasionnels, le plus récemment daté.
    chequesRepas: {
      obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, aVerifier: true,
      aVerifierRaison: "Deux régimes coexistent sous ce seul numéro (VVM/OTW : 4,72€/3,63€ ; spécialisés/occasionnels : 3,09€/2,00€ au 01/04/2026) — la fiche ne porte qu'un triplet, à ventiler par sous-régime si un dossier réel l'exige.",
      source: 'Synova (ex-CGSLB), CP 140.01',
      dateEffet: '2026-04-01',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 140.01 — 6 CCT lues (2019-2020, couvrant les trois sous-régimes bus/autocars : réguliers OTW-TEC, réguliers spécialisés, autres services réguliers), toutes qualifient l'octroi d'« éénmalige ecocheque »/« éco-chèque unique » (125 EUR ou 180 EUR/ETP selon le sous-régime, en décembre 2019 ou janvier 2020) — jamais un droit annuel permanent. Aucune CCT plus récente n'en fait mention, texte primaire lu le 21/09/2026.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — recherche plein texte « formation »/
    // « opleiding » filtrée CP 140.01 → « 0 résultat(s) trouvé(s) » confirmé.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT de formation propre à la CP 140.01 dans la banque CCT (0 résultat confirmé sur formation/opleiding)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 140.01 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 140.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 140.02 — Taxis (SCP) (généré batch 29/04/2026)
  '140.02': {
    nom: 'CP 140.02 — Taxis (SCP)',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ DEUX POPULATIONS QUI NE SE RÉMUNÈRENT PAS DU TOUT PAREIL, et confondre
    // les deux donne un salaire faux : les CHAUFFEURS sont payés à un
    // pourcentage de la recette brute, le PERSONNEL DE GARAGE à un barème
    // horaire par catégorie. Seul le second a une classification de fonctions.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (jcId d1f0d545…, CP 1400200, date=31/08/2022), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF ne suit plus cette sous-commission depuis le 01/09/2022. Structure codée, montants non codés.",
      systeme: 'deux populations : chauffeurs de taxi (pourcentage de la recette brute) et personnel de garage (cinq catégories horaires)',
      chauffeurs: {
        mode: 'pourcentage de la recette brute multiplié par un coefficient',
        manqueVehicule: "si l'employeur n'est pas en mesure de mettre un véhicule à disposition du chauffeur, un régime propre s'applique",
        revenuMinimum: 'les chauffeurs ayant des prestations complètes bénéficient d\'un revenu minimum moyen (CCT du 20/11/2014)',
        classification: null, // pas de catégories de fonction : la rémunération est liée à la recette
      },
      fonctions: [
        { categorie: 1, fonction: 'Manœuvre', population: 'personnel de garage' },
        { categorie: 2, fonction: 'Ouvrier qualifié 3e catégorie', population: 'personnel de garage' },
        { categorie: 3, fonction: 'Ouvrier qualifié 2e catégorie', population: 'personnel de garage' },
        { categorie: 4, fonction: 'Ouvrier qualifié 1re catégorie', population: 'personnel de garage' },
        { categorie: 5, fonction: 'Ouvrier hors catégorie', population: 'personnel de garage' },
      ],
      champPersonnelGarage: 'personnel de garage des entreprises de taxis ou de services de location de voitures avec chauffeur (CCT du 21/11/2019, n° 155923)',
    },
    nomCourt: 'Taxis (SCP)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'INDEX-PIVOT (spilindex, corrigé 2026-07-16) : taxis, +2% au franchissement (moyenne 4 mois), dates observées 01/04, 01/05 — jamais janvier. Source: ACLVB CP 140.02 + icb-institute.' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : AUCUNE convention dans la banque CCT pour cette sous-commission, quel que soit le critère de recherche.
    // ✅ SOURCÉ LE 22/09/2026 — même méthode que 140.01, voir son commentaire
    // pour le détail des deux sources. L'Annexe structurée ONSS nomme la
    // catégorie 068, mot pour mot : « Employeurs relevant de la Commission
    // paritaire du transport et de la logistique n° 140 et redevables d'une
    // cotisation au "Fonds social des entreprises de taxi et des services de
    // location de voitures avec chauffeur" ». Le fichier des taux ONSS 2026/3
    // donne le taux de la catégorie 068, code 820 : 1,90 %.
    fondsSE: {
      nom: "Fonds social des entreprises de taxi et des services de location de voitures avec chauffeur",
      taux_patronal: 0.019,
      onssSourcee: true,
      base: '108',
      source: "ONSS — fichier des taux DmfA 2026/3 (catégorie 068, code 820 : 1,90 %) + annexe 27 (catégorie 068 : Fonds social des taxis et voitures avec chauffeur). Taux lu le 25/08/2026 ; libellé complet de l'annexe 27 relu le 22/09/2026 sur le dépôt Smals/egov3-services-catalog (EMPLOYER_CLASS_FR.xlsx — le fichier généré du dépôt, ltds-codes.js, le tronque à 120 caractères).",
      dateVerif: '2026-09-22',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 140.02 + « chèque
    // repas » : 0 résultat (« 0 résultat(s) trouvé(s) » affiché par le site
    // lui-même) — cohérent avec l'absence de couverture déjà mesurée pour
    // cette sous-commission (cf. cotisationSpeciale ci-dessus). Remplace le
    // triplet recopié 8/6,91/1,09.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 140.02 — recherche plein texte « chèque repas » : 0 résultat",
      dateVerif: '2026-09-18',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 140.02 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — recherche plein texte « formation »/
    // « opleiding » filtrée CP 140.02 → « 0 résultat(s) trouvé(s) » confirmé.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT de formation propre à la CP 140.02 dans la banque CCT (0 résultat confirmé sur formation/opleiding)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 140.02 : 0 résultat, relevé le 25/09/2026",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 140.02: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 140.03 — Transport routier et logistique pour compte de tiers (généré batch 29/04/2026)
  '140.03': {
    nom: 'CP 140.03 — Transport routier et logistique pour compte de tiers',
    type: 'O',
    regimeHebdo: 38,
    // MÊME référence (novembre) que l'industrie alimentaire — déjà confirmée dans la note
    // ci-dessous le 16/07/2026. Ce qui diffère est le MODE D'ARRONDI : 2,188604 % tronqué
    // donne les 2,18 % publiés là où l'arrondi donnerait 2,19 %. La troncature est déduite
    // de la reproduction du taux, pas lue dans la CCT → à confirmer sur le texte.
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0218, derniereDate: '2026-01-01', formule: 'sante_lisse_nov_tronque', note: 'Indexation annuelle au 1er janvier (indice santé lissé de novembre), CONFIRMÉ 2026-07-16. Source: CGSLB CP 140.03 + UPTR + salairesminimums jcId 1400300.' },
    // ✅ TAUX ÉTABLI LE 15/09/2026. Le « census fait, aucun taux établi » du
    // 26/08/2026 avait cherché sous la SEULE SCP 140.03 (8 conventions) : la
    // cotisation au FSTL est déposée sous la CP 140 MÈRE, avec un champ restreint
    // au sous-secteur. Leçon : chercher sous la CP mère ET sous la sous-CP.
    // CCT du 15/01/2026 « relative à la cotisation due au Fonds Social Transport
    // et Logistique » (CP 140, dépôt 197847, banque CCT du SPF,
    // 140/140-2026-000445.pdf), texte primaire lu le 15/09/2026 :
    //   · art. 1 §1 — employeurs de la CP du transport et de la logistique
    //     « appartenant au sous-secteur du transport de choses par voie terrestre
    //     pour compte de tiers et/ou au sous-secteur de la manutention de choses
    //     pour compte de tiers » ; champ publié par le SPF : ouvriers de la
    //     catégorie ONSS 083 (codes 015 ou 027), apprentis exclus ;
    //   · art. 3 — « la cotisation, due par les employeurs visés à l'article 1er
    //     au Fonds social, est fixée à partir du 1er juillet 2026, à 7,70 % des
    //     salaires déclarés à l'Office national de sécurité sociale à 108 % » ;
    //   · art. 4 — en vigueur le 01/01/2026, durée indéterminée.
    // Rendue obligatoire par AR du 27/08/2026 selon la banque CCT du SPF
    // (publication au Moniteur non encore indiquée au 15/09/2026). Recoupée par le
    // fichier des taux ONSS 2026/3 déjà branché : 7,70 % pour la catégorie 083.
    // 📏 EFFET SUR LA PAIE : AUCUN, et c'est mesuré. Le moteur ne lit la fiche d'une
    // sous-CP que si son taux est PROUVÉ (fonds-se-cotisation.js, `blocFondsSE`) :
    // le 5 % d'ici ne l'était pas, il lisait donc déjà la fiche « 140 » — 7,70 % à
    // 108 %, soit 204,38 €/mois sur un brut de 2 457,73 €, avec ou sans catégorie
    // 083 (mesure du 15/09/2026). Seul l'écran de la fiche CP affichait 5 %.
    // ⚠️ La note posée plus tôt le même jour (PR #3041) disait qu'appliquer ce taux
    // « changerait le coût employeur calculé » : c'était FAUX, faute d'avoir mesuré.
    fondsSE: {
      nom: "Fonds Social Transport et Logistique (FSTL)",
      taux_patronal: 0.077,
      base: '108',
      cctSourcee: {
        taux: 0.077,
        base: "salaires déclarés à l'ONSS à 108 % (ouvriers de la catégorie ONSS 083)",
        depuis: '2026-07-01',
        horsChamp: "apprentis (code travailleur 035, et apprentis déclarés sous le code 015 avec un contrat d'apprentissage)",
        rendueObligatoire: "AR du 27/08/2026 selon la banque CCT du SPF — publication au Moniteur belge non encore indiquée au 15/09/2026",
        source: "CCT du 15/01/2026 relative à la cotisation due au Fonds Social Transport et Logistique (CP 140, dépôt 197847), art. 1 et 3 — banque CCT du SPF Emploi, 140/140-2026-000445.pdf, texte primaire lu le 15/09/2026. Recoupée par le fichier des taux ONSS 2026/3 (catégorie 083 : 7,70 %).",
        dateVerif: '2026-09-15',
      },
      dateVerif: '2026-09-15',
      // Historique, conservé pour la trace : le census du 26/08/2026 qui laissait 5 %.
      historique: "Census du 26/08/2026, sous la seule SCP 140.03 : 8 conventions, la plus récente touchant au financement étant « formation continue » du 17/12/2015 (dépôt 132301), qui cite 1,9 % et 0,15 % — échue, et sans qu'un fonds percepteur y soit nommé pour ces montants. Il est conservé faute de valeur sourcée, et NOMMÉ pour qu'il ne se lise pas comme un chiffre vérifié. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation.",
    },
    // ✅ TEXTE PRIMAIRE LU le 20/08/2026 — CCT n° 197738/CO/140.03 du 15/01/2026,
    // « Octroi et/ou augmentation des chèques-repas et/ou de l'indemnité de repas »,
    // déposée le 21/01/2026, enregistrée le 09/02/2026, EN VIGUEUR, conclue en
    // exécution de l'accord sectoriel du 18/12/2025 (son art. 2 le dit).
    // Obtenue par la banque CCT du SPF Emploi — `app/lib/cct-search.js`.
    //
    // ⚠️ CE BLOC ANNONÇAIT 8,00 € / 6,91 € SANS AUCUNE SOURCE, et c'est LUI que
    // le code lit : `TAUX_SECTORIELS[cp]` est interrogé avec la CP telle qu'elle
    // est stockée sur la fiche, sans normalisation. Un employé en « 140.03 »
    // tombait donc ici, et l'agent d'onboarding lui annonçait 8 €/jour — plus du
    // quadruple de ce que la CCT impose. La même paire 8,00/6,91 figure encore à
    // l'identique sur 140.01, 140.02, 140.04 et 140.05 : quatre entrées qui n'ont
    // pas été vérifiées ici et qui portent la même odeur de valeur par défaut.
    // 🟠 À reprendre CP par CP, chacune ayant sa propre CCT.
    //
    // Ce que la CCT fixe VRAIMENT :
    //   · art. 3 §1 — quote-part PATRONALE de 2 €/jour presté, au plus tard au
    //     01/07/2026. C'est la seule valeur que ce texte impose ;
    //   · art. 3 §2 — qui accorde déjà l'avantage AUGMENTE sa part de 2 €/jour
    //     presté à la même échéance ;
    //   · art. 3 §3 — dispense pour qui a introduit ou augmenté entre le
    //     01/01/2025 et le 30/06/2026 ; si la hausse était inférieure à 2 €, la
    //     différence reste due au 01/07/2026 ;
    //   · art. 3 §4 — SIX MOIS d'ancienneté ininterrompue (l'intérim dans la
    //     même entreprise est assimilé) ; l'indemnité de repas exige au moins
    //     4 h prestées le jour même, le chèque-repas admet un autre mode de
    //     calcul que le nombre de jours prestés ;
    //   · art. 3 §5 — le choix entre chèque et indemnité appartient à l'EMPLOYEUR ;
    //   · art. 1 §2 — ouvriers de la catégorie ONSS 083, ÉTUDIANTS EXCLUS
    //     (code travailleur 840) ;
    //   · art. 4 — en vigueur au 01/01/2026, durée indéterminée.
    //
    // 🟠 CE QU'ELLE NE FIXE PAS : la valeur faciale (3,09 €) et la part
    // travailleur (1,09 €) n'apparaissent nulle part dans cette CCT, qui n'impose
    // que la part patronale. Elles viennent de l'accord sectoriel et gardent leur
    // cran 🟠 — à confirmer sur un texte qui les porte.
    chequesRepas: {
      obligatoire: true,
      valeur: 3.09,                        // 🟠 accord sectoriel 2025-2026 — ABSENT de la CCT 197738
      part_employeur: 2.00,                // ✅ CCT 197738 art. 3 §1
      part_travailleur: 1.09,              // 🟠 accord sectoriel 2025-2026 — ABSENT de la CCT 197738
      dateEntreeEnVigueur: '2026-07-01',   // ✅ art. 3 §1 « au plus tard à partir du 01.07.2026 »
      ancienneteMinimaleMois: 6,           // ✅ art. 3 §4
      dureeMinimaleJourHeures: 4,          // ✅ art. 3 §4 — pour l'INDEMNITÉ de repas uniquement
      choixParEmployeur: true,             // ✅ art. 3 §5
      note: 'OBLIGATOIRE au plus tard le 01/07/2026 : chèque-repas OU indemnité de repas, quote-part patronale de 2 € par jour presté, au choix de l\'employeur. Six mois d\'ancienneté ininterrompue requis (intérim dans la même entreprise assimilé) ; l\'indemnité exige 4 h prestées minimum. Dispense si l\'avantage a été introduit ou augmenté entre le 01/01/2025 et le 30/06/2026, le solde restant dû si la hausse était inférieure à 2 €. Étudiants (code travailleur 840) exclus. Source : CCT n° 197738/CO/140.03 du 15/01/2026, en vigueur, lue le 20/08/2026 via la banque CCT du SPF Emploi, en exécution de l\'accord sectoriel du 18/12/2025.',
    },
    // ⚠️ 15/09/2026 — deux valeurs recopiées retirées, une réponse sourcée à la place :
    //  · `ecoChequesAnnuel: 250` (la valeur par défaut de 155 fiches) → le régime de la
    //    CCT 199085 : 200 € aux seuls non roulants, sous condition (voir la constante) ;
    //  · `primeFinAnnee_pct: 8.33` + « 1 mois brut (anc. min 6 mois) », SANS source :
    //    la prime de cette SCP n'est pas payée par l'employeur mais par le FSTL, 5 % du
    //    brut ONSS de juillet à juin — elle vit dans prime-fin-annee.js ('140.03'), qui
    //    rendait null pour cette clé jusqu'à ce jour. Le verrou
    //    prime-fin-annee-une-seule-verite.test.js refuse désormais la divergence.
    ecoChequesConditionnel: ECO_CHEQUES_CONDITIONNELS_14003,
    // 🔴 CORRIGÉ LE 25/09/2026 — aucune CCT de formation vivante retrouvée
    // (motif formation/opleiding) pour cette sous-CP seule ; seule trace :
    // « formation continue » du 17/12/2015 (dépôt 132301), expirée, sans
    // fonds percepteur nommé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT de formation vivante retrouvée pour la SCP 140.03 seule (recherche formation/opleiding, 25/09/2026) — seule trace : « formation continue » du 17/12/2015 (dépôt 132301), expirée, sans fonds percepteur nommé ; à défaut, droit individuel fédéral (loi du 03/10/2022)",
      source: "Banque CCT du SPF Emploi, recherche plein texte formation/opleiding filtrée CP 140.03, relevé le 25/09/2026.",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "Piste non épuisée : chercher sous la CP mère 140 avec filtre restreint au sous-secteur — c'est ainsi que le fondsSE (FSTL) a été trouvé le 15/09/2026.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // ✅ TEXTE PRIMAIRE LU LE 15/09/2026 — CCT du 21/12/2023 (CP 140, dépôt 185637,
    // banque CCT du SPF, 140/140-2024-000100.pdf), champ : SCP 140.03, ouvriers de la
    // catégorie ONSS 083 ; en vigueur au 01/01/2024, durée indéterminée. Reprise par
    // la fiche Limosa du SPF (PSC 140.03, dernière adaptation 16/01/2026).
    //  · art. 5 — « A partir du 1er janvier 2024, l'intervention de l'employeur dans
    //    le prix du transport en commun public est portée à 90% du prix du titre de
    //    transport, sauf si l'entreprise […] a conclu un contrat de tiers payant […],
    //    auquel cas l'intervention de 80% est maintenue. » La fiche portait 1.00.
    //  · art. 7 — vélo 0,27 €/km aller et retour à partir du 01/01/2024 (confirme la
    //    valeur sourcée le 08/07/2026, cf. registre Garde-fou Sources).
    indemnites: { transportPublic_pct: 0.90, transportPublicTiersPayant_pct: 0.80, velo: 0.27 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 140.04 — Assistance en escale dans les aéroports (généré batch 29/04/2026)
  '140.04': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 24/10/2019 (SCP 140.04),
    // banque CCT du SPF Emploi, 14004/14004-2019-008590.pdf. Mot pour mot :
    //   « … une délégation syndicale est instituée dans les entreprises visées
    //     à l'article 1er, et occupant au moins 30 travailleurs, si au moins
    //     25 p.c. des travailleurs en font la demande. »
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_et_demande',
        seuilTravailleurs: 30,
        pctDemandeursRequis: 0.25,
        source: 'CCT du 24/10/2019 (SCP 140.04) — statut de la délégation syndicale, banque CCT du SPF Emploi, 14004/14004-2019-008590.pdf. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 140.04 — Assistance en escale dans les aéroports',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'CORRIGÉ 2026-08-05 : +2,23% au 01/01/2026 — l\'escale aéroportuaire ne suit PAS le 2,18% du transport routier 140.03. Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : 10 conventions ; les deux qui chiffrent une cotisation groupes à risque (24/11/2016, dépôt 136766, et 23/09/2015, dépôt 129825) donnent 0,10 % dont 0,05 % réservés — toutes deux échues.
    // ✅ SOURCÉ LE 22/09/2026, ET LE NOM DE FONDS CORRIGÉ AU PASSAGE. Même
    // méthode que 140.01 (voir son commentaire). Ce bloc disait « Déménagement »
    // — repéré comme faux le 05/09/2026 (« semble copié depuis la CP 140.05»)
    // mais non corrigé faute de la vraie valeur. L'Annexe structurée ONSS
    // confirme l'identité réelle de la catégorie 283, mot pour mot :
    // « Employeurs redevables d'une cotisation au Fonds social pour
    // l'assistance dans les aéroports et ressortissant à la Commission
    // paritaire du transport et de la logistique et appartenant au
    // sous-secteur de l'assistance en escale dans les aéroports (CP n° 140). »
    // Le fichier des taux ONSS 2026/3 donne le taux de la catégorie 283,
    // code 820 : 0,87 %.
    fondsSE: {
      nom: "Fonds social pour l'assistance dans les aéroports — sous-secteur assistance en escale",
      taux_patronal: 0.0087,
      onssSourcee: true,
      base: '108',
      source: "ONSS — fichier des taux DmfA 2026/3 (catégorie 283, code 820 : 0,87 %) + annexe 27 (catégorie 283 : Fonds social pour l'assistance dans les aéroports). Taux lu le 25/08/2026 ; libellé complet de l'annexe 27 relu le 22/09/2026 sur le dépôt Smals/egov3-services-catalog (EMPLOYER_CLASS_FR.xlsx — le fichier généré du dépôt, ltds-codes.js, le tronque à 120 caractères).",
      dateVerif: '2026-09-22',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}. Synova (ex-CGSLB), CP 140.04 : « Le chèque
    // s'élève à 10 € (à partir du 01/02/2026) », 8,91 € employeur. Condition :
    // travailleur (hors apprentis) ≥6 mois d'ancienneté, par jour de travail
    // effectif ≥3h.
    chequesRepas: {
      obligatoire: true, valeur: 10.00, part_employeur: 8.91, part_travailleur: 1.09,
      condition: 'travailleur (hors apprentis) avec ≥6 mois d\'ancienneté, par jour de travail effectif ≥3h',
      source: 'Synova (ex-CGSLB), CP 140.04',
      dateEffet: '2026-02-01',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 150,
    ecoChequesSource: "CCT du 22/01/2026 « éco-chèques et chèques-repas » (14004/14004-2026-001669.pdf), banque CCT du SPF Emploi, dépôt 197810/CO/140.04, texte primaire lu le 21/09/2026, art. 3 : dans le cadre du rapprochement des statuts ouvriers/employés, des éco-chèques sont octroyés aux ouvriers, évoluant vers un montant récurrent de 250 EUR/an. Trajectoire de croissance : 150 EUR au 01/12/2025 (montant EN VIGUEUR aujourd'hui), 200 EUR au 01/12/2026, 250 EUR à terme (date non précisée dans cette CCT — à re-vérifier après le 01/12/2026). Entreprises octroyant déjà un avantage équivalent, exclues (art. 4).",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT n° 180952/CO/140.04 du 27/06/2023
    // (remplace 145001/CO/140.04 du 25/01/2018, durée indéterminée) : PAS de
    // fonds — droit individuel variable selon la taille de l'entreprise,
    // art. 6 : <10 travailleurs = aucun droit ; 10-19 = 1j/an (ETP) ;
    // ≥20 = trajectoire 2j (2023-24) → 3j (2025-26, valeur actuelle) →
    // 4j (2027) → 5j (2030 seulement).
    formation: {
      joursSectoriels: null,
      mecanisme: "droit individuel à la formation, variable selon la taille d'entreprise (loi du 03/10/2022) — <10 travailleurs : rien ; 10-19 : 1 j/an ETP ; ≥20 : trajectoire de croissance 2j(2023-24)→3j(2025-26)→4j(2027)→5j(2030) — PAS un fonds sectoriel",
      renvoi: "CCT n° 180952/CO/140.04 du 27/06/2023, art. 5-6 (remplace 145001/CO/140.04 du 25/01/2018), durée indéterminée",
      source: "Banque CCT du SPF Emploi, CCT 180952/CO/140.04, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 140.04: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 140.05 — Déménagement (généré batch 29/04/2026)
  '140.05': {
    nom: 'CP 140.05 — Déménagement',
    nomCourt: 'Déménagement',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0218, derniereDate: '2026-01-01', note: 'Indexation annuelle au 1er janvier depuis 2024 (confirmé 2026-07-16). Source: CGSLB fiche CP 140.05 + fiche Limosa SPF Emploi. Taux exact à confirmer (Telegram/robot).' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : AUCUNE convention dans la banque CCT pour cette sous-commission, quel que soit le critère de recherche.
    // ✅ SOURCÉ LE 22/09/2026, ET LE NOM DE FONDS CORRIGÉ AU PASSAGE. Même
    // méthode que 140.01 (voir son commentaire). Ce bloc disait « Assistance
    // en aéroport » — c'est en réalité le nom qui manquait à la fiche 140.04
    // (le duo était inversé). L'Annexe structurée ONSS nomme la catégorie 084,
    // mot pour mot : « Employeurs relevant de la Commission paritaire du
    // transport et de la logistique n° 140 et redevables d'une cotisation au
    // "Fonds Social des entreprises de déménagement, gardes-meubles et leurs
    // activités connexes" ». Le fichier des taux ONSS 2026/3 donne le taux de
    // la catégorie 084, code 820 : 2,52 %.
    fondsSE: {
      nom: "Fonds Social des entreprises de déménagement, gardes-meubles et leurs activités connexes",
      taux_patronal: 0.0252,
      onssSourcee: true,
      base: '108',
      source: "ONSS — fichier des taux DmfA 2026/3 (catégorie 084, code 820 : 2,52 %) + annexe 27 (catégorie 084 : Fonds Social des entreprises de déménagement, gardes-meubles). Taux lu le 25/08/2026 ; libellé complet de l'annexe 27 relu le 22/09/2026 sur le dépôt Smals/egov3-services-catalog (EMPLOYER_CLASS_FR.xlsx — le fichier généré du dépôt, ltds-codes.js, le tronque à 120 caractères).",
      dateVerif: '2026-09-22',
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 19/03/2026 (SCP 140.05, déménagement), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Dans les entreprises qui n'octroient pas encore de chèques-repas, ceux-ci seront introduits à partir du 1er janvier 2026 avec une valeur nominale nette de 3,09 EUR, dont 1,09 EUR à charge du travailleur et 2,00 EUR à charge de l'employeur. » ⚠️ La convention détaille TROIS exemples chiffrés pour les entreprises qui en accordaient déjà (dont une à 5,00 EUR avec condition d'ancienneté de 6 mois) : la valeur codée ne vaut que pour celles qui n'en avaient pas.", regimeSecondaire: "entreprise ayant deja des cheques-repas au 31/12/2025 : voir les trois exemples chiffres de la CCT", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '14005/14005-2026-004124.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 140.05 (déménagement) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — recherche plein texte « formation »/
    // « opleiding » filtrée CP 140.05 → « 0 résultat(s) trouvé(s) » confirmé.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT de formation propre à la CP 140.05 dans la banque CCT (0 résultat confirmé sur formation/opleiding)",
      source: "Banque CCT du SPF Emploi, recherche plein texte formation/opleiding filtrée CP 140.05 : 0 résultat, relevé le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 140.05: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 142 — Valorisation de matières premières de récupération (généré batch 29/04/2026)
  '142': {
    nom: 'CP 142 — Valorisation de matières premières de récupération',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0223, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Partena (142.03=avril)' },
    // ── FAÎTIÈRE : LE TAUX VIT DANS LES SOUS-COMMISSIONS, TOUTES SOURCÉES ──
    // 📉 5,0 % → 0 le 26/08/2026, et c'est le cas le plus net de la série.
    // Cette commission n'a AUCUNE convention sous son propre numéro dans la
    // banque CCT du SPF — quatre sous-commissions sur quatre portent, elles, un taux
    // lu à la source, de 1,55 % à 11,70 %. Le nombre rond de la mère n'était donc ni
    // l'un ni l'autre : il valait sept fois et demie l'un d'eux, et il ne servait
    // qu'aux dossiers codés sur la commission mère, sans sous-commission.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE, et on ne fait pas de
    // moyenne : un employeur relève d'une sous-commission précise, c'est une
    // donnée du dossier. La fiche renvoie donc, et le calcul rend 0 tant qu'on
    // ne sait pas laquelle — même règle que la CP 152, posée le 25/08.
    fondsSE: {
      nom: "Récupération de matières premières — faîtière : le taux vit dans les sous-commissions",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '142.01': "Récupération de métaux — 1,55 %",
        '142.02': "Récupération de chiffons — 1,85 %",
        '142.03': "Récupération du papier — 2,60 %",
        '142.04': "Récupération de produits divers — 11,70 %",
      },
      source: "Banque CCT du SPF Emploi : aucune convention publiée sous le numéro 142 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Les sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS ou dans leur convention.",
      dateVerif: '2026-08-26',
      note: "Le 5,0 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (aucune
    // convention publiée sous le numéro 142 lui-même). Chaque sous-commission
    // (142.01/03/04/06) porte des montants qui DIVERGENT — une valeur unique
    // pour la mère serait nécessairement fausse pour au moins deux d'entre
    // elles. À ventiler par sous-CP si besoin, pas hérité de la mère.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — la CP 142 mère n'a pas de CCT propre",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 142 + « eco cheque » :
    // 0 résultat. Cohérent avec chequesRepas ci-dessus : la CP 142 mère n'a
    // pas de CCT propre.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 142 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CP mère : recherche plein texte « formation »/
    // « opleiding » filtrée CP 142 → « 0 résultat(s) trouvé(s) » confirmé.
    // Cohérent : le contenu réel vit dans les sous-CP 142.01-142.04.
    formation: {
      joursSectoriels: null,
      renvoi: "CP mère sans CCT de formation propre (0 résultat confirmé) — le contenu réel est dans les sous-CP 142.01-142.04, chacune vérifiée individuellement ; à défaut, droit individuel fédéral (loi du 03/10/2022)",
      source: "Banque CCT du SPF Emploi, recherche plein texte formation/opleiding filtrée CP 142 : 0 résultat, relevé le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 142: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 142.01 — Récupération de métaux (généré batch 29/04/2026)
  '142.01': {
    nom: 'CP 142.01 — Récupération de métaux',
    nomCourt: 'Récupération de métaux',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // 🕰️ Vingt-deux ans de « on va l'actualiser » : chaque accord national depuis
    // 1999 (art. 4.3 de 1999, art. 13 de 2003, art. 15 de 2005, art. 13 de 2021)
    // crée un groupe de travail pour moderniser « la classification EXISTANTE ».
    // Autrement dit, la classification existe bel et bien — elle vit dans la CCT
    // « salaires horaires », pas dans les accords nationaux qui en parlent.
    classifications: {
      source:
        'CCT du 29/11/2021 « Salaires horaires » (CP 142.01), n° 170143/CO/142.01, art. 2 — banque CCT du SPF Emploi, 14201/14201-2021-013989.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 38,
      systeme: 'cinq catégories A à E, chacune affectée d\'un coefficient de tension par rapport au non-qualifié (base 100)',
      fonctions: [
        { categorie: 'A', libelle: 'Non qualifié', tension: 100 },
        { categorie: 'B', libelle: 'Spécialisé 3e catégorie', tension: 112.5 },
        { categorie: 'C', libelle: 'Spécialisé 2e catégorie', tension: 125 },
        { categorie: 'D', libelle: 'Spécialisé 1re catégorie', tension: 132 },
        { categorie: 'E', libelle: 'Qualifié', tension: 140 },
      ],
      etudiants:
        'les étudiants jobistes ont droit à 90 % du montant de la catégorie professionnelle de l\'ouvrier exerçant une fonction comparable (art. 4, en exécution de la CCT du 22/06/2011 sur la formation salariale). Le second tableau de la CCT n\'est donc PAS un autre barème d\'ouvriers : c\'est cette grille étudiants.',
      planchersAccord:
        'accord national 2025-2026 (CCT du 17/03/2026, n° 200411/CO/142.01, art. 6) : engagement de rémunérer au minimum selon la catégorie A toute personne qui débute, contrats d\'étudiants exclus.',
      chantierOuvert:
        'un groupe de travail paritaire de modernisation de la classification est ouvert (art. 13 de l\'accord national 2021-2022 du 29/11/2021) et relié aux travaux d\'Educam sur la validation des compétences. Les tensions ci-dessus sont donc à re-vérifier à chaque accord national.',
      montantsNonCodes: 'les montants attachés à ces tensions datent du 01/01/2022 — non repris ici, indexation annuelle au 1er janvier.',
    },
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'CORRIGÉ 2026-08-05 : +2,23% au 01/01/2026 — récupération de métaux, distinct du 2,18% du transport. Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    fondsSE: {
      nom: 'Fonds social CP 142.01',
      taux_patronal: 0.0155,
      taux_patronal_employe: 0.0023,
      pension_sectorielle: 0.0195,
      pension_sectorielle_employe: 0.011899999999999999,
      base108Ouvrier: true,
      onssCategorie: '079',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 079 : 820 ouvriers 1,55 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "Accord national 2025-2026 du 09/06/2026 (SCP 142.01, récupération de métaux), banque CCT du SPF, texte primaire lu le 24/08/2026. ⚠️ RÈGLE À DEUX BRANCHES : « Augmentation de la part patronale des chèques repas de 2 EUR pour toutes les entreprises qui ont DÉJÀ introduit ce système, OU un chèque repas d'une valeur faciale de 3,09 EUR pour toutes les entreprises qui n'ont PAS ENCORE introduit de chèques repas, à partir du 1er juin 2026. » La valeur codée est celle de la seconde branche.", regimeSecondaire: "entreprise ayant deja des cheques-repas : part patronale +2,00 EUR", dateVerif: '2026-08-24', enVigueurLe: '2026-06-01', lien: '14201/14201-2026-013338.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 142.01 — le système sectoriel d'éco-chèques (CCT du 14/09/2023, dépôt 183200/CO/142.01, 250 à 260 EUR/an selon l'article) a été ABROGÉ par CCT du 09/04/2026 « Abrogation du système sectoriel des éco-chèques » (14201/14201-2026-005974.pdf), en exécution de l'accord national 2025-2026 du 17/03/2026, en compensation de l'introduction/augmentation des chèques-repas à partir du 01/06/2026 (voir chequesRepas ci-dessus). Texte primaire lu le 21/09/2026 : plus de régime éco-chèques en vigueur à ce jour.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT n° 199467/CO/142.01 du 17/03/2026
    // « Formation et emplois-tremplins » : PAS un fonds — droit
    // individuel/collectif variable par taille, art. 10 : <10 travailleurs
    // = collectif 3j/2ans ; 10-19 = individuel 3j/2ans ; ≥20 = trajectoire
    // 4j/2ans(2023-24)→6j/2ans(2025-26, valeur actuelle)→8j/2ans(2027-28)
    // →25j/5ans(dès 2029). Cotisation groupes à risque 0,15% séparée
    // (finance demandeurs d'emploi, pas les travailleurs en place).
    formation: {
      joursSectoriels: null,
      mecanisme: "droit à la formation variable selon taille — <10 travailleurs : droit collectif 3j/2ans ; 10-19 : droit individuel 3j/2ans ; ≥20 : trajectoire 4j/2ans(2023-24)→6j/2ans(2025-26)→8j/2ans(2027-28)→25j/5ans(dès 2029) — PAS un fonds ; cotisation groupes à risque 0,15% séparée (finance demandeurs d'emploi, pas les travailleurs en place)",
      renvoi: "CCT n° 199467/CO/142.01 du 17/03/2026 « Formation et emplois-tremplins », art. 10-11",
      source: "Banque CCT du SPF Emploi, CCT 199467/CO/142.01, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // ⚠️ velo 0,27→0,32 EUR/km des le 01/06/2026 (max 40 km/jour A-R) — CCT du 09/06/2026, banque CCT SPF, texte primaire lu le 24/08/2026, portee generale
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 142.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 142.02 — Récupération de chiffons (généré batch 29/04/2026)
  '142.02': {
    nom: 'CP 142.02 — Récupération de chiffons',
    nomCourt: 'Récupération de chiffons',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ Le PIÈGE de cette CP, à ne pas refaire : la banque CCT sert bien une
    // convention INTITULÉE « concernant la classification des fonctions »
    // (27/06/2005, 14202/14202-2005-002208.pdf). Elle ne contient AUCUNE
    // classification — six articles de mise en œuvre qui renvoient à l'art. 26
    // de la convention-cadre du 29/03/2005. C'est là, et nulle part ailleurs,
    // que les groupes salariaux et leurs fonctions sont écrits.
    classifications: {
      source:
        'CCT-cadre 2005-2006 du 29/03/2005 (CP 142.02), chapitre IX « Classification des fonctions », art. 26 — banque CCT du SPF Emploi, 14202/14202-2005-000933.pdf ; structure toujours en vigueur, confirmée par la CCT du 09/12/2021 n° 170822/CO/142.02 (tableau « Classifications des fonctions ») et par l\'accord national 2025-2026 du 24/02/2026 n° 200974/CO/142.02, art. 13. Textes primaires lus le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 36.75, // « régime 36h45/semaine », CCT du 09/12/2021, art. 3
      systeme: 'groupes salariaux (loongroepen) 1 à 6, deux familles d\'entreprises',
      familles: [
        { cle: 'traditionnel', libelle: 'Entreprises traditionnelles de chiffons', groupes: [1, 2, 3, 4, 5, 6] },
        {
          cle: 'automobile',
          libelle: 'Entreprises de récupération du textile pour l\'automobile',
          groupes: [1, 2, 3, 4, 5],
          note: 'le groupe 6 est « pas d\'application » ; le barème propre à cette famille ne vaut que pour les travailleurs ayant au moins 5 ans d\'ancienneté dans l\'entreprise — en dessous, ce sont les montants des entreprises traditionnelles qui s\'appliquent (art. 4 de la CCT du 27/06/2005). Lecture confirmée par extraction PAR COORDONNÉES du tableau 2021 : deux colonnes de chiffres pour trois cas, la colonne de gauche couvrant à la fois le traditionnel et l\'automobile de moins de 5 ans.',
        },
      ],
      fonctions: [
        { groupe: 1, fonction: 'Contremaître / chef de production' },
        { groupe: 1, fonction: 'Chef de l\'entretien' },
        { groupe: 1, fonction: 'Chef de file ST / mécanicien tooling' },
        { groupe: 1, fonction: 'Superviseur d\'équipe' },
        { groupe: 2, fonction: 'Chef de file / chef d\'équipe' },
        { groupe: 2, fonction: 'Chef de file / premier homme' },
        { groupe: 2, fonction: 'Électricien' },
        { groupe: 2, fonction: 'Mécanicien' },
        { groupe: 3, fonction: 'Collaborateur technique' },
        { groupe: 3, fonction: 'Premier homme collaborateur' },
        { groupe: 3, fonction: 'Magasinier technique' },
        { groupe: 3, fonction: 'Conducteur de chariot élévateur / chargement / déchargement / préparation des commandes' },
        { groupe: 3, fonction: 'Transport interne' },
        { groupe: 3, fonction: 'Opérateur de presses d\'emballage' },
        { groupe: 4, fonction: 'Inspecteur de qualité' },
        { groupe: 4, fonction: 'Contrôle chaîne de tri' },
        { groupe: 5, fonction: 'Ouvrier de production / opérateur' },
        { groupe: 5, fonction: 'Second triage sur le produit et la qualité' },
        { groupe: 5, fonction: 'Triage sur le produit et la qualité' },
        { groupe: 5, fonction: 'Coupe des chiffons d\'essuyage' },
        { groupe: 6, fonction: 'Triage sur le produit et la qualité sur la chaîne de tri' },
        { groupe: 6, fonction: 'Ouverture des sacs et premier tri brut' },
      ],
      // ⛔ Les montants de l'art. 26 (10,6425 €/h … ) datent de 2006 et ceux du
      // tableau 2021 du 01/01/2022 : le secteur a indexé depuis. La STRUCTURE
      // est ce qui est codé ici ; aucun montant n'en sort vers un client.
      montantsNonCodes: 'les barèmes horaires attachés à ces groupes ne sont PAS repris ici — les derniers lus datent du 01/01/2022 et le secteur indexe chaque 1er janvier. Passer par la fiche SPF ou la dernière CCT « salaires horaires ».',
    },
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0218, derniereDate: '2026-01-01', note: 'Indexation annuelle au 1er janvier (confiance MOYENNE, 1 source — 2026-07-16). Source: CGSLB récupération de matériaux (art. indexation janv. 2026). Provenance batch « transport+UBT » requalifiée. Taux exact à confirmer.' },
    fondsSE: {
      nom: 'Fonds social CP 142.02',
      taux_patronal: 0.018500000000000003,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '092',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 092 : 820 ouvriers 1,85 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 6.3, part_employeur: 5.21, part_travailleur: 1.09, source: "CCT du 24/02/2026 (SCP 142.02, récupération de chiffons), banque CCT du SPF, texte primaire lu le 24/08/2026 : « A partir du 1er septembre 2026, la part patronale dans le chèque-repas est augmentée de 1,00 euro, portant la valeur nominale du chèque-repas à 6,30 euros. Le part personnelle de l'ouvrier est de 1,09 euro par jour. » ⚠️ PAS ENCORE EN VIGUEUR au 24/08/2026 : cette valeur s'applique au 01/09/2026. Le texte n'énonce PAS le montant antérieur — il ne se déduit que par soustraction (5,30 EUR), ce qui n'est pas une lecture : ne pas l'affirmer.", dateVerif: '2026-08-24', enVigueurLe: '2026-09-01', lien: '14202/14202-2026-003068.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 142.02 (récupération de chiffons) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT du 24/02/2026 (dépôt/enregistrement
    // 198503), art. 10-15 : le fonds finance le remboursement de la
    // formation SYNDICALE et la « promotion de l'emploi et formation de
    // groupes à risque » (initiatives collectives, financement de projets
    // FOREM/VDAB/ACTIRIS) — aucun droit individuel en jours chiffré.
    formation: {
      joursSectoriels: null,
      fondsFormationExiste: true,
      fonds: "Fonds social CP 142.02 — finance formation syndicale (art. 10-11) et initiatives de formation des groupes à risque (art. 12-14), pas un droit individuel en jours",
      renvoi: "aucun droit individuel en jours trouvé dans la CCT relevée — le fonds finance des initiatives collectives (groupes à risque, formation syndicale), il ne garantit pas X jours au travailleur ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 24/02/2026 (CP 142.02), art. 10-15, dépôt/enregistrement 198503, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 142.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 142.03 — Récupération du papier (généré batch 29/04/2026)
  '142.03': {
    // ── Classification CP 142.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 13/02/2026 « conditions de travail et de
    // rémunération », banque CCT du SPF Emploi (14203/14203-2026-002505.pdf),
    // chapitre II, art. 2. Trouvée par recherche plein texte.
    // ⚠️ La catégorie 1 est DÉDOUBLÉE en 1A et 1B : six échelons, pas cinq.
    classifications: {
      source: 'CCT du 13/02/2026, chapitre II art. 2 — banque CCT du SPF Emploi, 14203/14203-2026-002505.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « conditions de travail et de rémunération »',
      categories: [
        { code: '1A', critere: "travaux d'entretien électriques et mécaniques exigeant des connaissances spéciales" },
        { code: '1B', critere: "travaux ordinaires d'entretien de mécanique" },
        { code: '2', critere: 'conduite de camions' },
        { code: '3', critere: "travaux lourds d'emballage et de déballage" },
        { code: '4', critere: "travaux d'aide au magasin, convoyage" },
        { code: '5', critere: 'manutention et travaux légers, triage' },
      ],
      primeEquipes: 0.10,
      primeEquipesNote: "dans les entreprises où le travail est organisé en équipes successives, les salaires horaires minimums sont augmentés d'une prime de 10 % (art. 3 de la même CCT)",
    },
    nom: 'CP 142.03 — Récupération du papier',
    nomCourt: 'Récupération du papier',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [4], dernierTaux: 0.0218, derniereDate: '2026-04-01', note: 'Indexation annuelle au 1er AVRIL (CORRIGÉ de janvier 2026-07-16). Source: CGSLB CP 142.03 récupération du papier (+2% au 01/04/2025, barème daté 01.04.2025). Provenance batch « transport+UBT » (erronée) remplacée. Taux exact à confirmer (Telegram/robot).' },
    fondsSE: {
      nom: 'Fonds social CP 142.03',
      taux_patronal: 0.026000000000000002,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '082',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 082 : 820 ouvriers 2,60 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 5.65, part_employeur: 4.56, part_travailleur: 1.09, source: "CCT du 13/02/2026 (SCP 142.03, récupération du papier), banque CCT du SPF, texte primaire lu le 24/08/2026 : « A partir du 1er mars 2026, il est attribué aux ouvriers un chèque-repas par jour effectivement presté, dont la valeur nominale est fixée comme suit : 5,65 euro, en ce compris une contribution patronale de 4,56 euro et une contribution personnelle du travailleur de 1,09 euro. » ⚠️ §2 — dans les entreprises qui accordaient DÉJÀ plus de 4,15 EUR au 31/12/2025, c'est la part patronale qui augmente de 1,50 EUR, sans valeur nominale sectorielle.", regimeSecondaire: "entreprise au-dessus de 4,15 EUR au 31/12/2025 : part patronale +1,50 EUR au 01/03/2026", dateVerif: '2026-08-24', enVigueurLe: '2026-03-01', lien: '14203/14203-2026-002691.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 142.03 (récupération du papier) — 2 CCT lues (12/10/2017, 200 EUR ; 18/10/2019, 220 EUR, dépôt 155372/CO/142.03), toutes deux qualifient l'octroi « de manière unique et non récurrente ». Aucune CCT plus récente n'en fait mention, texte primaire lu le 21/09/2026. Même précédent que CP 330/113.04/120.01/140.01.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT n° 198263/CO/142.03 du 13/02/2026
    // « Efforts en matière de formation permanente des ouvriers » (remplace
    // 185707/CO/142.03 du 20/12/2023) : même mécanique que 142.01 (droit
    // variable par taille, PAS un fonds de jours). Un « Fonds Social des
    // Entreprises pour la Récupération du Papier » EXISTE (cotisation
    // exceptionnelle 2,20% du 01/04 au 30/06/2026 puis 1,10% jusqu'au
    // 31/12/2027, brut à 108%) mais ne finance pas un droit en jours — fonds
    // social général.
    formation: {
      joursSectoriels: null,
      mecanisme: "droit à la formation variable selon taille — <10 : collectif 3j/2ans ; 10-19 : individuel 3j/2ans ; ≥20 : trajectoire 4j/2ans(base)→6j/2ans(2025-26)→8j/2ans(2027-28)→25j/5ans(dès 2029) — PAS un fonds de jours",
      fondsFormationExiste: false,
      fonds: "Fonds Social des Entreprises pour la Récupération du Papier — EXISTE (cotisation patronale exceptionnelle 2,20% du 01/04 au 30/06/2026 puis 1,10% jusqu'au 31/12/2027, brut à 108%) mais ne finance pas un droit en jours, c'est un fonds social général",
      renvoi: "CCT n° 198263/CO/142.03 du 13/02/2026, art. 2-3 (remplace 185707/CO/142.03 du 20/12/2023)",
      source: "Banque CCT du SPF Emploi, CCT 198263/CO/142.03, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 142.03: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 142.04 — Récupération de produits divers (généré batch 29/04/2026)
  '142.04': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 10/05/2005 (SCP 142.04),
    // statut de la délégation syndicale, banque CCT du SPF Emploi,
    // 14204/14204-2005-001381.pdf, art. 6 § 1er et § 2.
    //
    // 🔎 DÉBLOQUÉE PAR LA LECTURE EN COLONNES (app/lib/pdf-colonnes.js) : le
    // barème et le § 2 étaient coupés par la colonne néerlandaise.
    //
    // ⚠️ LE SEUIL NE S'ÉNONCE PAS COMME UNE INTERDICTION MAIS COMME UNE
    // FACULTÉ : « Pour les entreprises ayant moins de 35 ouvriers,
    // l'institution d'une délégation syndicale est facultative. » En dessous
    // de 35, elle reste donc POSSIBLE — elle n'est simplement plus imposée.
    // Répondre « pas de délégation en dessous de 35 » serait faux.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif',
        seuilOuvriers: 35,
        seuilTravailleurs: 35,
        endessousDuSeuil: 'facultative — l\'institution reste possible, elle n\'est plus imposée',
        delegues: [
          { de: 35, a: 50, effectifs: 2, suppleants: 2 },
          { de: 51, a: 150, effectifs: 3, suppleants: 3 },
          { de: 151, a: 200, effectifs: 4, suppleants: 4 },
        ],
        auDela200: 'un délégué effectif et un suppléant de plus par tranche entamée de 50 ouvriers',
        source: 'CCT du 10/05/2005 (SCP 142.04) — statut de la délégation syndicale, banque CCT du SPF Emploi, 14204/14204-2005-001381.pdf, art. 6. Texte primaire lu par colonnes.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 142.04 — Récupération de produits divers',
    classifications: {
      source: "CCT du 23/10/2023 (SCP 142.04, valorisation du verre), chapitre II « Classification des fonctions », banque CCT du SPF, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "CINQ categories, progression possible jusqu'a la plus haute (art. 3). Cat. 1 travail manuel (ni connaissances speciales ni aptitudes physiques particulieres : manoeuvre, trieur, chef de chaine) · cat. 2 operateur (travaux professionnels exigeant des connaissances acquises par l'experience, ou conduite d'une machine) · categories 3 a 5 au-dessus.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'CORRIGÉ 2026-08-05 : +2,23% au 01/01/2026 — récupération de produits divers. Source : Partena Professional (adaptations salariales 01/2026) + Easypay Group (fiche indexation sectorielle), deux sources indépendantes, vérifiées 2026-08-05.' },
    fondsSE: {
      nom: 'Fonds social CP 142.04',
      taux_patronal: 0.117,
      taux_patronal_employe: 0.0023,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '102',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 102. Codes travailleur : 820 ouvriers 11,70 % · 831 employés 0,23 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Récupération de produits divers »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Valeur codée précédente : 5,00 %.",
    },
    chequesRepas: { obligatoire: true, valeur: 5.16, part_employeur: 4.07, part_travailleur: 1.09, source: "CCT du 24/02/2026 (SCP 142.04, récupération de produits divers), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Il est attribué aux ouvriers, à partir du 1er janvier 2026, un chèque-repas par jour effectivement presté, dont la valeur nominale MINIMALE est fixée comme suit : 5,16 euros, en ce compris une contribution patronale de 4,07 euros et une contribution personnelle du travailleur de 1,09 euro. » ⚠️ C'est un PLANCHER. Et §2 — dans les entreprises accordant déjà plus de 3,16 EUR au 31/12/2025, la part patronale augmente de 2 EUR au 01/01/2026.", regimeSecondaire: "entreprise au-dessus de 3,16 EUR au 31/12/2025 : part patronale +2,00 EUR au 01/01/2026", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '14204/14204-2026-002993.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 142.04 (récupération de produits divers) — CCT du 26/11/2019 (215 EUR, dépôt 155978/CO/142.04), texte primaire lu le 21/09/2026, art. 3 : « des éco-chèques pour un montant total de 215 EUR sont octroyés de manière unique et non récurrente ». Aucune CCT plus récente n'en fait mention. Même précédent que CP 330/113.04/120.01/140.01/142.03.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT n° 183067/CO/142.04 du 26/09/2023
    // (remplace 168898/CO/142.04 du 19/11/2021, mod. par 173787 du
    // 20/01/2022) : PAS un fonds — droit variable par taille : <10 =
    // collectif 1,25j/an (≈10h/2ans) ; 10-19 = individuel 2j/2ans ; ≥20 =
    // trajectoire 4j/2ans(base)→6j/2ans(2025-26, valeur actuelle)
    // →8j/2ans(2027-28)→25j/5ans(dès 2029). Un « Fonds social » est évoqué
    // seulement pour « réfléchir à comment stimuler » les employeurs, pas
    // pour verser des jours.
    formation: {
      joursSectoriels: null,
      mecanisme: "droit à la formation variable selon taille — <10 : collectif 1,25j/an (≈10h/2ans) ; 10-19 : individuel 2j/2ans ; ≥20 : trajectoire 4j/2ans(base)→6j/2ans(2025-26)→8j/2ans(2027-28)→25j/5ans(dès 2029) — PAS un fonds",
      renvoi: "CCT n° 183067/CO/142.04 du 26/09/2023, art. 3 (remplace 168898/CO/142.04 du 19/11/2021, mod. par 173787 du 20/01/2022)",
      source: "Banque CCT du SPF Emploi, CCT 183067/CO/142.04, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 142.04: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB transport + UBT',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 143 — Pêche maritime (généré batch 29/04/2026)
  '143': {
    // ── Classification CP 143 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 04/07/2013, n° 116310/CO/143, banque CCT
    // du SPF Emploi (143/143-2013-008077.pdf), art. 3. Remplace la CCT du
    // 01/12/1989 (AR 15/05/1990, M.B. 29/06/1990).
    //
    // ⚠️ CHAMP RESTREINT, ÉCRIT DANS LA DONNÉE : la CCT ne vise que les
    // entreprises connues sous le code ONSS 086 (secteur des ENTREPÔTS) de la
    // pêche maritime. Elle ne couvre pas toute la CP 143.
    //
    // ⚠️ CETTE CLASSIFICATION EST UNE PROGRESSION DANS LE TEMPS, pas une
    // grille de métiers : on passe d'un niveau au suivant par l'ancienneté
    // dans le secteur ou par une formation VDAB. Le dire évite qu'on cherche
    // des libellés de fonctions qui n'existent pas.
    classifications: {
      source: 'CCT du 04/07/2013, n° 116310/CO/143, art. 3 — banque CCT du SPF Emploi, 143/143-2013-008077.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      perimetreSource: "entreprises sous le code ONSS 086 (secteur des entrepôts) — ne couvre pas toute la CP 143",
      nature: "progression par ancienneté et formation, pas une grille de métiers",
      niveaux: [
        { code: 'a', label: 'Non qualifiés (ongeschoolden)', critere: "ouvriers et ouvrières n'ayant reçu aucune formation" },
        { code: 'b', label: 'Exercés (geoefenden)', critere: "1) ceux ayant suivi la formation professionnelle dans un centre agréé par le VDAB ; 2) les non-qualifiés après 6 mois d'occupation dans le secteur ; 3) les chauffeurs" },
        { code: 'c', label: 'Qualifiés (geschoolden)', critere: "1) les personnes visées en b.1 après 1 mois d'occupation dans le secteur ; 2) les personnes visées en b.2 après 6 mois ; 3) les ouvriers occupés dans les minques et non titulaires d'une carte de reconnaissance délivrée par la commission paritaire" },
      ],
      egaliteHF: "l'art. 4 inscrit expressément l'égalité de traitement entre hommes et femmes dans l'exercice des professions reprises à la classification",
    },
    nom: 'CP 143 — Pêche maritime',
    nomCourt: 'Pêche maritime',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.015429, derniereDate: '2026-04-01', note: 'AUDIT 2026-06 — a terre +1,5429% au 01/04; marins a-la-part = RMMMG. Easypay 143' },
    // ── 4,00 % → 3,50 % OUVRIER (plancher sourcé) ET 0,23 % EMPLOYÉ (exact) ──
    // 📏 Trois catégories d'employeur ONSS pour la CP 143, et il faut lire les
    // deux colonnes séparément, parce qu'elles ne disent pas la même chose :
    //   · OUVRIERS — 3,50 %, 4,70 % et 5,45 % selon le métier. Réellement
    //     ambigu : le taux dépend de ce que fait l'employeur (vente en criée,
    //     armement avec personnel navigant, entrepôt près des halles). C'est
    //     une donnée du dossier, pas une convention de codage.
    //   · EMPLOYÉS — 0,23 % dans les TROIS catégories sur trois. Unanime, donc
    //     exact : il n'y a rien à trancher de ce côté.
    //
    // Le 4,00 % ne correspondait à aucune des trois. On retient la borne la
    // plus BASSE (3,50 %), sourcée, qui ne peut surfacturer aucun employeur, et
    // l'écran continue d'afficher « à trancher » pour les ouvriers.
    fondsSE: {
      nom: "Waarborg en sociaal Fonds voor de zeevisserij (CP 143)",
      taux_patronal: 0.035,
      taux_patronal_employe: 0.0023,
      base108Ouvrier: true,
      onssCategorie: '186',
      onssSourcee: true,
      perceptionOnssSeule: true,
      aVerifier: true,
      aVerifierRaison: "Le taux OUVRIER dépend de l'activité de l'employeur, que l'ONSS distingue en trois catégories (3,50 %, 4,70 %, 5,45 %). La valeur codée est la borne la plus basse — ventes de poissons, catégorie 186 — retenue comme plancher sourcé pour ne surfacturer personne. Le taux EMPLOYÉ, lui, n'est PAS concerné : il vaut 0,23 % dans les trois catégories et il est codé tel quel. Pour trancher le taux ouvrier : lire la catégorie d'employeur ONSS du dossier (DmfA Web).",
      tauxNonUnique: {
        raison: "trois activités de la pêche maritime, trois taux ouvriers — le taux employé, lui, est unanime",
        parCategorieOnss: {
          '186': { ouvrier: 0.035,  employe: 0.0023, activite: "exploitation et gestion de ventes de poissons (criées)" },
          '019': { ouvrier: 0.047,  employe: 0.0023, activite: "personnel NAVIGANT — travailleurs liés par un contrat d'engagement pour la pêche maritime, déclarés sur rémunérations forfaitaires" },
          '086': { ouvrier: 0.0545, employe: 0.0023, activite: "marchands de poissons et acheteurs en halles du littoral, pour le personnel de leurs entrepôts situés dans ou près de ces halles" },
        },
        source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml) croisé avec l'annexe 27, lu le 26/08/2026. Code 820 (ouvriers) et code 831 (employés), régime général.",
        dateVerif: '2026-08-26',
      },
      pensionSectorielle: {
        taux: 0.0125,
        note: "Second pilier sectoriel (code 825, 1,25 %) dans la seule catégorie 086. Ce n'est PAS un fonds de sécurité d'existence : il n'est pas additionné au taux ci-dessus.",
      },
      note: "Valeur précédente : 4,00 %, sans source ni date — et ne correspondant à aucune des trois catégories.",
    },
    // 🔴 RE-CONFIRMÉ LE 18/09/2026 (recherche plein texte CP 143 + « chèque
    // repas », 2 résultats) — MÊME fait que le registre CHEQUES_REPAS_BORNE_
    // BASSE de cheques-repas-hors-service.js (relevé le 24/08/2026) : le seul
    // document qui mentionne des chèques-repas (CCT du 03/11/2009, dépôt
    // 97549/CO/143) est un dispositif « pouvoir d'achat » PONCTUEL — 125 €
    // versés en chèques-repas de 4,07 € (2,98 € patronal / 1,09 € travailleur)
    // UNIQUEMENT en nov-déc 2009 et nov-déc 2010, CCT expirée le 31/12/2010.
    // ⚠️ Volontairement PAS classé `obligatoire: false` : une CCT expirée
    // n'est pas la preuve qu'aucune n'a pris le relais depuis (recherche
    // bornée à 2 résultats) — c'est un PLANCHER, géré comme tel par le
    // registre borne-basse (statut a-sourcer conservé, cf. fiche-cp.js). Le
    // triplet recopié ci-dessous reste un DÉFAUT non confirmé, distinct du
    // plancher réel de 4,07 €.
    chequesRepas: { obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09, aVerifier: true },
    // ✅ SOURCÉ LE 22/09/2026 — recherche EXHAUSTIVE reprise (les 4 orthographes
    // standard, workflow cct-search.yml, run 35713503061), pas bornée à 1
    // résultat cette fois. La note du 18/09/2026 ci-dessous n'avait trouvé que
    // la CCT « programmation sociale 2009-2010 » (125 € UNIQUE, expirée
    // 31/12/2010) et avait, à raison, refusé de conclure « aucun régime
    // récurrent » sur une recherche non exhaustive (R2 ter). Le tour complet
    // trouve TROIS autres CCT du même secteur : la CCT du 08/09/2011
    // (108123/CO/143, art. 7, « toujours d'application ») qui INSTAURE le
    // régime récurrent, et sa PROLONGATION par la CCT du 04/07/2013
    // (116312/CO/143, 143/143-2013-008079.pdf), entrée en vigueur le
    // 01/01/2013, DURÉE INDÉTERMINÉE, « toujours d'application » au
    // 22/09/2026. Texte primaire lu (annexe complète) :
    //   · Montant MAXIMUM 250 €/an, versé au plus tard le 15 juillet.
    //   · Période de référence : 1er juillet (année précédente) → 30 juin
    //     (année en cours).
    //   · Temps plein ayant presté toute la période : 250 €. Sinon 20,83 €
    //     par mois complet de prestations effectives ou assimilées (pro rata
    //     temps partiel), arrondi au multiple de 5 supérieur.
    // ⚠️ CHAMP RESTREINT, comme classifications/fondsSE ci-dessus : ne vise
    // que les entreprises sous le code ONSS 086 (secteur des entrepôts) de la
    // pêche maritime — pas toute la CP 143. Même périmètre que partout
    // ailleurs dans cette fiche, donc pas une restriction nouvelle.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 143 (Pêche maritime, secteur des entrepôts — indice ONSS 086) — CCT du 04/07/2013 n° 116312/CO/143 (143/143-2013-008079.pdf), prolongeant l'art. 7 de la CCT du 08/09/2011 (108123/CO/143). Entrée en vigueur le 01/01/2013, durée indéterminée, « toujours d'application » au 22/09/2026. Annexe, texte primaire lu : maximum 250 €/an aux temps plein ayant presté toute la période de référence (1er juillet → 30 juin), sinon 20,83 €/mois complet presté ou assimilé (pro rata temps partiel, arrondi au multiple de 5 supérieur), paiement au plus tard le 15 juillet. Recherche exhaustive (4 orthographes) le 22/09/2026, workflow cct-search.yml run 35713503061 — remplace la recherche bornée du 18/09/2026 qui n'avait trouvé que le versement unique de 2010 (CCT « programmation sociale 2009-2010 », expirée) sans voir ce régime récurrent.",
    ecoChequesDateVerif: '2026-09-22',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — aucun droit général « 5 jours »
    // retrouvé. Seul mécanisme concret lu : CCT n° 196408/CO/143 du
    // 22/10/2025, durée LIMITÉE à 1 an (16/09/2025→~16/09/2026, remplace
    // 190692/CO/143 du 26/09/2024, renouvelée annuellement), restreinte au
    // seul sous-secteur « entrepôts » (indice ONSS 086) : intervention
    // (remboursement employeur, pas jours travailleur) de 8h/ETP à 25€/h
    // versée par le Waarborg- en Sociaal Fonds voor de Zeevisserij, max
    // 40h/travailleur/an, jusqu'à épuisement du budget annuel.
    formation: {
      joursSectoriels: null,
      mecanisme: "aucun droit individuel général en jours retrouvé pour la CP 143 — seul mécanisme lu : intervention (remboursement employeur, pas jours travailleur) de 8h/ETP à 25 €/h, max 40h/travailleur/an, versée par le Waarborg- en Sociaal Fonds voor de Zeevisserij, jusqu'à épuisement du budget annuel, RESTREINTE au sous-secteur entrepôts (indice ONSS 086)",
      source: "Banque CCT du SPF Emploi, CCT 196408/CO/143 du 22/10/2025, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
      aVerifier: true,
      aVerifierRaison: "CCT source (196408/CO/143 du 22/10/2025) est à durée LIMITÉE (1 an, 16/09/2025→~16/09/2026, renouvelée annuellement depuis 190692/CO/143 de 2024) — à re-vérifier après cette échéance. Le doc « Droit à la formation » (143-2023-013560.pdf), non téléchargé dans ce lot, pourrait couvrir le reste de la CP hors entrepôts.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 143: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB agriculture + Boerenbond',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 144 — Agriculture (généré batch 29/04/2026)
  '144': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 12/06/2024 (CP 144), banque
    // CCT du SPF Emploi, 144/144-2024-010567.pdf. Mot pour mot : « Une
    // délégation syndicale peut être instituée dans les entreprises occupant
    // 35 travailleurs ou plus. »
    //
    // ⚠️ LE MODE DE COMPTAGE EST ÉCRIT, ET IL EST PARTICULIER — c'est un
    // secteur saisonnier : « Le comptage du nombre de travailleurs au sein
    // d'une entreprise se fait au moyen des déclarations ONSS des 4 trimestres
    // précédant le trimestre dans lequel la demande d'institution … a été
    // introduite. » Lire l'effectif du jour donnerait la mauvaise réponse.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif',
        seuilTravailleurs: 35,
        modeCalcul: 'déclarations ONSS des 4 trimestres précédant le trimestre de la demande',
        source: 'CCT du 12/06/2024 (CP 144) — statut de la délégation syndicale, banque CCT du SPF Emploi, 144/144-2024-010567.pdf. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 144 — Agriculture',
    nomCourt: 'Agriculture',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0219, derniereDate: '2026-01-01', source: 'CGSLB/Securex/Horval — CCT 15/12/2023, indexation indice santé au 1er janvier, vérifié 2026-07-16', note: '2e indexation conditionnelle possible si inflation >3% — pas de mois fixe attesté (ne pas confondre avec le lin, avril/octobre).' },
    // ── 4,00 % → 10,58 %, ET C'EST LA FICHE QUI SOUS-COMPTAIT (26/08/2026) ──
    // 📏 Cette fiche a été classée « ONSS ambigu » entre 0,10 % et 10,58 %, donc
    // laissée sur son nombre rond. L'ambiguïté était FAUSSE : elle venait de la
    // catégorie 039 (« personnel de maison »), dont la description cite la
    // CP 144 pour l'EXCLURE — « en sont exclus les employeurs, qui relèvent des
    // Commissions paritaires n° 144 de l'agriculture et n° 145 pour les
    // entreprises horticoles ». Le pont textuel annexe 27 → CP lisait un
    // rattachement là où le texte disait le contraire. Corrigé dans
    // `cpDepuisDescription()`, avec son test.
    //
    // Une fois la 039 écartée, l'ONSS ne publie qu'UNE catégorie pour la
    // CP 144 : la 193. Le taux est unique, il n'y a rien à trancher.
    //
    // 💰 Ici l'app ne surfacturait pas, elle SOUS-COMPTAIT : 4,00 % au lieu de
    // 10,58 %, soit 6,58 points manquants. Sur un ouvrier à 2 500 € bruts, base
    // 108 % : 108,00 € portés au coût employeur pour 285,66 € réellement dus.
    fondsSE: {
      nom: "Fonds social et de garantie pour l'agriculture (CP 144)",
      taux_patronal: 0.1058,
      taux_patronal_employe: 0.0115,
      base108Ouvrier: true,
      onssCategorie: '193',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-26',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml) croisé avec l'annexe 27, lu le 26/08/2026. Catégorie d'employeur 193, seule catégorie rattachée à la CP 144 : code 820 ouvriers 10,58 %, code 830 employés 1,15 %. La catégorie 039 a été écartée — sa description exclut expressément les employeurs de la CP 144.",
      pensionSectorielle: {
        taux: 0.0239,
        note: "Second pilier sectoriel (codes 825 ouvriers et 835 employés, 2,39 %). Ce n'est PAS un fonds de sécurité d'existence : il n'est pas additionné au taux ci-dessus, il est nommé pour qu'aucun écran ne les confonde.",
      },
      note: "Valeur précédente : 4,00 %, sans source ni date. ⚠️ Ce taux est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, source: "CCT du 27/04/2026 « Pouvoir d'achat - titres-repas » (CP 144 Agriculture), banque CCT du SPF Emploi, texte primaire lu le 23/08/2026, art. 4. ⚠️ RÈGLE À DEUX BRANCHES, et la seconde n'est PAS celle codée ici : §1 — SI aucun chèque-repas n'existait dans l'entreprise au 01/11/2025, la valeur nominale est de 3,09 € au 01/01/2026 (patronal 2,00 €, travailleur 1,09 €) ; §2 — SI des chèques-repas existaient DÉJÀ au 01/11/2025, c'est la part PATRONALE qui augmente de 2,00 €, plafonnée à 8,91 €, la part travailleur restant à 1,09 €. Un chèque par journée effectivement prestée (art. 3). ⚠️ La fiche portait 8,00 € / 6,91 € — le défaut du lot du 29/04/2026 — soit un coût patronal SURÉVALUÉ de 4,91 € par chèque dans la branche §1.", regimeSecondaire: 'entreprise ayant deja des cheques-repas au 01/11/2025 : part patronale +2,00 EUR, plafond 8,91 EUR', dateVerif: '2026-08-23', enVigueurLe: '2026-01-01', lien: '144/144-2026-009186.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 15/06/2023 « relative aux éco-chèques électroniques »
    // (144/144-2023-…pdf), toujours en vigueur (« Toujours d'application »).
    // Art. 4 §1 : « une fois par an, à charge de l'employeur, des éco-chèques
    // à chaque travailleur occupé à temps plein avec une période de référence
    // complète d'une valeur de 250 EUR. » §2 — temps partiel : 250 EUR dès
    // 4/5e, 200 EUR dès 3/5e, 150 EUR dès 1/2, 125 EUR en-deçà. Paiement en
    // décembre avec la prime de fin d'année (art. 6). Valeur nominale max.
    // 10 EUR/chèque.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 15/06/2023 « relative aux éco-chèques électroniques » (144/144-2023-…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 4 : 250 EUR/an temps plein (période de référence complète), paliers temps partiel 250/200/150/125 EUR, paiement en décembre.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT du 25/11/2025 (modifiant la CCT du
    // 18/05/1995, enregistrement 196793), art. 6c : le fonds « assure le
    // financement et l'organisation de la formation professionnelle des
    // travailleurs » repris à l'art. 7b (groupes à risque) — aucun nombre
    // de jours individuel fixé.
    formation: {
      joursSectoriels: null,
      fondsFormationExiste: true,
      fonds: "Fonds de sécurité d'existence CP 144 — finance la formation professionnelle des travailleurs « groupes à risque » (art. 6c, renvoi art. 7b), pas un droit individuel en jours",
      renvoi: "aucun droit individuel en jours trouvé dans la CCT relevée — le fonds finance la formation professionnelle des groupes à risque (art. 7b), il ne garantit pas X jours au travailleur ordinaire ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 25/11/2025 (CP 144), art. 2/6, modifiant la CCT du 18/05/1995, enregistrement 196793, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloSource: "CCT du 27/04/2026 (CP 144, agriculture), banque CCT du SPF, thème 268, texte primaire lu le 24/08/2026 : « Les travailleurs qui font usage de la bicyclette pour le trajet entre le domicile et le lieu de travail ont, à partir du 1er février 2026, droit à une indemnité de 0,37 EUR par kilomètre à charge de l'employeur. » ⚠️ La fiche portait 0,27 EUR — sous-paiement de 0,10 EUR/km, soit 27 %.", veloDateVerif: '2026-08-24' },  // vélo CP 144: 0,37 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB agriculture + Boerenbond',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 145 — Entreprises horticoles (généré batch 29/04/2026)
  '145': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 12/06/2024 (CP 145), banque
    // CCT du SPF Emploi, 145/145-2024-010569.pdf. Mot pour mot : « Une
    // délégation syndicale peut être instituée dans les entreprises occupant
    // 35 travailleurs ou plus. »
    //
    // ⚠️ MÊME SEUIL QUE LA CP 144, ET CE N'EST PAS UNE RECOPIE : les deux CCT
    // ont été déposées le même jour et lues séparément, chacune dans son
    // propre PDF. Agriculture et horticulture négocient de concert.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif',
        seuilTravailleurs: 35,
        source: 'CCT du 12/06/2024 (CP 145) — statut de la délégation syndicale, banque CCT du SPF Emploi, 145/145-2024-010569.pdf. Texte primaire lu.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 145 — Entreprises horticoles',
    nomCourt: 'Entreprises horticoles',
    classifications: {
      source: "CCT du 18/12/2025 (CP 145, entreprises horticoles), « Fixation du salaire et des conditions de travail », banque CCT du SPF, chapitre III, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "Classification PROFESSIONNELLE par barème de sous-secteur (chapitre III de la CCT). Pour la floriculture (bareme 145.1) : categories 1 a 5. La categorie 1 est TEMPORAIRE par construction — elle accueille les travailleurs sans experience, et le passage a la categorie superieure est AUTOMATIQUE apres 18 mois d'exercice ; une ponderation sur le contenu permet un passage anticipe. La meme CCT porte des baremes distincts par activite (145.1 floriculture, 145.5 fructiculture, taille d'arbres fruitiers pour compte de tiers…) : la categorie seule ne suffit pas, le SOUS-BAREME doit etre determine avant.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0219, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — ACV/Horval' },
    // ── 4,00 % → 8,58 %, PLANCHER SOURCÉ D'UN TAUX QUI RESTE À TRANCHER ──
    // 📏 Comme la CP 144, cette fiche traînait une ambiguïté en partie fausse :
    // la catégorie 039 (« personnel de maison ») cite la CP 145 pour l'EXCLURE,
    // et le pont textuel la comptait comme un rattachement. Corrigé.
    //
    // Mais ici, une fois la 039 écartée, l'ambiguïté RESTE et elle est réelle :
    // l'ONSS publie CINQ catégories pour la CP 145, une par sous-activité, et
    // elles ne financent pas le même fonds. Aucune ne peut être élue : la
    // sous-activité est une donnée du dossier de l'employeur, pas une
    // convention de codage.
    //
    // ⚠️ CE QUI CHANGE LA DÉCISION : le 4,00 % n'était pas « au milieu », il
    // était SOUS TOUTES les bornes réelles (8,58 % à 12,53 %). Le laisser,
    // c'était sous-compter à coup sûr, quelle que soit la sous-activité. On
    // retient donc la borne la plus BASSE, qui est sourcée et qui ne peut
    // surfacturer personne — et l'écran continue d'afficher « à trancher ».
    fondsSE: {
      nom: 'Fonds social et de garantie pour les entreprises horticoles (CP 145)',
      taux_patronal: 0.0858,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '494',
      onssSourcee: true,
      perceptionOnssSeule: true,
      aVerifier: true,
      aVerifierRaison: "Le taux dépend de la SOUS-ACTIVITÉ horticole, que l'ONSS distingue en cinq catégories d'employeur aux taux différents (8,58 % à 12,53 % pour les ouvriers). La valeur codée est la borne la plus BASSE — floriculture, catégorie 494 — retenue comme plancher sourcé pour ne surfacturer aucun employeur. Pour trancher : lire la catégorie d'employeur ONSS du dossier (elle est dans DmfA Web) et retenir la ligne correspondante ci-dessous.",
      tauxNonUnique: {
        raison: "cinq sous-activités horticoles, cinq fonds, cinq taux — la sous-activité est une donnée du dossier",
        parCategorieOnss: {
          '494': { ouvrier: 0.0858, employe: 0.0115, activite: "floriculture, à l'exclusion des autres entreprises horticoles, des parcs et jardins, des chicons et des champignons" },
          '094': { ouvrier: 0.117,  employe: 0.0195, activite: "implantation et entretien de parcs et jardins" },
          '294': { ouvrier: 0.117,  employe: 0.001,  activite: "implantation et entretien de parcs et jardins — employeurs de caractère PUBLIC (cotisation patronale de base du secteur public)" },
          '194': { ouvrier: 0.1253, employe: 0.0115, activite: "entreprises horticoles générales, hors floriculture, parcs et jardins, chicons et champignons" },
          '594': { ouvrier: 0.1253, employe: 0.0115, activite: "employeurs pouvant occuper des travailleurs occasionnels 100 jours par an — culture des chicons, des champignons et, depuis le 01/01/2020, du fruit" },
        },
        source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml) croisé avec l'annexe 27, lu le 26/08/2026. Codes 820 (ouvriers) et 830 (employés), régime général. La catégorie 039 a été écartée : sa description exclut expressément les employeurs de la CP 145.",
        dateVerif: '2026-08-26',
      },
      pensionSectorielle: {
        taux: 0.0239,
        note: "Second pilier sectoriel (codes 825 et 835, 2,39 %) dans les catégories 094, 194, 294, 494 et 594. Ce n'est PAS un fonds de sécurité d'existence : il n'est pas additionné au taux ci-dessus.",
      },
      note: "Valeur précédente : 4,00 %, sans source ni date — et inférieure à chacune des cinq bornes réelles.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 27/04/2026 (CP 145, entreprises horticoles), banque CCT du SPF, texte primaire lu le 24/08/2026. ⚠️ RÈGLE À DEUX BRANCHES, identique à celle de la CP 144 : §1 — si aucun chèque-repas n'existait au 01/11/2025, valeur nominale 3,09 EUR au 01/01/2026 (patronal 2,00, travailleur 1,09) ; §2 — s'il en existait déjà, la part PATRONALE augmente de 2,00 EUR, plafonnée à 8,91 EUR.", regimeSecondaire: "entreprise ayant deja des cheques-repas au 01/11/2025 : part patronale +2,00 EUR, plafond 8,91 EUR", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '145/145-2026-009221.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT « Eco-chèques électroniques » (145/145-2023-010414.pdf), entrée en
    // vigueur le 01/01/2023, remplace celle du 12/09/2018 (dénoncée par les
    // CCT 145-2023-010416/010417.pdf, même date). Art. 4, même structure que
    // la CP 144 : 250 EUR/an temps plein (période de référence complète),
    // paliers temps partiel 250/200/150/125 EUR, paiement en décembre avec
    // la prime de fin d'année.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT « Eco-chèques électroniques » du 15/06/2023, en vigueur le 01/01/2023 (145/145-2023-010414.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 4 : 250 EUR/an temps plein, paliers 250/200/150/125 EUR temps partiel, paiement en décembre.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — aucun droit individuel en jours trouvé.
    // Seule mesure « formation » repérée : un effort groupes à risque de
    // 0,10% de la rémunération globale (0,05% réservé aux groupes à risque
    // AR 19/02/2013), versé au Fonds social pour l'implantation et
    // l'entretien de parcs et jardins — pas un quota de jours individuel.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours de formation trouvé — le secteur finance un effort « groupes à risque » de 0,10 % de la rémunération globale (0,05 % réservé aux groupes à risque de l'AR du 19/02/2013), versé au Fonds social pour l'implantation et l'entretien de parcs et jardins (CCT du 25/11/2025, n°196797/CO/145 hors parcs/jardins et n°196795/CO/145 parcs/jardins) ; ce n'est pas un droit à jours de formation individuel. À défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "Banque CCT du SPF Emploi, CP 145 — CCT du 25/11/2025 « Mesures en faveur des groupes à risque », n°196797/CO/145 et n°196795/CO/145, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // vélo CP 145 corrigé 0,27→0,37 €/km (vague 1, 2026-07-30) : CCT du 27/04/2026 n° 199667, effet 01/02/2026.
    // Source primaire : fiche SPF Emploi/ETCS « Détachement CP 145 » (MAJ 16/06/2026). Ancienne valeur 0,27 = sourcée 2026-07-08, périmée depuis.
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB agriculture + Boerenbond',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 146 — Entreprises forestières (généré batch 29/04/2026)
  '146': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 27/04/2026 (CP 146), banque CCT
    // du SPF Emploi, 146/146-2026-011224.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 27/04/2026 (CP 146) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 146/146-2026-011224.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 146 — Entreprises forestières',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF — historique au 31/08/2022, jcId 1eb2db3e… (CP 1460000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ Lecture d'un HISTORIQUE au 31/08/2022 : le SPF ne suit plus cette commission depuis le 01/09/2022. L'absence de grille est constatée à cette date.",
      grilleSectorielle: false,
      systeme: "une seule catégorie, « ouvriers et ouvrières », plus un pourcentage par âge pour les mineurs d'âge. Aucune distinction de fonction.",
    },
    nomCourt: 'Entreprises forestières',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.011, derniereDate: '2026-07-01', source: 'AUDIT 2026-06 — Horval', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 146',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '039',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 039 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 146 + « chèque
    // repas » dans la banque CCT du SPF Emploi : 0 résultat (« 0 résultat(s)
    // trouvé(s) » affiché par le site lui-même). Remplace le triplet recopié
    // 8/6,91/1,09.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 146 — recherche plein texte « chèque repas » : 0 résultat",
      dateVerif: '2026-09-18',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 146 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 146 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 25/09/2026, À RECONFIRMER — 1 seul résultat sur le motif :
    // engagement à augmenter de 5%/an le taux de participation à la
    // formation (pas 5 jours), CCT expirée depuis 2010, aucun renouvellement
    // trouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "seule CCT trouvée sur ce motif (1 résultat) : engagement d'augmenter de 5 %/an le taux de participation à la formation (pas 5 jours), CCT n°95853/CO/146 du 16/06/2009, expirée le 31/12/2010 sans renouvellement retrouvé dans la banque CCT. Aucun fonds sectoriel de formation identifié. À défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "Banque CCT du SPF Emploi, CP 146 — CCT n°95853/CO/146 du 16/06/2009 (dépôt 31/08/2009, expirée 31/12/2010), texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
      aVerifier: true,
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 146: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB agriculture + Boerenbond',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 148 — Fourrure et peau en poil (généré batch 29/04/2026)
  '148': {
    nom: 'CP 148 — Fourrure et peau en poil',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ ICI L'ABSENCE EST DÉCLARÉE PAR LA SOURCE ELLE-MÊME, ce qui n'est pas
    // la même chose que « nous n'avons rien trouvé ». La fiche écrit mot pour
    // mot : « Dans la CP 1480000, il n'y a pas encore de CCT avec un barème
    // salarial pour ces sous-secteurs repris. »
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      absenceDeclareeParLaSource: true,
      systeme: "aucune CCT barémique n'a encore été conclue au niveau de la CP 148. Depuis le 02/05/2014 (AR du 02/04/2014), les travailleurs des sous-commissions 148.01 (couperie de poils), 148.03 (fabrication industrielle et artisanale de fourrure) et 148.05 (tanneries de peaux) sont repris dans la CP 148, et ces trois sous-commissions sont abrogées. Le SPF attend les CCT à conclure.",
      ouChercherEnAttendant: "les données salariales des trois sous-commissions abrogées restent consultables via l'HISTORIQUE de leurs propres fiches SPF (148.01, 148.03, 148.05) — pour la période antérieure au 02/05/2014 seulement, donc à ne servir qu'en borne basse, jamais comme minimum applicable aujourd'hui.",
    },
    nomCourt: 'Fourrure et peau en poil',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', dernierTaux: 0.0143, derniereDate: '2026-04-01', source: 'AUDIT 2026-06 — CGSLB' },
    // ── CENSUS COMPLET : AUCUN FONDS RELEVÉ ───────────────────────────────
    // 📏 Mesure du 26/08/2026, avec son dénominateur. Six conventions en tout ; les deux qui touchent au financement (efforts de formation, 2013 et 2015) ne contiennent aucun taux. Aucune n'institue un fonds de sécurité d'existence.
    // 🚫 L'ONSS ne rattache non plus aucune catégorie d'employeur à cette
    // commission dans le fichier des taux DmfA 2026/3.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Sur un brut de
    // 2 500 €, il facturait 100,0 € par mois et par travailleur.
    //
    // ⚖️ Le 0 vient d'un CENSUS, pas d'un acte de dissolution : il prouve que le
    // SPF ne publie rien, pas qu'aucune cotisation n'existe. La réserve est
    // écrite ci-dessous pour qu'aucune session ne le lise comme un fait clos.
    // ── L'ACTE DE DISSOLUTION QUI MANQUAIT EST TROUVÉ (26/08/2026) ────────────
    // La fiche portait 0 « sur un CENSUS de titres, pas un acte de dissolution »
    // — et disait honnêtement que ça ne prouvait rien. L'acte existe, il était
    // simplement hors du filet : le census cherchait « fonds de sécurité
    // d'existence », le titre dit « Fonds SOCIAL ».
    // 🔁 Sixième fois le même piège : la source répondait, le motif était faux.
    fondsSE: {
      nom: 'Fourrure et peau en poil — fonds social dissous au 01/11/2009',
      taux_patronal: 0,
      fondsDissous: '2009-11-01',
      source: "CCT du 07/04/2009 (CP 148, fourrure et peau en poil) « portant dissolution du Fonds social de la fabrication industrielle et de la fabrication artisanale de fourrure » — dépôt 92524/CO/148, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Art. 3 : le fonds « institué par les décisions des 16 février 1966 et 9 juin 1966, rendues obligatoires par arrêté royal du 17 août 1966 (Moniteur belge du 15 septembre 1966) » est dissous « avec effet au 1er novembre 2009 ». Art. 2 : deux liquidateurs désignés, mandat du 07/04/2009 au 31/10/2009. Census complet des 54 conventions de la commission : aucune n'institue de fonds postérieur.",
      dateVerif: '2026-08-26',
      note: "Le 0 ne vient plus d'un census de titres mais d'un ACTE DATÉ, comme pour les CP 316 et 328.02. Il n'y a plus rien à reconfirmer.",
    },
    // 🔴 Recherché le 05/09/2026 sans conclusion positive — aucune source
    // (Synova/CGSLB introuvable pour ce numéro, PDF Partena/salairesminimums.be
    // illisibles, Securex/Group S bloqués par authentification). Cohérent
    // avec le fonds social dissous depuis 2009 ci-dessus (secteur résiduel).
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 148 + « chèque
    // repas » : CCT du 29/01/2026 (n°197754/CO/148, dépôt 02/02/2026, enreg.
    // 09/02/2026), art. 2 : « à partir du 1er janvier 2026, la quote-part
    // patronale est majorée de 2,00 EUR/chèque, la valeur nominale s'élève à
    // 10,00 EUR » — donc 10,00 € = 8,91 € patronal + 1,09 € travailleur
    // (part travailleur fixe, non citée par cette CCT mais déduite : la même
    // CCT publie ce même 10,00 € comme valeur TOTALE, et le régime fédéral
    // fixe la part travailleur à 1,09 €). ⚠️ Ne vise QUE la fabrication de
    // fourrure (ex-148.03) et les tanneries de peaux (ex-148.05), art. 1er —
    // exclut explicitement la couperie de poils (ex-148.01, sous-commission
    // abrogée), dont le régime n'a pas été retrouvé dans cette recherche.
    // Coïncide avec le plafond fédéral (10/8,91/1,09) mais c'est ICI une
    // valeur SOURCÉE et datée, pas le triplet recopié par défaut.
    chequesRepas: {
      obligatoire: true, valeur: 10.0, part_employeur: 8.91, part_travailleur: 1.09,
      source: "CCT du 29/01/2026 (n°197754/CO/148, dépôt 02/02/2026, enreg. 09/02/2026), art. 2 — banque CCT SPF Emploi",
      dateVerif: '2026-09-18',
      exclusions: "Ne vise que la fabrication de fourrure (ex-148.03) et les tanneries de peaux (ex-148.05). La couperie de poils (ex-148.01, sous-commission abrogée) est explicitement exclue (art. 1er) — son régime n'a pas été retrouvé.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 148 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 148 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CCT la plus récente (29/01/2026, en vigueur
    // 01/01/2025-31/12/2026) : droit COLLECTIF (pas individuel, pas de
    // fonds) uniquement pour entreprises <10 ETP, trajectoire relevée de
    // 2,6 à 2,8 j/an/ETP. Rien trouvé pour ≥10 travailleurs.
    formation: {
      joursSectoriels: null,
      renvoi: "droit collectif à la formation (pas individuel, pas de fonds dédié), applicable uniquement aux entreprises de moins de 10 travailleurs (ETP) : trajectoire relevée de 2,6 à 2,8 jours/an/ETP en moyenne, CCT n°197741/CO/148 du 29/01/2026, en vigueur du 01/01/2025 au 31/12/2026. Rien trouvé pour les entreprises ≥10 travailleurs. À défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "Banque CCT du SPF Emploi, CP 148 — CCT n°197741/CO/148 du 29/01/2026 « Efforts en matière de formation », texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 148: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB textile + IVOC',
      indexationFrequence: 'semestrielle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 149 — Secteurs connexes aux constructions métallique, méc. (généré batch 29/04/2026)
  '149': {
    nom: 'CP 149 — Secteurs connexes aux constructions métallique, méc.',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'CONTENEUR — CP 149 mère n\'a PAS d\'indexation propre : tout travailleur relève d\'une sous-CP → 149.01/149.03 = janvier, 149.02/149.04 = FÉVRIER. Le placeholder janvier n\'est vrai que pour 2 sous-CP sur 4. Vérifié 2026-07-16.' },
    // ── FAÎTIÈRE : LE TAUX VIT DANS LES SOUS-COMMISSIONS, TOUTES SOURCÉES ──
    // 📉 4,5 % → 0 le 26/08/2026, et c'est le cas le plus net de la série.
    // Cette commission n'a AUCUNE convention sous son propre numéro dans la
    // banque CCT du SPF — quatre sous-commissions sur quatre portent, elles, un taux
    // lu à la source, de 1,10 % à 2,35 %. Le nombre rond de la mère n'était donc ni
    // l'un ni l'autre : il valait deux à quatre fois l'un d'eux, et il ne servait
    // qu'aux dossiers codés sur la commission mère, sans sous-commission.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE, et on ne fait pas de
    // moyenne : un employeur relève d'une sous-commission précise, c'est une
    // donnée du dossier. La fiche renvoie donc, et le calcul rend 0 tant qu'on
    // ne sait pas laquelle — même règle que la CP 152, posée le 25/08.
    fondsSE: {
      nom: "Secteurs connexes aux constructions métallique, mécanique et électrique — faîtière : le taux vit dans les sous-commissions",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '149.01': "Électriciens — 1,10 % (composition du code ONSS 820 écrite et recoupée)",
        '149.02': "Carrosserie — 2,24 %",
        '149.03': "Métaux précieux — 1,40 %",
        '149.04': "Commerce du métal — 2,35 %",
      },
      source: "Banque CCT du SPF Emploi : aucune convention publiée sous le numéro 149 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Les sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS ou dans leur convention.",
      dateVerif: '2026-08-26',
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (aucune
    // convention publiée sous le numéro 149 lui-même). Chaque sous-CP
    // (149.01-04) porte sa propre réponse.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — la CP 149 mère n'a pas de CCT propre",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 149 + « eco cheque » :
    // 0 résultat. Cohérent avec chequesRepas ci-dessus : la CP 149 mère n'a
    // pas de CCT propre, les sous-CP (149.01-04) portent leur propre réponse.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 149 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — CP mère : 0 résultat confirmé (légitime),
    // cohérent avec le `fondsSE` déjà présent qui documente que la mère n'a
    // aucune CCT propre. Le contenu réel est dans les sous-CP.
    formation: {
      joursSectoriels: null,
      renvoi: "CP mère sans CCT de formation propre (0 résultat confirmé sur le motif formation/opleiding, recherche du 25/09/2026) — le contenu réel est dans les sous-CP 149.01-149.04, chacune vérifiée individuellement ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "Banque CCT du SPF Emploi, CP 149 — recherche plein texte « formation »/« opleiding » : 0 résultat (relevé le 25/09/2026), cohérent avec fondsSE ci-dessus.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 149: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB métal + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 149.02 — Carrosserie (généré batch 29/04/2026)
  '149.02': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 02/03/2026 (CP 149.02), banque CCT
    // du SPF Emploi, 14902/14902-2026-013170.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 02/03/2026 (CP 149.02) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 14902/14902-2026-013170.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 149.02 — Carrosserie',
    nomCourt: 'Carrosserie',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [2], dernierTaux: 0.0223, derniereDate: '2026-02-01', note: 'Indexation annuelle au 1er FÉVRIER (corrigé de janvier 2026-07-16). Source: CSC METEA — barèmes CP 149.02 « à partir du 20250201 » (lacsc.be) + CGSLB « 1er février ». Taux 2026 exact à confirmer (robot fetch/Telegram).' },
    fondsSE: {
      nom: 'Fonds social CP 149.02',
      taux_patronal: 0.022400000000000003,
      taux_patronal_employe: 0.0023,
      pension_sectorielle: 0.023799999999999998,
      pension_sectorielle_employe: 0.011899999999999999,
      base108Ouvrier: true,
      onssCategorie: '065',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 065 : 820 ouvriers 2,24 % · employés 0,23 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // 🔴 Repris le 18/09/2026 via la banque CCT (recherche plein texte, 5
    // résultats), NON résolu — ça confirme que des chèques-repas EXISTENT
    // dans ce secteur : la CCT du 16/06/2011 (calcul de l'indemnité
    // complémentaire de prépension, dépôt 14902-2011-006271) convertit « le
    // montant journalier des chèques-repas » en montant mensuel pour ce
    // calcul, donc un montant journalier était déjà en vigueur en 2011 —
    // mais ce montant lui-même n'est cité dans AUCUN des 5 résultats. Piste
    // suivante pour une prochaine session : chercher la CCT qui a INSTAURÉ
    // le régime, probablement antérieure à 2011 ou hors des mots-clés
    // essayés (« cheque repas »). Ne PAS ajouter de `source` tant que la
    // valeur elle-même n'est pas lue (R12).
    chequesRepas: { obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09, aVerifier: true },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 01/02/2022 (SCP 149.02, carrosserie), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : l'article prévoit, pour chaque ouvrier occupé à temps plein, deux tranches semestrielles d'éco-chèques de 125,00 EUR chacune, soit 250 EUR par an (texte original néerlandais : « 2 semestriële schijven van ecocheques […] elk ter waarde van € 125,00 »). Portée : toute la sous-commission.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — CCT complète et récente (accord national
    // métal du 02/03/2026). Cotisation formation 0,55% des salaires bruts à
    // 108%, durée indéterminée, gérée par EDUCAM. Trajectoire par taille :
    // ≥20 ouvriers 4j(2026)→4,5j(27-28)→5j(2029+) ; 10-19 ouvriers
    // 1,25j(2026)→1,5j(2027+) + 5j collectifs/2ans ; <10 ouvriers 1j/an +
    // 5j collectifs/2ans.
    formation: {
      joursSectoriels: 4,
      renvoi: "droit individuel à la formation (art. 6, ≥20 ouvriers) : 4 j/an en 2026, 4,5 j en 2027-2028, 5 j dès 2029 ; entreprises 10-19 ouvriers (art. 6 §2) : 1,25 j/an en 2026, 1,5 j dès 2027, + droit collectif de 5 j/ouvrier sur 2 ans ; entreprises <10 ouvriers (art. 7) : 1 j/an individuel + 5 j/ouvrier collectif sur 2 ans. Financé par une cotisation formation de 0,55 % des salaires bruts à 108 % (art. 5, durée indéterminée), gérée par EDUCAM.",
      source: "CCT du 23/06/2026 « Formation » (SCP 149.02 Carrosserie), dépôt 201199/CO/149.02, enregistrement 24/08/2026, durée indéterminée, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // ⚠️ velo 0,27→0,32 EUR/km des le 01/07/2026 (max 40 km/j) — CCT du 17/04/2026 art. 10 §1, lu le 24/08/2026. PLANCHER §2 : jamais moins que l'indemnite journaliere fondee sur l'abonnement hebdo (CCT 19/9)
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 149.02: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB métal + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 149.03 — Métaux précieux (généré batch 29/04/2026)
  '149.03': {
    // ── Classification CP 149.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 27/06/2017, n° 140522/CO/149.03, banque
    // CCT du SPF Emploi (14903/14903-2017-009346.pdf), art. 3.
    // Six catégories, réparties selon la NATURE des travaux, les APTITUDES
    // professionnelles et le DEGRÉ D'AUTONOMIE — les trois critères sont
    // cumulés par la CCT elle-même, ce n'est pas une simple échelle.
    classifications: {
      source: 'CCT du 27/06/2017, n° 140522/CO/149.03, art. 3 — banque CCT du SPF Emploi, 14903/14903-2017-009346.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      criteres: "nature des travaux exécutés · aptitudes professionnelles · degré d'autonomie dans l'exécution des tâches confiées",
      categories: [
        { code: 'A', label: "Travail simple", description: "l'ouvrier qui exécute un travail simple, ne requérant ni connaissance spéciale ni adresse particulière" },
        { code: 'B', label: 'Activités simples et répétitives sur machine', description: "l'ouvrier qui exerce des activités simples et répétitives avec une machine ou un appareil dans la fabrication" },
        { code: 'C', label: 'Grande diversité de travaux', description: "l'ouvrier qui exécute une grande diversité de travaux faisant partie de la fabrication" },
        { code: 'D', label: 'Métier — formation théorique et expérience', description: "l'ouvrier qui exécute des tâches requérant une formation théorique et demandant de l'expérience pour acquérir les connaissances, la précision et la vitesse nécessaires à l'exercice du métier" },
        { code: 'E', label: 'Métier complet', description: "l'ouvrier qui exécute des tâches requérant une formation théorique et demandant de l'expérience pour acquérir les connaissances, la précision, l'habileté et la vitesse nécessaires à l'exercice du métier COMPLET" },
        { code: 'F', label: 'Toutes les spécialités du métier', description: "l'ouvrier qui exécute des tâches spécialisées requérant une formation théorique et demandant de l'expérience pour acquérir les connaissances, la précision, l'habileté et la vitesse nécessaires à l'exercice de TOUTES les spécialités du métier" },
      ],
      // La CCT détaille en outre, pour chaque catégorie, les qualités
      // personnelles, les activités (mentales et physiques) et les
      // responsabilités. Non reprises ici : elles servent à trancher un cas
      // limite, pas à identifier l'échelon, et les recopier partiellement
      // donnerait une grille d'apparence complète qui ne l'est pas.
      detailsNonRepris: 'qualités personnelles, activités mentales et physiques, responsabilités — voir la CCT',
    },
    nom: 'CP 149.03 — Métaux précieux',
    nomCourt: 'Métaux précieux',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0223, derniereDate: '2026-01-01', note: 'Indexation annuelle au 1er janvier (CGSLB « à partir du 1er janvier 2026 », +2,23%), vérifié 2026-07-16. ⚠️ Ne PAS confondre avec 149.02 carrosserie (=1er février) ; re-confirmer jan-2027.', formule: 'sante_lisse_dec', formuleAConfirmer: true },
    fondsSE: {
      nom: 'Fonds social CP 149.03',
      taux_patronal: 0.014,
      // Le côté employé n'est PAS ambigu par accident : cette CP ne couvre pas
      // les employés. Ils relèvent d'une autre commission, que l'ONSS nomme dans
      // la description de chaque catégorie d'employeur. Leur cotisation se calcule
      // sur la fiche de LEUR CP — servir le taux ouvrier ici le multiplierait par dix.
      employesRelevantDUneAutreCp: ['200', '201'],
      base108Ouvrier: true,
      onssCategorie: '078, 478',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégories d'employeur 078 et 478 : 1,40 % pour les ouvriers (code 820) dans les deux, sans écart. Lu le 25/08/2026.",
      note: "Valeur précédente : 4,50 %, sans source. ⚠️ Commission d'OUVRIERS (métaux précieux) : les employés relèvent de la CP 201 ou de la CP 200 selon l'employeur — descriptions ONSS des catégories 078 et 478.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (8,00 €) était la famille
    // recopiée {8/6.91/1.09}, sans source. Group S, fiche sectorielle CP 149.03 :
    // « Au 1er octobre 2023, la part patronale dans le chèque-repas a été
    // augmentée de 1 EUR, portant la valeur du chèque-repas à 7,00 EUR ».
    chequesRepas: {
      obligatoire: true, valeur: 7.00, part_employeur: 5.91, part_travailleur: 1.09,
      source: 'Group S, fiche sectorielle CP 149.03, chèques-repas au 1er octobre 2023',
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 149.03 (métaux précieux) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 1 document remonté (14903/14903-2023-011029.pdf), mais c'est une CCT « prime pouvoir d'achat » du 28/06/2023, expirée le 31/12/2024, qui ne fait que RÉFÉRENCER la liste de produits éligibles de la CCT n° 98 sur les éco-chèques — aucun régime d'éco-chèques propre au secteur. Repli générique du moteur sur le mot « ecocheques », pas une trouvaille. Relevé le 21/09/2026.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 149.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // ⚠️ velo 0,27→0,32 EUR/km des le 01/04/2026 (max 40 km A-R) — CCT du 17/03/2026, accord national metaux precieux, art. 7 §1, lu le 24/08/2026
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 149.03: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB métal + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 149.04 — Commerce du métal (généré batch 29/04/2026)
  '149.04': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 17/04/2026 (CP 149.04), banque CCT
    // du SPF Emploi, 14904/14904-2026-006358.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 17/04/2026 (CP 149.04) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 14904/14904-2026-006358.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 149.04 — Commerce du métal',
    nomCourt: 'Commerce du métal',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [2], dernierTaux: 0.0223, derniereDate: '2026-02-01', note: 'Indexation annuelle au 1er FÉVRIER (corrigé de janvier 2026-07-16). Source: CSC METEA — commerce du métal (lacsc.be, publication sectorielle) + CGSLB CP 149.04. Taux 2026 exact à confirmer (robot fetch/Telegram).' },
    fondsSE: {
      nom: 'Fonds social CP 149.04',
      taux_patronal: 0.0235,
      // Le côté employé n'est PAS ambigu par accident : cette CP ne couvre pas
      // les employés. Ils relèvent d'une autre commission, que l'ONSS nomme dans
      // la description de chaque catégorie d'employeur. Leur cotisation se calcule
      // sur la fiche de LEUR CP — servir le taux ouvrier ici le multiplierait par dix.
      employesRelevantDUneAutreCp: ['200', '201'],
      base108Ouvrier: true,
      onssCategorie: '077, 477',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégories d'employeur 077 et 477 : 2,35 % pour les ouvriers (code 820) dans les deux, sans écart. Lu le 25/08/2026.",
      note: "Valeur précédente : 4,50 %, sans source. ⚠️ Commission d'OUVRIERS (commerce du métal) : les employés relèvent de la CP 201 ou de la CP 200 selon l'employeur — descriptions ONSS des catégories 077 et 477.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 149.04 + « chèque
    // repas » : 4 résultats (2023, 2021, 2014), tous les 4 lus en entier.
    // AUCUN ne mentionne de chèques-repas — cette sous-commission connaît
    // des « chèques-carrière » (formation), des « chèques consommation »
    // (pouvoir d'achat ponctuel) et des éco-chèques (250 €/an, cf.
    // ecoChequesSource ci-dessous), mais rien sur les chèques-repas.
    // Remplace le triplet recopié 8/6,91/1,09.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 149.04 — recherche plein texte (4 CCT, 2014-2023, toutes lues), aucune mention de chèque-repas (seulement chèques-carrière, chèques consommation et éco-chèques)",
      dateVerif: '2026-09-18',
    },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 11/09/2017 (SCP 149.04, commerce du métal), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « Par période de référence de 12 mois du 1er juin de l'année précédente au 31 mai de l'année en cours, des écochèques d'une valeur de € 250,00 sont versés à tout ouvrier occupé à temps plein. » Versement en UNE tranche, au plus tard le 15 juin, depuis le 01/01/2018. ⚠️ La période de référence n'est PAS l'année civile : juin → mai.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 25/09/2026 — même accord national que 149.02, CCT sœur
    // intégralement lue.
    formation: {
      joursSectoriels: 4,
      renvoi: "droit individuel à la formation (art. 6, ≥20 ouvriers) : 4 j/an en 2026, 4,5 j en 2027-2028, 5 j dès 2029 ; entreprises 10-19 ouvriers (art. 6 §2) : 1,25 j/an en 2026, 1,5 j dès 2027, + droit collectif de 5 j/ouvrier sur 2 ans ; entreprises <10 ouvriers (art. 7) : 1 j/an individuel + 5 j/ouvrier collectif sur 2 ans. Financé par une cotisation formation de 0,55 % des salaires bruts à 108 % (art. 5, durée indéterminée), gérée par EDUCAM.",
      source: "CCT du 23/06/2026 « Formation » (SCP 149.04 Commerce du métal), dépôt 200887/CO/149.04, durée indéterminée, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // ⚠️ velo 0,27→0,32 EUR/km des le 01/07/2026 (max 40 km/j) — CCT du 17/04/2026 art. 10 §1, lu le 24/08/2026. Meme plancher CCT 19/9 qu'en 149.02
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 149.04: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB métal + Agoria',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 152.01 — Enseignement libre — Communauté flamande (généré batch 29/04/2026)
  '152.01': {
    nom: 'CP 152.01 — Enseignement libre — Communauté flamande',
    type: 'O',
    // Corrigé 36→38 (vague 1, 2026-07-30). Sources (3, concordantes) : SPF fiche SCP 1520100,
    // ACV/CSC (152.01 contractuele arbeiders vrij onderwijs), Securex. L'ancienne valeur 36 venait du batch générique.
    // ⚠️ heuresSup.seuilHebdomadaire reste à 36 : NON corrigé volontairement (impact paie direct, non sourcé explicitement) — à trancher, cf. rapport vague 1.
    regimeHebdo: 38,
    // ⚠️ CORRIGÉ le 05/09/2026 — `mois: [3]` et `derniereDate: '2026-03-01'` étaient
    // faux. Un secteur à index-pivot (spilindex) n'a PAS de mois calendaire fixe :
    // l'historique de cette CP indexe de façon irrégulière (02/2022, plusieurs fois
    // en 2022, 03/2025, puis 02/2026) — jamais deux années de suite au même mois.
    // ✅ Fiche SPF primaire (salairesminimums.be, jcId bfc13fa44ff74754a41373bf29b8176b,
    // CP 152.02, sœur immédiate de 152.01, même mécanisme) lue le 05/09/2026 : la
    // dernière indexation +2 % a pris effet le 01/02/2026, pas le 01/03/2026.
    // Corroboré par le registre vault (CP sœurs pivot_public de la même vague fin
    // 2025 — 318.01, 319.01, 327.01/.02/.03, 329.02/.03 — toutes datées 02/2026).
    // `mois` est retiré : `app/lib/indexation-calendar.js` (calendrierDe) ignore de
    // toute façon ce champ dès que `frequence` contient « pivot » (mois:null forcé,
    // statut 'pivot') — le laisser à [3] n'a jamais influencé l'anticipation, mais
    // induisait en erreur quiconque lit la donnée brute.
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-02-01', note: "INDEX-PIVOT barèmes fonction publique / enseignement libre (spilindex, +2% au 2e mois après dépassement pivot) — pas de mois calendaire fixe. Dernière indexation le 01/02/2026, corrigée le 05/09/2026 (source primaire SPF, remplace le 01/03/2026 non sourcé). Mécanisme confirmé 2026-07-16 : Acerta + CSC enseignement + SeGEC." },
    fondsSE: {
      nom: 'Fonds social CP 152.01',
      taux_patronal: 0.0126,
      taux_patronal_employe: 0.0015,
      pension_sectorielle: 0.011399999999999999,
      pension_sectorielle_employe: 0.011399999999999999,
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "825", "type": "8", "taux": 0, "forfait": false}],
      base108Ouvrier: true,
      onssCategorie: '074',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 074 : 820 ouvriers 1,26 % · employés 0,15 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 3,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    // ✅ SOURCÉ LE 25/09/2026 (cct-search.yml, retry après collision d'écriture
    // au 1er passage) : ZÉRO résultat dans la banque CCT du SPF pour la 152.01.
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 152.01 (Enseignement libre — Communauté flamande) — recherche plein texte (cct-search.yml, 25/09/2026, eco cheque/ecocheque/ecocheques/éco-chèques) : 0 résultat sur les 4 orthographes. Aucune CCT éco-chèques déposée sous ce numéro.",
    ecoChequesDateVerif: '2026-09-25',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — pas de fonds. Trajectoire de croissance
    // individuelle : 2,5j(24)→3j(25)→3,5j(2026, valeur actuelle)→4j(27)
    // →4,5j(28)→5j(2029+). « 5 jours » n'est atteint qu'en 2029.
    formation: {
      joursSectoriels: 3.5,
      renvoi: "droit individuel à la formation, trajectoire de croissance (art. 3 §1) : 2,5 j/an (2024), 3 j (2025), 3,5 j (2026, valeur actuelle), 4 j (2027), 4,5 j (2028), 5 j dès 2029. Pas de fonds sectoriel dédié : obligation directe de l'employeur (art. 53-54 §2 de la loi du 03/10/2022).",
      source: "CCT du 27/09/2023 « Droit individuel à la formation » (SCP 152.01), dépôt 182867/CO/152.01 (erratum publié), banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    // 🔴 `seuilHebdomadaire: 36` INTROUVABLE dans aucune source — recherché le
    // 05/09/2026 : CCT du 25/02/2022 (173651/CO/152.01, sf-vov.be) confirme
    // `regimeHebdo: 38` (« durée hebdomadaire de travail de 38 heures », art.3
    // §1) mais ne mentionne aucun seuil distinct de déclenchement des heures
    // sup. Limosa n'a pas de fiche pour la CP 152. groups.be — sondé le
    // 05/09/2026 (3 variantes, verdict lu-mais-non-compris) : la page
    // d'accueil répond bien (200, 105233 o), mais ne mentionne pas la CP 152 —
    // ce n'est PAS une preuve d'inaccessibilité, juste une page qui n'est pas
    // la bonne ; les pages sectorielles profondes n'ont pas répondu au second
    // essai (verdict site-muet, probablement transitoire, à ressonder). Probable
    // confusion avec une autre CP (ex. 225.01, employés). NE PAS CORRIGER SANS
    // SOURCE — pour l'instant le seuil retombe sur `regimeHebdo` si un moteur
    // l'utilise en repli.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.21 },  // vélo CP 152.01: 0,21 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC enseignement + SeGEC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 152.02 — Enseignement libre — Communauté française et germ. (généré batch 29/04/2026)
  '152.02': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 29/05/2026 (CP 152.02), banque CCT
    // du SPF Emploi, 15202/15202-2026-012154.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 29/05/2026 (CP 152.02) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 15202/15202-2026-012154.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 152.02 — Enseignement libre — Communauté française et germ.',
    type: 'O',
    // Corrigé 36→37 (vague 1, 2026-07-30). Source : SPF minimumlonen.be, SCP 1520200,
    // jcId bfc13fa44ff74754a41373bf29b8176b (barème d'entrée 13,2453 €/h à 37h). Ancienne valeur 36 = batch générique.
    // ⚠️ heuresSup.seuilHebdomadaire reste à 36 : NON corrigé volontairement (impact paie direct, non sourcé explicitement) — à trancher, cf. rapport vague 1.
    regimeHebdo: 37,
    // ⚠️ CORRIGÉ le 05/09/2026 — même erreur que sur la CP sœur 152.01 : `mois: [3]`
    // et `derniereDate: '2026-03-01'` étaient faux pour un mécanisme index-pivot.
    // ✅ Fiche SPF primaire (salairesminimums.be, jcId bfc13fa44ff74754a41373bf29b8176b,
    // cette CP même) lue le 05/09/2026 : dernière indexation +2 % le 01/02/2026, pas
    // le 01/03/2026. Historique irrégulier (02/2022, plusieurs fois en 2022, 03/2025,
    // 02/2026) — corroboré par les CP sœurs pivot_public de la même vague fin 2025
    // (318.01, 319.01, 327.01/.02/.03, 329.02/.03), toutes datées 02/2026.
    // `mois` retiré : `indexation-calendar.js` (calendrierDe) l'ignore de toute façon
    // dès que `frequence` contient « pivot » (mois:null forcé) — ce champ n'a jamais
    // influencé l'anticipation, il induisait seulement en erreur à la lecture.
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-02-01', note: "INDEX-PIVOT barèmes fonction publique / enseignement libre (spilindex, +2% au 2e mois après dépassement pivot) — pas de mois calendaire fixe. Dernière indexation le 01/02/2026, corrigée le 05/09/2026 (source primaire SPF, remplace le 01/03/2026 non sourcé). Mécanisme confirmé 2026-07-16 : Acerta + CSC enseignement + SeGEC." },
    fondsSE: {
      nom: 'Fonds social CP 152.02',
      taux_patronal: 0.006,
      // Le côté employé n'est PAS ambigu par accident : cette CP ne couvre pas
      // les employés. Ils relèvent d'une autre commission, que l'ONSS nomme dans
      // la description de chaque catégorie d'employeur. Leur cotisation se calcule
      // sur la fiche de LEUR CP — servir le taux ouvrier ici le multiplierait par dix.
      employesRelevantDUneAutreCp: ['225'],
      base108Ouvrier: true,
      onssCategorie: '174',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégorie d'employeur 174 : 0,60 % pour les ouvriers (code 820). La catégorie ne porte aucune ligne de cotisation pour les employés. Lu le 25/08/2026.",
      note: "Valeur précédente : 4,50 %, sans source. ⚠️ Le personnel employé des institutions subsidiées de l'enseignement libre relève de la CP 225 ; la catégorie 174 ne porte pas de cotisation employé.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    // ✅ SOURCÉ LE 25/09/2026 (cct-search.yml, 4 orthographes) : ZÉRO résultat
    // dans la banque CCT du SPF pour la CP 152.02.
    ecoChequesAnnuel: 0,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 152.02 (Enseignement libre — Communauté française et germanophone) — recherche plein texte (cct-search.yml, 25/09/2026, eco cheque/ecocheque/ecocheques/éco-chèques) : 0 résultat sur les 4 orthographes. Aucune CCT éco-chèques déposée sous ce numéro.",
    ecoChequesDateVerif: '2026-09-25',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — fonds réel confirmé (statuts cités, art.
    // 3-4) mais aucun quota de jours : heures de formation rémunérées comme
    // temps de travail (sans sursalaire), remboursées par le Fonds —
    // mécanisme horaire, pas un nombre de jours fixé.
    formation: {
      joursSectoriels: null,
      renvoi: "pas de quota de jours fixé : les heures de formation suivies par le travailleur sont du temps de travail rémunéré par l'employeur (sans sursalaire), remboursables par le Fonds Social et de Garantie pour les institutions subsidiées de l'enseignement libre des Communautés française et germanophone, pour les formations reconnues par ce Fonds dans sa politique « groupes à risque » (art. 2-3). À défaut de quota sectoriel, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "CCT du 27/11/2015 « Formation » (SCP 152.02), dépôt 131321/CO/152.02, durée indéterminée depuis le 01/01/2016, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    // 🔴 `seuilHebdomadaire: 36` INTROUVABLE dans aucune source — même défaut
    // que la CP sœur 152.01, recherché le 05/09/2026 : le document lacsc.be
    // (CP 152.02, pied de page daté 25/08/2026) confirme `regimeHebdo: 37`
    // (« limite hebdomadaire : 37 heures/semaine », CCT du 05/05/2003) mais ne
    // porte aucune section heures supplémentaires ni seuil distinct. NE PAS
    // CORRIGER SANS SOURCE — pour l'instant le seuil retombe sur `regimeHebdo`
    // si un moteur l'utilise en repli.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30, veloFutur: 0.32, veloDateChangement: '2026-10-01', veloMaxJourFutur: 12.80 },  // vélo CP 152.02: 0,30 €/km plancher n°164 (À CONFIRMER) → 0,32 €/km (max 12,80 €/j) dès 01/10/2026 (CSC Enseignement) — sourcé 2026-07-08
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC enseignement + SeGEC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 202.01 — Moyennes entreprises d\ (généré batch 29/04/2026)
  '202.01': {
    nom: "CP 202.01 — Moyennes entreprises d'alimentation",
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'INDEX-PIVOT sectoriel moyennes surfaces alim. (corrigé de « annuelle janvier » 2026-07-16) : +2% quand indice santé lissé > pivot (constaté +2% au 01/05 et +1% au 01/07/2026 — pas janvier). Source: CSC-CNE + SD Worx + CGSLB moyennes entreprises alim.' },
    fondsSE: {
      nom: 'Fonds social CP 202.01',
      taux_patronal: 0.0059,
      taux_patronal_employe: 0.006,
      base108Ouvrier: true,
      onssCategorie: '157',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 157 : 820 ouvriers 0,59 % · employés 0,60 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 202.01 + « chèque
    // repas » : 5 résultats (2009-2017), tous lus. Les chèques-repas y sont
    // une OPTION parmi d'autres pour transposer une « prime annuelle » en
    // pouvoir d'achat (CCT du 04/12/2009 et suivantes) : l'employeur peut
    // choisir entre la prime en espèces, l'éco-chèque, ou — seulement
    // certaines années — l'introduction/majoration de chèques-repas
    // existants. Aucun montant sectoriel FIXE de chèques-repas n'en ressort
    // — remplace le triplet recopié 8/6,91/1,09.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 202.01 — CCT du 04/12/2009 et suivantes (transposition de la prime annuelle) : les chèques-repas sont une option de substitution au choix de l'employeur, pas une obligation sectorielle fixe",
      dateVerif: '2026-09-18',
    },
    ecoChequesConditionnel: ECO_CHEQUES_CONDITIONNELS_20201,
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — pas de fonds. Trajectoire par taille :
    // <10 exclus ; 10-19 = 1j/an fixe ; ≥20 = 3j(24)→4j(2026, valeur
    // actuelle)→5j(2028+).
    formation: {
      joursSectoriels: 4,
      renvoi: "droit individuel à la formation, trajectoire de croissance par taille d'entreprise (art. 3) : <10 travailleurs exclus ; 10-19 travailleurs = 1 j/an fixe ; ≥20 travailleurs = 3 j (2024), 4 j (2026, valeur actuelle), 5 j dès 2028. Pas de fonds sectoriel dédié : obligation directe de l'employeur (art. 50 §3 de la loi du 03/10/2022 pour le prorata temps partiel).",
      source: "CCT du 28/09/2023 « Formation professionnelle » (SCP 202.01), dépôt 183074/CO/202.01, durée indéterminée depuis le 01/01/2024, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.29, veloSource: "CCT du 26/05/2026 (SCP 202.01, commerce de détail alimentaire — moyennes entreprises), banque CCT du SPF, thème 268, texte primaire lu le 24/08/2026 : « À partir du 1er juin 2026, l'indemnité vélo accordée par l'employeur est de 0,29 EUR par kilomètre pour les employés se rendant sur leur lieu de travail en vélo ou à vélo électrique, avec une distance maximale de 40 km (aller-retour). » ⚠️ PLAFOND DE DISTANCE : 40 km A/R, au-delà rien n'est dû. La fiche portait 0,27 EUR.", veloDateVerif: '2026-08-24' },  // vélo CP 202.01: 0,29 €/km — CCT sectorielle 2026 relue à la source le 24/08/2026 (voir veloSource). Remplace le 0,27 de la campagne du 08/07/2026, qui lisait la fiche Limosa (état 2024).
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 203 — Employés des carrières de petit granit (généré batch 29/04/2026)
  '203': {
    // ── Classification CP 203 — ajoutée le 23/08/2026 ───────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 28/10/2014, n° 124766/CO/203, banque CCT
    // du SPF Emploi (203/203-2014-011779.pdf), art. 2 (sections A et B).
    // En vigueur au 01/01/2015, durée indéterminée ; remplace le chapitre II
    // de la CCT du 22/04/2014.
    //
    // ⚠️ DEUX SECTIONS, PAS UNE — et elles n'ont pas le même nombre
    // d'échelons. Le personnel technique et de maîtrise a une catégorie 3
    // DÉDOUBLÉE en 3A et 3B ; aplatir les deux sections en une seule échelle
    // de 1 à 5 ferait disparaître un échelon et en décalerait deux.
    // (Même discipline que « lire par SECTION, jamais par montant ».)
    classifications: {
      source: 'CCT du 28/10/2014, n° 124766/CO/203, art. 2 — banque CCT du SPF Emploi, 203/203-2014-011779.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      enVigueurDepuis: '2015-01-01',
      critereres: "chaque catégorie est définie par TROIS critères cumulés : connaissances et formation requises · expérience informatique et compétence technique · diplôme OU expérience équivalente",
      administratif: [
        { code: 'A1', label: 'Catégorie 1', exigences: "connaissances et formation suffisantes pour une fonction simple, sans exercice de responsabilités, dans le cadre de procédures bien définies", diplome: "pas d'exigence de diplôme" },
        { code: 'A2', label: 'Catégorie 2', exigences: "connaissances et formation suffisantes pour travaux simples comportant une certaine responsabilité", diplome: "enseignement professionnel, général ou technique secondaire inférieur, ou expérience équivalente" },
        { code: 'A3', label: 'Catégorie 3', exigences: "connaissances suffisantes pour un travail diversifié, avec compétences spécifiques ; initiative et responsabilité", diplome: "formation équivalente à l'enseignement secondaire supérieur général ou technique, ou expérience équivalente" },
        { code: 'A4', label: 'Catégorie 4', exigences: "expérience pratique et compétences spécifiques pour un travail complexe ou sensible, dans une fonction nécessitant autonomie de décision et travail de confiance", diplome: "formation équivalente à l'enseignement supérieur général ou technique, ou expérience équivalente" },
        { code: 'A5', label: 'Catégorie 5', exigences: "expérience pratique plus développée que la 4e catégorie, avec une plus grande expérience", diplome: "formation équivalente à l'enseignement supérieur de type long ou universitaire, ou expérience équivalente" },
      ],
      techniqueEtMaitrise: [
        { code: 'B1', label: 'Catégorie 1', exigences: "un minimum de formation intellectuelle et professionnelle, technique ou pratique, pour une fonction simple sans responsabilité" },
        { code: 'B2', label: 'Catégorie 2' },
        { code: 'B3A', label: 'Catégorie 3A' },
        { code: 'B3B', label: 'Catégorie 3B' },
        { code: 'B4', label: 'Catégorie 4' },
        { code: 'B5', label: 'Catégorie 5' },
      ],
      // ⚠️ Les libellés détaillés de la section B ne sont pas tous repris :
      // le tableau du PDF entrelace NL et FR colonne par colonne et plusieurs
      // cellules sortent tronquées de l'extraction. Les ÉCHELONS, eux, sont
      // certains (six, dont 3A et 3B). Écrire des libellés reconstitués
      // serait inventer — ils se relèveront sur le PDF ouvert à la main.
      libellesSectionBIncomplets: true,
    },
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 26/06/2026 (CP 203), banque CCT
    // du SPF Emploi, 203/203-2026-013829.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 26/06/2026 (CP 203) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 203/203-2026-013829.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 203 — Employés des carrières de petit granit',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.0201, derniereDate: '2026-05-01', note: '+1% x2 au 01/05/2026 (=+2,01% cumule), saut indice-pivot. Source: AUDIT 2026-06 Easypay/Partena CP 203' },
    // ── 0,50 % DE LA MASSE SALARIALE ONSS, PERÇUS PAR DEUX ORGANISMES ─────
    // ✅ CCT du 26/06/2026 « Emploi et formation de personnes appartenant aux
    // groupes à risque », art. 3 — dépôt 201170/CO/203, banque CCT du SPF
    // Emploi, 203/203-2026-011005.pdf, texte primaire lu le 26/08/2026 : « les
    // entreprises du secteur consacreront 0,50 p.c. de la masse salariale
    // déclarée à l'Office National de Sécurité Sociale à des initiatives de
    // formation et d'emploi ».
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — neuf fois trop.
    //
    // 🗺️ ET LE DESTINATAIRE DÉPEND DE LA PROVINCE, ce que l'art. 3 détaille :
    // en Hainaut, l'ASBL « CEFOMEPI - Centre de formation aux métiers de la
    // pierre » (Soignies) « perçoit les fonds, les gère et affecte la
    // contribution » ; à Liège, Namur et Luxembourg, c'est le Fonds de sécurité
    // d'existence des sous-commissions du petit granit et des carrières de grès
    // et quartzite. Le TAUX, lui, est le même partout — c'est le seul point qui
    // compte pour le coût employeur.
    fondsSE: {
      nom: "Formation et emploi des employés des carrières (CEFOMEPI en Hainaut, FSE à Liège-Namur-Luxembourg)",
      taux_patronal: 0.005,
      base: "masse salariale déclarée à l'ONSS",
      cctSourcee: {
        taux: 0.005,
        base: "masse salariale déclarée à l'Office National de Sécurité Sociale (art. 3)",
        source: "CCT du 26/06/2026 (CP 203, employés des carrières de petit granit et de calcaire à tailler / grès et quartzite), art. 3 — dépôt 201170/CO/203, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Art. 4 : au moins 0,05 % de la masse salariale doit être réservé aux groupes cibles énumérés.",
        dateVerif: '2026-08-26',
        destinataireParProvince: {
          hainaut: "ASBL « CEFOMEPI - Centre de formation aux métiers de la pierre », rue Mademoiselle Hanicq 30, 7060 Soignies",
          liegeNamurLuxembourg: "Fonds de sécurité d'existence des sous-commissions paritaires du petit granit et des carrières de grès et quartzite, boulevard de la Sauvenière 73, 4000 Liège",
        },
      },
      // ⏰ CCT à durée déterminée : effets au 01/01/2025, cesse le 31/12/2026.
      expireLe: '2026-12-31',
      expireQuoi: "la totalité du taux : la CCT est à durée déterminée",
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    chequesRepas: { obligatoire: true, valeur: 10.00, part_employeur: 8.91, part_travailleur: 1.09, source: "CCT du 28/04/2026 (n° 199560/CO/203), banque CCT du SPF Emploi, texte primaire lu le 23/08/2026 : « À partir du 1er janvier 2026, la valeur faciale du chèque-repas est de 10,00 EUR pour tous les travailleurs. La participation du travailleur est de 1,09 EUR par chèque-repas. » ⚠️ La fiche portait 8,00 € / 6,91 € — le triplet par défaut du lot du 29/04/2026 — donc un coût patronal SOUS-ESTIMÉ de 2,00 € par chèque.", dateVerif: '2026-08-23', enVigueurLe: '2026-01-01', lien: '203/203-2026-011267.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 203 + « eco cheque » :
    // 1 résultat, faux positif (CCT « Conditions de rémunération ou de
    // travail » du 12/02/2020, expirée le 31/12/2021 — « eco »/« cheque »
    // matchés séparément via « Economie »/« économique » et « chèques-repas »,
    // zéro occurrence de « ecoch »/« écoch »). Aucune CCT éco-chèques trouvée.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT SPF Emploi, CP 203 — recherche plein texte « eco cheque » : 1 résultat, faux positif (économie/économique + chèques-repas, CCT du 12/02/2020 expirée), aucune CCT éco-chèques trouvée",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — pas de fonds, pas de quota chiffré : la CCT
    // générale se borne à un engagement d'implémenter les futures
    // obligations légales.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours ni fonds sectoriel trouvé — l'art. 23 de la CCT générale se limite à un engagement des partenaires sociaux à évaluer les dispositifs existants et à mettre en œuvre « les nouvelles obligations légales en matière de formation » (renvoi explicite à la loi, sans jours propres). À défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral).",
      source: "CCT du 26/06/2026 « Fixation des règles générales applicables aux conditions de rémunération ou de travail » (CP 203), art. 23, dépôt 201193/CO/203, banque CCT du SPF Emploi, texte primaire lu le 25/09/2026.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 203: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 205 — Employés des charbonnages (généré batch 29/04/2026)
  '205': {
    nom: 'CP 205 — Employés des charbonnages',
    nomCourt: 'Employés des charbonnages',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Partena' },
    // ── CENSUS COMPLET : AUCUN FONDS RELEVÉ ───────────────────────────────
    // 📏 Mesure du 26/08/2026, avec son dénominateur. Aucune convention, quel que soit le critère de recherche. C'est le pendant employés de la CP 101 : même secteur éteint.
    // 🚫 L'ONSS ne rattache non plus aucune catégorie d'employeur à cette
    // commission dans le fichier des taux DmfA 2026/3.
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Sur un brut de
    // 2 500 €, il facturait 112,5 € par mois et par travailleur.
    //
    // ⚖️ Le 0 vient d'un CENSUS, pas d'un acte de dissolution : il prouve que le
    // SPF ne publie rien, pas qu'aucune cotisation n'existe. La réserve est
    // écrite ci-dessous pour qu'aucune session ne le lise comme un fait clos.
    fondsSE: {
      nom: "Employés des charbonnages — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des conventions publiées pour la CP 205, lu le 26/08/2026 (0 convention). Aucune convention, quel que soit le critère de recherche. C'est le pendant employés de la CP 101 : même secteur éteint. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à cette commission.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 vient d'un CENSUS de titres, pas d'un acte de dissolution comme pour les CP 316 et 328.02. Un census prouve que le SPF ne publie rien sous ce numéro, pas qu'aucune cotisation n'existe. À reconfirmer avant d'affirmer à un client qu'il ne doit rien. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation. Reste aussi le site du fonds.",
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (secteur éteint,
    // aucune convention relevée, pendant employés de la CP 101).
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — census complet sans convention publiée (secteur des charbonnages éteint)",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 205 + « eco cheque » :
    // 0 résultat. Cohérent avec chequesRepas ci-dessus : secteur des
    // charbonnages éteint, aucune convention publiée.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 205 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — recherche plein texte « formation »/
    // « opleiding » filtrée CP 205 → « 0 résultat(s) trouvé(s) » confirmé
    // (secteur charbonnier quasi éteint en Belgique).
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucune CCT sectorielle de formation retrouvée sur le motif formation/opleiding (secteur charbonnier quasi éteint)",
      source: "Banque CCT du SPF Emploi, recherche plein texte formation/opleiding filtrée CP 205 : 0 résultat, relevé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 205: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 210 — Employés de la sidérurgie (généré batch 29/04/2026)
  '210': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 29/01/2026 (CP 210), banque CCT
    // du SPF Emploi, 210/210-2026-001614.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 29/01/2026 (CP 210) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 210/210-2026-001614.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 210 — Employés de la sidérurgie',
    nomCourt: 'Employés de la sidérurgie',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay/ACLVB' },
    // ── UN « EFFORT », PAS UNE COTISATION — et la nuance vaut de l'argent ──
    // ✅ CCT du 11/02/2026 « Détermination de l'effort en faveur des personnes
    // appartenant aux groupes à risque », art. 4 à 8 — dépôt 198655/CO/210,
    // banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il se trompait de
    // NATURE autant que de valeur. L'art. 4 fixe bien 0,10 % des rémunérations
    // déclarées à la sécurité sociale — mais c'est un **effort à consentir**,
    // pas une cotisation à verser. L'art. 8 le dit sans ambiguïté : « La
    // convention collective de travail D'ENTREPRISE comporte impérativement un
    // engagement d'AFFECTER À CES INITIATIVES un budget équivalent à 0,10 p.c.
    // […] de la masse salariale annuelle déclarée à l'ONSS ». L'argent ne quitte
    // pas l'entreprise vers un fonds : elle le dépense elle-même en formation,
    // et dépose chaque année son rapport au Greffe.
    //
    // ⛔ D'OÙ LE 0. Le champ `taux_patronal` alimente le coût employeur comme une
    // cotisation versée à un tiers. Y écrire 0,10 % ferait payer deux fois une
    // entreprise qui forme réellement — et facturerait un versement qui n'a
    // aucun destinataire. L'obligation est réelle, elle est écrite ci-dessous.
    fondsSE: {
      nom: "Sidérurgie employés — effort de formation à charge de l'entreprise, pas de cotisation à un fonds",
      taux_patronal: 0,
      effortDeFormation: {
        taux: 0.001,
        base: "ensemble des rémunérations déclarées à la sécurité sociale du personnel sous contrat de travail d'employé",
        nature: "budget que l'entreprise s'engage à AFFECTER elle-même à des initiatives de formation et d'emploi, par CCT d'entreprise déposée au Greffe — pas un versement à un fonds",
        affectationObligatoire: "0,05 % réservé aux groupes à risque de l'AR du 19/02/2013 ; les partenaires sociaux demandent au Ministre de l'Emploi de réaffecter la moitié (0,025 %) aux travailleurs de 40 ans et plus menacés de licenciement, au lieu des moins de 26 ans",
        annees: [2025, 2026],
        source: "CCT du 11/02/2026 (CP 210, employés de la sidérurgie), art. 4 (« Cet effort doit être équivalent à 0,10 p.c. de l'ensemble des rémunérations déclarées à la sécurité sociale du personnel sous contrat de travail d'employé ») et art. 8 (« La convention collective de travail d'entreprise comporte impérativement un engagement d'affecter à ces initiatives un budget équivalent à 0,10 p.c. pour 2025-2026 de la masse salariale annuelle déclarée à l'ONSS ») — dépôt 198655/CO/210, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
      },
      aVerifier: true,
      aVerifierRaison: "Le taux est mis à 0 parce que l'obligation de la CP 210 est un EFFORT que l'entreprise dépense elle-même (CCT d'entreprise, rapport annuel au Greffe), pas une cotisation versée à un fonds — l'écrire dans `taux_patronal` facturerait un versement sans destinataire. L'obligation de 0,10 % reste réelle et est décrite sous `effortDeFormation`. À rouvrir si un fonds sectoriel percepteur est trouvé pour cette commission : le census n'a porté que sur les conventions les plus récentes.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait une cotisation à un fonds, ce que cette commission n'a pas.",
    },
    // 🔴 Recherché le 05/09/2026 sans conclusion — la valeur 10,00 € est le
    // PLAFOND FÉDÉRAL (cf. CP 111/118/119, corrigés ci-dessus après vérification :
    // même famille recopiée sans provenance sectorielle). Rien de sourcé trouvé
    // spécifiquement pour la CP 210 (sidérurgie employés).
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 210 + « chèque
    // repas » dans la banque CCT du SPF Emploi : 0 résultat (« 0 résultat(s)
    // trouvé(s) » affiché par le site lui-même, après un premier essai
    // ayant échoué sur un timeout réseau transitoire — rejoué avec succès).
    // Remplace le triplet fortement suspect ci-dessus.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 210 — recherche plein texte « chèque repas » : 0 résultat",
      dateVerif: '2026-09-18',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 210 + « eco cheque » :
    // 0 résultat (après un premier essai transitoire échoué sur timeout
    // réseau, rejoué avec succès).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 210 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 210 » était FAUX,
    // et se contredisait déjà avec `fondsSE` juste au-dessus sur cette même
    // fiche : la CCT du 11/02/2026 (dépôt 198655/CO/210, lue le 26/08/2026) dit
    // que l'obligation de cette CP est un EFFORT de 0,10 % que l'entreprise
    // dépense ELLE-MÊME (CCT d'entreprise, rapport au Greffe) — pas un
    // versement à un fonds, et rien n'y établit un droit individuel de 5 jours.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — l'obligation sectorielle propre à la CP 210 est un EFFORT de 0,10 % à charge de l'entreprise elle-même (voir fondsSE.effortDeFormation ci-dessus), pas un droit individuel en jours ni un fonds percepteur.",
      source: "Même source que fondsSE.effortDeFormation : CCT du 11/02/2026 (CP 210, employés de la sidérurgie), dépôt 198655/CO/210, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Aucune mention d'un droit individuel en jours ni d'un fonds sectoriel percepteur.",
      dateVerif: '2026-08-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
      source: "Aucune CCT sectorielle CP 210 trouvée fixant un taux de sursalaire propre lors des recherches menées sur cette fiche (11/02/2026 effort formation, 18/09/2026 chèques-repas/éco-chèques) — taux légaux par défaut (loi du 16/03/1971) appliqués faute de déviation sectorielle sourcée.",
      dateVerif: '2026-09-25',
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 210: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    // ✅ FICHE ENTIÈREMENT RELUE LE 25/09/2026 — remplace le tampon de lot du
    // 29/04/2026. Chaque dimension substantielle porte désormais sa propre
    // source datée : avantages/finDeCarrière (22/08), fondsSE (26/08, effort
    // 0,10% plutôt que cotisation), chequesRepas (18/09), ecoCheques (18/09),
    // formation (25/09, corrigé — même source que fondsSE), heuresSup (25/09,
    // confirmé sans déviation). `indexation` et `indemnites.velo` restent sur
    // des sources secondaires (Easypay/ACLVB, CCT n°164 « à confirmer ») — pas
    // rouvertes aujourd'hui, signalé honnêtement plutôt que tu.
    _meta: {
      dateVerif: '2026-09-25',
      source: "Fiche relue en entier le 25/09/2026 : avantages, fondsSE, chequesRepas, ecoCheques, formation et heuresSup portent chacun leur propre source datée et primaire. `indexation` (Easypay/ACLVB) et `indemnites.velo` (CCT n°164, à confirmer) restent sur source secondaire — non rouverts à cette occasion, signalé pour une prochaine passe.",
    },
  },

  // CP 214 — Employés de l\ (généré batch 29/04/2026)
  '214': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 06/02/2026 (CP 214), banque CCT
    // du SPF Emploi, 214/214-2026-001707.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 06/02/2026 (CP 214) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 214/214-2026-001707.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: "CP 214 — Employés de l'industrie textile",
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay' },
    // ⚠️ 4 % INVENTÉ → 0,60 %, LU AU TEXTE PRIMAIRE (26/08/2026).
    // Statuts coordonnés du 25/02/2026 (dépôt 198827/CO/214), art. 9 § 1 :
    // « Les avantages sociaux complémentaires visés au chapitre IV sont
    // financés par une cotisation patronale de 0,80 % sur les salaires bruts
    // plafonnés. À compter du premier trimestre 2026, la réduction de 0,2 % sur
    // les salaires plafonnés, arrivée à échéance le 01/01/2025, est
    // réintroduite ; la cotisation applicable aux salaires plafonnés sera donc
    // de 0,6 % à compter de ce moment. »
    // S'y ajoute l'art. 8 § 1 : une subvention de fonctionnement de 0,125 %
    // des salaires annuels bruts NON plafonnés.
    //
    // ⚠️ « PLAFONNÉS » EST LE PIÈGE DE CETTE FICHE, ET IL EST ASSUMÉ. Les
    // 0,60 % ne portent pas sur le brut entier mais sur un salaire PLAFONNÉ,
    // et l'app ne modélise pas ce plafond. Appliquer 0,60 % au brut complet
    // SURESTIME donc la cotisation des hauts salaires. On l'écrit quand même,
    // pour deux raisons : c'est le sens le moins dangereux (un surcoût se
    // discute, un sous-comptage se découvre chez le client), et 0,60 % reste
    // six fois plus proche de la réalité que les 4 % inventés. Le plafond est
    // nommé ici pour être branché le jour où le montant sera connu.
    // ⏰ La réduction de 0,2 % a une histoire : échue au 01/01/2025, réintroduite
    // au T1 2026. Un taux qui a déjà bougé deux fois bougera encore.
    fondsSE: {
      nom: "Fonds de sécurité d'existence pour les employés de l'industrie textile",
      taux_patronal: 0.006,
      assiette: 'salaires bruts PLAFONNÉS — le plafond n’est pas modélisé, voir la note',
      cctSourcee: {
        taux: 0.006,
        tauxNominal: 0.008,
        reductionDepuisT1_2026: 0.002,
        subventionFonctionnement: 0.00125,
        baseSubvention: 'salaires annuels bruts NON plafonnes',
        base: 'salaires bruts plafonnes',
        depuis: '2026-01-01',
        source: "Statuts coordonnes du 25/02/2026, art. 9 § 1 (cotisation) et art. 8 § 1 (subvention de fonctionnement) — depot 198827/CO/214, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Le 4 % qui figurait ici n'avait aucune source.",
        dateVerif: '2026-08-26',
        reserveEcrite: "les 0,60 % portent sur un salaire PLAFONNE que l'app ne modelise pas : appliques au brut complet, ils SURESTIMENT la cotisation des hauts salaires. Sens assume — un surcout se discute, un sous-comptage se decouvre chez le client.",
      },
      note: "La reduction de 0,2 % etait echue au 01/01/2025 et a ete reintroduite au 1er trimestre 2026 : ce taux a deja bouge deux fois.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (10,00 €) était le plafond
    // fédéral recopié {10/8.91/1.09}. Régime partagé avec la CP 120 (industrie
    // textile, ouvriers) : ACCG/FGTB + ACV-CSC METEA concordants, 7,20 €
    // (6,11 € employeur) au 01/02/2026.
    chequesRepas: {
      obligatoire: true, valeur: 7.20, part_employeur: 6.11, part_travailleur: 1.09,
      source: 'Régime commun CP 120/214 — ACCG (Centrale Générale-FGTB) + ACV-CSC METEA, publication sectorielle textile 2023-2024',
      dateEffet: '2026-02-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Seul texte trouvé : CCT du 11/06/2010 (214/…pdf), chapitre II « prime
    // UNIQUE sous forme d'écochèques » — 75 EUR versés avec le salaire de
    // septembre 2010, expirée le 31/12/2010. Aucune CCT plus récente trouvée
    // par la recherche plein texte.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 11/06/2010 (214/…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, chap. II : prime UNIQUE de 75 EUR versée en septembre 2010, expirée. Aucun régime annuel permanent identifié.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — tranche l'ambiguïté laissée ouverte le
    // 25/09 (session antérieure) : un vrai Fonds de sécurité d'existence
    // existe (CCT 198827/CO/214 du 25/02/2026, cotisation patronale 0,6%
    // sur salaires plafonnés) mais finance un effort COLLECTIF « travail
    // faisable »/groupes à risque (CCT 191983/CO/214 du 29/09/2023) — aucun
    // droit individuel en jours n'est fixé par une CCT sectorielle.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — le Fonds de sécurité d'existence CP 214 finance un effort collectif (budget « travail faisable »/groupes à risque, CCT 191983/CO/214 du 29/09/2023) sans jours individuels garantis par CCT sectorielle",
      fondsFormationExiste: true,
      fonds: "Fonds de sécurité d'existence pour les employés de l'industrie textile (CCT 198827/CO/214 du 25/02/2026, cotisation patronale 0,6% des salaires plafonnés)",
      source: "Banque CCT du SPF Emploi, CCT 191983/CO/214 (29/09/2023) et 198827/CO/214 (25/02/2026), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 214: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 215 — Employés de l\ (généré batch 29/04/2026)
  '215': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 18/02/2026 (CP 215), banque CCT
    // du SPF Emploi, 215/215-2026-002612.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 18/02/2026 (CP 215) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 215/215-2026-002612.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: "CP 215 — Employés de l'habillement et de la confection",
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0143, derniereDate: '2026-04-01', source: 'AUDIT 2026-06 — Easypay/CGSLB' },
    fondsSE: {
      nom: 'Fonds social CP 215',
      taux_patronal: 0.03,
      taux_patronal_employe: 0.0098,
      base108Ouvrier: true,
      onssCategorie: '038',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 038 : 820 ouvriers 3,00 % · employés 0,98 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — l'ancienne valeur (10,00 €) était le plafond
    // fédéral recopié {10/8.91/1.09}. Régime partagé avec la CP 109 (habillement
    // et confection, ouvriers) : ACCG (Centrale Générale-FGTB), 6,25 € (5,16 €
    // employeur) au 01/04/2026. 🟠 Confiance moyenne : l'identité de traitement
    // avec les employés CP 215 est déduite du regroupement systématique
    // « 109/215 » (Monizze, Liantis), aucune fiche ne cite « 215 » à côté du
    // chiffre — à recorroborer si un doute survient sur un dossier réel.
    chequesRepas: {
      obligatoire: true, valeur: 6.25, part_employeur: 5.16, part_travailleur: 1.09, aVerifier: true,
      source: 'Régime commun CP 109/215 — ACCG (Centrale Générale-FGTB), habillement-confection, confiance moyenne (déduit du regroupement 109/215)',
      dateEffet: '2026-04-01',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 215 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 215 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 26/09/2026, À RECONFIRMER — CCT 194487/CO/215 du
    // 03/07/2025 (art. 6-8), valide seulement 01/01/2025-31/12/2025, fixe
    // la trajectoire de croissance du droit individuel à la formation
    // (dérogation art. 54§2 loi du 03/10/2022) : 2,5j(2023)→3j(2024)
    // →3,5j(2025, valeur actuelle)→4j(2026, prévu mais non confirmé par
    // renouvellement)→5j(2027). Effort collectif complémentaire via IREC
    // (cotisation 0,20%, 2,5j/an).
    formation: {
      joursSectoriels: 3.5,
      aVerifier: true,
      mecanisme: "droit individuel à la formation (dérogation art. 54§2 loi du 03/10/2022) — trajectoire CP 215 : 2025 = 3,5 j/an ; palier 2026 = 4 j/an prévu mais non confirmé par une CCT de renouvellement retrouvée (CCT 194487/CO/215 expirée le 31/12/2025) ; effort collectif complémentaire via IREC (cotisation 0,20%, 2,5 j/an)",
      source: "CCT 194487/CO/215 du 03/07/2025 (art. 6-8), banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 215: 0,27 €/km — CCT sectorielle habillement/confection employés, accord 2023-2024 déposé 16/10/2023, effet 01/01/2024, max 40 km A/R (Liantis+CGSLB, corroboré 2026-07-09 ; corrige l'ancienne valeur 0,12 = 2022 périmée)
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 216 — Employés occupés chez les notaires (généré batch 29/04/2026)
  '216': {
    nom: 'CP 216 — Employés occupés chez les notaires',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'plurielle', mois: [1, 5, 9], dernierTaux: 0.012, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay ; calendrier vérifié 2026-07-16 : indexation 3×/an (jan/mai/sep), SETCa/SSN CP 216. ⚠️ CORRIGÉ LE 31/08/2026 : ce champ annonçait encore le 01/01/2026 (+0,42 %) alors que la CP avait indexé le 01/05/2026 de +1,2 % — la fiche SPF 2160000 (jcId e151e3dd20a14676acfc3ec5757b45d4, relue le 31/08/2026) écrit « Indexation de 1,2% en 5/2026 » et se donne « en vigueur au 01/05/2026 ». Le BARÈME de baremes-cp-grid.js, lui, portait bien les valeurs de mai : c\'est la seule date de la veille qui avait sauté un cycle, et une date périmée fausse la détection de dérive. Prochaine échéance : 01/09/2026, pas encore publiée par le SPF au 31/08/2026.' },
    fondsSE: {
      nom: 'Fonds social CP 216',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.0248,
      pension_sectorielle_employe: 0.0495,
      base108Ouvrier: true,
      onssCategorie: '434, 435',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 434, 435 : 820 ouvriers 0,10 % · employés 2,48 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // 🔴 Recherché le 05/09/2026 sans conclusion — un régime historique daté de
    // 2017 existe mais sa valeur actuelle n'est pas confirmée ; les négociations
    // sectorielles ont été gelées 2023-2025 (CSC).
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 216 + « chèque
    // repas » : 3 CCT (2005, 2016, 2017). Les deux plus récentes (2016 et
    // 2017) sont, comme la CP 209, des « MENUS CAFÉTÉRIA » de mise en œuvre
    // de marge salariale interprofessionnelle — l'augmentation de la
    // participation patronale dépend du montant DÉJÀ accordé par
    // l'entreprise avant l'entrée en vigueur (ex. CCT 2016 : +1 € si la part
    // patronale valait déjà 5,91 €, +1,60 € si elle valait entre 5,31 € et
    // 5,91 €, ou 4 options en 2017 dont une prime en % au lieu des
    // chèques-repas). Aucun montant sectoriel FIXE n'en ressort — remplace
    // le triplet 10/8,91/1,09 (plafond fédéral recopié, fortement suspect).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT SPF Emploi, CP 216 — CCT du 08/04/2016 (dépôt notarisbedienden) et du 05/10/2017 (n°142404/CO/216) : augmentation conditionnelle de la part patronale selon le montant déjà accordé par l'entreprise (menu à plusieurs options en 2017), aucun montant sectoriel fixe",
      dateVerif: '2026-09-18',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // CCT du 2011 « mise en œuvre de l'AIP 2011-2012 » (216/216-2011-…pdf),
    // art. 5 : « Vanaf 2011 worden jaarlijks ecocheques ter waarde van 150€
    // toegekend » (« À partir de 2011, des éco-chèques d'une valeur de 150 €
    // sont octroyés chaque année ») — régime ANNUEL et RÉCURRENT, PAS un
    // versement unique. Confirmé encore actif par la CCT du 18/06/2021
    // (216/216-2021-008865.pdf), qui organise l'assimilation des jours de
    // chômage temporaire « pour l'octroi des éco-chèques avec comme période
    // de référence l'année 2021 ». Montant sectoriel = 150 EUR/an (PAS 250,
    // la valeur recopiée) ; possibilité de remplacement par un avantage
    // équivalent négocié en entreprise (§5). Paiement en une fois, au plus
    // tard le 31 mars de l'année suivante.
    ecoChequesAnnuel: 150,
    ecoChequesSource: "CCT de 2011 « mise en œuvre de l'AIP 2011-2012 » (216/216-2011-…pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026, art. 5 : 150 EUR/an (pas 250), régime annuel récurrent depuis 2011, confirmé actif par la CCT du 18/06/2021. Paiement en une fois, au plus tard le 31 mars de l'année suivante.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT 194236/CO/216 du 17/06/2025 (art. 3-4) :
    // effort groupes à risque de 0,10% de la masse salariale, versé au
    // Fonds de Sécurité d'Existence du Notariat ; le CA du fonds décide
    // chaque année du programme de formation — aucun jour individuel
    // garanti par CCT.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — le Fonds de Sécurité d'Existence du Notariat finance un effort collectif groupes à risque (0,10% de la masse salariale, CCT 194236/CO/216 du 17/06/2025) sans jours individuels garantis par CCT sectorielle",
      fondsFormationExiste: true,
      fonds: "Fonds de Sécurité d'Existence du Notariat",
      source: "Banque CCT du SPF Emploi, CCT 194236/CO/216 (17/06/2025, art. 3-4), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.24 },  // vélo CP 216: 0,24 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + CPNAE',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 217 — Employés de casino (généré batch 29/04/2026)
  '217': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 02/12/2025 (CP 217), banque CCT
    // du SPF Emploi, 217/217-2025-014614.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 02/12/2025 (CP 217) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 217/217-2025-014614.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 217 — Employés de casino',
    nomCourt: 'Employés de casino',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02189, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay', formule: 'sante_lisse_nov', formuleAConfirmer: true },
    // ── DEUX COTISATIONS QUI S'ADDITIONNENT : 0,15 % + 0,10 % = 0,25 % ────
    // ✅ CCT du 12/06/2025 « Groupes à risque », art. 2, 3 et 9 — dépôt
    // 193869/CO/217, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.
    //   · art. 2 : « la perception de 0,15 p.c. des salaires bruts des employés,
    //     prévue pour une DURÉE INDÉTERMINÉE, est confirmée » ;
    //   · art. 3 : « les employeurs […] sont tenus de verser POUR LES ANNÉES
    //     2025 ET 2026 une cotisation spéciale correspondant à 0,10 p.c.,
    //     calculée sur la base du salaire global des employés qu'ils occupent ».
    //   · art. 4 : les deux sont perçues par le « Fonds social de formation ».
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — dix-huit fois trop.
    //
    // ⏰ ET LES DEUX N'ONT PAS LA MÊME DURÉE DE VIE : le 0,15 % est à durée
    // indéterminée, le 0,10 % cesse le 31/12/2026 (art. 9). Sans reconduction,
    // le taux retombe à 0,15 % au 1er janvier 2027 — d'où `expireQuoi`.
    fondsSE: {
      nom: 'Fonds social de formation pour employés (CP 217)',
      taux_patronal: 0.0025,
      base: 'salaires bruts des employés',
      cctSourcee: {
        taux: 0.0025,
        composantes: {
          perceptionDureeIndeterminee: 0.0015,   // art. 2
          cotisationSpeciale2025_2026: 0.0010,   // art. 3
        },
        base: "salaires bruts des employés / salaire global des employés occupés (art. 2 et 3)",
        source: "CCT du 12/06/2025 (CP 217, employés), art. 2, 3, 4 et 9 — dépôt 193869/CO/217, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Les deux cotisations sont perçues par le Fonds social de formation (art. 4). Art. 6 : au moins 0,05 % de la masse salariale réservé aux groupes à risque énumérés ; art. 7 : au moins la moitié (0,025 %) aux jeunes de moins de 26 ans.",
        dateVerif: '2026-08-26',
        perimetre: 'employés uniquement',
      },
      expireLe: '2026-12-31',
      expireQuoi: "la cotisation spéciale de 0,10 % (art. 3), à durée déterminée. Le 0,15 % de l'art. 2 est, lui, à durée indéterminée : sans reconduction, le taux retombe à 0,15 % au 01/01/2027.",
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CONFIRMÉ STRUCTURELLEMENT LE 05/09/2026 — il n'existe PAS de montant
    // sectoriel unique à trouver. Census exhaustif refait sur les 57 CCT de la
    // CP 217 depuis 1999 (thème 246) : SEULES 2 CCT touchent aux chèques-repas
    // dans toute l'histoire de la commission (31/08/2009 et 02/12/2025) et les
    // DEUX ne disent QUE des incréments-planchers, jamais un total — aucune
    // CCT intermédiaire (2010-2024) n'en parle. Synova (ex-CGSLB), page CP 217 :
    // « le secteur attribue des chèques-repas, les modalités sont déterminées
    // par chaque casino. » Confirmation empirique : un casino annonce 8 €/jour
    // dans ses offres d'emploi, un autre un « avantage équivalent net de 75 € »
    // — les bases divergent réellement d'un employeur à l'autre.
    chequesRepas: {
      obligatoire: 'conditionnel',
      valeur: null,
      part_employeur: null,
      part_travailleur: null,
      conditionnelRaison: "chaque casino fixe sa propre base de chèque-repas (ou avantage équivalent) ; la CP n'impose qu'un incrément-plancher : +1,00 € employeur au 01/07/2009 (CCT du 31/08/2009), +0,5 € employeur au 01/01/2026 (CCT du 02/12/2025, 217/217-2025-014613.pdf)",
      source: "Banque CCT du SPF Emploi (census exhaustif, 57 CCT depuis 1999) + Synova (ex-CGSLB), page CP 217, consultée le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 217 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 217 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT 193869/CO/217 du 12/06/2025 (art. 2-4) :
    // cotisation permanente 0,15% + cotisation spéciale 0,10% (2025-2026)
    // versées au « Fonds social de formation pour employés de casino » pour
    // la formation/l'emploi des groupes à risque — aucun jour individuel
    // garanti.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — le Fonds social de formation pour employés de casino finance les groupes à risque (0,15% permanent + 0,10% 2025-2026, CCT 193869/CO/217 du 12/06/2025) sans jours individuels garantis par CCT sectorielle",
      fondsFormationExiste: true,
      fonds: "Fonds social de formation pour employés de casino",
      source: "Banque CCT du SPF Emploi, CCT 193869/CO/217 (12/06/2025, art. 2-4), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 217: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 219 — Services et organismes de contrôle technique (généré batch 29/04/2026)
  '219': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 07/07/2026 (CP 219), banque CCT
    // du SPF Emploi, 219/219-2026-014160.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 07/07/2026 (CP 219) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 219/219-2026-014160.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 219 — Services et organismes de contrôle technique',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0166, derniereDate: '2026-04-01', source: 'AUDIT 2026-06 — Easypay/CSC' },
    // ── FONDS DE SÉCURITÉ D'EXISTENCE — un fonds à FORFAITS, pas à taux ────
    // ✅ CCT du 25/10/2023 « Statuts Fonds Social CP 219 - Fonds de Sécurité
    // d'Existence », art. 14 à 16 — dépôt 183682/CO/219, banque CCT du SPF
    // Emploi, 219/219-2023-014514.pdf, texte primaire lu le 26/08/2026.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il se trompait aussi
    // de NATURE. L'art. 15, § 1er est explicite : « Les cotisations sont
    // perçues sous la forme de cotisations forfaitaires trimestrielles. Elles
    // sont calculées sur le NOMBRE de travailleurs […] en service le dernier
    // jour du trimestre. » Un pourcentage du brut ne décrit pas ce fonds.
    // Une seule composante est un pourcentage : les groupes à risque.
    //
    // 📏 CE QUI EST ÉCRIT ICI EST DONC INCOMPLET, ET C'EST DIT. Le taux couvre
    // 1 des 3 composantes ; les 2 autres sont des forfaits par tête, dont l'un
    // est indexé chaque 1er janvier et dont la valeur 2026 n'a pas été lue.
    // Une fiche honnêtement incomplète vaut mieux qu'un 4 % inventé (R12).
    fondsSE: {
      nom: "FS219 — Fonds social CP 219 (Fonds de sécurité d'existence)",
      taux_patronal: 0.001,
      base: 'masse salariale',
      cctSourcee: {
        taux: 0.001,
        composantes: { groupesARisqueEtEmploisTremplins: 0.001 },  // art. 15, § 4, depuis le 01/01/2020
        base: "masse salariale (art. 15, § 4 : « la cotisation est égale à 0,10 % de la masse salariale »)",
        source: "CCT du 25/10/2023 (CP 219, services et organismes de contrôle technique), statuts du FS219, art. 15, § 4 — dépôt 183682/CO/219, banque CCT du SPF Emploi, 219/219-2023-014514.pdf, texte primaire lu le 26/08/2026. Cotisations perçues trimestriellement et versées à l'ASBL « Fonds pour l'emploi et la formation des Employés des fabrications Métalliques du Brabant » (BCE 0434.364.911).",
        dateVerif: '2026-08-26',
      },
      // 💰 LES DEUX AUTRES COMPOSANTES SONT DES FORFAITS PAR TÊTE, dus chaque
      // trimestre pour chaque employé en service le dernier jour du trimestre.
      // NON facturés par l'app à ce jour — ils manquent donc au coût employeur.
      forfaitsTrimestrielsParEmploye: {
        garantiesOrganisationsDeTravailleurs: { montant: 18.71, depuis: '2024-01-01', source: 'art. 15, § 2' },
        formationSyndicale: {
          montant: 39.39,
          depuis: '2024-01-01',
          echeance: "à durée déterminée jusqu'au 31/12/2024, prolongée par les statuts — reconduction au-delà NON vérifiée",
          indexation: "adapté chaque 1er janvier sur les augmentations salariales sectorielles et les indexations (art. 9 § 2 de la CCT des 23/06 et 07/09/1999, dépôt 53277/CO/219) — la valeur 2026 n'a PAS été lue",
          source: 'art. 15, § 3',
        },
      },
      aVerifier: true,
      aVerifierRaison: "Deux composantes sur trois sont des forfaits par tête, hors du taux : le coût employeur affiché est donc SOUS-ÉVALUÉ. Le forfait de formation syndicale est indexé chaque 1er janvier et sa valeur 2026 n'a pas été lue ; son échéance du 31/12/2024 n'a pas été retrouvée prolongée. À reprendre sur une CCT postérieure à 2023.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait un fonds à pourcentage, ce que ce fonds n'est pas.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — 4 CCT (2005, 2007, 2011, 2015, 219/219-2015-010796.pdf
    // lue intégralement) décrivent un menu OPTIONNEL au niveau ENTREPRISE, réservé
    // aux entreprises avec délégation syndicale employés : au choix, +10€/mois de
    // salaire, OU +1€ chèques-repas, OU +210€ pension complémentaire, OU +210€
    // éco-chèques. Ce n'est ni obligatoire, ni universel, ni un montant fixé par
    // la CP — aucune obligation sectorielle de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: "CCT du 219/219-2015-010796.pdf (et 2005/2007/2011), banque CCT du SPF Emploi — menu optionnel entreprise, pas d'obligation sectorielle",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — la recherche plein texte CP 219 + « eco cheque »
    // rend 0 résultat, MAIS le commentaire ci-dessus (chequesRepas, lu le
    // 05/09/2026 sur 219/219-2015-010796.pdf) documente déjà un menu
    // OPTIONNEL à quatre choix, réservé aux entreprises avec délégation
    // syndicale employés : +10 €/mois de salaire, OU +1 € chèques-repas, OU
    // +210 € pension complémentaire, OU +210 € éco-chèques. Ce n'est PAS une
    // obligation sectorielle inconditionnelle (même raisonnement que
    // chequesRepas ci-dessus) : ni universel, ni automatique, ni un montant
    // fixé par la CP pour tout travailleur. D'où AUCUN droit annuel garanti,
    // cohérent avec le 0 résultat de cette recherche-ci (le document n'est
    // pas indexé « éco-chèques » comme sujet principal).
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 219/219-2015-010796.pdf (et 2005/2007/2011), banque CCT du SPF Emploi, lue le 05/09/2026 : menu optionnel à 4 choix réservé aux entreprises avec délégation syndicale (dont +210 EUR éco-chèques), aucune obligation sectorielle inconditionnelle. Recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 2 CCT, dont celle du 05/10/2015 (dépôt 131168/CO/219, texte primaire lu le 21/09/2026) qui confirme le menu : « 10 € brut/mois d'augmentation salariale + éco-chèques d'une valeur de 210 €, dans les possibilités légales » est l'une des options, à côté de l'indemnité journalière de 1 € et de la pension complémentaire. Aucune obligation sectorielle inconditionnelle : rien à coder.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — mécanisme faux (aucun fonds de formation,
    // seulement un FSE générique pour les avantages sociaux) mais le
    // chiffre 5 est confirmé pour 2026 : CCT 183180/CO/219 du 25/09/2023
    // (art. 3 §1) fixe le droit individuel direct par employé temps plein :
    // 2j(2023)→2,5j(2024)→3,5j(2025)→5j(2026, palier atteint). Plan de
    // formation annuel obligatoire ≥20 travailleurs (CCT 184510/CO/219,
    // erratum 25/10/2023).
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      mecanisme: "droit individuel à la formation (pas un fonds) — CCT 183180/CO/219 du 25/09/2023, trajectoire : 2 j (2023), 2,5 j (2024), 3,5 j (2025), 5 j (2026, palier atteint) ; plan de formation annuel obligatoire ≥20 travailleurs (CCT 184510/CO/219, erratum 25/10/2023)",
      source: "Banque CCT du SPF Emploi, CCT 183180/CO/219 (25/09/2023, art. 3 §1), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 219: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 221 — Employés de l\ (généré batch 29/04/2026)
  '221': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 19/01/2026 (CP 221), banque CCT
    // du SPF Emploi, 221/221-2026-000544.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 19/01/2026 (CP 221) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 221/221-2026-000544.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: "CP 221 — Employés de l'industrie papetière",
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', dernierTaux: 0.0189, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay' },
    fondsSE: {
      nom: 'Fonds social CP 221',
      taux_patronal: 0.0139,
      taux_patronal_employe: 0.0075,
      base108Ouvrier: true,
      onssCategorie: '189',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 189 : 820 ouvriers 1,39 % · employés 0,75 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — zéro résultat sur les 15 CCT déposées pour cette
    // CP (industrie papetière, employés), 4 recherches indépendantes (thème SPF
    // 246, plein texte FR/NL, lecture intégrale des 2 CCT salariales les plus
    // récentes) : aucune occurrence de « repas ».
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 221 (industrie papetière, employés) — recherche exhaustive sur les 15 CCT déposées, zéro mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 221 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 221 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT 197624/CO/221 du 19/01/2026 (art. 3-4),
    // entreprises ≥20 ETP : trajectoire sur 10 ans dès 2023. Période
    // 2025-2026 = droit individuel 2,5j/ETP/an + droit collectif 1,5j
    // (moyenne/ETP) réparti sur 2 ans. Le motif actuel (5 jours) est faux
    // pour 2026.
    formation: {
      joursSectoriels: 2.5,
      fondsFormationExiste: false,
      mecanisme: "droit individuel à la formation (pas un fonds) — trajectoire CP 221 (≥20 ETP), période 2025-2026 : 2,5 j/ETP/an (individuel) + 1,5 j collectif (moyenne/ETP) réparti sur 2 ans",
      source: "CCT 197624/CO/221 du 19/01/2026, art. 3-4, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27 },  // vélo CP 221: 0,27 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 222 — Employés de la transformation du papier et carton (généré batch 29/04/2026)
  '222': {
    nom: 'CP 222 — Employés de la transformation du papier et carton',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.0205, derniereDate: '2026-07-01', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 222',
      taux_patronal: 0.016,
      taux_patronal_employe: 0.0048,
      base108Ouvrier: true,
      onssCategorie: '089',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 089 : 820 ouvriers 1,60 % · employés 0,48 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 17/12/2025 (CP 222, employés de la transformation du papier et du carton), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Évolutions ultérieures : à compter du 1er janvier 2026, la valeur faciale du chèque-repas s'élève à 3,09 EUR. » ⚠️ PIÈGE DE LECTURE : ce texte empile son historique depuis 2009 (1,59 puis 2,09 EUR) — il faut la DERNIÈRE valeur. ⚠️ RÉGIME DISTINCT POUR LES COURTES JOURNÉES (moins de 4 h/jour) : valeur nominale 2,09 EUR au 01/01/2026, puis 2,59 EUR au 01/07/2026.", regimeSecondaire: "journee de moins de 4 heures : 2,09 EUR au 01/01/2026, 2,59 EUR au 01/07/2026", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '222/222-2025-015061.pdf' },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 222 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 222 — recherches « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » le 21/09/2026 : 3 CCT (dépôts 173489 du 16/02/2022, 168455 du 09/11/2021, 153918 du 05/09/2019). Toutes prévoient un écochèque UNIQUE de 150 € (ou une alternative de même coût salarial), payable une fois — décembre 2019, puis janvier 2022. Ce n'est pas un montant annuel récurrent : rien à coder comme avantage en vigueur.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT 196610/CO/222 du 14/11/2025 (art. 3 §1),
    // entreprises ≥20 ETP : trajectoire sur 10 ans dès 2023. Période
    // 2025-2026 = droit individuel 2,5j/ETP/an + 2j collectifs (moyenne/ETP)
    // sur 2 ans. La trajectoire n'atteint 5j qu'à partir de 2031-2032.
    formation: {
      joursSectoriels: 2.5,
      fondsFormationExiste: false,
      mecanisme: "droit individuel à la formation (pas un fonds) — trajectoire CP 222 (≥20 ETP), période 2025-2026 : 2,5 j/ETP/an (individuel) + 2 j collectifs (moyenne/ETP) sur 2 ans ; la trajectoire n'atteint 5 j qu'à partir de la période 2031-2032",
      source: "CCT 196610/CO/222 du 14/11/2025, art. 3 §1, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.32 },  // vélo CP 222: 0,32 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 223 — Commission nationale des sports (généré batch 29/04/2026)
  '223': {
    nom: 'CP 223 — Commission nationale des sports',
    // ── AVANTAGE SECTORIEL — prime syndicale ──────────────────────────────
    // ✅ Texte primaire lu le 23/08/2026.
    //
    // ⚠️ PÉRIMÈTRE TRÈS ÉTROIT, ET IL EST DANS LA DONNÉE, PAS EN COMMENTAIRE.
    // Cette prime ne vise PAS tous les sportifs rémunérés de la CP 223 : elle
    // est financée par la Ligue de volleyball et versée aux joueurs de
    // VOLLEYBALL affiliés. L'annoncer à un footballeur ou à un basketteur de
    // la même commission serait promettre un droit qui n'existe pas — le même
    // défaut que le drapeau nu.
    avantages: {
      primeSyndicale_montant: 100,              // EUR par an et par joueur affilié
      primeSyndicale_enveloppe: 3000,           // EUR mis à disposition chaque année
      primeSyndicale_perimetre: 'joueurs de volleyball affiliés uniquement — PAS tous les sportifs rémunérés de la CP 223',
      primeSyndicale_financeur: 'la Ligue de volleyball',
      primeSyndicale_soldeNonUtilise: "le montant de l'enveloppe non versé en primes est affecté à un projet commun à définir entre les parties",
      primeSyndicale_source: "CCT du 24/06/2026 (CP 223), art. 15, chapitre VIII « Projet commun » — banque CCT du SPF Emploi, 223/223-2026-013559.pdf, lue le 23/08/2026",
      primeSyndicale_dateVerif: '2026-08-23',
    },
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId 5932853e… (CP 2230000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "revenu minimum moyen garanti, plus un minimum propre aux footballeurs professionnels hors EEE. Aucune catégorie de fonction.",
    },
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', note: '+2% au 01/02/2026 (saut indice-pivot, PAS 01/07). Source: AUDIT 2026-06 Sociare/CESSOC + CSC CP 223' },
    // ── FONDS SOCIAL POUR LE SPORT — c'est le percepteur qui donne le taux ─
    // ✅ ONSS, fichier des taux DmfA 2026/3, catégorie d'employeur 070, code
    // travailleur 830 : 0,10 %, intitulé « Fonds de sécurité d'existence
    // (autres que CPAE 200, commerce de détail 201 et socio-culturel) ».
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — quarante-cinq fois
    // le taux réel. Sur un brut de 2 500 €, 112,50 € par mois au lieu de 2,50.
    //
    // 🔎 ET LA CCT NE POUVAIT PAS RÉPONDRE, C'EST POUR ÇA QU'ON PASSE PAR
    // L'ONSS. Les statuts du Fonds social pour le sport (CCT du 26/03/2004,
    // dépôt 71057/CO/223, art. 9 à 11) ont été lus au texte primaire le
    // 26/08/2026 : ils disent que « les cotisations sont fixées par convention
    // collective de travail conclue au sein de la Commission paritaire
    // nationale des sports » et « perçues et encaissées par l'ONSS » — sans
    // jamais donner de montant, et la banque CCT ne compte que DEUX conventions
    // pour cette commission. C'est le premier des trois pièges du 26/08 : des
    // statuts ne portent pas toujours le taux. Ici le percepteur, lui, le
    // publie.
    fondsSE: {
      nom: 'Fonds social pour le sport',
      taux_patronal: 0.001,
      onssSourcee: true,
      onssCategorie: '070',
      base: 'rémunérations brutes',
      source: "ONSS, fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie d'employeur 070, code travailleur 830 : 0,10 % patronal, « Fonds de sécurité d'existence (autres que CPAE 200, commerce de détail 201 et socio-culturel) ». Recoupé le 26/08/2026 sur les statuts du fonds (CCT du 26/03/2004, dépôt 71057/CO/223, art. 9 à 11), qui confirment que la perception passe par l'ONSS mais ne chiffrent pas la cotisation.",
      dateVerif: '2026-08-26',
      note: "⚠️ L'ONSS ne publie, pour la catégorie 070, QUE la ligne du code 830 — celle des travailleurs intellectuels. Aucune ligne 820 (ouvriers sur les rémunérations à 108 %) n'existe pour cette catégorie. Le taux écrit ici est donc celui du personnel EMPLOYÉ, qui est le régime de la quasi-totalité du secteur sportif. Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — seule CCT taguée thème chèque-repas (11/01/2018,
    // 223/223-2018-000366.pdf) lue intégralement (76 617 caractères) : aucune
    // occurrence de « repas »/« chèque »/« maaltijd ». Cohérent avec le périmètre
    // très étroit déjà documenté de cette CP (prime syndicale volleyball
    // uniquement, pas de grille générale).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 223 (223/223-2018-000366.pdf lue intégralement) — aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 223 + « eco cheque » :
    // 0 résultat. Cohérent avec le périmètre très étroit déjà documenté de
    // cette CP (prime syndicale volleyball uniquement).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 223 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 223' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 223: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 224 — Employés des métaux non-ferreux (généré batch 29/04/2026)
  '224': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 09/02/2026 (CP 224), banque CCT
    // du SPF Emploi, 224/224-2026-001821.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 09/02/2026 (CP 224) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 224/224-2026-001821.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 224 — Employés des métaux non-ferreux',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.0194, derniereDate: '2026-05-01', source: 'AUDIT 2026-06 — Easypay/CSC' },
    // ── NON-FERREUX EMPLOYÉS — 0,10 % du brut, par trimestre ──────────────
    // ✅ CCT du 09/02/2026 « emploi et formation des groupes à risque », art. 2
    // et 7 — dépôt 198062/CO/224, banque CCT du SPF Emploi, texte primaire lu le
    // 26/08/2026 : « les entreprises versent, dans le mois suivant l'échéance de
    // chaque trimestre, une cotisation de 0,10 p.c. des appointements bruts des
    // employés du dernier trimestre écoulé sur le compte de l'ASBL "Fonds pour
    // l'emploi et la formation des employés du secteur non ferreux" ».
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — quarante fois trop.
    //
    // 🔁 Miroir exact de la CP 105 (ouvriers du même secteur), à une différence
    // près qui compte : là-bas l'assiette est le brut à 108 %, ici ce sont les
    // appointements bruts des employés, sans majoration. Même secteur, même
    // taux, deux assiettes — c'est le genre d'écart qu'un taux nu efface.
    fondsSE: {
      nom: "Fonds pour l'emploi et la formation des employés du secteur non ferreux",
      taux_patronal: 0.001,
      base: 'appointements bruts des employés du trimestre écoulé',
      cctSourcee: {
        taux: 0.001,
        base: "appointements bruts des employés du dernier trimestre écoulé (art. 2) — PAS de majoration à 108 %, contrairement à la CP 105 ouvriers",
        periodicite: "versement trimestriel, dans le mois suivant l'échéance du trimestre",
        source: "CCT du 09/02/2026 (CP 224, employés des métaux non ferreux), art. 2 — dépôt 198062/CO/224, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Conclue en exécution du titre XIII, chapitre VIII, section 1 de la loi du 27/12/2006 et de l'AR du 19/02/2013. Art. 5 : 0,05 % de la masse salariale réservé aux groupes à risque énumérés ; art. 6 : au moins la moitié aux moins de 26 ans.",
        dateVerif: '2026-08-26',
        perimetre: 'employés uniquement',
      },
      expireLe: '2027-06-30',
      expireQuoi: "la totalité du taux : la CCT groupes à risque est à durée déterminée (art. 7)",
      aVerifier: true,
      aVerifierRaison: "Le 0,10 % écrit ici est la seule cotisation publiée pour la CP 224, et elle est étiquetée « groupes à risque ». Aucune convention décrivant un fonds de sécurité d'existence général n'a été trouvée pour cette commission. Un census n'est pas une preuve d'absence — à reconfirmer avant d'affirmer qu'aucune autre cotisation sectorielle n'est due.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — chèque-repas est un item OPTIONNEL d'un menu
    // "pouvoir d'achat" (à côté d'éco-chèques [choix par défaut], assurance
    // hospitalisation, pension complémentaire), art. 6-7 CCT du 19/06/2009
    // (224/224-2009-006583.pdf, texte primaire lu). Protocole 2025-2026 (CCT du
    // 09/02/2026, 224/224-2026-001823.pdf, texte primaire lu) : pour les
    // entreprises qui accordent déjà des CR, +2€ patronal au 01/07/2026 ; pour
    // celles qui n'en accordent pas encore, introduction à 2€ patronal — un
    // plancher très inférieur aux 8,91€ recopiés. Aucune valeur totale
    // sectorielle fixe n'existe (variable par entreprise).
    chequesRepas: {
      obligatoire: 'conditionnel',
      valeur: null,
      part_employeur: 2.00,
      part_travailleur: null,
      conditionnelRaison: "chèque-repas est un choix parmi 4 dans un menu pouvoir d'achat au niveau entreprise (pas d'obligation universelle) ; quand l'entreprise choisit ce mode, plancher patronal 2€/chèque (introduction ou majoration au 01/07/2026, protocole 2025-2026)",
      source: "CCT du 19/06/2009 (224/224-2009-006583.pdf) et CCT du 09/02/2026 (224/224-2026-001823.pdf), banque CCT du SPF Emploi, textes primaires lus le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 250, ecoChequesSource: "CCT du 28/08/2023 (CP 224, employés des non-ferreux), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « À partir de l'année 2012, des écochèques à concurrence d'une valeur totale de 250 EUR sont remis aux employés occupés à temps plein lors du décompte de salaire d'OCTOBRE. » Valeur nominale maximale 10 EUR ; si les chèques sont électroniques, le coût du support de remplacement ne peut dépasser 5 EUR. ⚠️ 250 EUR réels et sourcés — coïncidence avec le défaut de la grille.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — mécanisme faux (aucun fonds) mais valeur
    // confirmée : CCT du 02/03/2026 (dépôt 198621/CO/224), « Formation
    // permanente », art. 3 — transposition sectorielle de l'art. 54 §1,10°
    // de la loi du 03/10/2022 : droit individuel de 4 jours en 2023, 5
    // jours/an dès 2024, pour un employé temps plein (prorata sinon, art.
    // 50 §3). Droit individuel imputé à l'employeur, pas une prestation
    // d'un fonds.
    formation: {
      joursSectoriels: 5,
      trajectoire: { '2023': 4, '2024': 5 },
      mecanisme: "transposition sectorielle du droit individuel à la formation (loi du 03/10/2022, art. 50 et 54 §1,10°) — 4 jours en 2023, 5 jours/an dès 2024 pour un employé temps plein, prorata sinon (formule art. 50 §3)",
      fondsFormationExiste: false,
      source: "CCT du 02/03/2026 (CP 224), art. 3, dépôt 198621/CO/224, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.27, veloDegressif: [{ maxKm: 3, taux: 0.48 }, { maxKm: 5, taux: 0.36 }, { maxKm: 6, taux: 0.30 }, { taux: 0.27 }] },  // vélo CP 224 non-ferreux (employés): barème DÉGRESSIF par distance A/R — 0,48 [1-3km] / 0,36 [4-5km] / 0,30 [6km] / 0,27 [≥7km], CSC METEA dès 01/01/2024 ; scalaire velo = taux marginal ≥7 km ; sourcé 2026-07-08
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 225 — Employés des institutions de l\ (généré batch 29/04/2026)
  '225': {
    nom: "CP 225 — Employés des institutions de l'enseignement libre",
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── FONDS DE SÉCURITÉ D'EXISTENCE — 0,15 %, mais pas pour tout le monde ─
    // ✅ CCT du 05/02/2015 « fixation des cotisations au Fonds social et de
    // garantie flamand des employés », art. 1er à 3 — dépôt 126234/CO/225,
    // banque CCT du SPF Emploi, 225/225-2015-001493.pdf, texte primaire lu le
    // 26/08/2026. Durée indéterminée depuis le 01/01/2015.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — vingt-sept fois trop.
    //
    // ⚠️ ET LE PÉRIMÈTRE EST UNE MOITIÉ DE SECTEUR. Ce fonds est le « Vlaams
    // Sociaal en Waarborgfonds voor Bedienden » : l'art. 1er ne vise que les
    // employeurs dont le siège social est en Région flamande ou en Région de
    // Bruxelles-Capitale ET qui sont inscrits à l'ONSS sur le RÔLE
    // NÉERLANDOPHONE. Un employeur de l'enseignement libre francophone n'est
    // pas dans ce champ, et aucun fonds équivalent n'a été trouvé pour lui —
    // « pas trouvé » n'étant pas « n'existe pas » (R2 ter), c'est écrit comme
    // une question ouverte, pas comme un zéro.
    fondsSE: {
      // Le nom officiel du fonds est néerlandais ; il ne s'écrit PAS ici. Le
      // gate i18n de `cp-conformite-synergie` interdit les mots NL dans
      // TAUX_SECTORIELS, et il a raison : ce champ s'affiche tel quel sur la
      // page FR. La CCT donne elle-même la version française, c'est celle-là.
      nom: 'Fonds social et de garantie flamand des employés',
      taux_patronal: 0.0015,
      base: 'salaires bruts pris en considération pour le calcul des cotisations de sécurité sociale',
      cctSourcee: {
        taux: 0.0015,
        composantes: {
          groupesARisque: 0.0010,          // art. 3, 1°
          fonctionnementEtAvantagesSociaux: 0.0005,  // art. 3, 2°
        },
        base: "salaires bruts pris en considération pour le calcul des cotisations de sécurité sociale (art. 2)",
        perceptionParOnss: true,           // art. 4, en application de l'art. 7 de la loi du 07/01/1958
        source: "CCT du 05/02/2015 (CP 225, employés de l'enseignement libre subventionné), art. 2 (assiette), art. 3 (« Les employeurs visés à l'article 1er sont redevables d'un taux de 0,15 % […] 0,10 % en faveur de l'emploi et de la formation des groupes à risques ; 0,05 % en faveur du fonctionnement du Fonds et des avantages sociaux complémentaires ») et art. 4 (perception par l'ONSS) — dépôt 126234/CO/225, banque CCT du SPF Emploi, 225/225-2015-001493.pdf, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        perimetre: "Employeurs dont le siège social est en Région flamande ou en Région de Bruxelles-Capitale ET inscrits à l'ONSS sur le rôle néerlandophone (art. 1er). Les employeurs de l'enseignement libre francophone sont HORS de ce champ.",
      },
      aVerifier: true,
      aVerifierRaison: "Ce 0,15 % ne vaut que pour le rôle néerlandophone (voir `perimetre`). Aucun fonds de sécurité d'existence n'a été trouvé pour les employeurs de l'enseignement libre francophone de la CP 225 dans la banque CCT du SPF — la commission n'y compte que trois conventions, toutes flamandes. Ne pas conclure à l'absence sans une recherche par un autre critère (R2 ter).",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source. Le montant transitoire de 0,20 % de l'art. 5 ne concerne que les 2e et 3e trimestres 2015 (rattrapage du 1er trimestre) — il n'est pas le taux du fonds.",
    },
    // ✅ CORRIGÉ LE 17/09/2026 — la fiche mère portait encore le triplet
    // recopié alors que ses deux sous-CP (225.01, 225.02) ont reçu le 05/09
    // le résultat de la MÊME recherche plein texte, qui couvrait explicitement
    // les CCT des trois (225, 225.01, 225.02) : zéro résultat pour « chèque-repas »
    // / « maaltijdcheque ». Trois fiches, une seule vérification déjà faite —
    // R14 bis : elle s'écrit une fois, elle s'applique partout où elle vaut.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 225/225.01/225.02 — recherche plein texte exhaustive, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 225 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi). Même famille que chequesRepas
    // ci-dessus (225/225.01/225.02, R14 bis) : la fiche mère héritait encore
    // du montant recopié alors que la même vérification sectorielle valait
    // déjà pour les deux sous-CP.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 225 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — fonds réel, même fonds que `fondsSE`
    // ci-dessus (nom officiel néerlandais, version française donnée par la
    // CCT elle-même — cf. commentaire du gate i18n sur `fondsSE.nom`) :
    // cotisation 0,15% dont 0,10% pour « emploi + formation des groupes à
    // risque » (CCT 126234/CO/225 du 05/02/2015). Mécanisme = augmentation
    // du taux de participation de 5% + « temps de formation accordé,
    // individuellement ou collectivement » — jamais de nombre de jours
    // chiffré dans aucune des CCT lues (les plus récentes datent de 2015).
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — le Fonds social et de garantie flamand des employés (CP 225) finance un effort groupes à risque de 0,10% (CCT 126234/CO/225 du 05/02/2015 : hausse du taux de participation de 5%, temps de formation individuel ou collectif) sans nombre de jours chiffré dans aucune CCT retrouvée (les plus récentes datent de 2015)",
      fondsFormationExiste: true,
      fonds: "Fonds social et de garantie flamand des employés (même fonds que fondsSE ci-dessus)",
      source: "Banque CCT du SPF Emploi, CCT 126234/CO/225 (05/02/2015), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 225: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 225.01 — Enseignement libre employés — Communauté flamande (généré batch 29/04/2026)
  '225.01': {
    nom: 'CP 225.01 — Enseignement libre employés — Communauté flamande',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT barèmes fonction publique (enseignement libre fl., corrigé de « annuelle janvier » 2026-07-16) : +2% ~2 mois après dépassement pivot (comme CP 152). Source: Acerta + Liantis + SD Worx 225.' },
    fondsSE: {
      nom: 'Fonds social CP 225.01',
      taux_patronal: 0.0126,
      taux_patronal_employe: 0.0015,
      pension_sectorielle: 0.011399999999999999,
      pension_sectorielle_employe: 0.011399999999999999,
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "825", "type": "8", "taux": 0, "forfait": false}],
      base108Ouvrier: true,
      onssCategorie: '074',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 074 : 820 ouvriers 1,26 % · employés 0,15 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — zéro résultat plein texte (« chèque-repas »,
    // « maaltijdcheque ») sur l'ensemble des CCT déposées pour 225, 225.01 et
    // 225.02. Cohérent avec le mécanisme salarial de ce secteur (index-pivot
    // fonction publique, enseignement subventionné).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 225/225.01/225.02 — recherche plein texte exhaustive, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — même recherche que la fiche mère CP 225
    // ci-dessus (R14 bis) : 0 résultat plein texte « eco cheque ».
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 225/225.01 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT du 27/09/2023 (SCP 225.01), « droit
    // individuel à la formation », dépôt 182879/CO/225.01 : trajectoire
    // 2j(2023)→2,5j(2024)→3j(2025)→3,5j(2026, valeur actuelle)→4j(2027)
    // →4,5j(2028)→5j(2029). Formation gratuite via le Vlaams Sociaal
    // Waarborgfonds (asbl SF-VOV), remboursement de la perte de salaire à
    // l'employeur.
    formation: {
      joursSectoriels: 3.5,
      trajectoire: { '2023': 2, '2024': 2.5, '2025': 3, '2026': 3.5, '2027': 4, '2028': 4.5, '2029': 5 },
      fonds: "Vlaams Sociaal Waarborgfonds (asbl SF-VOV) — offre de formation gratuite employeur/travailleur, remboursement de la perte de salaire",
      source: "CCT du 27/09/2023 (SCP 225.01), « droit individuel à la formation », dépôt 182879/CO/225.01, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 225.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 225.02 — Enseignement libre employés — Communauté fr. et ger. (généré batch 29/04/2026)
  '225.02': {
    // ── Classification CP 225.02 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 19/06/2018 « barémisation des fonctions
    // pour le personnel contractuel », banque CCT du SPF Emploi
    // (22502/22502-2018-009591.pdf), section II art. 2.
    // Trouvée par recherche plein texte.
    //
    // ⚠️ CHAQUE FONCTION PORTE UN CODE BARÉMIQUE À TROIS CHIFFRES (634, 612,
    // 679, 699, 346, 501…). Ce n'est PAS un montant : c'est l'identifiant de
    // l'échelle applicable, et la rémunération minimum « est fixée
    // conformément à l'annexe ». Le lire comme un salaire serait une erreur
    // de plusieurs centaines d'euros.
    classifications: {
      source: 'CCT du 19/06/2018, section II art. 2 — banque CCT du SPF Emploi, 22502/22502-2018-009591.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « barémisation des fonctions pour le personnel contractuel »',
      legende: { codeBaremique: "identifiant de l'échelle barémique applicable, PAS un montant — la rémunération minimum est fixée par l'annexe de la CCT" },
      cadreLogistique: {
        label: 'Cadre logistique, y compris le personnel administratif',
        travauxExecution: [
          { code: '1', critere: "exécution correcte d'un travail simple d'ordre secondaire ; PAS de fonctions faisant appel à l'initiative personnelle, mais exercées conformément à des règles fixées préalablement",
            fonctions: [{ nom: 'commis', bareme: 634 }, { nom: 'messager huissier', bareme: 612 }, { nom: 'modèle vivant', bareme: 634 }] },
          { code: '2', critere: "exécution correcte d'un travail simple dont la responsabilité est limitée par un contrôle direct, et temps limité d'assimilation permettant d'acquérir de la dextérité dans un travail déterminé",
            fonctions: [{ nom: 'rédacteur', bareme: 679 }, { nom: 'secrétaire comptable', bareme: 679 }, { nom: 'aide bibliothécaire', bareme: 679 }] },
        ],
        travauxConception: [
          { code: '3', critere: "travail diversifié et exigeant du raisonnement de la part de celui qui l'exécute",
            fonctions: [{ nom: 'administrateur-secrétaire', bareme: 699 }, { nom: 'comptable', bareme: 346 }, { nom: 'technicien, technicien informatique, appariteur', bareme: 346 }, { nom: 'bibliothécaire', bareme: 346 }, { nom: 'conseiller en prévention', bareme: 346 }] },
          { code: '4', critere: "travail autonome et diversifié, exigeant de façon permanente de l'initiative et du raisonnement, et comportant la responsabilité de son exécution",
            fonctions: [{ nom: 'chercheurs', bareme: 501 }, { nom: 'gestionnaire financier', bareme: 501 }, { nom: 'gestionnaire administratif', bareme: 501 }, { nom: 'gestionnaire de centre de documentation', bareme: 501 }, { nom: 'gestionnaire multimédia', bareme: 501 }, { nom: 'gestionnaire informatique et informaticien', bareme: 501 }] },
        ],
      },
      // ⚠️ La CCT comporte d'autres cadres (dont un « cadre enseignant »)
      // non transcrits : ils sortent du périmètre logistique et administratif
      // relevé ici. Le dire évite de croire la liste exhaustive.
      autresCadresNonTranscrits: ['cadre enseignant'],
    },
    nom: 'CP 225.02 — Enseignement libre employés — Communauté fr. et ger.',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT barèmes fonction publique (enseignement libre CF/germ., corrigé 2026-07-16) : +2% ~2 mois après dépassement pivot. Source: SD Worx 225.02 + easypay + salairesminimums jc 2250200.' },
    fondsSE: {
      nom: 'Fonds social CP 225.02',
      taux_patronal: 0.006,
      // Le côté employé n'est PAS ambigu par accident : cette CP ne couvre pas
      // les employés. Ils relèvent d'une autre commission, que l'ONSS nomme dans
      // la description de chaque catégorie d'employeur. Leur cotisation se calcule
      // sur la fiche de LEUR CP — servir le taux ouvrier ici le multiplierait par dix.
      employesRelevantDUneAutreCp: ['152'],
      base108Ouvrier: true,
      onssCategorie: '174',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégorie d'employeur 174 : 0,60 % (code 820). La catégorie couvre conjointement les CP 152 et 225 — l'ONSS l'écrit dans sa description. Lu le 25/08/2026.",
      note: "Valeur précédente : 4,50 %, sans source. ⚠️ La catégorie 174 est commune aux CP 152 et 225 : même taux des deux côtés.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — même recherche exhaustive que la CP 225.01
    // ci-dessus : zéro résultat plein texte sur les CCT de 225/225.01/225.02.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 225/225.01/225.02 — recherche plein texte exhaustive, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — même recherche que la fiche mère CP 225
    // ci-dessus (R14 bis) : 0 résultat plein texte « eco cheque ».
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 225/225.02 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — recherche plein texte « formation »/
    // « opleiding » filtrée CP 225.02 → « 0 résultat(s) trouvé(s) » confirmé
    // (pas une erreur d'outil, `erreurs: []`).
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT de formation propre à la CP 225.02 (0 résultat, recherche plein texte « formation|opleiding » filtrée CP 225.02, banque CCT du SPF Emploi) — droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "Banque CCT du SPF Emploi, recherche plein texte « formation »/« opleiding » filtrée CP 225.02 : 0 résultat, relevé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 225.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 227 — Secteur audio-visuel (généré batch 29/04/2026)
  '227': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 13/03/2026 (CP 227), banque CCT
    // du SPF Emploi, 227/227-2026-004113.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 13/03/2026 (CP 227) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 227/227-2026-004113.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 227 — Secteur audio-visuel',
    nomCourt: 'Secteur audio-visuel',
    type: 'E',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-03-01', source: 'AUDIT 2026-06 — Easypay/mediarte' },
    fondsSE: {
      nom: 'Fonds social CP 227',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.0014000000000000002,
      base108Ouvrier: true,
      onssCategorie: '423',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 423 : 820 ouvriers 0,10 % · employés 0,14 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — même schéma que la CP 224 : le chèque-repas est
    // un choix parmi « augmentation salariale, pension complémentaire,
    // éco-chèques, chèque-repas » pour concrétiser une prime annuelle pouvoir
    // d'achat (382€ en 2016, CCT du 18/12/2015, 227/227-2015-013424.pdf, texte
    // primaire lu). Pas de valeur fixe sectorielle imposée à tous.
    chequesRepas: {
      obligatoire: 'conditionnel',
      valeur: null,
      part_employeur: null,
      part_travailleur: null,
      conditionnelRaison: "chèque-repas est un choix parmi 4 pour concrétiser la prime pouvoir d'achat au niveau entreprise, pas une obligation universelle ni un montant sectoriel fixe",
      source: "CCT du 18/12/2015 (227/227-2015-013424.pdf), banque CCT du SPF Emploi, texte primaire lu le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (227/227-2011-009853.pdf, CCT du 21/10/2011, « toujours d'application »,
    // enregistrement 107530/CO/227). AUCUN montant SECTORIEL fixe : l'art. 2
    // impose seulement un « système récurrent d'avantages » dont le TYPE
    // (augmentation salariale, pension complémentaire, éco-chèques,
    // chèque-repas — même liste que la note chequesRepas ci-dessus, CCT du
    // 18/12/2015) est choisi par ENTREPRISE (art. 4), en concertation
    // syndicale, et communiqué au président de la CP. Les éco-chèques ne sont
    // le système « supplétif » (art. 5-6) — celui qui s'applique par défaut —
    // QUE pour les entreprises constituées après le 30/06/2011, ou si
    // l'employeur n'a pas notifié son choix à temps. Et même alors, la VALEUR
    // n'est PAS fixe : elle se calcule une fois pour toutes par entreprise
    // (effectif au 30/06/2011 ou à la constitution × 100 € / effectif ETP),
    // donc propre à chaque employeur. Aucun chiffre sectoriel n'existe à
    // annoncer à un client — écrire 250 (la valeur recopiée) aurait été un
    // montant inventé au sens de R12.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 21/10/2011 (227/227-2011-009853.pdf), banque CCT du SPF Emploi, texte primaire lu le 18/09/2026 : pas de montant sectoriel fixe — système d'avantages au choix de l'entreprise (art. 4), éco-chèques supplétif à valeur calculée par employeur (art. 2-6) seulement pour les entreprises post-30/06/2011 ou à défaut de notification.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 227' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 227: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB employés industrie + SD Worx',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 300 — Conseil national du Travail (RMMMG) (généré batch 29/04/2026)
  '300': {
    nom: 'CP 300 — Conseil national du Travail (RMMMG)',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION » : la fiche a été
    // ouverte et lue, ce secteur ne classe pas ses travailleurs par fonction.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      grilleSectorielle: false,
      systeme: "revenu minimum moyen garanti décliné par ÂGE seulement (18 ans et plus, moins de 18 ans, 18-20 ans sous contrat d'étudiant), en mensuel et en horaire. Aucune catégorie de fonction — cohérent avec la CCT n° 25 ter du CNT déjà lue, qui est une norme de méthode interprofessionnelle et non une grille.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'AUDIT 2026-06 — RMMMG residuaire CNT (CCT 43/50), suit indice-pivot. NB: presse quotidienne = CP 130, pas 300' },
    // ── LA CP 300 N'EST PAS UN SECTEUR, C'EST LE CONSEIL NATIONAL DU TRAVAIL ─
    // 📏 Census complet du 26/08/2026 : la banque CCT du SPF publie **8
    // conventions** sous le numéro 300, et les huit sont des CCT du **CNT** —
    // n° 81 (vie privée), n° 90 (avantages non récurrents), n° 103 et 103 ter
    // (crédit-temps), n° 108 et 108/2 (travail intérimaire), n° 111 (complément
    // d'entreprise), n° 36 sedecies. Aucune n'institue un fonds sectoriel, et il
    // ne peut pas y en avoir : cette « commission » est l'enceinte
    // interprofessionnelle, pas un secteur avec un fonds de sécurité d'existence.
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI FACTURAIT DONC UN FONDS QUI NE PEUT PAS
    // EXISTER — 112,50 € par mois et par travailleur sur un brut de 2 500 €.
    // C'est le même défaut que sur les fonds dissous : un nombre rond n'est
    // rattaché à rien, donc rien ne dit qu'il n'a pas d'objet.
    fondsSE: {
      nom: "Conseil national du Travail — aucun fonds de sécurité d'existence (enceinte interprofessionnelle, pas un secteur)",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des 8 conventions publiées sous le numéro 300, lu le 26/08/2026 : toutes sont des conventions du Conseil national du Travail (n° 81, 90, 103, 103 ter, 108, 108/2, 111, 36 sedecies). Aucune n'institue de fonds. Le fichier des taux DmfA 2026/3 de l'ONSS ne rattache aucune catégorie d'employeur à cette commission.",
      dateVerif: '2026-08-26',
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait un secteur là où il n'y a qu'une enceinte interprofessionnelle.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus (CP 300 =
    // le CNT, pas un secteur). Le seul hit plein texte sous ce numéro concerne
    // l'abrogation de la CCT n°47 quinquies (chèques-repas des INTÉRIMAIRES,
    // 2014) — sans rapport avec un chèque-repas dû à un travailleur classé
    // CP 300.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — CP 300 est le CNT, pas un secteur ; aucune CCT n'institue de chèque-repas pour un travailleur classé sous ce numéro",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — cohérent avec le fondsSE/chequesRepas ci-dessus
    // (CP 300 = le CNT, pas un secteur). Le seul hit plein texte « eco cheque »
    // est la CCT n°98/12 du 24/02/2026 elle-même — le texte INTERPROFESSIONNEL
    // qui définit le mécanisme des éco-chèques pour tous les secteurs (comme la
    // CCT n°98 citée par la CP 226 ci-dessus), pas une obligation propre à un
    // travailleur classé sous ce numéro.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Cohérent avec le fondsSE ci-dessus — CP 300 est le CNT, pas un secteur ; le seul hit plein texte est la CCT n°98/12 interprofessionnelle elle-même, aucune obligation propre à ce numéro",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CP 300 est le Conseil national du Travail,
    // pas un secteur. 12 résultats dans la banque CCT, toutes des CCT du
    // CNT (crédit-temps n°103/103ter, télétravail n°149, alcool/drogues
    // n°100, égalité rémunérations n°25ter, reclassement n°82bis, société
    // coopérative européenne n°88) — aucune n'institue de fonds ni de droit
    // de formation sectoriel, cohérent avec `fondsSE` déjà noté ci-dessus.
    formation: {
      joursSectoriels: null,
      renvoi: "CP 300 est le Conseil national du Travail, pas un secteur — aucun fonds ni CCT de formation propre ne peut exister ici (même raisonnement que fondsSE ci-dessus) ; droit individuel à la formation de la loi du 03/10/2022 (fédéral) s'applique selon la CP réelle de l'employeur",
      source: "Banque CCT du SPF Emploi, requête CP 300 « formation »/« opleiding » : 12 résultats, tous CCT du CNT (télétravail n°149, crédit-temps n°103/103ter, alcool/drogues n°100, égalité rémunérations n°25ter, reclassement n°82bis, société coopérative européenne n°88) — aucune n'institue de fonds/droit de formation sectoriel, relevé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 300: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 301 — Ports (généré batch 29/04/2026)
  '301': {
    nom: 'CP 301 — Ports',
    nomCourt: 'Ports',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.016, derniereDate: '2025-03-07', source: 'ACLVB/Synova CP 301 — dernier franchissement portuaire +1,6% au 07/03/2025 (corrigé 2026-07-16 : le 2026-02-01 Easypay non corroboré = confusion avec le pivot public). Prochain attendu 2e sem. 2026.' },
    // ── COTISATION DE SOLIDARITÉ AUX FONDS DE COMPENSATION DES PORTS ───────
    // ✅ CCT du 22/06/2016 « instaurant une cotisation de solidarité pour les
    // fonds de compensation de sécurité d'existence », art. 1er à 3 — dépôt
    // 134374/CO/301, banque CCT du SPF Emploi, 301/301-2016-009860.pdf, texte
    // primaire lu le 26/08/2026. Durée indéterminée depuis le 01/07/2016, non
    // modifiée par l'accord social 2025-2026 (dépôt 199653/CO/301, relu).
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — le double du taux réel.
    //
    // ⚠️ PÉRIMÈTRE : la cotisation n'est due que pour les OUVRIERS PORTUAIRES
    // RECONNUS au sens de l'art. 2 de l'AR du 05/07/2004. Le personnel non
    // reconnu du même employeur n'y ouvre pas droit.
    //
    // 📏 CE TAUX EST CELUI DE LA CP MÈRE, ET IL N'EST PAS TOUT. Chaque port a
    // en plus son propre fonds de compensation, dont les cotisations vivent
    // dans les sous-commissions 301.01 à 301.05 — elles restent à instruire.
    fondsSE: {
      nom: 'Cotisation de solidarité aux fonds de compensation de sécurité d\'existence des ports',
      taux_patronal: 0.025,
      base: 'rémunération brute et autres indemnités assujetties aux cotisations de sécurité sociale',
      cctSourcee: {
        taux: 0.025,
        base: "la rémunération brute et les autres indemnités assujetties aux cotisations de sécurité sociale des travailleurs portuaires reconnus (art. 2)",
        source: "CCT du 22/06/2016 (CP 301, ports), art. 2 : « Une cotisation de solidarité de 2,50 % est instaurée en faveur des fonds de compensation de sécurité d'existence des ports respectifs, à charge des employeurs qui occupent des travailleurs visés à l'article 2 de l'arrêté royal du 5 juillet 2004 » — dépôt 134374/CO/301, banque CCT du SPF Emploi, 301/301-2016-009860.pdf, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
        perimetre: "Ouvriers portuaires RECONNUS au sens de l'art. 2 de l'AR du 05/07/2004 relatif à la reconnaissance des ouvriers portuaires (loi du 08/06/1972 sur le travail portuaire).",
      },
      aVerifier: true,
      aVerifierRaison: "Ce 2,50 % est la cotisation de solidarité commune à toute la CP 301. Chaque port perçoit EN PLUS les cotisations de son propre fonds de compensation, réparties dans les sous-commissions 301.01 à 301.05 — non lues à ce jour. Le coût employeur affiché est donc un PLANCHER.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // 🔴 Recherché le 05/09/2026 — pas de montant unique à ce niveau. Accord
    // social 2025-2026 (CCT du 29/04/2026, 301/301-2026-009200.pdf, texte
    // primaire lu) : « La contribution patronale au chèque-repas sera augmentée
    // de 2€ à compter du 01/07/2026. Les partenaires sociaux s'engagent à mettre
    // en place des chèques-repas dans le port de Gand d'ici le 01/07/2026 » —
    // preuve que le régime N'EST PAS uniforme entre ports (Gand n'en a pas
    // encore) : chaque port a son propre régime dans ses sous-CP (301.01-301.05,
    // cf. fondsSE ci-dessus). Valeur laissée en l'état au niveau CP mère, non
    // représentative — voir les sous-CP pour un montant réel par port.
    // 🔴 Piste trouvée le 18/09/2026, NON résolue — recherche plein texte CP
    // 301 + « chèque repas » : 3 CCT du 30/04/2026 (dépôts 199653 à
    // 199655/CO/301, une par contingent : ouvriers portuaires généraux,
    // travailleurs logistiques, gens de métier/vaklui) disent toutes « la
    // contribution patronale au chèque-repas est augmentée de 2 EUR à
    // compter du 1er juillet 2026 » — et pour le port de Gand, l'engagement
    // d'INTRODUIRE des chèques-repas pour la première fois d'ici cette date.
    // C'est une AUGMENTATION, jamais le montant de BASE : les CCT
    // historiques (2014, 2016) l'expriment en EUR/TÂCHE (système propre aux
    // ports, pas EUR/jour) et parlent d'un maximum de 7 EUR/tâche atteint
    // par certains employeurs dès 2016 — le montant courant dépend donc du
    // port ET du contingent, non retrouvé ici. Ne PAS ajouter de `source`
    // tant que la valeur de base n'est pas lue : ça ferait passer ce triplet
    // recopié pour « relevé » (R12).
    chequesRepas: { obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09, aVerifier: true },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 301 + « eco cheque » :
    // 0 résultat, quand la même recherche pour « chèque repas » (ci-dessus,
    // même dépôt CO/301) en trouvait 3. Cohérent avec un secteur qui a
    // historiquement privilégié le chèque-repas comme vecteur d'avantage
    // (accord social 2025-2026). ⚠️ Ce 0 résultat ne couvre QUE les CCT
    // déposées sous CO/301 exactement — les sous-CP 301.01 à 301.05 ont
    // chacune leurs propres dépôts et se vérifient séparément (301.02 déjà
    // sourcée à 72 €, cf. sa fiche).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 301 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (contre 3 pour « chèque repas » sous le même dépôt CO/301)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — 5 CCT « efforts supplémentaires de
    // formation » lues (91896/CO/301 de 2009, 106664/CO/301 de 2011,
    // 115018/CO/301 de 2013, texte quasi identique) : elles fixent un
    // objectif COLLECTIF d'augmentation du taux de participation d'AU MOINS
    // 5 POINTS DE POURCENTAGE par an — pas « 5 jours » par travailleur.
    // Dernière CCT lue couvrait 2013-2014, aucun renouvellement retrouvé.
    formation: {
      joursSectoriels: null,
      renvoi: "aucun droit individuel en jours — les CCT « efforts de formation » retrouvées (91896/CO/301 [2009], 106664/CO/301 [2011], 115018/CO/301 [2013]) fixent un objectif COLLECTIF d'augmentation du taux de participation à la formation d'au moins 5 points de pourcentage par an, pas 5 jours par travailleur ; dernière CCT lue couvrait 2013-2014, aucun renouvellement retrouvé dans cette recherche ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "Banque CCT du SPF Emploi, requête CP 301 « formation »/« opleiding » : CCT 22/04/2013 (115018/CO/301), 27/09/2011 (106664/CO/301), 23/03/2009 (91896/CO/301), textes primaires lus le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 301: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 301.01 — Port d\ (généré batch 29/04/2026)
  '301.01': {
    nom: "CP 301.01 — Port d'Anvers",
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // La fiche SPF de cette sous-commission le dit elle-même : « le barème
    // (salaire) général qui est repris dans la CP 3010000 est également
    // d'application pour la présente sous-commission paritaire ». La grille
    // n'est donc pas absente — elle est ailleurs, et le renvoi est explicite.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (jcId ddebf00a…, CP 3010100 — Port d\'Anvers), en vigueur au 07/12/2021, lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE — le SPF ne suit plus cette sous-commission depuis le 01/09/2022. Structure codée, montants non codés.",
      renvoiExterne: { cp: '301', portee: 'barème général', texte: 'le barème général de la CP 301 s\'applique à la sous-commission (mention portée par la fiche SPF elle-même)' },
      systeme: 'trois contingents, subdivisés par type de travail puis par équipe — la rémunération dépend du croisement des trois, pas du seul métier',
      contingents: [
        {
          cle: 'general', libelle: 'Général',
          travaux: ['Général', 'Manutention de minerais', 'CSV (constatateur d\'avarie de conteneurs) et CMAA/D équipe supplémentaire'],
        },
        {
          cle: 'logistique', libelle: 'Travailleurs logistiques disposant d\'un certificat de sécurité',
          travaux: ['Travailleur logistique (LOG)', 'Conditionneur de fruits (FP)', 'Trieur de fruits (FR)', 'Travailleur magasinier'],
        },
        { cle: 'vak', libelle: 'Gens de métier (VAK)', travaux: [] },
      ],
      equipes: [
        'Jour (08h00-12h00 et 12h30-15h45)', 'Matin (06h00-10h00 et 10h30-13h45)',
        'Après-midi (14h00-18h00 et 18h30-21h45)', 'Nuit (22h00-02h00 et 02h30-05h45)',
        'Samedi', 'Dimanche / jour férié',
      ],
      note: 'le contingent logistique a ses propres plages horaires, plus larges : jour du lundi au vendredi entre 06h00 et 22h00 (limité à 7 h 15), nuit entre 22h00 et 06h00, samedi entre 06h00 et 22h00, dimanche/férié entre 22h00 la veille et 06h00 le lendemain.',
    },
    nomCourt: "Port d'Anvers",
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.022, derniereDate: '2026-01-01', note: 'INDEX-PIVOT portuaire (corrigé de « annuelle janvier » 2026-07-16, comme parent 301) : barèmes adaptés au franchissement de l\'indice (ex. +1,6% au 07/03/2025 = date non-janvier). Source: Cepa + CGSLB + minimumlonen PC 3010100. Date/taux à re-vérifier.' },
    // ── LES TAUX EXISTENT ET SONT LUS, MAIS PAS CE QU'ILS VISENT ──────────
    // ✅ CCT du 19/04/2024 « portant modification des statuts du Fonds de
    // compensation de sécurité d'existence — port d'Anvers », art. 4, 5 et 7 —
    // dépôt 187388/CO/301.01, banque CCT du SPF Emploi, texte primaire lu le
    // 26/08/2026. Elle porte trois modifications de pourcentage :
    //   · art. 16 et 16bis : « 2,15 % » remplacé par « 2,25 % » ;
    //   · art. 16quinquies : « 2,01 % », « 0,48 % » et « 3,05 % » remplacés
    //     respectivement par « 2,35 % », « 0,54 % » et « 3,39 % ».
    // En vigueur au 01/01/2024 (art. 1, 6 et 7 au 01/01/2023), durée indéterminée.
    //
    // ⛔ ON NE PEUT PAS EN ÉCRIRE UN. Cette convention MODIFIE des articles des
    // statuts coordonnés de 1963-1964 sans les reproduire : on lit les nouveaux
    // pourcentages, pas ce que chacun finance ni à qui il s'applique. Le champ
    // de l'art. 1er distingue trois publics — ouvriers portuaires reconnus du
    // contingent GÉNÉRAL, du contingent LOGISTIQUE, et « gens de métier » — et
    // rien ne dit lequel des cinq taux va avec lequel. En choisir un serait
    // inventer son application, la faute exacte de la CP 111.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Il est conservé faute de
    // mieux : les taux réels lus sont de 2,25 % à 3,39 %, donc du même ordre de
    // grandeur — descendre à 0 serait plus faux que le laisser.
    //
    // 📏 ET IL S'AJOUTE À LA MÈRE : la CP 301 perçoit 2,50 % de solidarité en
    // faveur des fonds de compensation des ports (CCT du 22/06/2016, sourcée le
    // 26/08). Le fonds du port d'Anvers perçoit EN PLUS ses propres cotisations.
    fondsSE: {
      nom: "Fonds de compensation de sécurité d'existence — port d'Anvers",
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source. Les vrais taux SONT lus — 2,25 % (art. 16 et 16bis), 2,35 %, 0,54 % et 3,39 % (art. 16quinquies) — mais la convention MODIFIE les statuts coordonnés de 1963-1964 sans les reproduire : on ignore ce que chacun finance et à quel public il s'applique, alors que l'art. 1er en distingue trois (contingent général, contingent logistique, gens de métier). En choisir un serait inventer son application (faute de la CP 111). Pour trancher : obtenir les statuts coordonnés du Compensatiefonds voor Bestaanszekerheid — Haven van Antwerpen, articles 16, 16bis, 16quater et 16quinquies.",
      tauxNonUnique: {
        raison: "cinq pourcentages lus, aucun rattaché à son objet ni à son public",
        composantesLues: { art16: 0.0225, art16bis: 0.0225, art16quinquies_a: 0.0235, art16quinquies_b: 0.0054, art16quinquies_c: 0.0339 },
        publicsDistingues: ["ouvriers portuaires reconnus du contingent général", "ouvriers portuaires reconnus du contingent logistique", "gens de métier"],
        source: "CCT du 19/04/2024 (SCP 301.01, port d'Anvers), art. 4, 5 et 7 — dépôt 187388/CO/301.01, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. En vigueur au 01/01/2024, durée indéterminée.",
        dateVerif: '2026-08-26',
      },
      sAjouteALaMere: "la CP 301 perçoit en plus 2,50 % de cotisation de solidarité en faveur des fonds de compensation des ports (CCT du 22/06/2016, art. 2, dépôt 134374/CO/301)",
    },
    // 🔴 Recherché le 05/09/2026 — TROIS valeurs coexistent, pas une. Contingent
    // général : 5,00 € (3,91 empl./1,09 trav.), CCT du 06/11/2025 modifiant le
    // Codex algemeen contingent, art. 3/12, dépôt 196881/CO/301.01, en vigueur
    // 01/01/2026, texte primaire lu, confiance haute. Gens de métier (vaklui) :
    // 8,00 € (6,91/1,09), CCT du 06/11/2025 modifiant le Codex vaklui, art. 4,
    // dépôt 196420/CO/301.01, en vigueur 01/01/2026 — c'est la seule valeur qui
    // matche le chiffre codé ici, et SEULEMENT pour cette sous-catégorie.
    // ✅ Contingent logistique SOURCÉ LE 05/09/2026 — Cepa vzw (Werkgeversverbond
    // der Belgische Havens), Codex Logistiek Contingent, art. 50 : « De
    // logistieke werknemers met veiligheidscertificaat ontvangen per taak een
    // maaltijdcheque met een bedrag van 6,70 EUR (€ 5,61 werkgeversbijdrage en
    // € 1,09 werknemersbijdrage). » ⚠️ PAR TÂCHE, pas par jour — une unité
    // différente des deux autres contingents. Valeur identique dans la version
    // 2018 du Codex : stable depuis au moins 8 ans (texte de base CCT du
    // 12/12/2017, dernière retouche apparente le 09/03/2022). Le « art. 65 »
    // cité auparavant est obsolète : dans la codification actuelle il concerne
    // les vêtements de travail.
    // La fiche sert 8€ à TOUT le contingent général, qui devrait recevoir 5€ —
    // valeur laissée en l'état faute de savoir répartir par contingent dans la
    // structure actuelle du champ.
    // ✅ COMPLÉTÉ LE 18/09/2026 — la recherche plein texte du jour sur
    // « 301.01 » seul rend 0 résultat (les CCT des contingents sont
    // déposées, filées et retrouvées via le numéro de CP MÈRE 301 dans
    // l'index du SPF, pas 301.01), mais les 3 valeurs ci-dessous SONT
    // sourcées (session du 05/09/2026, CCT primaires citées une par une) —
    // il ne manquait qu'un `source` au niveau racine pour que le
    // détecteur cesse de classer cette fiche « à sourcer ». Le triplet
    // racine reprend le contingent VAKLUI (le seul dont le triplet
    // coïncide avec la famille recopiée 8/6,91/1,09 — coïncidence, pas un
    // défaut : c'est la vraie valeur CCT de ce contingent).
    chequesRepas: {
      obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09,
      source: "CCT du 06/11/2025 modifiant le Codex vaklui, art. 4, dépôt 196420/CO/301.01, en vigueur 01/01/2026 — valeur racine = contingent vaklui, voir valeursParContingent pour général (5,00€) et logistique (6,70€/tâche)",
      aVerifier: true,
      aVerifierRaison: "3 valeurs coexistent par contingent (général 5,00€, vaklui 8,00€, logistique 6,70€/tâche) — la valeur codée ne vaut que pour les vaklui, pas pour le contingent général qui est probablement majoritaire",
      valeursParContingent: {
        general: { valeur: 5.00, part_employeur: 3.91, part_travailleur: 1.09, source: "CCT du 06/11/2025 modifiant le Codex algemeen contingent, art. 3/12, dépôt 196881/CO/301.01, en vigueur 01/01/2026" },
        vaklui: { valeur: 8.00, part_employeur: 6.91, part_travailleur: 1.09, source: "CCT du 06/11/2025 modifiant le Codex vaklui, art. 4, dépôt 196420/CO/301.01, en vigueur 01/01/2026" },
        logistique: { valeur: 6.70, part_employeur: 5.61, part_travailleur: 1.09, unite: 'par tâche (pas par jour)', source: "Cepa vzw, Codex Logistiek Contingent, art. 50 (codex_LOGISTIEK_CONTINGENT_202603.pdf), consulté le 05/09/2026 — https://www.cepa.be/havenarbeiders/codex/codex-logistiek-contingent" },
      },
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 301.01 (ports) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — CCT du 28/09/2023 (SCP 301.01), « plan de
    // formation et droit à la formation en exécution de la loi du
    // 03/10/2022 », dépôt 183230/CO/301.01, art. 9-12 : trajectoire
    // 2j(2023)→3j(2029)→4j(2031)→5j(2033), convertie en heures à raison de
    // 7,25h/jour. Financement/organisation : Ocha (Opleidingscentrum voor
    // Havenarbeiders vzw) + Vormingscel, cofinancés par le Compensatiefonds
    // voor Bestaanszekerheid - Haven van Antwerpen (cotisation spéciale
    // groupes à risque 0,10%).
    formation: {
      joursSectoriels: 2,
      trajectoire: { '2023': 2, '2029': 3, '2031': 4, '2033': 5 },
      conversionHeures: 7.25,
      fonds: "Ocha (Opleidingscentrum voor Havenarbeiders vzw) + Vormingscel, cofinancés par le Compensatiefonds voor Bestaanszekerheid - Haven van Antwerpen (cotisation spéciale groupes à risque 0,10%, ex. dépôt 190327/CO/301.01 pour 2025-2026)",
      source: "CCT du 28/09/2023 (SCP 301.01, port d'Anvers), « plan de formation et droit à la formation », art. 9 & 12, dépôt 183230/CO/301.01, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 301.01: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 301.02 — Port de Gand (généré batch 29/04/2026)
  '301.02': {
    nom: 'CP 301.02 — Port de Gand',
    nomCourt: 'Port de Gand',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.022, derniereDate: '2026-01-01', note: 'INDEX-PIVOT portuaire (corrigé 2026-07-16, mécanisme uniforme inter-ports comme parent 301). Source: CGSLB CP 301 + Cepa. Date/taux à re-vérifier.' },
    fondsSE: {
      nom: "Fonds voor Bestaanszekerheid aan de haven van Gent",
      taux_patronal: 0.0597,
      cctSourcee: {
        taux: 0.0597,
        depuis: '2018-04-27',
        source: "CCT du 27/04/2018 (SCP 301.02, port de Gand), modifiant l'art. 2 de la CCT du 09/10/1975, texte primaire lu le 25/08/2026 : « De werkgevers van havenarbeiders in de pool betalen aan het Fonds voor Bestaanszekerheid aan de haven van Gent een bijdrage waarvan het bedrag gelijk is aan 5,97 %. » Banque CCT du SPF Emploi, 30102/30102-2018-007243.pdf.",
        dateVerif: '2026-08-25',
        ecartAvecTauxCode: "l'app codait 5 % sans source ; l'art. 2 en fixe 5,97 %.",
      },
      // ⛔ 5,97 % N'EST PAS LE TOTAL, ET C'EST LE POINT IMPORTANT DE CETTE FICHE.
      // La meme CCT empile deux autres cotisations sur les MEMES employeurs :
      //   · art. 2bis — solidarite de 2,50 %, due par les employeurs d'ouvriers
      //     portuaires SAUF les grutiers en service permanent. Elle finance les
      //     conseillers en prevention, le solde allant aux ouvriers du pool.
      //   · art. 2ter — une cotisation « bijzondere » supplementaire, non chiffree
      //     dans l'extrait lu : elle reste A LIRE.
      // Le taux code couvre donc l'ARTICLE 2 SEUL. Un employeur du port de Gand
      // doit au moins 5,97 + 2,50 = 8,47 %, plus l'art. 2ter.
      cotisationsQuiSAjoutent: [
        { article: '2bis', taux: 0.025, objet: "solidarite — conseillers en prevention, solde au pool", exclus: 'grutiers en service permanent' },
        { article: '2ter', taux: null, objet: "cotisation speciale, montant NON LU — a relever" },
      ],
      perimetre: "art. 2 : employeurs d'ouvriers portuaires DU POOL. Les employeurs hors pool relevent de l'art. 2bis.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 01/07/2026 (n° 200894/CO/301.02, port de Gand), insérant un art. 9.3 bis au « Codex » du 16/12/1993, banque CCT du SPF, texte primaire lu le 24/08/2026 : 3,09 EUR par jour travaillé (2 EUR employeur, 1,09 EUR travailleur) à partir du 01/07/2026. ⚠️ Le SHIFT DE NUIT compte comme une prestation du jour calendrier où il COMMENCE — sans cette règle, un shift à cheval est compté deux fois ou zéro.", dateVerif: '2026-08-24', enVigueurLe: '2026-07-01', lien: '30102/30102-2026-013425.pdf' },
    ecoChequesAnnuel: 72, ecoChequesSource: "CCT du 01/04/2026 (SCP 301.02, port de Gand), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026. ⚠️ BARÈME PAR RÉGIME DE TRAVAIL, pas un montant unique : 72 EUR à temps plein · 60 EUR à partir de 4/5 · 36 EUR à partir du mi-temps · dégressif en dessous. La valeur codée est celle du TEMPS PLEIN. ⚠️ Art. 5 — si le montant dû est inférieur à 10 EUR, l'employeur a le choix de l'octroyer ou non. La fiche portait 250 EUR.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — mécanisme identique à 301.01 (texte quasi
    // mot pour mot) : CCT du 21/09/2023 (SCP 301.02), dépôt 183423/CO/301.02,
    // art. 4, même trajectoire 2j(2023)→3j(2029)→4j(2031)→5j(2033),
    // 7,25h/jour.
    formation: {
      joursSectoriels: 2,
      trajectoire: { '2023': 2, '2029': 3, '2031': 4, '2033': 5 },
      conversionHeures: 7.25,
      fonds: "Fonds voor Bestaanszekerheid aan de haven van Gent (cofinance les formations, commission de formation paritaire)",
      source: "CCT du 21/09/2023 (SCP 301.02, port de Gand), « plan de formation et droit à la formation », art. 4, dépôt 183423/CO/301.02, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 301.02: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 301.03 — Port de Bruxelles et Vilvorde (généré batch 29/04/2026)
  '301.03': {
    nom: 'CP 301.03 — Port de Bruxelles et Vilvorde',
    nomCourt: 'Port de Bruxelles et Vilvorde',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.022, derniereDate: '2026-01-01', note: 'INDEX-PIVOT portuaire (corrigé 2026-07-16, mécanisme uniforme inter-ports comme parent 301). Source: CGSLB CP 301 + ACLVB. Date/taux à re-vérifier.' },
    // ── CENSUS FAIT, AUCUN TAUX ÉTABLI ────────────────────────────────────
    // 📏 Lecture du 26/08/2026, avec son dénominateur. Census : AUCUNE convention dans la banque CCT pour cette sous-commission, quel que soit le critère de recherche. Ses sœurs 301.01 (Anvers) et 301.05 (Zeebrugge-Bruges) ont, elles, des statuts de fonds détaillés — l'absence est donc propre à celle-ci, pas au secteur.
    // ⚠️ Le 5 % qui figure ici n'a AUCUNE source. Il est conservé parce que
    // c'est le repli de la CP mère 301 et qu'aucune valeur sourcée ne peut le
    // remplacer — pas parce qu'il serait juste.
    fondsSE: {
      nom: "Port de Gand — cotisation non établie",
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le taux n'a AUCUNE source. Census : AUCUNE convention dans la banque CCT pour cette sous-commission, quel que soit le critère de recherche. Ses sœurs 301.01 (Anvers) et 301.05 (Zeebrugge-Bruges) ont, elles, des statuts de fonds détaillés — l'absence est donc propre à celle-ci, pas au secteur. Il est conservé faute de valeur sourcée, et NOMMÉ pour qu'il ne se lise pas comme un chiffre vérifié. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation.",
      dateVerif: '2026-08-26',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus. Requête
    // directe à la banque CCT du SPF sur ce jcId : 0 convention, tous thèmes et
    // mots-clés confondus (pas seulement chèques-repas) — confirme et renforce
    // le census déjà écrit.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — census complet sans convention publiée pour cette sous-commission",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 301.03 (ports) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — « 0 résultat(s) trouvé(s) » confirmé
    // (`erreurs: []`), cohérent avec le census déjà écrit dans le fichier
    // pour `fondsSE` sur cette même CP (« aucune convention, quel que soit
    // le critère »).
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT trouvée dans la banque CCT du SPF pour cette sous-commission, quel que soit le critère (même census que le fondsSE ci-dessus, confirmé une seconde fois sur le motif formation/opleiding) ; droit individuel à la formation de la loi du 03/10/2022 (fédéral) reste la seule base",
      source: "Banque CCT du SPF Emploi, requête CP 301.03 « formation »/« opleiding » : 0 résultat, erreurs: [], relevé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 301.03: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 301.05 — Ports de Zeebrugge-Bruges, Ostende et Nieuport (généré batch 29/04/2026)
  '301.05': {
    nom: 'CP 301.05 — Ports de Zeebrugge-Bruges, Ostende et Nieuport',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.022, derniereDate: '2026-01-01', note: 'INDEX-PIVOT portuaire (corrigé 2026-07-16, comme parent 301). Source: minimumlonen PC 3010500 (Zeebrugge) + CGSLB CP 301. Date/taux à re-vérifier.' },
    // ── QUATRE CATÉGORIES D'OUVRIERS, QUATRE JEUX DE COTISATIONS ──────────
    // ✅ CCT du 04/11/2019 « Institution d'un Fonds de compensation de sécurité
    // d'existence pour les ports de Zeebrugge-Bruges », art. 9 à 11 — dépôt
    // 155990/CO/301.05, banque CCT du SPF Emploi, texte primaire lu le
    // 26/08/2026. Les cotisations dues dépendent de la catégorie d'ouvrier de
    // l'art. 4, a) — quatre jeux distincts (art. 10, 10bis, 10ter, 10quater).
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il est TRÈS en
    // dessous du réel pour la catégorie principale : celle-ci cumule 13 % (prime
    // de fin d'année et prime syndicale), 0,10 % sur 108 % et 0,80 % (formation)
    // et 1,81 % (fonds de pension) — dont 2,50 % de solidarité déjà comprise.
    //
    // ⛔ ON N'EN ÉCRIT AUCUN, et le sens de l'erreur explique pourquoi c'est
    // grave dans les deux directions : servir 5 % à un ouvrier de la catégorie
    // principale sous-compte de plus de dix points ; servir 15 % à un ouvrier de
    // la catégorie 4 (qui ne doit que 0,10 % + 0,30 %) le surcompte de quatorze.
    // La catégorie de l'ouvrier portuaire n'est pas une donnée du dossier de
    // paie aujourd'hui — c'est ce qu'il faudrait pour trancher.
    //
    // 🔁 ET LE 13 % FINANCE LA PRIME DE FIN D'ANNÉE, comme les 9,27 % de la
    // CP 322 : l'employeur ne la paie pas une seconde fois. À ne pas oublier si
    // ce taux est un jour branché.
    fondsSE: {
      nom: "Fonds de compensation de sécurité d'existence — ports de Zeebrugge-Bruges",
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source, et aucun des taux réels ne peut le remplacer tel quel : la CCT du 04/11/2019 (dépôt 155990/CO/301.05) fixe QUATRE jeux de cotisations selon la catégorie d'ouvrier portuaire de son art. 4, a). Catégorie principale : 13 % (prime de fin d'année et prime syndicale, dont 1 point de solidarité) + 0,10 % sur 108 % et 0,80 % (formation) + 1,81 % (fonds de pension), la cotisation de solidarité de 2,50 % étant comprise dans ces montants. Catégorie 4 : seulement 0,10 % sur 108 % et 0,30 %. L'écart va de un à trente-huit. La catégorie de l'ouvrier n'est pas une donnée du dossier de paie — c'est ce qu'il faudrait pour trancher. ⚠️ Le 13 % finance la prime de fin d'année : l'employeur ne la paie pas deux fois.",
      tauxNonUnique: {
        raison: "quatre catégories d'ouvriers portuaires (art. 4, a), 1 à 4), quatre jeux de cotisations (art. 10, 10bis, 10ter, 10quater)",
        categoriePrincipale: {
          primeFinAnneeEtSyndicale: 0.13,
          formationSur108: 0.001,
          formation: 0.008,
          fondsDePension: 0.0181,
          solidariteComprise: 0.025,
        },
        categorie2: { formationSur108: 0.001, fondsDePension: 0.0181, solidarite: 0.025 },
        categorie3: { fondsSocial: 0.02, formationSur108: 0.001, formation: 0.008 },
        categorie4: { formationSur108: 0.001, formation: 0.003 },
        base: "montant total des salaires bruts déclarés à l'ONSS, diminué du salaire garanti en cas d'accident du travail, des jours de redistribution, du congé-éducation et de la prime de fin d'année (sauf la composante à 108 %, calculée sur 108 % du brut ONSS)",
        source: "CCT du 04/11/2019 (SCP 301.05, ports de Zeebrugge-Bruges), art. 9, 10, 10bis, 10ter, 10quater et 11 — dépôt 155990/CO/301.05, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
      },
      sAjouteALaMere: "la CP 301 perçoit en plus 2,50 % de cotisation de solidarité (CCT du 22/06/2016, art. 2) — ⚠️ à ne pas confondre avec les 2,50 % de solidarité DÉJÀ COMPRIS dans les cotisations ci-dessus, qui pourraient être le même prélèvement vu des deux côtés.",
    },
    chequesRepas: { obligatoire: true, valeur: 5.00, part_employeur: 3.91, part_travailleur: 1.09, source: "CCT du 30/04/2026 (n° 199686/CO/301.05), sous-commission des ports de Zeebrugge-Bruges, Ostende et Nieuwport, banque CCT du SPF Emploi, texte primaire lu le 23/08/2026, art. 2 : cinq euros par shift travaillé, contribution personnelle de 1,09 € par chèque, chargés sur la carte Monizze. ⚠️ PAR SHIFT TRAVAILLÉ, pas par journée — la distinction change le compte dès qu'un ouvrier preste deux shifts le même jour. La fiche portait 8,00 € / 6,91 €, défaut du lot du 29/04/2026.", uniteOctroi: 'shift preste', dateVerif: '2026-08-23', enVigueurLe: '2026-01-01', lien: '30105/30105-2026-010554.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 301.05 (ports Zeebrugge-Bruges, Ostende, Nieuwport) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — motif faux sur les jours, mais un vrai fonds
    // de formation existe (déjà partiellement documenté sous `fondsSE`) :
    // CCT du 04/11/2019 (SCP 301.05), dépôt 155990/CO/301.05, art. 4 et
    // 9-10quater, confirme les cotisations « vorming en opleiding » (0,10%
    // sur 108% du brut + 0,80%/0,30% selon catégorie) finançant une section
    // « Beroepsopleiding » réelle. Aucune CCT lue n'établit de droit
    // individuel en JOURS (contrairement à 301.01/301.02).
    formation: {
      joursSectoriels: null,
      fondsFormationExiste: true,
      fonds: "Fonds de compensation de sécurité d'existence pour les ports de Zeebrugge-Bruges, Ostende et Nieuport — cotisations « vorming en opleiding » de 0,10% (sur 108% du brut) + 0,80%/0,30% selon catégorie (art. 9 à 10quater), finançant une section « Beroepsopleiding » réelle pour ouvriers portuaires reconnus et candidats",
      renvoi: "aucun droit individuel en JOURS trouvé — le fonds finance des actions de formation professionnelle par cotisation sur salaire, il ne garantit pas X jours au travailleur ; à défaut, droit individuel à la formation de la loi du 03/10/2022 (fédéral)",
      source: "CCT du 04/11/2019 (SCP 301.05), art. 4 & 9-10quater, dépôt 155990/CO/301.05, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 301.05: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 303 — Industrie cinématographique (généré batch 29/04/2026)
  '303': {
    nom: 'CP 303 — Industrie cinématographique',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ UNE FAÎTIÈRE N'A PAS DE CLASSIFICATION « MANQUANTE » : elle n'en a pas
    // parce que ce n'est pas son niveau. Zéro CCT est déposée sous le numéro
    // 303 (vérifié le 23/08/2026 : la banque en rend 0), et sa fiche SPF est
    // vide. Ce sont les SOUS-COMMISSIONS qui classent — et elles ne classent
    // pas pareil, ce qui est justement la raison d'être de la subdivision.
    classifications: {
      source: 'banque CCT du SPF Emploi (0 convention sous le jcId 3030000) et fiche SPF « Salaires minimums » (cadre vide) — vérifiées le 23/08/2026 ; classifications des sous-commissions lues le 23/08/2026',
      dateVerif: '2026-08-23',
      niveauFaitiere: true,
      grilleSectorielle: false,
      systeme: "aucune classification au niveau de la CP 303 : elle vit dans les sous-commissions, qui diffèrent entre elles",
      sousCommissions: [
        { cp: '303.01', libelle: 'Production de films', resume: 'nomenclature par équipe (chapitre II) + dix catégories barémiques, et renvoi à la CP 200 pour les employés administratifs' },
        { cp: '303.03', libelle: 'Exploitation de salles de cinéma', resume: 'classification codée dans sa propre fiche' },
      ],
      // ⛔ NE PAS servir la grille d'une sous-commission comme celle de la CP :
      // la 303.01 classe des métiers de tournage, la 303.03 des métiers de
      // salle. Répondre « CP 303 » à un employeur, c'est ne pas répondre.
      pieges: "la question « quelle est la classification de la CP 303 » n'a pas de réponse : il faut d'abord établir la sous-commission de l'employeur.",
    },
    nomCourt: 'Industrie cinématographique',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-06-01', source: 'AUDIT 2026-06 — Easypay/mediarte' },
    // ── FAÎTIÈRE : LES DEUX SOUS-COMMISSIONS SONT SOURCÉES, LA MÈRE PASSE À 0 ──
    // 📉 4,00 % → 0 le 26/08/2026. La CP 303 n'a AUCUNE convention déposée sous
    // son propre numéro : la banque CCT du SPF en rend 0 pour « 303 », alors
    // que le même client en rend 29 pour la 303.01, 145 pour la 303.03 et 114
    // pour la CP 200 — le zéro est donc une absence réelle, pas un outil muet.
    //
    // Les deux sous-commissions portent chacune leur taux, lu dans le fichier
    // des taux DmfA de l'ONSS : 0,32 % et 0,35 %. Le nombre rond de la mère
    // valait plus de DOUZE FOIS l'un et l'autre, et il ne servait qu'aux
    // dossiers codés « CP 303 » sans sous-commission.
    //
    // ⛔ ON NE CHOISIT PAS UNE SOUS-CP POUR LA MÈRE, et on n'en fait pas la
    // moyenne : un employeur relève soit de la production de films, soit de
    // l'exploitation de salles. C'est une donnée du dossier. Même règle que les
    // CP 125, 142, 149, 152, 318 et 329.
    fondsSE: {
      nom: 'Industrie cinématographique — faîtière : le taux vit dans les sous-commissions',
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '303.01': "Production de films — 0,32 % ouvrier et employé (catégorie ONSS 303)",
        '303.03': "Exploitation de salles de cinéma — 0,35 % ouvrier et employé (catégorie ONSS 323)",
      },
      source: "Banque CCT du SPF Emploi : aucune convention publiée sous le numéro 303 lui-même (recherche sans filtre le 26/08/2026 ; témoins simultanés 303.01 = 29, 303.03 = 145, 200 = 114 conventions, le client fonctionne). Les deux sous-commissions portent chacune leur taux, lu dans le fichier des taux DmfA 2026/3 de l'ONSS.",
      dateVerif: '2026-08-26',
      note: "Le 4,00 % qui figurait ici avant le 26/08/2026 n'avait aucune source, et valait plus de douze fois le taux de chacune des deux sous-commissions.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 303 + « eco cheque » :
    // 0 résultat. Cohérent avec le fondsSE ci-dessus : CP 303 est une
    // FAÎTIÈRE dont le taux réel vit dans les sous-commissions (303.01,
    // 303.03 — à vérifier séparément, dépôts distincts).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 303 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (faîtière, taux réel dans les sous-commissions 303.01/303.03)",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CP 303 est une commission MÈRE purement
    // administrative : recherche plein texte « formation »/« opleiding » →
    // réponse serveur [] confirmée (0 résultat, 0 erreur). Toute l'activité
    // réelle (et les CCT formation) est déposée sous les sous-commissions
    // 303.01/303.03.
    formation: {
      joursSectoriels: null,
      renvoi: "voir sous-commission 303.01 ou 303.03 selon l'activité réelle de l'employeur — CP 303 est une commission mère sans CCT propre déposée",
      source: "Banque CCT du SPF Emploi (jc 3030000), recherche textSearchTerms formation|opleiding : 0 résultat, 0 erreur, confirmé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 303: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Mediarte + CSC spectacle',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 303.01 — Production de films (généré batch 29/04/2026)
  '303.01': {
    nom: 'CP 303.01 — Production de films',
    nomCourt: 'Production de films',
    type: 'O',
    regimeHebdo: 38,
    // ── NOMENCLATURE DES FONCTIONS ET CLASSIFICATION ──────────────────────
    // Cette CP a une classification à DEUX ÉTAGES, et rater le second fait
    // sous-payer les administratifs : les métiers du tournage sont classés par
    // la CP elle-même (catégories 1 à 10), mais les employés administratifs
    // relèvent de la classification de la CPNAE (CP 200).
    classifications: {
      source:
        'CCT du 30/10/2012 « Conditions de travail et de rémunération » (CP 303.01), chapitres II et III, art. 5, 6 et 10 — banque CCT du SPF Emploi, 30301/30301-2012-009990.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 38,
      systeme: 'nomenclature de fonctions par équipe (chapitre II) + dix catégories barémiques 1 à 10 évoluant à l\'ancienneté (art. 10)',
      renvoiExterne: {
        cp: '200',
        portee: 'employés administratifs autres que ceux dont les fonctions sont définies aux chapitres II et III',
        texte: 'CCT du 29/05/1989 de la Commission paritaire nationale auxiliaire pour employés, chapitres II, III et IV, concernant les classifications professionnelles',
      },
      cadreProduction: [
        'Réalisateur', 'Directeur de production', 'Directeur de la photographie',
        'Chef opérateur du son', 'Architecte décorateur ou chef décorateur',
        'Chef monteur', 'Mixeur',
      ],
      equipes: ['réalisation', 'production', 'prises de vue', 'prise de son', 'décor', 'montage'],
      fonctions: [
        { categorie: 1, fonction: 'Secrétaire ou assistant de production' },
        { categorie: 2, fonction: '2e assistant opérateur' },
        { categorie: 2, fonction: 'Assistant costumier' },
        { categorie: 2, fonction: 'Électricien' },
        { categorie: 2, fonction: 'Machiniste' },
        { categorie: 2, fonction: 'Habilleur' },
        { categorie: 2, fonction: 'Assistant maquilleur' },
        { categorie: 2, fonction: 'Coiffeur' },
        { categorie: 2, fonction: 'Assistant monteur' },
        { categorie: 2, fonction: '2e assistant réalisateur d\'animation' },
        { categorie: 2, fonction: 'Travailleur et dessinateur d\'animation' },
        { categorie: 3, fonction: 'Chef électricien' },
        { categorie: 3, fonction: 'Chef machiniste' },
        { categorie: 3, fonction: 'Scripte' },
        { categorie: 3, fonction: 'Régisseur d\'extérieurs' },
        { categorie: 3, fonction: 'Photographe de plateau' },
        { categorie: 3, fonction: 'Accessoiriste' },
        { categorie: 3, fonction: 'Chef costumier' },
        { categorie: 3, fonction: 'Coiffeur perruquier' },
        { categorie: 3, fonction: 'Assistant story-boarder' },
        { categorie: 3, fonction: 'Assistant « lay-out »' },
        { categorie: 3, fonction: 'Assistant dessinateur d\'animation' },
        { categorie: 3, fonction: 'Assistant modèles couleurs' },
        { categorie: 3, fonction: 'Assistant animateur' },
        { categorie: 3, fonction: 'Assistant compositing' },
        { categorie: 4, fonction: 'Assistant son / perchman' },
        { categorie: 4, fonction: 'Chef maquilleur' },
        { categorie: 4, fonction: 'Chef traceur gouacheur' },
        { categorie: 5, fonction: 'Administrateur de production' },
        { categorie: 5, fonction: '1er assistant opérateur' },
        { categorie: 6, fonction: 'Assistant décorateur' },
        { categorie: 6, fonction: 'Ensemblier' },
        { categorie: 6, fonction: 'Assistant décorateur d\'animation' },
        { categorie: 7, fonction: '1er assistant réalisateur' },
        { categorie: 7, fonction: 'Régisseur général' },
        { categorie: 7, fonction: '1er assistant réalisateur d\'animation' },
        { categorie: 7, fonction: 'Story-boarder' },
        { categorie: 7, fonction: 'Dessinateur « lay-out »' },
        { categorie: 7, fonction: 'Animateur' },
        { categorie: 7, fonction: 'Cameraman (image par image)' },
        { categorie: 7, fonction: 'Opérateur compositing' },
        { categorie: 8, fonction: 'Chef monteur' },
        { categorie: 8, fonction: 'Cameraman' },
        { categorie: 8, fonction: 'Acteur / actrice' },
        { categorie: 8, fonction: 'Chef dessinateur d\'animation' },
        { categorie: 8, fonction: 'Chef modèles couleurs' },
        { categorie: 9, fonction: 'Chef opérateur du son' },
        { categorie: 9, fonction: 'Mixeur' },
        { categorie: 10, fonction: 'Directeur de la photographie' },
        { categorie: 10, fonction: 'Directeur de production' },
        { categorie: 10, fonction: 'Chef décorateur' },
        { categorie: 10, fonction: 'Réalisateur (autre que long-métrage)' },
      ],
      anciennete:
        'l\'ancienneté est acquise dans les organisations de secteurs apparentés, pour une fonction analogue, quel que soit le statut. Les CDD conclus dans une même année civile comptent : à hauteur de leur durée effective sous 3 mois cumulés, pour 6 mois entre 3 et 6 mois, pour un an entre 6 et 12 mois (art. 10).',
      cumulFonctions:
        'quand un travailleur cumule plusieurs fonctions, c\'est la fonction la plus élevée en rémunération qui est prise en considération ; les fonctions cumulées doivent être mentionnées au préalable dans le contrat (art. 11).',
      montantsNonCodes: 'les rémunérations minimums hebdomadaires de l\'art. 10 sont celles du 01/02/2009 et le forfait réalisateur long-métrage (20 000 € bruts) date de la même CCT — non repris ici, indexation index-pivot depuis.',
    },
    indexation: { frequence: 'pivot_public', mois: [2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT audiovisuel (indice santé lissé, +2% au 1er jour du 2e mois), CONFIRMÉ 2026-07-16. Source: Mediarte + CGSLB + CSC spectacle.' },
    fondsSE: {
      nom: 'Fonds social CP 303.01',
      taux_patronal: 0.0032,
      taux_patronal_employe: 0.0032,
      base108Ouvrier: true,
      onssCategorie: '303',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 303 : 820 ouvriers 0,32 % · employés 0,32 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 303.01 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 26/09/2026, À RECONFIRMER — CCT du 25/09/2023 (dépôt
    // 184981/CO/303.01, enregistrée 08/01/2024), art. 5 : <20 travailleurs
    // = 3j/an (dont 1 individuel) ; ≥20 travailleurs, trajectoire de
    // croissance 2024=3j → 2026=4j (valeur actuelle) → 2028=5j. Fonds :
    // Fonds social de la Production de films (cotisation 0,20% dès
    // 01/01/2025, CCT du 26/04/2024, dépôt 187709/CO/303.01).
    formation: {
      seuilEffectif: {
        moins20: { joursIndividuels: 3, dontIndividuelGaranti: 1 },
        ge20: { joursIndividuels: 4 },
      },
      trajectoireGe20: { '2024': 3, '2026': 4, '2028': 5 },
      fonds: "Fonds social de la Production de films (CP 303.01) — cotisation 0,20% dès 01/01/2025, CCT du 26/04/2024, dépôt 187709/CO/303.01",
      source: "CCT du 25/09/2023 (CP 303.01), art. 5, dépôt 184981/CO/303.01, enregistrée 08/01/2024, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
      aVerifier: true,
      aVerifierRaison: "Passera à 5 jours/an (≥20 travailleurs) au 01/01/2028 — reprogrammer un contrôle.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 303.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Mediarte + CSC spectacle',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 303.03 — Exploitation de salles de cinéma (généré batch 29/04/2026)
  '303.03': {
    // ── Classification CP 303.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 22/10/2019 « conditions de travail et de
    // rémunération », banque CCT du SPF Emploi (30303/30303-2019-014250.pdf),
    // chapitre II, art. 2 (ouvriers) et art. 4 (employés).
    // Trouvée par recherche plein texte. Secteur : exploitation de salles de
    // cinéma.
    //
    // ⚠️ DEUX ÉCHELLES INDÉPENDANTES QUI PORTENT LES MÊMES CHIFFRES ROMAINS.
    // Les ouvriers ont des catégories I à V, les employés AUSSI — et elles ne
    // veulent pas dire la même chose. Une « catégorie III » est un opérateur
    // côté ouvriers, un aide-comptable côté employés. Confondre les deux
    // donnerait le mauvais barème à tout le monde.
    //
    // ⚠️ Et côté ouvriers, la coupure structurante n'est pas le niveau mais
    // le CONTACT AVEC LA CLIENTÈLE : les catégories I à III sont « sans
    // contact », les IV et V « avec contact ».
    classifications: {
      source: 'CCT du 22/10/2019, chapitre II art. 2 et 4 — banque CCT du SPF Emploi, 30303/30303-2019-014250.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      trouveePar: 'recherche plein texte — le titre est « conditions de travail et de rémunération »',
      deuxEchellesMemeNumerotation: true,
      ouvriers: {
        coupure: 'catégories I à III sans contact avec la clientèle, IV et V avec contact',
        categories: [
          { code: 'I', label: 'Nettoyage et entretien', metiers: ['personnel de nettoyage des bâtiments et salles', 'personnel aux toilettes (rémunéré à l\'heure)', "personnel d'entretien (homme ou femme à tout faire)", "personnel qualifié chargé de l'entretien technique des bâtiments, salles et matériel"] },
          { code: 'II', label: 'Surveillance', metiers: ['personnel de surveillance : contrôle et prévention de la sécurité du complexe, intervention en cas d\'urgence ou de crise'] },
          { code: 'III', label: 'Opérateur', taches: ['montage et démontage des films', 'surveillance pendant les projections', "contrôle de la qualité de l'image et du son", 'réparation de petites pannes', 'maintenance de la cabine et des machines'],
            // ⭐ Une progression dans le temps, pas trois métiers différents.
            progression: [
              { code: 'a', label: 'opérateur débutant' },
              { code: 'b', label: 'aide-opérateur', condition: 'après 6 mois de service comme opérateur débutant' },
              { code: 'c', label: 'opérateur qualifié', condition: "après 1 200 heures de prestations comme opérateur dans le secteur (les 6 mois inclus) ou, à défaut, 2 ans de service effectifs dans cette fonction dans la même entreprise" },
            ] },
          { code: 'IV', label: "Accueil (avec contact clientèle)", metiers: ["personnel d'accueil : accueil des clients, contrôle des tickets, accompagnement aux places, contrôle de la sécurité, maintien de l'ordre et de la propreté des salles, foyers et couloirs pendant les séances, vente de confiserie, boissons, crème glacée et programmes", "personnel d'accueil ET caissier : mêmes conditions, avec occupation à la caisse"] },
          { code: 'V', label: 'Responsables de groupes', metiers: ["chef opérateur : fonction opérateur, plus plan de travail, contrôle du travail des autres opérateurs, contrôle des sapeurs-pompiers et de l'inspection technique, contrôle suivant le Règlement général pour la protection du travail", "autres responsables de groupes (dont chef nettoyage) : leur fonction, plus élaboration du plan de travail et/ou contrôle et guidance du travail des membres du personnel sous leurs ordres"] },
        ],
      },
      employes: {
        categories: [
          { code: 'I', label: "Personnel d'exécution", metiers: ['employé de bureau'] },
          { code: 'II', label: 'Collaborateur administratif — exécute certaines tâches d\'initiative', metiers: ['réceptionniste-téléphoniste', 'secrétaire', 'responsable de caisse'],
            precision: "la tâche de responsable de caisse comprend notamment la vente des tickets, l'information de la clientèle sur le film, l'information et les documents à remplir pour les instances officielles (SABAM, Affaires économiques, services communaux pour les taxes, distributeurs de films) et la clôture de la caisse" },
          { code: 'III', label: 'Aide-comptable', metiers: ['aide-comptable'] },
          { code: 'IV', label: 'Encadrement — complexes de moins de 5 salles', metiers: ['chef caissier', 'chef personnel au comptoir', 'comptable (complexes de moins de 5 salles)', 'chef de salle (complexes de moins de 5 salles)'] },
          { code: 'V', label: 'Encadrement — complexes d\'au moins 5 salles', metiers: ['assistant manager', 'chef de salle', 'comptable'] },
        ],
        // ⚠️ Définition commune aux catégories IV et V : elle décide de qui
        // est « chef de salle », donc du barème.
        chefDeSalle: "chef hiérarchique du personnel, chargé de la surveillance de la salle et responsable de la bonne exécution des directives données par le chef d'entreprise",
      },
      procedureAppel: 'la CCT prévoit une procédure de recours (section C)',
    },
    nom: 'CP 303.03 — Exploitation de salles de cinéma',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT audiovisuel/cinéma (indice santé lissé, +2% au 1er jour du 2e mois), CONFIRMÉ 2026-07-16. Source: Mediarte + CGSLB exploitation cinémas.' },
    fondsSE: {
      nom: 'Fonds social CP 303.03',
      taux_patronal: 0.0034999999999999996,
      taux_patronal_employe: 0.0034999999999999996,
      base108Ouvrier: true,
      onssCategorie: '323',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 323 : 820 ouvriers 0,35 % · employés 0,35 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: 138.73,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 303.03 (cinémas) — art. 9 de la CCT du 22/10/2019 (155543/CO/303.03) : « chaque année, le personnel a droit à... des éco-chèques », valeur indexée avec le salaire, progression tracée sur 4 CCT lues (104,94 EUR mi-2017, 106,09 EUR fin 2017, 111,58 EUR depuis le 01/10/2019, 138,73 EUR au 21/09/2026 — dernière valeur citée dans la CCT du 20/05/2026, dépôt 200172/CO/303.03). Texte primaire lu le 21/09/2026. ⚠️ À PARTIR DU 01/01/2027, l'employeur qui octroyait déjà des éco-chèques en 2026 dispose d'une OPTION : continuer les éco-chèques (138,73 EUR, indexés à la prochaine indexation sectorielle) OU les convertir en une hausse de 0,66 EUR de la part patronale des chèques-repas — la valeur codée reste le régime actuellement en vigueur, à re-vérifier après le 01/01/2027.",
    ecoChequesDateVerif: '2026-09-21',
    ecoChequesPaiementDate: 'decompte de decembre',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 26/09/2026, À RECONFIRMER — CCT du 11/12/2023 (dépôt
    // 185546/CO/303.03, enregistrée 25/01/2024), art. 3 : <10 travailleurs
    // = pas de droit individuel ; 10-19 = 1j/an en moyenne ; ≥20
    // travailleurs, trajectoire 2023-24=2j → 2025-26=3j (valeur actuelle)
    // → 2027-28=4j → dès 2029=5j.
    formation: {
      seuilEffectif: {
        moins10: { exclu: true },
        entre10et19: { joursIndividuels: 1 },
        ge20: { joursIndividuels: 3 },
      },
      trajectoireGe20: { '2023-2024': 2, '2025-2026': 3, '2027-2028': 4, '2029': 5 },
      source: "CCT du 11/12/2023 (CP 303.03), art. 3, dépôt 185546/CO/303.03, enregistrée 25/01/2024, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
      aVerifier: true,
      aVerifierRaison: "Palier suivant (4 jours, ≥20 travailleurs) au 01/01/2027 — reprogrammer un contrôle.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 303.03: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Mediarte + CSC spectacle',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 307 — Entreprises de courtage et agences d\ (généré batch 29/04/2026)
  '307': {
    nom: "CP 307 — Entreprises de courtage et agences d'assurances",
    classifications: {
      source: "CCT du 24/06/2008 (CP 307, entreprises de courtage et agences d'assurances), chapitre II « Classification des fonctions », banque CCT du SPF, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "Classification en TROIS groupes de personnel, et non en categories numerotees (chapitre II) : personnel d'execution · personnel cadre d'execution · inspecteurs. Le chapitre III des baremes suit la meme partition, avec une section distincte pour les travailleurs entrant en fonction apres le nombre normal d'annees d'experience.",
    },
    type: 'O',
    regimeHebdo: 36,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-03-01', source: 'AUDIT 2026-06 — CSC/Easypay' },
    fondsSE: {
      nom: 'Fonds social CP 307',
      taux_patronal: 0.0062,
      taux_patronal_employe: 0.0062,
      base108Ouvrier: true,
      onssCategorie: '530',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 530 : 820 ouvriers 0,62 % · employés 0,62 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 3,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — banque CCT SPF (jcId 3070000), recherche par
    // thème et mots-clés : aucune CCT n'institue de chèque-repas. Le secteur
    // utilise des éco-chèques (CCT 2009/2014/2016/2019) et une prime pouvoir
    // d'achat fédérale (CCT du 06/12/2023, dépôt 184984, texte primaire lu — ne
    // mentionne jamais "repas").
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 307 (jcId 3070000) — aucune CCT chèque-repas ; le secteur utilise éco-chèques et prime pouvoir d'achat",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 18/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (307/307-2019-013378.pdf, CCT du 17/10/2019, dépôt 155327/CO/307), art.
    // 4 §2 : « Aan elke voltijds tewerkgestelde met een volledige
    // referteperiode worden jaarlijks ecocheques toegekend ter waarde van
    // 250 EUR » (à chaque travailleur temps plein avec période de référence
    // complète, 250 EUR/an d'éco-chèques). Cohérent avec la note chequesRepas
    // ci-dessus (« le secteur utilise éco-chèques »). Valeur nominale max.
    // 10 EUR/chèque (art. 3 §1), validité 24 mois (art. 3 §3).
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 17/10/2019 (307/307-2019-013378.pdf), banque CCT du SPF Emploi, dépôt 155327/CO/307, texte primaire lu le 18/09/2026, art. 4 §2 : 250 EUR/an pour tout travailleur temps plein avec période de référence complète.",
    ecoChequesDateVerif: '2026-09-18',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CCT du 26/01/2026 (dépôt 198195/CO/307,
    // durée indéterminée, remplace la CCT du 06/12/2023 n° 184988/CO/307),
    // art. 4 : <10 travailleurs = 2j/an ; 10-19 travailleurs, trajectoire
    // 2025-26=2,5j → 2027-28=3j → 2029=3,5j → 2030=4j ; ≥20 travailleurs =
    // 5j/an fixe (confirmé depuis 2020). Le motif actuel « 5 jours » n'est
    // donc juste QUE pour les entreprises ≥20 travailleurs. Coordination
    // via CEPOM, pas de cotisation chiffrée dans ce texte.
    formation: {
      seuilEffectif: {
        moins10: { joursIndividuels: 2 },
        entre10et19: { joursIndividuels: 2.5, trajectoire: { '2025-2026': 2.5, '2027-2028': 3, '2029': 3.5, '2030': 4 } },
        ge20: { joursIndividuels: 5 },
      },
      mecanisme: "coordination via CEPOM, pas de cotisation chiffrée dans ce texte",
      source: "CCT du 26/01/2026 (CP 307), art. 4, dépôt 198195/CO/307, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.35 },  // vélo CP 307: 0,35 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB finances + Febelfin',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 315 — Aviation commerciale (généré batch 29/04/2026)
  '315': {
    nom: 'CP 315 — Aviation commerciale',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    classifications: {
      source: 'banque CCT du SPF Emploi (0 convention sous le jcId 3150000) et fiche SPF « Salaires minimums » (cadre vide) — vérifiées le 23/08/2026',
      dateVerif: '2026-08-23',
      niveauFaitiere: true,
      grilleSectorielle: false,
      systeme: "aucune classification au niveau de la CP 315 : l'aviation commerciale est subdivisée en trois sous-commissions",
      sousCommissions: [
        { cp: '315.01', libelle: 'Techniciens au sol', resume: 'salaire minimum par régime hebdomadaire (37,5 h / 38 h / 40 h), aucune catégorie de fonction' },
        { cp: '315.02', libelle: 'Compagnies aériennes autres que la S.A. Sabena', resume: "revenu minimum mensuel seul ; un groupe de travail devait étudier la faisabilité d'un système de classification (CCT du 16/11/2005, art. 7), rien n'en est sorti" },
        { cp: '315.03', libelle: 'Gestion des aéroports', resume: null },
      ],
      // 📏 La dette se dit avec son dénominateur (R2 ter-A) : 2 sous-CP sur 3
      // sont tranchées ; la 315.03 reste ouverte — sa fiche SPF s'arrête au
      // 21/08/2009 et la banque ne rend aucune CCT de classification.
      resteOuvert: '315.03 (gestion des aéroports) — 2 sous-commissions tranchées sur 3',
    },
    nomCourt: 'Aviation commerciale',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT (corrigé de « annuelle janvier » 2026-07-16) : mois VARIABLE — 315.01 a indexé au 01/01/2026 ET 01/07/2026, 315.02 vers 03/2026. Dates réelles portées par les sous-CP 315.01/.02/.03. Source: lacsc.be + CGSLB compagnies aériennes + Securex.' },
    // ── FAÎTIÈRE À MOITIÉ SOURCÉE — ET C'EST POURQUOI ON NE MET PAS 0 ─────
    // 📏 Mesure du 26/08/2026, avec son dénominateur : la banque CCT du SPF ne
    // publie AUCUNE convention sous le numéro 315 lui-même. Ses sous-commissions,
    // elles, sont **1 sur 3** sourcées : 315.02 (0,40 %, taux ONSS).
    // Les autres — 315.01 et 315.03, toutes deux à 4 % sans source — n'ont
    // rien derrière elles.
    //
    // ⛔ POURQUOI LE 4 % RESTE, ALORS QU'ON L'A MIS À 0 SUR LES CP 125, 142,
    // 149, 318 ET 329. Là-bas, TOUTES les sous-commissions étaient sourcées :
    // la mère ne servait plus à personne. Ici, les sous-CP muettes **retombent
    // sur la mère** — c'est la règle de `blocFondsSE`, qui ne descend que sur un
    // taux prouvé. Mettre 0 ne rendrait pas la mère honnête : ça mettrait à zéro
    // le fonds de plusieurs sous-secteurs qui en ont un, sans l'avoir vérifié.
    //
    // ✅ Ce qui change quand même : le 4 % est désormais NOMMÉ comme non
    // sourcé, avec le ratio écrit. Il tombera quand les sous-CP muettes auront
    // été lues — pas avant.
    fondsSE: {
      nom: "Aviation commerciale — faîtière, taux non sourcé servant de repli aux sous-CP muettes",
      taux_patronal: 0.04,
      aVerifier: true,
      aVerifierRaison: "Le 4 % n'a AUCUNE source : la banque CCT du SPF ne publie aucune convention sous le numéro 315 lui-même (recherche par thème, par mot-clé et sans filtre, le 26/08/2026). Il n'est PAS mis à 0 parce que les sous-commissions encore muettes retombent dessus — 315.01 et 315.03, toutes deux à 4 % sans source — et qu'un 0 leur retirerait un fonds qu'elles ont peut-être. Il tombera quand elles auront été lues, pas avant. Sous-CP déjà sourcées : 315.02 (0,40 %, taux ONSS) (1 sur 3).",
      dateVerif: '2026-08-26',
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3150000 : 0 convention sur "chèque" tous
    // critères confondus. Contrairement au fondsSE ci-dessus (qui sert de repli
    // aux sous-CP encore muettes), les sous-CP 315.01 et 315.03 ont chacune leur
    // propre réponse chèques-repas sourcée séparément — aucune ne dépend de
    // cette valeur mère.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi (jcId 3150000) — aucune convention chèque-repas au niveau faîtière ; voir les sous-CP 315.01/315.03 pour leurs régimes propres",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 315 + « eco cheque » :
    // 0 résultat. Cohérent avec chequesRepas ci-dessus : CP 315 est une
    // faîtière, les sous-CP 315.01/315.02/315.03 ont leurs propres dépôts —
    // à vérifier séparément.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 315 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (faîtière, sous-CP 315.01/315.02/315.03 à vérifier séparément)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — CP 315 est une CP faîtière (aviation) :
    // recherche plein texte « formation »/« opleiding » → 0 résultat, 0
    // document confirmé. Aucune CCT n'est déposée sous ce numéro lui-même —
    // le mécanisme vit dans les sous-commissions (315.02 : droit individuel
    // confirmé, voir TAUX_SECTORIELS['315.02'].formation).
    formation: {
      jours: null,
      renvoi: "aucune CCT n'est déposée sous le numéro de CP 315 lui-même (banque CCT SPF Emploi, recherche « formation »/« opleiding » : 0 résultat) — le mécanisme vit dans les sous-commissions. 315.02 : droit individuel confirmé, voir TAUX_SECTORIELS['315.02'].formation. 315.01 et 315.03 : non vérifiés (recherche polluée par l'intitulé de la CP ou données insuffisantes).",
      source: "Banque CCT du SPF Emploi, recherche CP 315 + « formation »/« opleiding » : 0 résultat, 0 document, relevé le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 315: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB aviation + ACV-CSC',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 315.01 — Maintenance, assistance et formation (aviation) (généré batch 29/04/2026)
  '315.01': {
    nom: 'CP 315.01 — Maintenance, assistance et formation (aviation)',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION » : la fiche a été
    // ouverte et lue, ce secteur ne classe pas ses travailleurs par fonction.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      grilleSectorielle: false,
      systeme: "le barème ne se décline que par RÉGIME hebdomadaire — 37,5 h, 38 h ou 40 h — avec un salaire minimum unique pour chacun. Aucune catégorie de fonction.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-07-01', note: 'INDEX-PIVOT (corrigé de janvier fixe 2026-07-16) : a indexé au 01/01/2026 ET 01/07/2026 (mois variable). Source: lacsc.be + CGSLB aviation.' },
    // ── AUCUNE CONVENTION POSTÉRIEURE À 1999 ──────────────────────────────
    // 📏 Census du 26/08/2026, avec son dénominateur : la banque CCT du SPF
    // publie **20 conventions** pour la SCP 315.01, et les plus récentes qui
    // touchent au financement datent de **1999** — « emploi et formation des
    // groupes à risque » (dépôts 54483 et 51586) et « formation et emploi pour
    // 1999-2000 » (dépôt 52542). Elles citent 0,10 %, 0,20 % et 0,40 % selon
    // l'année, toutes échues depuis vingt-cinq ans.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et aucune des valeurs
    // lues ne peut le remplacer : elles décrivent l'état du secteur en 1999.
    // 🚫 L'ONSS ne rattache aucune catégorie d'employeur à cette sous-commission.
    //
    // ⛔ On ne met pas 0 : la sœur 315.02 (compagnies aériennes) perçoit bien
    // 0,40 %, sourcé sur le fichier ONSS. Rien ne dit que la maintenance
    // aéronautique n'a pas de fonds — seulement que le SPF n'en publie plus la
    // cotisation depuis 1999.
    fondsSE: {
      nom: 'Maintenance, assistance et formation aéronautiques — cotisation non établie',
      taux_patronal: 0.04,
      aVerifier: true,
      aVerifierRaison: "Le 4 % n'a AUCUNE source. Census REFAIT le 26/08/2026 sur les 103 conventions publiées — et non 20, dénominateur issu d'une recherche filtrée. Sur ces 103, ZÉRO ne porte sur un fonds ou une cotisation, y compris au filet élargi aux « programmations sociales » et « accords sectoriels » : les seuls titres approchants sont les cinq volets de l'accord sectoriel 2017-2018 et une prime syndicale de 2012. Ancienne mesure : census de 20 conventions ; les seules qui chiffrent une cotisation datent de 1999 (0,10 %, 0,20 % et 0,40 % selon l'année, dépôts 51586, 54483 et 52542), toutes échues depuis vingt-cinq ans — elles ne peuvent pas servir de valeur courante. L'ONSS ne rattache aucune catégorie d'employeur à cette sous-commission. On ne met pas 0 pour autant : la sœur 315.02 perçoit bien 0,40 %, et rien ne dit que celle-ci n'a pas de fonds. À reprendre par le fonds sectoriel de l'aviation ou par une recherche par date.",
      dateVerif: '2026-08-26',
    },
    // 🔴 Recherché le 05/09/2026 — un régime sectoriel EXISTE (CCT du 15/10/2015,
    // dépôt 130298/CO/315.01, art. 3 §1, texte primaire lu : « à partir du
    // 01/01/2016, les entreprises augmentent leur intervention patronale
    // EFFECTIVE dans les chèques-repas de 0,50 € »), mais c'est un INCRÉMENT sur
    // une base variable par entreprise, pas un montant sectoriel unique. 10
    // autres CCT examinées (restructurations Sabena 1998-2005) ne chiffrent
    // aucune base. Le 10,0/8,91/1,09 codé (plafond fédéral) n'est pas confirmé.
    chequesRepas: { obligatoire: true, valeur: 10.0, part_employeur: 8.91, part_travailleur: 1.09, aVerifier: true },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 315.01 (maintenance aéronautique) — CCT du 21/03/2014 (121717/CO/315.01), texte primaire lu le 21/09/2026 : « le droit aux éco-chèques DANS LA MESURE OÙ CELA EST PRÉVU AU NIVEAU DE L'ENTREPRISE » — aucun régime SECTORIEL, seulement une clause d'assimilation (jours de chômage temporaire) pour les entreprises qui ont DÉJÀ un régime propre. Aucune autre CCT ne chiffre un montant sectoriel.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 315.01' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 315.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB aviation + ACV-CSC',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 315.02 — Compagnies aériennes (généré batch 29/04/2026)
  '315.02': {
    nom: 'CP 315.02 — Compagnies aériennes',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId 80a5f80e… (CP 3150200), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "la fiche ne porte que le revenu minimum mensuel pour les travailleurs à temps plein. Confirme la CCT du 16/11/2005 déjà lue (art. 7 : un groupe de travail devait étudier la FAISABILITÉ d'un système de classification, échéance fin 2006 — rien n'en est sorti).",
    },
    nomCourt: 'Compagnies aériennes',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-03-01', note: 'INDEX-PIVOT (corrigé de janvier fixe 2026-07-16) : indexé ~03/2026 (mois variable). Source: CGSLB compagnies aériennes + Securex 315.02.' },
    fondsSE: {
      nom: 'Fonds social CP 315.02',
      taux_patronal: 0.004,
      taux_patronal_employe: 0.004,
      base108Ouvrier: true,
      onssCategorie: '080',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 080 : 820 ouvriers 0,40 % · employés 0,40 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 7.5, part_employeur: 6.41, part_travailleur: 1.09, source: "CCT du 12/01/2026 (SCP 315.02, compagnies aériennes), banque CCT du SPF, texte primaire lu le 24/08/2026, art. 3 : « La valeur nominale du chèque-repas atteint AU MOINS 7,5 euros. La participation de l'employeur dans le coût du chèque-repas atteint AU MOINS 6,41 euros. » ⚠️ DEUX PLANCHERS, pas deux montants. ⚠️ Le personnel NAVIGANT reçoit un chèque par jour de vol effectivement presté, SAUF s'il bénéficie déjà d'un avantage par jour de vol d'une valeur nette d'au moins 6,41 EUR (art. 2) — l'octroi n'est donc pas automatique.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-12', lien: '31502/31502-2026-000507.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 315.02 (compagnies aériennes) — 5 CCT lues (2021-2022, régimes de chômage temporaire COVID), toutes disent « le droit aux écochèques, SI D'APPLICATION » — clause d'assimilation, pas un régime sectoriel chiffré. Aucune CCT ne fixe un montant sectoriel. Texte primaire lu le 21/09/2026.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — CCT du 15/09/2023 (enr. 172532/CO/315.02,
    // modifiée par CCT du 18/10/2024, dépôt 190715/CO/315.02) : droit
    // individuel à la formation (art. 50 §1 g) loi du 03/10/2022) —
    // trajectoire 2j(2023)→3j(2024)→4j(2025)→5j (dès 2026, valeur
    // actuelle) pour entreprises ≥20 travailleurs temps plein ; 1j/an pour
    // <20 travailleurs. Prorata temps partiel/entrée-sortie en cours
    // d'année. 1 jour = 7,6h. Droit individuel à charge employeur, pas de
    // fonds sectoriel.
    formation: {
      jours: 5,
      seuilEffectif: 20,
      joursSiSousLeSeuil: 1,
      base: "art. 50 §1 g) loi du 03/10/2022 (trajectoire de croissance sectorielle du droit individuel à la formation)",
      mecanisme: "CCT du 15/09/2023 (enr. 172532/CO/315.02) : trajectoire 2j(2023)→3j(2024)→4j(2025)→5j(dès 2026) pour entreprises ≥20 travailleurs temps plein ; 1j/an pour <20 travailleurs. Modifiée par CCT du 18/10/2024 (dépôt 190715/CO/315.02). Prorata temps partiel/entrée-sortie en cours d'année civile. 1 jour = 7,6h. Droit individuel à charge employeur, pas de fonds sectoriel.",
      fonds: null,
      source: "Banque CCT du SPF Emploi, CCT du 15/09/2023 (enr. 172532/CO/315.02) et CCT du 18/10/2024 (dépôt 190715/CO/315.02), textes primaires lus le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.35 },  // vélo CP 315.02: 0,35 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB aviation + ACV-CSC',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 315.03 — Gestion des aéroports (généré batch 29/04/2026)
  '315.03': {
    nom: 'CP 315.03 — Gestion des aéroports',
    nomCourt: 'Gestion des aéroports',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT (corrigé de janvier fixe 2026-07-16, aligné famille 315). Source: CGSLB aviation. Mois exact à confirmer.' },
    // ── 90 € PAR TRAVAILLEUR ET PAR AN, PAS UN POURCENTAGE ────────────────
    // ✅ CCT du 04/12/2017 « fixation de la cotisation des employeurs au Fonds
    // social », art. 3 et 6 — dépôt 144381/CO/315.03, banque CCT du SPF Emploi,
    // texte primaire lu le 26/08/2026 : « Pour chaque année de référence, la
    // cotisation des employeurs est fixée à 90 EUR par travailleur. Ce montant
    // est pro ratisé en fonction des mois de service pendant l'année précédente
    // […]. Un mois entamé compte pour 1 mois. » En vigueur au 01/01/2018, pour
    // une durée INDÉTERMINÉE.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI SE TROMPAIT DE NATURE. Sur un brut de 2 500 €,
    // il facturait 100 € par MOIS — 1 200 € par an — là où l'employeur doit 90 €
    // par an et par travailleur.
    //
    // 🔁 Cinquième fonds de la journée qui ne se compte pas en pourcentage,
    // après les CP 219, 102.03, 207 et 210.
    fondsSE: {
      nom: 'Fonds social de la sous-commission paritaire pour la gestion des aéroports',
      taux_patronal: 0,
      cctSourcee: {
        taux: 0,
        raison: "Ce fonds ne perçoit aucune cotisation proportionnelle au brut : sa ressource est un forfait annuel par travailleur. Un taux de 0 est la réponse JUSTE à « quel pourcentage du brut ? » — le montant réel est écrit sous `cotisationForfaitaire`.",
        source: "CCT du 04/12/2017 (SCP 315.03, gestion des aéroports), art. 3, 5 et 6 — dépôt 144381/CO/315.03, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Conclue en exécution de l'art. 13 des statuts du fonds (CCT du 04/12/2017, dépôt 144380/CO/315.03).",
        dateVerif: '2026-08-26',
      },
      cotisationForfaitaire: {
        montantAnnuelParTravailleur: 90.00,
        prorata: "au prorata des mois de service pendant l'année précédente ; un mois entamé compte pour un mois entier",
        echeance: "virement avant le 31 janvier de l'année qui suit l'année de référence",
        revision: "art. 4 : en décembre de chaque année, une évaluation des recettes et dépenses du fonds ajuste le financement de l'année suivante — le montant peut donc bouger sans nouvelle CCT.",
        depuis: '2018-01-01',
      },
      aVerifier: true,
      aVerifierRaison: "Le forfait de 90 € date de la CCT du 04/12/2017, à durée indéterminée — mais son art. 4 prévoit une évaluation annuelle en décembre qui ajuste le financement de l'année suivante. Le montant appliqué aujourd'hui peut donc différer sans qu'une nouvelle convention soit déposée. À confirmer auprès du fonds avant de le facturer.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source et supposait un fonds à pourcentage.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT du 14/04/2016 (dépôt 133429/CO/315.03),
    // texte primaire lu intégralement, art. 3 §2 : plancher sectoriel = 2,10 €
    // (1,01 € employeur + 1,09 € travailleur) par jour presté, en vigueur
    // 01/01/2016 ; §3 : les entreprises qui accordaient déjà un CR majorent la
    // part employeur de +1,00 € (part travailleur inchangée). Aucune CCT plus
    // récente trouvée — ce plancher de 2016 est potentiellement dépassé sans
    // qu'une CCT plus récente l'ait remplacé, d'où `aVerifier`.
    chequesRepas: {
      obligatoire: true, valeur: 2.10, part_employeur: 1.01, part_travailleur: 1.09,
      aVerifier: true,
      aVerifierRaison: "Plancher de 2016 (CCT du 14/04/2016, dépôt 133429/CO/315.03), aucune CCT plus récente trouvée — potentiellement dépassé depuis. +1,00€ patronal en plus pour les entreprises qui accordaient déjà un CR avant cette CCT (§3).",
      source: "CCT du 14/04/2016 (315.03, gestion des aéroports), art. 3 §§2-3, dépôt 133429/CO/315.03, banque CCT du SPF Emploi, texte primaire lu le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 315.03 (gestion des aéroports) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 315.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 315.03: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB aviation + ACV-CSC',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 316 — Marine marchande (généré batch 29/04/2026)
  '316': {
    nom: 'CP 316 — Marine marchande',
    nomCourt: 'Marine marchande',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'aucune', dernierTaux: null, derniereDate: null, note: 'Pas d index pivot — CAO sectorielle (Pool marins). NB: le +3,95% est la CAO NL, pas CP 316 BE. Source: AUDIT 2026-06 Easypay/BTB CP 316' },
    // ── LE FONDS GÉNÉRAL DE LA MARINE MARCHANDE A ÉTÉ DISSOUS EN 2003 ──────
    // ✅ Trois textes primaires lus le 26/08/2026, banque CCT du SPF Emploi :
    //   · CCT du 08/05/2003, « dissolution du Fonds de sécurité d'existence »,
    //     dépôt 67336/CO/316 : « le fonds est dissous le 8 mai 2003 » ;
    //   · CCT du 16/12/2009, dépôt 97546/CO/316, qui ABROGE au 01/01/2010 les
    //     conventions instituant ce fonds (dont 56232/CO/316) ;
    //   · CCT du 02/02/2005, dépôt 73946/CO/316, art. 1er et 5, qui institue le
    //     SEUL fonds encore debout — et il ne vise pas toute la commission.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il facturait à toute
    // la marine marchande un fonds qui n'existe plus depuis vingt-trois ans.
    // C'est le même cas que la CP 102.01 (fonds dissous fin 2010, 6 % facturés).
    //
    // 🚢 CE QUI EXISTE, ET SEULEMENT LÀ : le « Fonds de sécurité d'existence
    // services de remorquage en mer ». Son art. 5 limite le champ aux
    // ENTREPRISES EXPLOITANT DES REMORQUEURS dont l'activité de remorquage est
    // le « transport maritime », et aux marins inscrits au Pool belge des
    // Marins. Ses cotisations sont documentées ci-dessous, NON facturées :
    // l'app ne sait pas si un employeur de la CP 316 exploite des remorqueurs,
    // et deux des trois se comptent PAR JOUR PRESTÉ, pas sur le brut.
    //
    // 🔁 C'est le piège du périmètre, rencontré une fois de plus. Lue seule, la
    // CCT du 17/01/2013 (dépôt 113435) semble ramener « le montant de 1,75 p.c.
    // à 0,10 p.c. » à l'article 12 des statuts — un article général. Ce sont les
    // statuts de 2005 qui tranchent : ce fonds n'a JAMAIS visé autre chose que
    // le remorquage en mer. Écrire 0,10 % pour la CP 316 entière aurait été la
    // faute de la fiche Limosa CP 118 servie comme toute l'industrie alimentaire.
    fondsSE: {
      nom: "Marine marchande — le fonds de sécurité d'existence est dissous, mais le Fonds professionnel perçoit des forfaits journaliers",
      taux_patronal: 0,
      fondsDissous: '2003-05-08',
      source: "CCT du 08/05/2003 (CP 316), « dissolution du Fonds de sécurité d'existence », dépôt 67336/CO/316 : « le fonds est dissous le 8 mai 2003 ». Ses conventions instituantes (dont 56232/CO/316 du 17/11/2000) ont été abrogées au 01/01/2010 par la CCT du 16/12/2009, dépôt 97546/CO/316. Banque CCT du SPF Emploi, textes primaires lus le 26/08/2026.",
      dateVerif: '2026-08-26',
      // ── ⚠️ « AUCUNE COTISATION » ÉTAIT FAUX : IL Y EN A 1,60 € PAR JOUR ──────
      // Corrigé le 26/08/2026. La fiche disait « pas de fonds » et affichait 0,
      // sur la foi de la dissolution de 2003. La dissolution est réelle — mais
      // elle ne concerne QUE le « Fonds de sécurité d'existence » de la CP 316.
      // Un AUTRE fonds, le « Fonds professionnel de la marine marchande »
      // (Bedrijfsfonds voor de koopvaardij), est bien vivant et perçoit des
      // cotisations patronales fixées par convention.
      //
      // 🔁 Exactement le piège des carrières 102.02, rencontré le même jour :
      // un acte de dissolution ne vaut que pour LE fonds qu'il nomme. Deux
      // fonds peuvent coexister dans une même commission, et l'un mourir sans
      // l'autre. Avant d'écrire 0 sur la foi d'une dissolution, vérifier
      // qu'aucun second fonds ne subsiste.
      //
      // 📏 Ce qui l'a fait sortir : un census refait au FILET LARGE sur les 137
      // conventions (« programmation », « accord sectoriel », « avantages
      // sociaux » en plus de « fonds » et « cotisation ») — la leçon de la
      // CP 114. Le titre gagnant est « Fixation des montants des primes à
      // l'emploi et les cotisations patronales », qui ne dit pas « fonds ».
      fondsProfessionnelMarineMarchande: {
        nom: 'Fonds professionnel de la marine marchande (Bedrijfsfonds voor de koopvaardij)',
        perimetre: "employeurs de la CP 316, À L'EXCEPTION de ceux qui exploitent des remorqueurs dont l'activité de remorquage est le « transport en mer » — ceux-là relèvent du fonds décrit sous `sousSecteurRemorquageEnMer`. Vise les marins et shoregangers.",
        cotisationsForfaitaires: {
          formationSyndicaleEtObjectifsSociaux: {
            parJourEtParMarin: 0.70,
            precision: "0,70 € par jour et par marin/shoreganger occupé sur navire sous pavillon BELGE et déclaré à la Caisse de Secours et de Prévoyance en faveur des Marins ; 0,70 € également par jour et par marin inscrit sur la liste du Pool occupé sur navire ÉTRANGER lorsque les cotisations de sécurité sociale belges sont dues",
            renvoiStatuts: "art. 3, 2° des statuts coordonnés",
          },
          supplementIndemnitesDAttente: {
            parJourEtParMarin: 0.90,
            precision: "par jour pour lequel les cotisations de sécurité sociale sont dues",
            renvoiStatuts: "art. 3, 3° des statuts coordonnés",
          },
          salaireMensuelGarantiShoregangers: { parJourEtParMarin: 0, renvoiStatuts: 'art. 3, 6° — fixé à 0 € depuis le 01/01/2022' },
          primesALEmploi: { parJourEtParMarin: 0, renvoiStatuts: 'art. 3, 7° — fixé à 0,00 € pour les deux pavillons depuis le 01/01/2022' },
        },
        totalParJourEtParMarin: 1.60,
        depuisLe: '2022-01-01',
        source: "CCT du 20/01/2022 (CP 316, marine marchande), art. 2 et 3 — dépôt 176065/CO/316, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026 (14 091 caractères). Elle fixe les montants en exécution de l'art. 13 des statuts coordonnés du Fonds professionnel (CCT des 18/10/2004, dépôt 72990, et 20/04/2005, dépôt 74708). ⚠️ Un ERRATUM du SPF figure en tête du même document : à l'art. 3, « la cotisation de l'armateur » doit se lire « la cotisation des employeurs » — la portée est donc plus large que le seul armateur.",
        dateVerif: '2026-08-26',
        pourquoiPasDansLeTaux: "Ce sont des forfaits PAR JOUR et PAR MARIN, pas un pourcentage du brut : ils ne peuvent pas s'écrire dans `taux_patronal` sans être multipliés par une masse salariale, ce qui donnerait n'importe quoi. Le taux reste 0 — la réponse juste à « quel pourcentage ? » — et le coût réel est ici.",
      },

      // L'autre fonds encore debout, et son périmètre étroit.
      sousSecteurRemorquageEnMer: {
        nom: "Fonds de sécurité d'existence services de remorquage en mer",
        perimetre: "Entreprises exploitant des remorqueurs dont l'activité de remorquage est le « transport maritime », et travailleurs liés par un contrat d'engagement maritime inscrits au Pool belge des Marins (art. 5 des statuts du 02/02/2005).",
        fraisAdministration: {
          taux: 0.0010,
          base: 'salaire brut normal et garanti',
          source: "Art. 12 des statuts (CCT du 02/02/2005, dépôt 73946/CO/316) : « une cotisation de 1,75 p.c. calculée sur le salaire brut normal et garanti […] à titre d'intervention dans les frais d'administration ». Le montant de 1,75 p.c. a été remplacé par 0,10 p.c. par la CCT du 17/01/2013, art. 2 (dépôt 113435/CO/316), en vigueur au 01/01/2013 pour une durée indéterminée.",
        },
        primeSyndicale: {
          parJourPresteOuAssimile: 0.64,
          montantAnnuelParAyantDroit: 128.00,
          source: "CCT du 02/02/2005 (CP 316), « prime syndicale », art. 3 et 5 — dépôt 73943/CO/316. ⚠️ Montants de 2005, aucune actualisation trouvée dans la banque CCT.",
        },
        pensionComplementaire: {
          parJourPresteOuAssimile: 0.7189,
          prestationAnnuelle: 24.79,
          prestationMaximale: 594.94,
          source: "CCT du 02/02/2005 (CP 316), « pension complémentaire », art. 4 et 7 — dépôt 73944/CO/316. ⚠️ Montants de 2005, aucune actualisation trouvée dans la banque CCT.",
        },
        dateVerif: '2026-08-26',
        note: "NON facturé par l'app : l'exploitation de remorqueurs n'est pas une donnée du dossier, et deux des trois cotisations se comptent par jour presté.",
      },
      aVerifier: true,
      aVerifierRaison: "La cotisation « groupes à risque » de 0,10 % de la masse salariale (CCT du 04/07/2019, dépôt 152863/CO/316), elle, visait TOUTE la CP 316 — mais son art. 4 la fait cesser le 31/12/2020, et aucune convention postérieure n'a été trouvée dans la banque CCT. Sans CCT sectorielle, l'effort groupes à risque redevient la cotisation fédérale due à l'ONSS, qui n'est pas un fonds de sécurité d'existence : elle n'est donc pas écrite ici. À reconfirmer avant d'affirmer qu'aucune cotisation sectorielle n'est due depuis 2021.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source et facturait un fonds dissous depuis 2003.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3160000 : 0 résultat sur "chèque" et
    // "maaltijdcheque", tous critères. Cohérent avec le census déjà fait dans le
    // fondsSE ci-dessus (137 conventions au filet large, aucune sur un chèque-
    // repas).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 316 (jcId 3160000) — census 137 conventions, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 316 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 316 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 26/09/2026, À RECONFIRMER — seulement 7 résultats réels
    // dans la banque CCT, le plus récent datant de 2013 (CCT du 17/10/2013,
    // dépôt 118276/CO/316, cotisation groupes à risque 0,10%, valable
    // 2013-2014 SEULEMENT, expirée). Aucune trace d'un mécanisme « jours de
    // formation individuels ». La formation professionnelle des marins
    // passe par un circuit distinct (STCW international, recyclages
    // quinquennaux organisés par le Centre de Formation des Marins), pas
    // par un quota annuel de jours.
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT récente (dernière trace 2013, cotisation groupes à risque 2013-2014 expirée) n'établit de droit individuel à des jours de formation pour la CP 316 — la formation professionnelle du secteur (STCW, recyclages obligatoires) est organisée par le Centre de Formation des Marins asbl / Fonds de sécurité d'existence remorquage en mer, hors mécanisme de jours individuels",
      source: "Banque CCT du SPF Emploi (jc 3160000), 7 résultats réels, le plus récent daté de 2013 (dépôt 118276/CO/316), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
      aVerifier: true,
      aVerifierRaison: "Vérifier si le secteur maritime est exempté du droit supplétif de la loi du 03/10/2022 (statut international des marins) — non tranché ici.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 316: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 317 — Services de gardiennage et/ou de surveillance (généré batch 29/04/2026)
  '317': {
    // ── Avantage SECTORIEL — statut de la délégation syndicale ───────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 30/10/2003 (CP 317), statut
    // de la délégation syndicale et formation syndicale, banque CCT du SPF
    // Emploi, 317/317-2004-000231.pdf, art. 2 et section employés.
    //
    // 🔎 CETTE FICHE N'EXISTERAIT PAS SANS LA LECTURE PAR COLONNES. Le PDF est
    // bilingue ; lu ligne à ligne, l'article rendait une bouillie
    // franco-néerlandaise inexploitable. Relu colonne par colonne
    // (app/lib/pdf-colonnes.js), il dit, mot pour mot : « La présente section
    // applicable aux employeurs et aux ouvriers des entreprises où sont
    // occupés en moyenne 5 ouvriers au moins ».
    //
    // ⚠️ SEUIL TRÈS BAS, ET C'EST VOULU DANS CE SECTEUR — cinq personnes. Le
    // relever par analogie avec les autres CP (20, 30, 35) serait une erreur
    // grossière : le gardiennage a négocié ce plancher-là.
    // ⚠️ DEUX SECTIONS DISTINCTES, ouvriers et employés, avec le même seuil
    // mais des délégations séparées.
    avantages: {
      delegationSyndicale: {
        critere: 'effectif_moyen',
        seuilOuvriers: 5,
        seuilEmployes: 5,
        seuilTravailleurs: 5,
        modeCalcul: 'effectif occupé en moyenne ; sections ouvriers et employés distinctes',
        source: 'CCT du 30/10/2003 (CP 317) — statut de la délégation syndicale et formation syndicale, banque CCT du SPF Emploi, 317/317-2004-000231.pdf. Texte primaire lu par colonnes.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 317 — Services de gardiennage et/ou de surveillance',
    type: 'O',
    // Corrigé 38→37 (vague 1, 2026-07-30) : le gardiennage est à 37h/sem (moyenne annuelle).
    // Sources (2, concordantes) : SPF salairesminimums.be, CP 3170000, jcId 52962fda66f84a7199077e2bccf4a712
    // + ACCG-FGTB (durée du travail, secteur gardiennage). L'ancienne valeur 38 venait du batch générique 29/04/2026.
    regimeHebdo: 37,
    indexation: {
      indicePivot: 98.31, indicePivotBase: '2025', indicePivotSource: "CCT du 22/04/2026 (CP 317, gardiennage), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Les salaires payes a partir du 1er fevrier 2026 correspondent a l'indice pivot 98,31 (base 2025). »", indicePivotDateVerif: '2026-08-24', frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', note: '+2% au 01/02/2026 sur baremiques+effectifs. Source: AUDIT 2026-06 CGSLB Flash 202602 CP 317' },
    // ── LE TAUX EST FIXÉ PAR LE CONSEIL D'ADMINISTRATION, PAS PAR LA CCT ───
    // ✅ CCT du 15/09/2016 « modification et coordination des statuts du Fonds
    // de sécurité d'existence », chapitre V, art. 17 à 19 — dépôt
    // 135595/CO/317, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.
    // Art. 18, mot pour mot : « Le montant total des cotisations patronales
    // dues au fonds […] est un pourcentage calculé sur le montant de la masse
    // salariale totale à 100 p.c., des travailleurs(euses) OUVRIERS(ÈRES),
    // DÉTERMINÉ PAR LE CONSEIL D'ADMINISTRATION du fonds à l'unanimité et
    // entériné par la Commission paritaire. »
    //
    // ⛔ Le taux n'est donc dans aucune convention : il est voté. L'art. 19
    // confirme que tout changement passe par une décision du conseil, ensuite
    // entérinée. Les 96 conventions de la CP 317 ont été passées au crible :
    // treize touchent au fonds, aucune ne chiffre la cotisation générale.
    //
    // 📐 CE QU'ON APPREND QUAND MÊME, ET QUI COMPTE : l'assiette est la masse
    // salariale **à 100 %** (pas 108) et elle ne vise que les **OUVRIERS**.
    // Les employés relèvent d'un régime distinct — leur prime syndicale est
    // « fixée par une convention collective de travail » propre (art. 6bis).
    // Servir le même taux aux deux publics est donc au minimum imprécis.
    //
    // ⚠️ Le 5 % — pardon, le 4,5 % — reste faute de mieux, comme pour la CP 326 :
    // le fonds EXISTE et perçoit, c'est son taux qui n'est pas publié. Mettre 0
    // dirait à un client qu'il ne doit rien, ce qui serait faux.
    // ── CENSUS REFAIT SUR LE BON DÉNOMINATEUR (26/08/2026) ────────────────────
    // La note du 25/08 concluait « les 96 conventions ont été passées au crible
    // sans le trouver ». La banque CCT en publie **269**. Le census a donc été
    // REFAIT en entier — et cette fois il tient : aucune des 269 ne fixe le
    // taux de cotisation générale.
    //
    // ✅ CETTE FOIS LA CONCLUSION EST FERME, parce que la source dit elle-même
    // pourquoi : l'art. 18 des statuts prévoit que le taux est « déterminé par
    // le conseil d'administration du fonds à l'unanimité et entériné par la
    // Commission paritaire ». Il n'a donc jamais vocation à figurer dans une
    // convention déposée. C'est le cas du point 8 du playbook : la source
    // répond, et sa réponse est « ce n'est pas publié ici ».
    //
    // ⛔ DEUX FAUSSES PISTES ÉCARTÉES, lues au texte primaire le 26/08 — elles
    // portent le mot « cotisation » dans leur titre et n'ajoutent pourtant rien
    // au coût sectoriel :
    //   · « Utilisation de la cotisation de responsabilisation » (CCT du
    //     21/05/2025, dépôt 193720/CO/317) — la cotisation de responsabilisation
    //     est FÉDÉRALE (loi-programme du 27/12/2021, art. 147 ; AR du
    //     01/10/2023). Elle est déjà due à l'ONSS par l'employeur dont les
    //     malades de longue durée dépassent le seuil. Cette CCT se borne à
    //     DÉSIGNER le fonds qui en reçoit le produit et à dire à quoi il sert.
    //     L'ajouter au taux sectoriel compterait deux fois la même charge.
    //   · « Fonds de pénibilité » (CCT du 18/01/2024, dépôt 185715/CO/317) —
    //     son art. 3 dit expressément que ses activités « sont financées par
    //     les ressources EXISTANTES du Fonds ». Aucune cotisation nouvelle.
    //
    // ⚠️ ON NE MET PAS 0 : le fonds existe, il perçoit, et son assiette est
    // connue. Seul le pourcentage manque, et il ne manque pas par négligence.
    fondsSE: {
      nom: "Fonds de sécurité d'existence pour les services de gardiennage et/ou de surveillance",
      taux_patronal: 0.045,
      base: "masse salariale totale à 100 % des travailleurs OUVRIERS (art. 18 des statuts) — PAS 108 %, et pas les employés, qui relèvent d'un régime distinct (art. 6bis)",
      aVerifier: true,
      aVerifierRaison: "Le 4,5 % n'a AUCUNE source, et il n'en aura pas par la voie publique : l'art. 18 des statuts (CCT du 15/09/2016, dépôt 135595/CO/317) prévoit que le taux est « déterminé par le conseil d'administration du fonds à l'unanimité et entériné par la Commission paritaire » — il n'a donc jamais vocation à figurer dans une convention déposée. Census REFAIT le 26/08/2026 sur les 269 conventions publiées (et non 96 comme l'annonçait la note précédente, dénominateur faux issu d'une recherche filtrée) : aucune ne le fixe. Deux titres trompeurs écartés au texte primaire — la cotisation de responsabilisation est FÉDÉRALE et déjà due à l'ONSS, cette CCT désigne seulement le fonds bénéficiaire ; le fonds de pénibilité est financé « par les ressources existantes du Fonds » (art. 3). Ce qui est acquis : l'assiette est la masse salariale à 100 % et elle ne vise que les OUVRIERS. SEULE VOIE RESTANTE : demander le taux au fonds lui-même — Rue Jean-Baptiste Janssen 43-45, 1080 Bruxelles (siège lu dans la CCT du 21/05/2025) — ou à un secrétariat social du secteur. Le fonds existe et perçoit : on ne met pas 0.",
      censusComplet: {
        date: '2026-08-26',
        conventionsLues: 269,
        conclusion: "aucune convention ne fixe le taux de cotisation générale, et les statuts expliquent pourquoi",
        remplaceUnCensusFaux: "96 conventions annoncées le 25/08/2026 — dénominateur issu d'une recherche filtrée par mots-clés",
      },
      statutsSource: "CCT du 15/09/2016 (CP 317, services de gardiennage et/ou de surveillance), chapitre V « Montants des cotisations patronales dues au fonds », art. 18 — dépôt 135595/CO/317, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026.",
      statutsDateVerif: '2026-08-26',
      contactFonds: "Fonds voor bestaanszekerheid van de bewaking, Rue Jean-Baptiste Janssen 43-45, 1080 Bruxelles — adresse lue dans la CCT du 21/05/2025 (dépôt 193720/CO/317), art. 3.",
    },
    // 🔴 VALEUR NON REPRÉSENTATIVE DE LA MAJORITÉ DU SECTEUR, signalé le
    // 05/09/2026 — NE PAS CHANGER SANS RESTRUCTURATION (le schéma actuel n'a
    // qu'un seul triplet par CP, or ce secteur en a QUATRE distincts) :
    // Synova + ACCG (Centrale Générale-FGTB), concordants —
    //   convoyeurs de fonds, personnel roulant : 8,00 € (6,91 employeur) — LA
    //     VALEUR CI-DESSOUS, mais c'est une MINORITÉ du secteur ;
    //   convoyeurs de fonds, personnel non-roulant : 3,70 € (2,61 employeur) ;
    //   personnel opérationnel (majorité des gardiens) : 4,19 € (3,10 employeur) ;
    //   personnel administratif : 4,46 € (3,37 employeur).
    // Évolution connue : +1 € part patronale au 01/05/2026 (minimum 2,09 € pour
    // tous), +1 € de plus au 01/12/2026 (minimum 3,09 €).
    // Servir 8,00 € par défaut à un client CP 317 SURESTIME le chèque-repas
    // pour la majorité de ses gardiens (opérationnels/administratifs). Le
    // corriger correctement demande une structure par catégorie de fonction,
    // pas un remplacement de nombre — hors périmètre de cette session.
    chequesRepas: {
      obligatoire: true, valeur: 8.0, part_employeur: 6.91, part_travailleur: 1.09, aVerifier: true,
      aVerifierRaison: "Valeur exacte pour les convoyeurs de fonds (personnel roulant) SEULEMENT — la majorité du secteur (personnel opérationnel/administratif) touche 4,19 € ou 4,46 €. Voir commentaire ci-dessus.",
      source: 'Synova (ex-CGSLB) + ACCG (Centrale Générale-FGTB), gardiennage, concordants',
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (317/317-2020-008303.pdf, CCT du 18/05/2020, dépôt 159556/CO/317,
    // « toujours d'application »). Art. 1er-2 : réservé aux « transporteurs
    // de fonds » (personnel roulant) — même sous-population que la note
    // chequesRepas ci-dessus. 150 EUR/an depuis le 01/01/2019, période de
    // référence année civile, payé en janvier, assimilations (maladie,
    // chômage temporaire, congés légaux…) et prorata temps partiel prévus.
    // ⚠️ Pas une obligation générale du secteur gardiennage/surveillance.
    ecoChequesConditionnel: {
      montant: 150,
      condition: "Réservé aux travailleurs exerçant la fonction de « transporteur de fonds » (personnel roulant du transport de fonds), pas à l'ensemble du personnel de gardiennage/surveillance (art. 1er).",
      source: "CCT du 18/05/2020 (317/317-2020-008303.pdf), banque CCT du SPF Emploi, dépôt 159556/CO/317, texte primaire lu le 21/09/2026, art. 2 : 150 EUR/an depuis le 01/01/2019, payé en janvier.",
      dateVerif: '2026-09-21',
    },
    // 🔴 CORRIGÉ LE 24/09/2026 — `ecoChequesAnnuel: 250` recopié était resté
    // à côté du `ecoChequesConditionnel` ci-dessus malgré la recherche du
    // 21/09 : aucune CCT ne donne 250 EUR à toute la CP 317, seul le régime
    // conditionnel des transporteurs de fonds (150 EUR) existe. Confirmé une
    // 2e fois indépendamment le 24/09/2026 (audit 20.25, relecture directe de
    // la même CCT — l'extraction unpdf porte une substitution OCR
    // systématique 1→l, ex. « l.50 EUR » = 150 EUR, cohérente sur tout le
    // document : « 20l.9 » = 2019, « 3l. » = 31, etc.).
    ecoChequesAnnuel: null,
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — la valeur 5 jours est confirmée exacte pour
    // 2026, mais le champ « fonds » était faux : CCT du 26/06/2024 (dépôt
    // 188666/CO/317), art. 7 — crédit de formation individuel/an, plein
    // temps : 2023=2j, 2024=3j, 2025=4j, à partir du 01/01/2026=5j/an,
    // palier suivant dès 2029. Aucun fonds financeur dédié : crédit à
    // charge DIRECTE de l'employeur (temps de travail rémunéré, art. 12/14)
    // — le fondsSE du secteur (déjà présent ci-dessus) finance autre chose
    // (chèques-repas, éco-chèques, 2e pilier).
    formation: {
      joursSectoriels: 5,
      fondsFormationExiste: false,
      base: "crédit de formation individuel annuel, temps plein — à charge directe de l'employeur (temps de travail rémunéré), PAS un fonds sectoriel dédié",
      source: "CCT du 26/06/2024 (CP 317), art. 7 : 2023=2j, 2024=3j, 2025=4j, à partir du 01/01/2026=5j/an, dépôt 188666/CO/317, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    // ✅ CORRIGÉ LE 05/09/2026 : `seuilHebdomadaire` 38 → 42, arbitrage rendu.
    // Fiche SPF Emploi Limosa CP317 (mise à jour 05/05/2026, lue le 05/09/2026),
    // §2.2.8 / art. 11 §2 de la CCT du 12/12/2013 (119473) : « Sont considérées
    // comme heures supplémentaires... toutes les heures au-delà des 9 heures...
    // ou au-delà des 42 heures par semaine. » 🟠 Corroboré par accg.be (secteur
    // gardiennage) : « sursalaires après 42 heures de prestations effectives par
    // semaine ». Aucune source ne confirmait 38 h — probable confusion avec une
    // autre CP. `regimeHebdo: 37` (durée normale) reste correct et distinct,
    // sourcé par ailleurs : ce sont deux notions différentes (durée normale vs
    // seuil de déclenchement des heures sup), comme pour la CP 121.
    // ⚠️ RÉTROACTIVITÉ : s'applique aux paies calculées à partir du 05/09/2026 ;
    // reprise des paies passées à trancher séparément.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 42,
      dateVerif: '2026-09-05',
      source: 'Fiche Limosa SPF Emploi CP317 (05/05/2026) §2.2.8, CCT 12/12/2013 (119473) art.11§2 ; corroboré accg.be.',
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 317: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB gardiennage + APEG-BVBO',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 318.01 — Aides familiales — Communauté française, RW et germ. (généré batch 29/04/2026)
  '318.01': {
    nom: 'CP 318.01 — Aides familiales — Communauté française, RW et germ.',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex) — 2e mois après dépassement pivot 133,28 (effet 02/2026), vérifié 2026-07-16. Source: CSC / Unisoc / FILE asbl.' },
    // ⚠️ 5 % INVENTÉ → TROIS TAUX SELON LE PUBLIC, LUS AU TEXTE PRIMAIRE (26/08/2026).
    // Statuts du 05/02/2020 (dépôt 157725/CO/318.01, AR du 24/11/2020), art. 8 :
    // « Les employeurs versent sur le compte bancaire du fonds :
    //     1,08 p.c. de la masse salariale brute portée à 100 p.c. pour les
    //       travailleurs qu'ils occupent sous statut OUVRIER ;
    //     0,44 p.c. de la masse salariale brute pour les travailleurs qu'ils
    //       occupent sous statut EMPLOYÉ, HORS AIDES FAMILIALES ;
    //     1,08 p.c. de la masse salariale brute portée à 100 p.c. pour les
    //       AIDES FAMILIALES sous statut employé. »
    //
    // ⚠️ LA TROISIÈME LIGNE EST UN PIÈGE, ET ELLE EST LA PLUS FRÉQUENTE ICI.
    // Une aide familiale sous statut employé paie 1,08 % et non 0,44 % — dans
    // un secteur qui s'appelle « services des aides familiales », c'est le cas
    // GÉNÉRAL, pas l'exception. Notre modèle ne distingue que deux publics :
    // `taux_patronal_employe` porte donc le 0,44 % du personnel administratif,
    // et le cas des aides familiales est nommé dans la donnée pour être branché
    // quand la fiche employé saura dire la fonction.
    // ⛔ Sens de l'erreur assumé : pour une aide familiale employée, l'app
    // sous-comptera de 0,64 point tant que ce n'est pas branché. C'est écrit.
    // Art. 9 : les cotisations sont versées avant le 15 mars de chaque année.
    fondsSE: {
      nom: "Fonds de sécurité d'existence des services des aides familiales et des aides seniors (Communauté française)",
      taux_patronal: 0.0108,
      taux_patronal_employe: 0.0044,
      base: 'masse salariale brute portée à 100 %',
      cctSourcee: {
        taux: 0.0108,
        tauxOuvrier: 0.0108,
        tauxEmployeHorsAidesFamiliales: 0.0044,
        tauxAideFamilialeSousStatutEmploye: 0.0108,
        base: 'masse salariale brute portee a 100 %',
        echeance: 'versement avant le 15 mars de chaque annee (art. 9)',
        source: "Statuts du 05/02/2020, art. 8 — depot 157725/CO/318.01, AR du 24/11/2020, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Le 5 % qui figurait ici n'avait aucune source.",
        dateVerif: '2026-08-26',
        reserveEcrite: "l'app ne distingue que deux publics : une AIDE FAMILIALE sous statut employe doit 1,08 % et non 0,44 %, et c'est le cas general dans ce secteur. Tant que la fiche employe ne dit pas la fonction, l'app sous-compte de 0,64 point pour ces travailleuses.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3180100 : 0 résultat "maaltijdcheque". Les
    // seules CCT liées à un avantage pécuniaire sont "chèques-consommation"
    // (2020, one-off COVID, dépôt 162942) et un avantage social complémentaire
    // versé par le Fonds social (CCT 2001 dépôt 66556, CCT 2007 dépôt 85309) —
    // pas des chèques-repas.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 318.01 (jcId 3180100) — aucune CCT chèque-repas, seuls des chèques-consommation ponctuels et un avantage social complémentaire distincts",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 318.01 (aides familiales et aides seniors) — CCT lue (accord non-marchand Cocof/Cocom), texte primaire lu le 21/09/2026 : une prime forfaitaire brute non indexée de 64 EUR REMPLACE la mesure éco-chèques de l'accord non-marchand Cocof de 2010 — donc plus de régime éco-chèques, converti en prime cash. Aucune autre CCT n'en fait mention.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — les 10 CCT les plus récentes (2022-2025)
    // lues en entier (classification de fonctions, groupes à risque ×2,
    // cotisation de responsabilisation, digitalisation, stabilité des
    // contrats) : aucune n'institue de droit individuel à des jours de
    // formation. Le seul mécanisme « formation » du secteur est un effort
    // COLLECTIF de 0,15% de la masse salariale pour les groupes à risque.
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT sectorielle CP 318.01 n'institue un droit individuel à des jours de formation annuels — parmi les 10 CCT les plus récentes de la banque du SPF Emploi (2022-2025), aucune ne porte ce mécanisme. L'effort formation du secteur est collectif : 0,15% de la masse salariale pour les groupes à risque (CCT du 17/10/2025, dépôt 196212/CO/318.01, art. 3)",
      source: "Banque CCT du SPF Emploi, CP 318.01, 10 CCT les plus récentes (2022-2025) lues intégralement le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 318.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 318.02 — Aides familiales — Communauté flamande (généré batch 29/04/2026)
  '318.02': {
    nom: 'CP 318.02 — Aides familiales — Communauté flamande',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand aides familiales fl. (spilindex, +2% ~2e mois après dépassement pivot, effet 02/2026), CONFIRMÉ 2026-07-16. Source: CSC CNE + CGSLB + Horval.' },
    // ⚠️ IL N'Y A PAS DE FONDS DE SÉCURITÉ D'EXISTENCE ICI — corrigé le
    // 25/08/2026. La fiche portait `taux_patronal: 0.05` (5 % du brut), sans
    // source d'aucune sorte. Deux sources indépendantes disent qu'il n'y a
    // rien à ce titre :
    //   · ONSS, fichier des taux DmfA 2026/3 — la catégorie d'employeur 211,
    //     la seule rattachée à cette sous-commission, ne porte AUCUNE ligne de
    //     fonds de sécurité d'existence (codes 820/830). Elle ne porte que les
    //     codes 825 et 835, qui sont le PENSION sectoriel.
    //   · Banque CCT du SPF Emploi, 25/08/2026 — sur les 283 conventions de la
    //     SCP 318.02, les 30 rangées sous le thème « fonds de sécurité
    //     d'existence » concernent TOUTES le second pilier de pension, le
    //     Fonds Maribel social ou le fonds de formation. Aucune n'institue une
    //     cotisation générale sur le brut. (Dénominateur écrit : 30 sur 30, pas
    //     un échantillon.)
    // ⛔ Baisser un taux fait baisser un coût employeur affiché, et la doctrine
    // maison dit qu'un sous-comptage est pire qu'un surcoût. Mais 5 % n'était
    // pas prudent : c'était inventé. Garder un chiffre faux parce qu'il est
    // élevé, c'est le signer.
    fondsSE: {
      nom: 'Fonds social 318.02 de financement du second pilier de pension',
      taux_patronal: 0,
      onssSourcee: true,
      onssCategorie: '211',
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      // Ce qui EXISTE réellement, et qui n'est pas un FSE : un second pilier.
      // Il n'entre pas dans `taux_patronal` — l'y mettre le ferait afficher
      // sous l'étiquette « fonds de sécurité d'existence », ce qu'il n'est pas.
      pensionSectorielle: {
        tauxAnnuelParTrimestre: 0.0022,
        tauxPercuT3T4_2026: 0.0044,
        assiette: 'montant brut des rémunérations, avant retenue des cotisations personnelles de sécurité sociale',
        source: "CCT du 05/02/2026 (SCP 318.02), art. 2, dépôt 197958/CO/318.02 du 09/02/2026, durée indéterminée — banque CCT du SPF Emploi, 31802/31802-2026-001672.pdf, texte primaire lu le 25/08/2026 : « le pourcentage des cotisations pour l'année 2026 sur base annuelle est fixé comme suit : par trimestre 0,22 p.c. du montant brut des rémunérations […] pas de perception aux premier et deuxième trimestres ; 0,44 p.c. […] aux troisième et quatrième trimestres. »",
        dateVerif: '2026-08-25',
        tauxOnss: 0.0048,
        ecartANommer: "L'ONSS perçoit 0,48 % sous les codes 825/835 au trimestre 2026/3, là où la CCT annonce 0,44 % pour ce même trimestre : 0,04 point d'écart, NON expliqué à ce jour. À trancher avant d'annoncer un coût employeur dessus — on ne choisit pas le chiffre le plus commode.",
      },
      source: "ONSS, fichier des taux DmfA 2026/3 (catégorie d'employeur 211) : aucune ligne de fonds de sécurité d'existence, seulement le pension sectoriel sous les codes 825/835. Recoupé le 25/08/2026 sur la banque CCT du SPF Emploi — 30 conventions sur 283 portent le thème « fonds de sécurité d'existence » et toutes visent le second pilier, le Maribel social ou la formation.",
      note: "Le 5 % qui figurait ici avant le 25/08/2026 n'avait aucune source et ne correspond à rien de perçu.",
    },
    chequesRepas: { obligatoire: true, valeur: 5.0, part_employeur: 3.91, part_travailleur: 1.09, source: "CCT du 04/12/2025 (SCP 318.02, aides familiales et aides seniors de la Communauté flamande), modifiant l'art. 5 de la CCT du 13/12/2021 (n° 172233/CO/318.02), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Par conséquent, à partir de la date d'entrée en vigueur de la présente CCT, la valeur nominale de chaque chèque-repas est de 5,00 euros. L'intervention du travailleur dans le montant du chèque-repas est de 1,09 euro et sera déduite de son salaire net. » ⚠️ PÉRIMÈTRE : travailleurs occupés dans le cadre d'un contrat de travail TITRES-SERVICES.", dateVerif: '2026-08-24', enVigueurLe: '2025-12-04', lien: '31802/31802-2025-015152.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 318.02 (aides familiales/seniors Communauté flamande) — CCT du 29/11/2017 (144647/CO/318.02, travailleurs titres-services), texte primaire lu le 21/09/2026, art. 4 : « eenmalig ecocheques toegekend voor een maximale totale waarde van 225 EUR » (octroi UNIQUE, pas récurrent), pour la période de référence 01/01/2017-31/12/2017 uniquement. Aucune CCT plus récente n'en fait mention.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 318.02' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 318.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 319 — Établissements et services d\ (généré batch 29/04/2026)
  '319': {
    nom: "CP 319 — Établissements et services d'éducation et d'hébergement",
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', source: 'AUDIT 2026-06 — Easypay/CSC' },
    fondsSE: {
      nom: 'Fonds social CP 319',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '462',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 462 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3190000, thème 246 et texte libre : 0
    // résultat. La CP a pourtant 3+ conventions actives sur presque tous les
    // autres axes — le thème chèques-repas y est spécifiquement absent.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 319 (jcId 3190000) — recherche thème et texte libre, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 319 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi). Sous-CP 319.01/319.02 à
    // vérifier séparément (dépôts distincts).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 319 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — 9 CCT déposées sous ce numéro (ensemble
    // exhaustif de la banque CCT) : rémunération, crédit-temps,
    // classification, prépension, promotion de l'emploi — aucune sur la
    // formation. Cohérent avec le correctif du même jour sur 319.01 (même
    // verdict, même méthode).
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT sectorielle CP 319 n'institue de droit individuel à des jours de formation — les 9 CCT déposées sous ce numéro (ensemble exhaustif, banque CCT du SPF Emploi) portent sur rémunération, crédit-temps, classification, prépension, promotion de l'emploi ; aucune sur la formation. Cf. TAUX_SECTORIELS['319.01'].formation, même verdict le même jour.",
      source: "Banque CCT du SPF Emploi, CP 319, 9 CCT déposées (ensemble exhaustif) — titres lus intégralement, les 5 plus récentes lues en texte complet le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 319: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 319.01 — Éducation et hébergement — Communauté flamande (généré batch 29/04/2026)
  '319.01': {
    nom: 'CP 319.01 — Éducation et hébergement — Communauté flamande',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex) — 2e mois après dépassement pivot (effet 02/2026), vérifié 2026-07-16. Source: SOM / FILE asbl.' },
    fondsSE: {
      nom: 'Fonds social CP 319.01',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      pension_sectorielle: 0.0048,
      pension_sectorielle_employe: 0.0048,
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "825", "type": "8", "taux": 0, "forfait": false}],
      base108Ouvrier: true,
      onssCategorie: '062',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 062 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3190100 : 0 résultat (thème 246 et texte
    // libre). CP également très active sur les autres axes.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 319.01 (jcId 3190100) — recherche thème et texte libre, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 319.01 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — les 10 CCT les plus récentes (2022-2026)
    // lues en entier (engagements de pension sectorielle, projet de
    // reconversion #KiesVoorPersoonsbegeleider — formation d'un travailleur
    // EXTÉRIEUR au secteur en 2e carrière, cotisation de responsabilisation
    // malades longue durée, aidants qualifiés) : aucun droit individuel à
    // des jours de formation.
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT sectorielle CP 319.01 n'institue un droit individuel à des jours de formation annuels — parmi les 10 CCT les plus récentes (2022-2026), aucune ne porte ce mécanisme",
      source: "Banque CCT du SPF Emploi, CP 319.01, 10 CCT les plus récentes (2022-2026) lues intégralement le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 319.01: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 319.02 — Éducation et hébergement — Communauté fr. et ger. (généré batch 29/04/2026)
  '319.02': {
    nom: 'CP 319.02 — Éducation et hébergement — Communauté fr. et ger.',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand éducation/hébergement CF-germ. (spilindex, +2% ~2e mois après dépassement pivot, effet 02/2026), CONFIRMÉ 2026-07-16. Source: CSC CNE + CGSLB.' },
    fondsSE: {
      nom: 'Fonds social CP 319.02',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '162',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 162 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3190200 : 0 résultat en thème 246 et en
    // texte « maaltijdcheque ». Une recherche texte « repas » remonte 2 CCT
    // (2017, 2002) lues : faux positifs, le mot matché est « repasseuses/
    // repasseurs » (métier du pressing), aucune occurrence de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 319.02 (jcId 3190200) — recherche thème et texte libre, faux positifs sur « repasseuses » écartés, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 319.02 (éducation/hébergement) — 6 CCT lues (2013, 2021 x2, 2022 x2, 2023). La plus récente (184999/CO/319.02, texte primaire lu le 21/09/2026) : art. 6 « Pour l'année 2023, des éco-chèques sont EXCEPTIONNELLEMENT octroyés » (250 EUR), financée par le solde d'une subvention 2023 de l'accord non-marchand wallon 2021-2024 — octroi ponctuel lié à un budget exceptionnel, pas un régime récurrent. Même précédent que CP 330.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — pas de droit sectoriel général à des jours
    // de formation. Un mécanisme réel existe mais est un budget monétaire,
    // pas des jours, et limité à un sous-ensemble d'employeurs : CCT du
    // 27/04/2023 (dépôt 180383/CO/319.02) accorde 300€/ETP pour la
    // formation, réservé aux Agences Immobilières Sociales et Associations
    // de Promotion du Logement agréées/subsidiées par la Région wallonne
    // (art. 1-3). Non généralisable à toute la sous-commission.
    formation: {
      joursSectoriels: null,
      renvoi: "pas de droit sectoriel général à des jours de formation en CP 319.02. Un mécanisme réel existe mais est un budget monétaire, pas des jours, et limité à un sous-ensemble d'employeurs : CCT du 27/04/2023 (dépôt 180383/CO/319.02) accorde 300 €/ETP pour la formation, réservé aux Agences Immobilières Sociales et Associations de Promotion du Logement agréées/subsidiées par la Région wallonne (art. 1-3). Non généralisable à toute la sous-commission.",
      budgetPartielExistant: { euroParEtp: 300, perimetre: 'AIS et APL agréées/subsidiées Région wallonne uniquement', source: 'CCT du 27/04/2023, dépôt 180383/CO/319.02', dateVerif: '2026-09-26' },
      source: "Banque CCT du SPF Emploi, CP 319.02, CCT du 27/04/2023 (dépôt 180383/CO/319.02), texte primaire lu le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 319.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 320 — Pompes funèbres (généré batch 29/04/2026)
  '320': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 16/04/2026 (CP 320), banque CCT
    // du SPF Emploi, 320/320-2026-006851.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 16/04/2026 (CP 320) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 320/320-2026-006851.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 320 — Pompes funèbres',
    nomCourt: 'Pompes funèbres',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'semestrielle', mois: [1, 7], dernierTaux: 0.0205, derniereDate: '2026-07-01', source: 'AUDIT 2026-06 — Partena', note: 'CORRIGÉ 2026-08-05 : indexation du 01/07/2026 que nos données ignoraient (elles s\'étaient arrêtées à janvier). Mois 7 ajouté au calendrier — c\'est parce qu\'il manquait que la détection n\'a rien vu. ⚠️ SOURCE UNIQUE : Partena Professional « Adaptations salariales à partir du 1er juillet 2026 ». Écrit sur validation explicite du fondateur (05/08/2026), R2 demandant normalement deux sources concordantes — à re-confirmer dès qu\'une 2e source est accessible.' },
    fondsSE: {
      nom: 'Fonds social CP 320',
      taux_patronal: 0.13,
      taux_patronal_employe: 0.13,
      pension_sectorielle: 0.0054,
      pension_sectorielle_employe: 0.0054,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '320',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 320. Codes travailleur : 820 ouvriers 13,00 % · 830 employés 13,00 % · 825/835 pension sectorielle 0,54 %. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Entreprises de pompes funèbres »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      note: "Même taux pour les deux publics. Valeur codée précédente : 5,00 %.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.0, part_travailleur: 1.09, source: "CCT du 16/04/2026 (CP 320, entreprises de pompes funèbres), banque CCT du SPF, texte primaire lu le 24/08/2026, art. 4 : « Dans les entreprises où aucun chèque-repas n'est encore accordé, des chèques-repas avec une valeur faciale de 3,09 EUR seront introduits à partir du 1er juillet 2026. Par chèque repas la contribution patronale sera de 2 EUR et la contribution du travailleur sera de 1,09 EUR. » ⚠️ Ne vise QUE les entreprises sans régime existant ; le texte ne fixe rien pour les autres.", dateVerif: '2026-08-24', enVigueurLe: '2026-07-01', lien: '320/320-2026-006848.pdf' },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi.
    // Le secteur A EU un régime d'éco-chèques : CCT du 16/11/2009
    // (320/320-2010-000981.pdf, durée indéterminée) — 250 EUR/an (régime
    // ≥4/5 temps) ou 200 EUR/an (régime 3/5-4/5 temps), à partir du 1er
    // octobre, avec faculté de conversion en chèques-repas dès 2012 (CCT du
    // 21/10/2011, 320/320-2011-009234.pdf, art. 2). ⛔ MAIS les deux CCT
    // portent, au fichier officiel du SPF, « Fin de validité : 01/10/2016 » —
    // dénoncées (art. 3 du texte de 2011 : préavis de 3 mois par lettre
    // recommandée). Aucune CCT éco-chèques postérieure à 2016 n'existe sous
    // ce numéro. Cohérent avec le passage du secteur au chèque-repas comme
    // vecteur choisi (CCT du 16/04/2026 ci-dessus) : le sujet a changé de
    // véhicule, pas juste de montant.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 320 — régime historique (250/200 EUR/an, CCT du 16/11/2009) dénoncé, « Fin de validité : 01/10/2016 » au fichier officiel, aucune CCT éco-chèques active depuis. Texte primaire lu le 21/09/2026.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🟠 CORRIGÉ LE 26/09/2026, À RECONFIRMER — CCT du 16/04/2026 (dépôt
    // 199555/CO/320), art. 3 : « tous les travailleurs, hors occasionnels,
    // ont le droit de suivre, pendant le temps de travail, 5 jours de
    // formation par an, coûts salariaux à charge de l'employeur »,
    // prorata pour les temps partiels. ⚠️ N'entre en vigueur que le
    // 01/01/2027, durée déterminée jusqu'au 31/12/2028. Fonds institué
    // séparément (CCT du 10/07/2026, dépôt 201969/CO/320).
    formation: {
      jours: 5,
      fonds: 'Fonds de formation pour les entreprises de pompes funèbres (Opleidingsfonds 320)',
      base: "droit individuel annuel, coûts salariaux à charge de l'employeur, prorata pour les temps partiels",
      source: "CCT du 16/04/2026 (CP 320) « Droit à la formation », art. 3, dépôt 199555/CO/320 ; fonds institué par CCT du 10/07/2026, dépôt 201969/CO/320, banque CCT du SPF Emploi, textes primaires lus le 26/09/2026.",
      dateVerif: '2026-09-26',
      aVerifier: true,
      aVerifierRaison: "La CCT « Droit à la formation » n'entre en vigueur que le 01/01/2027 (durée déterminée jusqu'au 31/12/2028) — à re-confirmer à cette échéance.",
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 320: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 321 — Grossistes-répartiteurs de médicaments (généré batch 29/04/2026)
  '321': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 24/03/2026 (CP 321), banque CCT
    // du SPF Emploi, 321/321-2026-005319.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 24/03/2026 (CP 321) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 321/321-2026-005319.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 321 — Grossistes-répartiteurs de médicaments',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'index-pivot', dernierTaux: 0.02, derniereDate: '2026-05-01', note: 'INDEX-PIVOT confirmé (corrigé de « annuelle » 2026-07-16) : +2% au franchissement de l\'indice-pivot (dépassé avril 2026 → effet 01/05/2026). Sources: CGSLB CP 321 accord sectoriel 2025-2026 + SETCa + Securex. (Ancien blocage CCT 403 levé par 3 sources concordantes.)' },
    fondsSE: {
      nom: 'Fonds social CP 321',
      taux_patronal: 0.001,
      taux_patronal_employe: 0.001,
      base108Ouvrier: true,
      onssCategorie: '321',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 321 : 820 ouvriers 0,10 % · employés 0,10 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 5,00 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT du 24/03/2026 (dépôt 199499/CO/321, en
    // vigueur 01/04/2026, durée indéterminée), texte primaire lu intégralement :
    // ce n'est PAS une valeur faciale unique, c'est un PLANCHER PROGRESSIF selon
    // l'existant au 01/04/2026 : (a) aucun CR avant → introduction obligatoire,
    // part patronale ≥2€/chèque ; (b) CR existant avec part patronale <4€ →
    // +2€/chèque ; (c) CR existant avec part patronale ≥4€ → +1€/chèque. Aucune
    // valeur faciale nominale fixée par le texte.
    chequesRepas: {
      obligatoire: 'conditionnel',
      valeur: null,
      part_employeur: null,
      part_travailleur: null,
      conditionnelRaison: "plancher progressif selon la situation de l'employeur au 01/04/2026 : introduction à 2€ patronal minimum si aucun CR avant, +2€ si part patronale <4€, +1€ si ≥4€ — pas de montant sectoriel fixe",
      source: "CCT du 24/03/2026 (321/321-2026-…, dépôt 199499/CO/321), banque CCT du SPF Emploi, texte primaire lu le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (321/321-2009-006070.pdf, CCT du 08/07/2009, dépôt 95390/CO/321,
    // « toujours d'application »). Art. 5-6 : 125 EUR (prime unique) en
    // 2009, puis « 250 EUR vanaf 2010 » — RÉCURRENT depuis 2010, sans terme.
    // Temps partiel proratisé par palier (200/150/120/90 EUR). Paiement
    // annuel en juin (art. 7). Valeur nominale max. 10 EUR/chèque (art. 4).
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 08/07/2009 (321/321-2009-006070.pdf), banque CCT du SPF Emploi, dépôt 95390/CO/321, texte primaire lu le 21/09/2026, art. 5-6 : 250 EUR/an depuis 2010 (125 EUR ponctuel en 2009 seulement), proratisé par palier de temps partiel.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 26/09/2026 — seuls 9 résultats existent dans la banque
    // (pas de CCT déposée après 2021 : protocoles d'accord, accords
    // sectoriels, crédit-temps, salaires minimums, classification de
    // fonctions, tous lus). Aucun droit individuel à la formation ; seule
    // mention connexe : CCT du 09/11/2015 sur l'emploi et la formation des
    // groupes à risque (effort collectif, pas un droit individuel en
    // jours).
    formation: {
      joursSectoriels: null,
      renvoi: "aucune CCT sectorielle CP 321 n'institue un droit individuel à des jours de formation — les 9 CCT disponibles dans la banque du SPF Emploi (2009-2021, aucune plus récente déposée) ne portent que sur accords sectoriels, salaires minimums, classification de fonctions et crédit-temps ; seule mention connexe : CCT du 09/11/2015 sur l'emploi et la formation des groupes à risque (effort collectif, non un droit individuel en jours)",
      source: "Banque CCT du SPF Emploi, CP 321, 9 CCT disponibles (2009-2021) lues intégralement le 26/09/2026.",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 321: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE non-marchand',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 322 — Travail intérimaire et entreprises de proximité (généré batch 29/04/2026)
  '322': {
    nom: 'CP 322 — Travail intérimaire et entreprises de proximité',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'herite_utilisateur', dernierTaux: null, derniereDate: null, note: 'Intérim CP 322/322.00 — PAS de barème ni d\'indexation propre : suit la CP de l\'entreprise UTILISATRICE (Securex/CSC, vérifié 2026-07-16). Titres-services = entrée 322.01 séparée (index-pivot). Corrigé de « annuelle janvier » qui déclenchait une indexation fantôme.' },
    // ── FONDS DE SÉCURITÉ D'EXISTENCE — le plus lourd de la grille ─────────
    // ✅ CCT du 09/06/2022 « Institution d'un Fonds de sécurité d'existence pour
    // les intérimaires et fixation de ses statuts », art. 16, 17 et 18 — dépôt
    // 174460/CO/322, banque CCT du SPF Emploi, 322/322-2022-011200.pdf, texte
    // primaire lu le 26/08/2026.
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE, et il était DEUX FOIS
    // TROP BAS. Le vrai total est de 9,77 %. Sur un brut de 2 500 €, l'écran
    // annonçait 112,50 € de fonds là où l'employeur en doit 244,25 (263,75 sur
    // la base 108 % d'un ouvrier intérimaire). C'est le seul sens d'erreur qui
    // fasse perdre de l'argent à l'employeur qui budgète dessus.
    //
    // 🔁 ET CE 9,27 % FINANCE LA PRIME DE FIN D'ANNÉE — donc l'employeur ne la
    // paie PAS une seconde fois. L'art. 16 le dit : « Une cotisation globale
    // pour le financement de la prime de fin d'année et des avantages sociaux
    // […] s'élève à 9,27 p.c. » Et la CCT du 12/09/2022 (dépôt 175630/CO/322),
    // art. 2, met la prime « à charge du Fonds Social pour les intérimaires ».
    // Additionner ce taux et une prime de fin d'année facturée à l'employeur
    // compterait la même dépense deux fois. Voir `primeFinAnnee_pct` plus bas.
    fondsSE: {
      nom: 'Fonds Social pour les Intérimaires',
      taux_patronal: 0.0977,
      assiette: "rémunérations brutes ; pour les intérimaires OUVRIERS, rémunération brute à 108 %",
      cctSourcee: {
        taux: 0.0977,
        composantes: {
          cotisationGlobale: 0.0927,   // art. 16 — prime de fin d'année + avantages sociaux
          groupesARisque: 0.0010,      // art. 17, § 1er
          formation: 0.0040,           // art. 17, § 2
        },
        base: "rémunérations brutes des travailleurs intérimaires mis à disposition en Belgique ; pour les intérimaires ouvriers, la rémunération brute à 108 % (art. 16 et 17, « Procédure de perception »)",
        source: "CCT du 09/06/2022 (CP 322), statuts du Fonds Social pour les Intérimaires, art. 16 (« cette cotisation s'élève à 9,27 p.c. des rémunérations brutes »), art. 17 § 1er (0,10 %) et § 2 (0,40 %) — dépôt 174460/CO/322, banque CCT du SPF Emploi, 322/322-2022-011200.pdf, texte primaire lu le 26/08/2026. Les deux composantes de l'art. 17 sont confirmées en vigueur en 2026 : CCT du 23/06/2026 « Formation et emploi des travailleurs intérimaires », art. 5 § 2 (0,40 %, en vigueur du 01/07/2026 au 30/06/2027, dépôt 200711/CO/322) et CCT du 01/10/2025 « Mise au travail de groupes à risque », art. 3 et 4 (0,10 %, du 01/01/2025 au 31/12/2026, dépôt 195682/CO/322).",
        dateVerif: '2026-08-26',
        exclusion: "non due pour les intérimaires sans obligation de cotiser à l'ONSS (détachés relevant de l'art. 3 de la directive 96/71/CE, étudiants sous cotisation de solidarité) — art. 17",
      },
      // ⏰ ÉCHÉANCE : la composante « groupes à risque » cesse le 31/12/2026.
      expireLe: '2026-12-31',
      expireQuoi: 'la composante « groupes à risque » de 0,10 % — à revérifier au 1er janvier 2027',
      // 🏗️ SURCOÛT CIBLÉ, hors du taux général parce qu'il ne vise qu'un cas.
      supplementConstruction: {
        taux: 0.0035,
        condition: "intérimaires mis à disposition dans une entreprise relevant de la CP 124 (construction)",
        source: "art. 18 de la CCT du 09/06/2022 — « une cotisation de 0,35 p.c. sur les rémunérations brutes ». NON facturé par l'app à ce jour : l'entreprise UTILISATRICE n'est pas une donnée du dossier de paie.",
      },
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT du 11/03/2014 (dépôt 121732/CO/322, en
    // vigueur, aucune plus récente jusqu'en 2026), texte primaire lu intégralement,
    // art. 4 : « L'octroi de titres-repas aux travailleurs intérimaires se fait
    // dans le respect des MÊMES CONDITIONS que celles fixées par les conventions
    // en vigueur chez les UTILISATEURS » — cohérent avec `indexation` ci-dessus
    // (hérite de l'entreprise utilisatrice). Aucun montant sectoriel CP 322 :
    // seulement l'obligation procédurale de neutralité sociale.
    chequesRepas: {
      obligatoire: 'herite_utilisateur',
      valeur: null,
      part_employeur: null,
      part_travailleur: null,
      note: "art. 4 de la CCT du 11/03/2014 (dépôt 121732/CO/322) : le CR de l'intérimaire suit les mêmes conditions que chez l'entreprise utilisatrice — pas de montant sectoriel CP 322 propre",
      source: "CCT du 11/03/2014 (dépôt 121732/CO/322), banque CCT du SPF Emploi, texte primaire lu le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (322/322-2016-002009.pdf, CCT du 16/02/2016, dépôt 132753/CO/322).
    // Même mécanisme que chequesRepas ci-dessus : art. 4 « l'octroi des
    // éco-chèques aux travailleurs intérimaires se fait dans les mêmes
    // conditions que celles prévues par les conventions applicables chez
    // les utilisateurs » — cette CCT ne fixe AUCUN montant sectoriel, elle
    // règle seulement l'obligation procédurale de neutralité sociale
    // (art. 3, art. 19quater de l'AR du 28/11/1969) pour que les
    // éco-chèques échappent aux cotisations. Le montant réel dépend
    // entièrement de la CP de l'entreprise utilisatrice.
    ecoChequesAnnuel: null,
    ecoChequesSource: "CCT du 16/02/2016 (322/322-2016-002009.pdf), banque CCT du SPF Emploi, dépôt 132753/CO/322, texte primaire lu le 21/09/2026, art. 4 : le CR intérimaire suit les mêmes conditions que chez l'entreprise utilisatrice — pas de montant sectoriel CP 322 propre (même mécanisme que chequesRepas ci-dessus).",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    // ⚠️ « 1 mois brut (anc. min 6 mois) » était le libellé générique, et il
    // était faux sur les trois points qui comptent : le payeur, la période de
    // référence et la condition d'ouverture. Le détail sourcé vit dans le
    // registre dédié `app/lib/prime-fin-annee.js`, entrée '322'.
    primeFinAnnee_formule: "8,33 % du brut de la période de référence (1er juillet → 30 juin), payée en décembre PAR LE FONDS, dès 65 jours ou 494 heures",
    primeFinAnneePayeur: 'Fonds Social pour les intérimaires',
    primeFinAnneeSource: "CCT du 12/09/2022 (CP 322), art. 2, 4, 7 et 9 — dépôt 175630/CO/322, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Financée par la cotisation globale de 9,27 % de l'art. 16 des statuts du fonds : l'employeur ne la paie pas une seconde fois.",
    primeFinAnneeDateVerif: '2026-08-26',
    // ✅ CORRIGÉ LE 26/09/2026 — mécanisme confirmé mais chiffre faux pour
    // 2026 : CCT du 19/09/2023 (dépôt 182984/CO/322), art. 2-3, droit
    // individuel par ETP temps plein, calendrier progressif imposé par la
    // loi du 03/10/2022 (art. 54 §1er,2°) : 2j(2023)→2,5j(2024)→3j(2025)
    // →3,5j(2026, valeur actuelle)→4j(2027)→4,5j(2028)→5j(2029).
    formation: {
      jours: 3.5,
      joursCible: 5,
      joursCibleAnnee: 2029,
      trajectoire: { '2023': 2, '2024': 2.5, '2025': 3, '2026': 3.5, '2027': 4, '2028': 4.5, '2029': 5 },
      fonds: 'Fonds Social pour les Intérimaires',
      base: "droit individuel à la formation par ETP à temps plein, calendrier progressif (art. 54 §1er, 2° de la loi du 03/10/2022)",
      source: "CCT du 19/09/2023 (CP 322, intérim), dépôt 182984/CO/322, art. 2-3, banque CCT du SPF Emploi, texte primaire lu le 26/09/2026. Valeur en vigueur en 2026 : 3,5 jours/ETP (le calendrier atteint 5 jours seulement au 01/01/2029).",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 322: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 324 — Industrie et commerce du diamant (généré batch 29/04/2026)
  '324': {
    nom: 'CP 324 — Industrie et commerce du diamant',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (jcId b6829cfb…, CP 3240000, date=31/08/2022 en JJ/MM/AAAA avec slashes), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR. Le SPF a cessé de suivre cette commission au 01/09/2022 ; c'est l'état à cette date. La STRUCTURE est ce qui est codé — pas les montants.",
      systeme: 'trois activités, chacune avec ses propres catégories de fonction ; rémunération hebdomadaire et journalière',
      activites: [
        {
          cle: 'A', libelle: 'Grofbranche — taille, débrutage et sertissage de diamant de 0,75 carat poids brut par pièce ou plus',
          categories: ['Examinateur du diamant-spécialiste', 'Examinateur du diamant de première classe'],
        },
        { cle: 'B', libelle: 'Sciage, marquage, clivage du diamant', categories: ['Travailleur'] },
        {
          cle: 'C', libelle: 'Kleinbranche — taille, débrutage, sertissage, triage de diamant de moins de 0,75 carat poids brut par pièce, sertissage de pièces planes, sertissage pour coquille lumineuse, travail de pierres précieuses colorées, sertissage pour sciage',
          categories: ['Examinateur du diamant de deuxième classe, et autres fonctions'],
        },
      ],
      formation:
        'un contrat de formation professionnelle complémentaire existe, à durée et pourcentage progressifs (1re période 24 h/semaine à 60 % du salaire minimum, 2e période 32 h/semaine à 80 %).',
      montantsNonCodes: 'les salaires hebdomadaires et journaliers du 31/08/2022 ne sont pas repris — obsolètes.',
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', dernierTaux: 0.02, derniereDate: '2026-01-05', source: 'AUDIT 2026-06 — Easypay/Partena' },
    // ── FONDS POUR L'INDUSTRIE DIAMANTAIRE — et une assiette à 110 % ───────
    // ✅ CCT du 03/10/2022 « Institution et fixation des statuts d'un fonds pour
    // l'industrie diamantaire (coordination) », art. 19 à 22 — dépôt
    // 176066/CO/324, banque CCT du SPF Emploi, 324/324-2022-016528.pdf, texte
    // primaire lu le 26/08/2026.
    //
    // ⚠️ LE 4,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE — plus du double.
    //
    // 📐 ET L'ASSIETTE N'EST NI 100 % NI 108 %, MAIS 110 %. L'art. 21 renvoie à
    // la CCT du 25/03/1982 : « les rémunérations/salaires bruts sont déclarés à
    // 110 % » pour la déclaration à la Caisse nationale de vacances de
    // l'industrie diamantaire. C'est la seule assiette de ce genre dans la
    // grille — d'où le champ `coeffAssiette`, qui la déclare en clair plutôt
    // que de la faire deviner à une expression régulière.
    //
    // 📏 CE QUI EST ÉCRIT EST UN PLANCHER, ET C'EST DIT. Deux autres cotisations
    // existent et n'ont PAS de taux dans ce texte : l'art. 20 (fermeture
    // d'entreprises) renvoie au taux du Fonds fédéral d'indemnisation, l'art. 22
    // (groupes à risque) renvoie à la CCT du 12/07/2011. Les chiffrer d'ici
    // serait les inventer.
    fondsSE: {
      nom: "Fonds pour l'industrie diamantaire",
      taux_patronal: 0.02,
      coeffAssiette: 1.10,
      base: "rémunérations déclarées dans le cadre de la sécurité sociale, déclarées à 110 % (CCT du 25/03/1982, jours de repos compensatoire)",
      cctSourcee: {
        taux: 0.02,
        composantes: { remunerationPourReductionDuTempsDeTravail: 0.02 },  // art. 21
        base: "rémunérations déclarées dans le cadre de la sécurité sociale ; pour la déclaration à la Caisse nationale de vacances, les salaires bruts sont déclarés à 110 % (art. 21, renvoyant à la CCT du 25/03/1982 rendue obligatoire par AR du 08/09/1982)",
        periodicite: 'versement MENSUEL à la Caisse nationale de vacances de l\'industrie diamantaire',
        source: "CCT du 03/10/2022 (CP 324, industrie et commerce du diamant), statuts coordonnés, art. 21 : « Pour le financement du salaire visé à l'article 5, les employeurs versent une cotisation égale à 2 % des rémunérations déclarées dans le cadre de la sécurité sociale » — dépôt 176066/CO/324, banque CCT du SPF Emploi, 324/324-2022-016528.pdf, texte primaire lu le 26/08/2026.",
        dateVerif: '2026-08-26',
      },
      aVerifier: true,
      aVerifierRaison: "Deux cotisations des mêmes statuts n'ont pas de taux dans le texte : art. 20 (avantages liés à la fermeture d'entreprises — « au moins égale à la cotisation fixée pour le Fonds d'indemnisation des travailleurs licenciés en cas de fermeture d'entreprise ») et art. 22 (groupes à risque, renvoi à la CCT du 12/07/2011). Le 2 % écrit ici est donc un PLANCHER, pas le coût total du fonds.",
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT du 19/01/2026 (dépôt 197442/CO/324, en
    // vigueur 01/01/2026 → 01/01/2028, succède à celle du 27/10/2023), texte
    // primaire lu intégralement, art. 6 mot pour mot : « La participation aux
    // coûts des chèques-repas de l'employeur doit s'élever à 7,91 EUR par
    // chèque au minimum et la participation du travailleur s'élève au moins à
    // 1,09 EUR par chèque. » → valeur faciale minimale 9,00 € — ce sont des
    // PLANCHERS obligatoires. L'ancienne valeur codée (8,0/6,91/1,09) était
    // fausse et en dessous du minimum légal sectoriel.
    chequesRepas: {
      obligatoire: true, valeur: 9.00, part_employeur: 7.91, part_travailleur: 1.09,
      note: "planchers minimaux — l'entreprise peut accorder plus",
      source: "CCT du 19/01/2026 (324/324-2026-…, dépôt 197442/CO/324), art. 6, banque CCT du SPF Emploi, texte primaire lu le 05/09/2026",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 10/12/2021 (CP 324, industrie et commerce du diamant), banque CCT du SPF, thème 247, SEULE convention éco-chèques de cette CP, texte primaire lu le 24/08/2026 : « Les travailleurs bénéficient d'un écochèque de 100 EUR UNE FOIS EN 2021, à la charge de l'employeur. » ⚠️ VERSEMENT UNIQUE ET ÉCHU : il n'établit AUCUN régime récurrent. La fiche annonçait 250 EUR/an — un droit annuel qui n'existe pas.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 324' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 324: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 325 — Institutions publiques de crédit (généré batch 29/04/2026)
  '325': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 22/08/2026 — CCT du 04/12/2025 (CP 325), banque CCT
    // du SPF Emploi, 325/325-2025-014774.pdf. PDF bilingue relu COLONNE PAR COLONNE
    // (app/lib/pdf-colonnes.js) : lu ligne à ligne, l'article était illisible.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général.
    //
    // 🔎 Retenue au filtre OPÉRATOIRE : la phrase contenant « 55 ans » porte un
    // verbe qui FAIT quelque chose. Le même filtre a écarté, dans cette vague,
    // 106.02, 107, 114, 115, 116, 128, 129, 133, 143, 148, 225, 300, 304, 319,
    // 324 et 337 — leur CCT ne fait que citer la n° 179, ou ne la cite pas.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 04/12/2025 (CP 325) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 325/325-2025-014774.pdf. Texte primaire lu par colonnes, article opératoire confirmé.',
        dateVerif: '2026-08-22',
      },
    },
    nom: 'CP 325 — Institutions publiques de crédit',
    type: 'O',
    regimeHebdo: 36,
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // 🟠 CE SECTEUR N'A PAS DE GRILLE SECTORIELLE, ET CE N'EST PAS UN TROU DANS
    // NOS DONNÉES : c'est la règle qu'il s'est donnée. Elle est écrite, datée,
    // et elle a une conséquence concrète — la classification se contrôle au
    // niveau de l'entreprise, pas au niveau du secteur.
    classifications: {
      source:
        'CCT-cadre du 21/01/2025 (CP 325), art. 26 « Classifications de fonctions » — banque CCT du SPF Emploi, 325/325-2025-000643.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: null,
      grilleSectorielle: false,
      systeme:
        'aucune grille sectorielle : les entreprises du secteur basent leurs classifications de fonctions sur un système clair et transparent, sur lequel l\'avis des représentants des travailleurs est demandé',
      consequence:
        'la classification applicable est celle de l\'entreprise. Le contrôle porte donc sur deux points vérifiables — le système est-il clair et transparent, et l\'avis des représentants des travailleurs a-t-il été demandé — et non sur la comparaison à un barème de secteur.',
      // Vérifié sur les 8 CCT de la CP 325 que la recherche plein texte remonte
      // (2011 → 2025) : aucune ne contient de grille, l'art. 26 est reconduit
      // d'accord-cadre en accord-cadre.
      etendueVerification: '8 CCT de la CP 325 citant la classification, de 2011 à 2025, lues le 23/08/2026 — aucune ne porte de grille.',
    },
    indexation: { frequence: 'aucune', dernierTaux: null, derniereDate: null, note: 'Pas d index sectoriel — CCT entreprise (BNB/FSMA/Credendo). Source: AUDIT 2026-06 CGSLB/Synova CP 325' },
    // ── UN EFFORT COLLECTIF DU SECTEUR, PAS MÊME UNE OBLIGATION PAR TÊTE ──
    // ✅ CCT du 04/12/2025 « Effort en faveur des personnes appartenant aux
    // groupes à risque », art. 2 — dépôt 196838/CO/325, banque CCT du SPF
    // Emploi, texte primaire lu le 26/08/2026 : « les entreprises du secteur
    // s'engagent à affecter ENSEMBLE durant l'année 2026 au moins 0,10 % de la
    // masse salariale annuelle GLOBALE DU SECTEUR […] au recrutement, au
    // maintien et à la formation des personnes appartenant aux groupes à
    // risque ».
    //
    // ⚠️ LE 3,5 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE.
    //
    // ⛔ ET L'ENGAGEMENT EST ENCORE PLUS ÉLOIGNÉ D'UNE COTISATION QUE CELUI DE
    // LA CP 104. Il ne porte pas sur la masse salariale de CHAQUE entreprise
    // mais sur celle du SECTEUR pris ensemble : aucun employeur n'y a de
    // montant individuel dû, et il n'y a pas de fonds percepteur. Écrire un
    // taux ici facturerait une obligation qui n'existe pas à l'échelle d'un
    // employeur.
    fondsSE: {
      nom: "Institutions publiques de crédit — effort sectoriel collectif, pas de cotisation à un fonds",
      taux_patronal: 0,
      effortDeFormation: {
        taux: 0.001,
        base: "masse salariale annuelle globale DU SECTEUR (art. 23 de la loi du 29/06/1981)",
        nature: "engagement COLLECTIF : les entreprises du secteur s'engagent à affecter ensemble au moins 0,10 % — aucun montant individuel dû par employeur, aucun fonds percepteur",
        annee: 2026,
        source: "CCT du 04/12/2025 (CP 325, institutions publiques de crédit), art. 2 — dépôt 196838/CO/325, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Conclue en application de l'art. 189 de la loi du 27/12/2006.",
        dateVerif: '2026-08-26',
      },
      aVerifier: true,
      aVerifierRaison: "Le taux est mis à 0 parce que l'engagement de la CP 325 est COLLECTIF — 0,10 % de la masse salariale du SECTEUR pris ensemble, pas de chaque entreprise — et qu'aucun fonds ne le perçoit. Aucun montant n'est donc dû à l'échelle d'un employeur. Census : sur les 59 conventions que la banque CCT publie pour cette commission — census REFAIT le 26/08/2026, et non 25 comme l'annonçait la première mesure, dénominateur issu d'une recherche filtrée —, aucune n'institue de fonds de sécurité d'existence. Le filet a été élargi aux « accords sectoriels », la famille qui porte souvent ces cotisations ailleurs : l'Accord sectoriel 2025-2026 (CCT du 04/12/2025, 10 066 caractères) a été lu au texte primaire le 26/08/2026 et ne contient AUCUNE occurrence de « cotisation », « fonds de sécurité d'existence » ni d'aucun pourcentage. La conclusion tient donc désormais sur le bon dénominateur. À reconfirmer avant d'affirmer à un client qu'il ne doit rien.",
      note: "Le 3,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — Accord sectoriel 2025-2026 (CCT du 04/12/2025,
    // dépôt 196839/CO/325), seule convention CP 325 taguée thème chèques-repas,
    // texte primaire lu, art. 2 mot pour mot : dans les entreprises qui
    // accordent déjà des CR, « la possibilité de majorer de maximum 2€ …sera
    // DISCUTÉE » au niveau entreprise ; dans celles qui n'en accordent pas,
    // « la possibilité d'accorder un avantage équivalent…sera DISCUTÉE ».
    // Aucune obligation sectorielle, ni montant, ni même une hausse ferme —
    // une simple clause de discussion facultative. Le 10,0/8,91/1,09 codé
    // (plafond fédéral) n'a aucun fondement sectoriel.
    chequesRepas: {
      obligatoire: false,
      source: "Accord sectoriel 2025-2026 (CCT du 04/12/2025, dépôt 196839/CO/325), art. 2, banque CCT du SPF Emploi, texte primaire lu le 05/09/2026 — clause de discussion facultative au niveau entreprise, aucune obligation",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (325/325-2017-014648.pdf, CCT du 27/11/2017, dépôt 143356/CO/325,
    // « toujours d'application »). Art. 3.2-3.4 : « Les éco-chèques sont
    // octroyés chaque année, de façon récurrente » — 250 EUR/an pour un
    // travailleur temps plein sur période de référence complète (01/11 au
    // 31/10), proratisé pour le temps partiel/incomplet. Valeur nominale
    // max. 10 €/chèque (art. 3.1). Art. 4 : conversion possible en avantage
    // équivalent par accord paritaire ou CCT d'entreprise, sans dépasser le
    // coût patronal de 250 EUR.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 27/11/2017 (325/325-2017-014648.pdf), banque CCT du SPF Emploi, dépôt 143356/CO/325, texte primaire lu le 21/09/2026, art. 3.2-3.4 : 250 EUR/an récurrent, temps plein sur période de référence complète, proratisé sinon.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // ✅ CORRIGÉ LE 26/09/2026 — chiffre confirmé mais pas de fonds sectoriel :
    // CCT n° 191976/CO/325 du 21/01/2025 (identique à la CCT antérieure,
    // dépôt 325-2023-015729), art. 9 « Droit individuel à la formation » :
    // « chaque travailleur dispose d'un droit individuel à au moins 5 jours
    // de formation par an ». Organisé en interne à l'entreprise (rapport
    // annuel à la Commission permanente de l'emploi du secteur) — aucun
    // fonds sectoriel de formation mentionné.
    formation: {
      jours: 5,
      fonds: null,
      base: "droit individuel annuel à la formation professionnelle, organisé en interne à l'entreprise (rapport annuel à la Commission permanente de l'emploi du secteur) ; pas de fonds sectoriel dédié",
      source: "CCT n° 191976/CO/325 du 21/01/2025 (CP 325), art. 9 « Droit individuel à la formation », banque CCT du SPF Emploi, texte primaire lu le 26/09/2026 (confirmé identique dans la CCT antérieure, dépôt 325-2023-015729).",
      dateVerif: '2026-09-26',
    },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 325: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB finances + Febelfin',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 326 — Industrie du gaz et de l\ (généré batch 29/04/2026)
  '326': {
    nom: "CP 326 — Industrie du gaz et de l'électricité",
    nomCourt: "Gaz et électricité",
    type: 'O',
    regimeHebdo: 38,
    // 🔴 PÉRIMÉ CONFIRMÉ LE 24/09/2026 (audit 20.25, lecture directe de la fiche 3260020 sur
    // runner GitHub, réseau réel) — la fiche affiche désormais « En vigueur au 01/09/2026 » et
    // « Indexation de 0,1687% en 9/2026 » : texte primaire, pour la première fois depuis le
    // 05/08 où seule une plaquette Partena (secondaire) donnait un chiffre pour juillet. La
    // fiche a donc bien republié depuis — mais les ancres codées dans baremes-parsers.js (coefficient
    // ×1,3583, en vigueur 01/06/2026) restent celles d'AVANT cette indexation de septembre : elles
    // sont PÉRIMÉES. Cette CP a un coefficient qui bouge presque chaque mois (historique : ×1,3333
    // janvier → ×1,3583 juin 2026) : ne PAS déduire le nouveau coefficient par un calcul (×1,001687
    // sur l'ancien) — la CP 310 a démontré le 31/08/2026 qu'un recalcul uniforme sur une grille à
    // paliers/plafonds produit des montants faux ; les ancres se relèvent cellule par cellule sur
    // la fiche republiée, pas par arithmétique.
    // Ne pas re-dater sans avoir revu la fiche SPF republiée.
    indexation: { frequence: 'mensuelle', mois: [1,2,3,4,5,6,7,8,9,10,11,12], dernierTaux: 0.001687, derniereDate: '2026-09-01', source: 'SPF Emploi — salairesminimums.be, fiche 3260020, lue en direct le 24/09/2026 (audit 20.25)', note: '🔴 PÉRIMÉ CONFIRMÉ — fiche primaire lue le 24/09/2026 : « En vigueur au 01/09/2026 », « Indexation de 0,1687% en 9/2026 ». Les ancres numériques (baremes-parsers.js, coefficient ×1,3583 du 01/06/2026) n\'ont pas encore été remises à jour sur ce nouveau palier — relevé cellule par cellule à refaire avant d\'annoncer un montant à un client.' },
    // ── LE TAUX N'EST PAS PUBLIÉ, ET LES STATUTS DISENT POURQUOI ───────────
    // ✅ CCT du 14/04/2005 « institution d'un fonds de sécurité d'existence et
    // fixation de ses statuts », chapitre VI (Financement), art. 24 et 25 —
    // dépôt 74734/CO/326, banque CCT du SPF Emploi, texte primaire lu le
    // 26/08/2026. L'art. 25 : « Le montant de la cotisation est défini en
    // fonction du BUDGET ANNUEL établi par le conseil d'administration et fixé
    // par une convention collective de travail rendue obligatoire. »
    //
    // ⛔ CETTE CCT N'EXISTE PAS DANS LA BANQUE. Census complet : la banque du
    // SPF publie **13 conventions** pour la CP 326, toutes lues — régimes de
    // pension complémentaire, prime syndicale, groupes à risque, matériel TIC.
    // Aucune ne fixe la cotisation au fonds. Le montant change chaque année
    // avec le budget : il n'y a pas de taux stable à coder.
    //
    // 📄 Ce que la CCT du 12/10/2023 (dépôt 183474/CO/326) ajoute, et qui ne
    // donne pas non plus de taux : son art. 19 dit que « l'allocation à verser
    // par chaque entreprise au Fonds de formation syndicale est CALCULÉE CHAQUE
    // ANNÉE par le FAC sur base de la masse salariale des entreprises », répartie
    // au prorata de la masse salariale de chaque entreprise. Même mécanique :
    // un montant global voté, réparti — pas un pourcentage.
    //
    // ⚠️ LE 5 % RESTE FAUTE DE MIEUX, MAIS IL EST DÉSORMAIS NOMMÉ. On ne le
    // remplace pas par 0 : contrairement aux CP 316 et 328, ici le fonds EXISTE
    // et perçoit — c'est son taux qui n'est pas publiable, pas le fonds qui
    // manque. Mettre 0 dirait à un client qu'il ne doit rien, ce qui est faux.
    fondsSE: {
      nom: "Fonds de sécurité d'existence de l'industrie du gaz et de l'électricité",
      taux_patronal: 0.05,
      aVerifier: true,
      aVerifierRaison: "Le 5 % n'a AUCUNE source. Les statuts (CCT du 14/04/2005, art. 25, dépôt 74734/CO/326) fixent la cotisation « en fonction du budget annuel établi par le conseil d'administration » — un montant qui change chaque année, entériné par une CCT que la banque du SPF ne publie pas (census REFAIT le 26/08/2026 sur les 121 conventions publiées — et non 13 comme l'annonçait la note précédente, dénominateur issu d'une recherche filtrée par mots-clés. Aucune des 121 ne fixe le taux de cotisation générale, ce qui est cohérent avec le mécanisme de l'art. 25 : la cotisation est arrêtée chaque année avec le budget, elle n'a donc pas vocation à être déposée. SEUL CHIFFRE TROUVÉ, et il ne remplace pas le taux : un EFFORT « groupes d'insertion » de 0,10 % de la masse salariale, maintenu pour 2023 et 2024 par l'art. 13 de la CCT du 21/12/2023 « Programmation Sociale 2023-2024 » (dépôt 326-2023-017240, texte primaire lu le 26/08/2026), qui reconduit la CCT du 31/03/2014 (dépôt 122076/CO/326). ⛔ On ne l'écrit PAS dans le taux, pour deux raisons : (1) le texte parle d'un EFFORT, c'est-à-dire d'une dépense de l'employeur, et ne nomme aucun fonds percepteur ni aucun versement par défaut — la distinction est la même que pour les CP 104, 113.04, 210 et 325 ; (2) il est borné à 2023-2024 et rien de plus récent n'a été déposé. À vérifier sur la CCT du 31/03/2014 s'il existe un article de paiement par défaut : si oui, ces 0,10 % deviennent une vraie cotisation). Le fonds EXISTE et perçoit : on ne met donc pas 0. À obtenir auprès du fonds lui-même ou d'un secrétariat social du secteur.",
      statutsSource: "CCT du 14/04/2005 (CP 326, industrie du gaz et de l'électricité), chapitre VI « Financement », art. 24 et 25 — dépôt 74734/CO/326, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Recoupé sur la CCT du 12/10/2023 (dépôt 183474/CO/326), art. 19 : la dotation au Fonds de formation syndicale est « calculée chaque année par le FAC sur base de la masse salariale des entreprises », répartie au prorata — même mécanique, pas de pourcentage.",
      statutsDateVerif: '2026-08-26',
    },
    chequesRepas: { obligatoire: true, valeur: 10.0, part_employeur: 8.91, part_travailleur: 1.09, source: "CCT du 17/04/2026 (n° 199577/CO/326, industrie du gaz et de l'électricité), banque CCT du SPF, texte primaire lu le 24/08/2026 : au 01/01/2026, pour les travailleurs BARÉMISÉS, la valeur nominale du chèque-repas passe de 8 à 10 EUR ; l'intervention de l'employeur est fixée à 8,91 EUR et celle du travailleur à 1,09 EUR (texte néerlandais de la convention, traduit ici — le champ est affiché) ⚠️ Vise les travailleurs BARÉMISÉS ; le texte ne dit rien des non-barémisés.", dateVerif: '2026-08-24', enVigueurLe: '2026-01-01', lien: '326/326-2026-006843.pdf' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 326 + « eco cheque » :
    // 0 résultat (banque CCT du SPF Emploi).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 326 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    // 🔴 CORRIGÉ LE 25/09/2026 — « 5 jours, Fonds sectoriel CP 326 » était FAUX.
    // Recherche CCT réelle (cct-search.yml, motifs formation/heures sup/sursalaire) :
    // la CCT du 12/10/2023 (dépôt 183474/CO/326, art. 8) dit noir sur blanc que,
    // suite à la loi du 03/10/2022, les CCT sectorielles de formation de la CP 326
    // (01/07/2004, 31/08/2011, 01/03/2018, et l'art. 12 de la CCT de programmation
    // sociale du 13/01/2020) sont devenues SANS OBJET. Il ne reste que le droit
    // individuel fédéral (2 jours/an, entreprises <20 travailleurs, art. 50 de
    // cette loi) — aucun effort sectoriel propre, aucun fonds. Même motif que
    // CP 211/334 (renvoi, pas de duplication du chiffre fédéral — R1).
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — les CCT sectorielles de formation de la CP 326 sont devenues sans objet suite à cette loi (CCT du 12/10/2023, art. 8, dépôt 183474/CO/326)",
      source: "Banque CCT du SPF Emploi, CP 326 — CCT du 12/10/2023 (dépôt 183474/CO/326, 326/326-2023-017240.pdf), art. 8 : « Suite à la loi du 03.10.2022 portant des dispositions diverses relatives au travail, les CCT sectorielles suivantes sont devenues sans objet » (liste les 4 CCT formation 2004/2011/2018/2020). Recherche cct-search.yml du 25/09/2026, motifs formation/heures supplémentaires/sursalaire, 13 documents lus.",
      dateVerif: '2026-09-25',
    },
    cotisationSpeciale: null,
    // ✅ CONFIRMÉ LE 25/09/2026 — recherche CCT réelle sur « heures supplémentaires »
    // et « sursalaire » (13 documents lus) : aucune CCT CP 326 ne fixe de taux de
    // sursalaire différent des taux légaux (loi du 16/03/1971) — les mentions de
    // « sursalaire » trouvées ne concernent que des règlements de pension
    // sectorielle qui l'EXCLUENT de leur calcul, pas des CCT de sursalaire. Les
    // taux codés ci-dessous sont donc les taux légaux par défaut.
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
      source: "Aucune CCT sectorielle CP 326 ne fixe de taux de sursalaire propre (recherche cct-search.yml du 25/09/2026, motifs heures supplémentaires/sursalaire, 13 documents lus — seules des mentions incidentes dans des CCT de pension sectorielle, qui EXCLUENT le sursalaire de leur calcul). Taux légaux par défaut (loi du 16/03/1971) appliqués faute de déviation sectorielle sourcée.",
      dateVerif: '2026-09-25',
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.35 },  // vélo CP 326: 0,35 €/km — CCT 21/12/2022 (Prog. sociale 2023-2024, art.11), confirmé PRIMAIRE ACV-BIE overview 2026 (02/06/2026). Corrige l'ancien 0,37 (secondaire). 2026-07-10
    // ✅ FICHE ENTIÈREMENT RELUE LE 25/09/2026 — remplace le tampon de lot du
    // 29/04/2026 (« valeurs basées sur famille sectorielle »), devenu faux :
    // chaque dimension substantielle de cette fiche porte désormais sa propre
    // source datée — indexation (24/09), fondsSE (26/08, taux non publiable mais
    // honnêtement expliqué), chequesRepas (24/08), ecoCheques (21/09), formation
    // (25/09, corrigé), heuresSup (25/09, confirmé sans déviation), indemnites
    // (10/07). Seul fondsSE.taux_patronal reste un montant "faute de mieux"
    // explicitement signalé (`aVerifier: true`) — le fonds existe et perçoit,
    // mais son taux annuel n'est publié dans aucune des 121 CCT de la banque.
    _meta: {
      dateVerif: '2026-09-25',
      source: "Fiche relue en entier le 25/09/2026 : chaque dimension substantielle porte sa propre source datée (voir les champs source/dateVerif de indexation, fondsSE, chequesRepas, ecoCheques, formation, heuresSup). Seul le taux de fondsSE reste explicitement signalé comme non publié (aVerifier: true) — assumé, pas caché.",
    },
  },

  // CP 327.01 — ETA — secteur flamand (maatwerkbedrijven) (généré batch 29/04/2026)
  '327.01': {
    nom: 'CP 327.01 — ETA — secteur flamand',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // 🔁 CETTE FICHE CORRIGE UNE NOTE ÉCRITE LE MATIN MÊME. Le 23/08/2026 au
    // matin, la CP 327.01 a été classée « hors de portée de la banque CCT » :
    // sa classification vit dans une CCT du 21/11/1997 que la banque ne sert
    // pas (rien avant le 02/02/2005). C'était vrai, et ça ne suffisait pas à
    // conclure. La STRUCTURE des catégories est publiée par un AUTRE canal —
    // la fiche SPF « Salaires minimums » — qui n'avait pas été essayé.
    // C'est exactement R2 ter : un canal muet ne prouve rien sur les autres.
    classifications: {
      source:
        'fiche SPF « Salaires minimums », jcId 98a30748… (CP 3270100), en vigueur au 01/07/2026, dernière actualisation SPF du 04/08/2026 — lue le 23/08/2026',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: 38,
      systeme: 'cinq catégories, déclinées sur TROIS populations distinctes, avec progression par ancienneté en mois (0 à 20)',
      populations: [
        { cle: 'ateliers-sociaux-encadrement', libelle: 'Ateliers sociaux — personnel d\'encadrement', categories: [1, 2, 3, 4, 5] },
        { cle: 'eta-encadrement', libelle: 'Entreprises de travail adapté — personnel d\'encadrement', categories: [1, 2, 3, 4, 5] },
        { cle: 'eta-groupes-cibles', libelle: 'Entreprises de travail adapté — groupes cibles', categories: [1, 2, 3, 4, 5] },
      ],
      progression: 'par ancienneté exprimée en MOIS, de 0 à 20, chaque catégorie ayant sa propre courbe',
      // ⚠️ CE QUI RESTE HORS DE PORTÉE, ET SEULEMENT ÇA : quelle FONCTION tombe
      // dans quelle catégorie. La fiche SPF donne la grille, pas le dictionnaire
      // des fonctions — celui-ci est bien dans la CCT du 21/11/1997.
      definitionsHorsPortee:
        'la fiche SPF donne les catégories et leurs montants, pas le rattachement fonction → catégorie. Celui-ci vit au chapitre III de la CCT du 21/11/1997 (citée à l\'art. 1er de la CCT du 15/06/2021, n° 166089/CO/327.01), que la banque CCT ne sert pas — à demander au SPF ou à la sous-commission.',
      indexation:
        'formule à « montants de base » fixes multipliés par un coefficient d\'indexation. ⚠️ Le SPF avertit lui-même que, faute de montants de base adaptés dans les CCT, il a appliqué un ×1,02 : de légères différences d\'arrondi avec le barème sectoriel sont possibles sur la dernière décimale.',
      montantsNonCodes: 'les montants du 01/07/2026 ne sont pas repris ici — la dimension codée est la structure.',
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé de « annuelle janvier » 2026-07-16) : +2% au 2e mois après dépassement pivot → effet 01/02/2026. Source: Sociare / FILE asbl. Aligne 327.01 (ETA flamand) sur son mécanisme réel.' },
    // 🟠 TROIS COTISATIONS DISTINCTES, PAS UNE (CCT lues le 21/08/2026).
    // Le 4,5 % n'a aucune source. Ce que la banque CCT du SPF publie pour la
    // 327.01 (« ateliers sociaux » / maatwerkbedrijven) :
    //   · 0,60 % du salaire brut À 108 % — cotisation patronale au fonds
    //     (CCT du 18/11/2025, 32701-2025-014780) ;
    //   · 0,22 % par trimestre sur base annuelle, perçu 0,44 % aux 3e et 4e
    //     trimestres — fonds « 2e pilier de pension » (CCT du 24/03/2026) ;
    //   · 0,10 % à tous les trimestres 2025 — groupes à risque (CCT du 18/11/2025).
    // Un `taux_patronal` unique ne peut pas representer ca honnetement : les
    // trois n'ont ni la meme base, ni le meme calendrier, ni le meme objet.
    // AUCUNE valeur n'est reecrite ici — les additionner serait une invention.
    fondsSE: {
      nom: 'Fonds social CP 327.01',
      // ✅ DEUX SOURCES INDÉPENDANTES QUI TOMBENT SUR LE MÊME NOMBRE. La lecture
      // CCT du 21/08/2026 avait relevé « 0,60 % du brut à 108 % » pour le fonds
      // (CCT du 18/11/2025) ; le fichier des taux de l'ONSS perçoit exactement
      // 0,60 % pour la catégorie 173. La convention et le percepteur concordent.
      taux_patronal: 0.006,
      taux_patronal_employe: 0.006,
      base108Ouvrier: true,
      onssCategorie: '173',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "CCT du 18/11/2025 (banque CCT du SPF, lue le 21/08/2026) : 0,60 % du brut à 108 %. Recoupé le 25/08/2026 avec le fichier des taux DmfA 2026/3 de l'ONSS, catégorie d'employeur 173 : 0,60 %. Deux sources indépendantes, même valeur.",
      note: "Valeur précédente : 4,50 %, sans source. ⚠️ Deux autres cotisations existent et ne s'additionnent PAS à celle-ci — bases et calendriers différents : 0,22 %/trimestre pour le 2e pilier de pension (CCT du 24/03/2026, soit 0,44 % aux T3-T4) et 0,10 % pour les groupes à risque sur tous les trimestres 2025.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — les 2 CCT taguées thème chèques-repas (06/01/2025
    // et 24/03/2021, dépôts 191612 et 164564/CO/327.01) sont en réalité des CCT
    // ÉCO-CHÈQUES (175€/an obligatoire) dont l'art. 3 §4 permet de CONVERTIR au
    // niveau entreprise l'avantage éco-chèques en « avantage équivalent en
    // chèques-repas » (accord délégation syndicale, ou notification au président
    // de la sous-commission à défaut). Ce n'est pas une obligation sectorielle
    // de chèques-repas, mais une faculté de substitution à l'éco-chèque.
    chequesRepas: {
      obligatoire: false,
      source: "CCT du 06/01/2025 (dépôt 191612/CO/327.01) et CCT du 24/03/2021 (dépôt 164564/CO/327.01), banque CCT du SPF Emploi, textes primaires lus le 05/09/2026 — faculté de conversion de l'éco-chèque, pas une obligation de chèque-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 175,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 327.01 (ateliers protégés/sociaux flamands) — régime de BASE récurrent : CCT du 24/03/2021 (164564/CO/327.01), art. 3 : les travailleurs reçoivent CHAQUE ANNÉE des éco-chèques d'un montant de 175 EUR — progression depuis 50 EUR (CCT du 20/02/2019, 150928/CO/327.01). Texte primaire lu le 21/09/2026. ⚠️ EN PLUS de ce régime de base, une CCT du 06/01/2025 (191612/CO/327.01) octroie une prime SUPPLÉMENTAIRE, explicitement « non récurrente », de 150 EUR étalée sur 2025-2026 (75 EUR/an) — un bonus ponctuel, pas une hausse du régime de base.",
    ecoChequesDateVerif: '2026-09-21',
    ecoChequesPaiementDate: 'mai',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 327.01' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.31 },  // vélo CP 327.01: 0,31 €/km (max 30 km, indexé art.178 §3 WIB) — CCT 22/04/2024 woon-werk art.7, confirmé PRIMAIRE ACV-BIE overview 2026 (02/06/2026). Corrige l'ancien 0,27. 2026-07-10
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 327.02 — ETA — Commission communautaire française (généré batch 29/04/2026)
  '327.02': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 18/12/2025 (CP 327.02), banque CCT
    // du SPF Emploi, 32702/32702-2026-000108.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 18/12/2025 (CP 327.02) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 32702/32702-2026-000108.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 327.02 — ETA — Commission communautaire française',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé de « annuelle janvier » 2026-07-16) : +2% au 2e mois après dépassement pivot = effet 01/02/2026. Source: ACV-CSC 327.02 + CGSLB + easypay. Aligné sur 327.01.' },
    // 🔴 CE N'EST PAS UN TAUX SUR LE BRUT — piege de base, evite de justesse.
    // La CCT du 21/01/2020 (32702-2020-001646, art. 3) ecrit : « l'Office
    // national de securite sociale PRELEVE les pourcentages suivants DES
    // COTISATIONS versees trimestriellement […] pour l'alimentation du fonds :
    // jusqu'au 30 juin 2020 : 0,95 % ; a partir du 1er juillet 2020 pour une
    // duree indeterminee : 0,18 % ».
    // C'est un prelevement SUR LES COTISATIONS DEJA DUES, redistribue au fonds —
    // pas un cout patronal supplementaire sur la masse salariale. L'art. 4
    // confirme : le fonds vit de ce prelevement PLUS des dotations du SPFB.
    // Ecrire 0,0018 comme `taux_patronal` serait donc aussi faux que 0,045 : la
    // BASE n'est pas la meme. Aucune valeur reecrite tant qu'une CCT etablissant
    // une cotisation sur le brut n'a pas ete trouvee.
    // ── LA PRUDENCE DU 21/08 ÉTAIT JUSTE, ET ELLE SE RÉSOUT (26/08/2026) ──────
    // La session du 21/08 avait REFUSÉ d'écrire 0,18 %, et sa raison était
    // bonne : la CCT du 21/01/2020 (art. 3) parle de 0,18 % **DES COTISATIONS
    // VERSÉES**, pas du brut. Les cotisations valent un quart à un tiers de la
    // masse salariale — le même nombre sur deux bases n'est pas le même
    // montant. Elle a donc gardé le 4,5 %, en notant la coïncidence à trancher.
    //
    // ✅ CE QUI TRANCHE : un taux du fichier des taux DmfA s'applique, PAR
    // CONSTRUCTION, à la rémunération déclarée. Un « pourcentage des
    // cotisations » ne peut pas s'exprimer comme un taux de cotisation DmfA :
    // ce sont deux mécanismes distincts, dont l'un (le nôtre) est bien assis
    // sur le brut. La coïncidence de nombre reste curieuse et elle est dite —
    // mais elle ne rend pas la ligne ONSS ambiguë.
    //
    // 💰 L'écart est de vingt-cinq fois. Le 4,5 % n'avait AUCUNE source. Sur un
    // travailleur à 2 500 € bruts en base 108 % : 121,50 € par mois portés au
    // coût employeur là où l'ONSS en perçoit 4,86 €.
    //
    // La catégorie 173 nomme ce fonds mot pour mot : « redevables d'une
    // cotisation au Fonds de Sécurité d'existence pour les ETA agréées par la
    // Commission communautaire française ». Il n'y a qu'une catégorie pour
    // cette sous-commission : rien à trancher.
    fondsSE: {
      nom: "Fonds de sécurité d'existence pour les ETA agréées par la Commission communautaire française",
      taux_patronal: 0.0018,
      taux_patronal_employe: 0.0018,
      base108Ouvrier: true,
      onssCategorie: '173',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-26',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml) croisé avec l'annexe 27, lu le 26/08/2026. Catégorie d'employeur 173, seule catégorie rattachée à la SCP 327.02 : code 820 ouvriers 0,18 % · code 830 employés 0,18 %. Sa description nomme le fonds : « ETA bruxelloises francophones ; redevables d'une cotisation au Fonds de Sécurité d'existence pour les ETA agréées par la Commission communautaire française ».",
      mecanismeParallele: {
        quoi: "0,18 % DES COTISATIONS versées (0,95 % jusqu'au 30/06/2020), prélevés par l'ONSS et redistribués au fonds, plus des dotations du SPFB",
        source: "CCT du 21/01/2020 (SCP 327.02), art. 3, banque CCT du SPF Emploi, lue le 21/08/2026",
        pourquoiCeNestPasLeTauxCode: "une assiette « pourcentage des cotisations » ne s'exprime pas comme un taux DmfA, qui porte toujours sur la rémunération déclarée. Les deux 0,18 % coexistent donc sur deux bases différentes — la coïncidence de nombre est notée, elle n'est pas expliquée.",
      },
      note: "Valeur précédente : 4,50 %, sans aucune source — vingt-cinq fois le taux perçu. ⚠️ Ce taux est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      ancienneReserve:
        "Le 21/08/2026, ce taux avait été délibérément laissé à 4,5 % faute de pouvoir trancher l'assiette. La réserve est levée le 26/08 : le fichier DmfA porte sur la rémunération, pas sur les cotisations.",
      note: "🔴 Le 4,5 % n'est pas source, et la seule CCT trouvee ne fixe PAS un taux sur le brut : la CCT du 21/01/2020 (art. 3) fait prelever par l'ONSS 0,18 % DES COTISATIONS versees (0,95 % jusqu'au 30/06/2020), redistribues au fonds, plus des dotations du SPFB. Prelevement sur cotisations, pas cout patronal sur masse salariale — la base differe. Lue a la banque CCT du SPF le 21/08/2026. ⚠️ L'ONSS affiche 0,18 % pour la categorie 173 : meme nombre, base possiblement differente — coincidence a trancher avant toute ecriture.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — jcId 3270200 : 0 résultat en thème 246 et en
    // texte libre « maaltijdcheque »/« repas »/« titre-repas ». Recherche
    // « chèque » : 2 résultats sans rapport (chèques-consommation 2023, prime
    // corona 2021 — primes ponctuelles, pas des chèques-repas récurrents).
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 327.02 (jcId 3270200) — aucune CCT chèque-repas, seuls des primes ponctuelles sans rapport",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 327.02 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 327.02' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.37 },  // vélo CP 327.02: 0,37 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 327.03 — ETA — Région wallonne et Communauté germanophone (généré batch 29/04/2026)
  '327.03': {
    nom: 'CP 327.03 — ETA — Région wallonne et Communauté germanophone',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé de « annuelle janvier » 2026-07-16) : effet 01/02/2026. Source: ACV-CSC 327.03 + CGSLB. Aligné sur 327.01/327.02.' },
    // ✅ TEXTE PRIMAIRE LU LE 21/08/2026 — 5 %, et surtout PAS 7,50 %.
    // CCT du 24/05/2023 (32703-2023-009575), FSE ETAW (Region wallonne et
    // Communaute germanophone), art. 2 et 3, mot pour mot :
    //   « il est institue une cotisation patronale de 5 p.c. de la masse
    //     salariale brute trimestrielle a verser a l'Office national de securite
    //     sociale. L'Office national de securite sociale les ristournant audit
    //     fonds de securite d'existence. »
    //   « A partir du 2eme semestre 2023, la cotisation patronale est fixee a
    //     7,50 p.c. AFIN DE RECUPERER les cotisations non percues pour les 1er et
    //     2eme trimestres 2023. Toutefois, la cotisation patronale REPASSE A
    //     5 P.C. A DATER DU 1ER JANVIER 2024. »
    //   « conclue pour une duree indeterminee »
    // ⚠️ Le 7,50 % est le nombre le plus visible du document et il est PERIME
    // depuis le 01/01/2024 : un releve automatique l'aurait pris. C'est pour ca
    // qu'on lit la section, pas le montant.
    // ⚠️ Cette correction est INERTE tant que `payroll-engine` tronque la CP a
    // 3 chiffres : il applique le taux de la mere '327' a tous les ETA.
    // ── FSE ETAW — deux sources indépendantes disent le même 5 % ───────────
    // ✅ Le taux était lu à la CCT depuis le 21/08/2026, mais sans drapeau : il
    // restait donc INERTE — le moteur, faute de preuve, appliquait le 3 % de la
    // mère CP 327, qui n'a plus aucune CCT derrière lui depuis 2011. Sur un brut
    // de 2 500 €, 50 € par mois et par travailleur que la paie ne comptait pas.
    //
    // Le fichier des taux DmfA de l'ONSS ferme la question : catégorie 273,
    // trimestre 2026/3, code 820 = 5,00 % et code 830 = 5,00 %. Deux sources
    // indépendantes — la convention et le percepteur — au centième près.
    // La fiche porte donc `onssSourcee` et le moteur descend enfin à la sous-CP.
    fondsSE: {
      nom: 'FSE ETAW — entreprises de travail adapte (RW + Communaute germanophone)',
      taux_patronal: 0.05,
      onssSourcee: true,
      onssCategorie: '273',
      base: 'masse salariale brute trimestrielle',
      dateVerif: '2026-08-26',
      source: "ONSS, fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie d'employeur 273 : code 820 = 5,00 %, code 830 = 5,00 %. Lu le 26/08/2026. Concordant avec la CCT du 24/05/2023 (32703-2023-009575), art. 2 et 3, texte primaire lu le 21/08/2026 via la banque CCT du SPF Emploi, durée indéterminée.",
      note: "5 % de la masse salariale brute trimestrielle, verses a l'ONSS qui les ristourne au FSE ETAW. Le 7,50 % du 2e semestre 2023 etait un rattrapage des cotisations non percues aux T1-T2 2023 : la CCT dit elle-meme que le taux repasse a 5 % au 01/01/2024. Remplace un 4,5 % sans source.",
    },
    chequesRepas: { obligatoire: true, valeur: 3.09, part_employeur: 2.00, part_travailleur: 1.09, choixParEmployeur: true, source: "CCT du 20/05/2026 (n° 200394/CO/327.03), entreprises de travail adapté de la Région wallonne et de la Communauté germanophone, banque CCT du SPF Emploi, texte primaire lu le 23/08/2026 : chèque-repas d'une valeur nominale de 3,09 € (contribution patronale 2,00 €) à partir du 01/07/2026. ⚠️ CONVENTION SUPPLÉTIVE (art. 2) : elle ne s'applique QU'À DÉFAUT d'un accord d'entreprise conclu avant le 30/06/2026 prévoyant un avantage jugé équivalent, ou des modalités liées à des difficultés financières objectivables. ⚠️ Champ : ETA reconnues par l'AViQ, À L'EXCEPTION des ETA de la Communauté germanophone ; étudiants et stagiaires de la cellule d'accueil EXCLUS. La fiche portait 8,00 € / 6,91 €, défaut du lot du 29/04/2026.", dateVerif: '2026-08-23', enVigueurLe: '2026-07-01', lien: '32703/32703-2026-012277.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 327.03 (entreprises de travail adapté RW/Communauté germanophone) — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 327.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 327.03: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 328 — Transport urbain et régional (généré batch 29/04/2026)
  '328': {
    nom: 'CP 328 — Transport urbain et régional',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // Même cas que la CP 303 : faîtière, zéro CCT sous son propre numéro
    // (banque interrogée le 23/08/2026 : 0 résultat), fiche SPF vide. Les
    // trois sous-commissions régionales portent chacune la leur, et elles sont
    // toutes les trois codées.
    classifications: {
      source: 'banque CCT du SPF Emploi (0 convention sous le jcId 3280000) et fiche SPF « Salaires minimums » (cadre vide) — vérifiées le 23/08/2026',
      dateVerif: '2026-08-23',
      niveauFaitiere: true,
      grilleSectorielle: false,
      systeme: "aucune classification au niveau de la CP 328 : le transport urbain et régional est organisé par RÉGION, et chaque sous-commission a la sienne",
      sousCommissions: [
        { cp: '328.01', libelle: 'Région flamande (De Lijn)' },
        { cp: '328.02', libelle: 'Région wallonne (TEC / OTW)', resume: "classification en CLASSES calquée sur le modèle employés du Groupe TEC ; chefs d'équipe en classes 4 et 5" },
        { cp: '328.03', libelle: 'Région de Bruxelles-Capitale (STIB)' },
      ],
      pieges: "répondre au niveau 328 revient à ne pas répondre : la région de l'employeur décide.",
    },
    nomCourt: 'Transport urbain et régional',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'aucune', dernierTaux: null, derniereDate: null, note: 'Pas d index sectoriel auto — baremes propres operateurs (TEC/STIB/De Lijn). Source: AUDIT 2026-06 Easypay CP 328' },
    // ── FAÎTIÈRE SANS CCT, ET AUCUNE DES TROIS SOUS-CP N'A DE FONDS ────────
    // 📏 Mesure du 26/08/2026, avec ses dénominateurs (R2 ter-A). Le 5 % qui
    // figurait ici n'avait aucune source, et il n'y a rien derrière lui :
    //   · CP 328 (faîtière) — **ZÉRO** convention sous son propre numéro dans
    //     la banque CCT du SPF, quel que soit le critère de recherche. Le même
    //     constat était déjà écrit plus haut pour sa classification de fonctions,
    //     et il vaut ici pour la même raison : elle ne conventionne pas.
    //   · SCP 328.01 (Flandre, De Lijn) — **12 conventions sur 12** lues, aucune
    //     sur un fonds de sécurité d'existence.
    //   · SCP 328.02 (Wallonie) — fonds **DISSOUS le 03/05/2012** (CCT du même
    //     jour, dépôt 109687/CO/328.02, texte primaire lu).
    //   · SCP 328.03 (Bruxelles, STIB) — **30 conventions sur 30** lues, aucune
    //     sur un fonds de sécurité d'existence.
    // 🚫 Et l'ONSS ne rattache aucune catégorie d'employeur à la CP 328 dans le
    // fichier des taux DmfA 2026/3.
    //
    // Les trois moitiés du secteur disent la même chose : il n'y a pas de fonds
    // de sécurité d'existence dans le transport urbain et régional. Ce sont
    // trois opérateurs publics qui financent leurs avantages par des règlements
    // de pension d'entreprise, pas par un fonds sectoriel.
    fondsSE: {
      nom: "Transport urbain et régional — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      faitiereRenvoiSousCp: {
        '328.01': "Région flamande (De Lijn) — census de 12 conventions, aucun fonds relevé",
        '328.02': "Région wallonne — fonds dissous le 03/05/2012 (CCT du même jour)",
        '328.03': "Région de Bruxelles-Capitale (STIB) — census de 30 conventions, aucun fonds relevé",
      },
      source: "Banque CCT du SPF Emploi : aucune convention sous le numéro 328 lui-même ; census complet des sous-commissions (12 pour la 328.01, 30 pour la 328.03) sans aucun fonds de sécurité d'existence ; acte de dissolution du fonds de la 328.02 daté du 03/05/2012 (dépôt 109687/CO/328.02). Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à cette commission. Lu le 26/08/2026.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 des sous-CP 328.01 et 328.03 vient d'un CENSUS de titres, pas d'un acte de dissolution comme pour la 328.02. Un census prouve que le SPF ne publie rien sous ce thème, pas qu'aucune cotisation n'existe. À reconfirmer par une autre voie avant d'affirmer à un client qu'il ne doit rien.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus : 0
    // convention au total sous jc 3280000 (pas seulement 0 sur le thème repas —
    // la CP elle-même est une faîtière sans aucune CCT déposée). Aucune CCT ne
    // peut donc imposer de chèque-repas sous ce numéro ; voir les sous-CP
    // 328.01/328.02/328.03 pour un régime réel.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — 0 convention au total sous jc 3280000, structurellement aucune obligation possible au niveau faîtière",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 328 + « eco cheque » :
    // 0 résultat, cohérent avec chequesRepas ci-dessus (faîtière, 0
    // convention au total). Sous-CP 328.03 à vérifier séparément.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 328 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026) (faîtière, cohérent avec chequesRepas ci-dessus)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 328' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 328: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 328.01 — Transport urbain — Région flamande (De Lijn) (généré batch 29/04/2026)
  '328.01': {
    // ── Classification CP 328.01 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 27/02/2008, n° 87608/CO/328.01, banque CCT
    // du SPF Emploi (32801/32801-2008-001322.pdf). Transport urbain et
    // régional de la Région flamande — De Lijn / V.V.M.
    //
    // 🛑 CE DOCUMENT EMPLOIE TROIS NUMÉROTATIONS QU'IL NE RÉCONCILIE PAS, et
    // c'est le piège de cette fiche. Les mélanger classerait un travailleur
    // au mauvais niveau :
    //   · des CLASSES EN LETTRES A à G, qui sont les « nouvelles classes » ;
    //   · des BARÈMES V.V.M. K2 à K10, auxquels ces lettres sont accrochées ;
    //   · des CLASSES NUMÉROTÉES (2 à 5 pour l'embauche, « jusqu'à la classe
    //     de fonction 10 » pour le périmètre du système).
    // Seule la correspondance lettres ↔ barèmes est écrite noir sur blanc.
    // Le lien entre les lettres et les classes numérotées, lui, n'est PAS
    // dans ce texte : le déduire serait inventer.
    classifications: {
      source: 'CCT du 27/02/2008, n° 87608/CO/328.01, points 1 et 2 — banque CCT du SPF Emploi, 32801/32801-2008-001322.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      enVigueurDepuis: '2008-05-01',
      perimetre: "fonctions barémisées jusques et y compris la classe de fonction 10, plus les brigadiers rémunérés à l'heure",

      // ✅ La seule correspondance explicite du texte.
      nouvellesClasses: [
        { code: 'A', baremeVVM: 'K2' },
        { code: 'B', baremeVVM: 'K3' },
        { code: 'C', baremeVVM: 'K4' },
        { code: 'D', baremeVVM: 'K5' },
        { code: 'E', baremeVVM: 'K7' },
        { code: 'F', baremeVVM: 'K9' },
        { code: 'G', baremeVVM: 'K10' },
      ],
      // ⚠️ La suite des barèmes SAUTE : K2, K3, K4, K5, puis K7, K9, K10 —
      // pas de K6 ni de K8. Ce n'est pas une erreur de relevé, c'est ce que
      // la CCT écrit. « Corriger » la suite pour la rendre continue
      // décalerait trois classes.
      barremesNonContinus: true,

      // Embauche du personnel technique, par niveau de diplôme.
      embaucheTechnique: {
        A1: 'classe 5', A2: 'classe 4', A3: 'classe 3', sousA3: 'classe 2',
        reserve: "s'applique « pour autant que la fonction l'exige » ; les conditions d'embauche sont fixées à l'ouverture de la procédure de sélection",
        // ⚠️ Ces « classes » sont écrites en CHIFFRES. Le texte ne dit pas
        // si elles correspondent aux lettres ci-dessus. Ne pas les confondre.
        numerotationDistincte: true,
      },

      // Le déroulement de carrière, qui n'est pas la classification mais qui
      // en dépend — et qui décide d'un salaire.
      carriereTechnique: {
        departAccelere: "dans chaque échelle, le salaire horaire de 0 à 3 ans d'ancienneté barémique est aligné sur celui de 3 ans ; l'échelon de 4 ans s'acquiert à 4 ans d'ancienneté de SERVICE effective",
        echelleBis: "chaque échelle De Lijn des services techniques a une échelle bis, accordée après 8 ans dans l'échelle de départ (au lieu de 10) et sous réserve d'une évaluation favorable",
        biennaleFinDeCarriere: "la dernière biennale de chaque échelle est majorée d'une demi-biennale",
        echelle15_3: "nouvelle échelle 15.3, accessible après 8 ans d'ancienneté dans l'échelle 15.2 ou 9.1 avec évaluation favorable ; ceux qui passent de la 9.1 à la 15.3 au 01/03/2008 conservent les droits liés à leur ancien barème",
        depuis: '2008-03-01',
      },
    },
    nom: 'CP 328.01 — Transport urbain — Région flamande (De Lijn)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-03-01', note: 'INDEX-PIVOT transport urbain De Lijn (spilindex, corrigé de « annuelle janvier » 2026-07-16) : +2% au 2e mois après franchissement (ex. effet 01/03/2025). Source: CGSLB CP 328 + brupartners. Provenance « port » erronée corrigée.' },
    // ── AUCUN FONDS DE SÉCURITÉ D'EXISTENCE — census complet, pas un échec ─
    // 📏 MESURE AVEC SON DÉNOMINATEUR (26/08/2026, R2 ter-A). La banque CCT du
    // SPF Emploi publie **DOUZE** conventions pour la SCP 328.01 ; les douze
    // titres ont été lus un par un. Aucune ne concerne un fonds de sécurité
    // d'existence : trois modifications du règlement de pension De Lijn, deux
    // programmations sociales, un régime de garde, des efforts de formation,
    // deux harmonisations de primes, deux allocations complémentaires pour
    // travailleurs âgés. La recherche par thème « fonds de sécurité d'existence »
    // rend 0 — et ce 0 est fiable ici, parce que la MÊME requête sans filtre de
    // thème rend bien 12 : le silence ne vient pas d'un outil qui ne cherche pas.
    //
    // 🚫 L'ONSS ne comble pas : le fichier des taux DmfA 2026/3 ne rattache
    // aucune catégorie d'employeur à la CP 328 ni à ses sous-commissions.
    //
    // 🔁 ET LA SŒUR CONFIRME : la SCP 328.02 (Région wallonne) a DISSOUS son
    // fonds le 03/05/2012. Cette sous-commission-ci n'en a jamais eu trace.
    // C'est cohérent avec sa nature — une sous-commission d'un seul opérateur
    // public, qui finance ses avantages par un règlement de pension d'entreprise.
    //
    // ⚠️ CE QUI JUSTIFIE LE 0 ET SA RÉSERVE. Un census de titres est une preuve
    // plus faible qu'un acte de dissolution : il prouve que le SPF ne publie
    // rien sous ce thème, pas qu'aucune cotisation n'existe. Mais le 5 % qui
    // figurait ici ne reposait, lui, sur rien du tout — et il facturait 125 € par
    // mois et par travailleur sur un brut de 2 500 €.
    fondsSE: {
      nom: "Transport urbain et régional flamand — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des 12 conventions publiées pour la SCP 328.01, lu le 26/08/2026 : aucune ne porte sur un fonds de sécurité d'existence. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à la CP 328.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 est CONFIRMÉ par un census REFAIT le 26/08/2026 sur les 75 conventions publiées — et non 12 comme l'annonçait la première mesure, dénominateur issu d'une recherche filtrée. Sur ces 75, DEUX seulement touchent à un fonds, et aucune n'institue de cotisation de sécurité d'existence : la CCT du 06/01/2026 change l'organisme de PENSION, et celle du 29/09/1999 (indexation de la dotation au fonds syndical) n'est plus en vigueur. Le filet a été élargi aux « programmations sociales » et « accords sectoriels », la famille qui porte souvent ces cotisations ailleurs — sans résultat. Ancienne formulation : census (12 conventions sur 12 lues, aucune sur un fonds), pas d'un acte de dissolution comme pour la SCP 328.02. Un census prouve que le SPF ne publie rien sous ce thème, pas qu'aucune cotisation n'existe. À reconfirmer avant d'affirmer à un client qu'il ne doit rien. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation. Reste aussi le site de De Lijn.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — CCT du 18/02/2016 (dépôt 136311/CO/328.01,
    // programmation sociale 2015-2016 SCP 328.01, De Lijn), texte primaire lu :
    // « L'intervention de l'employeur dans le montant du chèque-repas est de
    // 6,91 euros. L'intervention du travailleur…est de 1,09 euro » → valeur
    // nominale 8,00 €, en vigueur depuis le 01/01/2016. Sur 75 CCT publiées pour
    // cette SCP, aucune plus récente ne retouche ce montant. Coïncidence
    // notable : c'est exactement la valeur déjà codée — mais elle a maintenant
    // une vraie source, pas la famille par défaut.
    chequesRepas: {
      obligatoire: true, valeur: 8.00, part_employeur: 6.91, part_travailleur: 1.09,
      source: "CCT du 18/02/2016 (dépôt 136311/CO/328.01, SCP 328.01 De Lijn), banque CCT du SPF Emploi, texte primaire lu le 05/09/2026, en vigueur depuis 01/01/2016",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null, ecoChequesSource: "CCT du 20/05/2026 (SCP 328.01, transport urbain et régional flamand), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « Octroi UNIQUE d'éco-chèques en 2026 — à chaque travailleur à temps plein avec une période de référence complète sont octroyés une seule fois des éco-chèques d'une valeur de 150 EUR. » ⚠️ « Eenmalige toekenning » : un versement unique en 2026, pas un régime annuel.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 328.01' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 328.01: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 328.02 — Transport urbain — Région wallonne (TEC/OTW) (généré batch 29/04/2026)
  '328.02': {
    nom: 'CP 328.02 — Transport urbain — Région wallonne (TEC/OTW)',
    type: 'O',
    regimeHebdo: 38,
    // ── CLASSIFICATION DE FONCTIONS (partielle) ───────────────────────────
    // ⚠️ CE QUI EST CODÉ ICI EST UN FRAGMENT, ET C'EST DIT : la sous-commission
    // ne dépose pas de grille sectorielle complète. Ce qu'elle dépose, c'est
    // l'intégration de fonctions précises dans la classification que le Groupe
    // TEC applique à ses employés. Le fragment est opposable — il est dans une
    // CCT enregistrée — mais il ne remplace pas une grille de secteur.
    classifications: {
      source:
        'CCT du 22/04/2016 relative à la mise en œuvre de la carrière des chefs d\'équipe technique (SCP 328.02), n° 134123/CO/328.02, préambule et art. 2 à 5 — banque CCT du SPF Emploi, 32802/32802-2016-009104.pdf. Texte primaire lu le 23/08/2026.',
      dateVerif: '2026-08-23',
      regimeHebdoCCT: null,
      partielle: true,
      systeme:
        'classification en CLASSES, calquée sur le modèle des employés du Groupe TEC ; les fonctions sont pondérées selon la même méthodologie que celle utilisée pour les employés, ce qui ouvre les mêmes possibilités d\'accès et de progression barémique que les autres fonctions de la même classe',
      fonctions: [
        { categorie: 4, fonction: 'Chef d\'équipe « surfaces »' },
        { categorie: 4, fonction: 'Chef d\'équipe « magasin »', bareme: 'B47 (initialement prévu pour les contrôleurs)' },
        { categorie: 5, fonction: 'Chef d\'équipe « technique » (métiers AT, CE, INFRA, EL HT)' },
      ],
      conditionsAcces:
        'avis favorable de la hiérarchie sur base du dossier (disciplinaire et absentéisme), réussite des tests de connaissance technique, appréciation favorable à l\'issue d\'un assessment sur la capacité à encadrer une équipe, et au minimum 5 ans d\'ancienneté dans les plans de carrière 4 ouvrier (art. 2).',
      champ: 'ouvriers, employés et personnel de direction des entreprises ressortissant à la SCP 328.02 (art. 1er).',
      resteAObtenir:
        'la classification générale des employés du Groupe TEC, à laquelle cette CCT rattache les chefs d\'équipe, n\'est pas déposée à la banque CCT — la CCT du 05/07/2010 (n° 32802/32802-2012-003395) n\'en parle que comme d\'un thème du dialogue de groupe. À demander à l\'employeur ou à la sous-commission.',
    },
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-03-01', note: 'INDEX-PIVOT transport urbain TEC (spilindex, corrigé 2026-07-16) : +2% au franchissement. Source: CGSLB CP 328 wallon + easypay 328.02. Provenance « port » erronée corrigée.' },
    // ── LE FONDS A ÉTÉ DISSOUS EN 2012 — troisième cas de la grille ────────
    // ✅ CCT du 03/05/2012 « relative à la dissolution du Fonds de Sécurité
    // d'Existence du Transport Urbain et Régional Wallon », art. 3 et 4 —
    // dépôt 109687/CO/328.02, banque CCT du SPF Emploi,
    // 32802/32802-2012-005451.pdf, texte primaire lu le 26/08/2026 :
    // « Le solde des avoirs du Fonds ayant reçu une affectation conforme à
    // l'objet social et en l'absence de passif, les membres de la
    // Sous-Commission Paritaire procèdent à la dissolution dudit Fonds institué
    // par la convention collective de travail du 20 avril 2001 » ; l'art. 4
    // abroge cette convention et est conclu pour une durée indéterminée.
    //
    // ⚠️ LE 5 % QUI FIGURAIT ICI FACTURAIT UN FONDS DISSOUS DEPUIS QUATORZE ANS.
    // Sur un brut de 2 500 €, 125 € par mois et par travailleur, pour rien.
    // Troisième occurrence du même défaut après la CP 102.01 (dissous fin 2010,
    // 6 % facturés) et la CP 316 (dissous en 2003, 5 % facturés) : un nombre
    // rond ne se contente pas d'être faux, il survit à son objet.
    fondsSE: {
      nom: "Transport urbain et régional wallon — fonds de sécurité d'existence dissous le 03/05/2012",
      taux_patronal: 0,
      fondsDissous: '2012-05-03',
      source: "CCT du 03/05/2012 (SCP 328.02), art. 3 et 4 — dépôt 109687/CO/328.02, banque CCT du SPF Emploi, texte primaire lu le 26/08/2026. Le solde du fonds avait été intégralement affecté au financement d'un chèque-cadeau versé en décembre 2011 (art. 2, en exécution de l'art. 7 de la CCT du 06/07/2011 sur la programmation sociale 2011-2012).",
      dateVerif: '2026-08-26',
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source et facturait un fonds dissous depuis 2012.",
    },
    // ✅ SOURCÉ LE 05/09/2026 — CCT n° 132347/CO/328.02 du 19/01/2016
    // (« Programmation sociale 2015-2016 »), art. 2, texte primaire lu :
    // « La valeur faciale du Ticket-Restaurant est portée à 8 € avec
    // augmentation de la quote-part patronale de 0,75 € passant ainsi de
    // 5,91 € à 6,66 €. La quote-part personnelle est diminuée de 0,25 €
    // passant de 1,59 € à 1,34 €. » En vigueur depuis janvier 2016. Stabilité
    // vérifiée : les 6 conventions « programmation sociale » postérieures
    // (2017-2018 n°143069, 2019 n°155219, 2019-2020 n°156133/157712, 2021-2022
    // n°168875, 2023 n°184910) lues intégralement — 0 sur 6 ne touche aux
    // titres-repas. Le texte de 2001 signalé illisible a aussi été retrouvé :
    // CCT du 20/11/2001 (dépôt 61416/CO/328.02), OCRisée, fixait 6 € (4,46 €
    // patronal / 1,54 € travailleur) à partir du 01/01/2002 — c'est la CCT
    // « fondatrice » que le « Régime du personnel du 21/01/1991 » invoque.
    chequesRepas: {
      obligatoire: true, valeur: 8.0, part_employeur: 6.66, part_travailleur: 1.34,
      source: "CCT n° 132347/CO/328.02 du 19/01/2016 (« Programmation sociale 2015-2016 »), art. 2, banque CCT du SPF Emploi — public-search.emploi.belgique.be/website-download-service/joint-work-convention/32802/32802-2016-001369.pdf, texte primaire lu le 05/09/2026, en vigueur depuis janvier 2016",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 100, ecoChequesSource: "CCT du 20/02/2025 (SCP 328.02, transport urbain et régional bruxellois), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : « Les travailleurs qui ont été occupés à temps plein durant la totalité de la période de référence reçoivent des éco-chèques à concurrence d'un montant total de 100 EUR par année concernée. » Régime ANNUEL et récurrent. La fiche portait 250 EUR.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 328.02' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 328.02: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 328.03 — Transport urbain — Bruxelles-Capitale (STIB/MIVB) (généré batch 29/04/2026)
  '328.03': {
    // ── Classification CP 328.03 — ajoutée le 23/08/2026 ────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 31/03/2006, banque CCT du SPF Emploi
    // (32803/32803-2006-001970.pdf), « Classification de fonctions et
    // carrières — mesures nouvelles et transitoires ».
    //
    // ⚠️ CE QUE CETTE CCT DONNE EST LE MÉCANISME, PAS LA LISTE. Elle définit
    // comment une fonction est classée (matrice de fonctions, pesée par le
    // poids relatif dans l'organisation et les responsabilités), et ce qui
    // arrive ensuite — repositionnement, élargissement de fonction, test
    // d'aptitude comportementale, passage d'ouvrier à employé. Mais elle ne
    // REPRODUIT PAS la matrice : les classes de fonctions elles-mêmes ne sont
    // pas dans ce texte. Écrire une liste ici serait la fabriquer.
    classifications: {
      source: 'CCT du 31/03/2006 — banque CCT du SPF Emploi, 32803/32803-2006-001970.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      mecanisme: {
        principe: "chaque fonction est classée dans une MATRICE DE FONCTIONS, en tenant compte de son poids relatif dans l'organisation de l'entreprise et des responsabilités qu'elle recouvre",
        positionnement: "classement d'une fonction dans la classe qui correspond à son barème de rémunération actuel",
        repositionnement: "classement dans une classe au barème SUPÉRIEUR alors que les responsabilités n'ont pas changé (ou ont changé sans justifier une classe supérieure) — ne s'opère QU'À L'OCCASION de la mise en place du système",
        autresLeviers: ["élargissement de fonction", "test d'aptitude comportementale", "passage d'ouvrier à employé", "passage dans les nouveaux barèmes"],
        carrierePlane: "les primes fixes et la prochaine étape d'évolution de la carrière plane (dans un délai de 4 ans) sont prises en compte lors du repositionnement",
      },
      baremesCites: [22, 23, 24, 26, 27, 28],
      anciennete: "plusieurs passages sont conditionnés à 15 ans d'exercice de la fonction (barèmes 23, 24, 27)",
      // ⚠️ L'AVEU CENTRAL : sans la matrice, on ne peut pas dire à un
      // travailleur dans quelle classe il tombe. Le mécanisme est connu, la
      // grille non.
      matriceNonReproduite: true,
      ouTrouverLaMatrice: "la CCT y renvoie sans la reproduire — à chercher en annexe de la CCT d'origine ou auprès de la sous-commission",
    },
    nom: 'CP 328.03 — Transport urbain — Bruxelles-Capitale (STIB/MIVB)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', dernierTaux: 0.02, derniereDate: '2026-03-01', note: 'INDEX-PIVOT transport urbain STIB/MIVB (spilindex, corrigé 2026-07-16, confiance moyenne — inférence famille CP 328). Source: CGSLB CP 328 bruxellois. Provenance « port » erronée corrigée.' },
    // ── AUCUN FONDS DE SÉCURITÉ D'EXISTENCE — census complet (26/08/2026) ──
    // 📏 Même mesure que pour la SCP 328.01, avec son dénominateur : la banque
    // CCT du SPF Emploi publie **TRENTE** conventions pour la SCP 328.03, et les
    // trente titres ont été lus. Aucune ne concerne un fonds de sécurité
    // d'existence — ce sont des règlements de pension extralégale de la STIB,
    // des assurances groupe, des classifications de fonctions, des régimes de
    // fin de carrière, une couverture incapacité de travail. La recherche par
    // thème rend 0 alors que la même requête sans filtre rend 30 : l'outil
    // cherche bien, il ne trouve rien parce qu'il n'y a rien sous ce thème.
    //
    // 🚫 L'ONSS ne rattache aucune catégorie d'employeur à la CP 328.
    // 🔁 La SCP 328.02 (Wallonie) a dissous le sien en 2012 ; la 328.01
    // (Flandre) n'en a aucune trace. Les trois sous-commissions se rejoignent.
    fondsSE: {
      nom: "Transport urbain et régional bruxellois — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des 30 conventions publiées pour la SCP 328.03, lu le 26/08/2026 : aucune ne porte sur un fonds de sécurité d'existence. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à la CP 328.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 est CONFIRMÉ par un census REFAIT le 26/08/2026 sur les 163 conventions publiées — et non 30 comme l'annonçait la première mesure, dénominateur issu d'une recherche filtrée. Sur ces 163, UNE seule touche à un fonds — le financement d'une assurance de groupe « prestations définies » (CCT du 17/12/2018), qui est un régime de pension et n'est plus en vigueur. Filet élargi aux « programmations sociales » et « accords sectoriels » : sans résultat. Ancienne formulation : census (30 conventions sur 30 lues, aucune sur un fonds), pas d'un acte de dissolution comme pour la SCP 328.02. À reconfirmer par une autre voie avant d'affirmer à un client qu'il ne doit rien.",
      note: "Le 5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    chequesRepas: { obligatoire: true, valeur: 9.0, part_employeur: 7.91, part_travailleur: 1.09, source: "CCT du 24/02/2026 (SCP 328.03, transport urbain et régional wallon), banque CCT du SPF, texte primaire lu le 24/08/2026 : « Pour les titres-repas relatifs aux prestations effectives à partir du 01/02/2026, l'intervention de l'employeur dans le montant du titre-repas s'élève à 7,91 EUR. En conséquence, la valeur nominale du titre-repas est de 9,00 EUR. » ⚠️ ÉCHÉANCE À VENIR : au 01/12/2026, l'intervention passe à 8,91 EUR, soit une valeur nominale de 10,00 EUR. Valeur codée = celle EN VIGUEUR aujourd'hui.", regimeSecondaire: "au 01/12/2026 : intervention employeur 8,91 EUR, valeur nominale 10,00 EUR", dateVerif: '2026-08-24', enVigueurLe: '2026-02-01', lien: '32803/32803-2026-002378.pdf' },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 328.03 (transport urbain/régional Région Bruxelles-Capitale) — CCT du 24/02/2026 (198508/CO/328.03), texte primaire lu le 21/09/2026 : mentionne les « chèques-cadeau et écochèques » uniquement dans une clause de RENÉGOCIATION future (« si les conditions d'octroi ... viennent à être modifiées par le gouvernement, les partenaires sociaux se verront... ») — aucun montant sectoriel ÉTABLI par cette CCT. Aucune autre CCT ne chiffre un régime.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 328.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 328.03: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB port + Cepa',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 329.01 — Socio-culturel — Communauté flamande (généré batch 29/04/2026)
  '329.01': {
    nom: 'CP 329.01 — Socio-culturel — Communauté flamande',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé 2026-07-16) : effet 01/02/2026. Source: Sociare / hetACV. Aligne le sous-CP sur son parent 329 (déjà pivot_public) et 329.02/.03.' },
    fondsSE: {
      nom: 'Fonds social CP 329.01',
      // ⚠️ L'AMBIGUÏTÉ ÉTAIT UN ARTEFACT, PAS UNE VRAIE INDÉCISION. Trois
      // catégories d'employeur pointent vers la CP 329.01, mais deux d'entre
      // elles — 076 et 176 — sont réservées « uniquement à la déclaration des
      // SPORTIFS RÉMUNÉRÉS », une population particulière, pas le secteur.
      // La catégorie du secteur socio-culturel flamand est la 262, et elle
      // perçoit 0,20 % des deux côtés. Comparer les trois revenait à mettre
      // dans le même sac un régime sectoriel et un régime de sportifs.
      taux_patronal: 0.002,
      taux_patronal_employe: 0.002,
      base108Ouvrier: true,
      onssCategorie: '262',
      onssSourcee: true,
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3, catégorie d'employeur 262 (« Sociaal Fonds voor het Sociaal-Cultureel Werk van de Vlaamse Gemeenschap ») : 0,20 % pour les deux publics. Lu le 25/08/2026. Les catégories 076 et 176, également rattachées à la CP 329.01, sont réservées à la déclaration des sportifs rémunérés et ne décrivent pas le régime du secteur.",
      note: "Valeur précédente : 4,50 %, sans source.",
      sportifsRemuneres: {
        // Conservé parce que c'est une information, pas du bruit : un employeur
        // qui déclare des sportifs rémunérés en CP 329.01 relève d'une autre
        // catégorie, avec un autre taux.
        categorie076: 0.0065,
        categorie176: 0.002,
        source: "Descriptions des catégories d'employeur 076 et 176, annexe structurée ONSS n° 27, lues le 25/08/2026.",
      },
    },
    // ✅ CORRIGÉ LE 05/09/2026 — 137 CCT au total sous jc 3290100, 0 sur le
    // thème chèques-repas. Aucune CCT sectorielle socio-culturel flamand
    // n'institue de chèque-repas.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 329.01 (jc 3290100) — 137 conventions au total, 0 sur le thème chèques-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 329.01 (secteur socioculturel flamand) — CCT du 16/12/2019 (157215/CO/329.01), texte primaire lu le 21/09/2026, art. 3 : « een EENMALIGE toekenning van ecocheques » (octroi UNIQUE), en application du VIA 5, 121 EUR pour 2019, référence 01/01-30/09/2019. Aucune CCT plus récente n'en fait mention.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 329.01' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo 329.01 : CCT sectorielle propre = max exonéré, octroi OBLIGATOIRE → 0,37 dès 01/01/2026 — Sociare, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 329.02 — Socio-culturel — Communauté française, germ. et RW (généré batch 29/04/2026)
  '329.02': {
    nom: 'CP 329.02 — Socio-culturel — Communauté française, germ. et RW',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé de « annuelle janvier » 2026-07-16) : indexé 02/2026 ET 08/2026 (franchissements 12/2025 + 06/2026). Source: CGSLB 329.02 + CESSOC + incidence-asbl. Aligné sur 329.01 et parent 329.' },
    fondsSE: {
      nom: 'Fonds social CP 329.02',
      taux_patronal: 0.0022,
      taux_patronal_employe: 0.0022,
      base108Ouvrier: true,
      onssCategorie: '362',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 362 : 820 ouvriers 0,22 % · employés 0,22 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — 106 CCT au total sous jc 3290200, 0 sur le
    // thème chèques-repas. Même constat pour le socio-culturel francophone/
    // germanophone/RW.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 329.02 (jc 3290200) — 106 conventions au total, 0 sur le thème chèques-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: 200, ecoChequesSource: "CCT du 19/12/2022 (SCP 329.02, socio-culturel francophone et germanophone), banque CCT du SPF, thème 247, texte primaire lu le 24/08/2026 : montant MAXIMUM de 200 EUR, formule « 200 EUR × A × B » (A = régime de travail, B = fraction de la période de référence). ⚠️ PLAFOND proratisé sur DEUX facteurs, pas un dû fixe. La fiche portait 250 EUR.", ecoChequesDateVerif: '2026-08-24',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 329.02' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo 329.02 : CCT 21/10/2019 mod. → 0,35 €/km (trajet simple domicile-travail) depuis 01/01/2024, exonération CCT plafonnée 2 500 €/an ; aucune indexation 2026 trouvée — CESSOC, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.35, veloPlafondCCT: 2500, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 329.03 — Organisations socio-culturelles fédérales et bicommunautaires (généré batch 29/04/2026)
  '329.03': {
    nom: 'CP 329.03 — Organisations socio-culturelles fédérales et bicommunautaires',
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION » : la fiche a été
    // ouverte et lue, ce secteur ne classe pas ses travailleurs par fonction.
    classifications: {
      source: 'fiche SPF « Salaires minimums » — HISTORIQUE au 31/08/2022 (document.html?jcId=…&date=31/08/2022, en JJ/MM/AAAA avec SLASHES : tout autre format rend 500), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      reserve: "⚠️ BORNE BASSE, PAS LE MINIMUM DU JOUR — le SPF a cessé de suivre cette (sous-)commission au 01/09/2022. La STRUCTURE est ce qui est codé ; les montants ne le sont pas.",
      grilleSectorielle: false,
      systeme: "un revenu minimum mensuel moyen garanti unique (CCT du 30/06/2009, n° 95183), en mensuel et en horaire. Aucune catégorie de fonction.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-02-01', note: 'INDEX-PIVOT non-marchand (spilindex, corrigé de « annuelle janvier » 2026-07-16) : indexé 02/2026 ET 08/2026. Source: CGSLB socioculturel fédéral + SETCa + easypay 329.03. Aligné sur 329.01/329.02.' },
    fondsSE: {
      nom: 'Fonds social CP 329.03',
      taux_patronal: 0.002,
      taux_patronal_employe: 0.002,
      base108Ouvrier: true,
      onssCategorie: '762, 862',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 762, 862 : 820 ouvriers 0,20 % · employés 0,20 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — 19 CCT au total sous jc 3290300, 0 sur le
    // thème chèques-repas. Socio-culturel fédéral/bicommunautaire : idem.
    chequesRepas: {
      obligatoire: false,
      source: "Banque CCT du SPF Emploi, CP 329.03 (jc 3290300) — 19 conventions au total, 0 sur le thème chèques-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 329.03 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat sur les 4 orthographies (relevé le 21/09/2026, mode batch cct-search.mjs)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 329.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo 329.03 : pas de CCT vélo propre → CCT n°164 supplétive : 0,30 €/km dès 01/01/2026 (max 40 km/j) — Sociare + CESSOC, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.30, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Sources sectorielles standard',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 332.01 — Milieux d\ (généré batch 29/04/2026)
  '332.01': {
    nom: "CP 332.01 — Milieux d'accueil d'enfants (MAE) — Communauté fr.",
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT non-marchand accueil enfance/aide jeunesse (spilindex CNE, +2% au 1er/2e mois après dépassement pivot), CONFIRMÉ 2026-07-16. Source: CGSLB CP 332 + CNE + FILE.' },
    // ⚠️ AUCUN `fondsSE` PROPRE ICI, ET C'EST VOULU (25/08/2026).
    // Ces quatre clés portent un numéro de commission paritaire sans en être
    // une : la liste officielle du SPF ne connaît que la CP 332 (voir
    // `app/lib/cp-sous-division.js`). Aucune CCT ne peut donc être déposée
    // sous « 332.01 », et la recherche le confirme — la banque du SPF rend
    // ZÉRO convention pour ces quatre numéros.
    // Elles portaient pourtant un `fondsSE` à 6 %, sans source d'aucune sorte.
    // Or l'héritage de `ficheEffective` ne comble que ce qui est ABSENT : la
    // valeur propre l'emporte toujours. Ce 6 % écrasait donc le 0,15 % de la
    // CP 332, lui SOURCÉ sur le fichier des taux de l'ONSS (catégories 022 et
    // 222) — un facteur 40, en défaveur du client.
    // ⚠️ Et les deux surfaces ne disaient déjà PAS la même chose : le moteur de
    // paie, lui, retombait sur la mère (il n'accepte une sous-CP que si elle
    // est `cctSourcee` ou `onssSourcee`), tandis que la fiche à l'écran servait
    // les 6 %. Supprimer la clé fait converger les deux sur la seule valeur
    // sourcée. Verrou : __tests__/sous-division-nonsourcee-nefaitpas-loi.test.js
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 332.01 (Milieux d'accueil d'enfants) — l'autocomplete du site refuse de sélectionner cette sous-CP (bug d'interaction non résolu, cf. commentaires scripts/cct-search.mjs), contournée en cherchant sur la CP PARENTE 332 puis en filtrant les documents dont la population correspond aux MAE. 3 CCT identifiées (168091/CO/332 du 22/10/2021 « octroyant des éco-chèques pour l'année 2021 au personnel de l'accueil de la petite enfance et des équipes SOS-Enfants », remplace 167718/CO/332 ; 177847/CO/332 du 20/12/2022 « octroi exceptionnel d'éco-chèques en 2022 à certains travailleurs » incluant crèches/MCAE/prégardiennats/SASPE). Texte primaire lu le 22/09/2026 : art. 2 « l'octroi EXCEPTIONNEL pour l'année 2021 » et art. 7 « durée DÉTERMINÉE » (2021) — financé par une subvention exceptionnelle de l'ONE, jamais un régime récurrent.",
    ecoChequesDateVerif: '2026-09-22',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 332.01' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo : CCT CP 332 lie l'indemnité au max exonéré art. 38 §1er 14° CIR → 0,37 dès 01/01/2026 — Synova CP 332, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + IFIC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 332.02 — Services d\ (généré batch 29/04/2026)
  '332.02': {
    nom: "CP 332.02 — Services d'aide aux justiciables, CASG, CLP santé...",
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT non-marchand accueil enfance/aide jeunesse (spilindex CNE, +2% au 1er/2e mois après dépassement pivot), CONFIRMÉ 2026-07-16. Source: CGSLB CP 332 + CNE + FILE.' },
    // ⚠️ AUCUN `fondsSE` PROPRE ICI, ET C'EST VOULU (25/08/2026).
    // Ces quatre clés portent un numéro de commission paritaire sans en être
    // une : la liste officielle du SPF ne connaît que la CP 332 (voir
    // `app/lib/cp-sous-division.js`). Aucune CCT ne peut donc être déposée
    // sous « 332.02 », et la recherche le confirme — la banque du SPF rend
    // ZÉRO convention pour ces quatre numéros.
    // Elles portaient pourtant un `fondsSE` à 6 %, sans source d'aucune sorte.
    // Or l'héritage de `ficheEffective` ne comble que ce qui est ABSENT : la
    // valeur propre l'emporte toujours. Ce 6 % écrasait donc le 0,15 % de la
    // CP 332, lui SOURCÉ sur le fichier des taux de l'ONSS (catégories 022 et
    // 222) — un facteur 40, en défaveur du client.
    // ⚠️ Et les deux surfaces ne disaient déjà PAS la même chose : le moteur de
    // paie, lui, retombait sur la mère (il n'accepte une sous-CP que si elle
    // est `cctSourcee` ou `onssSourcee`), tandis que la fiche à l'écran servait
    // les 6 %. Supprimer la clé fait converger les deux sur la seule valeur
    // sourcée. Verrou : __tests__/sous-division-nonsourcee-nefaitpas-loi.test.js
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 332.02 (services d'aide aux justiciables, CASG, CLP santé...) — même contournement que CP 332.01 (recherche sur la CP PARENTE 332, filtrage par population). Deux populations distinctes, aucune n'a de régime récurrent : (1) services partenaires des maisons de justice — CCT 168092/CO/332 du 22/10/2021 « octroyant des éco-chèques pour l'année 2021 », remplace 167717/CO/332, et la CCT 177847/CO/332 du 20/12/2022 qui inclut aussi ces services dans son octroi EXCEPTIONNEL 2022 ; (2) secteur ambulatoire (CASG, planning familial, médiation de dettes...) — CCT 113227/CO/332 du 17/12/2012, DURÉE INDÉTERMINÉE, art. 3 : « La mesure Eco-chèques de l'Accord non-marchand 2010 est dès à présent SUPPRIMÉE ET REMPLACÉE » par une prime annuelle — donc plus d'éco-chèques du tout pour cette population, convertis en cash. Texte primaire lu le 22/09/2026.",
    ecoChequesDateVerif: '2026-09-22',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 332.02' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo : CCT CP 332 lie l'indemnité au max exonéré art. 38 §1er 14° CIR → 0,37 dès 01/01/2026 — Synova CP 332, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + IFIC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 332.03 — Aide à la jeunesse (excl. Communauté germanophone) (généré batch 29/04/2026)
  '332.03': {
    nom: 'CP 332.03 — Aide à la jeunesse (excl. Communauté germanophone)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT non-marchand aide à la jeunesse (spilindex CNE, +2% au 1er/2e mois après dépassement pivot), CONFIRMÉ 2026-07-16. Source: SETCa + CGSLB CP 332.' },
    // ⚠️ AUCUN `fondsSE` PROPRE ICI, ET C'EST VOULU (25/08/2026).
    // Ces quatre clés portent un numéro de commission paritaire sans en être
    // une : la liste officielle du SPF ne connaît que la CP 332 (voir
    // `app/lib/cp-sous-division.js`). Aucune CCT ne peut donc être déposée
    // sous « 332.03 », et la recherche le confirme — la banque du SPF rend
    // ZÉRO convention pour ces quatre numéros.
    // Elles portaient pourtant un `fondsSE` à 6 %, sans source d'aucune sorte.
    // Or l'héritage de `ficheEffective` ne comble que ce qui est ABSENT : la
    // valeur propre l'emporte toujours. Ce 6 % écrasait donc le 0,15 % de la
    // CP 332, lui SOURCÉ sur le fichier des taux de l'ONSS (catégories 022 et
    // 222) — un facteur 40, en défaveur du client.
    // ⚠️ Et les deux surfaces ne disaient déjà PAS la même chose : le moteur de
    // paie, lui, retombait sur la mère (il n'accepte une sous-CP que si elle
    // est `cctSourcee` ou `onssSourcee`), tandis que la fiche à l'écran servait
    // les 6 %. Supprimer la clé fait converger les deux sur la seule valeur
    // sourcée. Verrou : __tests__/sous-division-nonsourcee-nefaitpas-loi.test.js
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 332.03 (Aide à la jeunesse, hors Communauté germanophone) — l'autocomplete du site ne sélectionne pas cette sous-CP (« No results found », capture d'écran rapport/autocomplete-cp.png du 22/09/2026, même défaut que 332.01/332.02). Contournement : recherche sur la CP PARENTE 332 combinée au terme « jeunesse » — AUCUNE convention trouvée (relevé le 22/09/2026), alors que la même recherche sur CP 332 trouve bien des CCT éco-chèques pour d'autres populations (MAE, maisons de justice) — le moteur fonctionne, aucune CCT éco-chèques n'existe pour cette population sous CP 332.",
    ecoChequesDateVerif: '2026-09-22',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 332.03' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo : CCT CP 332 lie l'indemnité au max exonéré art. 38 §1er 14° CIR → 0,37 dès 01/01/2026 — Synova CP 332, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + IFIC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 332.04 — IDESS — Services de proximité à finalité sociale (RW) (généré batch 29/04/2026)
  '332.04': {
    nom: 'CP 332.04 — IDESS — Services de proximité à finalité sociale (RW)',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot_public', mois: [1, 2], dernierTaux: 0.02, derniereDate: '2026-01-01', note: 'INDEX-PIVOT non-marchand IDESS/proximité RW (spilindex CNE famille 332, +2% après dépassement pivot), CONFIRMÉ 2026-07-16 (confiance moyenne — libellé sous-CP sans fiche dédiée). Source: CGSLB CP 332.' },
    // ⚠️ AUCUN `fondsSE` PROPRE ICI, ET C'EST VOULU (25/08/2026).
    // Ces quatre clés portent un numéro de commission paritaire sans en être
    // une : la liste officielle du SPF ne connaît que la CP 332 (voir
    // `app/lib/cp-sous-division.js`). Aucune CCT ne peut donc être déposée
    // sous « 332.04 », et la recherche le confirme — la banque du SPF rend
    // ZÉRO convention pour ces quatre numéros.
    // Elles portaient pourtant un `fondsSE` à 6 %, sans source d'aucune sorte.
    // Or l'héritage de `ficheEffective` ne comble que ce qui est ABSENT : la
    // valeur propre l'emporte toujours. Ce 6 % écrasait donc le 0,15 % de la
    // CP 332, lui SOURCÉ sur le fichier des taux de l'ONSS (catégories 022 et
    // 222) — un facteur 40, en défaveur du client.
    // ⚠️ Et les deux surfaces ne disaient déjà PAS la même chose : le moteur de
    // paie, lui, retombait sur la mère (il n'accepte une sous-CP que si elle
    // est `cctSourcee` ou `onssSourcee`), tandis que la fiche à l'écran servait
    // les 6 %. Supprimer la clé fait converger les deux sur la seule valeur
    // sourcée. Verrou : __tests__/sous-division-nonsourcee-nefaitpas-loi.test.js
    chequesRepas: { obligatoire: false, valeur: 0, part_employeur: 0, part_travailleur: 1.09 },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 332.04 (IDESS — services de proximité à finalité sociale, RW) — l'autocomplete du site ne sélectionne pas cette sous-CP (« No results found », capture d'écran rapport/autocomplete-cp.png du 22/09/2026, même défaut que 332.01-332.03). Contournement : recherche sur la CP PARENTE 332 combinée au terme « IDESS » — AUCUNE convention trouvée (relevé le 22/09/2026), alors que la même recherche sur CP 332 trouve bien des CCT éco-chèques pour d'autres populations (MAE, maisons de justice) — le moteur fonctionne, aucune CCT éco-chèques n'existe pour cette population sous CP 332.",
    ecoChequesDateVerif: '2026-09-22',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 332.04' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    // Vélo : CCT CP 332 lie l'indemnité au max exonéré art. 38 §1er 14° CIR → 0,37 dès 01/01/2026 — Synova CP 332, consulté 2026-07-07
    indemnites: { transportPublic_pct: 1.00, velo: 0.37, veloMaxExonere: 0.37 },
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + IFIC',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 333 — Attractions touristiques et parcs de loisirs (généré batch 29/04/2026)
  '333': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 17/02/2026 (CP 333), banque CCT
    // du SPF Emploi, 333/333-2026-002192.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 17/02/2026 (CP 333) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 333/333-2026-002192.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 333 — Attractions touristiques et parcs de loisirs',
    classifications: {
      source: "CCT du 30/06/2011 (CP 333, attractions touristiques), chapitre II « Classification sectorielle des fonctions », banque CCT du SPF, texte primaire lu le 25/08/2026.",
      dateVerif: '2026-08-25',
      grilleSectorielle: true,
      systeme: "CINQ classes de fonctions, chacune definie par des FONCTIONS DE REFERENCE nommees (art. 2 §1). Classe 1 : collaborateur de base — controle d'acces, entretien general, operateur d'attractions, service touristique. Classe 2 : accueil point de vente, back-office, accompagnateur touristique, chauffeur C-D-E, ouvrier qualifie, maitre nageur. Classes 3 a 5 au-dessus. Le classement se fait par COMPARAISON a une fonction de reference, pas par intitule de poste.",
    },
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', source: 'ACV-CSC + CGSLB CP 333 — indexation annuelle au 1er janvier (+2,21% au 01/01/2026), vérifié 2026-07-16', note: '(le _meta.indexationFrequence « pivot_public » est une coquille batch — le mécanisme réel est annuel janvier)', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },
    fondsSE: {
      nom: 'Fonds social CP 333',
      taux_patronal: 0.0025,
      taux_patronal_employe: 0.0025,
      // Régimes CONDITIONNELS publiés par l'ONSS (seuil d'effectif, plan sectoriel).
      // Non branchés : le moteur ne connaît pas l'effectif de l'employeur.
      variantesOnss: [{"code": "820", "type": "5", "taux": 0.35, "forfait": false}],
      base108Ouvrier: true,
      onssCategorie: '095',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 095 : 820 ouvriers 0,25 % · employés 0,25 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,00 %, sans source ni date.",
    },
    chequesRepas: { obligatoire: true, valeur: 2.18, part_employeur: 1.09, part_travailleur: 1.09, source: "CCT du 17/02/2026 (CP 333, attractions touristiques), « Introduction des chèques repas », dépôt 198206, banque CCT du SPF, texte primaire lu le 24/08/2026, art. 4 : « §1. La valeur nominale du chèque repas est fixée à 2,18 €. §2. La participation aux coûts des chèques repas de l'employeur s'élèvera à 1,09 € par chèque et la participation du travailleur s'élèvera à 1,09 € par chèque. » En vigueur au 01/02/2026. Portée : toute la CP (aucune restriction déclarée au SPF).", conditionOuverture: "ANCIENNETE DE 12 MOIS (art. 3 §1) : le droit au cheque-repas n'est ouvert qu'aux travailleurs justifiant d'une anciennete ininterrompue de douze mois aupres du meme employeur a la date d'octroi. Les suspensions couvertes par la legislation sociale et les conges legaux n'interrompent pas l'anciennete ; une rupture effective suivie de plus de sept jours calendaires sans lien contractuel l'interrompt.", dateVerif: '2026-08-24', enVigueurLe: '2026-02-01', lien: '333/333-2026-002188.pdf' },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 333 + « eco cheque » :
    // 1 hit, faux positif. La CCT du 15/06/2009 (accord sectoriel 2009-2010,
    // expirée le 31/12/2012) prévoyait une augmentation ponctuelle du
    // pouvoir d'achat (100 € en 2009, 200 € en 2010) déclinable en 5
    // formes au choix — dont l'éco-chèque — jamais une obligation d'éco-
    // chèques en tant que telle, et cette mesure n'est plus en vigueur
    // depuis 2012. Cohérent avec le passage au chèque-repas comme vecteur
    // actuel du secteur (CCT du 17/02/2026 ci-dessus).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 333 — 1 hit plein texte, faux positif (accord sectoriel 2009-2010 expiré, éco-chèque un choix parmi 5 pour une augmentation ponctuelle du pouvoir d'achat, jamais une obligation propre)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 333' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 333: 0,30 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'Mediarte + CSC spectacle',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 338 — Activités maritimes et liées aux voies d'eau (AR 6/10/2008, MB 17/10/2008).
  // Libellé corrigé 2026-07-09 — source PRIMAIRE Moniteur belge / ejustice (numac 2013205928).
  // ⚠️ « Journalistes » était FAUX (les journalistes salariés relèvent de la CP 130, presse).
  //    Les taux ci-dessous avaient été générés en batch pour le mauvais secteur → NON SOURCÉS
  //    pour le maritime : à vérifier sur source officielle avant tout usage réel.
  '338': {
    nom: 'CP 338 — Activités maritimes et voies d\'eau',
    nomCourt: 'Activités maritimes',
    type: 'O',
    regimeHebdo: 36,
    indexation: { frequence: 'à vérifier', dernierTaux: null, derniereDate: null, note: 'PAS de barème ni d\'indexation SECTORIELS publiés pour CP 338 (easypay 338.00 / Securex indexlist = « pas de minima / pas d\'indexation »). Le « annuelle janvier +2,21% / Febelfin Academy » était un placeholder FAUX (corrigé 2026-07-16 — Febelfin = banque, sans rapport). Les minima suivent le RMMMG national (indexé au pivot), pas un calendrier janvier.' },
    // ── CENSUS COMPLET : AUCUN FONDS RELEVÉ ───────────────────────────────
    // 📏 Mesure du 26/08/2026, avec son dénominateur. Aucune convention, quel que soit le critère de recherche.
    // 🚫 L'ONSS ne rattache non plus aucune catégorie d'employeur à cette
    // commission dans le fichier des taux DmfA 2026/3.
    //
    // ⚠️ LE 4 % QUI FIGURAIT ICI N'AVAIT AUCUNE SOURCE. Sur un brut de
    // 2 500 €, il facturait 100,0 € par mois et par travailleur.
    //
    // ⚖️ Le 0 vient d'un CENSUS, pas d'un acte de dissolution : il prouve que le
    // SPF ne publie rien, pas qu'aucune cotisation n'existe. La réserve est
    // écrite ci-dessous pour qu'aucune session ne le lise comme un fait clos.
    fondsSE: {
      nom: "Activités maritimes et voies d'eau — aucun fonds de sécurité d'existence relevé",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des conventions publiées pour la CP 338, lu le 26/08/2026 (0 convention). Aucune convention, quel que soit le critère de recherche. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS, qui ne rattache aucune catégorie d'employeur à cette commission.",
      dateVerif: '2026-08-26',
      aVerifier: true,
      aVerifierRaison: "Le 0 vient d'un CENSUS de titres, pas d'un acte de dissolution comme pour les CP 316 et 328.02. Un census prouve que le SPF ne publie rien sous ce numéro, pas qu'aucune cotisation n'existe. À reconfirmer avant d'affirmer à un client qu'il ne doit rien. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation. Reste aussi le site du fonds.",
      note: "Le 4 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus : 0
    // convention au total, confirmé identique à la mesure du 26/08. Zéro CCT
    // possible sous ce numéro, et le montant recyclé (plafond fédéral 10€)
    // n'est même pas la famille « standard » à 8€.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — 0 convention au total pour la CP 338",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 338 + « eco cheque » :
    // 0 résultat, cohérent avec chequesRepas/fondsSE ci-dessus (0 convention
    // au total pour cette CP).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 338 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026), cohérent avec 0 convention au total",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 338' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 36,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 338: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + Febelfin Academy',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 339 — Sociétés de logement social agréées (libellé CORRIGÉ 2026-07-09 — le code
  // disait « Secteur non-marchand francophone (Wallonie) » = FAUX ; réel confirmé CGSLB/
  // Securex/Group S). ⚠️ Bloc généré batch 29/04 sous le mauvais libellé → données À RE-VÉRIFIER.
  '339': {
    nom: 'CP 339 — Sociétés de logement social agréées',
    // ── AVANTAGE SECTORIEL — seuil de délégation syndicale ────────────────
    // ✅ Texte primaire lu le 23/08/2026. C'est l'un des rares avantages qui
    // varie RÉELLEMENT d'un secteur à l'autre — la CP 200 est à 25, la CP 226
    // à 50 — et il décide d'un droit collectif concret : au-dessus du seuil,
    // l'employeur doit permettre la constitution d'une délégation.
    avantages: {
      delegationSyndicale_seuil: 30,   // travailleurs
      delegationSyndicale_source: "CCT du 05/12/2017 (CP 339), art. 2 : « Une délégation syndicale peut être constituée dans une entreprise qui ressort de la CP 339 à partir de 30 travailleurs » — banque CCT du SPF Emploi, 339/339-2017-014883.pdf, lue le 23/08/2026",
      delegationSyndicale_dateVerif: '2026-08-23',
    },
    // ── CLASSIFICATION DE FONCTIONS ───────────────────────────────────────
    // ⚠️ « PAS DE GRILLE » N'EST PAS « PAS D'INFORMATION ». La fiche a été
    // ouverte et lue : ce secteur ne classe pas ses travailleurs par fonction.
    // C'est une réponse complète, et la coder évite qu'une session suivante
    // reparte chercher une grille qui n'existe pas.
    classifications: {
      source: 'fiche SPF, jcId 4d0d20cf… (CP 3390000), lue le 23/08/2026',
      dateVerif: '2026-08-23',
      grilleSectorielle: false,
      systeme: "un salaire minimum garanti unique (mensuel et horaire), plus un barème étudiants par âge. Aucune catégorie de fonction.",
    },
    nomCourt: 'Logement social agréé',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'pivot', dernierTaux: 0.02, derniereDate: '2026-02-01', source: 'AUDIT 2026-06 — Easypay' },
    // ── CENSUS COMPLET SUR LA CP MÈRE : AUCUN FONDS RELEVÉ ────────────────
    // 📏 Mesure du 26/08/2026, avec son dénominateur. Trois conventions en tout, toutes du 05/12/2017 : salaire minimum, indexation, et institution d'une délégation syndicale. Aucune n'institue ni ne finance un fonds de sécurité d'existence.
    // ⚠️ CORRIGÉ LE 22/09/2026 — LA PHRASE « L'ONSS NE RATTACHE AUCUNE
    // CATÉGORIE » ÉTAIT FAUSSE, ET ELLE S'ADDITIONNAIT PARFAITEMENT AVEC LE
    // RESTE DU BLOC (R14 : un bloc qui se lit bien n'est pas forcément vrai).
    // Le fichier des taux DmfA 2026/3 rattache DEUX catégories à cette CP :
    //   · 532 — CP 339 en général (+ 339.02/339.03 implicitement, depuis que
    //     339.01 a sa propre catégorie) : AUCUNE ligne de taux publiée = 0 %,
    //     cohérent avec le census CCT ci-dessus.
    //   · 632 — CP 339.01 (région flamande) SEULE, depuis le 01/04/2022 :
    //     0,13 % (codes 820 et 830). Voir le bloc '339.01' ci-dessous, sorti
    //     de cette fiche mère le 22/09/2026.
    // Le 0 % ci-dessous reste donc exact pour la mère ET pour 339.02/339.03
    // (aucune catégorie dédiée, aucune ligne de taux) — seule 339.01 diverge.
    fondsSE: {
      nom: "Sociétés de logement social agréées — aucun fonds de sécurité d'existence relevé (hors 339.01, voir sa fiche propre)",
      taux_patronal: 0,
      source: "Banque CCT du SPF Emploi, census complet des conventions publiées pour la CP 339, lu le 26/08/2026 (3 conventions) — salaire minimum, indexation, délégation syndicale, aucune sur un fonds. Recoupé sur le fichier des taux DmfA 2026/3 de l'ONSS (dmfa_rate_2026_3.zip) : catégorie 532 (CP 339 générale) SANS ligne de taux publiée. Annexe 27 (Smals/egov3-services-catalog, EMPLOYER_CLASS_FR.xlsx) relue le 22/09/2026.",
      dateVerif: '2026-09-22',
      aVerifier: true,
      aVerifierRaison: "Le 0 vient d'un CENSUS de titres, pas d'un acte de dissolution comme pour les CP 316 et 328.02. Un census prouve que le SPF ne publie rien sous ce numéro, pas qu'aucune cotisation n'existe. À reconfirmer avant d'affirmer à un client qu'il ne doit rien. À reprendre par : (1) une recherche par DATE dans la banque CCT — la CCT fondatrice du fonds peut être antérieure à 1999, borne basse de couverture de la banque pour beaucoup de commissions ; (2) le Moniteur belge via Justel, en partant de l'arrêté royal qui a rendu cette CCT obligatoire ; (3) le fonds lui-même, dont l'adresse figure dans ses statuts ; (4) un secrétariat social du secteur. ⛔ PAS par la fiche Limosa : mesurée le 26/08/2026 sur la fiche de la CP 317 (40 385 caractères lus), elle ne contient AUCUNE occurrence de « bijdrage », « fonds » ou « bestaanszekerheid » — 67 « premie » et 33 « loon » en revanche. Elle ne décrit que ce qui est versé AU TRAVAILLEUR, jamais ce que l'employeur verse à un fonds. Elle reste la bonne voie pour un barème, pas pour une cotisation. Reste aussi le site du fonds.",
      note: "Le 4,5 % qui figurait ici avant le 26/08/2026 n'avait aucune source.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — cohérent avec le fondsSE ci-dessus : 3
    // conventions au total, toutes du 05/12/2017, aucune sur les chèques-repas.
    chequesRepas: {
      obligatoire: false,
      source: "Cohérent avec le fondsSE ci-dessus — 3 conventions au total pour la CP 339, aucune sur les chèques-repas",
      dateVerif: '2026-09-05',
    },
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 339 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 339' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.30 },  // vélo CP 339: 0,30 €/km (CCT n°164 supplétive / À CONFIRMER public-search SPF) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CSC CNE + SLRB',
      indexationFrequence: 'pivot_public',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // CP 340 — Orthopédie technique (généré batch 29/04/2026)
  '340': {
    // ── Avantage SECTORIEL — emplois de fin de carrière à 55 ans ─────────
    // ✅ Texte primaire lu le 21/08/2026 — CCT du 08/04/2026 (CP 340), banque CCT
    // du SPF Emploi, 340/340-2026-006865.pdf.
    //
    // ⚠️ CE QUI EST SECTORIEL, C'EST LA TRANSPOSITION, PAS LE NOMBRE. Les
    // 55 ans viennent du cadre interprofessionnel (CCT n° 179 du CNT du
    // 21/10/2025, période du 01/01/2026 au 31/12/2027). Sans CCT SECTORIELLE
    // qui l'active, l'âge reste celui du régime général. Recopier 55 comme une
    // valeur du secteur dupliquerait une constante nationale (R1).
    //
    // 🔎 COMMENT CETTE FICHE A ÉTÉ VALIDÉE — trois filtres successifs sur le
    // PDF, jamais sur le titre. (1) les quatre marqueurs présents ; (2) « 55
    // ans » et « fin de carrière » dans la MÊME phrase ; (3) un verbe
    // OPÉRATOIRE dans cette phrase. Le filtre 3 a écarté cinq CP dont la CCT
    // ne faisait que CITER la n° 179 dans ses bases juridiques — citer n'est
    // pas transposer. Détail : __tests__/fin-de-carriere-55-sourcee.test.js.
    avantages: {
      finDeCarriere: {
        activeParCCTSectorielle: true,
        age: 55,
        cadreInterprofessionnel: 'CCT n° 179 du CNT du 21/10/2025',
        du: '2026-01-01',
        au: '2027-12-31',
        conditions: 'Réduction à mi-temps ou d\'1/5. Réservé aux travailleurs à longue carrière (35 ans comme salarié), en métier lourd, ou occupés dans une entreprise en difficulté ou en restructuration.',
        source: 'CCT du 08/04/2026 (CP 340) — emplois de fin de carrière / crédit-temps, banque CCT du SPF Emploi, 340/340-2026-006865.pdf. Texte primaire lu, article opératoire confirmé.',
        dateVerif: '2026-08-21',
      },
    },
    nom: 'CP 340 — Orthopédie technique',
    nomCourt: 'Orthopédie technique',
    type: 'O',
    regimeHebdo: 38,
    indexation: { frequence: 'variable', dernierTaux: 0.0221, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Easypay (ouvriers=0,40%)' },
    fondsSE: {
      nom: 'Fonds social CP 340',
      taux_patronal: 0.004,
      taux_patronal_employe: 0.004,
      base108Ouvrier: true,
      onssCategorie: '369',
      onssSourcee: true,
      // ⚠️ PERCEPTION SEULE, et l'écran doit pouvoir le dire : ce taux est ce que
      // l'ONSS encaisse pour ce secteur, pas nécessairement tout ce que l'employeur
      // verse au fonds. Il vaut PLANCHER SOURCÉ, là où la valeur précédente était
      // un nombre rond sans provenance.
      perceptionOnssSeule: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml), catégorie(s) d'employeur 369 : 820 ouvriers 0,40 % · employés 0,40 %. Lu le 25/08/2026. ⚠️ C'est ce que l'ONSS PERÇOIT : une cotisation que le fonds prélèverait lui-même, hors DmfA, ne serait pas visible ici.",
      note: "Taux repris du percepteur. Valeur précédente : 4,50 %, sans source ni date.",
    },
    // ✅ CORRIGÉ LE 05/09/2026 — 79 CCT au total, 1 seule taguée thème 246 :
    // CCT du 02/10/2017 (dépôt 142855, exécution AIP 2017-2018). Lue en entier
    // (22 421 caractères) : traite prime unique 150€, barèmes, éco-chèques
    // (+115€), fin de carrière, crédit-temps — aucune occurrence de « chèque-
    // repas »/« maaltijdcheque » nulle part. Le tag thème 246 est un faux
    // positif du SPF.
    chequesRepas: {
      obligatoire: false,
      source: "CCT du 02/10/2017 (dépôt 142855/CO/340), banque CCT du SPF Emploi, texte primaire lu intégralement le 05/09/2026 — tag thème 246 faux positif, aucune mention de chèque-repas",
      dateVerif: '2026-09-05',
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (340/340-2020-001519.pdf, CCT du 05/02/2020, dépôt 157676/CO/340,
    // « toujours d'application », en remplacement de la CCT du 02/10/2017).
    // Art. 2 : « À partir du 1er juillet 2019, les entreprises octroieront,
    // pour les travailleurs, un éco-chèque de 250 EUR par an. » Octroi en
    // juin de chaque année (art. 3), valeur nominale max. 10 EUR/chèque.
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 05/02/2020 (340/340-2020-001519.pdf), banque CCT du SPF Emploi, dépôt 157676/CO/340, texte primaire lu le 21/09/2026, art. 2 : 250 EUR/an depuis le 01/07/2019, octroi en juin.",
    ecoChequesDateVerif: '2026-09-21',
    primeFinAnnee_pct: 8.33,
    primeFinAnnee_formule: '1 mois brut (anc. min 6 mois)',
    formation: { jours: 5, fonds: 'Fonds sectoriel CP 340' },
    cotisationSpeciale: null,
    heuresSup: {
      jour:     { taux: 0.50, label: 'Heures sup jour (+50%)' },
      nuit:     { taux: 0.50, label: 'Heures sup nuit (+50%)' },
      dimanche: { taux: 1.00, label: 'Heures sup dimanche/JF (+100%)' },
      seuilHebdomadaire: 38,
    },
    indemnites: { transportPublic_pct: 1.00, velo: 0.20 },  // vélo CP 340: 0,20 €/km (CCT sectorielle propre) — sourcé 2026-07-08, cf. registre Garde-fou Sources
    _meta: {
      derniereMAJ: '2026-04-29',
      sourceMAJ: 'CGSLB orthopédie',
      indexationFrequence: 'annuelle',
      genere: 'Batch 29/04/2026 — couverture exhaustive 160 CP',
      note: 'Valeurs basées sur famille sectorielle. À affiner avec sources officielles si activité réelle dans cette CP.',
    },
  },

  // ─── CP ajoutées 2026-06 (audit multi-agents, sources officielles) ───
  '211': {
    // ── Régime hebdomadaire — précision ajoutée le 23/08/2026 ─────────────
    // ⚠️ LA VALEUR 38 EST CONFIRMÉE, ET J'AI FAILLI CRIER À L'ERREUR.
    // La fiche Limosa annonce « een gemiddelde van 36 uren », ce qui ressemble
    // à une divergence de 5,6 % sur le taux horaire. La lecture du PARAGRAPHE
    // ENTIER montre qu'il n'en est rien : les deux nombres coexistent.
    //
    // ✅ Fiche Limosa de détachement, PC 211, § 4.1, CCT du 09/03/2026
    // (198801, force obligatoire demandée), dernière mise à jour 29/07/2026,
    // lue le 23/08/2026 :
    //   « § 1. De wekelijkse arbeidsduur wordt bepaald op 38 uren. »
    //   « § 2. Twaalf dagen bij wijze van arbeidsduurvermindering worden
    //     toegekend (pro rata temporis) … Deze arbeidsduur stemt overeen met
    //     een gemiddelde van 36 uren. »
    //
    // Soit : 38 h CONVENTIONNELLES, et douze jours de réduction du temps de
    // travail qui ramènent la moyenne EFFECTIVE à 36 h. Même motif que la
    // CP 124 (40 h conventionnelles compensées) — et la raison pour laquelle
    // « le régime hebdomadaire » d'une CP n'est pas un nombre mais deux.
    // 📏 Diviser un mensuel par 38 ou par 36 change le taux horaire de 5,6 % :
    // la distinction n'est pas cosmétique.
    regimeHebdoNote: {
      conventionnel: 38,
      moyenneEffective: 36,
      mecanisme: "douze jours de réduction du temps de travail par an, au prorata des prestations effectives",
      source: "Fiche Limosa de détachement, SPF Emploi, Detachering211.pdf, § 4.1 (§§ 1 et 2), CCT du 09/03/2026 (n° 198801). Dernière mise à jour de la fiche : 29/07/2026. Lue le 23/08/2026.",
      dateVerif: '2026-08-23',
    },
    nom: 'CP 211 — Industrie et commerce du pétrole',
    // ── Prime de fin d'année CP 211 — ajoutée le 23/08/2026 ─────────────
    // ✅ Fiche Limosa du SPF Emploi, Detachering211.pdf § 2.2.1, reproduisant
    // la CCT du 09/03/2026 (n° 198801), art. 24 et 25.
    // ⚠️ ELLE NE S'EXPRIME PAS EN MOIS NI EN POURCENTAGE, mais en HEURES —
    // d'où le nom de clé propre : la convertir en « 1 mois brut » pour
    // ressembler aux autres fiches en ferait un chiffre faux.
    primeFinAnnee_formule: '200 heures du salaire horaire de base INDEXÉ au moment du paiement',
    primeFinAnneeDetail: {
      heures: 200,
      base: "salaire horaire de base indexé de l'ouvrier au moment du paiement de la prime",
      paiement: "au cours de la dernière semaine de l'année civile",
      prorata: "les ouvriers qui n'ont travaillé qu'une partie de l'année reçoivent la prime au prorata des mois de service ; les entrants ont droit à autant de douzièmes que de mois prestés",
      assimiles: "les jours de maladie et d'accident du travail comptent comme des jours réellement prestés",
      exclusion: "aucun droit si l'ouvrier n'a effectué AUCUNE prestation durant l'année civile concernée",
      source: 'CCT du 09/03/2026 (198801), art. 24 et 25 — fiche Limosa du SPF Emploi, Detachering211.pdf § 2.2.1, lue le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Chèques-repas CP 211 — ajoutés le 23/08/2026 ────────────────────
    // ✅ CCT du 09/03/2026, banque CCT du SPF Emploi (211/211-2026-003715.pdf).
    // ⚠️ CE N'EST PAS UNE OBLIGATION SÈCHE : l'employeur a le CHOIX entre un
    // net équivalent et l'introduction de chèques-repas. Coder « obligatoire:
    // true » ferait réclamer des chèques à un employeur qui a régularisé en net.
    chequesRepas: {
      obligatoire: false,
      // ⚠️ LA RAISON DU `false` EST ÉCRITE ICI, PAS DANS UN COMMENTAIRE DE PR.
      // C'est ce qu'exige `cheques-repas-drapeau-suit-la-date` : un « non
      // obligatoire » sans raison lisible par une machine est le défaut qui a
      // coûté six semaines à la CP 140.
      raisonNonObligatoire: "la CCT laisse le CHOIX à l'employeur entre un net équivalent et l'introduction de chèques-repas — ce n'est donc pas une obligation de chèques-repas, mais une obligation d'octroyer l'un OU l'autre",
      alternative: true,
      // ⚠️ PAS `depuis` : ce champ est lu par le garde-fou comme la date à
      // laquelle une OBLIGATION commence. Ici la date est celle à laquelle le
      // CHOIX s'ouvre. Réutiliser `depuis` faisait dire à la fiche qu'elle se
      // contredisait, et le garde-fou avait raison de le signaler.
      choixApplicableDepuis: '2026-01-01',
      choixEmployeur: "soit un net équivalent à celui de l'art. 2 de la CCT, soit l'introduction de chèques-repas",
      siIntroduction: { part_employeur: 2.00, part_travailleur: 1.09 },
      siDejaExistants: "la part patronale est AUGMENTÉE de 2 € (et non portée à 2 €)",
      cadre: 'dans le respect de la loi sur la norme salariale',
      source: 'CCT du 09/03/2026, art. 3 et chapitre III — 211/211-2026-003715.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Formation CP 211 — ce qui a été trouvé, et ce qui ne l'a pas été ─
    // ⚠️ LA SEULE CCT « formation » DE LA CP 211 EST UN CONGÉ SYNDICAL, PAS
    // L'EFFORT DE FORMATION. La CCT du 09/03/2026 « Formation syndicale »
    // accorde 8 jours d'absence par an et par mandat effectif et suppléant,
    // répartis entre organisations syndicales au prorata des mandats. Ce
    // n'est pas le droit individuel à la formation, et les confondre ferait
    // annoncer un droit à un travailleur qui ne l'a pas.
    formation: {
      joursSectoriels: null,
      renvoi: "droit individuel à la formation de la loi du 03/10/2022 (fédéral) — aucun effort sectoriel propre trouvé pour la CP 211",
      congeSyndical: {
        joursParAnParMandat: 8,
        portee: 'mandats effectifs et suppléants, répartis entre organisations syndicales au prorata des mandats',
        source: 'CCT du 09/03/2026 « Formation syndicale », art. 5 — 211/211-2026-003697.pdf, lue le 23/08/2026',
      },
      recherche: '125 CCT listées (jcId 2110000), balayées le 23/08/2026 sur formation/vorming/opleiding — seule la formation syndicale ressort',
      dateVerif: '2026-08-23',
    },
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 125 CCT de la CP 211 le 23/08/2026 (jcId 2110000). Aucune cotisation sectorielle au-delà du FSE ; les textes « cotisation » relevés portent sur l'intervention dans les frais de transport.",
      dateVerif: '2026-08-23',
    },
    nomCourt: 'Pétrole',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — CSC/ACV +2% au 01/01/2026 (absorbe ex-CP 117 ouvriers depuis 01/04/2023)' },
    fondsSE: {
      nom: 'Fonds social pétrole',
      taux_patronal: 0.002,
      taux_patronal_employe: 0.002,
      // Le code 820 porte, mot pour mot chez l'ONSS, « sur les rémunérations à 108 %
      // des travailleurs manuels ». Appliquer ce taux au brut nu sous-compterait de 8 %.
      base108Ouvrier: true,
      onssCategorie: '230',
      // Drapeau lu par le moteur de paie : lui seul autorise le taux par public et la
      // base 108 %. Les CP sans ce drapeau gardent le comportement historique.
      onssSourcee: true,
      dateVerif: '2026-08-25',
      source: "ONSS — fichier des taux DmfA 2026/3 (NOSSContributionRate_2026_3.xml, téléchargé depuis la bibliothèque technique), catégorie(s) d'employeur 230. Codes travailleur : 820 ouvriers 0,20 % · 830 employés 0,20 % · 826/836 forfait prime syndicale 24,35 €. Recoupé au centième sur le tableau « Cotisations destinées au Fonds de sécurité d'existence » (FSE_3-2026.xlsx, situation au 1er juillet 2026, ligne « Secteur du pétrole »). Deux rendus du même publicateur, concordants — ce qui écarte une erreur de lecture, pas une erreur de l'ONSS. Lu le 25/08/2026.",
      forfait: "24,35 € par travailleur (prime syndicale, codes 826 et 836) — un forfait ne se multiplie pas par le brut, il n’entre donc pas dans le taux.",
      note: "Le taux était codé à 0 avec la note « à confirmer ». Il vaut 0,20 %. Valeur faible, mais elle n’est plus une inconnue.",
    },
    // ✅ CORRIGÉ LE 18/09/2026 — recherche plein texte CP 211 + « eco cheque » :
    // 0 résultat.
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 211 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026)",
    ecoChequesDateVerif: '2026-09-18',
    note: 'Depuis 01/04/2023, la CP 211 couvre employés ET ouvriers du pétrole (ex-CP 117 fusionnée).',

    // ── Heures supplémentaires CP 211 — ajoutées le 23/08/2026 ───────────
    // ✅ Fiche Limosa de détachement du SPF Emploi, Detachering211.pdf,
    // § 2.2.6, reproduisant la CCT du 09/03/2026 (n° 198801). Lue le
    // 23/08/2026 (fiche mise à jour le 29/07/2026).
    //
    // ⚠️ LE SECTEUR FAIT MIEUX QUE LE PLANCHER FÉDÉRAL, ET C'EST DE L'ARGENT.
    // L'art. 46 accorde +100 % DÈS LA CINQUIÈME heure supplémentaire du jour,
    // là où la loi du 16/03/1971 s'arrête à +50 % en semaine. Un employeur
    // qui applique le seul plancher fédéral sous-paie la 5e heure et les
    // suivantes de 50 points de pourcentage.
    heuresSup: {
      declenchement: {
        parJour: 8,
        parSemaine: 40,
        moyenne: 38,
        samedi: 'lorsque le samedi est presté sur la base de l\'art. 6 : au-delà de 8 h/jour, 48 h/semaine ou 80 h sur deux semaines',
        source: 'CCT du 09/03/2026 (198801), § 2.2.6.1.1 et art. 45',
      },
      jour: {
        taux: 0.50,                       // plancher fédéral, non amélioré pour les 4 premières heures
        note: 'loi du 16/03/1971 — le secteur ne descend pas en dessous et ne l\'améliore pas avant la 5e heure',
      },
      // ⭐ L'amélioration sectorielle.
      auDelaDe4HeuresParJour: {
        taux: 1.00,                       // +100 %
        condition: 'heures supplémentaires effectuées immédiatement après la fin de la tâche journalière, l\'un des cinq premiers jours de la semaine, à partir de la 5e heure supplémentaire du jour',
        prorata: true,
        arrondi: 'de 15 à 30 minutes = une demi-heure ; plus de 30 minutes = une heure',
        source: 'CCT du 09/03/2026 (198801), art. 46',
      },
      samedi: {
        taux_2premieresHeures: 0.50,
        taux_heuresSuivantes: 1.00,
        minimumPaye: 3,                   // heures, même si moins ont été prestées
        cumulAvecPrimeEquipe: true,       // « onverminderd de verschuldigde ploegenvergoedingen »
        source: 'CCT du 09/03/2026 (198801), art. 39 § 1',
      },
      dimanche: {
        taux: 1.00,
        cumulAvecPrimeEquipe: true,       // le +100 % n'exclut ni le shiftbonus ni la prime de 7,5 % de l'art. 53
        source: 'CCT du 09/03/2026 (198801), art. 40 §§ 1 et 2',
      },
      // Rappel après congé technique : rémunération plancher garantie.
      rappelApresJourneeComplete: {
        moins4h: 'quatre heures de salaire normal',
        plus4h: 'huit heures de salaire normal',
        deplacementAllerRetour: 'une heure de salaire normal, forfaitaire',
        taux_2premieresHeures: 0.50,
        taux_heuresSuivantes: 1.00,
        exclusion: 'ne s\'applique pas aux travaux ordinaires liés à une exploitation normale (chargement/déchargement de navires, allèges, wagons-citernes et camions-citernes)',
        source: 'CCT du 09/03/2026 (198801), art. 48 et 49',
      },
      source: 'Fiche Limosa de détachement, SPF Emploi, Detachering211.pdf, § 2.2.6, lue le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Indemnités sectorielles CP 211 — ajoutées le 23/08/2026 ──────────
    // ✅ Même fiche, §§ 2.2.2 à 2.2.5 et 3.1.
    // ⚠️ RÉSERVE DE PÉRIMÈTRE : la fiche Limosa ne couvre que le noyau dur
    // du détachement — son silence sur une indemnité ne prouve pas l'absence.
    indemnites: {
      perimetreSource: 'Fiche Limosa de détachement (noyau dur détachement) — son silence ne vaut pas absence',
      source: 'SPF Emploi, Detachering211.pdf, lue le 23/08/2026 (fiche mise à jour le 29/07/2026)',
      dateVerif: '2026-08-23',

      travailEnEquipes: {
        pct_jour: 0.095,                  // 9,5 % sur le salaire de base
        pct_nuit: 0.355,                  // 35,5 % sur le salaire de base
        definitionJour: 'équipe travaillant de 6 à 14 h ou de 14 à 22 h',
        definitionNuit: 'équipe travaillant entre 22 et 6 h',
        deuxEquipes: 'si deux équipes, la première commençant avant 7 h et la seconde finissant après 20 h sans dépasser 22 h, la prime de jour est due aux DEUX équipes',
        inclusDansSalaireNormal: 'petit chômage, semaine garantie et jours fériés légaux',
        source: 'CCT du 09/03/2026 (198801), art. 32 à 35',
      },

      travailEnEquipesOccasionnel: {
        pct_jour: 0.19,
        pct_nuit: 0.71,
        definition: 'tout travail d\'une durée maximale de sept jours ouvrables consécutifs ; dès le huitième, les primes d\'équipe ordinaires s\'appliquent',
        garantie: 'le travailleur ne peut subir aucun désavantage : salaire normal de ses heures de jour antérieures + ces primes',
        source: 'CCT du 09/03/2026 (198801), art. 36 à 38',
      },

      equipesContinuesSamedi: {
        pct_jour: 0.22,                   // s'AJOUTE aux primes d'équipe ci-dessus
        pct_nuit: 0.50,
        source: 'CCT du 09/03/2026 (198801), art. 39 § 2',
      },

      travauxSalissants: {
        indemniteJournaliere: 'au minimum six heures de salaire',
        majoration: 0.25,                 // salaire normal + 25 % pour les travaux énumérés
        exemples: 'nettoyage intérieur de fours, chaudières, ballons, drums, citernes, wagons et camions ayant contenu des produits noirs, butane ou propane ; retrait des boues des séparateurs d\'huile ; déchargement de charbon ; dégraissage et nettoyage chimique de wagons-citernes',
        source: 'CCT du 09/03/2026 (198801), art. 29',
      },

      fraisDeTransport: {
        base: 'abonnement de trajet SNCB, à 100 %, SANS plafond de rémunération',
        modesCouverts: 'transports publics (train, tram, bus, vicinaux) et tout moyen privé (voiture, moto, vélo…)',
        tarification: 'par tranche de 5 km, au tarif de la borne SUPÉRIEURE de la tranche',
        source: 'CCT du 17/10/2019 (155195), AR 11/06/2020 — B.S. 22/07/2020, art. 2 et 3',
      },
    },
  },
  '334': {
    nom: 'CP 334 — Loteries publiques',
    // Les trois dimensions ci-dessous découlent de la MÊME cause, déjà
    // établie dans `_sourcesVerifiees` : zéro CCT déposée pour la CP 334.
    // Ce ne sont pas trois « on n'a pas trouvé », c'est une absence unique
    // vérifiée par deux points d'entrée, appliquée trois fois.
    primeFinAnnee_formule: null,
    primeFinAnneeDetail: {
      constat: 'aucune prime de fin d\'année SECTORIELLE relevable',
      raison: 'aucune CCT déposée sous le jcId 3340000 — voir `_sourcesVerifiees`',
      ouChercherEncore: "convention d'entreprise de la Loterie Nationale, ou statut du personnel",
      dateVerif: '2026-08-23',
    },
    chequesRepas: {
      obligatoire: false,
      raisonNonObligatoire: "aucune CCT sectorielle ne prévoit de chèques-repas : le secteur ne les impose pas (balayage des 59 CCT le 23/08/2026)",
      constat: 'aucune CCT sectorielle sur les chèques-repas',
      raison: 'aucune CCT déposée sous le jcId 3340000 — voir `_sourcesVerifiees`',
      dateVerif: '2026-08-23',
    },
    formation: {
      joursSectoriels: null,
      renvoi: 'droit individuel à la formation de la loi du 03/10/2022 (fédéral)',
      raison: 'aucune CCT déposée sous le jcId 3340000 — voir `_sourcesVerifiees`',
      dateVerif: '2026-08-23',
    },
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "aucune CCT n'est déposée pour la CP 334 (0 convention sous le jcId 3340000, recoupé avec la liste officielle des 312 CP) — voir `_sourcesVerifiees`. Aucune cotisation sectorielle relevable par cette voie.",
      dateVerif: '2026-08-23',
    },
    nomCourt: 'Loteries publiques',
    regimeHebdo: 38,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.02, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — Synova/CGSLB +2% au 01/01/2026 (régime 38h à valider CCT)' },
    // ── LA DERNIÈRE FICHE MUETTE DES 160, TRANCHÉE LE 26/08/2026 ──────────────
    // Elle portait `note: 'FSE à confirmer'` — un drapeau NU, sans source ni
    // date, donc exactement ce que R12 refuse de compter comme tranché. Elle
    // était la seule des 160 dans ce cas.
    //
    // Deux voies indépendantes disent la même chose, et c'est ce qui permet
    // d'écrire 0 plutôt que « à vérifier » :
    //
    //  1. **Aucune CCT n'est déposée pour la CP 334** — 0 convention sous le
    //     jcId 3340000, recoupé avec la liste officielle des 312 CP (lecture du
    //     23/08/2026, cf. `_sourcesVerifiees` plus bas). Pas de convention, pas
    //     de fonds institué : un FSE se crée PAR convention rendue obligatoire.
    //  2. **L'ONSS ne perçoit aucune cotisation de fonds pour cet employeur.**
    //     La catégorie 351 de l'annexe 27 nomme la Loterie Nationale par son
    //     numéro d'entreprise et ne porte AUCUNE ligne de cotisation FSE —
    //     ni 820/830 (fonds), ni 825/835 (pension sectorielle), ni forfait.
    //
    // ⚠️ Une seule de ces deux voies n'aurait pas suffi. Le silence de l'ONSS
    // ne prouve jamais à lui seul l'absence de fonds : un fonds peut percevoir
    // lui-même, hors DmfA (c'est le cas de la CP 317, tranchée le même jour).
    // Ici c'est l'ABSENCE DE CONVENTION qui ferme la question, et l'ONSS qui la
    // confirme d'un second point d'entrée.
    fondsSE: {
      nom: 'Loteries publiques — aucun fonds de sécurité d\'existence institué',
      taux_patronal: 0,
      dateVerif: '2026-08-26',
      source: "DEUX voies concordantes. (1) Banque CCT du SPF Emploi : aucune convention déposée pour la CP 334 (0 sous le jcId 3340000, recoupé avec la liste officielle des 312 commissions), lu le 23/08/2026 — un fonds de sécurité d'existence s'institue par CCT rendue obligatoire, donc pas de convention, pas de fonds. (2) ONSS, annexe 27 de l'instantané DmfA 2026/3 : la catégorie d'employeur 351 (« entreprises publiques autonomes… concerne la RTBF n° 429083-37, la Loterie Nationale S.A. n° 930145-60 ») ne porte AUCUNE ligne de cotisation — ni fonds, ni pension sectorielle, ni forfait. Lu le 26/08/2026.",
      note: "Avant le 26/08/2026 : `note: 'FSE à confirmer'`, drapeau nu sans source ni date — la dernière des 160 fiches dans ce cas. ⚠️ Le personnel de la Loterie Nationale relève d'un statut et de conventions d'ENTREPRISE : l'absence de fonds SECTORIEL ne dit rien de ce que l'entreprise verse à ses propres régimes.",
    },
    // ✅ CORRIGÉ LE 21/09/2026 — recherche plein texte CP 334 + « eco cheque » :
    // 0 résultat, cohérent avec chequesRepas et fondsSE ci-dessus (zéro CCT
    // déposée sous ce numéro, vérifié).
    ecoChequesAnnuel: null,
    ecoChequesSource: "Banque CCT du SPF Emploi, CP 334 — recherches plein texte « eco cheque », « ecocheque », « ecocheques » et « éco-chèques » : 0 résultat (relevé le 21/09/2026), cohérent avec zéro CCT déposée sous ce numéro (voir chequesRepas/fondsSE ci-dessus)",
    ecoChequesDateVerif: '2026-09-21',
    note: 'Loterie Nationale et jeux/paris d\'intérêt général sous contrôle public. Régime 38h par défaut, à valider CCT.',

    // ── CP 334 : ZÉRO CCT DÉPOSÉE, ET C'EST VÉRIFIÉ (23/08/2026) ─────────
    // ⚠️ Cette fiche est maigre, et il faut dire POURQUOI — sinon la session
    // suivante repart en chasse et retrouve la même absence.
    //
    // La CP existe bel et bien : la liste officielle du SPF
    // (public-search.emploi.belgique.be/jc-service/joint_committees, 312
    // commissions au 23/08/2026) contient le numéro 3340000 « LOTERIES
    // PUBLIQUES / OPENBARE LOTERIJEN ». Mais la banque CCT interrogée sur ce
    // même jcId rend **zéro** convention.
    //
    // Ce n'est donc PAS un « je n'ai pas trouvé » (R2 ter) : c'est une absence
    // ÉTABLIE par deux points d'entrée indépendants — la CP est reconnue, et
    // rien n'est déposé sous son numéro. Aucune règle sectorielle n'est
    // relevable par cette voie, ni heures sup, ni indemnités, ni fonds.
    _sourcesVerifiees: {
      banqueCCT: { jcId: '3340000', conventions: 0, quand: '2026-08-23' },
      listeOfficielle: { present: true, libelle: 'LOTERIES PUBLIQUES / OPENBARE LOTERIJEN', quand: '2026-08-23' },
      consequence: "aucune règle sectorielle relevable par la banque CCT ; les valeurs de cette fiche qui ne sont pas sourcées le restent tant qu'une autre voie n'est pas ouverte (convention d'entreprise Loterie Nationale, statut du personnel)",
    },

    // Le renvoi au fédéral est ici une CONSÉQUENCE de l'absence ci-dessus,
    // pas une hypothèse : sans CCT déposée, il ne peut y avoir de dérogation
    // sectorielle aux taux de l'art. 29.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucune CCT n'est déposée pour la CP 334, donc aucune dérogation sectorielle possible par cette voie",
      source: 'banque CCT du SPF Emploi, jcId 3340000 : 0 convention, recoupé avec la liste officielle des 312 CP',
      dateVerif: '2026-08-23',
    },

    // Même conséquence pour les indemnités : sans CCT déposée, il n'existe
    // aucune indemnité SECTORIELLE relevable par la banque CCT. Ce n'est pas
    // « on n'a pas cherché » — c'est l'absence établie plus haut, appliquée à
    // cette dimension. Les indemnités éventuelles de la Loterie Nationale
    // relèveraient d'une convention d'entreprise ou d'un statut du personnel,
    // qui ne passent pas par cette voie.
    indemnites: {
      aucuneIndemniteSectorielle: true,
      raison: "aucune CCT déposée sous le jcId 3340000 (0 convention, banque CCT du SPF Emploi) alors que la CP figure bien dans la liste officielle des 312 commissions — voir `_sourcesVerifiees`",
      ouChercherEncore: "convention d'entreprise de la Loterie Nationale, ou statut du personnel — hors banque CCT",
      source: 'banque CCT du SPF Emploi, jcId 3340000 : 0 convention, recoupé avec la liste officielle des 312 CP',
      dateVerif: '2026-08-23',
    },
  },
  '341': {
    nom: 'CP 341 — Intermédiation en services bancaires et d\'investissement',
    // ── Formation CP 341 — ajoutée le 23/08/2026 ────────────────────────
    // ✅ CCT du 26/09/2023 (341/341-2023-013569.pdf), texte primaire lu : les
    // partenaires « confirment que 5 jours de formation par équivalent temps
    // plein doivent en moyenne être proposés depuis 2020 ».
    // ⚠️ « EN MOYENNE » ET « PROPOSÉS » : ce n'est pas un droit individuel
    // opposable de 5 jours par travailleur. Le formuler ainsi ferait promettre
    // à un travailleur ce que la CCT promet à l'effectif.
    formation: {
      joursParETP: 5,
      nature: 'moyenne à PROPOSER par équivalent temps plein, pas un droit individuel garanti',
      depuis: 2020,
      source: 'CCT du 26/09/2023 — banque CCT du SPF Emploi, 341/341-2023-013569.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Chèques-repas CP 341 — rien de sectoriel trouvé ─────────────────
    chequesRepas: {
      obligatoire: false,
      // ⚠️ 16/09/2026 — cette raison était celle de la CP 334, recopiée : « aucune CCT
      // n'est déposée pour la CP 334 […] voir `_sourcesVerifiees` », affichée sur la fiche
      // de la CP 341 alors que `recherche` ci-dessous compte 59 CCT et que ce bloc n'a
      // pas de `_sourcesVerifiees`. Réécrite depuis `recherche`, sans rien y ajouter.
      raisonNonObligatoire: "les 59 CCT de la CP 341 (jcId 3410000), balayées le 23/08/2026 sur maaltijd/chèque/repas, ne donnent aucun résultat : aucune obligation sectorielle relevée",
      constat: 'aucune CCT sectorielle sur les chèques-repas',
      recherche: '59 CCT listées (jcId 3410000), balayées le 23/08/2026 sur maaltijd/chèque/repas — aucun résultat',
      consequence: "s'il y en a dans une entreprise, ils relèvent d'une convention d'entreprise, pas du secteur",
      dateVerif: '2026-08-23',
    },
    // ⚠️ `null` EST UNE RÉPONSE, PAS UN VIDE — mais un `null` NU ne vaut rien.
    // La note dit ce qui a été balayé, sinon poser la clé serait le moyen le
    // plus rapide de faire monter le score de complétude sans rien savoir (R12).
    cotisationSpeciale: null,
    cotisationSpecialeNote: {
      constat: 'aucune cotisation sectorielle spéciale',
      balayage: "balayage des 59 CCT de la CP 341 le 23/08/2026 (jcId 3410000). Les textes « cotisation » portent sur le FSE SOFUBA (voir fondsSE.cctSourcee) et sur l'utilisation des cotisations groupes à risque, dont le cadre est fédéral (loi du 27/12/2006). Rien de sectoriel en propre.",
      dateVerif: '2026-08-23',
    },
    nomCourt: 'Intermédiation bancaire',
    regimeHebdo: 37.5,
    indexation: { frequence: 'annuelle', mois: [1], dernierTaux: 0.0221, derniereDate: '2026-01-01', source: 'AUDIT 2026-06 — SD Worx/Securex +2,21% au 01/01/2026', formule: 'sante_lisse_nov_dec', formuleAConfirmer: true },
    // 🔴 ÉCART EN SENS INVERSE, MESURÉ LE 23/08/2026 — TAUX NON TOUCHÉ.
    // Ici on ne facture RIEN (taux_patronal: 0, « à confirmer ») alors qu'une
    // CCT EN VIGUEUR, à durée indéterminée, impose une cotisation : « chaque
    // employeur […] est tenu, à partir du 1er janvier 2021, de verser au Fonds
    // de sécurité d'existence — SOFUBA — une cotisation de base égale à 0,65 %
    // du salaire soumis à l'ONSS ».
    // Conséquence : nos estimations de coût employeur pour la CP 341 sont
    // TROP BASSES d'environ 19,50 € par mois sur un brut de 3 000 €. Un devis
    // trop bas se découvre à la première facture — incident Solinas.
    // ⛔ Valeur laissée telle quelle : la porter à 0,0065 créerait un coût
    // patronal là où l'app n'en affiche aucun. Arbitrage fondateur (00.02).
    fondsSE: {
      nom: 'SOFUBA',
      taux_patronal: 0,
      aVerifier: true,
      note: "Fonds social SOFUBA — taux codé à 0 alors qu'une CCT en vigueur impose 0,65 % (voir cctSourcee)",
      cctSourcee: {
        taux: 0.0065,
        base: "salaire soumis à l'ONSS",
        depuis: '2021-01-01',
        duree: 'indéterminée',
        source: 'CCT du 23/09/2020, art. 3 — banque CCT du SPF Emploi, 341/341-2020-012020.pdf, texte primaire lu le 23/08/2026',
        dateVerif: '2026-08-23',
        ecartAvecTauxCode: 'nous facturons 0 %, la CCT impose 0,65 % — le coût employeur affiché est SOUS-ÉVALUÉ',
      },
    },
    // ✅ CORRIGÉ LE 21/09/2026 — texte primaire lu, banque CCT du SPF Emploi
    // (341/341-2016-001791.pdf, CCT du 03/02/2016, dépôt 132995/CO/341,
    // « toujours d'application »). Art. 4 §1 : « Aan elke voltijds
    // tewerkgestelde werknemer met een volledige referte periode worden
    // ecocheques een keer per jaar toegekend ter waarde van 250 EUR » (à
    // chaque travailleur temps plein avec période de référence complète,
    // 250 EUR/an). Temps partiel proratisé par palier (§2). Valeur
    // nominale max. 10 €/chèque (art. 3 §3).
    ecoChequesAnnuel: 250,
    ecoChequesSource: "CCT du 03/02/2016 (341/341-2016-001791.pdf), banque CCT du SPF Emploi, dépôt 132995/CO/341, texte primaire lu le 21/09/2026, art. 4 §1 : 250 EUR/an temps plein, proratisé par palier de temps partiel.",
    ecoChequesDateVerif: '2026-09-21',
    note: 'Agents/courtiers bancaires indépendants (ex-CP 218 depuis 02/10/2014). Régime 37h30/sem. Calculateur dédié: cp341-calculator.',

    // ── Indemnités CP 341 — ajoutées le 23/08/2026 ───────────────────────
    // ✅ TEXTE PRIMAIRE LU : CCT du 09/07/2015, n° 129876/CO/341, banque CCT
    // du SPF Emploi (341/341-2015-008922.pdf), art. 2 § 2 et annexe 1.
    indemnites: {
      source: 'CCT du 09/07/2015, n° 129876/CO/341 — 341/341-2015-008922.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
      fraisDeTransport: {
        // ⚠️ R1 : le montant n'est PAS recopié — la CCT renvoie elle-même à
        // l'AR du 28/07/1962, qui fixe le barème. Une seule vérité.
        base: "intervention de l'employeur dans le prix de la carte-train MENSUELLE assimilée à l'abonnement social, telle que prévue par l'AR du 28/07/1962",
        cartesCombinees: 'vaut aussi pour le calcul du prix des cartes-trains combinées SNCB/TEC ou DE LIJN',
        plafondDistance: 150,             // km — distances SNCB limitées à 150 km (annexe 1, note de bas de page)
        source: 'art. 2 § 2 et annexe 1',
      },
    },

    // ── Congés d'ancienneté CP 341 — ajoutés le 23/08/2026 ───────────────
    // ✅ CCT du 15/10/2019 (341/341-2019-013199.pdf), art. 2, qui REMPLACE le
    // premier alinéa de l'art. 4 de la CCT du 09/07/2015 sur le temps de
    // travail. Durée indéterminée depuis le 15/10/2019.
    congesAnciennete: {
      unJourParPalier: [5, 10, 15, 20, 25],   // années d'ancienneté ININTERROMPUE
      maxParAn: 5,                            // jours
      source: 'CCT du 15/10/2019 modifiant la CCT du 09/07/2015 (n° 129878/CO/341), art. 2 — 341/341-2019-013199.pdf, texte primaire lu le 23/08/2026',
      dateVerif: '2026-08-23',
    },

    // ── Heures supplémentaires CP 341 — ce qu'on sait, et ce qu'on ne sait pas
    // ⚠️ « PAS TROUVÉ » N'EST PAS « N'EXISTE PAS » (R2 ter) — d'où le détail de
    // la recherche, pour que la session suivante ne la refasse pas à l'identique.
    heuresSup: {
      tauxSectoriel: null,
      tauxRenvoi: "loi du 16/03/1971, art. 29 (tempsTravail.heuresSupp) — aucun taux propre à la CP 341 n'a été trouvé",
      recherche: {
        ou: 'banque CCT du SPF Emploi, POST /joint-work-convention/search, jcId 3410000',
        quand: '2026-08-23',
        combien: '59 conventions listées, 8 titres retenus au filtre (heure, sursalaire, supplémentaire, durée/temps de travail, nuit, dimanche, samedi, week-end, prestation, indemnité, frais, prime)',
        resultat: "aucune ne fixe de taux d'heures supplémentaires ; les CCT « temps de travail » de 2015, 2017 et 2019 portent sur les congés d'ancienneté, pas sur le sursalaire",
        reste: "recherche par TITRE uniquement, à reprendre par thème ou texte intégral",
      },
      source: "absence de taux sectoriel constatée sur les 59 CCT listées de la CP 341 le 23/08/2026",
      dateVerif: '2026-08-23',
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// FONDS SE D'UNE SOUS-CP RÉELLE, SANS FICHE COMPLÈTE — 22/09/2026
//
// ⚠️ POURQUOI CE N'EST PAS UNE CLÉ DE TAUX_SECTORIELS. Créer '339.01' comme
// clé de premier niveau (le geste qui a marché pour 140.01/.02/.04/.05,
// PR #3258, qui avaient déjà une fiche riche AVANT ce fondsSE) a fait chuter
// le minimum de complétude de la grille de 15/16 à 5/16 et cassé le cliquet
// R12 (« aucune fiche à moitié remplie ») : 339.01 n'a AUCUNE fiche, et R12
// interdit d'en ouvrir une à moitié vide pour un seul chiffre, tout comme R2
// interdit d'inventer les 10 autres dimensions (congés, classification…)
// pour la remplir.
//
// Ce registre porte donc le TAUX SEUL, prouvé indépendamment. La dimension
// `fondsSE` de la mesure de complétude reste couverte par la fiche MÈRE
// ('339'), qui répond déjà juste pour tout ce qui n'est pas le taux lui-même
// — 339.01 est une déclinaison RÉGIONALE de la CP 339, pas un secteur
// différent. Lu par `blocFondsSE()` (fonds-se-cotisation.js), avant le
// repli sur la mère.
//
// ⚠️ PAS DE TRADUCTION NL (R8 bis) — et ce n'est pas un oubli : mesuré le
// 22/09/2026, `getFicheCP('339.01')` (le seul chemin qui affiche du texte
// traduit à l'écran) ne visite ce bloc que si '339.01' est une clé de
// TAUX_SECTORIELS, ce qu'elle n'est plus. Le cliquet cp-notes-nl-cliquet
// ne le demande donc pas aujourd'hui — un texte non affiché ne se traduit
// pas (R8 bis vaut pour ce qui est SERVI, pas pour toute donnée qui existe).
// Si un écran vient un jour lire ce registre pour 339.01, le cliquet le
// détectera et la traduction manquante fera échouer le build.
export const FONDS_SE_SOUS_CP_SANS_FICHE = {
  '339.01': {
    nom: "Sociétés de logement social agréées de la Région flamande — catégorie ONSS dédiée depuis le 01/04/2022",
    taux_patronal: 0.0013,
    onssSourcee: true,
    base: '100',
    source: "ONSS — fichier des taux DmfA 2026/3 (catégorie 632, codes 820 et 830 : 0,13 %), en vigueur depuis le 01/04/2022 (« Employeurs ressortissant à la sous-commission paritaire pour les sociétés de logement social agréées de la région flamande (CP 339.01) ») + annexe 27 (Smals/egov3-services-catalog, EMPLOYER_CLASS_FR.xlsx), relues le 22/09/2026. Avant le 01/04/2022, 339.01 relevait de la catégorie générale 339 (532), sans ligne de taux — cohérent avec la fiche mère.",
    dateVerif: '2026-09-22',
  },
};

export async function initLoisBelgesOverrides(supabaseClient) {
  if (_overridesApplied && _overridesCache) return _overridesCache;
  try {
    const client = supabaseClient || (typeof window !== 'undefined' ? null : null);
    if (!client) {
      // Fallback: fetch from API
      const res = await fetch('/api/legal-constants');
      if (!res.ok) return null;
      const data = await res.json();
      if (data.overrides) applyOverrides(data.overrides);
      return data;
    }
    const { data, error } = await client.from('legal_constants').select('key, value, source, updated_at, status').eq('active', true);
    if (error || !data) return null;
    applyOverrides(data);
    return data;
  } catch (e) { return null; }
}

function applyOverrides(overrides) {
  if (!overrides || !Array.isArray(overrides)) return;
  let count = 0;
  for (const o of overrides) {
    const fn = OVERRIDE_MAP[o.key];
    if (fn && o.value != null) {
      const val = typeof o.value === 'string' ? parseFloat(o.value) : o.value;
      if (!isNaN(val)) { fn(val); count++; }
    }
  }
  if (count > 0) {
    _overridesApplied = true;
    _overridesCache = overrides;
    LB._meta.dernierChangement = `auto-patched ${count} constantes via veille — ${new Date().toISOString().slice(0,10)}`;
  }
}
// Cache-bust: Wed Apr  1 12:32:13 UTC 2026

// ═══════════════════════════════════════════════════════════════════════════
// AUTO-CORRECTION PAYROLL — Système de vérification automatique
// Vérifie la cohérence des constantes + calculs à chaque démarrage
// Loggue les anomalies en console (jamais silencieux sur les dérives)
// Source: Prestataire B référence net 2104.12€ pour brut 2458€ isolé sans Activa
// ═══════════════════════════════════════════════════════════════════════════
export function runPayrollSelfTest() {
  if (typeof window === 'undefined') return; // SSR skip
  const errs = [];
  const warns = [];
  const r2 = v => Math.round(v * 100) / 100;

  // ── TEST 1 : Constantes critiques présentes ──
  if (!LB.onss?.travailleur) errs.push('LB.onss.travailleur manquant');
  if (!LB.onss?.employeur?.total) errs.push('LB.onss.employeur.total manquant');
  if (!LB.pp?.tranches?.length) errs.push('LB.pp.tranches manquant');
  if (!LB.pp?.trancheNonImposable?.bareme1) errs.push('LB.pp.trancheNonImposable.bareme1 manquant');
  if (!LB.pp?.fraisPro?.salarie?.maxPP) errs.push('LB.pp.fraisPro.salarie.maxPP manquant');
  if (!LB.pp?.werkbonus?.maxReduction) errs.push('LB.pp.werkbonus.maxReduction manquant');
  if (!LB.csss?.isole?.tranches?.length) errs.push('LB.csss.isole.tranches manquant');

  // ── TEST 2 : Fourchettes légales 2026 ──
  const txW = LB.onss.travailleur;
  if (txW < 0.1300 || txW > 0.1315) errs.push(`TX ONSS travailleur hors fourchette: ${txW} (attendu ~0.1307)`);

  const txE = LB.onss.employeur.total;
  if (txE < 0.24 || txE > 0.27) warns.push(`TX ONSS employeur base inhabituel: ${txE} (attendu ~0.2507)`);

  // Alias de RÉFÉRENCE = palier baseline 01/04/2026. Le barème daté (dont le +2 % du
  // 01/07) vit dans werkbonusSocialParPalier, appliqué aux fiches selon leur période.
  const wbA = LB.pp.werkbonusSocial?.employe?.A, wbB = LB.pp.werkbonusSocial?.employe?.B;
  if (!wbA || Math.abs(wbA.max - 125.04) > 0.01) warns.push(`Werkbonus volet A max: ${wbA?.max}€ (attendu 125.04€ au 01/04/2026)`);
  if (!wbB || Math.abs(wbB.max - 168.62) > 0.01) warns.push(`Werkbonus volet B max: ${wbB?.max}€ (attendu 168.62€ au 01/04/2026)`);
  const wbPaliers = LB.pp.werkbonusSocialParPalier;
  if (!Array.isArray(wbPaliers) || wbPaliers.length < 2) warns.push('Werkbonus : barème daté par palier (werkbonusSocialParPalier) manquant ou incomplet');
  const wb0107 = (wbPaliers || []).find((p) => p.from === '2026-07-01');
  if (!wb0107 || Math.abs(wb0107.ouvrier.A.max - 137.74) > 0.01) warns.push('Werkbonus : palier 01/07/2026 (+2%) manquant ou incorrect (ouvrier A attendu 137.74€)');

  const tni = LB.pp.trancheNonImposable.bareme1;
  if (tni < 2500 || tni > 4000) warns.push(`TNI barème1 inhabituel: ${tni}€ (attendu ~2987.98€ en 2026)`);

  const maxPP = LB.pp.fraisPro.salarie.maxPP;
  if (maxPP < 5000 || maxPP > 10000) warns.push(`Frais pro maxPP inhabituel: ${maxPP}€ (attendu ~6070€ en 2026, SPF Finances)`);

  // ── TEST 3 : Calcul de référence brut 2458€ isolé sans Activa ──
  // Valeurs attendues calculées selon SPF Finances Annexe III 2026 + taxe communale 7%
  const brut = 2458;
  const onssW = r2(brut * LB.onss.travailleur);
  if (Math.abs(onssW - 321.26) > 0.05) errs.push(`ONSS travailleur 2458€: ${onssW} ≠ 321.26€ attendu`);

  const _A = brut <= wbA.s1 ? wbA.max : brut <= wbA.s2 ? r2(wbA.max - wbA.pente*(brut - wbA.s1)) : 0;
  const _B = brut <= wbB.s1 ? wbB.max : brut <= wbB.s2 ? r2(wbB.max - wbB.pente*(brut - wbB.s1)) : 0;
  const werkbonus = r2(Math.min(_A + _B, onssW));
  if (Math.abs(werkbonus - 239.01) > 1) warns.push(`Werkbonus 2458€: ${werkbonus} ≠ 239.01€ attendu (barème baseline 01/04/2026)`);

  const brutTrim = brut * 3;
  const csssT = LB.csss.isole.tranches;
  let csss = 0;
  for (const t of csssT) {
    if (brutTrim > t.trimMin && brutTrim <= t.trimMax) {
      const base = (t.forfaitTrim || 0) / 3;
      const surplus = t.mensuelTaux > 0 ? t.mensuelTaux * Math.max(0, brut - (t.mensuelSeuil || 0)) : 0;
      csss = r2(base + surplus);
      break;
    }
  }
  if (Math.abs(csss - 13.28) > 0.50) warns.push(`CSSS 2458€: ${csss} ≠ 13.28€ attendu`);

  // ── TEST 4 : Cohérence interne ONSS patronal ──
  const txTotal = (LB.onss.employeur.total || 0) + (LB.onss.employeur.detail?.moderation || 0);
  if (txTotal < 0.28 || txTotal > 0.35) warns.push(`ONSS patronal total (base+modération) inhabituel: ${(txTotal*100).toFixed(2)}% (attendu ~30.67%)`);

  // ── RAPPORT ──
  const ts = new Date().toISOString().slice(0, 10);
  if (errs.length > 0) {
    console.error(`🚨 [PAYROLL SELF-TEST ${ts}] ${errs.length} ERREUR(S) CRITIQUE(S):`, errs);
  }
  if (warns.length > 0) {
    console.warn(`⚠️ [PAYROLL SELF-TEST ${ts}] ${warns.length} avertissement(s):`, warns);
  }
  if (errs.length === 0 && warns.length === 0) {
    console.info(`✅ [PAYROLL SELF-TEST ${ts}] Toutes les constantes payroll 2026 sont cohérentes.`);
  }
  return { ok: errs.length === 0, errors: errs, warnings: warns };
}
